// src/pages/projects/TimelinesPage.jsx
// Multi-project Gantt-style timeline overview + per-project timeline editor
// Custom stage features: -N suffix, double-click rename, right-click Rename/Delete/Hide
import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, ZoomIn, ZoomOut, Calendar, Check, BarChart3, Table, Table2, Maximize2, Minimize2, Settings2, X, RotateCcw, Download, Upload, Copy, Clipboard, FileText, FileSpreadsheet, File, Plus, Pencil, Info, GitCompare, Minus, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Link, Eye, EyeOff, RefreshCw, Layers, Users, ChevronsDown, ChevronsRight, Trash2, Type } from 'lucide-react';
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BrioDatePicker } from '../../components/BrioComponents';
import { useProjectWeeklyTasks } from '../../hooks/useProjectWeeklyTasks';
import { useMOMData, DEFAULT_TEMPLATE_HTML } from '../../hooks/useMOMData';
import { resolveTimelineTrigger, TRIGGER_TYPES } from '../../utils/timelineTriggerEngine';
import { addCalendarDaysStr, applyInvoicePaidDates, deriveRowDates } from '../../utils/invoiceClosureSync';
import { resolveQuantityTokens } from '../../utils/contractScopeHelpers';
import { DEFAULT_TIMELINE_TEMPLATES } from '../../utils/residentialTimelineTemplate';
import { useTimelineVersions } from '../../hooks/useTimelineVersions';
import { useSupabaseHolidays } from '../../hooks/useSupabase';
import { getExcelStyle } from '../../utils/excelStyles';
import { useSettings } from '../../hooks/useSettings';
import { supabase } from '../../supabaseClient';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';

// Export fonts come from Accounting settings: Excel Export Styling (style1 body,
// style2 header, style3 stage) for .xlsx, Scope PDF Export Styling (title,
// heading, body) for printed PDFs. The Gantt exports take the family only and
// keep fixed sizes (8 in Excel, 10pt in PDF) to fit their bar columns. PDF
// sizes are points, so print matches the size the settings page names.
function xlsxFamily(style) {
  return style?.fontFamily || 'Calibri';
}
function xlsxFont(style, fallbackSize, extra = {}) {
  return { name: xlsxFamily(style), size: Number(style?.fontSize) || fallbackSize, ...extra };
}
// The PDFs fall back to Poppins (SIL Open Font License, files from @fontsource/poppins 5.3.0)
// whenever the configured family is not available to the browser. The font files are served
// from public/fonts/poppins: weights 300 to 600, upright and italic, Latin and Latin Extended.
const PDF_FALLBACK_FONT = 'Poppins';
function pdfFontFamily(style) {
  const fam = (style?.fontFamily || '').replace(/["\\]/g, '').trim();
  return fam && fam !== PDF_FALLBACK_FONT
    ? `"${fam}","${PDF_FALLBACK_FONT}",sans-serif`
    : `"${PDF_FALLBACK_FONT}",sans-serif`;
}
// @font-face rules for the fallback, by absolute URL because the print window is about:blank.
const PDF_FONT_RANGES = {
  latin: 'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD',
  'latin-ext': 'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF',
};
function pdfFontFaceCss() {
  const base = `${window.location.origin}/fonts/poppins/poppins`;
  const rules = [];
  [300, 400, 500, 600].forEach(weight => ['normal', 'italic'].forEach(style => {
    Object.entries(PDF_FONT_RANGES).forEach(([subset, range]) => {
      rules.push(`@font-face{font-family:"${PDF_FALLBACK_FONT}";src:url("${base}-${subset}-${weight}-${style}.woff2") format("woff2");font-weight:${weight};font-style:${style};font-display:block;unicode-range:${range}}`);
    });
  }));
  return rules.join('\n');
}
// Print once the fallback font has loaded, or the first page can print in a system face.
// Gives up waiting after a few seconds so a failed font load never blocks printing.
function printWhenFontsReady(win) {
  setTimeout(() => {
    const fonts = win.document.fonts;
    const loaded = fonts
      ? Promise.all([
        fonts.load(`300 10pt "${PDF_FALLBACK_FONT}"`),
        fonts.load(`600 10pt "${PDF_FALLBACK_FONT}"`),
      ]).then(() => fonts.ready).catch(() => {})
      : Promise.resolve();
    const timeout = new Promise(resolve => setTimeout(resolve, 3000));
    Promise.race([loaded, timeout]).then(() => { win.focus(); win.print(); });
  }, 300);
}

// Returns current ISO week ID in "YYYY-WNN" format
function getCurrentWeekId() {
  const now = new Date();
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
  const daysDiff = Math.floor((now - startOfWeek1) / 86400000);
  const week = Math.floor(daysDiff / 7) + 1;
  const year = week >= 52 && now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return year + '-W' + String(week).padStart(2, '0');
}


// Error boundary to catch render-time exceptions in ProjectTimelineEditor
class TimelineEditorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    this.setState({ stack: (error.stack || '') + '\n\nComponent stack:' + (info.componentStack || '') });
    console.error('[TimelineEditorBoundary]', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '32px 24px', fontFamily: 'Raleway, sans-serif', color: '#934948', border: '1px solid #f0d0d0', background: '#fff8f8', margin: '16px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>Timeline failed to render</div>
          <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', color: '#666', marginBottom: '12px', maxHeight: '300px', overflow: 'auto' }}>{String(this.state.error)}{this.state.stack ? '\n\n' + this.state.stack : ''}</pre>
          <button onClick={() => this.setState({ error: null, stack: null })} style={{ fontSize: '11px', padding: '4px 12px', cursor: 'pointer', border: '1px solid #934948', background: 'transparent', color: '#934948', fontFamily: 'Raleway, sans-serif' }}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── BRIO style constants (matching ProjectTasksPage) ──────────────────────────
const BRIO_GREEN  = '#689581';
const BRIO_RED    = '#934948';
const BRIO_ORANGE = '#D4824A';
const BRIO_GOLD   = '#9f902a';
const BRIO_GREY   = '#666666';
const BRIO_BORDER = '#e5e5e5';
const BRIO_BG     = '#fafaf8';

const CONTRACT_SET_LABELS  = ['A','B','C','D','E','F','G','H'];
const CONTRACT_SET_ACCENTS = [BRIO_GOLD, '#5a7ab5', '#6a9a6a', '#a05a7a', '#7a6aa0', '#a07a5a', '#5a8a9a', '#9a5a5a'];

// Instant-save: free-text cells (task/deliverable names and free-text columns) persist while the
// user is still typing, not only on blur, so an unexpected reload (network drop, browser crash,
// accidental refresh) can't lose the last few keystrokes. Writes are debounced by this interval so
// a burst of typing collapses into one save shortly after the user pauses; blur still flushes
// immediately. Keep this short enough that at most a fraction of a second of input is ever at risk.
const INSTANT_SAVE_DEBOUNCE_MS = 600;

// Table header style — matches ProjectTasksPage thStyle exactly
// Columns added by the user resize down to this width; the built in ones keep their 60px floor.
const CUSTOM_COL_MIN_WIDTH = 20;

const thStyle = {
  textAlign: 'center',
  verticalAlign: 'middle',
  padding: '4px 6px',
  fontSize: '11px',
  fontWeight: 500,
  color: BRIO_GREY,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderRight: '1px solid ' + BRIO_BORDER,
  lineHeight: '1.3',
  background: BRIO_BG, // required for sticky header to cover scrolling content
  // inset box-shadow draws reliable top/bottom lines on sticky header cells
  boxShadow: 'inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER,
};

// Table cell style — matches ProjectTasksPage tdStyle exactly
const tdStyle = {
  padding: '2px 6px',
  verticalAlign: 'middle',
  borderRight: '1px solid ' + BRIO_BORDER,
  borderBottom: '1px solid ' + BRIO_BORDER,
};

// ============================================================================
// CONSTANTS
// ============================================================================

const STAGE_NAMES = {
  pcd:        'PCD',
  advance:    'Advance',
  cd_ad: 'CD AD', cd_id: 'CD ID',
  sd_ad: 'SD AD', sd_id: 'SD ID',
  dd_ad: 'DD AD', dd_id: 'DD ID',
  aoc_ad: 'AoC AD', aoc_id: 'AoC ID',
  td_ad: 'TD AD', td_id: 'TD ID',
  gfc1: 'GFC 1', gfc2: 'GFC 2', gfc3: 'GFC 3',
  completion: 'Completion', cc: 'CC',
};

// Canonical stage order for sorting and display
const STAGE_ORDER = [
  'pcd',
  'advance',
  'cd_ad', 'cd_id',
  'sd_ad', 'sd_id',
  'dd_ad', 'dd_id',
  'aoc_ad', 'aoc_id',
  'td_ad', 'td_id',
  'gfc1', 'gfc2', 'gfc3',
  'completion', 'cc',
];

// Stage bar colours using BRIO design tokens
const STAGE_COLORS = {
  pcd:        '#7c5f9e',
  advance:    'var(--brio-teal, #689581)',
  cd_ad:      'var(--brio-blue, #3b82f6)',
  cd_id:      'var(--brio-blue, #3b82f6)',
  sd_ad:      '#6366f1',
  sd_id:      '#6366f1',
  dd_ad:      'var(--brio-gold, #9F902A)',
  dd_id:      'var(--brio-gold, #9F902A)',
  aoc_ad:     'var(--brio-orange, #DB6B5D)',
  aoc_id:     'var(--brio-orange, #DB6B5D)',
  td_ad:      '#8b5cf6',
  td_id:      '#8b5cf6',
  gfc1:       '#059669',
  gfc2:       '#059669',
  gfc3:       '#059669',
  completion: 'var(--brio-green, #16a34a)',
  cc:         'var(--brio-grey, #6b7280)',
};

// Zoom levels: pixels per day (used internally for gantt bar widths)
const ZOOM_LEVELS = [
  { label: 'Year', pxPerDay: 1.2 },
  { label: '6M', pxPerDay: 2.5 },
  { label: 'Quarter', pxPerDay: 5 },
  { label: 'Month', pxPerDay: 10 },
  { label: 'Week', pxPerDay: 20 },
];

// Timeline zoom levels for the all-projects gantt (no project selected)
// Ordered most-zoomed-IN (largest pxPerDay, shortest span) → most-zoomed-OUT. GANTT_ZOOM_DEFAULT_INDEX
// points at '1Y'; keep it in sync if levels are added/removed.
const GANTT_ZOOM_LEVELS = [
  { label: '1M',  pxPerDay: 10,    durationDays: 31 },
  { label: '2M',  pxPerDay: 6,     durationDays: 61 },
  { label: '3M',  pxPerDay: 4,     durationDays: 92 },
  { label: '6M',  pxPerDay: 2.5,   durationDays: 183 },
  { label: '1Y',  pxPerDay: 1.2,   durationDays: 365 },
  { label: '5Y',  pxPerDay: 0.24,  durationDays: 365 * 5 },
  { label: '7Y',  pxPerDay: 0.171, durationDays: 365 * 7 },
  { label: '10Y', pxPerDay: 0.12,  durationDays: 365 * 10 },
  { label: '15Y', pxPerDay: 0.08,  durationDays: 365 * 15 },
];
const GANTT_ZOOM_DEFAULT_INDEX = GANTT_ZOOM_LEVELS.findIndex(z => z.label === '1Y');

// Page zoom levels: CSS zoom applied to content for readability
const PAGE_ZOOM_LEVELS = [
  { label: '30%', scale: 0.30 },
  { label: '50%', scale: 0.50 },
  { label: '75%', scale: 0.75 },
  { label: '90%', scale: 0.90 },
  { label: '100%', scale: 1.00 },
  { label: '115%', scale: 1.15 },
  { label: '130%', scale: 1.30 },
  { label: '150%', scale: 1.50 },
  { label: '175%', scale: 1.75 },
  { label: '200%', scale: 2.00 },
];

const DAY_MS = 86400000;
const LS_PROJECT_KEY = 'timelines_selectedProjectId';
const LS_VIEW_KEY = 'timelines_viewMode';
const LS_GANTT_ZOOM_KEY = 'timelines_ganttZoom';           // per-project gantt zoom index
const LS_MULTI_GANTT_ZOOM_KEY = 'timelines_multiGanttZoom'; // multi-project gantt zoom index
const LS_GANTT_COLLAPSED_KEY = 'timelines_ganttCollapsed';  // per-project gantt collapsed stage ids: { [projectId]: string[] }

// Read a persisted gantt-zoom index, clamped to the current levels; falls back to the 1Y default.
function loadGanttZoom(key) {
  try {
    const v = parseInt(localStorage.getItem(key), 10);
    if (Number.isInteger(v) && v >= 0 && v < GANTT_ZOOM_LEVELS.length) return v;
  } catch { /* ignore */ }
  return GANTT_ZOOM_DEFAULT_INDEX;
}

// ============================================================================
// TOOLBAR ICON — frameless icon button matching ProjectTasksPage style
// ============================================================================

function ToolbarIcon({ onClick, tooltip, active, children, color }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '26px', height: '26px', border: 'none', background: 'transparent',
        // `color` (when provided) is a semantic state colour that wins over the default
        // gold/grey hover styling — used by the tri-state sync-status icon.
        cursor: 'pointer', color: color || (active ? BRIO_GOLD : (hovered ? BRIO_GOLD : BRIO_GREY)),
        transition: 'color 0.15s', position: 'relative', borderRadius: '3px',
        flexShrink: 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {hovered && tooltip && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)',
          padding: '3px 8px', background: '#333', color: '#fff', fontSize: '10px',
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 500, borderRadius: '3px',
        }}>
          {tooltip}
        </span>
      )}
    </button>
  );
}

// Table help tooltip — info icon with multiline keyboard/interaction hints
const TABLE_HELP_LINES = [
  'drag and drop: long press on task number or column header and drag',
  'new row: right click on task name opens pop-up',
  'new column: right click on column header opens pop-up',
  'multi select column: ctrl + select column headers',
  'clear cell: right click on cell for options',
  'edit task name: click on task name',
  'deliverables and stage names are non editable',
  'add dependencies in project settings',
  'double click on dependency cells for editor',
  'for column with selection property: right click on column header to add categories',
];
function TableHelpTooltip() {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '24px', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Info size={13} style={{ color: hovered ? BRIO_GOLD : BRIO_GREY, cursor: 'default', transition: 'color 0.15s' }} />
      {hovered && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0,
          background: '#333', color: '#fff', fontSize: '10px', fontWeight: 300,
          fontFamily: 'Raleway, sans-serif', lineHeight: '1.8',
          padding: '8px 12px', borderRadius: '4px', zIndex: 600,
          whiteSpace: 'nowrap', pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {TABLE_HELP_LINES.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Zoom % label — double-click resets zoom, hover shows tooltip
function ZoomResetLabel({ zoomLabel, onReset }) {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      onDoubleClick={onReset}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', fontSize: '10px', fontWeight: 400, color: '#555', fontFamily: 'Raleway, sans-serif', minWidth: '28px', textAlign: 'center', userSelect: 'none', cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {zoomLabel}
      {hovered && (
        <span style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)', padding: '3px 8px', background: '#333', color: '#fff', fontSize: '10px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 500, borderRadius: '3px' }}>
          double click to reset
        </span>
      )}
    </span>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDate(d) {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
// Display format: 13-Nov-2026
function formatDateDisplay(d) {
  if (!d) return '';
  return `${String(d.getDate()).padStart(2, '0')}-${MONTH_SHORT[d.getMonth()]}-${d.getFullYear()}`;
}
// A stored YYYY-MM-DD as the grid shows it (13-Nov-2026); anything else passes through.
function exportDateText(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return value == null ? '' : String(value);
  const d = parseDate(value.slice(0, 10));
  return d ? formatDateDisplay(d) : value;
}

// Resolve CSS variable color to a plain hex value, e.g. 'var(--brio-teal, #689581)' → '#689581'
function resolveCssColor(css) {
  if (!css) return '#cccccc';
  const m = css.match(/var\([^,]+,\s*(#[0-9a-fA-F]+)\)/);
  return m ? m[1] : css;
}

// Generate time period columns for gantt exports/header:
//   ≤62 days  → daily (one cell per day, label = day-of-month number)
//   63–730 days → monthly
//   >730 days  → quarterly
function buildGanttPeriods(rangeStart, rangeEnd) {
  const totalDays = daysBetween(rangeStart, rangeEnd);
  const periods = [];
  if (totalDays <= 62) {
    // Daily mode
    let cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    while (cur <= rangeEnd) {
      const next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
      periods.push({ label: String(cur.getDate()), start: new Date(cur), end: next });
      cur = next;
    }
  } else if (totalDays > 730) {
    // Quarterly mode
    let cur = new Date(rangeStart.getFullYear(), Math.floor(rangeStart.getMonth() / 3) * 3, 1);
    while (cur <= rangeEnd) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 3, 1);
      const label = `Q${Math.floor(cur.getMonth() / 3) + 1} '${String(cur.getFullYear()).slice(-2)}`;
      periods.push({ label, start: new Date(cur), end: next });
      cur = next;
    }
  } else {
    // Monthly mode
    let cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    while (cur <= rangeEnd) {
      const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const label = `${MONTH_SHORT[cur.getMonth()]} '${String(cur.getFullYear()).slice(-2)}`;
      periods.push({ label, start: new Date(cur), end: next });
      cur = next;
    }
  }
  return periods;
}

function daysBetween(a, b) {
  return Math.round((b - a) / DAY_MS);
}

function addDays(d, n) {
  return new Date(d.getTime() + n * DAY_MS);
}

function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

// ============================================================================
// STAGE STATUS DERIVATION
// ============================================================================

function getStageStatus(stageData) {
  if (!stageData || typeof stageData !== 'object') return { label: '—', cssClass: '' };
  if (stageData.paidDate) return { label: 'Paid', cssClass: 'brio-badge--approved' };
  if (stageData.invoiceDate) return { label: 'Invoiced', cssClass: 'brio-badge--warning' };
  if (stageData.clientApprovalDate) return { label: 'Approved', cssClass: 'brio-badge--pending' };
  if (stageData.actualDate) return { label: 'Completed', cssClass: 'brio-badge--info' };
  if (stageData.targetDate) return { label: 'Planned', cssClass: '' };
  return { label: '—', cssClass: '' };
}

// ============================================================================
// DATA PROCESSING
// ============================================================================

function buildProjectRows(activeProjects) {
  return activeProjects
    .filter(p => p.stageTimeline && typeof p.stageTimeline === 'object' && Object.keys(p.stageTimeline).length > 0)
    .map(project => {
      const timeline = project.stageTimeline;
      const stages = [];
      let projectStart = null;
      let projectEnd = null;

      // Process stages in canonical order, then any extra keys
      const processedKeys = new Set();
      const orderedKeys = [
        ...STAGE_ORDER.filter(k => timeline[k]),
        ...Object.keys(timeline).filter(k => !STAGE_ORDER.includes(k)),
      ];

      orderedKeys.forEach(stageKey => {
        if (processedKeys.has(stageKey)) return;
        processedKeys.add(stageKey);

        const data = timeline[stageKey];
        if (!data || typeof data !== 'object') return;

        // Determine bar start/end from available dates
        const contractDate = parseDate(data.contractDate);
        const targetDate = parseDate(data.targetDate);
        const actualDate = parseDate(data.actualDate);
        const invoiceDate = parseDate(data.invoiceDate);
        const paidDate = parseDate(data.paidDate);

        // Stage bar spans from earliest to latest known date
        const dates = [contractDate, targetDate, actualDate, invoiceDate, paidDate].filter(Boolean);
        if (dates.length === 0) return;

        const start = new Date(Math.min(...dates.map(d => d.getTime())));
        const end = new Date(Math.max(...dates.map(d => d.getTime())));

        // Track project-level range
        if (!projectStart || start < projectStart) projectStart = start;
        if (!projectEnd || end > projectEnd) projectEnd = end;

        const status = getStageStatus(data);

        stages.push({
          key: stageKey,
          name: STAGE_NAMES[stageKey] || stageKey,
          start,
          end: end.getTime() === start.getTime() ? addDays(end, 1) : end, // min 1-day bar
          contractDate,
          targetDate,
          actualDate,
          invoiceDate,
          paidDate,
          status,
          color: STAGE_COLORS[stageKey] || 'var(--brio-grey, #6b7280)',
          isOverdue: targetDate && !actualDate && targetDate < new Date(),
        });
      });

      return {
        id: project.id,
        name: project.name,
        code: project.projectCode,
        color: project.color || '#3b82f6',
        status: project.status,
        stages,
        projectStart,
        projectEnd,
      };
    })
    .filter(p => p.stages.length > 0)
    .sort((a, b) => {
      if (a.projectStart && b.projectStart) return a.projectStart - b.projectStart;
      return (a.name || '').localeCompare(b.name || '');
    });
}

// ============================================================================
// PER-PROJECT GANTT ROWS — derived from stageTimeline._rows (same as table)
// ============================================================================

function buildPerProjectGanttRows(project, confirmedScope) {
  const timeline = project.stageTimeline || {};
  const savedRows = Array.isArray(timeline._rows) ? timeline._rows : [];

  // Scope stage ids (timeline-key namespace) — used to flag stages surfaced from
  // the Tracking page that are not part of the confirmed scope.
  const scopeStageIds = confirmedScope
    ? new Set(buildRowsFromScope(confirmedScope).filter(r => (r.indent || 0) === 0).map(r => r.id))
    : null;

  // Determine the source rows to display — same priority as the table view's allRows
  let sourceRows;
  if (savedRows.length > 0) {
    sourceRows = ensureRowOrder(savedRows);
  } else if (isStandaloneTimeline(project)) {
    sourceRows = ensureRowOrder(STANDALONE_SEED_ROWS.map(r => ({ ...r })));
  } else if (confirmedScope) {
    sourceRows = ensureRowOrder(buildRowsFromScope(confirmedScope));
  } else {
    // Last fallback: extract stages from flat stageTimeline keys
    const fallback = buildProjectRows([project]);
    if (!fallback.length) return [];
    return fallback[0].stages.map(s => ({ ...s, indent: 0, isStage: true }));
  }

  // Task numbers mirror the table's # column: a continuous 1..N over every indent>0 row.
  const { keyToNum } = computeTaskNums(sourceRows);
  const now = new Date();

  // Pass 1 — per-row data. Deliverable/task bars come from the planned schedule (plannedStartDate →
  // plannedEndDate, the same dates the table computes), so every deliverable gets an activity bar.
  // Stage bars start from tracking dates and are widened in pass 2 to summarise their children. Each
  // deliverable inherits its parent stage's colour so the chart reads by stage.
  let curStageColor = BRIO_GOLD;
  const built = sourceRows.map((r, idx) => {
    const data = timeline[r.id] || {};
    const contractDate = parseDate(data.contractDate);
    const targetDate   = parseDate(data.targetDate);
    const actualDate   = parseDate(data.actualDate);
    const invoiceDate  = parseDate(data.invoiceDate);
    const paidDate     = parseDate(data.paidDate);
    const plannedStart = parseDate(data.plannedStartDate);
    const plannedEnd   = parseDate(data.plannedEndDate);
    // Kept for the range calc / export compatibility (usually null on this app's data).
    const targetStart  = parseDate(data.targetStartDate);
    const targetEnd    = parseDate(data.targetEndDate);
    const actualStart  = parseDate(data.actualStartDate);
    const actualEnd    = parseDate(data.actualEndDate);
    const indent = r.indent || 0;
    const isStage = indent === 0;
    const name = data.taskName || r.defaultLabel || STAGE_NAMES[r.id] || r.name || (() => {
      if (r.id.startsWith('closure_')) {
        const stageKey = r.id.slice('closure_'.length);
        return `Closure of ${STAGE_NAMES[stageKey] || stageKey} Stage`;
      }
      if (r.id.startsWith('invoicing_')) {
        const stageKey = r.id.slice('invoicing_'.length);
        return `Invoicing of ${STAGE_NAMES[stageKey] || stageKey} Stage`;
      }
      return r.id;
    })();
    // Carry-forward tasks keep the colour of the stage they were carried FROM (origin), and show
    // the origin-stage label prefix + "- CF" suffix on their name (mirrors the editable table).
    const isCF = !isStage && !!data._isCarriedForward;
    const cfFromLabel = isCF ? (data._cfFromLabel || '') : '';
    const cfOriginColor = isCF && data._cfFromStageId ? STAGE_COLORS[data._cfFromStageId] : null;
    const stageColor = STAGE_COLORS[r.id] || BRIO_GOLD;
    if (isStage) curStageColor = stageColor;
    const barColor = isStage ? stageColor : (cfOriginColor || curStageColor);
    const color = isStage ? stageColor : 'var(--brio-text-muted, #94a3b8)';

    // Bar extent: deliverables from planned start→end; stages from tracking dates (widened in pass 2).
    let start = null, end = null;
    if (isStage) {
      const dates = [contractDate, targetDate, actualDate, invoiceDate, paidDate].filter(Boolean);
      if (dates.length > 0) {
        start = new Date(Math.min(...dates.map(d => d.getTime())));
        const rawEnd = new Date(Math.max(...dates.map(d => d.getTime())));
        end = rawEnd.getTime() === start.getTime() ? addDays(rawEnd, 1) : rawEnd;
      }
    } else if (plannedStart || plannedEnd) {
      start = plannedStart || plannedEnd;
      const rawEnd = plannedEnd || plannedStart;
      end = rawEnd.getTime() === start.getTime() ? addDays(rawEnd, 1) : rawEnd;
    }

    return {
      id: r.id,
      name,
      isCF,
      cfFromLabel,
      indent,
      isStage,
      // Table-parity fields: sequential task # and whether this row heads a parent group.
      num: keyToNum[r.id] || null,
      hasChildren: !!(sourceRows[idx + 1] && (sourceRows[idx + 1].indent || 0) > indent),
      // Predecessor links (parsed seq numbers) — drive the dependency arrows.
      predecessors: parsePredecessors(data.predecessors),
      start, end,
      // Working-day duration (stored on deliverables; rolled up onto stages by the cascade) — shown
      // inside the bar. May be null when a row has no duration yet.
      duration: data.plannedDuration != null ? Number(data.plannedDuration) : null,
      contractDate, targetDate, actualDate, invoiceDate, paidDate,
      targetStart, targetEnd, actualStart, actualEnd,
      status: getStageStatus(data),
      color, barColor,
      isOverdue: !!(targetDate && !actualDate && targetDate < now),
      // Stage not in the confirmed scope — surfaced from the Tracking page
      fromTracking: isStage && !!scopeStageIds && !scopeStageIds.has(r.id),
    };
  });

  // Pass 2 — stage summary bars: widen each stage to also span its children's planned dates, so a
  // stage row reads as a summary over its deliverables (like MS Project), not just its tracking dates.
  for (let i = 0; i < built.length; i++) {
    if (!built[i].isStage) continue;
    let minS = built[i].start ? built[i].start.getTime() : null;
    let maxE = built[i].end ? built[i].end.getTime() : null;
    for (let j = i + 1; j < built.length && !built[j].isStage; j++) {
      if (built[j].start) minS = (minS == null) ? built[j].start.getTime() : Math.min(minS, built[j].start.getTime());
      if (built[j].end)   maxE = (maxE == null) ? built[j].end.getTime()   : Math.max(maxE, built[j].end.getTime());
    }
    if (minS != null && maxE != null) {
      built[i].start = new Date(minS);
      built[i].end = (maxE === minS) ? addDays(new Date(minS), 1) : new Date(maxE);
    }
  }

  return built;
}

// Filter per-project gantt rows by the active tab (stage/part filter view). Mirrors the table's
// tabFilter in displayRows so the gantt shows exactly what the table does for the same tab:
//   • a stage row (indent 0) shows only when the stage itself is selected;
//   • a deliverable assigned to a part shows when that part's substage key is selected — even if
//     the parent stage isn't (the stage bar is then hidden, matching the table);
//   • stage-level rows (start_/invoicing_/closure_, unassigned deliverables) show only when the
//     stage is selected.
// curStage is tracked for every indent-0 row (whether or not it is emitted) so child rows resolve
// against the right stage. Returns rows unchanged when no tab is active ('__all__').
function filterGanttRowsByTab(rows, activeTab, timeline) {
  if (!activeTab) return rows;
  const keys = new Set(activeTab.keys || []);
  const tl = timeline || {};
  const out = [];
  let curStage = null;
  let stageSelected = false;
  for (const row of rows) {
    if (row.isStage) {
      curStage = row.id;
      stageSelected = keys.has(row.id);
      if (stageSelected) out.push(row);
      continue;
    }
    const sid = tl[row.id]?._subStageId;
    if (sid && curStage) {
      if (keys.has(`substage_${curStage}_${sid}`)) out.push(row);
      continue;
    }
    if (stageSelected) out.push(row);
  }
  return out;
}

// A standalone timeline (BRM) treats its tabs as separate pages: every stage belongs to one tab, and a
// stage no tab lists belongs to the first tab. Returns the active tab as a { keys } filter in the same
// shape as a stored tab, so the table and Gantt filters read it unchanged. `stageIds` is the stage
// order (the editor passes its live rows; the page level reads the stored ones).
function standaloneTabView(timeline, activeTabId, stageIds = null) {
  const tl = timeline || {};
  const tabs = Array.isArray(tl._tabs) ? tl._tabs : [];
  const tab = activeTabId && activeTabId !== '__all__' ? tabs.find(t => t.id === activeTabId) : null;
  if (tab) return { ...tab, keys: tab.keys || [] };
  const claimed = new Set();
  tabs.forEach(t => (t.keys || []).forEach(k => claimed.add(k)));
  const ids = stageIds || (Array.isArray(tl._rows) && tl._rows.length ? tl._rows : STANDALONE_SEED_ROWS)
    .filter(r => (r.indent || 0) === 0).map(r => r.id);
  return { id: '__all__', keys: ids.filter(id => !claimed.has(id)) };
}

// Name of the first tab: BRM lets it be renamed, every other project keeps "Project timelines".
const mainTabName = (timeline) => (timeline && timeline._mainTabName) || 'Project timelines';

// ============================================================================
// TIMELINE HEADER (month/quarter markers)
// ============================================================================

function TimelineHeader({ rangeStart, rangeEnd, pxPerDay, durationDays }) {
  const cells = useMemo(() => {
    const totalDays = daysBetween(rangeStart, rangeEnd);
    const result = [];
    if (durationDays <= 62) {
      // Daily mode — one cell per calendar day; day 1 shows short month name instead of "1"
      let cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
      while (cur <= rangeEnd) {
        const startOffset = daysBetween(rangeStart, cur);
        const isWeekend = cur.getDay() === 0 || cur.getDay() === 6;
        const isMonthStart = cur.getDate() === 1;
        result.push({
          mode: 'day',
          label: isMonthStart ? MONTH_SHORT[cur.getMonth()] : String(cur.getDate()),
          left: startOffset * pxPerDay,
          width: pxPerDay,
          isWeekend,
          isMonthStart,
        });
        cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
      }
    } else if (durationDays > 730) {
      // Quarterly mode
      let cur = new Date(rangeStart.getFullYear(), Math.floor(rangeStart.getMonth() / 3) * 3, 1);
      while (cur <= rangeEnd) {
        const nextQ = new Date(cur.getFullYear(), cur.getMonth() + 3, 1);
        const startOffset = Math.max(0, daysBetween(rangeStart, cur));
        const endOffset = Math.min(totalDays, daysBetween(rangeStart, nextQ));
        const width = (endOffset - startOffset) * pxPerDay;
        if (width > 0) {
          const qNum = Math.floor(cur.getMonth() / 3) + 1;
          result.push({
            mode: 'quarter',
            label: `Q${qNum} '${String(cur.getFullYear()).slice(-2)}`,
            left: startOffset * pxPerDay,
            width,
            isQ1: qNum === 1,
            year: cur.getFullYear(),
          });
        }
        cur = nextQ;
      }
    } else {
      // Monthly mode
      const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let current = monthStart(rangeStart);
      while (current <= rangeEnd) {
        const nextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
        const startOffset = Math.max(0, daysBetween(rangeStart, current));
        const endOffset = Math.min(totalDays, daysBetween(rangeStart, nextMonth));
        const width = (endOffset - startOffset) * pxPerDay;
        if (width > 0) {
          result.push({
            mode: 'month',
            label: `${MONTH_NAMES_SHORT[current.getMonth()]} ${current.getFullYear()}`,
            shortLabel: MONTH_NAMES_SHORT[current.getMonth()],
            left: startOffset * pxPerDay,
            width,
            isQ1: current.getMonth() % 3 === 0,
            year: current.getFullYear(),
          });
        }
        current = nextMonth;
      }
    }
    return result;
  }, [rangeStart, rangeEnd, pxPerDay, durationDays]);

  return (
    <div style={{ position: 'relative', height: '36px', borderBottom: '1px solid var(--brio-border, #e2e8f0)' }}>
      {cells.map((cell, i) => {
        if (cell.mode === 'day') {
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: cell.left,
                width: cell.width,
                height: '100%',
                borderRight: cell.isMonthStart ? '2px solid var(--brio-border, #e2e8f0)' : '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                fontWeight: cell.isMonthStart ? 600 : 300,
                color: cell.isWeekend ? '#cbd5e1' : 'var(--brio-text-muted, #94a3b8)',
                background: cell.isWeekend ? 'var(--brio-grey-light, #f8fafc)' : 'transparent',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {cell.width > 8 ? cell.label : ''}
            </div>
          );
        }
        // month or quarter
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: cell.left,
              width: cell.width,
              height: '100%',
              borderRight: '1px solid var(--brio-border, #e2e8f0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: cell.width > 60 ? '10px' : '8px',
              fontWeight: cell.isQ1 ? 600 : 400,
              color: 'var(--brio-text-muted, #94a3b8)',
              background: cell.isQ1 ? 'var(--brio-grey-light, #f8fafc)' : 'transparent',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
            title={cell.label}
          >
            {cell.mode === 'month'
              ? (<>{cell.width > 40 ? cell.shortLabel : ''}{cell.width > 80 && <span style={{ marginLeft: '3px', opacity: 0.5 }}>{`'${String(cell.year).slice(-2)}`}</span>}</>)
              : (cell.width > 30 ? cell.label : '')
            }
          </div>
        );
      })}
      {/* Today marker in header */}
      {(() => {
        const today = new Date();
        if (today >= rangeStart && today <= rangeEnd) {
          const left = daysBetween(rangeStart, today) * pxPerDay;
          return (
            <div style={{
              position: 'absolute', left, top: 0, bottom: 0,
              width: '2px', background: 'var(--brio-orange, #DB6B5D)', zIndex: 2,
            }} />
          );
        }
        return null;
      })()}
    </div>
  );
}

// ============================================================================
// STAGE BAR
// ============================================================================

function StageBar({ stage, rangeStart, pxPerDay, showLabel }) {
  const left = daysBetween(rangeStart, stage.start) * pxPerDay;
  const width = Math.max(daysBetween(rangeStart, stage.end) * pxPerDay - left, 4);
  const barLeft = left;
  const barWidth = width;

  // Build tooltip
  const tooltipParts = [stage.name];
  if (stage.targetDate) tooltipParts.push(`Target: ${formatDateDisplay(stage.targetDate)}`);
  if (stage.actualDate) tooltipParts.push(`Actual: ${formatDateDisplay(stage.actualDate)}`);
  if (stage.invoiceDate) tooltipParts.push(`Invoice: ${formatDateDisplay(stage.invoiceDate)}`);
  if (stage.paidDate) tooltipParts.push(`Paid: ${formatDateDisplay(stage.paidDate)}`);
  if (stage.isOverdue) tooltipParts.push('⚠ OVERDUE');

  return (
    <div
      title={tooltipParts.join('\n')}
      style={{
        position: 'absolute',
        left: barLeft,
        top: '3px',
        height: '18px',
        width: barWidth,
        minWidth: '4px',
        background: stage.isOverdue ? 'var(--brio-red, #ef4444)' : stage.color,
        opacity: stage.status.label === 'Paid' ? 0.5 : 0.85,
        borderRadius: '3px',
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {showLabel && barWidth > 30 && (
        <span style={{
          fontSize: '8px',
          color: '#fff',
          fontWeight: 600,
          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
          whiteSpace: 'nowrap',
          padding: '0 2px',
        }}>
          {stage.name}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// PROJECT ROW
// ============================================================================

function ProjectRow({
  project,
  rangeStart,
  pxPerDay,
  totalWidth,
  isExpanded,
  onToggle,
  activeUsers,
}) {
  const today = new Date();
  const todayLeft = daysBetween(rangeStart, today) * pxPerDay;

  return (
    <>
      {/* Project header row */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: isExpanded ? 'var(--brio-grey-light, #f8fafc)' : 'transparent',
          minHeight: '32px',
        }}
      >
        {/* Left label panel */}
        <div
          style={{
            width: '240px',
            minWidth: '240px',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderRight: '1px solid var(--brio-border, #e2e8f0)',
            cursor: 'pointer',
            userSelect: 'none',
            position: 'sticky',
            left: 0,
            zIndex: 2,
            background: isExpanded ? 'var(--brio-grey-light, #f8fafc)' : '#fff',
          }}
          onClick={onToggle}
        >
          {isExpanded
            ? <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--brio-text-muted)', flexShrink: 0 }} />
            : <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--brio-text-muted)', flexShrink: 0 }} />
          }
          <div
            style={{
              width: '8px', height: '8px', borderRadius: '2px', flexShrink: 0,
              background: project.color,
            }}
          />
          <span style={{
            fontSize: '11px', fontWeight: 600,
            color: 'var(--brio-text, #334155)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {project.code || project.name}
          </span>
          <span style={{
            fontSize: '9px', color: 'var(--brio-text-muted, #94a3b8)',
            marginLeft: 'auto', flexShrink: 0,
          }}>
            {project.stages.length} stages
          </span>
        </div>

        {/* Timeline bar area — collapsed: stacked bars */}
        <div style={{ position: 'relative', flex: 1, height: '24px', alignSelf: 'center' }}>
          {!isExpanded && project.stages.map(stage => (
            <StageBar
              key={stage.key}
              stage={stage}
              rangeStart={rangeStart}
              pxPerDay={pxPerDay}
              showLabel={pxPerDay >= 5}
            />
          ))}
          {/* Today line */}
          {todayLeft > 0 && todayLeft < totalWidth && (
            <div style={{
              position: 'absolute', left: todayLeft, top: 0, bottom: 0,
              width: '1px', background: 'var(--brio-orange, #DB6B5D)', opacity: 0.3,
            }} />
          )}
        </div>
      </div>

      {/* Expanded stage rows */}
      {isExpanded && project.stages.map(stage => (
        <div
          key={stage.key}
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--brio-border-light, #f1f5f9)',
            minHeight: '28px',
            background: 'var(--brio-grey-light, #f8fafc)',
          }}
        >
          {/* Stage label */}
          <div style={{
            width: '240px', minWidth: '240px',
            padding: '4px 8px 4px 32px',
            display: 'flex', alignItems: 'center', gap: '6px',
            borderRight: '1px solid var(--brio-border, #e2e8f0)',
            position: 'sticky', left: 0, zIndex: 2,
            background: 'var(--brio-grey-light, #f8fafc)',
          }}>
            <div style={{
              width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
              background: stage.color,
            }} />
            <span style={{
              fontSize: '10px', color: 'var(--brio-text, #334155)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {stage.name}
            </span>
            {stage.isOverdue && (
              <span style={{
                fontSize: '8px', color: 'var(--brio-red, #ef4444)',
                fontWeight: 600, flexShrink: 0,
              }}>
                OVERDUE
              </span>
            )}
            {stage.status.label !== '—' && (
              <span
                className={stage.status.cssClass}
                style={{
                  fontSize: '8px', marginLeft: 'auto', flexShrink: 0,
                  padding: '1px 4px', borderRadius: '3px',
                }}
              >
                {stage.status.label}
              </span>
            )}
          </div>

          {/* Stage bar */}
          <div style={{ position: 'relative', flex: 1, height: '24px', alignSelf: 'center' }}>
            <StageBar
              stage={stage}
              rangeStart={rangeStart}
              pxPerDay={pxPerDay}
              showLabel={pxPerDay >= 3}
            />
            {/* Today line */}
            {todayLeft > 0 && todayLeft < totalWidth && (
              <div style={{
                position: 'absolute', left: todayLeft, top: 0, bottom: 0,
                width: '1px', background: 'var(--brio-orange, #DB6B5D)', opacity: 0.3,
              }} />
            )}
          </div>
        </div>
      ))}
    </>
  );
}

// ============================================================================
// PER-PROJECT GANTT ROW — renders one stage or deliverable row
// ============================================================================

// Fixed row heights (border-box) so the dependency-arrow overlay can place its endpoints by row index.
const GANTT_STAGE_ROW_H = 30;
const GANTT_TASK_ROW_H = 26;
// Per-project gantt sticky left region: the original 240px (# + Task Name) plus an editable
// Dependency column, so predecessors can be read and changed without leaving the gantt.
const GANTT_NAME_W = 240;
const GANTT_DEP_W = 76;
const GANTT_LEFT_W = GANTT_NAME_W + GANTT_DEP_W; // 316 — bar area + arrow overlay start after this

function PerProjectGanttRow({ row, rangeStart, pxPerDay, totalWidth, isCollapsed, onToggleCollapse, dim, canDrag, onBarDrag, showDeps, depDisplay, onDepChange }) {
  const [depEditing, setDepEditing] = useState(false);
  const leftW = showDeps ? GANTT_LEFT_W : GANTT_NAME_W;
  const today = new Date();
  const todayLeft = daysBetween(rangeStart, today) * pxPerDay;
  const [drag, setDrag] = useState(null); // live pixel preview { mode, left, width } while dragging, else null

  const tooltipParts = [row.name];
  if (row.isStage) {
    if (row.contractDate) tooltipParts.push(`Contract: ${formatDateDisplay(row.contractDate)}`);
    if (row.targetDate)   tooltipParts.push(`Target: ${formatDateDisplay(row.targetDate)}`);
    if (row.actualDate)   tooltipParts.push(`Actual: ${formatDateDisplay(row.actualDate)}`);
    if (row.invoiceDate)  tooltipParts.push(`Invoice: ${formatDateDisplay(row.invoiceDate)}`);
    if (row.paidDate)     tooltipParts.push(`Paid: ${formatDateDisplay(row.paidDate)}`);
  } else if (row.start) {
    tooltipParts.push(`Planned: ${formatDateDisplay(row.start)} → ${row.end ? formatDateDisplay(row.end) : '?'}`);
  }
  if (row.isOverdue) tooltipParts.push('⚠ OVERDUE');

  const hasBar = !!(row.start && row.end);
  const barLeft  = hasBar ? daysBetween(rangeStart, row.start) * pxPerDay : 0;
  const barWidth = hasBar ? Math.max(daysBetween(rangeStart, row.end) * pxPerDay - barLeft, 4) : 0;
  const barH = row.isStage ? 14 : 11;

  // ── Draggable bars ────────────────────────────────────────────────────────
  // Leaf/deliverable bars can be dragged to reschedule: the body moves the whole bar (shifts the
  // planned dates, preserving duration), the left edge changes the start date, the right edge changes
  // the duration. Stage bars are read-only roll-ups of their children, so they are never draggable.
  // The gesture tracks a pixel preview locally and commits day-snapped dates on release via onBarDrag.
  const draggable = canDrag && hasBar && !row.isStage;
  const startBarDrag = (e, forcedMode) => {
    if (!draggable) return;
    e.stopPropagation();
    e.preventDefault();
    const minW = Math.max(pxPerDay, 3); // never resize below ~1 day wide
    const origLeft = barLeft, origWidth = barWidth, right = barLeft + barWidth;
    const startX = e.clientX;
    const geomFor = (dx) => {
      if (forcedMode === 'move') return { left: origLeft + dx, width: origWidth };
      if (forcedMode === 'resize-left') { const left = Math.min(origLeft + dx, right - minW); return { left, width: right - left }; }
      return { left: origLeft, width: Math.max(origWidth + dx, minW) }; // resize-right
    };
    const onMove = (ev) => setDrag({ mode: forcedMode, ...geomFor(ev.clientX - startX) });
    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDrag(null);
      const dx = ev.clientX - startX;
      if (Math.abs(dx) < 3) return; // a click / tiny jitter — not a drag
      const g = geomFor(dx);
      const dayAt = (px) => Math.round(px / pxPerDay);
      const newStart = addDays(rangeStart, dayAt(g.left));
      const newEnd = addDays(rangeStart, dayAt(g.left + g.width));
      onBarDrag(row.id, forcedMode, newStart, newEnd);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const shownLeft = drag ? drag.left : barLeft;
  const shownWidth = drag ? drag.width : barWidth;
  // Working-day duration shown inside the bar (e.g. "3d"). It's a label off the committed row data, so
  // it updates when the drag commits rather than tracking the live drag width.
  const durLabel = row.duration != null ? `${row.duration}d` : '';

  return (
    <div style={{
      display: 'flex',
      borderBottom: '1px solid ' + (row.isStage ? 'var(--brio-border, #e2e8f0)' : 'var(--brio-border-light, #f1f5f9)'),
      height: (row.isStage ? GANTT_STAGE_ROW_H : GANTT_TASK_ROW_H) + 'px',
      boxSizing: 'border-box',
      background: row.isStage ? BRIO_BG : '#fff',
    }}>
      {/* Label — mirrors the table's # (Sr No) + Task Name (+ Dependency) columns, with the same hierarchy */}
      <div style={{
        width: leftW + 'px', minWidth: leftW + 'px',
        display: 'flex', alignItems: 'stretch',
        borderRight: '1px solid var(--brio-border, #e2e8f0)',
        position: 'sticky', left: 0, zIndex: 2,
        background: row.isStage ? BRIO_BG : '#fff',
      }}>
        {/* # (Sr No) — sequential task number, blank on stage rows (same as the table) */}
        <div style={{
          width: '32px', minWidth: '32px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRight: '1px solid var(--brio-border-light, #f1f5f9)',
          fontSize: '10px', fontWeight: 400, color: 'var(--brio-text-muted, #94a3b8)',
        }}>
          {row.num || ''}
        </div>
        {/* Task name — indented by hierarchy level; collapse chevron on stages and parent tasks */}
        <div style={{
          flex: 1, minWidth: 0,
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '4px 8px',
          paddingLeft: (8 + (row.indent || 0) * 12) + 'px',
        }}>
          {(row.isStage || row.hasChildren) ? (
            <span
              onClick={onToggleCollapse}
              style={{ cursor: onToggleCollapse ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: 'var(--brio-text-muted, #94a3b8)', fontSize: '10px', userSelect: 'none', width: '11px' }}
            >
              {isCollapsed ? '▶' : '▼'}
            </span>
          ) : (
            <span style={{ width: '11px', flexShrink: 0 }} />
          )}
          {row.isStage && (
            <div style={{ width: '8px', height: '8px', borderRadius: '2px', flexShrink: 0, background: row.color }} />
          )}
          <span style={{
            fontSize: row.isStage ? '11px' : '10px',
            fontWeight: row.isStage ? 600 : (row.hasChildren ? 500 : 300),
            color: 'var(--brio-text, #334155)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.isCF && row.cfFromLabel && (
              <span style={{ color: '#555', fontWeight: 400 }}>{row.cfFromLabel}_ </span>
            )}
            {row.name}
            {row.isCF && (
              <span
                title="Carried forward"
                style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px' }}
              >
                - CF
              </span>
            )}
            {row.fromTracking && !isCustomStage(row.id) && (
              <span
                title="Read from the Tracking page (not in the scope of work)"
                style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px' }}
              >
                -TR
              </span>
            )}
            {isCustomStage(row.id) && !row.hideCustomTag && (
              <span
                title="Manually added stage (not in the scope of work)"
                style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px' }}
              >
                -N
              </span>
            )}
          </span>
          {row.isOverdue && (
            <span style={{ fontSize: '8px', color: 'var(--brio-red, #ef4444)', fontWeight: 600, flexShrink: 0 }}>OVERDUE</span>
          )}
          {row.status && row.status.label !== '—' && (
            <span className={row.status.cssClass} style={{ fontSize: '8px', marginLeft: 'auto', flexShrink: 0, padding: '1px 4px', borderRadius: '3px' }}>
              {row.status.label}
            </span>
          )}
        </div>
        {/* Dependency — read + edit predecessors right in the gantt (task rows only). Same syntax as the
            table's Dependency column: task numbers with optional FS/SS/FF/SF type and ±Nd lag. */}
        {showDeps && (() => {
          const isSystemRow = String(row.id).startsWith('invoicing_') || String(row.id).startsWith('closure_') || String(row.id).startsWith('start_');
          const depEditable = !row.isStage && !isSystemRow && typeof onDepChange === 'function';
          return (
            <div style={{
              width: GANTT_DEP_W + 'px', minWidth: GANTT_DEP_W + 'px', flexShrink: 0,
              borderLeft: '1px solid var(--brio-border-light, #f1f5f9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
            }}>
              {row.isStage ? null : (depEditing && depEditable) ? (
                <input
                  autoFocus
                  defaultValue={depDisplay || ''}
                  onBlur={(e) => { setDepEditing(false); onDepChange(row.id, e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); else if (e.key === 'Escape') setDepEditing(false); }}
                  style={{ width: '100%', border: 'none', outline: 'none', fontSize: '9px', textAlign: 'center', background: 'rgba(159,144,42,0.10)', fontFamily: 'inherit', padding: '1px 2px' }}
                />
              ) : (
                <span
                  onClick={depEditable ? () => setDepEditing(true) : undefined}
                  title={depEditable ? 'Click to edit dependency (e.g. 12FS, 5SS+2d)' : (depDisplay || undefined)}
                  style={{ fontSize: '9px', fontWeight: 300, color: depDisplay ? 'var(--brio-text, #334155)' : '#c8c8c8', cursor: depEditable ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
                >
                  {depDisplay || (depEditable ? '—' : '')}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Bar area — one activity bar per row from the planned schedule (stages summarise their children) */}
      <div style={{ position: 'relative', flex: 1, height: '100%' }}>
        {hasBar && (
          <div
            title={draggable ? `${tooltipParts.join('\n')}\n\nDrag to move · drag either edge to resize` : tooltipParts.join('\n')}
            onPointerDown={draggable ? (e) => startBarDrag(e, 'move') : undefined}
            style={{
              position: 'absolute', left: shownLeft, top: '50%', transform: 'translateY(-50%)',
              height: barH + 'px', width: shownWidth, minWidth: '4px',
              background: row.isOverdue ? 'var(--brio-red, #ef4444)' : row.barColor,
              opacity: dim ? 0.18 : (row.status && row.status.label === 'Paid' ? 0.5 : (row.isStage ? 0.9 : 0.8)),
              borderRadius: row.isStage ? '3px' : '2px',
              cursor: draggable ? (drag ? 'grabbing' : 'grab') : 'default',
              display: 'flex', alignItems: 'center', overflow: 'hidden',
              transition: drag ? 'none' : 'opacity 0.12s',
              boxShadow: drag ? '0 0 0 1px var(--brio-gold, #9F902A)' : 'none',
              touchAction: draggable ? 'none' : undefined,
            }}
          >
            {row.isStage ? (
              shownWidth > 30 && (
                <span style={{ fontSize: '8px', color: '#fff', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap', padding: '0 4px', pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.name}{durLabel && ` · ${durLabel}`}
                </span>
              )
            ) : (
              durLabel && shownWidth > 16 && (
                <span style={{ flex: 1, textAlign: 'center', fontSize: '8px', color: '#fff', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.35)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 3px', pointerEvents: 'none' }}>
                  {durLabel}
                </span>
              )
            )}
            {draggable && shownWidth > 22 && (
              <>
                <div onPointerDown={(e) => startBarDrag(e, 'resize-left')}
                  style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '6px', cursor: 'ew-resize' }} />
                <div onPointerDown={(e) => startBarDrag(e, 'resize-right')}
                  style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'ew-resize' }} />
              </>
            )}
          </div>
        )}
        {/* Tiny-bar resize grip — a short bar can't host the inline edge handles above, so expose an
            external grip straddling its right edge to stretch the duration. Renders past the bar (the
            bar clips its own overflow), so it stays grabbable no matter how small the bar is. */}
        {draggable && shownWidth <= 22 && (
          <div
            title="Drag to extend duration"
            onPointerDown={(e) => startBarDrag(e, 'resize-right')}
            style={{ position: 'absolute', left: shownLeft + Math.max(shownWidth - 2, 2), top: '50%', transform: 'translateY(-50%)', height: (barH + 6) + 'px', width: '11px', cursor: 'ew-resize', zIndex: 3, display: 'flex', alignItems: 'center', touchAction: 'none' }}
          >
            <div style={{ width: '3px', height: '100%', background: row.barColor, opacity: 0.55, borderRadius: '1px' }} />
          </div>
        )}
        {/* Today line */}
        {todayLeft > 0 && todayLeft < totalWidth && (
          <div style={{ position: 'absolute', left: todayLeft, top: 0, bottom: 0, width: '1px', background: 'var(--brio-orange, #DB6B5D)', opacity: 0.3 }} />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PER-PROJECT GANTT CHART — rows + dependency-arrow overlay with chain highlighting
// ============================================================================

// Renders the per-project rows and a single SVG overlay that draws the dependency links between
// bars (predecessor finish → dependent start, MS-Project style). Hovering a link highlights every
// task in its connected dependency chain and dims the rest.
function PerProjectGanttChart({ rows, rangeStart, pxPerDay, totalWidth, collapsedIds, onToggle, canDrag, onBarDrag, showDeps, depDisplayFor, onDepChange }) {
  const [hoverChain, setHoverChain] = useState(null); // Set<rowId> | null
  const leftW = showDeps ? GANTT_LEFT_W : GANTT_NAME_W;

  const geom = useMemo(() => {
    // Row vertical layout (fixed heights → deterministic arrow endpoints).
    const info = {};
    let top = 0;
    rows.forEach(r => {
      const h = r.isStage ? GANTT_STAGE_ROW_H : GANTT_TASK_ROW_H;
      info[r.id] = { top, h, center: top + h / 2 };
      top += h;
    });
    // Bar geometry (matches PerProjectGanttRow: left/width from planned dates).
    const bar = {};
    rows.forEach(r => {
      if (r.start && r.end) {
        const left = daysBetween(rangeStart, r.start) * pxPerDay;
        const width = Math.max(daysBetween(rangeStart, r.end) * pxPerDay - left, 4);
        bar[r.id] = { left, right: left + width };
      }
    });
    // Resolve predecessor seq numbers → row ids, then build the arrow list + undirected adjacency
    // (only between rows that both currently have a bar and are both visible).
    const numToId = {};
    rows.forEach(r => { if (r.num != null) numToId[r.num] = r.id; });
    const arrows = [];
    const adj = {};
    const link = (a, b) => { (adj[a] = adj[a] || new Set()).add(b); (adj[b] = adj[b] || new Set()).add(a); };
    rows.forEach(r => {
      (r.predecessors || []).forEach(p => {
        const fromId = numToId[p.rowNumber];
        if (!fromId || fromId === r.id || !bar[fromId] || !bar[r.id]) return;
        arrows.push({ id: fromId + '->' + r.id, fromId, toId: r.id });
        link(fromId, r.id);
      });
    });
    return { info, totalH: top, bar, arrows, adj };
  }, [rows, rangeStart, pxPerDay]);

  // Connected dependency chain (transitive) containing the given seed rows.
  const chainFor = useCallback((seedIds) => {
    const seen = new Set();
    const stack = [...seedIds];
    while (stack.length) {
      const id = stack.pop();
      if (seen.has(id)) continue;
      seen.add(id);
      (geom.adj[id] || []).forEach(n => { if (!seen.has(n)) stack.push(n); });
    }
    return seen;
  }, [geom.adj]);

  const chainActive = !!hoverChain;
  const ARROW = '#94a3b8';
  const ARROW_HL = BRIO_GOLD;

  return (
    <div style={{ position: 'relative', minWidth: leftW + totalWidth }}>
      {rows.map(row => (
        <PerProjectGanttRow
          key={row.id}
          row={row}
          rangeStart={rangeStart}
          pxPerDay={pxPerDay}
          totalWidth={totalWidth}
          dim={chainActive && !hoverChain.has(row.id)}
          isCollapsed={(row.isStage || row.hasChildren) && collapsedIds.has(row.id)}
          onToggleCollapse={(row.isStage || row.hasChildren) ? () => onToggle(row.id) : undefined}
          canDrag={canDrag}
          onBarDrag={onBarDrag}
          showDeps={showDeps}
          depDisplay={showDeps && depDisplayFor ? depDisplayFor(row.id) : ''}
          onDepChange={onDepChange}
        />
      ))}
      {geom.arrows.length > 0 && (
        <svg
          width={totalWidth} height={geom.totalH}
          style={{ position: 'absolute', left: leftW, top: 0, width: totalWidth, height: geom.totalH, pointerEvents: 'none', zIndex: 1, overflow: 'visible' }}
        >
          <defs>
            <marker id="gantt-arrowhead" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto" markerUnits="userSpaceOnUse">
              <path d="M0,0 L6,3 L0,6 Z" fill={ARROW} />
            </marker>
            <marker id="gantt-arrowhead-hl" markerWidth="8" markerHeight="8" refX="6" refY="3.2" orient="auto" markerUnits="userSpaceOnUse">
              <path d="M0,0 L6.4,3.2 L0,6.4 Z" fill={ARROW_HL} />
            </marker>
          </defs>
          {geom.arrows.map(a => {
            const from = geom.bar[a.fromId], to = geom.bar[a.toId];
            const yF = geom.info[a.fromId].center, yT = geom.info[a.toId].center;
            const x0 = from.right, x1 = to.left, stub = 8;
            // Elbow: right stub off the predecessor's finish, vertical to the target row, into its start.
            const midX = Math.max(x0 + stub, x1 - stub);
            const d = `M ${x0} ${yF} H ${midX} V ${yT} H ${x1}`;
            const highlighted = chainActive && hoverChain.has(a.fromId) && hoverChain.has(a.toId);
            const faded = chainActive && !highlighted;
            return (
              <g key={a.id}
                onMouseEnter={() => setHoverChain(chainFor([a.fromId, a.toId]))}
                onMouseLeave={() => setHoverChain(null)}
                style={{ cursor: 'pointer' }}
              >
                <path d={d} fill="none" stroke="transparent" strokeWidth="10" style={{ pointerEvents: 'stroke' }} />
                <path d={d} fill="none"
                  stroke={highlighted ? ARROW_HL : ARROW}
                  strokeWidth={highlighted ? 1.6 : 1}
                  opacity={faded ? 0.12 : (highlighted ? 1 : 0.5)}
                  markerEnd={`url(#${highlighted ? 'gantt-arrowhead-hl' : 'gantt-arrowhead'})`}
                  style={{ pointerEvents: 'none', transition: 'opacity 0.12s' }}
                />
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

// ============================================================================
// LEGEND
// ============================================================================

function TimelineLegend() {
  const items = [
    { label: 'Advance', color: STAGE_COLORS.advance },
    { label: 'CD', color: STAGE_COLORS.cd_ad },
    { label: 'SD', color: STAGE_COLORS.sd_ad },
    { label: 'DD', color: STAGE_COLORS.dd_ad },
    { label: 'AoC', color: STAGE_COLORS.aoc_ad },
    { label: 'TD', color: STAGE_COLORS.td_ad },
    { label: 'GFC', color: STAGE_COLORS.gfc1 },
    { label: 'Completion', color: STAGE_COLORS.completion },
    { label: 'Overdue', color: 'var(--brio-red, #ef4444)' },
    { label: 'Today', color: 'var(--brio-orange, #DB6B5D)' },
  ];

  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', padding: '6px 0' }}>
      {items.map(it => (
        <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {it.label === 'Today' ? (
            <div style={{ width: '2px', height: '12px', background: it.color }} />
          ) : (
            <div style={{ width: '12px', height: '8px', borderRadius: '2px', background: it.color, opacity: 0.85 }} />
          )}
          <span style={{ fontSize: '9px', color: 'var(--brio-text-muted, #94a3b8)' }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// DEFAULT STAGE ID → KEY MAPPING (matches TimelinePage.jsx)
// ============================================================================

const DEFAULT_ID_TO_KEY = {
  1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
  6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
  12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
  17: 'pcd', // PCD (Preconcept/Masterplan) — stageId 17 in the default scope template
};

// Date columns for the per-project editor
const DATE_COLUMNS = [
  { key: 'contractDate', label: 'Contract' },
  { key: 'targetDate', label: 'Planned' },
  { key: 'actualDate', label: 'Actual' },
  { key: 'clientApprovalDate', label: 'Approved' },
  { key: 'invoiceDate', label: 'Invoice' },
  { key: 'paidDate', label: 'Paid' },
];

// Status options for the schedule status column — matches ProjectTasksPage progress style
const STATUS_OPTIONS = [
  { label: 'Not started', value: 0,   color: '#999',     bg: '#f0f0f0' },
  { label: 'In progress', value: 50,  color: BRIO_GOLD,  bg: 'rgba(159,144,42,0.1)' },
  { label: 'Done',        value: 100, color: BRIO_GREEN, bg: '#68958115' },
];

// ============================================================================
// HELPERS: duration & date arithmetic
// ============================================================================

function calcDaysBetweenDates(startStr, endStr) {
  const s = parseDate(startStr);
  const e = parseDate(endStr);
  if (!s || !e) return null;
  return Math.max(0, Math.round((e - s) / DAY_MS));
}

// Count working days (inclusive) between two Date objects, excluding weekends and holidays.
function countWorkingDays(startDate, endDate, workDays, holidaySet) {
  if (!startDate || !endDate) return null;
  let count = 0;
  const d = new Date(startDate);
  while (d <= endDate) {
    const dow = d.getDay();
    const iso = formatDate(d);
    if (workDays.has(dow) && !holidaySet.has(iso)) count++;
    d.setDate(d.getDate() + 1);
  }
  return count > 0 ? count : null;
}

function addDaysToDateStr(dateStr, days) {
  const d = parseDate(dateStr);
  if (!d || days == null) return '';
  const result = new Date(d.getTime() + days * DAY_MS);
  const yyyy = result.getFullYear();
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Low-saturation row backgrounds for stage rows (indent 0)
const STAGE_ROW_COLORS = {
  pcd:        '#f3f0f8',
  advance:    '#f5f5ef',
  cd_ad:      '#eef2fa',  cd_id:      '#eef2fa',
  sd_ad:      '#f0eef8',  sd_id:      '#f0eef8',
  dd_ad:      '#f5f4ec',  dd_id:      '#f5f4ec',
  aoc_ad:     '#f7f0eb',  aoc_id:     '#f7f0eb',
  td_ad:      '#f2eff8',  td_id:      '#f2eff8',
  gfc1:       '#edf5f0',  gfc2:       '#edf5f0',  gfc3: '#edf5f0',
  completion: '#eef6f1',
  cc:         '#f2f2f2',
};

// ============================================================================
// HIERARCHICAL ROW HELPERS
// ============================================================================

// Convert scope key (cd-ad) to stageTimeline key (cd_ad)
function scopeKeyToTimelineKey(scopeKey) {
  return scopeKey.replace(/-(\d)/g, '$1').replace(/-/g, '_');
}

// Text-based SOW reconciliation helpers. A deliverable link is matched to a SOW deliverable by its
// LABEL (not its position), so the link survives reordering AND id changes and only breaks on a
// genuine removal/replacement. normReconLabel canonicalises a label (lowercase, drop parentheticals
// & punctuation, collapse whitespace); reconLabelsMatch treats two normalised labels as the same
// deliverable when they are equal, one contains the other (>= 8 chars), or most of the shorter's
// meaningful tokens appear in the other.
// Sentinel `sowRef` value marking a row linked to its stage's info text rather than a numbered
// deliverable (see the LinkToSowModal docs below). Non-numeric on purpose so the numeric sowRef
// parsers/reconciliation ignore it; hoisted here so the module-level diff helpers can reference it.
const SOW_STAGE_REF = 'stage';
// Construction / completion stages that carry NO Scope-of-Work deliverables. On apply, the master
// template's rows for these stages are authoritative and copied verbatim: no SOW linking, no unlinked
// badges, no auto-added deliverables, no reconciliation tint. (AoC / GFC / CC / Completion.)
const RECON_EXCLUDED_STAGES = new Set(['aoc_ad', 'aoc_id', 'gfc1', 'gfc2', 'gfc3', 'cc', 'completion']);
// Master template rows that must NEVER carry a SOW link in any project — consultant/vendor appointment
// tasks and deliverables the standard SOW dropped. They're process tasks, not deliverables, so they're
// left as plain (un-highlighted) tasks on apply regardless of any name/anchor match.
const RECON_NO_LINK_ROWS = new Set([
  'row_dd_ad_appt_landscape',   // "Landscape consultant" (appointment)
  'row_dd_ad_appt_qs',          // "Quantity Surveyor" (appointment)
  'row_dd_ad_whitegoods',       // "Selection of white goods" (deliverable dropped by the standard SOW)
  'row_dd_id_id_lightconsult',  // "Appointment of lighting consultant" (appointment)
]);
const RECON_STOPWORDS = new Set(['of','the','and','with','for','all','a','an','to','in','on','as','or','if','required','applicable','any','other','etc','from']);
function normReconLabel(s) {
  return String(s || '').toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}
function reconMeaningfulTokens(norm) {
  return (norm ? norm.split(' ') : []).filter(w => w && !RECON_STOPWORDS.has(w));
}
function reconTokenSim(a, b) {
  // Two tokens are the same word when equal or sharing a long common prefix — handles plural / verb
  // forms (schedule/schedules, integrated/integration, vendor/vendors) without a full stemmer.
  if (a === b) return true;
  let i = 0; const n = Math.min(a.length, b.length);
  while (i < n && a[i] === b[i]) i++;
  return i >= 5 || (n >= 4 && i / n >= 0.75);
}
function reconLabelsMatch(aNorm, bNorm) {
  if (!aNorm || !bNorm) return false;
  if (aNorm === bNorm) return true;
  const shorter = aNorm.length <= bNorm.length ? aNorm : bNorm;
  const longer = aNorm.length <= bNorm.length ? bNorm : aNorm;
  if (shorter.length >= 8 && longer.includes(shorter)) return true;
  const a = reconMeaningfulTokens(aNorm), b = reconMeaningfulTokens(bNorm);
  if (a.length < 2) return false;
  let inter = 0;
  a.forEach(t => { if (b.some(u => reconTokenSim(t, u))) inter++; });
  return inter >= 2 && (inter / a.length) >= 0.7;
}

// Resolve a linked row's "fingerprint" — the deliverable label(s) it was consciously linked to.
// Prefers the label snapshot captured at link time (`sowRefLabels`), which survives in-place edits,
// reordering and id changes of the anchor SOW; falls back to deriving the label from the anchor SOW
// at the row's stage-relative sowRef position for legacy links authored before fingerprinting. The
// returned labels are normalised (normReconLabel) for matching. Stage-info links and unlinked rows
// carry no fingerprint (empty array), so reconciliation never flags them.
function resolveLinkFingerprint(rowData, stageKey, anchorStageDelivsNorm) {
  const stored = rowData && Array.isArray(rowData.sowRefLabels) ? rowData.sowRefLabels : null;
  if (stored && stored.length) return stored.map(normReconLabel).filter(Boolean);
  const ref = rowData && rowData.sowRef;
  if (ref == null || ref === '' || String(ref) === SOW_STAGE_REF) return [];
  const nums = String(ref).split(',').map(x => parseInt(x.trim(), 10)).filter(n => n > 0);
  const anchors = (anchorStageDelivsNorm && anchorStageDelivsNorm[stageKey]) || [];
  return nums.map(n => anchors[n - 1]).filter(Boolean);
}

// Symmetric diff of a timeline's deliverables against a TARGET SOW, per stage. Uses the intent-based
// matcher (reconLabelsMatch) so renames/reorders are followed, not mis-reported as remove+add. Two
// kinds of row participate as "deliverable coverage":
//   • an EXPLICIT link — any row carrying a `sowRef` (matched via its fingerprint), and
//   • a STRUCTURAL deliverable row — a `deliv_<id>` row seeded from scope, matched by its own label.
// Meeting/revision/invoicing/closure/start rows, stage-info links, unlinked rows and custom `row_`
// rows without a link never participate. Returns:
//   • orphanedLinks — Set of row ids WITH an explicit sowRef whose deliverable is gone (drives the
//                     editor's red tint; scoped to explicit links to preserve established behaviour).
//   • orphanedRows  — orphaned EXPLICIT-link rows only (a row carrying sowRef + its frozen sowRefLabels
//                     fingerprint), as { rowId, stageKey, label, hasSowRef } — drives the drift report's
//                     "removed / renamed" list. Because the fingerprint is frozen at link time, a row is
//                     reported only when the SOW side changed (deliverable removed/renamed, or a
//                     different SOW applied) — never for a manual task rename. Structural deliv_ rows
//                     (matched by their own current label) are intentionally NOT reported.
//   • extras        — target STANDARD deliverables no row covers (newly added / uncovered), as
//                     { stageKey, index, label } — the drift report's "new in SOW" list.
// anchorStageDelivsNorm: stageKey → normalised anchor standard-deliverable labels (legacy fallback).
// targetWideNorm: stageKey → normalised target labels across standard+optional+custom (a wide net so
//                 a deliverable that merely moved lists still counts as present).
// targetPrimary: stageKey → target standard deliverables [{ label }] (the extras candidates).
function computeSowLinkDiff({ rows, timelineData, anchorStageDelivsNorm, targetWideNorm, targetPrimary }) {
  const orphanedLinks = new Set();
  const orphanedRows = [];
  const claimed = {}; // stageKey → Set of claimed primary-deliverable indices
  const primaryNorm = {};
  Object.keys(targetPrimary || {}).forEach(sk => {
    primaryNorm[sk] = (targetPrimary[sk] || []).map(d => normReconLabel((d && d.label) || ''));
  });
  const td = timelineData || {};
  let stageKey = null;
  (rows || []).forEach(r => {
    if ((r.indent || 0) === 0) { stageKey = r.id; return; }
    if (RECON_EXCLUDED_STAGES.has(stageKey)) return; // no-SOW stage — never reconciled / tinted
    const rd = td[r.id] || {};
    const ref = rd.sowRef;
    const hasSowRef = ref != null && ref !== '' && String(ref) !== SOW_STAGE_REF;
    let fps;
    if (hasSowRef) {
      fps = resolveLinkFingerprint(rd, stageKey, anchorStageDelivsNorm);
    } else if (r.id.startsWith('deliv_') && String(ref) !== SOW_STAGE_REF && rd._sowUnlinked !== true) {
      // Structural deliverable row with no explicit link — its own label is its fingerprint.
      const lbl = normReconLabel(rd.taskName || r.defaultLabel || '');
      fps = lbl ? [lbl] : [];
    } else {
      return; // does not represent a SOW deliverable — skip
    }
    if (!fps.length) return; // intent can't be resolved — leave the row alone
    const wide = (targetWideNorm && targetWideNorm[stageKey]) || [];
    const matched = wide.some(tl => fps.some(fp => reconLabelsMatch(fp, tl)));
    if (!matched) {
      // Report ONLY explicit SOW links (a row carrying sowRef + its frozen sowRefLabels fingerprint),
      // and only because the SOW side changed: the fingerprint was captured at link time, so it no
      // longer matches only when the deliverable was removed/renamed in the SOW, or a different SOW was
      // applied/selected. A manual task rename doesn't touch the fingerprint, so it never flags.
      // Structural deliv_ rows (matched by their own CURRENT label) are excluded on purpose — renaming
      // or removing one in the template must not read as SOW drift.
      if (hasSowRef) {
        orphanedLinks.add(r.id); // faint-red tint on the row
        orphanedRows.push({ rowId: r.id, stageKey, label: rd.taskName || r.defaultLabel || '', hasSowRef });
      }
    }
    const pn = primaryNorm[stageKey] || [];
    pn.forEach((tl, i) => {
      if (tl && fps.some(fp => reconLabelsMatch(fp, tl))) {
        (claimed[stageKey] || (claimed[stageKey] = new Set())).add(i);
      }
    });
  });
  const extras = [];
  Object.keys(targetPrimary || {}).forEach(sk => {
    if (RECON_EXCLUDED_STAGES.has(sk)) return; // no-SOW stage — never reports "new in SOW" extras
    const c = claimed[sk] || new Set();
    (targetPrimary[sk] || []).forEach((d, i) => {
      if (!c.has(i)) extras.push({ stageKey: sk, index: i, label: (d && d.label) || '' });
    });
  });
  return { orphanedLinks, orphanedRows, extras };
}

// Architecture-deliverable synonyms so the closest-name matcher treats domain-equivalent words as the
// same term (e.g. "plans" ≈ "layouts"). Compared with reconTokenSim, so plural/verb forms are covered.
const DELIV_SYNONYM_GROUPS = [
  ['plan', 'layout', 'floorplan'],
  ['view', 'render', 'rendering', 'massing', '3d', 'perspective', 'visualisation', 'visualization'],
  ['elevation', 'facade'],
  ['sketch', 'preliminary', 'draft'],
  ['image', 'reference', 'moodboard'],
];
function synGroupOf(token) {
  for (let gi = 0; gi < DELIV_SYNONYM_GROUPS.length; gi++) {
    if (DELIV_SYNONYM_GROUPS[gi].some(m => reconTokenSim(token, m))) return gi;
  }
  return -1;
}
function tokensRelated(a, b) {
  if (reconTokenSim(a, b)) return true;
  const ga = synGroupOf(a);
  return ga !== -1 && ga === synGroupOf(b);
}
// Closest-by-name match: index of the deliverable in `scopeNorms` sharing the most meaningful terms with
// `taskNorm` (synonym-aware), or -1 when nothing shares even one. Looser than reconLabelsMatch.
function bestScopeMatchIndex(taskNorm, scopeNorms) {
  const tTokens = reconMeaningfulTokens(taskNorm);
  if (!tTokens.length) return -1;
  let bestIdx = -1, bestScore = 0, bestRatio = 0;
  (scopeNorms || []).forEach((sn, i) => {
    if (!sn) return;
    const sTokens = reconMeaningfulTokens(sn);
    if (!sTokens.length) return;
    let shared = 0;
    tTokens.forEach(t => { if (sTokens.some(u => tokensRelated(t, u))) shared++; });
    if (shared === 0) return;
    const ratio = shared / Math.max(tTokens.length, sTokens.length);
    if (shared > bestScore || (shared === bestScore && ratio > bestRatio)) { bestScore = shared; bestRatio = ratio; bestIdx = i; }
  });
  return bestScore >= 1 ? bestIdx : -1;
}

// Convert a SOW template (from useScopeTemplates) into a confirmedScope-shaped object
// so ProjectTimelineEditor can use it to auto-build rows.
// Timeline templates seed from the SOW's MAIN (standard) deliverables only — optional
// deliverables are left out (included: false) so a master template stays clean. Optional /
// additional deliverables belong to individual projects: a real project's confirmed scope
// drives buildRowsFromScope with its own per-deliverable `included` flags, so optionals surface
// only for the specific projects whose scope actually selects them.
function sowTemplateToScope(sowTemplate, projectId) {
  if (!sowTemplate) return null;
  return {
    projectId,
    status: 'confirmed',
    isActiveVersion: true,
    stages: (sowTemplate.stages || []).map(s => ({
      key: s.key,
      name: s.name,
      selected: true,
      deliverables: {
        standard: (s.standardDeliverables || []).map(d => ({ ...d, included: true })),
        optional: (s.optionalDeliverables || []).map(d => ({ ...d, included: false })),
        custom: [],
      },
      notesRevisions: s.notesRevisions || 0,
      notesMeetings: s.notesMeetings || 0,
    })),
  };
}

// ── Predecessor / task-number helpers ─────────────────────────────────────────

// Compute task number maps from a row array (non-stage rows only, 1-based).
// Returns { keyToNum: { rowId -> taskNum }, numToKey: { taskNum -> rowId } }
function computeTaskNums(rows) {
  const keyToNum = {};
  const numToKey = {};
  let n = 0;
  rows.forEach(row => {
    if (row.indent > 0 && !row.isSubStageHeader) {
      n++;
      keyToNum[row.id] = n;
      numToKey[n] = row.id;
    }
  });
  return { keyToNum, numToKey };
}

// Build the canonical extended row list used for task numbering and dependency logic.
// Mirrors the on-screen grouped order (independent of the expand/collapse + activeSubStages
// view toggles) so the # column reads chronologically top-to-bottom:
//   stage start_ → unassigned tasks → for each part [header, part start_, part's tasks,
//   part invoicing_, part closure_] → stage invoicing_/closure_.
// All defined parts are always included so numbering is stable regardless of which sub-stages
// the user has expanded. `timeline` provides each task's _subStageId assignment.
function buildExtendedRows(allRows, partStagesMap, timeline) {
  if (!partStagesMap || Object.keys(partStagesMap).length === 0) return allRows;
  const tl = timeline || {};
  const result = [];
  let i = 0;
  while (i < allRows.length) {
    const row = allRows[i];
    if (row.indent !== 0) { result.push(row); i++; continue; }
    result.push(row);
    i++;
    const stageId = row.id;
    const parts = (partStagesMap[stageId] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const children = [];
    while (i < allRows.length && allRows[i].indent > 0) { children.push(allRows[i]); i++; }
    if (parts.length === 0) { children.forEach(c => result.push(c)); continue; }

    const startRows = children.filter(c => c.id.startsWith('start_'));
    const sysRows   = children.filter(c => c.id.startsWith('invoicing_') || c.id.startsWith('closure_'));
    const taskRows  = children.filter(c => !c.id.startsWith('start_') && !c.id.startsWith('invoicing_') && !c.id.startsWith('closure_'));
    const partIds = new Set(parts.map(p => p.id));
    const grouped = {};
    parts.forEach(p => { grouped[p.id] = []; });
    const unassigned = [];
    taskRows.forEach(c => {
      const sid = tl[c.id]?._subStageId || null;
      if (sid && partIds.has(sid)) grouped[sid].push(c); else unassigned.push(c);
    });

    startRows.forEach(c => result.push(c));   // main stage start
    unassigned.forEach(c => result.push(c));
    const partSystem = !isAdvanceStage(stageId);
    parts.forEach(p => {
      const subKey = `substage_${stageId}_${p.id}`;
      const label = p.name || p.id;
      result.push({ id: subKey, indent: 1, isSubStageHeader: true, stageId, partId: p.id });
      if (partSystem) result.push({ id: `start_${subKey}`, indent: 1, isPartSystem: true, stageId, partId: p.id, defaultLabel: `Start of ${label} Stage` });
      grouped[p.id].forEach(c => result.push(c));
      if (!partSystem) return;
      result.push({ id: `invoicing_${subKey}`, indent: 1, isPartSystem: true, stageId, partId: p.id, defaultLabel: `Invoicing of ${label} Stage` });
      result.push({ id: `closure_${subKey}`,   indent: 1, isPartSystem: true, stageId, partId: p.id, defaultLabel: `Closure of ${label} Stage` });
    });
    sysRows.forEach(c => result.push(c));     // main stage invoicing/closure
  }
  return result;
}

// Legacy ordering used ONLY to reconstruct the baseline numbering for projects that were
// migrated by the earlier `_partDepsMigrated` pass (which appended ungrouped part rows
// [start, invoicing, closure] after the stage's tasks, with no headers and no task grouping).
// Needed so the idempotent reconciliation remaps those projects from the correct baseline.
function buildLegacyExtendedRows(allRows, partStagesMap) {
  if (!partStagesMap || Object.keys(partStagesMap).length === 0) return allRows;
  const result = [];
  let i = 0;
  while (i < allRows.length) {
    const row = allRows[i];
    if (row.indent !== 0) { result.push(row); i++; continue; }
    result.push(row);
    i++;
    const stageId = row.id;
    const parts = (partStagesMap[stageId] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const children = [];
    while (i < allRows.length && allRows[i].indent > 0) { children.push(allRows[i]); i++; }
    if (parts.length === 0) { children.forEach(c => result.push(c)); continue; }
    const sysRows = children.filter(c => c.id.startsWith('invoicing_') || c.id.startsWith('closure_'));
    const nonSys  = children.filter(c => !c.id.startsWith('invoicing_') && !c.id.startsWith('closure_'));
    nonSys.forEach(c => result.push(c));
    parts.forEach(p => {
      const subKey = `substage_${stageId}_${p.id}`;
      result.push({ id: `start_${subKey}`,     indent: 1 });
      result.push({ id: `invoicing_${subKey}`, indent: 1 });
      result.push({ id: `closure_${subKey}`,   indent: 1 });
    });
    sysRows.forEach(c => result.push(c));
  }
  return result;
}

// Part stages per stage key, read off a timeline (the same map the editor builds).
function partStagesMapOf(tl) {
  const map = {};
  Object.entries(tl || {}).forEach(([key, val]) => {
    if (val && Array.isArray(val.partStages) && val.partStages.length > 0) map[key] = val.partStages;
  });
  return map;
}

// The numbering a timeline's stored predecessors are expressed in: its `_depNumKey`, else the same
// fallbacks the editor's reconciliation uses (legacy part rows, then the plain row count).
function storedDepNumToKey(tl, rows) {
  if (tl && tl._depNumKey) return tl._depNumKey;
  if (tl && tl._partDepsMigrated) return computeTaskNums(buildLegacyExtendedRows(rows, partStagesMapOf(tl))).numToKey;
  return computeTaskNums(rows).numToKey;
}

// Remap predecessor field values in the timeline after row order changes.
// oldNumToKey: before-change { taskNum -> rowId }
// newKeyToNum: after-change  { rowId -> taskNum }
// Rows that were deleted or became stages get their predecessor references removed.
function remapPredecessorValues(timelineData, rows, oldNumToKey, newKeyToNum) {
  const predKey = 'predecessors';
  const updated = { ...timelineData };
  rows.forEach(row => {
    const rd = timelineData[row.id];
    if (!rd || !rd[predKey]) return;
    const parsed = parsePredecessors(rd[predKey]);
    if (!parsed.length) return;
    const remapped = parsed.map(p => {
      const rowId = oldNumToKey[p.rowNumber];
      if (!rowId) return null;
      const newNum = newKeyToNum[rowId];
      if (!newNum) return null;
      return { rowNumber: newNum, type: p.type, lag: p.lag };
    }).filter(Boolean);
    const newStr = formatPredecessors(remapped);
    if (newStr !== rd[predKey]) {
      updated[row.id] = { ...rd, [predKey]: newStr };
    }
  });
  return updated;
}

// Check if a row at given index has children (any subsequent rows with higher indent)
function rowHasChildren(rows, rowIndex) {
  if (rowIndex >= rows.length - 1) return false;
  return rows[rowIndex + 1].indent > rows[rowIndex].indent;
}

// Get all descendant indices for a row (all subsequent rows with higher indent until we hit same or lower)
function getDescendantIndices(rows, rowIndex) {
  const parentIndent = rows[rowIndex].indent;
  const indices = [];
  for (let i = rowIndex + 1; i < rows.length; i++) {
    if (rows[i].indent <= parentIndent) break;
    indices.push(i);
  }
  return indices;
}

// Generate unique row ID for custom/deliverable rows
function generateRowId() {
  return 'row_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

function generateCustomStageId() {
  return 'custom_stage_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

function isCustomStage(rowId) {
  return typeof rowId === 'string' && rowId.startsWith('custom_stage_');
}

// The Advance stage is paid up front, so it (and every part under it) has no Start / Invoicing /
// Closure rows, and the stage after it starts with no preset dependency on it.
const ADVANCE_STAGE_KEY = 'advance';
function isAdvanceStage(stageId) {
  return stageId === ADVANCE_STAGE_KEY;
}
// Whether `id` is a Start / Invoicing / Closure row of the Advance stage or one of its parts.
function isAdvanceSystemRow(id) {
  if (typeof id !== 'string') return false;
  const m = id.match(/^(start|invoicing|closure)_(.*)$/);
  if (!m) return false;
  return m[2] === ADVANCE_STAGE_KEY || m[2].startsWith(`substage_${ADVANCE_STAGE_KEY}_`);
}

// A Start row's manual stage link, or null when it points at the row's own stage or one of that stage's
// parts (the row's own dates hang off that link, so it could never yield a date). Such a link falls back
// to the default, the stage just above.
function startRowStageOf(startRowId) {
  const key = String(startRowId).slice('start_'.length);
  return key.startsWith('substage_') ? key.slice('substage_'.length).replace(/_[^_]+$/, '') : key;
}
function isOwnStageLink(startRowId, linked) {
  if (!linked) return false;
  const own = startRowStageOf(startRowId);
  return linked === own || linked.startsWith(`substage_${own}_`);
}
function manualStartLink(startRowId, rd) {
  const linked = rd?._manualStartLinkedStage || null;
  return linked && !isOwnStageLink(startRowId, linked) ? linked : null;
}

// Projects whose timeline stands on its own: no scope, no Tracking / Invoices link. Every stage is a
// custom stage (freely renamed), there are no Start / Invoicing / Closure rows and no contract dates,
// and nothing syncs to Tracking. Stages the Tracking page carries for the project stay stored there
// but are not shown here. Keyed by project code.
const STANDALONE_TIMELINE_CODES = new Set(['BRM']);
function isStandaloneTimeline(project) {
  return !!project && STANDALONE_TIMELINE_CODES.has(String(project.projectCode || '').toUpperCase());
}
// What a standalone timeline shows before anything is saved: one stage and one blank task. Fixed ids,
// so an edit made before the rows are first saved lands on the same rows the next render builds.
const STANDALONE_SEED_ROWS = [
  { id: 'custom_stage_standalone', indent: 0, defaultLabel: 'New stage' },
  { id: 'row_standalone_1', indent: 1 },
];

// ── BRM templates ───────────────────────────────────────────────────────────────────────────────
// BRM keeps its own template list, separate from the project templates: app_settings key
// 'brm_timeline_templates', written by can_edit_brm_templates() (admins, supervisors, a named
// allow-list) and applied by anyone who can edit BRM. Each entry is { id, name, timeline } where
// timeline holds _rows and the row data only: the column layout comes from the BRM page itself.
const BRM_TEMPLATES_KEY = 'brm_timeline_templates';
const BRM_TEMPLATE_PROJECT_ID = 'brm-tpl-edit';
// Page level keys a template never carries (the BRM page supplies its own).
const BRM_TEMPLATE_LAYOUT_KEYS = ['_viewSettings', '_customColumns', '_assignedToCategories', '_tabs', '_mainTabName',
  '_grantedAccess', '_settingsPresets', '_activePresetId', '_syncAutoOff', '_rowCreators', '_settings', '_colFmt'];
// Calendar and progress fields a template drops: it is a plan of stages, tasks, durations and
// dependencies, dated when it is applied.
const BRM_TEMPLATE_DROP_FIELDS = ['plannedStartDate', 'plannedEndDate', 'percentComplete', 'scheduleStatus',
  'actualDate', 'targetDate', 'contractDate', 'contractStartDate', 'contractEndDate', 'contractDuration',
  'invoiceDate', 'paidDate', 'delayCaused'];

// A template's stored timeline from an editor timeline: given rows only (a whole page, or one tab's
// stages), layout keys and dates removed, dependencies numbered over those rows.
function brmTemplateTimelineFrom(tl, rows) {
  const src = tl || {};
  const out = { _rows: rows.map(r => ({ id: r.id, indent: r.indent || 0, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) })) };
  const ids = new Set(out._rows.map(r => r.id));
  Object.entries(src).forEach(([k, v]) => {
    if (!ids.has(k) || !v || typeof v !== 'object') return;
    const copy = { ...v };
    BRM_TEMPLATE_DROP_FIELDS.forEach(f => { delete copy[f]; });
    out[k] = copy;
  });
  // Dependencies point at the page's numbering; renumber them over the template's own rows.
  const fullRows = Array.isArray(src._rows) ? src._rows : out._rows;
  const oldNumToKey = storedDepNumToKey(src, buildExtendedRows(fullRows, partStagesMapOf(src), src));
  const { keyToNum, numToKey } = computeTaskNums(buildExtendedRows(out._rows, partStagesMapOf(out), out));
  const remapped = remapPredecessorValues(out, out._rows, oldNumToKey, keyToNum);
  remapped._depNumKey = numToKey;
  return remapped;
}

// Add a BRM template's stages and tasks to one tab of the BRM timeline: new ids, placed after the
// tab's last stage (the end of the page when the tab is empty), the new stages listed in the tab,
// and every dependency renumbered, the template's own and the page's (rows after the insert move).
function applyBrmTemplateToTab(template, projectTimeline, tabId) {
  const tl = projectTimeline || {};
  const tplTl = (template && template.timeline) || {};
  const tplRows = (Array.isArray(tplTl._rows) ? tplTl._rows : []).filter(r => r && r.id);
  if (!tplRows.length) return null;
  const baseRows = (Array.isArray(tl._rows) && tl._rows.length ? tl._rows : STANDALONE_SEED_ROWS)
    .map(r => ({ id: r.id, indent: r.indent || 0, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));
  const idMap = {};
  tplRows.forEach(r => { idMap[r.id] = (r.indent || 0) === 0 ? generateCustomStageId() : generateRowId(); });
  const newRows = tplRows.map(r => ({ id: idMap[r.id], indent: r.indent || 0, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));

  const inTab = new Set(standaloneTabView({ ...tl, _rows: baseRows }, tabId).keys);
  let insertAt = baseRows.length;
  let lastStage = -1;
  baseRows.forEach((r, i) => { if (r.indent === 0 && inTab.has(r.id)) lastStage = i; });
  if (lastStage >= 0) {
    insertAt = lastStage + 1;
    while (insertAt < baseRows.length && baseRows[insertAt].indent > 0) insertAt++;
  }
  const finalRows = [...baseRows.slice(0, insertAt), ...newRows, ...baseRows.slice(insertAt)];

  const nt = { ...tl, _rows: finalRows };
  tplRows.forEach(r => {
    const d = tplTl[r.id];
    if (!d || typeof d !== 'object') return;
    const copy = { ...d };
    BRM_TEMPLATE_DROP_FIELDS.forEach(f => { delete copy[f]; });
    nt[idMap[r.id]] = copy;
  });
  const newStageIds = newRows.filter(r => r.indent === 0).map(r => r.id);
  if (tabId && tabId !== '__all__') {
    nt._tabs = (Array.isArray(tl._tabs) ? tl._tabs : []).map(t => (t.id === tabId ? { ...t, keys: [...(t.keys || []), ...newStageIds] } : t));
  }

  const pageOldNumToKey = storedDepNumToKey(tl, buildExtendedRows(baseRows, partStagesMapOf(tl), tl));
  const tplNumToKey = {};
  Object.entries(storedDepNumToKey(tplTl, buildExtendedRows(tplRows, partStagesMapOf(tplTl), tplTl)))
    .forEach(([n, k]) => { if (idMap[k]) tplNumToKey[n] = idMap[k]; });
  const { keyToNum, numToKey } = computeTaskNums(buildExtendedRows(finalRows, partStagesMapOf(nt), nt));
  let out = remapPredecessorValues(nt, baseRows, pageOldNumToKey, keyToNum);
  out = remapPredecessorValues(out, newRows, tplNumToKey, keyToNum);
  out._depNumKey = numToKey;
  return { timeline: out, stageCount: newStageIds.length };
}

// Apply a timeline template to a project, filtered to the project's SOW-selected stages, as a 3-WAY
// MERGE against the project's own confirmed scope:
//   1. KEEP the template structure verbatim — stages/deliverables/tasks, names, order, sub-task
//      nesting, dependencies, durations, meetings/revisions. Only stages the project's scope hasn't
//      selected are dropped. (No confirmed scope → the whole template is kept, no flagging/merge.)
//   2. FLAG UNLINKED — a template deliverable row whose deliverable is NOT in this project's SOW is
//      kept (structure preserved) but marked `_sowUnlinked` so the editor shows it as unbacked.
//   3. ADD EXTRAS — a project-SOW deliverable no template row covers is appended as a `deliv_<id>`
//      row within its stage (before invoicing/closure), so the project's real scope is fully present.
// Predecessors are remapped (dropping/adding rows renumbers tasks). The project's tracking / invoicing
// data, custom-column values and layout config are preserved on rows present in both. `_appliedTemplateId`
// records the template — the editor renders an applied template's rows verbatim.
// `anchorSow` (optional) is the template's own base SOW, used to resolve legacy explicit links that
// carry no label fingerprint. Returns { timeline, unlinkedCount, addedCount } for the apply summary.
// A template with no SOW of its own (no sowTemplateId) skips steps 2 and 3 and every SOW link: only its
// own rows come in, still limited to the project's selected stages.
function applyTemplateToProject(template, projectTimeline, confirmedScope, anchorSow = null, preserveTasks = false, excludeDelivRowIds = null) {
  const tplTl = template.stageTimeline || {};
  const tplRows = Array.isArray(tplTl._rows) ? tplTl._rows : [];
  const PRESERVE = ['contractDate', 'targetDate', 'actualDate', 'clientApprovalDate', 'invoiceDate', 'paidDate', 'invoicePercent', 'billing', 'partStages'];
  const KEEP_META = ['_viewSettings', '_customColumns', '_settingsPresets', '_activePresetId', '_grantedAccess', '_tabs', '_mainTabName', '_colFmt'];

  // Stages to keep = the SOW-selected stages (timeline keys). null → keep every template stage.
  const keepStages = (confirmedScope && Array.isArray(confirmedScope.stages))
    ? new Set(confirmedScope.stages.filter(s => s.selected).map(s => scopeKeyToTimelineKey(s.key)))
    : null;

  // Filter the template's rows to the kept stages — each stage row plus all its children stays or goes
  // as a block. Everything within a kept stage is preserved verbatim (order + indent = sub-tasks).
  const keptRows = [];
  let keepCur = true;
  tplRows.forEach(r => {
    if ((r.indent || 0) === 0) { keepCur = !keepStages || keepStages.has(r.id); if (keepCur) keptRows.push(r); }
    else if (keepCur) keptRows.push(r);
  });
  const keptIds = new Set(keptRows.map(r => r.id));

  // Project scope deliverables per selected stage: timelineKey → [{ id, label }] (standard+optional
  // included + custom), plus a normalised-label view for matching, plus the stage's normalised
  // matching (each deliverable's id + normalised label). Empty when there's no scope.
  const scopeDelivsByStage = {};
  const scopeNormByStage = {};
  (confirmedScope && Array.isArray(confirmedScope.stages) ? confirmedScope.stages : []).forEach(s => {
    if (!s.selected) return;
    const tk = scopeKeyToTimelineKey(s.key);
    const dv = s.deliverables || {};
    const list = [
      ...((dv.standard || []).filter(x => x.included)),
      ...((dv.optional || []).filter(x => x.included)),
      ...(dv.custom || []),
    ].map(x => ({ id: x.id, label: x.label || '' }));
    scopeDelivsByStage[tk] = list;
    scopeNormByStage[tk] = list.map(x => normReconLabel(x.label));
  });
  // No reconciliation against the project's SOW for a template that has no SOW linked: nothing is
  // linked, flagged or appended.
  const hasScope = !!template.sowTemplateId && Object.keys(scopeNormByStage).length > 0;
  // Anchor SOW standard labels (legacy explicit-link fingerprint fallback).
  const anchorNorm = {};
  (anchorSow?.stages || []).forEach(s => {
    anchorNorm[scopeKeyToTimelineKey(s.key)] = (s.standardDeliverables || []).map(d => normReconLabel(d?.label || ''));
  });

  // Reconcile each template row against THIS project's (possibly different) SOW. WHICH rows are linked
  // comes from the master; WHICH deliverable(s) each links to is resolved against THIS project's SOW.
  //
  // A master numbered link's sowRef indexes the master's OWN base SOW, so the NUMBER doesn't transfer to a
  // project whose SOW is re-ordered / re-split. The base-SOW deliverable's TEXT does. So each ref number is
  // resolved through the base SOW to its label (resolveLinkFingerprint — captured fingerprint, else the
  // anchor SOW), then that exact deliverable is found in THIS project (strong match). This reproduces the
  // master's intent, including MULTI-links (e.g. base 3,6 → this SOW's 3,4). Priority per row:
  //   (1) ANCHOR — base-SOW label(s) → the same deliverable(s) in this SOW (strong match; multi-link).
  //   (2) MISLINK override — a single-ref row whose own NAME strongly matches a DIFFERENT deliverable that
  //       the base label neither covers nor relates to is a stale master grouping; trust the row's name.
  //   (3) a deliv_ row's embedded scope id (exact, for a project that shares the base SOW).
  //   (4) the row's closest deliverable by NAME (synonym-aware fuzzy).
  //   (5) the master's own number, kept if still in range; otherwise the row is left unlinked.
  // A master-UNLINKED row is linked ONLY when it's a leaf task whose NAME strongly matches a deliverable
  // (so a project deliverable the master's rows spell out by a different name — e.g. "Facade & fenestration
  // details" — is still claimed, not re-added as a new row); a weak/fuzzy name is never enough.
  // A master stage-link (sowRef 'stage') is preserved verbatim. A structural deliv_ row the master left
  // UNNUMBERED is a stage-level activity/meeting (e.g. "Programming meetings") → linked to the STAGE
  // (highlighted, no number). Uncovered project deliverables are appended as "new" tasks. Stages with no
  // SOW deliverables (RECON_EXCLUDED_STAGES) are skipped — master rows are kept verbatim.
  const coveredByStage = {}; // timelineKey → Set of covered scope-deliverable indices
  const unlinkedRowIds = new Set();
  const stageLinkRowIds = new Set(); // structural deliv_ rows the master didn't number → linked to the stage
  const linkByRow = {};      // rowId → { sowRef, labels } — deliverable link, numbered to the project SOW
  // (Master stage-links — sowRef 'stage' — are preserved by the verbatim per-row copy below, not here.)
  // Leaf = no deeper-indent row immediately follows (a parent/header row is not a leaf).
  const isLeaf = keptRows.map((r, i) => {
    const next = keptRows[i + 1];
    return !next || (next.indent || 0) <= (r.indent || 0);
  });
  {
    let stageKey = null;
    keptRows.forEach((r, rowI) => {
      if ((r.indent || 0) === 0) { stageKey = r.id; return; }
      if (!hasScope || !(stageKey in scopeNormByStage)) return;
      if (RECON_EXCLUDED_STAGES.has(stageKey)) return; // no-SOW stage — master rows kept verbatim
      if (r.id.startsWith('start_') || r.id.startsWith('invoicing_') || r.id.startsWith('closure_')) return;
      // Process / dropped-deliverable row — never SOW-linked; strip any master link it carried.
      if (RECON_NO_LINK_ROWS.has(r.id)) { unlinkedRowIds.add(r.id); return; }
      const rd = tplTl[r.id] || {};
      const ref = rd.sowRef;
      if (String(ref) === SOW_STAGE_REF) return; // master stage-link — preserved verbatim
      const hasRef = ref != null && ref !== '';
      const isDeliv = r.id.startsWith('deliv_') && rd._sowUnlinked !== true;
      // A structural deliv_ row the master did NOT number is a stage-level activity/meeting (it lives in
      // the stage's description text, not a numbered deliverable). Link it to the STAGE — highlighted, no
      // number — and let it claim no deliverable, so the real deliverables still surface with their numbers.
      if (isDeliv && !hasRef) { stageLinkRowIds.add(r.id); return; }
      const scopeNorm = scopeNormByStage[stageKey] || [];
      const scopeList = scopeDelivsByStage[stageKey] || [];
      const cover = (k) => (coveredByStage[stageKey] || (coveredByStage[stageKey] = new Set())).add(k);
      const setLinks = (idxs) => {
        idxs.forEach(cover);
        linkByRow[r.id] = { sowRef: idxs.map(k => k + 1).join(','), labels: idxs.map(k => (scopeList[k] || {}).label).filter(Boolean) };
      };
      const taskNorm = normReconLabel(rd.taskName || r.defaultLabel || '');
      const strongName = taskNorm ? scopeNorm.findIndex(sl => sl && reconLabelsMatch(taskNorm, sl)) : -1;

      if (!hasRef) {
        // Master-UNLINKED row: link only a leaf task whose NAME strongly matches a deliverable.
        if (isLeaf[rowI] && strongName >= 0) setLinks([strongName]);
        return;
      }

      // NUMBERED master link → resolve each ref through the base SOW to its deliverable, then find the
      // SAME deliverable in this project (strong match). Reproduces the master's intent + multi-links.
      const refs = String(ref).split(',').map(x => parseInt(x.trim(), 10)).filter(n => n > 0);
      const fps = resolveLinkFingerprint(rd, stageKey, anchorNorm); // base-SOW / fingerprint label(s)
      const anchorIdxs = [];
      fps.forEach(fp => {
        const ci = scopeNorm.findIndex(sl => sl && reconLabelsMatch(fp, sl));
        if (ci >= 0 && !anchorIdxs.includes(ci)) anchorIdxs.push(ci);
      });
      // Mislink override — the row's own name points to a different, more specific deliverable.
      if (refs.length === 1 && strongName >= 0 && !anchorIdxs.includes(strongName)) {
        const bl = fps[0] || '';
        const relatesToBase = bl && (reconLabelsMatch(taskNorm, bl) || bestScopeMatchIndex(taskNorm, [bl]) >= 0);
        if (!relatesToBase) { setLinks([strongName]); return; }
      }
      if (anchorIdxs.length) { setLinks(anchorIdxs.slice().sort((a, b) => a - b)); return; }
      // Anchor couldn't place it → a deliv_ row's embedded scope id, then the closest deliverable by name,
      // then the master's own number if in range, else leave unlinked.
      if (isDeliv) {
        const di = scopeList.findIndex(d => String(d.id) === r.id.slice('deliv_'.length));
        if (di >= 0) { setLinks([di]); return; }
      }
      const fuzzyName = taskNorm ? bestScopeMatchIndex(taskNorm, scopeNorm) : -1;
      if (fuzzyName >= 0) { setLinks([fuzzyName]); return; }
      const valid = refs.filter(n => n <= scopeNorm.length);
      if (valid.length) setLinks(valid.map(n => n - 1)); else unlinkedRowIds.add(r.id);
    });
  }

  // Normalised names of the kept leaf tasks per stage — an uncovered deliverable whose label matches an
  // existing task's name is NOT re-appended as a "new" row (that task already represents it), which
  // prevents duplicates like a "new" deliverable being added next to an identically-named task.
  const keptTaskNormByStage = {};
  { let sk = null;
    keptRows.forEach((r, i) => {
      if ((r.indent || 0) === 0) { sk = r.id; return; }
      if (!isLeaf[i]) return;
      if (r.id.startsWith('start_') || r.id.startsWith('invoicing_') || r.id.startsWith('closure_')) return;
      const rd = tplTl[r.id] || {};
      const n = normReconLabel(rd.taskName || r.defaultLabel || '');
      if (n) (keptTaskNormByStage[sk] || (keptTaskNormByStage[sk] = [])).push(n);
    });
  }

  // Build the final row list = kept template rows + uncovered project deliverables appended within
  // their stage (before that stage's invoicing/closure rows), as deliv_<scopeDelivId> rows.
  const addedRowIds = [];
  const addedRows = [];     // [{ id, label, stageKey }] for the apply summary's per-deliverable list
  const addedLabelById = {};
  const addedLinkById = {}; // rowId → { sowRef, label } for an appended "new" deliverable row
  const preservedData = {}; // rowId → per-row data for a preserved (non-SOW) task, deps stripped
  let finalRows = [];
  {
    let i = 0;
    while (i < keptRows.length) {
      const row = keptRows[i];
      if ((row.indent || 0) !== 0) { finalRows.push(row); i++; continue; }
      const stageKey = row.id;
      finalRows.push(row); i++;
      const children = [];
      while (i < keptRows.length && (keptRows[i].indent || 0) > 0) { children.push(keptRows[i]); i++; }
      let insertIdx = children.length;
      for (let k = 0; k < children.length; k++) {
        if (children[k].id.startsWith('invoicing_') || children[k].id.startsWith('closure_')) { insertIdx = k; break; }
      }
      const extras = [];
      if (hasScope && !RECON_EXCLUDED_STAGES.has(stageKey)) {
        const covered = coveredByStage[stageKey] || new Set();
        (scopeDelivsByStage[stageKey] || []).forEach((d, idx) => {
          if (covered.has(idx)) return;
          const rid = 'deliv_' + d.id;
          if (excludeDelivRowIds && excludeDelivRowIds.has(rid)) return; // removed via the apply summary ✕
          if (keptIds.has(rid) || addedLabelById[rid] !== undefined) return; // no dup
          // Skip when an existing task already represents this deliverable (by label) — no duplicate row.
          const dNorm = (scopeNormByStage[stageKey] || [])[idx];
          if (dNorm && (keptTaskNormByStage[stageKey] || []).some(tn => reconLabelsMatch(dNorm, tn))) return;
          extras.push({ id: rid, indent: 1, defaultLabel: d.label });
          addedRowIds.push(rid);
          addedRows.push({ id: rid, label: d.label, stageKey });
          addedLabelById[rid] = d.label;
          addedLinkById[rid] = { sowRef: String(idx + 1), label: d.label };
        });
      }
      children.slice(0, insertIdx).forEach(c => finalRows.push(c));
      extras.forEach(c => finalRows.push(c));
      children.slice(insertIdx).forEach(c => finalRows.push(c));
    }
  }

  // "Preserve task list" — for a project that already has a hand-built timeline, keep its own tasks when
  // applying the template: every existing NON-SOW-linked task (i.e. not a grey/scope-highlighted row) is
  // moved under a "Preserved task list" parent placed right below each stage's Start row. Relative
  // indent/outdent and durations are kept; dependencies are dropped (their sequence numbers no longer
  // apply). SOW-linked tasks are left to the template. Spliced BEFORE numbering so predecessors remap.
  if (preserveTasks && projectTimeline && Array.isArray(projectTimeline._rows)) {
    const templateIds = new Set(finalRows.map(r => r.id));
    const isGreyRow = (id, d) => {
      d = d || {};
      if (d.sowRef != null && d.sowRef !== '') return true;      // numbered or stage link
      if (d.sowSource === true) return true;                     // scope-derived deliverable
      return id.startsWith('deliv_') || id.startsWith('meeting_') || id.startsWith('revision_');
    };
    const preservedByStage = {}; // stageKey → [{ id, indent, defaultLabel }]
    { let sk = null;
      projectTimeline._rows.forEach(r => {
        if ((r.indent || 0) === 0) { sk = r.id; return; }
        if (!sk) return;
        if (r.id.startsWith('start_') || r.id.startsWith('invoicing_') || r.id.startsWith('closure_')) return;
        if (templateIds.has(r.id)) return;                       // already provided by the template
        if (isGreyRow(r.id, projectTimeline[r.id])) return;      // SOW-linked → template's job
        (preservedByStage[sk] || (preservedByStage[sk] = [])).push({ id: r.id, indent: r.indent || 1, defaultLabel: r.defaultLabel });
      });
    }
    if (Object.keys(preservedByStage).length) {
      const out = [];
      let j = 0;
      while (j < finalRows.length) {
        const row = finalRows[j]; out.push(row); j++;
        if ((row.indent || 0) !== 0) continue;
        const sk = row.id;
        if (j < finalRows.length && finalRows[j].id === `start_${sk}`) { out.push(finalRows[j]); j++; } // keep after Start row
        const preserved = preservedByStage[sk];
        if (!preserved || !preserved.length) continue;
        const minIndent = Math.min(...preserved.map(p => p.indent || 1));
        const off = 2 - minIndent; // shallowest preserved task becomes a direct child of the parent (indent 2)
        const parentId = `row_preserved_${sk}`;
        out.push({ id: parentId, indent: 1 });
        preservedData[parentId] = { taskName: 'Preserved task list' };
        preserved.forEach(p => {
          out.push({ id: p.id, indent: (p.indent || 1) + off, ...(p.defaultLabel ? { defaultLabel: p.defaultLabel } : {}) });
          const od = { ...(projectTimeline[p.id] || {}) };
          delete od.predecessors; delete od.sowRef; delete od.sowRefLabels;
          delete od.sowSource; delete od._sowUnlinked; delete od._sowNew;
          preservedData[p.id] = od;
        });
      }
      finalRows = out;
    }
  }
  // Advance has no Start / Invoicing / Closure rows (a template may still carry them).
  finalRows = finalRows.filter(r => !isAdvanceSystemRow(r.id));
  const finalIds = new Set(finalRows.map(r => r.id));

  const nt = {};
  KEEP_META.forEach(k => { if (projectTimeline[k] !== undefined) nt[k] = projectTimeline[k]; });
  // Carry the template's column setup onto the project: its custom column definitions (+ each select
  // column's category options) and the Assigned-To categories are merged, as a union, with whatever the
  // project already has — so applying a template also brings its columns and dropdown categories, not
  // just the rows. The project's own columns/categories are kept; template-only ones are appended; and
  // for a column present in both, the two option lists are unioned (project order first).
  {
    const projCols = Array.isArray(projectTimeline._customColumns) ? projectTimeline._customColumns : [];
    const tplCols  = Array.isArray(tplTl._customColumns) ? tplTl._customColumns : [];
    const mergedCols = projCols.map(c => ({ ...c, options: Array.isArray(c.options) ? [...c.options] : c.options }));
    const byKey = new Map(mergedCols.map(c => [c.key, c]));
    tplCols.forEach(tc => {
      const existing = byKey.get(tc.key);
      if (!existing) {
        const copy = { ...tc, options: Array.isArray(tc.options) ? [...tc.options] : (tc.options || []) };
        mergedCols.push(copy); byKey.set(tc.key, copy);
      } else {
        const opts = Array.isArray(existing.options) ? [...existing.options] : [];
        (Array.isArray(tc.options) ? tc.options : []).forEach(o => { if (!opts.includes(o)) opts.push(o); });
        existing.options = opts;
        if (!existing.label && tc.label) existing.label = tc.label;
      }
    });
    if (mergedCols.length) nt._customColumns = mergedCols; else delete nt._customColumns;
    // Assigned-To categories — union (project's aren't in KEEP_META, so this also stops them being
    // dropped on every apply).
    const projCats = Array.isArray(projectTimeline._assignedToCategories) ? projectTimeline._assignedToCategories : [];
    const tplCats  = Array.isArray(tplTl._assignedToCategories) ? tplTl._assignedToCategories : [];
    if (projCats.length || tplCats.length) {
      const cats = [...projCats];
      tplCats.forEach(c => { if (!cats.includes(c)) cats.push(c); });
      nt._assignedToCategories = cats;
    }
  }
  nt._rows = finalRows.map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));
  // Copy the template's per-row data (names, durations, dependencies, dates, sowRef, sowRefLabels,
  // partStages, sub-stage assignment) verbatim for the kept rows.
  Object.keys(tplTl).forEach(key => {
    if (key.startsWith('_') || !keptIds.has(key)) return;
    const d = tplTl[key];
    if (d && typeof d === 'object') nt[key] = { ...d };
  });
  // Preserved (non-SOW) task rows carried over from the project's own timeline ("Preserve task list").
  Object.keys(preservedData).forEach(id => { nt[id] = { ...(nt[id] || {}), ...preservedData[id] }; });
  // Re-number every resolved link to the project deliverable position(s) it matched, refreshing the
  // fingerprint to the project's own label(s) so the "(N)" badge and future reconciliation are correct.
  Object.keys(linkByRow).forEach(id => {
    const m = linkByRow[id];
    nt[id] = { ...(nt[id] || {}), sowRef: m.sowRef, _sowUnlinked: false };
    if (m.labels && m.labels.length) nt[id].sowRefLabels = m.labels; else delete nt[id].sowRefLabels;
  });
  // A master deliverable row whose deliverable this project's SOW lacks: drop the stale link so the
  // row reads as a plain (non-SOW) task. `_sowUnlinked` only suppresses the scope-fill highlight now —
  // the old "unlinked" badge is gone, so no stray "(N)" is left behind.
  unlinkedRowIds.forEach(id => {
    nt[id] = { ...(nt[id] || {}), _sowUnlinked: true };
    delete nt[id].sowRef;
    delete nt[id].sowRefLabels;
  });
  // Structural deliv_ rows the master left unnumbered (stage-level activities/meetings) → linked to the
  // stage: highlighted, no "(N)" badge, matching how the master template itself renders them.
  stageLinkRowIds.forEach(id => {
    nt[id] = { ...(nt[id] || {}), sowRef: SOW_STAGE_REF, _sowUnlinked: false };
    delete nt[id].sowRefLabels;
  });
  // Appended project deliverables no existing task covered: flagged `_sowNew` (a review badge that opens
  // Link to SOW) and linked to the deliverable they represent — but with NO dependency / duration.
  addedRowIds.forEach(id => {
    const lk = addedLinkById[id];
    nt[id] = { ...(nt[id] || {}), sowSource: true, _sowUnlinked: false, _sowNew: true };
    if (lk) { nt[id].sowRef = lk.sowRef; nt[id].sowRefLabels = [lk.label]; }
  });
  // Excluded stages (AoC / GFC / CC / Completion): no SOW links, badges, highlights or reconciliation
  // artefacts appear there — even ones the master template itself carried. Strip every link field; and
  // because a structural deliv_/meeting_/revision_ row is scope-highlighted by its id alone, mark it
  // `_sowUnlinked` so that fill is suppressed too. Rows are otherwise kept verbatim (structure, names,
  // dates, dependencies).
  {
    let stageKey = null;
    finalRows.forEach(r => {
      if ((r.indent || 0) === 0) { stageKey = r.id; return; }
      if (!RECON_EXCLUDED_STAGES.has(stageKey)) return;
      const idHighlights = !r.id.startsWith('row_') && !r.id.startsWith('invoicing_')
        && !r.id.startsWith('closure_') && !r.id.startsWith('start_');
      if (!nt[r.id] && !idHighlights) return;
      const d = nt[r.id] || (nt[r.id] = {});
      delete d.sowRef;
      delete d.sowRefLabels;
      delete d._sowNew;
      delete d.sowSource;
      if (idHighlights) d._sowUnlinked = true; else delete d._sowUnlinked;
    });
  }

  // Remap predecessors: template seq → kept row → new seq (dropping/adding rows renumbers the rest).
  // The template's numbers are read in its own stored numbering, and the new ones are counted the way
  // the editor numbers rows: each part's Start / Invoicing / Closure rows included (buildExtendedRows),
  // with the parts the project keeps (its own, else the template's). Counting the plain row list instead
  // left every number past the first part short, and the editor then read them against other rows.
  const tplNumToKey = storedDepNumToKey(tplTl, tplRows);
  const finalPartsMap = {};
  finalRows.forEach(r => {
    if ((r.indent || 0) !== 0) return;
    const parts = projectTimeline[r.id]?.partStages !== undefined ? projectTimeline[r.id].partStages : nt[r.id]?.partStages;
    if (Array.isArray(parts) && parts.length > 0) finalPartsMap[r.id] = parts;
  });
  const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(buildExtendedRows(finalRows, finalPartsMap, nt));
  const bridgeNumToKey = {};
  Object.entries(tplNumToKey).forEach(([num, tplId]) => { if (finalIds.has(tplId)) bridgeNumToKey[num] = tplId; });
  const finalTl = remapPredecessorValues(nt, finalRows, bridgeNumToKey, newKeyToNum);
  finalTl._depNumKey = newNumToKey;

  // Preserve the project's tracking / invoicing data + custom-column values on rows present in both
  // (including appended project deliverables the project already tracked before this apply).
  Object.keys(projectTimeline).forEach(key => {
    if (key.startsWith('_') || !finalIds.has(key)) return;
    const pd = projectTimeline[key];
    if (!pd || typeof pd !== 'object') return;
    const kept = {};
    PRESERVE.forEach(f => { if (pd[f] !== undefined) kept[f] = pd[f]; });
    Object.keys(pd).forEach(f => { if (f.startsWith('custom_')) kept[f] = pd[f]; });
    if (Object.keys(kept).length) finalTl[key] = { ...(finalTl[key] || {}), ...kept };
  });

  finalTl._appliedTemplateId = template.id || null;
  return { timeline: finalTl, unlinkedCount: unlinkedRowIds.size, addedCount: addedRowIds.length, addedRows };
}

// Group an ordered _rows array into stage blocks: each stage row (indent 0) starts a block that runs
// until the next stage row. Returns the stage-key order and a Map of stageKey → [rows in that block].
function groupRowsByStage(rows) {
  const order = [];
  const blocks = new Map();
  let cur = null;
  (rows || []).forEach(r => {
    if ((r.indent || 0) === 0) { cur = r.id; order.push(cur); blocks.set(cur, [r]); }
    else if (cur) { blocks.get(cur).push(r); }
  });
  return { order, blocks };
}

// Apply a timeline template to the project's stages, never touching anything outside them. Stages the
// user picked get the template's content; every other stage keeps the project's existing rows/data
// untouched — including custom stages and any SOW stage the template has nothing for, which keep their
// place in the order. `pickedStageKeys` null means "every stage the template brings". Implemented as a
// merge on top of the full apply so all of applyTemplateToProject's reconciliation is reused: run the
// full apply, then for each stage outside the pick swap the template block back out for the project's
// own block. Predecessors are re-numbered in two passes (template-sourced rows use the full-apply
// numbering, project-sourced rows use the project's own) so cross-row dependency references stay
// correct in the merged order.
export function applyTemplateToSelectedStages(template, projectTimeline, confirmedScope, pickedStageKeys, anchorSow = null, preserveTasks = false, excludeDelivRowIds = null) {
  const full = applyTemplateToProject(template, projectTimeline, confirmedScope, anchorSow, preserveTasks, excludeDelivRowIds);
  const fullTl = full.timeline;
  const fullRows = Array.isArray(fullTl._rows) ? fullTl._rows : [];
  const projRows = Array.isArray(projectTimeline._rows) ? projectTimeline._rows : [];
  if (projRows.length === 0) return full; // nothing to preserve — behave like a full apply

  const { order: fullOrder, blocks: fullBlocks } = groupRowsByStage(fullRows);
  const { order: projOrder, blocks: projBlocks } = groupRowsByStage(projRows);
  const picked = pickedStageKeys
    ? (pickedStageKeys instanceof Set ? pickedStageKeys : new Set(pickedStageKeys))
    : new Set(fullOrder);

  const fullNumToKey = storedDepNumToKey(fullTl, fullRows);
  const projNumToKey = storedDepNumToKey(projectTimeline, projRows);

  // Stage order: the template's, with every project stage the apply does not replace (custom stages,
  // stages the template lacks) slotted back in right after the project stage it followed.
  const fromTemplate = (sk) => fullBlocks.has(sk) && (picked.has(sk) || !projBlocks.has(sk));
  const order = fullOrder.filter(sk => fullBlocks.has(sk));
  const projectOnly = projOrder.filter(sk => !fullBlocks.has(sk));
  let anchor = null;
  projOrder.forEach(sk => {
    if (projectOnly.includes(sk)) {
      const at = anchor ? order.indexOf(anchor) + 1 : 0;
      order.splice(at, 0, sk);
      anchor = sk;
    } else if (order.includes(sk)) {
      anchor = sk;
    }
  });

  const mergedRows = [];
  const pickedRowIds = new Set();
  const projRowIds = new Set();
  order.forEach(sk => {
    if (fromTemplate(sk)) {
      (fullBlocks.get(sk) || []).forEach(r => { mergedRows.push(r); pickedRowIds.add(r.id); });
    } else {
      (projBlocks.get(sk) || []).forEach(r => {
        if (isAdvanceSystemRow(r.id)) return; // Advance has no Start / Invoicing / Closure rows
        mergedRows.push(r); projRowIds.add(r.id);
      });
    }
  });

  // Timeline-level settings: the full apply's (it merges columns and categories), then anything else
  // the project had (_rowCreators, _syncPrefs, ...) so a kept stage keeps its bookkeeping.
  const mergedTl = {};
  Object.keys(fullTl).forEach(k => { if (k.startsWith('_') && k !== '_rows' && k !== '_depNumKey') mergedTl[k] = fullTl[k]; });
  Object.keys(projectTimeline).forEach(k => {
    if (k.startsWith('_') && k !== '_rows' && k !== '_depNumKey' && mergedTl[k] === undefined) mergedTl[k] = projectTimeline[k];
  });
  mergedTl._rows = mergedRows.map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));
  mergedRows.forEach(r => {
    const src = pickedRowIds.has(r.id) ? fullTl : projectTimeline;
    const d = src[r.id];
    if (d && typeof d === 'object') mergedTl[r.id] = { ...d };
  });
  // Stage-level data (custom stage names, tracking, part stages) lives under the stage key, not a row.
  order.forEach(sk => {
    if (fromTemplate(sk)) return;
    const d = projectTimeline[sk];
    if (d && typeof d === 'object') mergedTl[sk] = { ...d };
  });

  // Numbered as the editor numbers rows (part rows included), so the stored numbers read right on screen.
  const mergedNums = computeTaskNums(buildExtendedRows(mergedRows, partStagesMapOf(mergedTl), mergedTl));
  const mergedKeyToNum = mergedNums.keyToNum;
  const pickedSubset = mergedRows.filter(r => pickedRowIds.has(r.id));
  const projSubset = mergedRows.filter(r => projRowIds.has(r.id));
  let remapped = remapPredecessorValues(mergedTl, pickedSubset, fullNumToKey, mergedKeyToNum);
  remapped = remapPredecessorValues(remapped, projSubset, projNumToKey, mergedKeyToNum);
  remapped._depNumKey = mergedNums.numToKey;

  const addedRows = (full.addedRows || []).filter(a => picked.has(a.stageKey));
  return { timeline: remapped, unlinkedCount: full.unlinkedCount, addedCount: addedRows.length, addedRows };
}

// ── "Create from" template inheritance ──────────────────────────────────────
// A timeline template can be "created from" a base template (stored as baseTemplateId). The base is
// the source of truth: EVERY authored change to a base deliverable/task row flows one-way into its
// derivatives — row order & indent (reshuffles/reorganisation), predecessors (dependencies),
// duration, assignee, name and SOW link — plus any newly-added base rows, inserted at the base's
// position. A derivative's OWN local rows (ones the base doesn't have) are preserved, re-anchored
// after the shared row they currently follow. Nothing flows back to the base, and base deletions are
// not propagated (a row only in the derivative is treated as a local addition and kept).
// "Deliverable/task" rows are the deliv_* and row_* rows; start_/invoicing_/closure_ rows are
// structural (owned by the app's preset engine) and keep their own data — only their predecessor
// numbers are re-mapped to the merged order.
function isDeliverableRowId(id) {
  return typeof id === 'string' && (id.startsWith('deliv_') || id.startsWith('row_'));
}

// Authored fields carried from the base onto shared / newly-added deliverable rows. Computed dates
// (plannedStartDate/plannedEndDate) are intentionally NOT carried — templates are date-agnostic and
// each derivative recomputes them from the cascade once opened/edited.
const BASE_SYNCED_FIELDS = ['taskName', 'sowRef', 'sowRefLabels', '_sowUnlinked', 'plannedDuration', 'assignedTo', '_subStageId'];

// ── Per-stage / per-column sync control (derivative templates) ───────────────
// A derivative can, per stage, choose which authored COLUMNS the base is allowed to overwrite. The five
// columns below are the only template-authored fields (Start/End dates, Status, %, Delay are per-project
// tracking that a template never stores, so they can't sync). Each column maps to the base-synced
// field(s) it gates. Preferences live on the derivative timeline as `_syncPrefs`:
//     { [stageKey]: { columns: { taskName:false, ... } } }
// A column is ON (base controls it) unless explicitly set false — so the DEFAULT is full auto-sync, and
// unchecking is the only thing that holds the base back. A stage with EVERY column off is frozen: the
// base doesn't touch it at all (fields or structure).
const SYNC_COLUMNS = [
  { key: 'taskName',        label: 'Task Name' },
  { key: 'plannedDuration', label: 'Duration' },
  { key: 'predecessors',    label: 'Dependency' },
  { key: 'assignedTo',      label: 'Assigned To' },
  { key: 'sow',             label: 'SOW Link' },
];
const SYNC_COLUMN_KEYS = SYNC_COLUMNS.map(c => c.key);
// base-synced field -> checklist column key. `_subStageId` has no column (null): it's structural and
// follows the base whenever the stage is active, never individually toggled. Predecessors are gated by
// the 'predecessors' column but handled outside BASE_SYNCED_FIELDS (they're re-numbered, not copied).
const FIELD_TO_SYNC_COLUMN = {
  taskName: 'taskName', plannedDuration: 'plannedDuration', assignedTo: 'assignedTo',
  sowRef: 'sow', sowRefLabels: 'sow', _sowUnlinked: 'sow', _subStageId: null,
};
// A column is ON unless _syncPrefs turns it off (absent prefs/stage/column => ON => sync).
function syncColumnOn(prefs, stageKey, colKey) {
  const p = prefs && prefs[stageKey];
  if (!p || !p.columns) return true;
  return p.columns[colKey] !== false;
}
// A stage syncs at all only when ≥1 column is ON; every column off => the whole stage is frozen.
function syncStageActive(prefs, stageKey) {
  return SYNC_COLUMN_KEYS.some(k => syncColumnOn(prefs, stageKey, k));
}

// After the base→derived merge builds a fully-synced timeline, restore each FROZEN stage (all columns
// off, or a stage outside a restricted single-stage sync) to the derivative's own rows + data, so
// "no columns selected → the entire stage does not update" — structure included. Predecessors are
// re-mapped in two passes: merge-sourced rows keep the merged numbering, derivative-sourced rows use
// the derivative's own numbering, both projected onto the final row order.
function freezeSyncStages(mergedTl, derivedTl, frozenSet) {
  const mergedRows = Array.isArray(mergedTl._rows) ? mergedTl._rows : [];
  const derivedRows = Array.isArray(derivedTl._rows) ? derivedTl._rows : [];
  const { order: mOrder, blocks: mBlocks } = groupRowsByStage(mergedRows);
  const { blocks: dBlocks } = groupRowsByStage(derivedRows);

  const outRows = [];
  const fromDerived = new Set();
  mOrder.forEach(sk => {
    if (frozenSet.has(sk) && dBlocks.has(sk)) {
      (dBlocks.get(sk) || []).forEach(r => { outRows.push(r); fromDerived.add(r.id); });
    } else {
      (mBlocks.get(sk) || []).forEach(r => outRows.push(r));
    }
  });
  // A frozen stage the base deleted is gone from the merge — keep the derivative's block so freeze also
  // protects a whole stage from base deletion.
  dBlocks.forEach((blk, sk) => {
    if (mBlocks.has(sk) || !frozenSet.has(sk)) return;
    blk.forEach(r => { outRows.push(r); fromDerived.add(r.id); });
  });

  const numToIdOf = (rows) => { const m = {}; let n = 0; rows.forEach(r => { if ((r.indent || 0) > 0) m[++n] = r.id; }); return m; };
  const idToNumOf = (rows) => { const m = {}; let n = 0; rows.forEach(r => { if ((r.indent || 0) > 0) m[r.id] = ++n; }); return m; };
  const mergedNumToId = numToIdOf(mergedRows);
  const derivedNumToId = numToIdOf(derivedRows);
  const finalIdToNum = idToNumOf(outRows);

  const out = {};
  for (const k of Object.keys(mergedTl)) if (k.startsWith('_') && k !== '_rows') out[k] = mergedTl[k];
  out._rows = outRows.map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));
  outRows.forEach(r => {
    const useDerived = fromDerived.has(r.id);
    const d = (useDerived ? derivedTl : mergedTl)[r.id];
    if (!d || typeof d !== 'object') return;
    const cur = { ...d };
    if (d.predecessors) {
      const tp = remapPredecessors(d.predecessors, useDerived ? derivedNumToId : mergedNumToId, finalIdToNum);
      if (tp) cur.predecessors = tp; else delete cur.predecessors;
    }
    out[r.id] = cur;
  });
  return out;
}

// Predecessors are stored as MS-Project task NUMBERS (positions among indent>0 rows). A derivative
// with its own local rows numbers rows differently from the base, so predecessor strings can't be
// copied verbatim — each token is resolved to its row via the source numbering and re-emitted with
// that row's number in the merged order. Tokens whose row is absent from the merged template drop.
function remapPredecessors(predStr, srcNumToId, mergedIdToNum) {
  const parsed = parsePredecessors(predStr).map(p => {
    const srcId = srcNumToId[p.rowNumber];
    const newNum = srcId != null ? mergedIdToNum[srcId] : undefined;
    return newNum != null ? { ...p, rowNumber: newNum } : null;
  }).filter(Boolean);
  return formatPredecessors(parsed);
}

// Resolve a predecessor string to a comparable, position-independent shape: each token as
// { id, type, lag } where id is the row it points at (via the given num→id map). Lets us compare a
// derivative's dependencies against the base's even though the two number their rows differently.
function resolvePredKeys(predStr, numToId) {
  return parsePredecessors(predStr)
    .map(p => { const id = numToId[p.rowNumber]; return id ? { id, type: p.type, lag: p.lag } : null; })
    .filter(Boolean);
}

// Snapshot of a base template's inherited state (row order + per-row owned fields, indent, and resolved
// dependency keys), used as the common ancestor for the derived-template 3-way merge and stamped on a
// derivative at create-from time so the very first base edit merges correctly.
function buildBaseSnapshot(baseTl) {
  const baseRows = Array.isArray(baseTl?._rows) ? baseTl._rows : [];
  const baseNumToId = {}; { let n = 0; baseRows.forEach(r => { if ((r.indent || 0) > 0) baseNumToId[++n] = r.id; }); }
  return {
    order: baseRows.map(r => r.id),
    rows: Object.fromEntries(baseRows.map(r => {
      const bd = baseTl[r.id] || {};
      const s = { indent: r.indent || 0, predKeys: resolvePredKeys(bd.predecessors, baseNumToId) };
      for (const f of BASE_SYNCED_FIELDS) { if (bd[f] !== undefined) s[f] = JSON.parse(JSON.stringify(bd[f])); }
      return [r.id, s];
    })),
  };
}

// Merge a base ("Create from") template into ONE derived stageTimeline as a per-task 3-WAY merge, so the
// base is NOT a blunt source-of-truth that overwrites the derivative. The rules:
//   • base ADDS a task  → inserted into the derivative,
//   • base DELETES a task it previously shared → removed from the derivative,
//   • a shared task the user has NOT touched in the derivative takes the base's current version
//     (name / duration / dependency / SOW link / indent), so a base edit to that one task flows in,
//   • a shared task the user HAS edited in the derivative (shuffled, re-indented to a parent/subtask,
//     re-timed, re-linked, or re-dependency'd) keeps the derivative's version — a base edit never
//     clobbers a manual edit,
//   • row ORDER follows the base only when the user hasn't reordered; once the user shuffles, their
//     order is preserved (base adds/deletes still apply).
// The comparison uses `_baseSnapshot` — the base state captured at the last sync (per-row owned fields,
// indent, and resolved dependency keys, plus the base row order). Without a snapshot yet (a legacy
// derivative, or right after switching base) every shared task is treated as user-owned so nothing is
// clobbered on that first sync; the snapshot is then established for subsequent merges.
function mergeBaseDeliverablesIntoDerived(baseTl, derivedTl, opts) {
  const baseRows = Array.isArray(baseTl?._rows) ? baseTl._rows : [];
  const derivedRows = Array.isArray(derivedTl?._rows) ? derivedTl._rows : [];
  if (baseRows.length === 0) return derivedTl; // nothing to inherit from

  // Per-stage / per-column sync prefs on the derivative. `restrictToStages` (a Set of stage keys) limits
  // the sync to those stages — the per-stage Sync button; null = every stage (auto-sync on base save).
  const syncPrefs = (derivedTl && derivedTl._syncPrefs) || {};
  const restrictToStages = (opts && opts.restrictToStages) || null;

  const baseIds = new Set(baseRows.map(r => r.id));
  const derivedIds = new Set(derivedRows.map(r => r.id));
  const baseRowById = new Map(baseRows.map(r => [r.id, r]));
  const derivedRowById = new Map(derivedRows.map(r => [r.id, r]));

  const numToId = (rows) => { const m = {}; let n = 0; rows.forEach(r => { if ((r.indent || 0) > 0) m[++n] = r.id; }); return m; };
  const idToNum = (rows) => { const m = {}; let n = 0; rows.forEach(r => { if ((r.indent || 0) > 0) m[r.id] = ++n; }); return m; };
  const baseNumToId = numToId(baseRows);
  const oldDerivedNumToId = numToId(derivedRows);

  const snap = (derivedTl && typeof derivedTl._baseSnapshot === 'object' && derivedTl._baseSnapshot) || null;
  const snapRows = (snap && snap.rows) || {};
  const snapOrder = (snap && Array.isArray(snap.order)) ? snap.order : null;
  const snapOrderSet = new Set(snapOrder || []);
  const eq = (a, b) => JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);

  // Did the user edit this shared task in the derivative (vs the base state it was last synced with)?
  const isManuallyEdited = (id) => {
    if (!snap) return true;             // no baseline → treat as user's (never clobber)
    const s = snapRows[id];
    if (!s) return true;
    const dd = derivedTl[id] || {};
    for (const f of BASE_SYNCED_FIELDS) { if (!eq(dd[f], s[f])) return true; }
    if ((derivedRowById.get(id)?.indent || 0) !== (s.indent || 0)) return true;
    if (!eq(resolvePredKeys(dd.predecessors, oldDerivedNumToId), s.predKeys || [])) return true;
    return false;
  };
  // A shared task takes the base's version only when the user hasn't edited it; a base-only row (new) is
  // always taken from the base.
  const takeBase = (id) => baseIds.has(id) && (!derivedIds.has(id) || !isManuallyEdited(id));
  const inheritedDeleted = (id) => !!snap && snapOrderSet.has(id) && !baseIds.has(id);

  // ── Row order ──────────────────────────────────────────────────────────────
  // Follow the base order while the user hasn't shuffled (shared-row order still matches the snapshot);
  // once they have, keep the derivative's order. Either branch inserts base additions and drops base
  // deletions.
  const derivedSharedOrder = derivedRows.filter(r => baseIds.has(r.id)).map(r => r.id);
  const snapSharedOrder = snapOrder ? snapOrder.filter(id => baseIds.has(id)) : null;
  const userShuffled = !snap || !snapSharedOrder || JSON.stringify(derivedSharedOrder) !== JSON.stringify(snapSharedOrder);

  let orderedIds;
  if (!userShuffled) {
    // Base order backbone; derivative-only rows re-inserted after the shared row they follow.
    const extrasByAnchor = new Map();
    let lastShared = '';
    for (const r of derivedRows) {
      if (baseIds.has(r.id)) { lastShared = r.id; }
      else if (!inheritedDeleted(r.id)) { if (!extrasByAnchor.has(lastShared)) extrasByAnchor.set(lastShared, []); extrasByAnchor.get(lastShared).push(r.id); }
    }
    orderedIds = [];
    (extrasByAnchor.get('') || []).forEach(id => orderedIds.push(id));
    for (const br of baseRows) { orderedIds.push(br.id); (extrasByAnchor.get(br.id) || []).forEach(id => orderedIds.push(id)); }
  } else {
    // Derivative order backbone (preserve shuffle); base additions inserted at their anchor.
    const newByAnchor = new Map();
    { let anchor = '';
      for (const br of baseRows) {
        if (derivedIds.has(br.id)) { anchor = br.id; continue; }
        if (!newByAnchor.has(anchor)) newByAnchor.set(anchor, []);
        newByAnchor.get(anchor).push(br.id);
      }
    }
    orderedIds = [];
    (newByAnchor.get('') || []).forEach(id => orderedIds.push(id));
    for (const dr of derivedRows) {
      if (inheritedDeleted(dr.id)) continue;
      orderedIds.push(dr.id);
      (newByAnchor.get(dr.id) || []).forEach(id => orderedIds.push(id));
    }
  }

  // Resolve each row's indent + defaultLabel from whichever side owns it.
  const merged = orderedIds.map(id => {
    const useBase = takeBase(id);
    const src = (useBase ? baseRowById.get(id) : derivedRowById.get(id)) || baseRowById.get(id) || derivedRowById.get(id);
    const indent = (src && src.indent) || 0;
    const defaultLabel = (src && src.defaultLabel) || undefined;
    return { id, indent, ...(defaultLabel ? { defaultLabel } : {}) };
  });
  const mergedIdToNum = idToNum(merged);
  // Each merged row's owning stage (walk indent-0), so field copies can consult that stage's sync prefs.
  const stageOfId = {}; { let cs = null; for (const r of merged) { if ((r.indent || 0) === 0) cs = r.id; stageOfId[r.id] = cs; } }

  // ── Row data ───────────────────────────────────────────────────────────────
  const next = {};
  for (const k of Object.keys(derivedTl || {})) { if (k.startsWith('_') && k !== '_rows') next[k] = derivedTl[k]; } // keep derived metadata
  next._rows = merged;
  next._baseSnapshot = buildBaseSnapshot(baseTl);

  for (const r of merged) {
    const id = r.id;
    if (isDeliverableRowId(id)) {
      // Deliverable row — copy each authored field per the stage's sync prefs: the base WINS for a
      // checked column (overwriting a manual edit), the derivative's own value is KEPT for an unchecked
      // one. A brand-new base row has no derivative value to protect, so it takes the base wholesale.
      const st = stageOfId[id];
      const hasBase = baseIds.has(id);
      const isNew = hasBase && !derivedIds.has(id);
      const bd = hasBase ? (baseTl[id] || {}) : {};
      const dd = derivedTl[id] || {};
      const cur = {};
      for (const f of BASE_SYNCED_FIELDS) {
        const col = FIELD_TO_SYNC_COLUMN[f];
        const takeBaseF = hasBase && (isNew || col == null || syncColumnOn(syncPrefs, st, col));
        if (takeBaseF) { if (bd[f] !== undefined) cur[f] = JSON.parse(JSON.stringify(bd[f])); }
        else if (dd[f] !== undefined) cur[f] = JSON.parse(JSON.stringify(dd[f]));
      }
      const predBase = hasBase && (isNew || syncColumnOn(syncPrefs, st, 'predecessors'));
      const tp = predBase
        ? remapPredecessors(bd.predecessors, baseNumToId, mergedIdToNum)
        : remapPredecessors(dd.predecessors, oldDerivedNumToId, mergedIdToNum);
      if (tp) cur.predecessors = tp;
      next[id] = cur;
    } else {
      // Non-deliverable row (stage header / start_ / invoicing_ / closure_ / meeting_ / revision_):
      // structural — unchanged 3-way behaviour (base for untouched, derivative for edited / local).
      const useBase = takeBase(id);
      if (useBase) {
        const bd = baseTl[id] || {};
        const cur = { ...bd };
        const tp = bd.predecessors ? remapPredecessors(bd.predecessors, baseNumToId, mergedIdToNum) : null;
        if (tp) cur.predecessors = tp; else delete cur.predecessors;
        if (Object.keys(cur).length) next[id] = cur;
      } else {
        const dd = derivedTl[id];
        if (dd) {
          const cur = { ...dd };
          if (dd.predecessors) { const tp = remapPredecessors(dd.predecessors, oldDerivedNumToId, mergedIdToNum); if (tp) cur.predecessors = tp; else delete cur.predecessors; }
          next[id] = cur;
        }
      }
    }
  }

  // ── Freeze opted-out stages ──────────────────────────────────────────────────
  // A stage with every column off — or, during a restricted per-stage Sync, any stage that isn't the
  // target — keeps the derivative's own block verbatim: the base doesn't touch its fields or structure.
  const frozen = new Set();
  for (const r of merged) {
    if ((r.indent || 0) !== 0) continue;
    const st = r.id;
    if (!syncStageActive(syncPrefs, st) || (restrictToStages && !restrictToStages.has(st))) frozen.add(st);
  }
  return frozen.size ? freezeSyncStages(next, derivedTl, frozen) : next;
}

// Given a just-saved base template and the full saved-templates array, return a new array with every
// derivative (baseTemplateId === base.id) fully re-synced from the base (order, indent, deps,
// durations, assignees, names, SOW links; local rows preserved). Returns the same array reference
// when there are no derivatives, so callers can skip a redundant write.
function propagateBaseToDerived(baseTpl, savedTemplates, nowIso) {
  if (!baseTpl?.id || !Array.isArray(savedTemplates)) return savedTemplates;
  const baseTl = baseTpl.stageTimeline || {};
  let anyDerived = false;
  const out = savedTemplates.map(d => {
    if (!d || d.id === baseTpl.id || d.baseTemplateId !== baseTpl.id) return d;
    anyDerived = true;
    return { ...d, stageTimeline: mergeBaseDeliverablesIntoDerived(baseTl, d.stageTimeline || {}), updatedAt: nowIso };
  });
  return anyDerived ? out : savedTemplates;
}


function buildRowsFromScope(confirmedScope) {
  const rows = [];
  confirmedScope.stages.filter(s => s.selected).forEach(stage => {
    const timelineKey = scopeKeyToTimelineKey(stage.key);
    const systemRows = !isAdvanceStage(timelineKey);
    rows.push({ id: timelineKey, indent: 0 });
    if (systemRows) rows.push({ id: 'start_' + timelineKey, indent: 1, defaultLabel: `Start of ${stage.name} Stage` });
    const delivs = [
      ...(stage.deliverables.standard || []).filter(d => d.included),
      ...(stage.deliverables.optional || []).filter(d => d.included),
      ...(stage.deliverables.custom || []),
    ];
    const seenDelivIds = new Set();
    delivs.forEach(d => {
      if (seenDelivIds.has(d.id)) return; // skip duplicate deliverable IDs within a stage
      seenDelivIds.add(d.id);
      rows.push({ id: 'deliv_' + d.id, indent: 1, defaultLabel: resolveQuantityTokens(d.label, delivs) });
    });
    // Revision task rows (from notesRevisions count)
    const revCount = parseInt(stage.notesRevisions, 10) || 0;
    for (let i = 1; i <= revCount; i++) {
      rows.push({ id: `revision_${i}_${timelineKey}`, indent: 1, defaultLabel: `Revision ${i}` });
    }
    // Meeting task rows (from notesMeetings count)
    const mtgCount = parseInt(stage.notesMeetings, 10) || 0;
    for (let i = 1; i <= mtgCount; i++) {
      rows.push({ id: `meeting_${i}_${timelineKey}`, indent: 1, defaultLabel: `Meeting ${i}` });
    }
    // Invoicing row is always second-to-last; closure row is always last
    if (!systemRows) return;
    rows.push({ id: 'invoicing_' + timelineKey, indent: 1, defaultLabel: `Invoicing of ${stage.name} Stage` });
    rows.push({ id: 'closure_' + timelineKey, indent: 1, defaultLabel: `Closure of ${stage.name} Stage` });
  });
  return rows;
}

// Ensure every stage group has start_ first, then other children, then invoicing_, then closure_.
// Moves/creates start_, invoicing_, and closure_ rows as needed.
// Also deduplicates — if the input has multiple copies of any of these rows, only one is kept.
function ensureRowOrder(rows) {
  const result = [];
  let i = 0;
  while (i < rows.length) {
    if (rows[i].indent === 0) {
      const stageRow = rows[i];
      const stageId = stageRow.id;
      const stageName = stageRow.name || stageRow.defaultLabel || STAGE_NAMES[stageRow.id] || stageRow.id;
      const startId = 'start_' + stageId;
      const closureId = 'closure_' + stageId;
      const invoicingId = 'invoicing_' + stageId;
      // Collect the whole stage group (stage row + all children)
      let group = [stageRow];
      i++;
      while (i < rows.length && rows[i].indent > 0) {
        group.push(rows[i]);
        i++;
      }
      // Extract ALL closure rows (deduplicate), keep first occurrence
      const closureRows = group.filter(r => r.id === closureId);
      group = group.filter(r => r.id !== closureId);
      // Custom stages don't auto-get system rows — only include if already present
      const closureRow = closureRows[0] || (isCustomStage(stageId) ? null : (() => {
        const label = `Closure of ${stageName} Stage`;
        return { id: closureId, key: closureId, indent: 1, name: label, defaultLabel: label };
      })());
      // Extract ALL invoicing rows (deduplicate), keep first occurrence
      const invoicingRows = group.filter(r => r.id === invoicingId);
      group = group.filter(r => r.id !== invoicingId);
      const invoicingRow = invoicingRows[0] || (isCustomStage(stageId) ? null : (() => {
        const label = `Invoicing of ${stageName} Stage`;
        return { id: invoicingId, key: invoicingId, indent: 1, name: label, defaultLabel: label };
      })());
      // Extract ALL start rows (deduplicate), keep first occurrence
      const startRows = group.filter(r => r.id === startId);
      group = group.filter(r => r.id !== startId);
      const startRow = startRows[0] || (isCustomStage(stageId) ? null : (() => {
        const label = `Start of ${stageName} Stage`;
        return { id: startId, key: startId, indent: 1, name: label, defaultLabel: label };
      })());
      // Order: stage row → start → other children → invoicing → closure. Advance keeps none of the three.
      if (isAdvanceStage(stageId)) result.push(...group);
      else result.push(group[0], ...(startRow ? [startRow] : []), ...group.slice(1), ...(invoicingRow ? [invoicingRow] : []), ...(closureRow ? [closureRow] : []));
    } else {
      result.push(rows[i]);
      i++;
    }
  }
  return result;
}

// Find the parent stage group boundaries for a row index
function getStageGroupBounds(rows, rowIndex) {
  let stageStart = rowIndex;
  // Walk backwards to find the stage row (indent 0) that owns this row
  while (stageStart > 0 && rows[stageStart].indent > 0) stageStart--;
  // If the row itself is indent 0, the group is just its children
  let stageEnd = stageStart + 1;
  while (stageEnd < rows.length && rows[stageEnd].indent > 0) stageEnd++;
  return { stageStart, stageEnd };
}

// ============================================================================
// ADD COLUMN DIALOG
// ============================================================================

function AddColumnDialog({ onAdd, onClose }) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const types = [
    { key: 'text', label: 'Text' },
    { key: 'number', label: 'Duration' },
    { key: 'date', label: 'Date' },
    { key: 'singleSelect', label: 'Single Select' },
    { key: 'multiSelect', label: 'Multi Select' },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', padding: '20px', width: '300px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', fontFamily: 'Raleway, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '14px' }}>Add Column</div>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '11px', color: BRIO_GREY, marginBottom: '4px' }}>Column Name</div>
          <input
            ref={inputRef}
            value={label}
            onChange={e => setLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && label.trim()) onAdd(label, type); if (e.key === 'Escape') onClose(); }}
            placeholder="e.g. Priority"
            style={{ width: '100%', padding: '6px 8px', fontSize: '12px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, outline: 'none', fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: BRIO_GREY, marginBottom: '6px' }}>Column Type</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {types.map(t => (
              <button
                key={t.key}
                onClick={() => setType(t.key)}
                style={{
                  flex: 1, padding: '5px 8px', fontSize: '11px', fontWeight: type === t.key ? 500 : 300,
                  border: '1px solid ' + (type === t.key ? BRIO_GOLD : BRIO_BORDER),
                  background: type === t.key ? 'rgba(159,144,42,0.08)' : 'transparent',
                  color: type === t.key ? BRIO_GOLD : '#555', cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '6px 14px', fontSize: '11px', border: '1px solid ' + BRIO_BORDER, background: 'transparent', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#555' }}
          >
            Cancel
          </button>
          <button
            onClick={() => { if (label.trim()) onAdd(label, type); }}
            disabled={!label.trim()}
            style={{ padding: '6px 14px', fontSize: '11px', border: 'none', background: label.trim() ? BRIO_GOLD : '#ccc', color: '#fff', cursor: label.trim() ? 'pointer' : 'default', fontFamily: 'Raleway, sans-serif' }}
          >
            Add Column
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MANAGE OPTIONS PANEL (for single/multi select custom columns)
// ============================================================================

// Inline dropdown for managing select options — appears at right-click position
function ManageOptionsDropdown({ x, y, colLabel, options, onUpdate, onClose }) {
  const [opts, setOpts] = useState(options || []);
  const [newOpt, setNewOpt] = useState('');
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const addOpt = () => {
    const trimmed = newOpt.trim();
    if (!trimmed || opts.includes(trimmed)) return;
    const next = [...opts, trimmed];
    setOpts(next);
    onUpdate(next);
    setNewOpt('');
    inputRef.current?.focus();
  };

  const removeOpt = (opt) => {
    const next = opts.filter(o => o !== opt);
    setOpts(next);
    onUpdate(next);
  };

  // Clamp position to viewport
  const dropStyle = {
    position: 'fixed', left: x, top: y,
    zIndex: 10002, background: '#fff',
    border: '1px solid ' + BRIO_BORDER,
    boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
    width: '210px', fontFamily: 'Raleway, sans-serif',
  };

  return (
    <div ref={dropRef} style={dropStyle}>
      <div style={{ padding: '6px 10px 4px', fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER }}>
        {colLabel} — Options
      </div>
      <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
        {opts.map((opt, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', fontSize: '11px', fontWeight: 300, color: '#333' }}
            onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span>{opt}</span>
            <button onClick={() => removeOpt(opt)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#bbb', padding: '1px 4px', fontSize: '14px', lineHeight: 1 }}>×</button>
          </div>
        ))}
        {opts.length === 0 && (
          <div style={{ padding: '8px 10px', fontSize: '11px', color: '#ccc', fontStyle: 'italic' }}>No options yet.</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '4px', padding: '6px 8px', borderTop: '1px solid ' + BRIO_BORDER }}>
        <input
          ref={inputRef}
          value={newOpt}
          onChange={e => setNewOpt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addOpt(); if (e.key === 'Escape') onClose(); }}
          placeholder="Add option..."
          style={{ flex: 1, padding: '3px 6px', fontSize: '11px', border: '1px solid ' + BRIO_BORDER, outline: 'none', fontFamily: 'Raleway, sans-serif' }}
        />
        <button onClick={addOpt} style={{ padding: '3px 8px', border: 'none', background: BRIO_GOLD, color: '#fff', cursor: 'pointer', fontSize: '12px', fontFamily: 'Raleway, sans-serif' }}>+</button>
      </div>
    </div>
  );
}

// ── Assigned-to cell with hover triangle ─────────────────────────────────────
function AssignedToCell({ values, categories, canEdit, onRemove, onAdd }) {
  const [hovered, setHovered] = useState(false);
  const remaining = (categories || []).filter(n => !values.includes(n));

  if (!canEdit) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '1px 0' }}>
        {values.length > 0
          ? values.map(v => (
              <span key={v} style={{ padding: '1px 6px', background: 'transparent', color: BRIO_GOLD, fontSize: '11px', fontWeight: 400, borderRadius: '2px' }}>{v}</span>
            ))
          : <span style={{ fontSize: '11px', fontWeight: 300, color: '#ccc' }}></span>}
      </div>
    );
  }
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', alignItems: 'center', padding: '1px 0' }}
    >
      {values.map(v => (
        <span key={v} title="Click to remove"
          onClick={() => onRemove(v)}
          style={{ padding: '1px 5px', background: 'transparent', color: BRIO_GOLD, fontSize: '11px', fontWeight: 400, borderRadius: '2px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
          {v}<span style={{ fontSize: '11px', opacity: 0.6 }}>×</span>
        </span>
      ))}
      {remaining.length > 0 && (
        <select
          value=""
          onChange={e => { if (e.target.value) onAdd(e.target.value); }}
          style={{
            fontSize: '11px', border: 'none', background: 'transparent', cursor: 'pointer',
            padding: '1px 2px', fontFamily: 'Raleway, sans-serif', color: BRIO_GREY,
            maxWidth: '30px', opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
          }}
        >
          <option value="">▾</option>
          {remaining.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      )}
    </div>
  );
}

// ── Predecessor cell — single-click = inline text, double-click = modal ──────
function PredecessorCell({ display, canEdit, onSingleClick, onDoubleClick }) {
  const timerRef = useRef(null);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const handleClick = () => {
    if (!canEdit) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      onDoubleClick();
    } else {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onSingleClick();
      }, 220);
    }
  };
  return (
    <div
      onClick={handleClick}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', cursor: canEdit ? 'pointer' : 'default', minHeight: '20px' }}
      title={canEdit ? 'Click to type · Double-click for dependency editor' : undefined}
    >
      <span style={{ fontSize: '11px', fontWeight: 300, color: display ? '#333' : '#aaa', wordBreak: 'break-word', textAlign: 'center' }}>
        {display || (canEdit ? '—' : '')}
      </span>
    </div>
  );
}

// ── Unified select/multiselect cell ──────────────────────────────────────────
function SelectCell({ options, values, isMulti, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const cellRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (cellRef.current && !cellRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const remaining = options.filter(o => !values.includes(o));
  // No categories yet: the click still opens, to say where they are added.
  const noOptions = options.length === 0;
  const canOpen = canEdit && (noOptions || remaining.length > 0 || (!isMulti && values.length > 0));
  const showFrame = open || hovered;

  if (!canEdit) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '1px 0', width: '100%' }}>
        {values.map(v => <span key={v} style={{ color: BRIO_GOLD, fontSize: '11px', fontWeight: 400 }}>{v}</span>)}
      </div>
    );
  }

  return (
    <div ref={cellRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => canOpen && setOpen(o => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px',
          padding: showFrame ? '1px 18px 1px 3px' : '1px 3px',
          minHeight: '20px', width: '100%', boxSizing: 'border-box',
          border: showFrame ? ('1px solid ' + (open ? BRIO_GOLD : '#ccc')) : '1px solid transparent',
          cursor: canOpen ? 'pointer' : 'default', position: 'relative',
          transition: 'border-color 0.1s',
        }}
      >
        {values.map(v => (
          <span
            key={v}
            onClick={isMulti ? (e) => { e.stopPropagation(); onChange(values.filter(x => x !== v)); } : undefined}
            title={isMulti ? 'Click to remove' : undefined}
            style={{ color: BRIO_GOLD, fontSize: '11px', fontWeight: 400, cursor: isMulti ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: '2px' }}
          >
            {v}{isMulti && <span style={{ opacity: 0.5, fontSize: '10px' }}>×</span>}
          </span>
        ))}
        {showFrame && (
          <ChevronDown size={10} style={{ position: 'absolute', right: '3px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: open ? BRIO_GOLD : BRIO_GREY }} />
        )}
      </div>
      {open && noOptions && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 1px)', left: 0, zIndex: 200, minWidth: '180px',
          background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
          padding: '8px 10px', fontFamily: 'Raleway, sans-serif', whiteSpace: 'normal',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 500, color: '#333', marginBottom: '3px' }}>No categories added</div>
          <div style={{ fontSize: '10px', fontWeight: 300, color: BRIO_GREY, lineHeight: 1.4 }}>Right click on column header &gt; Add categories</div>
        </div>
      )}
      {open && !noOptions && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 1px)', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid ' + BRIO_BORDER,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '160px', overflowY: 'auto',
          fontFamily: 'Raleway, sans-serif',
        }}>
          {!isMulti && values.length > 0 && (
            <div onClick={() => { onChange([]); setOpen(false); }}
              style={{ padding: '4px 8px', fontSize: '11px', fontWeight: 300, cursor: 'pointer', color: '#aaa', fontStyle: 'italic' }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              — Clear
            </div>
          )}
          {remaining.map(opt => (
            <div key={opt}
              onClick={() => { onChange(isMulti ? [...values, opt] : [opt]); if (!isMulti) setOpen(false); }}
              style={{ padding: '4px 8px', fontSize: '11px', fontWeight: 300, cursor: 'pointer', color: '#333' }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              {opt}
            </div>
          ))}
          {remaining.length === 0 && isMulti && (
            <div style={{ padding: '6px 8px', fontSize: '11px', color: '#ccc', fontStyle: 'italic' }}>All options selected</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Preset categories for the Assigned To column ──────────────────────────────
const ASSIGNED_TO_PRESETS = ['BRIO', 'Client', 'Vendor', 'Consultant', 'PMC'];

// ── Add-categories dropdown (Assigned To column) ──────────────────────────────
function AddCategoriesDropdown({ x, y, currentCategories, onUpdate, onClose }) {
  const [cats, setCats] = useState(currentCategories || []);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const toggle = (cat) => {
    const next = cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat];
    setCats(next);
    onUpdate(next);
  };

  return (
    <div ref={dropRef} style={{
      position: 'fixed', left: x, top: y, zIndex: 10002,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
      width: '210px', fontFamily: 'Raleway, sans-serif',
    }} onClick={e => e.stopPropagation()}>
      <div style={{ padding: '6px 10px 4px', fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER }}>
        Assigned To — Categories
      </div>
      <div style={{ padding: '8px 10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {ASSIGNED_TO_PRESETS.map(cat => {
          const active = cats.includes(cat);
          return (
            <span
              key={cat}
              onClick={() => toggle(cat)}
              style={{
                padding: '2px 8px', borderRadius: '2px', fontSize: '11px', fontWeight: 400,
                cursor: 'pointer', border: '1px solid ' + BRIO_GOLD,
                background: active ? 'rgba(159,144,42,0.15)' : 'transparent',
                color: BRIO_GOLD,
              }}
            >
              {cat}
            </span>
          );
        })}
      </div>
      <div style={{ padding: '2px 10px 8px', fontSize: '10px', fontWeight: 300, color: '#aaa' }}>
        Active categories appear in the assignee dropdown.
      </div>
    </div>
  );
}

// ============================================================================
// CONTEXT MENU COMPONENT
// ============================================================================

// Hover note on the greyed Sync / Update Actuals items of an approved stage.
const TRACKING_LOCK_NOTE = 'Stage approved on the billing page (Appr.): its end date no longer syncs to Tracking';

// ── Derived custom date columns ───────────────────────────────────────────────────────────────────
// A custom Date column may carry derive: { start, duration } (two other custom column keys): its value
// is then the start plus the duration in working days, the start counting as day 1, by the project's
// working week with holidays ignored. TG's Target End Date works this way.
function derivedEndDate(start, duration, workDays) {
  const d = parseDate(start);
  const n = Math.round(Number(duration));
  if (!d || !Number.isFinite(n) || n < 1 || !workDays || !workDays.size) return null;
  let cur = new Date(d.getTime());
  let remaining = n - 1;
  while (remaining > 0) {
    cur = new Date(cur.getTime() + DAY_MS);
    if (workDays.has(cur.getDay())) remaining--;
  }
  return formatDate(cur);
}
// Working days from start to end, both counted, by a working week with holidays ignored.
function workdaysBetween(start, end, workDays) {
  const a = parseDate(start);
  const b = parseDate(end);
  if (!a || !b || b < a || !workDays || !workDays.size) return null;
  let n = 0;
  for (let cur = new Date(a.getTime()); cur <= b; cur = new Date(cur.getTime() + DAY_MS)) {
    if (workDays.has(cur.getDay())) n++;
  }
  return n;
}

// Status Done fills % with 100 (in place). Only Done does: other statuses leave % as typed.
function applyStatusDone(rd, field) {
  if (field === 'scheduleStatus' && Number(rd.scheduleStatus) === 100 && rd.scheduleStatus !== null) rd.percentComplete = 100;
}

// Recompute a row's derived columns after `field` changed (in place).
function applyDerivedColumns(rd, field, customColumns, workDays) {
  (customColumns || []).forEach(c => {
    if (!c.derive || (field !== c.derive.start && field !== c.derive.duration)) return;
    rd[c.key] = derivedEndDate(rd[c.derive.start], rd[c.derive.duration], workDays);
  });
}

// ── Cell formatting (Format in the right click menus) ─────────────────────────────────────────────
// A format is { b, i, u, size, align }: bold, italic, underline, font size in px and text alignment.
// Stored with the timeline so everyone sees it and the exports print it:
//   column → timeline._colFmt[colKey]
//   row    → the row's `bold` (the existing Ctrl+B bold) plus timeline[rowKey]._rowFmt for the rest
//   cell   → timeline[rowKey]._fmt[colKey]
// A cell shows column, then row, then its own, the later winning; a cell may store b: false to unbold
// inside a bold row.
const FMT_BASE_SIZE = 12;
const FMT_MIN_SIZE = 8;
const FMT_MAX_SIZE = 24;
function rowFormatOf(tl, rowKey) {
  const rd = (tl && tl[rowKey]) || {};
  return { ...(rd.bold ? { b: true } : {}), ...(rd._rowFmt || {}) };
}
function cellFormatOf(tl, rowKey, colKey) {
  const t = tl || {};
  return { ...((t._colFmt || {})[colKey] || {}), ...rowFormatOf(t, rowKey), ...(((t[rowKey] || {})._fmt || {})[colKey] || {}) };
}
// Class names and the font size variable a <td> carries for its format (rules in brio-styles.css).
function cellFormatProps(f) {
  if (!f) return null;
  const cls = [];
  if (f.b === true) cls.push('tl-fmt-b');
  if (f.b === false) cls.push('tl-fmt-nb');
  if (f.i) cls.push('tl-fmt-i');
  if (f.u) cls.push('tl-fmt-u');
  if (f.size) cls.push('tl-fmt-fs');
  if (f.align) cls.push('tl-fmt-al-' + f.align);
  if (!cls.length) return null;
  return { className: cls.join(' '), style: f.size ? { '--tl-fmt-fs': f.size + 'px' } : null };
}
// Inline CSS for the PDF export: sizes scale from the screen's 12px to the PDF body size.
function cellFormatCss(f, bodyPt) {
  if (!f) return '';
  const out = [];
  if (f.b === true) out.push('font-weight:600');
  if (f.b === false) out.push('font-weight:300');
  if (f.i) out.push('font-style:italic');
  if (f.u) out.push('text-decoration:underline');
  if (f.size) out.push(`font-size:${Math.round((bodyPt * f.size / FMT_BASE_SIZE) * 10) / 10}pt`);
  if (f.align) out.push('text-align:' + f.align);
  return out.join(';');
}

// Floating Format toolbar: bold, italic, underline, smaller / larger text and alignment, plus Clear.
// `state` is the format the targets share (a toggle reads on only when every target has it).
function FormatToolbar({ x, y, state, label, onToggle, onSize, onAlign, onClear, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  // Sits just above the clicked point, kept inside the window.
  const W = 300;
  const left = Math.max(8, Math.min(x - 20, (typeof window !== 'undefined' ? window.innerWidth : 1200) - W - 8));
  const top = Math.max(8, y - 44);
  const btn = (active, title, onClick, child) => (
    <button key={title} title={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '26px', height: '26px', padding: '0 5px', border: 'none', borderRadius: '2px', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', fontSize: '12px', color: active ? '#fff' : '#444', background: active ? BRIO_GOLD : 'transparent' }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = BRIO_BG; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
      {child}
    </button>
  );
  const sep = (k) => <span key={k} style={{ width: '1px', height: '18px', background: BRIO_BORDER, margin: '0 3px' }} />;
  return (
    <div ref={ref} style={{ position: 'fixed', left, top, zIndex: 10010, display: 'flex', alignItems: 'center', gap: '1px', padding: '3px 5px', background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 14px rgba(0,0,0,0.14)', fontFamily: 'Raleway, sans-serif' }}>
      {label && <span style={{ fontSize: '9px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '4px' }}>{label}</span>}
      {btn(state.b, 'Bold', () => onToggle('b'), <strong>B</strong>)}
      {btn(state.i, 'Italic', () => onToggle('i'), <em style={{ fontFamily: 'Georgia, serif' }}>I</em>)}
      {btn(state.u, 'Underline', () => onToggle('u'), <span style={{ textDecoration: 'underline' }}>U</span>)}
      {sep('s1')}
      {btn(false, 'Smaller text', () => onSize(-1), <span style={{ fontSize: '10px' }}>A−</span>)}
      <span style={{ fontSize: '10px', color: '#888', minWidth: '18px', textAlign: 'center' }}>{state.size || FMT_BASE_SIZE}</span>
      {btn(false, 'Larger text', () => onSize(1), <span style={{ fontSize: '13px' }}>A+</span>)}
      {sep('s2')}
      {btn(state.align === 'left', 'Align left', () => onAlign('left'), <AlignLeft size={13} />)}
      {btn(state.align === 'center', 'Align centre', () => onAlign('center'), <AlignCenter size={13} />)}
      {btn(state.align === 'right', 'Align right', () => onAlign('right'), <AlignRight size={13} />)}
      {sep('s3')}
      {btn(false, 'Clear formatting', onClear, <span style={{ fontSize: '10px' }}>Clear</span>)}
    </div>
  );
}

function RowContextMenu({ x, y, onAction, onClose, hasClipboard, hasRowClipboard, showManageOptions, manageOptionsLabel, showAddToProjectTasks, showRemoveFromProjectTasks, showAddDateToTracking, showClearTrackingDate, showTriggerPrompt, showDeleteRow, showAssignSubStage, showCarryForward, showRevertCarryForward, showHideRow, showUnhideAll, showUnhideAdjacent, showIndent, showOutdent, showAddMOM, hasMOM, showInsertMOM, cellOnly, trackingOnly, stageNameOnly, showAddNewStage, showAddPresetTask, showInsertInStage, showRenameStage, showDeleteStage, showHideStage, hideStageDisabled, unhideStageDisabled, unhideAllStagesDisabled, showCopyEndToContract, hideClearCell, showLinkToSow, canEdit = true, canSync = false, trackingLocked = false, taskOps = null, showFormat = false }) {
  const menuRef = useRef(null);
  // Per-direction row count for the Insert Row Above/Below spinners (reset each time the menu opens).
  const [insertCount, setInsertCount] = useState({ above: 1, below: 1 });

  useEffect(() => {
    const onMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('wheel', onClose, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('wheel', onClose);
    };
  }, [onClose]);

  const baseItems = stageNameOnly ? [
    ...(showInsertInStage ? [
      { key: 'insertAbove', label: 'Insert Row Above' },
      { key: 'insertBelow', label: 'Insert Row Below' },
      { key: 'dividerInsert' },
    ] : []),
    ...(showAddNewStage ? [{ key: 'addNewStage', label: 'Add New Stage' }] : []),
    ...(showAddPresetTask ? [{ key: 'addPresetTask', label: 'Add Preset Task' }] : []),
    ...((showRenameStage || showDeleteStage || showHideStage) ? [{ key: 'dividerCustomStage' }] : []),
    ...(showRenameStage ? [{ key: 'renameStage', label: 'Rename' }] : []),
    ...(showDeleteStage ? [{ key: 'deleteStage', label: 'Delete', danger: true }] : []),
    ...(showHideStage ? [{ key: 'hideStage', label: 'Hide' }] : []),
  ] : trackingOnly ? (canSync ? [
    { key: 'syncTracking', label: 'Sync', disabled: trackingLocked, note: trackingLocked ? TRACKING_LOCK_NOTE : undefined },
    { key: 'updateActuals', label: 'Update Actuals', disabled: trackingLocked, note: trackingLocked ? TRACKING_LOCK_NOTE : undefined },
  ] : []) : cellOnly ? [
    ...(showManageOptions ? [
      { key: 'manageOptions', label: manageOptionsLabel || 'Manage Options' },
      { key: 'dividerMgmt' },
    ] : []),
    ...(showAddMOM ? [
      { key: 'openMOM', label: hasMOM ? 'View / Edit M.O.M' : 'Add M.O.M' },
      ...(showInsertMOM ? [{ key: 'insertMOM', label: 'Insert M.O.M' }] : []),
      { key: 'dividerMOM' },
    ] : []),
    ...(showAddToProjectTasks ? [
      { key: 'addToProjectTasks', label: 'Add to project tasks' },
    ] : []),
    ...(showRemoveFromProjectTasks ? [
      { key: 'removeFromProjectTasks', label: 'Remove from project tasks', danger: true },
    ] : []),
    ...((showAddToProjectTasks || showRemoveFromProjectTasks) ? [{ key: 'dividerPT' }] : []),
    ...(showAddDateToTracking && canSync ? [
      { key: 'addDateToTracking', label: 'Add date to tracking' },
    ] : []),
    ...(showTriggerPrompt ? [{ key: 'triggerPrompt', label: 'Trigger Prompt' }] : []),
    ...(showClearTrackingDate ? [{ key: 'clearTrackingDate', label: 'Clear tracking date', danger: true }] : []),
    ...((showAddDateToTracking || showTriggerPrompt || showClearTrackingDate) ? [{ key: 'dividerTracking' }] : []),
    { key: 'copyCell', label: 'Copy Cell' },
    { key: 'pasteCell', label: 'Paste Cell', disabled: !hasClipboard },
    ...(hideClearCell ? [] : [{ key: 'clearCell', label: 'Clear Cell' }]),
  ] : !canEdit ? [
    // A user granted Full access (not a manager): add task rows anywhere in a stage, and indent /
    // outdent / delete only the rows they added (taskOps says which apply to the clicked row).
    ...(taskOps ? [
      { key: 'insertAbove', label: 'Insert Row Above' },
      { key: 'insertBelow', label: 'Insert Row Below' },
      ...(taskOps.indent ? [{ key: 'indent', label: 'Indent' }] : []),
      ...(taskOps.outdent ? [{ key: 'outdent', label: 'Outdent' }] : []),
      ...(taskOps.delete ? [{ key: 'delete', label: 'Delete Row', danger: true }] : []),
      ...((showLinkToSow || showAddMOM) ? [{ key: 'dividerTaskOps' }] : []),
    ] : []),
    // Read-only context (incl. Template Builder view mode). "Link to SOW" opens read-only so a row's
    // SOW link can be inspected without entering edit mode.
    ...(showLinkToSow ? [
      { key: 'linkToSow', label: 'Link to SOW' },
      ...(showAddMOM ? [{ key: 'dividerLinkSow' }] : []),
    ] : []),
    // Read-only users (non-admin, non-supervisor): show only M.O.M options
    ...(showAddMOM ? [
      { key: 'openMOM', label: hasMOM ? 'View / Edit M.O.M' : 'Add M.O.M' },
      ...(showInsertMOM ? [{ key: 'insertMOM', label: 'Insert M.O.M' }] : []),
    ] : []),
  ] : [
    ...(showManageOptions ? [
      { key: 'manageOptions', label: manageOptionsLabel || 'Manage Options' },
      { key: 'dividerMgmt' },
    ] : []),
    // Template Builder: link this row to SOW deliverable(s)
    ...(showLinkToSow ? [
      { key: 'linkToSow', label: 'Link to SOW' },
      { key: 'dividerLinkSow' },
    ] : []),
    // Structural row ops first
    { key: 'insertAbove', label: 'Insert Row Above' },
    { key: 'insertBelow', label: 'Insert Row Below' },
    { key: 'divider2' },
    ...(showIndent !== false ? [{ key: 'indent', label: 'Indent' }] : []),
    ...(showOutdent !== false ? [{ key: 'outdent', label: 'Outdent' }] : []),
    ...((showIndent !== false || showOutdent !== false) ? [{ key: 'divider3' }] : []),
    // Assign to sub-stage / Carry forward — their own section, above the MOM / project-task actions
    ...((showAssignSubStage || showCarryForward || showRevertCarryForward) ? [
      ...(showAssignSubStage ? [{ key: 'assignSubStage', label: 'Assign to sub-stage' }] : []),
      ...(showCarryForward ? [{ key: 'carryForward', label: 'Carry forward' }] : []),
      ...(showRevertCarryForward ? [{ key: 'revertCarryForward', label: 'Revert to stage' }] : []),
      { key: 'dividerSub' },
    ] : []),
    // MOM / project-task / tracking actions (below indent/outdent)
    ...(showAddMOM ? [
      { key: 'openMOM', label: hasMOM ? 'View / Edit M.O.M' : 'Add M.O.M' },
      ...(showInsertMOM ? [{ key: 'insertMOM', label: 'Insert M.O.M' }] : []),
    ] : []),
    ...(showAddToProjectTasks ? [
      { key: 'addToProjectTasks', label: 'Add to project tasks' },
    ] : []),
    ...(showRemoveFromProjectTasks ? [
      { key: 'removeFromProjectTasks', label: 'Remove from project tasks', danger: true },
    ] : []),
    ...(showAddDateToTracking && canSync ? [
      { key: 'addDateToTracking', label: 'Add date to tracking' },
    ] : []),
    ...(showCopyEndToContract ? [
      { key: 'copyEndToContract', label: 'Copy end date → contract' },
    ] : []),
    ...((showAddMOM || showAddToProjectTasks || showRemoveFromProjectTasks || showAddDateToTracking || showCopyEndToContract) ? [{ key: 'divider1' }] : []),
    { key: 'selectRow', label: 'Select Row' },
    { key: 'copyRow', label: 'Copy Row' },
    { key: 'pasteRow', label: 'Paste Row', disabled: !hasRowClipboard },
    { key: 'clearRow', label: 'Clear Row' },
    { key: 'dividerCell' },
    { key: 'copyCell', label: 'Copy Cell' },
    { key: 'pasteCell', label: 'Paste Cell', disabled: !hasClipboard },
    ...(hideClearCell ? [] : [{ key: 'clearCell', label: 'Clear Cell' }]),
    // Hide / Unhide (rows) — its own section under copy/paste/clear
    ...((showHideRow || showUnhideAll || showUnhideAdjacent) ? [{ key: 'dividerHide' }] : []),
    ...(showHideRow ? [{ key: 'hide', label: 'Hide row' }] : []),
    ...(showUnhideAll ? [{ key: 'unhideAll', label: 'Unhide all' }] : []),
    ...(showUnhideAdjacent ? [{ key: 'unhideAdjacent', label: 'Unhide' }] : []),
    ...(showDeleteRow ? [{ key: 'divider4' }, { key: 'delete', label: 'Delete Row', danger: true }] : []),
    // Stage-level hide/unhide (custom stages) — always shown, disabled when inapplicable
    { key: 'dividerStageOps' },
    { key: 'hideStage', label: 'Hide stage', disabled: hideStageDisabled },
    { key: 'unhideStage', label: 'Unhide stage', disabled: unhideStageDisabled },
    { key: 'unhideAllStages', label: 'Unhide all stages', disabled: unhideAllStagesDisabled },
  ];
  // Format (bold, italic, size, alignment) heads every menu for anyone who may edit the table.
  const items = showFormat
    ? [{ key: 'format', label: 'Format' }, ...(baseItems.length ? [{ key: 'dividerFormat' }] : []), ...baseItems]
    : baseItems;

  // Nothing to show (e.g. a tracking-only cell for a user who can't sync) — don't
  // render an empty popup box.
  if (!items.length) return null;

  // Flip upward if menu would overflow viewport bottom
  const approxMenuHeight = items.filter(i => !i.key.startsWith('divider')).length * 30 + items.filter(i => i.key.startsWith('divider')).length * 9 + 8;
  const adjustedTop = (y + approxMenuHeight > window.innerHeight - 8) ? Math.max(8, y - approxMenuHeight) : y;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed', left: x, top: adjustedTop, zIndex: 1000,
        background: '#fff', border: '1px solid ' + BRIO_BORDER,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        minWidth: '160px', padding: '4px 0',
        fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
      }}
    >
      {items.map(item => {
        if (item.key.startsWith('divider')) {
          return <div key={item.key} style={{ height: '1px', background: BRIO_BORDER, margin: '4px 0' }} />;
        }
        // Insert Row Above/Below carry an inline number spinner (+ ✓) so several empty rows can be
        // inserted at once. Clicking the label inserts the chosen count; the ✓ does the same. The
        // spinner controls stop propagation so adjusting the count doesn't fire the insert / close.
        if (item.key === 'insertAbove' || item.key === 'insertBelow') {
          const which = item.key === 'insertAbove' ? 'above' : 'below';
          const n = insertCount[which];
          const doInsert = () => { if (!item.disabled) { onAction(item.key, n); onClose(); } };
          return (
            <div
              key={item.key}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '6px 16px', cursor: item.disabled ? 'default' : 'pointer', color: item.disabled ? '#ccc' : '#333' }}
              onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span onClick={doInsert} style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.label}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={e => e.stopPropagation()}>
                <input
                  type="number" min={1} max={50} value={n}
                  onChange={e => { const v = Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)); setInsertCount(c => ({ ...c, [which]: v })); }}
                  onKeyDown={e => { if (e.key === 'Enter') doInsert(); }}
                  onClick={e => e.stopPropagation()}
                  style={{ width: '40px', fontFamily: 'Raleway, sans-serif', fontSize: '11px', padding: '2px 4px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', textAlign: 'center', color: '#333' }}
                />
                <button
                  onClick={doInsert}
                  title={`Insert ${n} row${n === 1 ? '' : 's'} ${which}`}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#5a7d3a', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                >✓</button>
              </span>
            </div>
          );
        }
        return (
          <div
            key={item.key}
            onClick={() => { if (!item.disabled) { onAction(item.key); onClose(); } }}
            title={item.note || undefined}
            style={{
              padding: '6px 16px', cursor: item.disabled ? 'default' : 'pointer',
              color: item.disabled ? '#ccc' : (item.danger ? BRIO_RED : '#333'),
            }}
            onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = BRIO_BG; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            {item.label}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// SORTABLE TABLE ROW
// ============================================================================

function SortableRow({ id, disabled, children, style: rowStyle, className }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    ...rowStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <tr ref={setNodeRef} data-rowid={id} className={className} style={style} {...attributes}>
      {typeof children === 'function' ? children(listeners) : children}
    </tr>
  );
}

// ============================================================================
// COLUMN DEFINITIONS FOR SCHEDULE TABLE
// ============================================================================

// Column definitions for the schedule table
const SCHEDULE_COLUMNS = [
  { key: 'taskName',        label: 'Task Name',        type: 'text',       width: '18%',  sticky: true },
  { key: 'plannedDuration', label: 'Duration',            type: 'number',   width: '64px', minWidth: '64px', noResize: true },
  { key: 'plannedStartDate',label: 'Start Date',          type: 'date',     width: '78px', minWidth: '78px', noResize: true },
  { key: 'plannedEndDate',  label: 'End Date',            type: 'date',     width: '78px', minWidth: '78px', noResize: true, computed: true },
  { key: 'predecessors',    label: 'Dependency',        type: 'text',       width: '80px', minWidth: '80px', noResize: true },
  { key: 'assignedTo',      label: 'Assigned To',      type: 'text',       width: '8%',  minWidth: '68px' },
  { key: 'remarks',         label: 'Remarks',          type: 'text',       width: '8%' },
  { key: 'delayCaused',     label: 'Delay Caused',     type: 'text',       width: '7%' },
  { key: 'scheduleStatus',  label: 'Status',           type: 'status',     width: '90px', minWidth: '90px', noResize: true },
  { key: 'percentComplete', label: '%',                 type: 'percent',    width: '41px', minWidth: '36px', noResize: true },
];

// ============================================================================
// PROJECT SETTINGS — DEFAULT CONFIG
// ============================================================================

const DEFAULT_PROJECT_SETTINGS = {
  dateRange: {
    startDateColumn: 'plannedStartDate',
    endDateColumn: 'plannedEndDate',
  },
  options: {
    percentCompleteColumn: 'percentComplete',
  },
  dependencies: {
    // On by default: a project that never saved a preset would otherwise show typed dependencies
    // and lags that never move a date.
    enabled: true,
    predecessorColumn: 'predecessors',
    durationColumn: 'plannedDuration',
  },
  workingDays: {
    days: [1, 2, 3, 4, 5], // Mon–Fri (0=Sun, 6=Sat)
    hoursPerDay: 8,
    holidays: [], // array of { date: 'YYYY-MM-DD', name: 'Holiday name' }
  },
  startRowConditions: {
    noDelay: { dep: 'Invoice row — SS',                  startDate: 'Invoice start + 1 wd',    badge: ''        },
    overdue: { dep: 'Invoice row — FS+Nd (grows daily)', startDate: 'Invoice end + lag',        badge: 'Delayed' },
    paid:    { dep: 'Invoice row — FS+finalLag (frozen)',startDate: 'Invoice end + finalLag',   badge: 'Delayed' },
  },
};

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Column option lists for settings dropdowns
const DATE_COL_OPTIONS = SCHEDULE_COLUMNS.filter(c => c.type === 'date');
const NUMBER_COL_OPTIONS = SCHEDULE_COLUMNS.filter(c => c.type === 'number' || c.type === 'percent');
const ALL_COL_OPTIONS = SCHEDULE_COLUMNS.filter(c => c.key !== 'taskName');

// A stored preset can name a column the table no longer has (Coffee Plantation carried
// targetStartDate / targetEndDate / targetDuration from an older naming). The dependency engine
// then reads and writes columns nobody renders, so lags and dependencies silently do nothing, and
// the settings dropdowns hide it by showing their first option. Any unknown key falls back to the
// default column.
function sanitizePresetColumns(preset) {
  const pick = (value, options, fallback) => (options.some(c => c.key === value) ? value : fallback);
  const dr = preset.dateRange || {};
  const dep = preset.dependencies || {};
  const opt = preset.options || {};
  return {
    ...preset,
    dateRange: {
      ...dr,
      startDateColumn: pick(dr.startDateColumn, DATE_COL_OPTIONS, DEFAULT_PROJECT_SETTINGS.dateRange.startDateColumn),
      endDateColumn:   pick(dr.endDateColumn,   DATE_COL_OPTIONS, DEFAULT_PROJECT_SETTINGS.dateRange.endDateColumn),
    },
    dependencies: {
      ...dep,
      predecessorColumn: pick(dep.predecessorColumn, ALL_COL_OPTIONS,    DEFAULT_PROJECT_SETTINGS.dependencies.predecessorColumn),
      durationColumn:    pick(dep.durationColumn,    NUMBER_COL_OPTIONS, DEFAULT_PROJECT_SETTINGS.dependencies.durationColumn),
    },
    options: {
      ...opt,
      // '' is a legitimate "None" choice in the dropdown.
      percentCompleteColumn: opt.percentCompleteColumn === ''
        ? ''
        : pick(opt.percentCompleteColumn, NUMBER_COL_OPTIONS, DEFAULT_PROJECT_SETTINGS.options.percentCompleteColumn),
    },
  };
}

// ============================================================================
// PROJECT SETTINGS MODAL
// ============================================================================

function makePresetId() {
  return 'preset_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 5);
}

function makeDefaultPreset(name) {
  return { id: makePresetId(), name, ...JSON.parse(JSON.stringify(DEFAULT_PROJECT_SETTINGS)) };
}

// Resolve the active project-settings preset straight from a stored timeline. Mirrors the resolution
// ProjectTimelineEditor does with settingsPresets/_activePresetId (and the legacy _settings shape), so
// code OUTSIDE the editor — e.g. the gantt bar-drag handler in TimelinesPage — can read the same
// working-days / dependencies configuration the editor would apply.
function resolveActiveTimelineSettings(timeline) {
  const tl = timeline || {};
  let presets = null;
  if (Array.isArray(tl._settingsPresets) && tl._settingsPresets.length > 0) presets = tl._settingsPresets;
  else if (tl._settings && typeof tl._settings === 'object') presets = [tl._settings];
  if (!presets) return DEFAULT_PROJECT_SETTINGS;
  const active = presets.find(p => p.id === tl._activePresetId) || presets[0] || {};
  return sanitizePresetColumns({
    ...DEFAULT_PROJECT_SETTINGS,
    ...active,
    dateRange: { ...DEFAULT_PROJECT_SETTINGS.dateRange, ...(active.dateRange || {}) },
    options: { ...DEFAULT_PROJECT_SETTINGS.options, ...(active.options || {}) },
    dependencies: { ...DEFAULT_PROJECT_SETTINGS.dependencies, ...(active.dependencies || {}) },
    workingDays: { ...DEFAULT_PROJECT_SETTINGS.workingDays, ...(active.workingDays || {}) },
  });
}

// Pure translation of a gantt bar drag into an updated timeline. Given the stored timeline, the dragged
// row and the geometry the row component produced (calendar Dates for the new start/end), it rewrites
// that row's planned schedule using the timeline's own working-day calendar and returns a NEW timeline
// object (or null when the drag is a no-op / invalid). Shared by the project gantt (writes via
// supabaseProjects) and the template-builder gantt (writes via the proxy) so both behave identically:
//   • move        → shift start, keep the stored working-day duration, recompute end
//   • resize-left → keep the end fixed, move the start, derive the new duration
//   • resize-right→ keep the start fixed, move the end, derive the new duration
function computeGanttBarDragTimeline(timeline, rowId, mode, newStart, newEnd) {
  const tl = timeline || {};
  const rd = tl[rowId];
  if (!rd || !newStart || !newEnd) return null;
  const settings = resolveActiveTimelineSettings(tl);
  const wd = new Set(settings.workingDays.days);
  const hol = new Set((settings.workingDays.holidays || []).map(h => h.date));
  const isWorkingDay = (d) => wd.has(d.getDay()) && !hol.has(formatDate(d));
  const snapFwd = (d) => { let cur = new Date(d.getTime()); let g = 0; while (!isWorkingDay(cur) && g++ < 31) cur = new Date(cur.getTime() + DAY_MS); return cur; };
  const addWd = (startDate, count) => { let cur = new Date(startDate.getTime()); let rem = count; while (rem > 0) { cur = new Date(cur.getTime() + DAY_MS); if (isWorkingDay(cur)) rem--; } return cur; };

  const start = snapFwd(newStart);
  let patch = null;
  if (mode === 'move') {
    const dur = rd.plannedDuration != null
      ? Number(rd.plannedDuration)
      : (countWorkingDays(parseDate(rd.plannedStartDate), parseDate(rd.plannedEndDate), wd, hol) || 1);
    const end = addWd(start, Math.max(dur, 1) - 1);
    patch = { plannedStartDate: formatDate(start), plannedDuration: Math.max(dur, 1), plannedEndDate: formatDate(end) };
  } else if (mode === 'resize-left') {
    const end = snapFwd(newEnd);
    const dur = countWorkingDays(start, end, wd, hol);
    if (!dur || dur < 1) return null;
    patch = { plannedStartDate: formatDate(start), plannedDuration: dur, plannedEndDate: formatDate(end) };
  } else { // resize-right — start stays put, the end moves
    const keepStart = parseDate(rd.plannedStartDate) || start;
    const end = snapFwd(newEnd);
    const dur = countWorkingDays(keepStart, end, wd, hol);
    if (!dur || dur < 1) return null;
    patch = { plannedDuration: dur, plannedEndDate: formatDate(end) };
  }
  return { ...tl, [rowId]: { ...rd, ...patch } };
}

function ProjectSettingsModal({ presets, activePresetId, onSave, onCancel }) {
  const [localPresets, setLocalPresets] = useState(() =>
    presets.length > 0 ? presets.map(p => JSON.parse(JSON.stringify(p))) : [makeDefaultPreset('Project Setting 1')]
  );
  const [selectedId, setSelectedId] = useState(() =>
    activePresetId && presets.some(p => p.id === activePresetId) ? activePresetId : (presets[0]?.id || null)
  );
  const [editingWorkDays, setEditingWorkDays] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [calendarHolidayFilter, setCalendarHolidayFilter] = useState('all'); // 'all' | 'NL' | 'IN'
  const [selectedCalHoliday, setSelectedCalHoliday] = useState('');
  const { holidays: calendarHolidays } = useSupabaseHolidays();

  const draft = localPresets.find(p => p.id === selectedId) || localPresets[0];

  const update = (path, value) => {
    setLocalPresets(prev => prev.map(p => {
      if (p.id !== (draft?.id)) return p;
      const next = JSON.parse(JSON.stringify(p));
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    }));
  };

  const addPreset = () => {
    const num = localPresets.length + 1;
    const np = makeDefaultPreset(`Project Setting ${num}`);
    setLocalPresets(prev => [...prev, np]);
    setSelectedId(np.id);
    setEditingWorkDays(false);
  };

  const deletePreset = (id) => {
    if (localPresets.length <= 1) return;
    const idx = localPresets.findIndex(p => p.id === id);
    const remaining = localPresets.filter(p => p.id !== id);
    setLocalPresets(remaining);
    if (selectedId === id) {
      setSelectedId(remaining[Math.max(0, idx - 1)]?.id || remaining[0]?.id);
    }
  };

  const commitRename = (id) => {
    const trimmed = renameVal.trim();
    if (trimmed) {
      setLocalPresets(prev => prev.map(p => p.id === id ? { ...p, name: trimmed } : p));
    }
    setRenamingId(null);
    setRenameVal('');
  };

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', zIndex: 10000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const modalStyle = {
    background: '#fff', width: '660px', maxWidth: '95vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    border: '1px solid ' + BRIO_BORDER,
  };

  const sectionTitle = {
    fontSize: '12px', fontWeight: 600, color: '#333',
    textTransform: 'uppercase', letterSpacing: '0.04em',
    margin: '0 0 10px 0', paddingBottom: '6px',
    borderBottom: '1px solid ' + BRIO_BORDER,
  };

  const fieldRow = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '10px', gap: '12px',
  };

  const labelStyle = { fontSize: '12px', fontWeight: 400, color: '#444', whiteSpace: 'nowrap' };

  const selectStyle = {
    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
    padding: '5px 8px', border: '1px solid ' + BRIO_BORDER,
    borderRadius: '0', background: '#fff', color: '#333',
    cursor: 'pointer', minWidth: '180px',
  };

  const checkboxRow = {
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px',
  };

  const depDisabled = !draft?.dependencies?.enabled;

  const workingDayNames = (draft?.workingDays?.days || []).slice().sort((a, b) => a - b).map(d => DAY_LABELS[d]).join(', ');

  const addHoliday = () => {
    if (!newHolidayDate) return;
    const existing = draft?.workingDays?.holidays || [];
    if (existing.some(h => h.date === newHolidayDate)) return;
    update('workingDays.holidays', [...existing, { date: newHolidayDate, name: newHolidayName || 'Holiday' }]);
    setNewHolidayDate('');
    setNewHolidayName('');
  };

  const removeHoliday = (date) => {
    update('workingDays.holidays', (draft?.workingDays?.holidays || []).filter(h => h.date !== date));
  };

  const toggleWorkDay = (dayIndex) => {
    const days = [...(draft?.workingDays?.days || [])];
    const idx = days.indexOf(dayIndex);
    if (idx !== -1) days.splice(idx, 1); else days.push(dayIndex);
    update('workingDays.days', days);
  };

  if (!draft) return null;

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '2px solid ' + BRIO_GOLD,
          background: BRIO_BG, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Settings2 size={16} style={{ color: BRIO_GOLD }} />
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#333' }}>Dependency Settings</span>
          </div>
          <button onClick={onCancel} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
            <X size={16} style={{ color: BRIO_GREY }} />
          </button>
        </div>

        {/* Body: sidebar + content */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflowY: 'auto' }}>

          {/* Presets sidebar */}
          <div style={{ width: '180px', flexShrink: 0, borderRight: '1px solid ' + BRIO_BORDER, background: BRIO_BG, padding: '14px 0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0 14px 8px' }}>Settings Presets</div>
            {localPresets.map((p) => (
              <div key={p.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                {renamingId === p.id ? (
                  <input
                    autoFocus
                    value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onBlur={() => commitRename(p.id)}
                    onKeyDown={e => { if (e.key === 'Enter') commitRename(p.id); if (e.key === 'Escape') { setRenamingId(null); setRenameVal(''); } }}
                    style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300, flex: 1, margin: '0 6px 0 14px', padding: '4px 6px', border: '1px solid ' + BRIO_GOLD, outline: 'none', background: '#fff' }}
                  />
                ) : (
                  <button
                    onClick={() => { setSelectedId(p.id); setEditingWorkDays(false); }}
                    onDoubleClick={() => { setRenamingId(p.id); setRenameVal(p.name); }}
                    title="Double-click to rename"
                    style={{
                      flex: 1, textAlign: 'left', padding: '7px 14px', fontSize: '12px', fontWeight: selectedId === p.id ? 500 : 300,
                      fontFamily: 'Raleway, sans-serif', border: 'none', cursor: 'pointer',
                      background: selectedId === p.id ? '#fff' : 'transparent',
                      color: selectedId === p.id ? BRIO_GOLD : '#444',
                      borderLeft: selectedId === p.id ? '3px solid ' + BRIO_GOLD : '3px solid transparent',
                      paddingRight: localPresets.length > 1 ? '28px' : '14px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {p.name}
                  </button>
                )}
                {localPresets.length > 1 && renamingId !== p.id && (
                  <button
                    onClick={() => deletePreset(p.id)}
                    title="Delete preset"
                    style={{ position: 'absolute', right: '6px', border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 4px', color: '#ccc', fontSize: '11px', lineHeight: 1 }}
                    onMouseEnter={e => e.target.style.color = BRIO_RED}
                    onMouseLeave={e => e.target.style.color = '#ccc'}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addPreset}
              style={{ margin: '10px 14px 0', fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: BRIO_GOLD, background: 'transparent', border: '1px dashed ' + BRIO_GOLD, padding: '5px 8px', cursor: 'pointer', textAlign: 'left' }}
            >
              + Add New Preset
            </button>
          </div>

          {/* Settings content for selected preset */}
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>

          {/* ─── 1. Date Range Display ─── */}
          <h4 style={sectionTitle}>Date Range Display</h4>
          <div style={fieldRow}>
            <span style={labelStyle}>Start date column</span>
            <select style={selectStyle} value={draft.dateRange.startDateColumn}
              onChange={e => update('dateRange.startDateColumn', e.target.value)}>
              {DATE_COL_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>End date column</span>
            <select style={selectStyle} value={draft.dateRange.endDateColumn}
              onChange={e => update('dateRange.endDateColumn', e.target.value)}>
              {DATE_COL_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          {/* ─── 2. Options ─── */}
          <h4 style={{ ...sectionTitle, marginTop: '18px' }}>Options</h4>
          <div style={fieldRow}>
            <span style={labelStyle}>% Complete column</span>
            <select style={selectStyle} value={draft.options.percentCompleteColumn}
              onChange={e => update('options.percentCompleteColumn', e.target.value)}>
              <option value="">-- None --</option>
              {NUMBER_COL_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          {/* ─── 3. Dependencies ─── */}
          <h4 style={{ ...sectionTitle, marginTop: '18px' }}>Dependencies</h4>
          <div style={checkboxRow}>
            <input type="checkbox" id="dep-enable"
              checked={draft.dependencies.enabled}
              onChange={e => update('dependencies.enabled', e.target.checked)}
              style={{ accentColor: BRIO_GOLD, width: '14px', height: '14px', cursor: 'pointer' }}
            />
            <label htmlFor="dep-enable" style={{ fontSize: '12px', fontWeight: 400, color: '#333', cursor: 'pointer' }}>
              Enable Dependencies
            </label>
          </div>
          <div style={{ ...fieldRow, opacity: depDisabled ? 0.4 : 1 }}>
            <span style={labelStyle}>Predecessor column</span>
            <select style={selectStyle} value={draft.dependencies.predecessorColumn}
              onChange={e => update('dependencies.predecessorColumn', e.target.value)}
              disabled={depDisabled}>
              {ALL_COL_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ ...fieldRow, opacity: depDisabled ? 0.4 : 1 }}>
            <span style={labelStyle}>Duration column</span>
            <select style={selectStyle} value={draft.dependencies.durationColumn}
              onChange={e => update('dependencies.durationColumn', e.target.value)}
              disabled={depDisabled}>
              {NUMBER_COL_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          {/* ─── 4. Working Days ─── */}
          <h4 style={{ ...sectionTitle, marginTop: '18px' }}>Working Days</h4>

          {!editingWorkDays ? (
            <div style={{ fontSize: '12px', fontWeight: 300, color: '#444', lineHeight: '1.8' }}>
              <div><span style={{ fontWeight: 500 }}>Working days:</span> {workingDayNames}</div>
              <div><span style={{ fontWeight: 500 }}>Length of day:</span> {draft.workingDays.hoursPerDay} hours</div>
              <div>
                <span style={{ fontWeight: 500 }}>Non-working days (holidays):</span>{' '}
                {(draft.workingDays.holidays || []).length === 0
                  ? 'None'
                  : `${draft.workingDays.holidays.length} defined`
                }
              </div>
              <button
                onClick={() => setEditingWorkDays(true)}
                style={{
                  marginTop: '8px', fontFamily: 'Raleway, sans-serif',
                  fontSize: '12px', fontWeight: 400, color: BRIO_GOLD,
                  background: 'transparent', border: '1px solid ' + BRIO_GOLD,
                  padding: '4px 14px', cursor: 'pointer',
                }}
              >
                Edit...
              </button>
            </div>
          ) : (
            <div style={{ background: '#fafaf8', border: '1px solid ' + BRIO_BORDER, padding: '14px', marginBottom: '4px' }}>
              {/* Day toggles */}
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: 500, color: '#444', display: 'block', marginBottom: '6px' }}>Working days</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {DAY_LABELS.map((label, idx) => {
                    const isActive = draft.workingDays.days.includes(idx);
                    return (
                      <button key={idx} onClick={() => toggleWorkDay(idx)}
                        style={{
                          width: '32px', height: '28px', fontSize: '11px', fontWeight: isActive ? 600 : 300,
                          fontFamily: 'Raleway, sans-serif',
                          border: '1px solid ' + (isActive ? BRIO_GOLD : BRIO_BORDER),
                          background: isActive ? BRIO_GOLD : '#fff',
                          color: isActive ? '#fff' : BRIO_GREY,
                          cursor: 'pointer',
                        }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Hours per day */}
              <div style={{ ...fieldRow, marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: 500, color: '#444' }}>Hours per day</span>
                <input type="number" min="1" max="24"
                  value={draft.workingDays.hoursPerDay}
                  onChange={e => update('workingDays.hoursPerDay', Math.max(1, Math.min(24, Number(e.target.value))))}
                  style={{ ...selectStyle, width: '60px', minWidth: '60px', textAlign: 'center' }}
                />
              </div>

              {/* Holidays */}
              <div>
                <span style={{ fontSize: '11px', fontWeight: 500, color: '#444', display: 'block', marginBottom: '6px' }}>Holidays</span>
                {(draft.workingDays.holidays || []).length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    {draft.workingDays.holidays.map(h => (
                      <div key={h.date} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', fontSize: '11px', fontWeight: 300, color: '#444' }}>
                        <span style={{ fontWeight: 400, minWidth: '80px' }}>{h.date}</span>
                        <span>{h.name}</span>
                        <button onClick={() => removeHoliday(h.date)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: BRIO_RED, fontSize: '11px', padding: '0 4px' }}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Import from calendar holidays */}
                <div style={{ marginBottom: '10px', paddingTop: '8px', borderTop: '1px solid ' + BRIO_BORDER }}>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Import from calendar</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Office filter */}
                    <select value={calendarHolidayFilter} onChange={e => { setCalendarHolidayFilter(e.target.value); setSelectedCalHoliday(''); }}
                      style={{ ...selectStyle, minWidth: '80px', width: '80px', fontSize: '10px' }}>
                      <option value="all">All</option>
                      <option value="NL">Netherlands</option>
                      <option value="IN">India</option>
                    </select>
                    {/* Holiday picker */}
                    <select value={selectedCalHoliday} onChange={e => setSelectedCalHoliday(e.target.value)}
                      style={{ ...selectStyle, minWidth: '200px', fontSize: '10px', flex: 1 }}>
                      <option value="">— select holiday —</option>
                      {calendarHolidays
                        .filter(h => calendarHolidayFilter === 'all' ? true : (h.office === calendarHolidayFilter || !h.office))
                        .filter(h => !(draft?.workingDays?.holidays || []).some(p => p.date === h.date))
                        .map(h => (
                          <option key={h.id} value={h.date + '|' + h.name}>
                            {h.date} — {h.name}{h.office ? ` (${h.office})` : ''}
                          </option>
                        ))
                      }
                    </select>
                    <button
                      disabled={!selectedCalHoliday}
                      onClick={() => {
                        if (!selectedCalHoliday) return;
                        const [date, name] = selectedCalHoliday.split('|');
                        const existing = draft?.workingDays?.holidays || [];
                        if (!existing.some(h => h.date === date)) {
                          update('workingDays.holidays', [...existing, { date, name }]);
                        }
                        setSelectedCalHoliday('');
                      }}
                      style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: '#fff', background: selectedCalHoliday ? BRIO_GOLD : '#ccc', border: 'none', padding: '5px 10px', cursor: selectedCalHoliday ? 'pointer' : 'default' }}>
                      Add
                    </button>
                    <button
                      onClick={() => {
                        const filtered = calendarHolidays
                          .filter(h => calendarHolidayFilter === 'all' ? true : (h.office === calendarHolidayFilter || !h.office))
                          .filter(h => !(draft?.workingDays?.holidays || []).some(p => p.date === h.date));
                        if (filtered.length === 0) return;
                        const existing = draft?.workingDays?.holidays || [];
                        update('workingDays.holidays', [...existing, ...filtered.map(h => ({ date: h.date, name: h.name }))]);
                        setSelectedCalHoliday('');
                      }}
                      style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: BRIO_GOLD, background: 'transparent', border: '1px solid ' + BRIO_GOLD, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Add All
                    </button>
                  </div>
                </div>
                {/* Manual add row */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input type="date" value={newHolidayDate} onChange={e => setNewHolidayDate(e.target.value)}
                    style={{ ...selectStyle, minWidth: '130px', width: '130px', fontSize: '10px' }} />
                  <input type="text" value={newHolidayName} onChange={e => setNewHolidayName(e.target.value)}
                    placeholder="Name" style={{ ...selectStyle, minWidth: '100px', width: '100px', fontSize: '11px' }} />
                  <button onClick={addHoliday}
                    style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: '#fff', background: BRIO_GOLD, border: 'none', padding: '5px 10px', cursor: 'pointer' }}>
                    Add
                  </button>
                </div>
              </div>

              <button
                onClick={() => setEditingWorkDays(false)}
                style={{
                  marginTop: '10px', fontFamily: 'Raleway, sans-serif',
                  fontSize: '11px', fontWeight: 400, color: BRIO_GREY,
                  background: 'transparent', border: '1px solid ' + BRIO_BORDER,
                  padding: '4px 12px', cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          padding: '12px 20px', borderTop: '1px solid ' + BRIO_BORDER,
          background: BRIO_BG, flexShrink: 0,
        }}>
          <button onClick={onCancel}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400,
              padding: '6px 20px', border: '1px solid ' + BRIO_BORDER,
              background: '#fff', color: '#333', cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button onClick={() => onSave(localPresets, selectedId || localPresets[0]?.id)}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 500,
              padding: '6px 20px', border: '1px solid ' + BRIO_GOLD,
              background: BRIO_GOLD, color: '#fff', cursor: 'pointer',
            }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PRESET SETTINGS TABLE MODAL
// ============================================================================

// Row labels for the three auto-conditions on Start-of-Stage rows.
// Dep, Start date and Badge are all stored per-preset and editable.
const START_ROW_CONDITION_ROWS = [
  { key: 'noDelay', state: 'No delay'          },
  { key: 'overdue', state: 'Overdue (live)'    },
  { key: 'paid',    state: 'Payment received'  },
];

function PresetSettingsModal({ presets, activePresetId, onSave, onCancel }) {
  const [localPresets, setLocalPresets] = useState(() =>
    presets.length > 0 ? presets.map(p => JSON.parse(JSON.stringify(p))) : [makeDefaultPreset('Project Setting 1')]
  );
  const [localActiveId, setLocalActiveId] = useState(
    activePresetId && presets.some(p => p.id === activePresetId) ? activePresetId : (presets[0]?.id || null)
  );
  const [selectedTabId, setSelectedTabId] = useState(
    activePresetId && presets.some(p => p.id === activePresetId) ? activePresetId : (presets[0]?.id || null)
  );

  const draft = localPresets.find(p => p.id === selectedTabId) || localPresets[0];

  const updateDraftCondition = (condKey, field, value) => {
    if (!draft) return;
    setLocalPresets(prev => prev.map(p => {
      if (p.id !== draft.id) return p;
      return {
        ...p,
        startRowConditions: {
          ...DEFAULT_PROJECT_SETTINGS.startRowConditions,
          ...(p.startRowConditions || {}),
          [condKey]: {
            ...(p.startRowConditions?.[condKey] || DEFAULT_PROJECT_SETTINGS.startRowConditions[condKey]),
            [field]: value,
          },
        },
      };
    }));
  };

  const renameDraft = (value) => {
    if (!draft) return;
    setLocalPresets(prev => prev.map(p => p.id === draft.id ? { ...p, name: value } : p));
  };

  const addPreset = () => {
    const np = makeDefaultPreset(`Project Setting ${localPresets.length + 1}`);
    setLocalPresets(prev => [...prev, np]);
    setSelectedTabId(np.id);
  };

  const deletePreset = (id) => {
    if (localPresets.length <= 1) return;
    setLocalPresets(prev => {
      const remaining = prev.filter(p => p.id !== id);
      if (localActiveId === id) setLocalActiveId(remaining[0]?.id || null);
      if (selectedTabId === id) setSelectedTabId(remaining[0]?.id || null);
      return remaining;
    });
  };

  const thSt = { fontSize: '10px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 14px', background: BRIO_BG, borderBottom: '1px solid ' + BRIO_BORDER, whiteSpace: 'nowrap', textAlign: 'left' };
  const tdSt = (shade) => ({ padding: '9px 14px', borderBottom: '1px solid ' + BRIO_BORDER, verticalAlign: 'middle', background: shade ? '#fafaf8' : '#fff' });

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', width: '680px', maxWidth: '96vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Table2 size={16} style={{ color: BRIO_GOLD }} />
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#333' }}>Preset Settings</span>
          </div>
          <button onClick={onCancel} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
            <X size={16} style={{ color: BRIO_GREY }} />
          </button>
        </div>

        {/* Preset tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG, flexShrink: 0, flexWrap: 'wrap', padding: '0 12px' }}>
          {localPresets.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <button
                onClick={() => setSelectedTabId(p.id)}
                style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: selectedTabId === p.id ? 500 : 300, padding: '8px 14px', border: 'none', borderBottom: selectedTabId === p.id ? '2px solid ' + BRIO_GOLD : '2px solid transparent', background: 'transparent', cursor: 'pointer', color: selectedTabId === p.id ? BRIO_GOLD : '#666', marginBottom: '-1px' }}>
                {localActiveId === p.id && <span style={{ color: BRIO_GOLD, marginRight: '4px', fontSize: '9px' }}>●</span>}
                {p.name}
              </button>
              {localPresets.length > 1 && (
                <button onClick={() => deletePreset(p.id)} title="Delete preset"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#ccc', padding: '0 2px', fontSize: '12px', lineHeight: 1 }}
                  onMouseEnter={e => e.currentTarget.style.color = BRIO_RED}
                  onMouseLeave={e => e.currentTarget.style.color = '#ccc'}>
                  ×
                </button>
              )}
            </div>
          ))}
          <button onClick={addPreset}
            style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 400, padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#aaa', marginLeft: '4px' }}
            onMouseEnter={e => e.currentTarget.style.color = BRIO_GOLD}
            onMouseLeave={e => e.currentTarget.style.color = '#aaa'}>
            + Add
          </button>
        </div>

        {/* Body */}
        {draft && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

            {/* Preset name + active selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 400, color: '#555', whiteSpace: 'nowrap' }}>
                <input type="radio" name="activePreset" checked={localActiveId === draft.id} onChange={() => setLocalActiveId(draft.id)}
                  style={{ accentColor: BRIO_GOLD, cursor: 'pointer' }} />
                Set as active preset
              </label>
              <input
                value={draft.name}
                onChange={e => renameDraft(e.target.value)}
                style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, padding: '4px 8px', flex: 1, outline: 'none', background: '#fff' }}
                placeholder="Preset name"
              />
            </div>

            {/* Section title */}
            <div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              Start of Stage — automatic dependency conditions
            </div>

            {/* Conditions table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid ' + BRIO_BORDER }}>
              <thead>
                <tr>
                  <th style={thSt}>State</th>
                  <th style={thSt}>Dep</th>
                  <th style={thSt}>Start date</th>
                </tr>
              </thead>
              <tbody>
                {START_ROW_CONDITION_ROWS.map((row, i) => {
                  const defaults = DEFAULT_PROJECT_SETTINGS.startRowConditions[row.key];
                  const condSettings = { ...defaults, ...(draft.startRowConditions?.[row.key] || {}) };
                  const shade = i % 2 === 0;
                  const inSt = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, padding: '3px 7px', width: '100%', outline: 'none', background: '#fff', boxSizing: 'border-box' };
                  return (
                    <tr key={row.key}>
                      <td style={{ ...tdSt(shade), fontSize: '11px', fontWeight: 500, color: '#333', whiteSpace: 'nowrap' }}>
                        {row.state}
                      </td>
                      <td style={{ ...tdSt(shade), minWidth: '180px' }}>
                        <input
                          value={condSettings.dep}
                          onChange={e => updateDraftCondition(row.key, 'dep', e.target.value)}
                          placeholder={defaults.dep}
                          style={{ ...inSt, fontFamily: 'monospace' }}
                        />
                      </td>
                      <td style={{ ...tdSt(shade), minWidth: '160px' }}>
                        <input
                          value={condSettings.startDate}
                          onChange={e => updateDraftCondition(row.key, 'startDate', e.target.value)}
                          placeholder={defaults.startDate}
                          style={inSt}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '10px 20px', borderTop: '1px solid ' + BRIO_BORDER, background: BRIO_BG, flexShrink: 0 }}>
          <button onClick={onCancel}
            style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400, padding: '6px 16px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#666', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => onSave(localPresets, localActiveId || localPresets[0]?.id)}
            style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 500, padding: '6px 20px', border: '1px solid ' + BRIO_GOLD, background: BRIO_GOLD, color: '#fff', cursor: 'pointer' }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DEPENDENCY CALCULATION HELPER
// ============================================================================

/**
 * Parse predecessor string (e.g. "3", "3FS+2d", "5FF", "4SS+1d") into entries.
 * Supports FS / FF / SS / SF dependency types and an optional working-day lag.
 */
function parsePredecessors(predStr) {
  if (!predStr || typeof predStr !== 'string') return [];
  return predStr.split(',').map(s => s.trim()).filter(Boolean).map(token => {
    const match = token.match(/^(\d+)\s*(FS|FF|SS|SF)?\s*(?:\+\s*(\d+)\s*d?)?$/i);
    if (!match) return null;
    return {
      rowNumber: Number(match[1]),
      type: (match[2] || 'FS').toUpperCase(),
      lag: match[3] ? Number(match[3]) : 0,
    };
  }).filter(Boolean);
}

/** Serialise parsed predecessor entries back to a string. */
function formatPredecessors(preds) {
  if (!preds || preds.length === 0) return null;
  return preds.map(p => {
    const t = (p.type && p.type !== 'FS') ? p.type : 'FS';
    const l = p.lag > 0 ? `+${p.lag}d` : '';
    return `${p.rowNumber}${t}${l}`;
  }).join(',');
}

/**
 * Apply dependency calculations: for each row with predecessors,
 * set its start date to the latest end date among predecessors + lag,
 * skipping non-working days.
 */
export function applyDependencies(allRows, timeline, settings) {
  if (!settings.dependencies.enabled) return timeline;
  const durCol = settings.dependencies.durationColumn;
  const predCol = settings.dependencies.predecessorColumn;
  const startCol = settings.dateRange.startDateColumn;
  const endCol = settings.dateRange.endDateColumn;
  const workDays = new Set(settings.workingDays.days);
  const holidaySet = new Set((settings.workingDays.holidays || []).map(h => h.date));

  const updated = JSON.parse(JSON.stringify(timeline));

  // Helper: add working days to a date (uses local date formatting to avoid UTC offset bugs)
  const addWorkingDays = (dateStr, count) => {
    let d = parseDate(dateStr);
    if (!d || !count) return dateStr;
    let remaining = count;
    while (remaining > 0) {
      d = new Date(d.getTime() + DAY_MS);
      const dow = d.getDay();
      const iso = formatDate(d);
      if (workDays.has(dow) && !holidaySet.has(iso)) remaining--;
    }
    return formatDate(d);
  };

  // Add working days INCLUDING brio holidays — a weekday counts even when it is a holiday. Used only for
  // the payment-delay chain (Invoicing/Closure links), whose lag is counted this way per product spec.
  const addWorkDaysInclHolidays = (dateStr, count) => {
    let d = parseDate(dateStr);
    if (!d || !count) return dateStr;
    let remaining = count;
    while (remaining > 0) {
      d = new Date(d.getTime() + DAY_MS);
      if (workDays.has(d.getDay())) remaining--; // holidays NOT excluded
    }
    return formatDate(d);
  };

  // Snap a date FORWARD to the next working day (no-op if it already is one). Used by start-to-start:
  // the successor inherits the predecessor's start, but a task must never begin on a weekend/holiday —
  // if the predecessor starts on one, the dependent starts on the following working day instead.
  const snapWorkingDay = (dateStr) => {
    let d = parseDate(dateStr);
    if (!d) return dateStr;
    let guard = 0;
    while (guard++ < 400 && !(workDays.has(d.getDay()) && !holidaySet.has(formatDate(d)))) {
      d = new Date(d.getTime() + DAY_MS);
    }
    return formatDate(d);
  };

  // Sanity guard: if a cascaded date exceeds 5 years from now, return null to skip writing it
  const maxSaneDate = new Date();
  maxSaneDate.setFullYear(maxSaneDate.getFullYear() + 5);
  const saneDate = (d) => {
    if (!d) return d;
    const parsed = parseDate(d);
    return parsed && parsed > maxSaneDate ? null : d;
  };

  // Build task-number → rowId map. MUST mirror computeTaskNums: count indent>0 rows but
  // skip sub-stage header rows, otherwise stored predecessor numbers (which exclude headers)
  // resolve to the wrong rows once extendedRows contains headers.
  const taskNumToId = {};
  let taskN = 0;
  allRows.forEach(r => { if (r.indent > 0 && !r.isSubStageHeader) taskNumToId[++taskN] = r.id; });

  // Ancestor (enclosing summary/parent) row-ids for each row. A row must never take a predecessor
  // that is itself or one of its ancestors: a parent's dates roll up from its children (end = latest
  // child end), so a child → parent dependency feeds the parent's end back into the child's start and
  // escalates both to the 5-year sanity cap on every pass (the runaway "1279d / 2031" summary bug).
  const ancestorsOf = {};
  {
    const stack = [];
    for (const r of allRows) {
      const ind = r.indent || 0;
      while (stack.length && stack[stack.length - 1].indent >= ind) stack.pop();
      ancestorsOf[r.id] = new Set(stack.map(s => s.id));
      stack.push({ id: r.id, indent: ind });
    }
  }

  // Process rows; repeat to a fixed point so chained / forward-referencing predecessors fully
  // resolve. Capped so a circular dependency can't escalate dates indefinitely within a call.
  const maxPasses = 50;
  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    for (let i = 0; i < allRows.length; i++) {
      const row = allRows[i];
      const rowData = updated[row.id];
      if (!rowData) continue;
      const preds = parsePredecessors(rowData[predCol]);
      if (preds.length === 0) continue;

      // For each predecessor, determine the constraining date based on dependency type
      let latestConstraint = null; // { date, lag }
      for (const pred of preds) {
        const predId = taskNumToId[pred.rowNumber];
        if (!predId) continue;
        if (predId === row.id || ancestorsOf[row.id]?.has(predId)) continue; // skip self / ancestor (circular)
        const predData = updated[predId];
        if (!predData) continue;
        const predEnd   = parseDate(predData[endCol]);
        const predStart = parseDate(predData[startCol]);

        let constraintDate = null;
        // eslint-disable-next-line default-case
        switch (pred.type) {
          case 'FS': constraintDate = predEnd;   break; // successor starts after pred ends
          case 'FF': constraintDate = predEnd;   break; // successor ends when pred ends
          case 'SS': constraintDate = predStart; break; // successor starts when pred starts
          case 'SF': constraintDate = predStart; break; // successor finishes when pred starts
        }
        if (constraintDate && (!latestConstraint || constraintDate > latestConstraint.date)) {
          // Delay-chain links (successor depends on an Invoicing/Closure row) count their lag as WORKING
          // days INCLUDING brio holidays — weekends excluded, holidays counted — not plain working days.
          latestConstraint = { date: constraintDate, lag: pred.lag, type: pred.type, delayLink: /^(invoicing_|closure_)/.test(predId) };
        }
      }

      if (!latestConstraint) continue;
      const { date, lag, type, delayLink } = latestConstraint;
      const baseDate = formatDate(date);
      const dur = rowData[durCol];

      if (type === 'FS' || type === 'SS') {
        // Successor START is constrained.
        //  • FS: successor starts the working day AFTER the predecessor finishes → base + (1 + lag).
        //  • SS: successor starts the SAME day the predecessor starts (start-to-start) → base + lag,
        //    then snapped to a working day. With lag 0 the two starts are identical when the
        //    predecessor's start is a working day; if it lands on a weekend/holiday the successor
        //    moves to the next working day (a task never begins on a non-working day).
        // A delayLink (payment-delay chain) adds working days INCLUDING holidays, so the successor lands
        // exactly `lag` working days (holidays counted) after the delay endpoint.
        const startDate = delayLink
          ? saneDate(addWorkDaysInclHolidays(baseDate, (type === 'SS' ? 0 : 1) + lag))
          : (type === 'SS')
            ? saneDate(snapWorkingDay(addWorkingDays(baseDate, lag)))
            : saneDate(addWorkingDays(baseDate, 1 + lag));
        if (!startDate) continue;
        const newEnd = (dur != null && dur > 0) ? (saneDate(addWorkingDays(startDate, Number(dur) - 1)) ?? rowData[endCol]) : rowData[endCol];
        if (rowData[startCol] !== startDate || rowData[endCol] !== newEnd) changed = true;
        rowData[startCol] = startDate;
        rowData[endCol] = newEnd;
      } else {
        // FF or SF — successor end is constrained (working days incl. holidays for a delay link, else working days)
        const endDate = delayLink ? saneDate(addWorkDaysInclHolidays(baseDate, lag)) : saneDate(addWorkingDays(baseDate, lag));
        if (!endDate) continue;
        const newStart = (dur != null && dur > 0) ? (saneDate(addWorkingDays(endDate, -(Number(dur) - 1))) ?? rowData[startCol]) : rowData[startCol];
        if (rowData[endCol] !== endDate || rowData[startCol] !== newStart) changed = true;
        rowData[endCol] = endDate;
        rowData[startCol] = newStart;
      }
    }

    // ── Parent (summary) rollup ────────────────────────────────────────────────
    // A task row with children (subsequent contiguous rows at a deeper indent) spans its
    // descendants: start = earliest child start, end = latest child end, duration = inclusive
    // working days across that span (NOT a sum of child durations). Run bottom-up so nested
    // parents roll up from already-updated inner parents, and let dependents that link to a
    // parent pick up its rolled-up dates on the next pass. Stage rows (indent 0) are excluded —
    // their dates are handled by the existing stage logic.
    for (let i = allRows.length - 1; i >= 0; i--) {
      const row = allRows[i];
      const myIndent = row.indent || 0;
      if (myIndent < 1 || row.isSubStageHeader) continue;  // stages + sub-stage headers handled elsewhere
      let minStart = null, maxEnd = null, hasKids = false;
      for (let j = i + 1; j < allRows.length && (allRows[j].indent || 0) > myIndent; j++) {
        const cd = updated[allRows[j].id];
        if (!cd) continue;
        hasKids = true;
        const cs = parseDate(cd[startCol]);
        const ce = parseDate(cd[endCol]);
        if (cs && (!minStart || cs < minStart)) minStart = cs;
        if (ce && (!maxEnd || ce > maxEnd)) maxEnd = ce;
      }
      if (!hasKids) continue;
      const rd = updated[row.id] || (updated[row.id] = {});
      // A parent's dates are purely an aggregate of its children, so when every child's date is
      // cleared the parent clears too, rather than keeping the span it last rolled up.
      const ns = minStart ? formatDate(minStart) : null;
      const ne = maxEnd ? formatDate(maxEnd) : null;
      if ((rd[startCol] ?? null) !== ns) { rd[startCol] = ns; changed = true; }
      if ((rd[endCol] ?? null) !== ne) { rd[endCol] = ne; changed = true; }
      if (!ns || !ne) {
        if (rd[durCol] != null) { rd[durCol] = null; changed = true; }
      } else {
        let dcount = 0;
        let cur = parseDate(ns);
        const end = parseDate(ne);
        while (cur && cur <= end) {
          if (workDays.has(cur.getDay()) && !holidaySet.has(formatDate(cur))) dcount++;
          cur = new Date(cur.getTime() + DAY_MS);
        }
        if (rd[durCol] !== dcount) { rd[durCol] = dcount; changed = true; }
      }
    }

    if (!changed) break;
  }
  return updated;
}

// ── Template Builder "Preview mode" ─────────────────────────────────────────
// Templates carry NO dates and no working calendar, so you can't feel a stage's collective duration or
// how dependencies interlink. Preview mode seeds a start date and cascades planned Start/End across the
// whole table from the template's OWN durations + dependencies, chaining stage-to-stage (each stage
// starts the working day after the previous stage's last activity — the app's default FS preset).
// PURE and DISPLAY-ONLY: the result is fed to the grid to render but is never persisted, so the template
// stays date-agnostic (see stripPreviewDates). Weekends are Mon–Fri; holidays aren't applied (a template
// isn't tied to an office).
function computePreviewTimeline(rawTl, startDateStr) {
  const rows = Array.isArray(rawTl?._rows) ? rawTl._rows : [];
  if (!startDateStr || rows.length === 0) return rawTl;
  const settings = {
    dependencies: { enabled: true, predecessorColumn: 'predecessors', durationColumn: 'plannedDuration' },
    dateRange: { startDateColumn: 'plannedStartDate', endDateColumn: 'plannedEndDate' },
    workingDays: { days: [1, 2, 3, 4, 5], hoursPerDay: 8, holidays: [] },
  };
  const workDays = new Set(settings.workingDays.days);
  const addWd = (dateStr, count) => {
    let d = parseDate(dateStr);
    if (!d) return dateStr;
    let remaining = count;
    while (remaining > 0) { d = new Date(d.getTime() + DAY_MS); if (workDays.has(d.getDay())) remaining--; }
    return formatDate(d);
  };
  const snapWd = (dateStr) => {
    let d = parseDate(dateStr);
    if (!d) return dateStr;
    let guard = 0;
    while (guard++ < 400 && !workDays.has(d.getDay())) d = new Date(d.getTime() + DAY_MS);
    return formatDate(d);
  };

  let tl = JSON.parse(JSON.stringify(rawTl));
  const stageIds = rows.filter(r => (r.indent || 0) === 0).map(r => r.id);
  const childrenByStage = {};
  { let cur = null; rows.forEach(r => { if ((r.indent || 0) === 0) { cur = r.id; childrenByStage[cur] = []; } else if (cur) childrenByStage[cur].push(r.id); }); }

  let prevStageMaxEnd = null;
  stageIds.forEach(sk => {
    const kids = childrenByStage[sk] || [];
    const startRowId = kids.find(id => id === `start_${sk}`) || kids[0];
    if (!startRowId) return;
    const startStr = prevStageMaxEnd ? addWd(formatDate(prevStageMaxEnd), 1) : snapWd(startDateStr);
    const srd = { ...(tl[startRowId] || {}) };
    srd.plannedStartDate = startStr;
    const dur = Number(srd.plannedDuration) || 1;
    srd.plannedEndDate = addWd(startStr, dur - 1);
    tl[startRowId] = srd;
    tl = applyDependencies(rows, tl, settings); // cascade dependents from the now-dated anchor
    let maxEnd = tl[startRowId]?.plannedEndDate ? parseDate(tl[startRowId].plannedEndDate) : null;
    kids.forEach(id => { const e = tl[id]?.plannedEndDate ? parseDate(tl[id].plannedEndDate) : null; if (e && (!maxEnd || e > maxEnd)) maxEnd = e; });
    prevStageMaxEnd = maxEnd || prevStageMaxEnd;
  });
  tl._rows = rows;
  return tl;
}

// Strip the ephemeral preview dates before a Template Builder write lands, so a template never stores
// planned dates (it stays date-agnostic) even though preview mode shows them. Durations, dependencies and
// everything else are kept, so edits made while previewing still persist and propagate to derivatives.
function stripPreviewDates(tl) {
  if (!tl || typeof tl !== 'object') return tl;
  const out = { ...tl };
  Object.keys(out).forEach(k => {
    if (k.startsWith('_')) return;
    const d = out[k];
    if (d && typeof d === 'object' && ('plannedStartDate' in d || 'plannedEndDate' in d)) {
      const { plannedStartDate, plannedEndDate, ...rest } = d; // eslint-disable-line no-unused-vars
      out[k] = rest;
    }
  });
  return out;
}

// ============================================================================
// DURATION CELL — controlled input with hover-only stepper arrows
// ============================================================================

function DurationCell({ value, inputBase, savedMark, onChange }) {
  const [localVal, setLocalVal] = useState(value ?? '');
  const [hovered, setHovered] = useState(false);

  // Sync when external value changes (e.g. from arrow clicks)
  useEffect(() => {
    setLocalVal(value ?? '');
  }, [value]);

  const commit = (num) => {
    if (num !== value) onChange(num);
  };

  const handleBlur = () => {
    const v = String(localVal).trim();
    const num = v === '' ? null : Number(v);
    commit(num);
  };

  const increment = () => {
    const next = (value || 0) + 1;
    setLocalVal(next);
    onChange(next);
  };

  const decrement = () => {
    const next = Math.max(0, (value || 0) - 1);
    setLocalVal(next);
    onChange(next);
  };

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={localVal}
        onChange={e => setLocalVal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        placeholder=""
        style={{ ...inputBase, fontSize: inputBase.fontSize || '11px', width: `${Math.max(12, (String(localVal || '').length || 1) * 8)}px`, textAlign: 'center', paddingRight: '0', paddingLeft: '0', flex: '0 0 auto' }}
        onFocus={e => { e.target.style.borderColor = BRIO_BORDER; e.target.style.background = '#fff'; }}
        onBlurCapture={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
      />
      {value != null && <span style={{ fontSize: inputBase.fontSize || '11px', fontWeight: 485, color: '#333' }}>d</span>}
      {savedMark}
      {/* Arrows positioned absolutely so they never shift the centred number */}
      <div style={{
        position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
        display: 'flex', flexDirection: 'column', gap: '0',
        visibility: hovered ? 'visible' : 'hidden',
      }}>
        <button onClick={increment} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', lineHeight: '1', fontSize: '9px', color: BRIO_GREY }}>&#9650;</button>
        <button onClick={decrement} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', lineHeight: '1', fontSize: '9px', color: BRIO_GREY }}>&#9660;</button>
      </div>
    </div>
  );
}

function PercentCell({ value, inputBase, savedMark, onChange }) {
  const [localVal, setLocalVal] = useState(value != null ? String(value) : '');
  const [hovered, setHovered] = useState(false);
  useEffect(() => { setLocalVal(value != null ? String(value) : ''); }, [value]);

  const pct = value != null ? Number(value) : null;
  const barColor = pct === 100 ? BRIO_GREEN : pct >= 50 ? BRIO_ORANGE : pct > 0 ? BRIO_RED : BRIO_BORDER;

  const handleBlur = () => {
    const v = localVal.trim();
    const num = v === '' ? null : Math.min(100, Math.max(0, Number(v)));
    if (num !== value) onChange(num);
  };

  const increment = (e) => { e.stopPropagation(); const cur = value != null ? Number(value) : 0; const next = Math.min(100, Math.round(cur / 5) * 5 + 5); onChange(next); };
  const decrement = (e) => { e.stopPropagation(); const cur = value != null ? Number(value) : 0; const next = Math.max(0, Math.floor(cur / 5) * 5 - 5); onChange(next); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0' }}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={localVal}
          onChange={e => setLocalVal(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          placeholder=""
          style={{ ...inputBase, fontSize: '11px', width: `${(String(localVal || '').length || 1) + 0.5}ch`, minWidth: '1.5ch', textAlign: value != null ? 'right' : 'center', paddingRight: '0', paddingLeft: '0', flex: '0 0 auto' }}
          onFocus={e => { e.target.style.borderColor = BRIO_BORDER; e.target.style.background = '#fff'; }}
          onBlurCapture={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
        />
        {value != null && <span style={{ fontSize: '11px', fontWeight: 300, color: '#333' }}>%</span>}
        {savedMark}
        <div style={{
          position: 'absolute', right: '-12px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', gap: '0',
          visibility: hovered ? 'visible' : 'hidden',
        }}>
          <button onClick={increment} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', lineHeight: '1', fontSize: '9px', color: BRIO_GREY }}>&#9650;</button>
          <button onClick={decrement} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 2px', lineHeight: '1', fontSize: '9px', color: BRIO_GREY }}>&#9660;</button>
        </div>
      </div>
      {pct != null && (
        <div style={{ width: '100%', padding: '0 3px', boxSizing: 'border-box' }}>
          <div style={{ width: '100%', height: '3px', background: BRIO_BORDER }}>
            <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PREDECESSOR EDITOR MODAL
// ============================================================================

const PRED_TYPES = [
  { value: 'FS', label: 'Finish-to-Start (FS)' },
  { value: 'FF', label: 'Finish-to-Finish (FF)' },
  { value: 'SS', label: 'Start-to-Start (SS)' },
  { value: 'SF', label: 'Start-to-Finish (SF)' },
];

function PredecessorEditorModal({ value, allRows, timeline, taskNumMap, hierNumMap, onSave, onClose }) {
  const [entries, setEntries] = useState(() => {
    const parsed = parsePredecessors(value || '');
    return parsed.map(p => ({ taskNum: p.rowNumber, type: p.type, lag: p.lag }));
  });

  // Non-stage rows as selectable options, keyed by stored (stable) task number.
  // Only rows currently visible (present in hierNumMap) are offered, so unselected part-stage
  // rows aren't listed. hierLabel is the flat sequential display number shown in the # column.
  const taskOptions = useMemo(() => {
    return allRows
      .filter(r => r.indent > 0 && !r.isSubStageHeader)
      .map(r => {
        const num = taskNumMap[r.id];
        const hierLabel = hierNumMap && hierNumMap[r.id];
        const name = (timeline[r.id] && timeline[r.id].taskName) || r.defaultLabel || r.name || '';
        return { num, hierLabel, name };
      })
      .filter(o => o.num != null && o.hierLabel)
      .sort((a, b) => a.num - b.num);
  }, [allRows, timeline, taskNumMap, hierNumMap]);

  // Lookup: sequential num → hierarchical label
  const numToHier = useMemo(() => {
    const map = {};
    taskOptions.forEach(o => { map[o.num] = o.hierLabel; });
    return map;
  }, [taskOptions]);

  const addEntry    = () => setEntries(prev => [...prev, { taskNum: null, type: 'FS', lag: 0 }]);
  const removeEntry = (i) => setEntries(prev => prev.filter((_, j) => j !== i));
  const updateEntry = (i, field, val) => setEntries(prev => prev.map((e, j) => j === i ? { ...e, [field]: val } : e));

  const handleSave = () => {
    const preds = entries
      .filter(e => e.taskNum != null)
      .map(e => ({ rowNumber: Number(e.taskNum), type: e.type || 'FS', lag: Number(e.lag) || 0 }));
    onSave(formatPredecessors(preds));
    onClose();
  };

  const selectStyle = {
    fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300,
    border: '1px solid ' + BRIO_BORDER, padding: '3px 4px', background: '#fff',
    color: '#333', cursor: 'pointer', outline: 'none', width: '100%',
  };
  const inputStyle = {
    fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300,
    border: '1px solid ' + BRIO_BORDER, padding: '3px 6px', outline: 'none',
    width: '60px', textAlign: 'center',
  };
  const cellPad = { padding: '5px 8px', borderBottom: '1px solid ' + BRIO_BORDER, borderRight: '1px solid ' + BRIO_BORDER, verticalAlign: 'middle' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', minWidth: '560px', maxWidth: '700px', borderRadius: '4px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#333', letterSpacing: '0.02em' }}>Edit Predecessors</span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: BRIO_GREY, lineHeight: 1, padding: '2px 4px', fontSize: '16px' }}>×</button>
        </div>

        {/* Table */}
        <div style={{ padding: '12px 16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontWeight: 300, borderTop: '1px solid ' + BRIO_BORDER, borderLeft: '1px solid ' + BRIO_BORDER }}>
            <thead>
              <tr style={{ background: BRIO_BG }}>
                {['#', 'Task Name', 'Type', 'Lag (days)', ''].map((h, i) => (
                  <th key={i} style={{ ...cellPad, textAlign: 'center', fontSize: '10px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...cellPad, textAlign: 'center', color: '#bbb', fontSize: '11px', padding: '14px' }}>
                    No predecessors. Click "+ Add" below.
                  </td>
                </tr>
              )}
              {entries.map((entry, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : BRIO_BG }}>
                  <td style={{ ...cellPad, textAlign: 'center', width: '32px', color: BRIO_GREY }}>
                    {entry.taskNum != null ? (numToHier[entry.taskNum] || entry.taskNum) : '—'}
                  </td>
                  <td style={{ ...cellPad, minWidth: '180px' }}>
                    <select value={entry.taskNum ?? ''} onChange={e => updateEntry(i, 'taskNum', e.target.value !== '' ? Number(e.target.value) : null)} style={selectStyle}>
                      <option value="">— Select task —</option>
                      {taskOptions.map(o => (
                        <option key={o.num} value={o.num}>{o.hierLabel}. {o.name || '(unnamed)'}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...cellPad, minWidth: '160px' }}>
                    <select value={entry.type} onChange={e => updateEntry(i, 'type', e.target.value)} style={selectStyle}>
                      {PRED_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td style={{ ...cellPad, textAlign: 'center', width: '80px' }}>
                    <input type="number" min="0" value={entry.lag} onChange={e => updateEntry(i, 'lag', Math.max(0, Number(e.target.value) || 0))} style={inputStyle} />
                  </td>
                  <td style={{ ...cellPad, textAlign: 'center', width: '32px' }}>
                    <button onClick={() => removeEntry(i)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#bbb', fontSize: '14px', lineHeight: 1, padding: '0 4px' }} title="Remove">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={addEntry} style={{ marginTop: '8px', padding: '4px 12px', fontSize: '11px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: 'transparent', cursor: 'pointer', color: BRIO_GREY, fontFamily: 'Raleway, sans-serif' }}>
            + Add
          </button>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '10px 16px', borderTop: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
          <button onClick={onClose} style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', color: '#555', fontFamily: 'Raleway, sans-serif' }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: 'none', background: BRIO_GOLD, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// (`SOW_STAGE_REF` — the sentinel `sowRef` for a row linked to its stage's info text rather than a
// numbered deliverable — is defined up top with the reconciliation helpers.)
//
// Link a timeline row (task or deliverable) to SOW deliverable(s) via a checklist. The SOW deliverable
// number is stage-relative (sowRef N = the Nth standard deliverable of the row's stage — see the
// reconciliation anchor), so the stage picker defaults to the row's own stage and the current links
// are pre-checked there. Saving writes the checked numbers to the row's `sowRef` (comma-joined).
//
// A row may instead be linked to the STAGE itself — its info text (the stage's `introduction`) — rather
// than a numbered deliverable. That link is stored as the sentinel `sowRef = SOW_STAGE_REF`. Because it
// isn't a positive integer, the numeric reconciliation/parsing everywhere else safely ignores it (a
// stage's info text always exists as long as the stage does, so a stage link never needs flagging).
function LinkToSowModal({ rowName, rowStageKey, sowTemplate, currentSowRef, onSave, onClose, editable = true }) {
  // Read-only when the Template Builder is in view mode (not editing): the stage picker, deliverable
  // list and "Link to stage" toggle all render as static info, and only a Close button is offered.
  const readOnly = !editable;
  const hasExistingLink = currentSowRef != null && String(currentSowRef) !== '';
  const stages = useMemo(() => (sowTemplate?.stages || []).map(s => ({
    tk: scopeKeyToTimelineKey(s.key),
    name: s.name,
    delivs: (s.standardDeliverables || []),
    // The stage's "info text description" — the introduction block shown for the stage in the SOW.
    intro: (s.introduction || s.description || '').trim(),
  })), [sowTemplate]);
  const hasRowStage = stages.some(s => s.tk === rowStageKey);
  const initSelectedFor = (tk) => {
    // Current links belong to the row's own stage (sowRef is stage-relative), so only pre-check there.
    if (tk !== rowStageKey) return new Set();
    return new Set(String(currentSowRef ?? '').split(',').map(x => parseInt(x.trim(), 10)).filter(n => n > 0));
  };
  const [stageTk, setStageTk] = useState(hasRowStage ? rowStageKey : (stages[0]?.tk || ''));
  const [selected, setSelected] = useState(() => initSelectedFor(hasRowStage ? rowStageKey : (stages[0]?.tk || '')));
  // "Link to stage" — link the row to the stage's info text instead of a deliverable. Pre-checked when
  // the row already carries the stage sentinel.
  const [linkStage, setLinkStage] = useState(() => String(currentSowRef ?? '') === SOW_STAGE_REF);
  const changeStage = (tk) => { setStageTk(tk); if (!linkStage) setSelected(initSelectedFor(tk)); };
  const toggle = (n) => setSelected(prev => { const s = new Set(prev); if (s.has(n)) s.delete(n); else s.add(n); return s; });
  const toggleLinkStage = () => setLinkStage(prev => {
    const next = !prev;
    if (next) {
      // Linking to the whole stage's info text: pin to the row's own stage and drop deliverable picks.
      if (hasRowStage) setStageTk(rowStageKey);
      setSelected(new Set());
    } else {
      setSelected(initSelectedFor(hasRowStage ? rowStageKey : stageTk));
    }
    return next;
  });
  const curStage = stages.find(s => s.tk === stageTk);
  const isRowStage = stageTk === rowStageKey;

  const handleSave = () => {
    if (linkStage) { onSave(SOW_STAGE_REF, null); onClose(); return; }
    const nums = [...selected].sort((a, b) => a - b);
    if (!nums.length) { onSave(null, null); onClose(); return; }
    // Snapshot the linked deliverables' labels as a durable fingerprint (survives later in-place edits
    // / reordering of the SOW). Captured from the deliverables the user actually checked.
    const labels = nums
      .map(n => (curStage && curStage.delivs && curStage.delivs[n - 1] && curStage.delivs[n - 1].label) || '')
      .filter(Boolean);
    onSave(nums.join(','), labels.length ? labels : null);
    onClose();
  };

  // "Remove Link" — clear the row's SOW link entirely (deliverable or stage-info), then close.
  const handleRemoveLink = () => { onSave(null, null); onClose(); };

  const selectStyle = {
    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
    border: '1px solid ' + BRIO_BORDER, padding: '5px 8px', background: '#fff',
    color: '#333', cursor: 'pointer', outline: 'none', minWidth: '220px',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', minWidth: '460px', maxWidth: '620px', borderRadius: '4px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#333', letterSpacing: '0.02em' }}>Link to SOW{rowName ? ` — ${rowName}` : ''}</span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: BRIO_GREY, lineHeight: 1, padding: '2px 4px', fontSize: '16px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px' }}>
          {!sowTemplate ? (
            <div style={{ fontSize: '12px', fontWeight: 300, color: '#999', padding: '10px 0' }}>
              Select a SOW template from the picker above the grid first.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', fontWeight: 400, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</span>
                {/* Editable dropdown in edit mode; read-only info text in view mode (mirrors the SOW selector) */}
                {readOnly ? (
                  <span style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300, color: '#555' }}>
                    {curStage?.name || '—'}{stageTk === rowStageKey ? ' (this row’s stage)' : ''}
                  </span>
                ) : (
                  <select value={stageTk} onChange={e => changeStage(e.target.value)} disabled={linkStage}
                    style={{ ...selectStyle, opacity: linkStage ? 0.5 : 1, cursor: linkStage ? 'not-allowed' : 'pointer' }}>
                    {stages.map(s => (
                      <option key={s.tk} value={s.tk}>{s.name}{s.tk === rowStageKey ? ' (this row’s stage)' : ''}</option>
                    ))}
                  </select>
                )}
                {/* Link to the stage's info text instead of a deliverable */}
                <label
                  title="Link this row to the stage’s info text (its description) instead of a deliverable in the stage."
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: readOnly ? 'default' : 'pointer', fontSize: '11.5px', fontWeight: 400, color: linkStage ? '#333' : BRIO_GREY, whiteSpace: 'nowrap' }}
                >
                  <input type="checkbox" checked={linkStage} onChange={toggleLinkStage} disabled={readOnly} style={{ accentColor: BRIO_GOLD, cursor: readOnly ? 'default' : 'pointer' }} />
                  Link to stage
                </label>
              </div>
              {!isRowStage && !linkStage && (
                <div style={{ fontSize: '11px', fontWeight: 300, color: '#b07b2a', background: '#fbf6ea', border: '1px solid #eadfc2', padding: '6px 8px', marginBottom: '10px', lineHeight: 1.4 }}>
                  The deliverable number is interpreted against this row’s own stage. Linking to a different stage is unusual — keep the default stage unless you know you need this.
                </div>
              )}
              {linkStage ? (
                /* Stage-info link — preview the stage's info text (what the row links to) */
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid ' + BRIO_BORDER, background: '#faf9f5', padding: '10px 12px' }}>
                  {hasRowStage && curStage?.intro ? (
                    <div style={{ fontSize: '12px', fontWeight: 300, color: '#444', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{curStage.intro}</div>
                  ) : (
                    <div style={{ fontSize: '12px', fontWeight: 300, color: '#bbb', textAlign: 'center', padding: '4px 0' }}>
                      {hasRowStage ? 'This stage has no info text yet.' : 'This row’s stage isn’t in the selected SOW.'}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid ' + BRIO_BORDER }}>
                  {(curStage?.delivs || []).length === 0 ? (
                    <div style={{ fontSize: '12px', fontWeight: 300, color: '#bbb', padding: '14px', textAlign: 'center' }}>No deliverables in this stage.</div>
                  ) : (
                    (curStage?.delivs || []).map((d, i) => {
                      const n = i + 1;
                      const checked = selected.has(n);
                      return (
                        <label
                          key={d.id || n}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '7px 10px', cursor: readOnly ? 'default' : 'pointer', fontSize: '12px', fontWeight: 300, color: '#333', borderBottom: '1px solid ' + BRIO_BORDER, background: checked ? 'rgba(159,144,42,0.07)' : '#fff' }}
                          onMouseEnter={e => { if (!checked && !readOnly) e.currentTarget.style.background = '#f8f6f0'; }}
                          onMouseLeave={e => { if (!checked) e.currentTarget.style.background = '#fff'; }}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggle(n)} disabled={readOnly} style={{ accentColor: BRIO_GOLD, cursor: readOnly ? 'default' : 'pointer', marginTop: '2px' }} />
                          <span><span style={{ color: BRIO_GREY, fontWeight: 400 }}>{n}.</span> {d.label || '(unnamed deliverable)'}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
              <div style={{ fontSize: '10.5px', fontWeight: 300, color: '#aaa', marginTop: '8px' }}>
                {readOnly
                  ? 'Read-only — switch the template to Edit mode to change this link.'
                  : (linkStage
                    ? 'This row links to the stage’s info text — its "(stage)" link. Uncheck "Link to stage" to link deliverables instead.'
                    : 'Checked deliverables become this row’s "(N)" link. Uncheck all to remove the link.')}
              </div>
            </>
          )}
        </div>

        {/* Footer — Remove Link sits on the left; Cancel/Save (or Close when read-only) on the right */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '10px 16px', borderTop: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
          <div>
            {editable && hasExistingLink && sowTemplate && (
              <button onClick={handleRemoveLink}
                title="Unlink this row from the Scope of Work"
                style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: '1px solid #e0c0c0', background: '#fff', cursor: 'pointer', color: '#b05050', fontFamily: 'Raleway, sans-serif' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fdf2f2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >Remove Link</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={onClose} style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', color: '#555', fontFamily: 'Raleway, sans-serif' }}>{editable ? 'Cancel' : 'Close'}</button>
            {editable && (
              <button onClick={handleSave} disabled={!sowTemplate} style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: 'none', background: sowTemplate ? BRIO_GOLD : '#ccc', color: '#fff', cursor: sowTemplate ? 'pointer' : 'default', fontFamily: 'Raleway, sans-serif' }}>Save</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MOM PANEL — Minutes of Meeting side panel (rich-text editor)
// ============================================================================

// Builds CSS for the contentEditable document area using the UI font (Raleway)
function buildMOMEditorCSS() {
  const fontStack = 'Raleway, sans-serif';
  const bodySize = 13;
  const h2Size   = 14;
  const h1Size   = 16;
  const tblSize  = Math.max(10, bodySize - 1);
  const thSize   = Math.max(9, tblSize - 1);
  return `
.mom-editor { outline: none; min-height: 300px; font-family: ${fontStack}; font-size: ${bodySize}px; font-weight: 300; color: #222; line-height: 1.7; }
.mom-editor b, .mom-editor strong { font-weight: 700; }
.mom-editor i, .mom-editor em { font-style: italic; }
.mom-editor h1 { font-size: ${h1Size}px; font-weight: 600; margin: 0 0 10px; color: #222; }
.mom-editor h2 { font-size: ${h2Size}px; font-weight: 600; margin: 18px 0 8px; color: #333; }
.mom-editor h3 { font-size: ${bodySize}px; font-weight: 600; margin: 14px 0 6px; color: #555; text-transform: uppercase; letter-spacing: 0.05em; }
.mom-editor p  { margin: 0 0 6px; }
.mom-editor hr { border: none; border-top: 1px solid #ddd; margin: 14px 0; }
.mom-editor ul, .mom-editor ol { padding-left: 26px; margin: 4px 0 6px; }
.mom-editor ul ul, .mom-editor ul ol,
.mom-editor ol ul, .mom-editor ol ol { padding-left: 26px; margin: 2px 0; }
.mom-editor ul { list-style-type: disc; }
.mom-editor ul ul { list-style-type: circle; }
.mom-editor ul ul ul { list-style-type: square; }
.mom-editor ol { list-style-type: decimal; }
.mom-editor ol ol { list-style-type: lower-alpha; }
.mom-editor ol ol ol { list-style-type: lower-roman; }
.mom-editor li { margin-bottom: 3px; line-height: 1.6; }
.mom-editor table { border-collapse: collapse; width: 100%; margin: 10px 0 14px; font-size: ${tblSize}px; }
.mom-editor th { background: transparent; color: #222; font-weight: 400; text-align: left; padding: 5px 8px; border: 1px solid #D1D5DB; font-size: ${thSize}px; position: relative; }
.mom-editor td { padding: 5px 8px; border: 1px solid #D1D5DB; vertical-align: top; position: relative; }
.mom-editor td:focus, .mom-editor th:focus { outline: 2px solid rgba(159,144,42,0.35); outline-offset: -1px; }
.mom-editor img { max-width: 100%; height: auto; display: block; margin: 6px 0; border-radius: 2px; }
.mom-table--editing { outline: 2px solid rgba(159,144,42,0.5); outline-offset: 2px; }
.mom-editor td, .mom-editor th { cursor: default; }
.mom-cell--selected { background-color: rgba(159,144,42,0.12) !important; }
`;
}

// ── Hierarchical list helpers ─────────────────────────────────────────────────
const _ROMAN_U = new Set(['I','V','X','L','C','D','M']);
const _ROMAN_L = new Set(['i','v','x','l','c','d','m']);
function _romanVal(s) {
  const M = {I:1,V:5,X:10,L:50,C:100,D:500,M:1000,i:1,v:5,x:10,l:50,c:100,d:500,m:1000};
  let n = 0, prev = 0;
  for (let k = s.length - 1; k >= 0; k--) { const v = M[s[k]]||0; n += v < prev ? -v : v; prev = v; }
  return n;
}
function detectListMarker(text) {
  const t = text.trim();
  if (!t) return null;
  if (t === '-' || t === '•' || t === '*') return { tag: 'ul', style: 'disc' };
  if (t === '○') return { tag: 'ul', style: 'circle' };
  if (!t.endsWith('.')) return null;
  const body = t.slice(0, -1);
  if (/^\d+$/.test(body)) return { tag: 'ol', style: 'decimal' };
  if (/^[A-Z]+$/.test(body)) {
    const allR = [...body].every(c => _ROMAN_U.has(c));
    return allR && _romanVal(body) > 0
      ? { tag: 'ol', style: 'upper-roman' }
      : { tag: 'ol', style: 'upper-alpha' };
  }
  if (/^[a-z]+$/.test(body)) {
    const allR = [...body].every(c => _ROMAN_L.has(c));
    return allR && _romanVal(body) > 0
      ? { tag: 'ol', style: 'lower-roman' }
      : { tag: 'ol', style: 'lower-alpha' };
  }
  return null;
}
function getListNestingDepth(node, editorEl) {
  let depth = 0;
  let el = node?.nodeType === 3 ? node.parentElement : node;
  while (el && el !== editorEl) { if (el.tagName === 'UL' || el.tagName === 'OL') depth++; el = el.parentElement; }
  return depth;
}

export function MOMPanel({ projectId, taskKey, rowName, onClose, onDeleteRow, forceNew, canEdit, isAdmin, isSupervisor, momHook, projectTasksHook, contractCurrency }) {
  const { documents, templates, loading: momLoading, ensureDefaultTemplate, createDocument, updateDocument, deleteDocument, createTemplate, updateTemplate, deleteTemplate } = momHook;
  const canManageTemplates = isAdmin || isSupervisor;

  // ── Letterhead header image (from invoice branding settings) ─────────────
  const { invoiceBranding } = useSettings();
  const officeKey = contractCurrency === 'EUR' ? 'rotterdam' : 'mumbai';
  const headerImageUrl = resolveBrandingImageSrc(invoiceBranding?.offices?.[officeKey]?.headerImage);

  const excelFontStack  = 'Raleway, sans-serif';
  const excelFontSize   = 13;

  // ── Current document state ────────────────────────────────────────────────
  // forceNew: always open as a blank new doc, even if one exists for this taskKey
  const existingDoc = forceNew ? null : (documents.find(d => d.task_key === taskKey) || null);
  const [currentDocId, setCurrentDocId]       = useState(existingDoc?.id || null);
  const [docTitle,     setDocTitle]            = useState(existingDoc?.title || '');
  const [activeTemplateId, setActiveTemplateId] = useState(existingDoc?.template_id || null);

  // ── View / edit mode ──────────────────────────────────────────────────────
  // New docs always start in edit mode; saved docs open in view mode.
  const [isEditing, setIsEditing] = useState(!existingDoc);

  // ── Rich-text editor refs ─────────────────────────────────────────────────
  const editorRef    = useRef(null);
  const fileInputRef = useRef(null); // DOCX import
  const pdfInputRef  = useRef(null); // PDF import
  const imageInputRef = useRef(null); // image insert
  const editorInited = useRef(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving,       setSaving]      = useState(false);
  const [saveMsg,      setSaveMsg]     = useState('');
  const [showDropdown, setShowDropdown] = useState(null); // 'notes'|'template'|'import'|'export'|'createTpl'|null
  const [createTplName, setCreateTplName] = useState('');
  const [isSent,       setIsSent]      = useState(false);
  const [showSendPopup, setShowSendPopup] = useState(false);

  // ── Table edit mode state ─────────────────────────────────────────────────
  const [tableCtxMenu,  setTableCtxMenu]  = useState(null);
  const [editedTable,   setEditedTable]   = useState(null);
  const [tableBounds,   setTableBounds]   = useState(null);
  const [bulletCtxMenu, setBulletCtxMenu] = useState(null);
  const tableSelectionRef = useRef(null);
  const levelStyleRef    = useRef({});  // depth → last-used list-style-type
  const hdrFieldsRef     = useRef({});  // key → DOM element for contentEditable header fields

  // ── Structured header fields (Date, Project, Attendees, Subject) ──────────
  const initMeta = (() => {
    const m = existingDoc?.content_html?.match(/^<!--MOM_META:([\s\S]*?)-->\n?/);
    if (!m) return {};
    try { return JSON.parse(m[1]); } catch { return {}; }
  })();
  const [momDate,      setMomDate]      = useState(initMeta.date    || '');
  const [momProject,   setMomProject]   = useState(initMeta.project || '');
  const [momAttendees, setMomAttendees] = useState(() => {
    const norm = (arr) => arr.map(a => ({ label: a.label || '', value: a.value || '' }));
    if (Array.isArray(initMeta.attendees) && initMeta.attendees.length > 0) return norm(initMeta.attendees);
    const migrated = [];
    if (initMeta.brio       !== undefined) migrated.push({ label: 'Architecture BRIO', value: initMeta.brio       || '' });
    if (initMeta.client     !== undefined) migrated.push({ label: 'Client',            value: initMeta.client     || '' });
    if (initMeta.pmc        !== undefined) migrated.push({ label: 'PMC',               value: initMeta.pmc        || '' });
    if (initMeta.contractor !== undefined) migrated.push({ label: 'Contractor',        value: initMeta.contractor || '' });
    return norm(migrated.length > 0 ? migrated : [
      { label: 'Architecture BRIO', value: '' },
      { label: 'Client',            value: '' },
      { label: 'PMC',               value: '' },
      { label: 'Contractor',        value: '' },
    ]);
  });
  const [momSubject,        setMomSubject]        = useState(initMeta.subject        || '');
  const [momDateLabel,      setMomDateLabel]      = useState(initMeta.dateLabel      || 'Date:');
  const [momProjectLabel,   setMomProjectLabel]   = useState(initMeta.projectLabel   || 'Project:');
  const [momAttendeesLabel, setMomAttendeesLabel] = useState(initMeta.attendeesLabel || 'Attendees:');
  const [momSubjectLabel,   setMomSubjectLabel]   = useState(initMeta.subjectLabel   || 'Subject:');
  const [listDropdown,     setListDropdown]     = useState(false);
  const [numDropdown,      setNumDropdown]      = useState(false);
  const [fontSizeDropdown, setFontSizeDropdown] = useState(false);
  const [isGlobalTpl,  setIsGlobalTpl]  = useState(false);
  const [momPageCount, setMomPageCount] = useState(1);
  const momPageRef = useRef(null);

  const btnSm = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '3px 9px', border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', borderRadius: '2px', color: '#444', whiteSpace: 'nowrap' };

  // ── Initialize editor — wait for templates to finish loading so global templates are visible ──
  useEffect(() => {
    if (editorInited.current || !editorRef.current) return;
    if (existingDoc?.content_html) {
      editorInited.current = true;
      editorRef.current.innerHTML = existingDoc.content_html.replace(/^<!--MOM_META:[\s\S]*?-->\n?/, '');
    } else {
      if (momLoading) return; // wait until templates are fetched
      editorInited.current = true;
      ensureDefaultTemplate(templates).then(tpl => {
        if (editorRef.current) {
          editorRef.current.innerHTML = tpl?.content_html || DEFAULT_TEMPLATE_HTML;
          if (tpl) setActiveTemplateId(tpl.id);
        }
      });
    }
  }, [momLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inject MOM editor styles (refresh on each panel open to pick up Excel font settings) ──
  useEffect(() => {
    let el = document.getElementById('mom-editor-style');
    if (!el) {
      el = document.createElement('style');
      el.id = 'mom-editor-style';
      document.head.appendChild(el);
    }
    el.textContent = buildMOMEditorCSS();
  }, [excelFontStack]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initialize contentEditable header fields from state ──────────────────────
  // Runs every time edit mode is entered so that spans freshly mounted on
  // entering edit mode are populated with the saved values from state.
  useEffect(() => {
    const editMode = isEditing || !currentDocId;
    if (!editMode) return; // spans only exist while editing
    const f = hdrFieldsRef.current;
    if (f.date)           f.date.innerHTML           = momDate           || '';
    if (f.project)        f.project.innerHTML        = momProject        || '';
    if (f.subject)        f.subject.innerHTML        = momSubject        || '';
    if (f.dateLabel)      f.dateLabel.innerHTML      = momDateLabel      || 'Date:';
    if (f.projectLabel)   f.projectLabel.innerHTML   = momProjectLabel   || 'Project:';
    if (f.attendeesLabel) f.attendeesLabel.innerHTML = momAttendeesLabel || 'Attendees:';
    if (f.subjectLabel)   f.subjectLabel.innerHTML   = momSubjectLabel   || 'Subject:';
    momAttendees.forEach((att, i) => {
      if (f[`al${i}`]) f[`al${i}`].innerHTML = att.label || '';
      if (f[`av${i}`]) f[`av${i}`].innerHTML = att.value || '';
    });
  }, [isEditing, currentDocId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── A4 page count: track content height via ResizeObserver ──────────────────
  useEffect(() => {
    const el = momPageRef.current;
    if (!el) return;
    const A4_H = 1122;
    const update = () => setMomPageCount(Math.max(1, Math.ceil(el.scrollHeight / A4_H)));
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  // ── Table edit mode: keep + button positions in sync with table rect ────────
  useLayoutEffect(() => {
    if (!editedTable) { setTableBounds(null); return; }
    const update = () => {
      const rect = editedTable.getBoundingClientRect();
      setTableBounds({ top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [editedTable]);

  // ── Table edit mode: exit on click-outside or Escape ─────────────────────
  useEffect(() => {
    if (!editedTable) return;
    const onMouseDown = (e) => {
      if (!editedTable.contains(e.target) && !e.target.closest('.mom-tbl-add-btn')) {
        editedTable.classList.remove('mom-table--editing');
        setEditedTable(null);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { editedTable.classList.remove('mom-table--editing'); setEditedTable(null); }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('mousedown', onMouseDown); document.removeEventListener('keydown', onKeyDown); };
  }, [editedTable]);

  // ── Bullet right-click highlight ──────────────────────────────────────────
  useEffect(() => {
    if (!bulletCtxMenu?.li) return;
    const el = bulletCtxMenu.li;
    const prev = el.style.backgroundColor;
    el.style.backgroundColor = 'rgba(159,144,42,0.12)';
    return () => { el.style.backgroundColor = prev; };
  }, [bulletCtxMenu]);

  // ── Add bullet text to project tasks ─────────────────────────────────────
  const handleAddBulletToTask = useCallback(() => {
    if (!bulletCtxMenu?.li || !projectTasksHook) { setBulletCtxMenu(null); return; }
    const text = bulletCtxMenu.li.textContent?.trim() || '';
    if (text) projectTasksHook.addTask({ task: text, categories: ['MOM_Linked'] });
    setBulletCtxMenu(null);
  }, [bulletCtxMenu, projectTasksHook]);

  // ── Format commands ───────────────────────────────────────────────────────
  const fmt = useCallback((cmd, val = null) => {
    const sel = window.getSelection();
    const savedRange = (sel && sel.rangeCount > 0) ? sel.getRangeAt(0).cloneRange() : null;
    // Only force-focus the editor when no contentEditable element is already active
    // (header fields are contentEditable too, and we must not steal their selection)
    const activeEl = document.activeElement;
    if (!activeEl?.isContentEditable || activeEl === document.body) {
      editorRef.current?.focus();
    }
    if (savedRange) { sel.removeAllRanges(); sel.addRange(savedRange); }
    // Use element tags (b/i/u) rather than inline CSS styles so that our CSS rules
    // like font-weight: 300 on .mom-editor don't cause queryCommandState to misread
    // the bold state — this fixes bold toggling inside table cells.
    document.execCommand('styleWithCSS', false, false);
    document.execCommand(cmd, false, val);
  }, []);

  const LIST_TYPES = [
    { label: '• Disc',     style: 'disc',        tag: 'ul' },
    { label: '○ Circle',   style: 'circle',      tag: 'ul' },
    { label: '■ Square',   style: 'square',      tag: 'ul' },
    { label: '1. Decimal', style: 'decimal',     tag: 'ol' },
    { label: 'A. Alpha',   style: 'upper-alpha', tag: 'ol' },
    { label: 'a. alpha',   style: 'lower-alpha', tag: 'ol' },
    { label: 'I. Roman',   style: 'upper-roman', tag: 'ol' },
    { label: 'i. roman',   style: 'lower-roman', tag: 'ol' },
  ];

  const changeLevelStyle = useCallback((style, tag) => {
    const sel = window.getSelection();
    const saved = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    editorRef.current?.focus();
    if (saved) { sel.removeAllRanges(); sel.addRange(saved); }
    const r = sel?.getRangeAt(0);
    if (!r) { setListDropdown(false); return; }
    const nd = r.startContainer;
    const curList = (nd.nodeType === 3 ? nd.parentElement : nd)?.closest('ul, ol');
    const curTag = curList?.tagName?.toLowerCase() || null;
    if (!curList) {
      document.execCommand(tag === 'ol' ? 'insertOrderedList' : 'insertUnorderedList', false, null);
    } else if (curTag !== tag) {
      document.execCommand(curTag === 'ol' ? 'insertOrderedList' : 'insertUnorderedList', false, null);
      document.execCommand(tag === 'ol' ? 'insertOrderedList' : 'insertUnorderedList', false, null);
    }
    const r2 = sel.getRangeAt(0);
    const n2 = r2.startContainer;
    const list2 = (n2.nodeType === 3 ? n2.parentElement : n2)?.closest('ul, ol');
    if (list2) {
      list2.style.listStyleType = style;
      levelStyleRef.current[getListNestingDepth(list2, editorRef.current)] = style;
    }
    setListDropdown(false);
  }, []);

  const insertTable = useCallback(() => {
    editorRef.current?.focus();
    document.execCommand('insertHTML', false,
      `<table><thead><tr><th>Header 1</th><th>Header 2</th><th>Header 3</th></tr></thead>` +
      `<tbody><tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>` +
      `<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr></tbody></table><p><br></p>`
    );
  }, []);

  // ── Save document ─────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;
    const meta = JSON.stringify({ date: momDate, project: momProject, attendees: momAttendees, subject: momSubject, dateLabel: momDateLabel, projectLabel: momProjectLabel, attendeesLabel: momAttendeesLabel, subjectLabel: momSubjectLabel });
    const contentHtml = `<!--MOM_META:${meta}-->\n${editorRef.current.innerHTML}`;
    setSaving(true);
    setSaveMsg('');
    try {
      if (currentDocId) {
        await updateDocument(currentDocId, { title: docTitle, contentHtml, templateId: activeTemplateId });
      } else {
        const doc = await createDocument({ taskKey, templateId: activeTemplateId, title: docTitle, contentHtml });
        setCurrentDocId(doc.id);
      }
      setSaveMsg('Saved');
      setIsEditing(false); // return to view mode after save
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (e) {
      setSaveMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [currentDocId, docTitle, activeTemplateId, taskKey, momDate, momProject, momAttendees, momSubject, updateDocument, createDocument]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Delete document ───────────────────────────────────────────────────────
  const handleDeleteDoc = useCallback(async () => {
    if (!currentDocId) return;
    if (!window.confirm('Delete this meeting note permanently?')) return;
    try {
      await deleteDocument(currentDocId);
      if (taskKey?.startsWith('meeting_') && onDeleteRow) {
        await onDeleteRow(taskKey);
      }
      onClose();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  }, [currentDocId, deleteDocument, onClose, taskKey, onDeleteRow]);

  // ── Switch between existing documents ─────────────────────────────────────
  const handleSwitchDoc = useCallback((docId) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc || !editorRef.current) return;
    setCurrentDocId(doc.id);
    setDocTitle(doc.title || '');
    setActiveTemplateId(doc.template_id || null);
    editorRef.current.innerHTML = doc.content_html || '';
    setShowDropdown(null);
    setIsEditing(false); // view mode for existing docs
    setIsSent(false);
  }, [documents]);

  // ── New note (blank from default template) ────────────────────────────────
  const handleNewNote = useCallback(async () => {
    setCurrentDocId(null);
    setDocTitle('');
    const tpl = await ensureDefaultTemplate(templates);
    if (editorRef.current) editorRef.current.innerHTML = tpl?.content_html || DEFAULT_TEMPLATE_HTML;
    setActiveTemplateId(tpl?.id || null);
    setShowDropdown(null);
    setIsEditing(true); // new notes always start in edit mode
  }, [ensureDefaultTemplate, templates]);

  // ── Load a template into editor ───────────────────────────────────────────
  const handleLoadTemplate = useCallback((tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl || !editorRef.current) return;
    const currentContent = editorRef.current.innerHTML.replace(/<br\s*\/?>/gi, '').trim();
    if (currentContent && !window.confirm('Load this template? Unsaved content will be replaced.')) return;
    editorRef.current.innerHTML = tpl.content_html || '';
    setActiveTemplateId(tpl.id);
    setShowDropdown(null);
  }, [templates]);

  // ── Create template from current content ─────────────────────────────────
  const handleCreateTemplate = useCallback(async () => {
    if (!createTplName.trim() || !editorRef.current) return;
    try {
      const tpl = await createTemplate({ name: createTplName.trim(), contentHtml: editorRef.current.innerHTML, isGlobal: isGlobalTpl });
      setActiveTemplateId(tpl.id);
      setCreateTplName('');
      setIsGlobalTpl(false);
      setShowDropdown(null);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  }, [createTplName, isGlobalTpl, createTemplate]);

  // ── Set / toggle default template ────────────────────────────────────────
  const handleSetDefaultTemplate = useCallback(async (tplId, e) => {
    e.stopPropagation();
    const tpl = templates.find(t => t.id === tplId);
    const willBeDefault = !tpl?.is_default;
    try {
      if (willBeDefault) {
        const oldDefault = templates.find(t => t.is_default && t.id !== tplId);
        if (oldDefault) await updateTemplate(oldDefault.id, { isDefault: false });
      }
      await updateTemplate(tplId, { isDefault: willBeDefault });
    } catch (e) {
      alert('Error: ' + e.message);
    }
  }, [templates, updateTemplate]);

  // ── Structured fields HTML for export ────────────────────────────────────
  const buildMetaHtml = useCallback((bodyFontPx = 12) => {
    const s1 = getExcelStyle('style1');
    const fontStack = `'${s1.name}', Raleway, Georgia, serif`;
    const fs = `font-size:${bodyFontPx}px;font-family:${fontStack};font-weight:300;`;
    // Fields store HTML (user may have applied bold/italic via toolbar) — use raw values
    const raw = (s) => String(s || '');
    const momtSize = Math.round((bodyFontPx + 4) * 0.8);
    const momt = `<div style="text-align:right;font-size:${momtSize}px;font-weight:300;letter-spacing:0.15em;color:#444;margin-bottom:14px;">M.O.M</div>`;
    const attendeeRows = momAttendees.map(a => {
      const lb = raw(a.label); const val = raw(a.value);
      const labelText = lb.replace(/<[^>]+>/g, '').trim();
      return `<p style="margin:0 0 2px"><span style="font-weight:300;">${lb}${labelText ? ':' : ''}</span> <span style="font-weight:600;">${val}</span></p>`;
    }).join('');
    return `<div style="${fs}line-height:1.8;margin-bottom:18px;">${momt}
  <p style="margin:0 0 2px"><span style="font-weight:400;">${raw(momDateLabel)}</span> <span style="font-weight:600;">${raw(momDate)}</span></p>
  <p style="margin:0 0 13px"><span style="font-weight:400;">${raw(momProjectLabel)}</span> <span style="font-weight:600;">${raw(momProject)}</span></p>
  <p style="margin:0 0 2px;font-weight:400;">${raw(momAttendeesLabel)}</p>
  ${attendeeRows}
  <p style="margin:3px 0 0;text-align:center"><span style="font-weight:600;">${raw(momSubjectLabel)} ${raw(momSubject)}</span></p>
</div>`;
  }, [momDate, momProject, momAttendees, momSubject, momDateLabel, momProjectLabel, momAttendeesLabel, momSubjectLabel]);

  // ── Export PDF (opens print dialog in new tab) ────────────────────────────
  const handleExportPDF = useCallback(() => {
    const content = editorRef.current?.innerHTML || '';
    const css = buildMOMEditorCSS();
    const s1 = getExcelStyle('style1');
    const fontFamily = `'${s1.name}', Raleway, Georgia, serif`;
    const title = docTitle || 'Meeting Notes';
    const win = window.open('', '_blank');
    if (!win) { alert('Allow popups to export PDF.'); return; }
    const hdrHtml = headerImageUrl
      ? `<div style="margin:0;padding:0;line-height:0"><img src="${headerImageUrl}" style="width:100%;height:auto;display:block;" /></div>`
      : '';
    const contentPad = headerImageUrl ? '28px 40px 72px' : '40px 60px 72px';
    const metaHtml = buildMetaHtml();
    const pageNumCss = `
@page { margin-bottom: 60pt; }
@media print {
  body::after {
    content: "Page " counter(page) " of " counter(pages);
    position: fixed; bottom: 18pt; right: 24pt;
    font-size: 8pt; color: #888; font-family: ${fontFamily};
  }
}`;
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600&display=swap" rel="stylesheet"><style>body{margin:0;background:#fff;font-family:${fontFamily};}@media print{body{margin:0;}}${css}${pageNumCss}</style></head><body>${hdrHtml}<div style="padding:${contentPad}">${metaHtml}<div class="mom-editor">${content}</div></div><script>window.onload=function(){window.print();}<\/script></body></html>`);
    win.document.close();
  }, [docTitle, headerImageUrl, buildMetaHtml]);

  // ── Export Word-compatible .doc ───────────────────────────────────────────
  const handleExportDOCX = useCallback(() => {
    const content = editorRef.current?.innerHTML || '';
    const name = docTitle || 'Meeting Notes';
    const hdrHtml = headerImageUrl
      ? `<div style="margin:0;padding:0"><img src="${headerImageUrl}" style="width:100%;display:block;" /></div>`
      : '';
    const metaHtml = buildMetaHtml();
    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>${name}</title></head><body>${hdrHtml}${metaHtml}${content}</body></html>`;
    const blob = new Blob([html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = name + '.doc'; a.click();
    URL.revokeObjectURL(url);
  }, [docTitle, headerImageUrl, buildMetaHtml]);

  // ── Import PDF (text extraction via pdfjs-dist) ───────────────────────────
  const handleImportPDF = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editorRef.current) return;
    try {
      const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      let html = '';
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const tc = await page.getTextContent();
        let lastY = null, block = '';
        tc.items.forEach(item => {
          if (lastY !== null && Math.abs(item.transform[5] - lastY) > 8) { html += `<p>${block}</p>`; block = ''; }
          block += item.str;
          lastY = item.transform[5];
        });
        if (block) html += `<p>${block}</p>`;
        if (p < pdf.numPages) html += '<hr>';
      }
      const cur = editorRef.current.innerHTML.replace(/<br\s*\/?>/gi, '').trim();
      if (cur && !window.confirm('Import PDF? Current content will be replaced.')) { e.target.value = ''; return; }
      editorRef.current.innerHTML = html || '<p>No text found in PDF.</p>';
      setActiveTemplateId(null);
    } catch (err) {
      alert('Failed to import PDF: ' + err.message);
    }
    e.target.value = '';
  }, []);

  // ── Insert image from file ────────────────────────────────────────────────
  const handleImageSelected = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !editorRef.current) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, `<img src="${ev.target.result}" alt="${file.name}" style="max-width:100%;height:auto;display:block;margin:8px 0;border-radius:2px;" /><p><br></p>`);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  // ── Context menu: bullet list items + table borders ──────────────────────
  const handleEditorContextMenu = useCallback((e) => {
    if (!canEdit) return;

    // 1. Bullet / numbered list item — highest priority
    const li = e.target.closest('li');
    if (li && editorRef.current?.contains(li)) {
      e.preventDefault();
      setBulletCtxMenu({ x: e.clientX, y: e.clientY, li });
      return;
    }

    // 2. Table — fire on any click within a cell
    const td = e.target.closest('td, th');
    if (!td) return;
    const table = td.closest('table');
    if (!table || !editorRef.current?.contains(table)) return;
    e.preventDefault();
    setTableCtxMenu({ x: e.clientX, y: e.clientY, table, td });
  }, [canEdit]);

  // ── Enter table edit mode (shows + buttons) ───────────────────────────────
  const handleEnterTableEdit = useCallback((table) => {
    if (editedTable && editedTable !== table) editedTable.classList.remove('mom-table--editing');
    table.classList.add('mom-table--editing');
    setEditedTable(table);
    setTableCtxMenu(null);
  }, [editedTable]);

  // ── Add row / column to the table currently in edit mode ─────────────────
  const handleAddTableRow = useCallback(() => {
    if (!editedTable) return;
    const numCols = editedTable.rows[0]?.cells.length || 1;
    const tbody = editedTable.querySelector('tbody') || editedTable;
    const newRow = document.createElement('tr');
    for (let i = 0; i < numCols; i++) {
      const cell = document.createElement('td');
      cell.innerHTML = '&nbsp;';
      newRow.appendChild(cell);
    }
    tbody.appendChild(newRow);
    const rect = editedTable.getBoundingClientRect();
    setTableBounds({ top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left, width: rect.width });
  }, [editedTable]);

  const handleAddTableColumn = useCallback(() => {
    if (!editedTable) return;
    const hasHead = !!editedTable.querySelector('thead');
    Array.from(editedTable.rows).forEach((row, ri) => {
      const cell = (hasHead && ri === 0) ? document.createElement('th') : document.createElement('td');
      cell.innerHTML = '&nbsp;';
      row.appendChild(cell);
    });
    const rect = editedTable.getBoundingClientRect();
    setTableBounds({ top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left, width: rect.width });
  }, [editedTable]);

  // ── Clear table row/column selection highlights ───────────────────────────
  const clearTableSelection = useCallback(() => {
    if (tableSelectionRef.current) {
      tableSelectionRef.current.forEach(c => c.classList.remove('mom-cell--selected'));
      tableSelectionRef.current = null;
    }
  }, []);

  // ── Apply table row/column selection highlights ───────────────────────────
  const applyTableSelection = useCallback((cells) => {
    clearTableSelection();
    cells.forEach(c => c.classList.add('mom-cell--selected'));
    tableSelectionRef.current = cells;
  }, [clearTableSelection]);

  // ── Quick table ops from context menu (no edit mode needed) ───────────────
  const handleTableOp = useCallback((op) => {
    if (!tableCtxMenu) return;
    const { table, td } = tableCtxMenu;
    const row = td?.closest('tr');
    setTableCtxMenu(null);
    if (!row || !table) return;
    const numCols = table.rows[0]?.cells.length || 1;
    const colIdx = Array.from(row.cells).indexOf(td);
    const hasHead = !!table.querySelector('thead');
    if (op === 'insertRowAbove' || op === 'insertRowBelow') {
      const newRow = document.createElement('tr');
      for (let i = 0; i < numCols; i++) { const c = document.createElement('td'); c.innerHTML = '&nbsp;'; newRow.appendChild(c); }
      if (op === 'insertRowAbove') row.before(newRow); else row.after(newRow);
    } else if (op === 'insertColLeft' || op === 'insertColRight') {
      Array.from(table.rows).forEach((r, ri) => {
        const cell = (hasHead && ri === 0) ? document.createElement('th') : document.createElement('td');
        cell.innerHTML = '&nbsp;';
        const ref = r.cells[op === 'insertColRight' ? colIdx + 1 : colIdx];
        if (ref) r.insertBefore(cell, ref); else r.appendChild(cell);
      });
    } else if (op === 'deleteRow') {
      if (table.rows.length > 1) row.remove();
    } else if (op === 'deleteCol') {
      if (table.rows[0]?.cells.length > 1) Array.from(table.rows).forEach(r => r.cells[colIdx]?.remove());
    } else if (op === 'selectRow') {
      applyTableSelection(Array.from(row.cells));
    } else if (op === 'selectCol') {
      applyTableSelection(Array.from(table.rows).map(r => r.cells[colIdx]).filter(Boolean));
    }
  }, [tableCtxMenu, applyTableSelection]);

  // ── Table resize + margin-click row/col selection ─────────────────────────
  const handleTableResizeMouseDown = useCallback((e) => {
    const td = e.target.closest('td, th');
    if (!td) { clearTableSelection(); return; }
    const table = td.closest('table');
    if (!table || !editorRef.current?.contains(table)) return;
    const rect = td.getBoundingClientRect();
    const GRIP = 7;
    const SEL  = 10;
    const row     = td.closest('tr');
    const allRows = Array.from(table.rows);
    const colIdx  = Array.from(row.cells).indexOf(td);
    const rowIdx  = allRows.indexOf(row);
    const isFirstCol = colIdx === 0;
    const isFirstRow = rowIdx === 0;

    // Selection zones: left edge of first col = select row; top edge of first row = select col
    if (isFirstCol && e.clientX - rect.left < SEL) {
      e.preventDefault();
      applyTableSelection(Array.from(row.cells));
      return;
    }
    if (isFirstRow && e.clientY - rect.top < SEL) {
      e.preventDefault();
      applyTableSelection(allRows.map(r => r.cells[colIdx]).filter(Boolean));
      return;
    }

    // Column resize: right edge or left edge of non-first column
    const isColResizeRight = e.clientX >= rect.right - GRIP;
    const isColResizeLeft  = !isFirstCol && e.clientX - rect.left < GRIP;
    const isColResize = isColResizeRight || isColResizeLeft;

    // Row resize: bottom edge or top edge of non-first row
    const isRowResizeBottom = e.clientY >= rect.bottom - GRIP;
    const isRowResizeTop    = !isFirstRow && e.clientY - rect.top < GRIP;
    const isRowResize = !isColResize && (isRowResizeBottom || isRowResizeTop);

    if (!isColResize && !isRowResize) { clearTableSelection(); return; }
    if (!canEdit) return;
    e.preventDefault();

    const startX = e.clientX;
    const startY = e.clientY;

    if (isColResize) {
      const targetColIdx = isColResizeLeft ? colIdx - 1 : colIdx;
      const targetCell = row.cells[targetColIdx];
      const startW = targetCell ? targetCell.offsetWidth : td.offsetWidth;
      const onMove = (me) => {
        const newW = Math.max(24, startW + me.clientX - startX);
        allRows.forEach(r => { if (r.cells[targetColIdx]) r.cells[targetColIdx].style.width = newW + 'px'; });
      };
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    } else {
      const targetRow = isRowResizeTop ? row.previousElementSibling : row;
      if (!targetRow) return;
      const startH = targetRow.offsetHeight;
      const onMove = (me) => { targetRow.style.height = Math.max(18, startH + me.clientY - startY) + 'px'; };
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
  }, [canEdit, clearTableSelection, applyTableSelection]);

  const handleTableResizeMouseMove = useCallback((e) => {
    if (!editorRef.current) return;
    const td = e.target.closest('td, th');
    if (!td || !editorRef.current.contains(td)) { editorRef.current.style.cursor = ''; return; }
    const rect = td.getBoundingClientRect();
    const GRIP = 7;
    const SEL  = 10;
    const row   = td.closest('tr');
    const table = td.closest('table');
    const allRows = Array.from(table.rows);
    const colIdx = Array.from(row.cells).indexOf(td);
    const rowIdx = allRows.indexOf(row);
    const isFirstCol = colIdx === 0;
    const isFirstRow = rowIdx === 0;

    // Selection zones take priority (outer edges of the table)
    if (isFirstCol && e.clientX - rect.left < SEL) { editorRef.current.style.cursor = 'e-resize'; }
    else if (isFirstRow && e.clientY - rect.top < SEL) { editorRef.current.style.cursor = 's-resize'; }
    // Column resize: right edge of any cell, or left edge of non-first column
    else if (e.clientX >= rect.right - GRIP) { editorRef.current.style.cursor = 'col-resize'; }
    else if (!isFirstCol && e.clientX - rect.left < GRIP) { editorRef.current.style.cursor = 'col-resize'; }
    // Row resize: bottom edge of any cell, or top edge of non-first row
    else if (e.clientY >= rect.bottom - GRIP) { editorRef.current.style.cursor = 'row-resize'; }
    else if (!isFirstRow && e.clientY - rect.top < GRIP) { editorRef.current.style.cursor = 'row-resize'; }
    else { editorRef.current.style.cursor = ''; }
  }, []);

  // ── Import DOCX ───────────────────────────────────────────────────────────
  const handleImportDocx = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editorRef.current) return;
    try {
      const mammoth = (await import('mammoth')).default;
      const arrayBuffer = await file.arrayBuffer();

      // Collect font usage across all runs to detect the document's primary font
      const fontCounts = new Map();
      const fontTransform = mammoth.transforms.run(function(run) {
        if (run.font) fontCounts.set(run.font, (fontCounts.get(run.font) || 0) + 1);
        return run;
      });

      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          // Convert embedded images to base64 data URIs so they display inline
          convertImage: mammoth.images.imgElement(function(image) {
            return image.read('base64').then(function(imageBase64) {
              return { src: 'data:' + image.contentType + ';base64,' + imageBase64 };
            });
          }),
          // Read run font properties (does not change the document — pure observation)
          transformDocument: fontTransform,
        }
      );

      const currentContent = editorRef.current.innerHTML.replace(/<br\s*\/?>/gi, '').trim();
      if (currentContent && !window.confirm('Import DOCX? Current content will be replaced.')) return;

      editorRef.current.innerHTML = result.value || '';

      // Apply the configured formatting font (from Account & Settings) rather than the DOCX's own font
      const s1 = getExcelStyle('style1');
      editorRef.current.style.fontFamily = `'${s1.name}', Raleway, Georgia, serif`;

      setActiveTemplateId(null);
    } catch (err) {
      alert('Failed to import DOCX: ' + err.message);
    }
    e.target.value = '';
  }, []);

  // ── Toolbar button style ──────────────────────────────────────────────────
  const tbBtn = (title, onClick, content, extraStyle = {}) => (
    <button
      key={title}
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 5px', borderRadius: '2px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 500, color: '#444', lineHeight: '18px', ...extraStyle }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f0ede4'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >{content}</button>
  );

  const activeTpl = templates.find(t => t.id === activeTemplateId);
  const thisDoc   = documents.find(d => d.id === currentDocId);
  const inEditMode = isEditing || !currentDocId;

  const ddMenu = { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 301, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: '3px' };
  const ddHdr  = { padding: '5px 12px 4px', fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER };
  const ddItem = { padding: '6px 14px', cursor: 'pointer', fontSize: '11px', fontWeight: 300, color: '#333' };
  const goldBtn = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500, padding: '4px 14px', border: 'none', background: BRIO_GOLD, color: '#fff', cursor: 'pointer', borderRadius: '2px' };

  return (
    <>
      {/* Dim overlay */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.22)' }} onClick={onClose} />

      {/* Side panel */}
      <div
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '760px', zIndex: 9999, background: '#dde0e4', boxShadow: '-6px 0 40px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header bar ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 18px', borderBottom: '2px solid ' + BRIO_GOLD, background: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <FileText size={15} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#333', letterSpacing: '0.02em' }}>Minutes of Meeting</div>
              <div style={{ fontSize: '10px', color: BRIO_GREY, fontWeight: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '580px' }}>{rowName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <X size={15} style={{ color: BRIO_GREY }} />
          </button>
        </div>

        {/* ── Action bar ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderBottom: '1px solid ' + BRIO_BORDER, background: '#fff', flexShrink: 0, position: 'relative', flexWrap: 'wrap' }}>

          {/* Meeting Notes dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowDropdown(p => p === 'notes' ? null : 'notes')}
              style={{ ...btnSm, background: showDropdown === 'notes' ? BRIO_BG : '#fff', paddingRight: '20px', position: 'relative' }}>
              {thisDoc ? (thisDoc.title || 'Untitled Note') : <em style={{ fontWeight: 300, color: BRIO_GREY }}>New Note</em>}
              <span style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', fontSize: '8px', color: BRIO_GREY }}>▾</span>
            </button>
            {showDropdown === 'notes' && (
              <div style={{ ...ddMenu, minWidth: '240px' }}>
                <div style={ddHdr}>All Meeting Notes — this project</div>
                {documents.length === 0
                  ? <div style={{ padding: '10px 12px', fontSize: '11px', color: BRIO_GREY, fontStyle: 'italic' }}>No saved notes yet</div>
                  : documents.map(doc => (
                    <div key={doc.id} onClick={() => handleSwitchDoc(doc.id)}
                      style={{ padding: '7px 12px', fontSize: '11px', fontWeight: doc.id === currentDocId ? 600 : 300, cursor: 'pointer', color: '#333', borderLeft: doc.id === currentDocId ? '2px solid ' + BRIO_GOLD : '2px solid transparent' }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <div>{doc.title || 'Untitled Note'}</div>
                      <div style={{ fontSize: '9px', color: BRIO_GREY, marginTop: '1px' }}>{doc.task_key}</div>
                    </div>
                  ))
                }
                <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                  <div onClick={handleNewNote}
                    style={{ padding: '7px 12px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', color: BRIO_GOLD, display: 'flex', alignItems: 'center', gap: '5px' }}
                    onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <Plus size={11} /> New Meeting Note
                  </div>
                </div>
              </div>
            )}
          </div>

          <span style={{ color: BRIO_BORDER, fontSize: '14px', lineHeight: 1 }}>|</span>

          {/* Templates dropdown — star sets default, × deletes (admin/supervisor only) */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowDropdown(p => p === 'template' ? null : 'template')}
              style={{ ...btnSm, background: showDropdown === 'template' ? BRIO_BG : '#fff', paddingRight: '20px', position: 'relative' }}>
              Templates
              <span style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', fontSize: '8px', color: BRIO_GREY }}>▾</span>
            </button>
            {showDropdown === 'template' && (
              <div style={{ ...ddMenu, minWidth: '230px' }}>
                <div style={ddHdr}>Templates</div>
                {templates.length === 0
                  ? <div style={{ padding: '10px 12px', fontSize: '11px', color: BRIO_GREY, fontStyle: 'italic' }}>No templates yet</div>
                  : templates.map(tpl => (
                    <div key={tpl.id}
                      style={{ padding: '6px 10px 6px 12px', fontSize: '11px', cursor: 'pointer', color: '#333', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: tpl.id === activeTemplateId ? 600 : 300 }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <span
                        onClick={e => handleSetDefaultTemplate(tpl.id, e)}
                        title={tpl.is_default ? 'Default template' : 'Set as default'}
                        style={{ fontSize: '13px', color: tpl.is_default ? '#f59e0b' : '#ccc', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>★</span>
                      <span onClick={() => handleLoadTemplate(tpl.id)} style={{ flex: 1 }}>{tpl.name}</span>
                      {canManageTemplates && (
                        <span
                          onClick={e => {
                            e.stopPropagation();
                            if (window.confirm(`Delete template "${tpl.name}"?`)) {
                              deleteTemplate(tpl.id).catch(err => alert('Error: ' + err.message));
                            }
                          }}
                          title="Delete template"
                          style={{ fontSize: '12px', color: '#ccc', cursor: 'pointer', flexShrink: 0, lineHeight: 1, padding: '0 2px' }}
                          onMouseEnter={e => { e.currentTarget.style.color = BRIO_RED; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#ccc'; }}
                        >×</span>
                      )}
                    </div>
                  ))
                }
              </div>
            )}
          </div>

          {/* Edit-mode only controls */}
          {inEditMode && canEdit && (
            <>
              <span style={{ color: BRIO_BORDER, fontSize: '14px', lineHeight: 1 }}>|</span>

              {/* Import from */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowDropdown(p => p === 'import' ? null : 'import')}
                  style={{ ...btnSm, background: showDropdown === 'import' ? BRIO_BG : '#fff', paddingRight: '20px', position: 'relative' }}>
                  Import from
                  <span style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', fontSize: '8px', color: BRIO_GREY }}>▾</span>
                </button>
                {showDropdown === 'import' && (
                  <div style={{ ...ddMenu, minWidth: '160px' }}>
                    <div style={ddHdr}>Import from</div>
                    <div style={ddItem}
                      onClick={() => { setShowDropdown(null); pdfInputRef.current?.click(); }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>PDF</div>
                    <div style={ddItem}
                      onClick={() => { setShowDropdown(null); fileInputRef.current?.click(); }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>Word / DOCX</div>
                    <div style={{ ...ddItem, opacity: 0.4, cursor: 'default' }}>Excel (not supported)</div>
                  </div>
                )}
              </div>

              {/* Export as */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowDropdown(p => p === 'export' ? null : 'export')}
                  style={{ ...btnSm, background: showDropdown === 'export' ? BRIO_BG : '#fff', paddingRight: '20px', position: 'relative' }}>
                  Export as
                  <span style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', fontSize: '8px', color: BRIO_GREY }}>▾</span>
                </button>
                {showDropdown === 'export' && (
                  <div style={{ ...ddMenu, minWidth: '160px' }}>
                    <div style={ddHdr}>Export as</div>
                    <div style={ddItem}
                      onClick={() => { handleExportPDF(); setShowDropdown(null); }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>PDF (print)</div>
                    <div style={ddItem}
                      onClick={() => { handleExportDOCX(); setShowDropdown(null); }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>Word (.doc)</div>
                  </div>
                )}
              </div>

              {/* Create Template — admin / supervisor only */}
              {canManageTemplates && (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowDropdown(p => p === 'createTpl' ? null : 'createTpl')}
                    style={{ ...btnSm, background: showDropdown === 'createTpl' ? BRIO_BG : '#fff' }}>
                    Create Template
                  </button>
                  {showDropdown === 'createTpl' && (
                    <div style={{ ...ddMenu, width: '260px', padding: '12px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Save current content as template</div>
                      <input autoFocus placeholder="Template name…" value={createTplName}
                        onChange={e => setCreateTplName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreateTemplate(); if (e.key === 'Escape') setShowDropdown(null); }}
                        style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', padding: '5px 8px', width: '100%', outline: 'none', background: '#fff', boxSizing: 'border-box', marginBottom: '8px' }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={isGlobalTpl} onChange={e => setIsGlobalTpl(e.target.checked)}
                          style={{ margin: 0, accentColor: BRIO_GOLD }} />
                        <span style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, color: '#444' }}>Available across all projects</span>
                      </label>
                      <button onClick={handleCreateTemplate} disabled={!createTplName.trim()}
                        style={{ ...goldBtn, opacity: createTplName.trim() ? 1 : 0.5, cursor: createTplName.trim() ? 'pointer' : 'default' }}>
                        Save Template
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Right-side action buttons ─────────────────────────────────── */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* SAVED VIEW MODE: Edit | Delete | Send | [sent checkbox] */}
            {currentDocId && !inEditMode && (
              <>
                <button onClick={() => setIsEditing(true)} style={btnSm}>Edit</button>
                <button onClick={handleDeleteDoc}
                  style={{ ...btnSm, color: BRIO_RED }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_RED; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; }}>Delete</button>
                <button
                  onClick={() => { if (!isSent) setShowSendPopup(true); }}
                  style={{ ...goldBtn, background: isSent ? '#e5e7eb' : BRIO_GOLD, color: isSent ? BRIO_GREY : '#fff', cursor: isSent ? 'default' : 'pointer' }}>
                  {isSent ? 'Sent' : 'Send'}
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '11px', color: BRIO_GREY, userSelect: 'none' }}
                  title="Mark as sent">
                  <input type="checkbox" checked={isSent} onChange={e => setIsSent(e.target.checked)} style={{ cursor: 'pointer', accentColor: BRIO_GOLD }} />
                </label>
              </>
            )}
            {/* EDIT / NEW MODE: Save | Cancel (if editing saved doc) */}
            {inEditMode && canEdit && (
              <>
                {saveMsg && <span style={{ fontSize: '10px', color: saveMsg.startsWith('Error') ? BRIO_RED : BRIO_GREEN, fontWeight: 500 }}>{saveMsg}</span>}
                <button onClick={handleSave} disabled={saving}
                  style={{ ...goldBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'default' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save M.O.M'}
                </button>
                {currentDocId && (
                  <button onClick={() => setIsEditing(false)} style={btnSm}>Cancel</button>
                )}
              </>
            )}
          </div>

          {/* Click-outside closer */}
          {showDropdown && <div style={{ position: 'fixed', inset: 0, zIndex: 300 }} onClick={() => setShowDropdown(null)} />}
        </div>

        {/* ── Formatting toolbar — edit mode only ──────────────────────────── */}
        {inEditMode && canEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 14px', borderBottom: '1px solid ' + BRIO_BORDER, background: '#fff', flexShrink: 0, flexWrap: 'wrap' }}>
            {tbBtn('Bold',             () => fmt('bold'),                         <strong>B</strong>)}
            {tbBtn('Italic',           () => fmt('italic'),                       <em>I</em>)}
            {tbBtn('Underline',        () => fmt('underline'),                    <u>U</u>)}
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {/* Font size picker */}
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={() => { setFontSizeDropdown(v => !v); setListDropdown(false); setNumDropdown(false); }}
                title="Font size"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 5px', borderRadius: '2px', color: '#444', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 500 }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0ede4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >A<ChevronDown size={9} /></button>
              {fontSizeDropdown && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 500 }} onClick={() => setFontSizeDropdown(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 501, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '80px', padding: '3px 0', maxHeight: '220px', overflowY: 'auto' }}>
                    {[8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32].map(size => (
                      <div key={size}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          const sel = window.getSelection();
                          const saved = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
                          editorRef.current?.focus();
                          if (saved) { sel.removeAllRanges(); sel.addRange(saved); }
                          document.execCommand('styleWithCSS', false, true);
                          document.execCommand('fontSize', false, '7');
                          // Replace the browser's font size with our pixel size
                          editorRef.current?.querySelectorAll('font[size="7"]').forEach(el => {
                            el.removeAttribute('size');
                            el.style.fontSize = size + 'px';
                          });
                          document.execCommand('styleWithCSS', false, false);
                          setFontSizeDropdown(false);
                        }}
                        style={{ padding: '4px 12px', fontSize: size > 16 ? '13px' : '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 300, cursor: 'pointer', color: '#333', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = BRIO_BG}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontSize: '10px', color: '#aaa', minWidth: '22px' }}>{size}px</span>
                        <span style={{ fontSize: size + 'px', lineHeight: 1 }}>A</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {tbBtn('Heading 1',        () => fmt('formatBlock', 'h1'),            'H1', { fontWeight: 700 })}
            {tbBtn('Heading 2',        () => fmt('formatBlock', 'h2'),            'H2', { fontWeight: 700 })}
            {tbBtn('Heading 3',        () => fmt('formatBlock', 'h3'),            'H3', { fontWeight: 600 })}
            {tbBtn('Paragraph',        () => fmt('formatBlock', 'p'),             'P')}
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {/* Alignment */}
            {tbBtn('Align left',   () => fmt('justifyLeft'),   <AlignLeft   size={12} />)}
            {tbBtn('Align center', () => fmt('justifyCenter'), <AlignCenter size={12} />)}
            {tbBtn('Align right',  () => fmt('justifyRight'),  <AlignRight  size={12} />)}
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {/* Bullet list style picker */}
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={() => { setListDropdown(v => !v); setNumDropdown(false); }}
                title="Bullet list style"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 4px', borderRadius: '2px', color: '#444', display: 'flex', alignItems: 'center', gap: '2px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0ede4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              ><List size={13} /><ChevronDown size={9} /></button>
              {listDropdown && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 500 }} onClick={() => setListDropdown(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 501, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '120px', padding: '3px 0' }}>
                    {LIST_TYPES.slice(0, 3).map(item => (
                      <div key={item.style}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => changeLevelStyle(item.style, item.tag)}
                        style={{ padding: '5px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 300, cursor: 'pointer', color: '#333', whiteSpace: 'nowrap' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = BRIO_BG}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                      >{item.label}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Numbered / alpha list style picker */}
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <button
                onMouseDown={e => e.preventDefault()}
                onClick={() => { setNumDropdown(v => !v); setListDropdown(false); }}
                title="Numbered / alpha list style"
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 4px', borderRadius: '2px', color: '#444', display: 'flex', alignItems: 'center', gap: '2px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0ede4'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              ><ListOrdered size={13} /><ChevronDown size={9} /></button>
              {numDropdown && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 500 }} onClick={() => setNumDropdown(false)} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 501, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '130px', padding: '3px 0' }}>
                    {LIST_TYPES.slice(3).map(item => (
                      <div key={item.style}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => changeLevelStyle(item.style, item.tag)}
                        style={{ padding: '5px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 300, cursor: 'pointer', color: '#333', whiteSpace: 'nowrap' }}
                        onMouseEnter={ev => ev.currentTarget.style.background = BRIO_BG}
                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                      >{item.label}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Promote / Demote list level */}
            {tbBtn('Promote level (Shift+Tab)', () => {
              const sel = window.getSelection();
              const saved = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
              editorRef.current?.focus();
              if (saved) { sel.removeAllRanges(); sel.addRange(saved); }
              document.execCommand('outdent', false, null);
            }, '⇤', { fontSize: '13px' })}
            {tbBtn('Demote level (Tab)', () => {
              const sel = window.getSelection();
              const saved = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
              editorRef.current?.focus();
              if (saved) { sel.removeAllRanges(); sel.addRange(saved); }
              document.execCommand('indent', false, null);
              const r2 = sel.getRangeAt(0);
              const n2 = r2.startContainer;
              const list2 = (n2.nodeType === 3 ? n2.parentElement : n2)?.closest('ul, ol');
              if (list2) {
                const depth = getListNestingDepth(list2, editorRef.current);
                const stored = levelStyleRef.current[depth];
                if (stored) list2.style.listStyleType = stored;
              }
            }, '⇥', { fontSize: '13px' })}
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {tbBtn('Insert table',     insertTable,                               '⊞')}
            {tbBtn('Insert image',     () => imageInputRef.current?.click(),      '🖼', { fontSize: '13px' })}
            {tbBtn('Horizontal rule',  () => fmt('insertHorizontalRule'),         '—')}
            <span style={{ width: '1px', height: '14px', background: BRIO_BORDER, margin: '0 3px', flexShrink: 0 }} />
            {tbBtn('Clear formatting', () => { fmt('removeFormat'); fmt('formatBlock', 'p'); }, 'Tx', { color: BRIO_GREY })}
          </div>
        )}

        {/* ── Document canvas (A4 page view) ───────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 24px 40px', background: '#dde0e4' }}>
          {/* A4 page card — 794px = A4 at 96dpi */}
          <div ref={momPageRef} style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.18)', width: '794px', margin: '0 auto', minHeight: '1122px', position: 'relative', overflow: 'visible' }}>
            {/* BRIO letterhead header — full bleed, matches invoice PDF layout */}
            {headerImageUrl && (
              <div style={{ margin: 0, padding: 0, lineHeight: 0 }}>
                <img
                  src={headerImageUrl}
                  alt="BRIO Letterhead"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            )}
            {/* M.O.M — below letterhead image, right-aligned to page margin */}
            <div style={{ padding: headerImageUrl ? '10px 64px 0' : '0 64px 0', textAlign: 'right', fontFamily: excelFontStack, fontSize: `${excelFontSize}px`, fontWeight: 300, letterSpacing: '0.18em', color: '#444' }}>
              M.O.M
            </div>
            {/* Content area */}
            <div style={{ padding: headerImageUrl ? '14px 64px 72px' : '52px 64px 72px' }}>
              {/* Structured header fields */}
              {(() => {
                const editable = inEditMode && canEdit;
                const baseFont = { fontFamily: excelFontStack, fontSize: `${excelFontSize}px`, color: '#222', lineHeight: 1.4 };
                const ceStyle = (extra = {}) => ({
                  ...baseFont, fontWeight: 600, display: 'inline-block',
                  borderBottom: editable ? '1px solid ' + BRIO_BORDER : '1px solid transparent',
                  outline: 'none', padding: '1px 4px', minWidth: '40px',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  cursor: editable ? 'text' : 'default', ...extra,
                });
                const ceSpan = (key, html, setter, extra = {}) => (
                  editable
                    ? <span ref={el => { if (el) hdrFieldsRef.current[key] = el; }}
                        contentEditable suppressContentEditableWarning
                        onInput={e => setter(e.currentTarget.innerHTML)}
                        onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                        style={ceStyle(extra)} />
                    : <span dangerouslySetInnerHTML={{ __html: html || '' }}
                        style={{ ...baseFont, fontWeight: 600, padding: '1px 4px', display: 'inline', ...extra }} />
                );
                const labelStyle = { ...baseFont, fontWeight: 400, marginRight: '2px', whiteSpace: 'nowrap' };
                const rowStyle = { display: 'flex', alignItems: 'baseline', marginBottom: '3px' };
                return (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={rowStyle}>
                      {ceSpan('dateLabel', momDateLabel, setMomDateLabel, { fontWeight: 400, marginRight: '2px', whiteSpace: 'nowrap', minWidth: 'unset' })}
                      {ceSpan('date', momDate, setMomDate, { minWidth: '130px' })}
                    </div>
                    <div style={{ ...rowStyle, marginBottom: '13px' }}>
                      {ceSpan('projectLabel', momProjectLabel, setMomProjectLabel, { fontWeight: 400, marginRight: '2px', whiteSpace: 'nowrap', minWidth: 'unset' })}
                      {ceSpan('project', momProject, setMomProject, { flex: 1 })}
                    </div>
                    <div style={{ ...rowStyle, marginBottom: '3px' }}>
                      {ceSpan('attendeesLabel', momAttendeesLabel, setMomAttendeesLabel, { fontWeight: 400, marginRight: '2px', whiteSpace: 'nowrap', minWidth: 'unset' })}
                    </div>
                    {momAttendees.map((att, i) => (
                      <div key={i} style={{ ...rowStyle, marginBottom: '2px' }}>
                        {editable
                          ? <span ref={el => { if (el) hdrFieldsRef.current[`al${i}`] = el; }}
                              contentEditable suppressContentEditableWarning
                              onInput={e => { const html = e.currentTarget.innerHTML; setMomAttendees(prev => prev.map((a, j) => j === i ? { ...a, label: html } : a)); }}
                              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                              style={ceStyle({ fontWeight: 300, marginRight: '2px', whiteSpace: 'nowrap', minWidth: 'unset' })} />
                          : <span dangerouslySetInnerHTML={{ __html: (att.label || '') + (att.label ? ':' : '') }}
                              style={{ ...baseFont, fontWeight: 300, padding: '1px 4px', marginRight: '2px', whiteSpace: 'nowrap', display: 'inline' }} />
                        }
                        {editable
                          ? <span ref={el => { if (el) hdrFieldsRef.current[`av${i}`] = el; }}
                              contentEditable suppressContentEditableWarning
                              onInput={e => { const html = e.currentTarget.innerHTML; setMomAttendees(prev => prev.map((a, j) => j === i ? { ...a, value: html } : a)); }}
                              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                              style={ceStyle({ flex: 1 })} />
                          : <span dangerouslySetInnerHTML={{ __html: att.value || '' }}
                              style={{ ...baseFont, fontWeight: 600 }} />
                        }
                        {editable && (
                          <button onClick={() => setMomAttendees(prev => prev.filter((_, j) => j !== i))} style={{ marginLeft: '4px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '14px', lineHeight: 1, padding: '0 2px', flexShrink: 0 }} title="Remove">×</button>
                        )}
                      </div>
                    ))}
                    {editable && (
                      <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                        <button onClick={() => setMomAttendees(prev => [...prev, { label: '', value: '' }])} style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: BRIO_GOLD, background: 'transparent', border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', cursor: 'pointer', padding: '1px 8px' }}>+ Add</button>
                      </div>
                    )}
                    {/* Subject — label + value centered together as one unit */}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', marginTop: '3px' }}>
                      {ceSpan('subjectLabel', momSubjectLabel, setMomSubjectLabel, { fontWeight: 600, flexShrink: 0, marginRight: '2px', whiteSpace: 'nowrap', minWidth: 'unset' })}
                      {editable
                        ? <span ref={el => { if (el) hdrFieldsRef.current.subject = el; }}
                            contentEditable suppressContentEditableWarning
                            onInput={e => setMomSubject(e.currentTarget.innerHTML)}
                            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                            style={ceStyle({ maxWidth: '420px' })} />
                        : <span dangerouslySetInnerHTML={{ __html: momSubject || '' }}
                            style={{ ...baseFont, fontWeight: 600, padding: '1px 4px' }} />
                      }
                    </div>
                  </div>
                );
              })()}
              {/* Rich-text content */}
              <div
                ref={editorRef}
                className="mom-editor"
                contentEditable={inEditMode && canEdit}
                suppressContentEditableWarning
                style={{ cursor: inEditMode && canEdit ? 'text' : 'default', minHeight: '600px' }}
                onContextMenu={handleEditorContextMenu}
                onKeyDown={(e) => {
                  const sel = window.getSelection();
                  if (!sel?.rangeCount) return;
                  const range = sel.getRangeAt(0);
                  const node = range.startContainer;
                  const li = (node.nodeType === 3 ? node.parentElement : node)?.closest('li');

                  // ── Space: auto-convert typed list markers (1. A. a. I. i. - • ○) ──
                  if (e.key === ' ' && range.collapsed && !li) {
                    const editorEl = editorRef.current;
                    let blockEl = node.nodeType === 3 ? node.parentElement : node;
                    while (blockEl && blockEl !== editorEl && blockEl.parentElement !== editorEl &&
                           !['P','DIV','H1','H2','H3','H4','H5','H6'].includes(blockEl.tagName)) {
                      blockEl = blockEl.parentElement;
                    }
                    if (blockEl && blockEl !== editorEl) {
                      const textBefore = node.nodeType === 3 ? node.textContent.slice(0, range.startOffset) : '';
                      if (blockEl.textContent.trim() && blockEl.textContent.trim() === textBefore.trim()) {
                        const marker = detectListMarker(textBefore.trim());
                        if (marker) {
                          e.preventDefault();
                          const cr = document.createRange();
                          cr.selectNodeContents(blockEl);
                          sel.removeAllRanges();
                          sel.addRange(cr);
                          document.execCommand('delete');
                          document.execCommand(marker.tag === 'ol' ? 'insertOrderedList' : 'insertUnorderedList', false, null);
                          const r2 = sel.getRangeAt(0);
                          const n2 = r2.startContainer;
                          const list2 = (n2.nodeType === 3 ? n2.parentElement : n2)?.closest('ul, ol');
                          if (list2) {
                            list2.style.listStyleType = marker.style;
                            levelStyleRef.current[getListNestingDepth(list2, editorEl)] = marker.style;
                          }
                          return;
                        }
                      }
                    }
                  }

                  // ── Tab inside a list: indent / outdent with style memory ──────────
                  if (e.key === 'Tab' && li) {
                    e.preventDefault();
                    if (e.shiftKey) {
                      document.execCommand('outdent', false, null);
                    } else {
                      document.execCommand('indent', false, null);
                      const r2 = sel.getRangeAt(0);
                      const n2 = r2.startContainer;
                      const list2 = (n2.nodeType === 3 ? n2.parentElement : n2)?.closest('ul, ol');
                      if (list2) {
                        const depth = getListNestingDepth(list2, editorRef.current);
                        const stored = levelStyleRef.current[depth];
                        if (stored) list2.style.listStyleType = stored;
                      }
                    }
                    return;
                  }

                  // ── Enter on empty list item: exit or outdent ─────────────────────
                  if (e.key === 'Enter' && !e.shiftKey && li && range.collapsed) {
                    const isEmpty = li.textContent === '' || li.innerHTML === '<br>' || li.innerHTML === '';
                    if (isEmpty) {
                      e.preventDefault();
                      const list = li.parentElement;
                      if (list?.parentElement?.tagName === 'LI') {
                        document.execCommand('outdent', false, null);
                      } else {
                        const p = document.createElement('p');
                        p.innerHTML = '<br>';
                        if (list.children.length === 1) { list.replaceWith(p); } else { li.remove(); list.after(p); }
                        const r = document.createRange();
                        r.setStart(p, 0); r.collapse(true);
                        sel.removeAllRanges(); sel.addRange(r);
                      }
                      return;
                    }
                  }

                  // ── Backspace on last empty list item: exit list ───────────────────
                  if (e.key === 'Backspace' && li) {
                    if (!range.collapsed) return;
                    const list = li.parentElement;
                    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return;
                    const empty = li.textContent === '' || li.innerHTML === '<br>' || li.innerHTML === '';
                    const isLast = !li.nextElementSibling;
                    if (!empty || !isLast) return;
                    e.preventDefault();
                    const p = document.createElement('p');
                    p.innerHTML = '<br>';
                    if (list.children.length === 1) { list.replaceWith(p); } else { li.remove(); list.after(p); }
                    const r = document.createRange();
                    r.setStart(p, 0); r.collapse(true);
                    sel.removeAllRanges(); sel.addRange(r);
                  }
                }}
                onMouseDown={handleTableResizeMouseDown}
                onMouseMove={handleTableResizeMouseMove}
              />
            </div>
            {/* A4 page break indicators and page numbers */}
            {Array.from({ length: momPageCount }).map((_, i) => (
              <React.Fragment key={i}>
                <div style={{ position: 'absolute', top: (i + 1) * 1122 - 26, right: 40, fontFamily: 'Raleway, sans-serif', fontSize: '9px', fontWeight: 300, color: '#bbb', pointerEvents: 'none', userSelect: 'none', zIndex: 10 }}>
                  Page {i + 1} / {momPageCount}
                </div>
                {i < momPageCount - 1 && (
                  <div style={{ position: 'absolute', top: (i + 1) * 1122, left: 0, right: 0, height: '1px', background: '#d0d5db', pointerEvents: 'none', zIndex: 10 }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── Send popup ───────────────────────────────────────────────────────── */}
      {showSendPopup && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10200, background: 'rgba(0,0,0,0.3)' }} onClick={() => setShowSendPopup(false)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10201, background: '#fff', borderRadius: '6px', padding: '28px 28px 22px', width: '340px', boxShadow: '0 12px 40px rgba(0,0,0,0.24)', fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#222', marginBottom: '6px' }}>Send Meeting Notes</div>
            <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '20px', lineHeight: 1.6 }}>
              Download a PDF of this document, then open your email app to attach it.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button onClick={handleExportPDF}
                style={{ ...goldBtn, flex: 1 }}>
                Download PDF
              </button>
              <button
                onClick={() => {
                  const subj = encodeURIComponent('Minutes of Meeting: ' + (docTitle || rowName || ''));
                  window.open(`mailto:?subject=${subj}`, '_self');
                  setShowSendPopup(false);
                }}
                style={{ ...btnSm, flex: 1, textAlign: 'center' }}>
                Open Email App
              </button>
            </div>
            <button onClick={() => setShowSendPopup(false)}
              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, color: BRIO_GREY, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0', width: '100%', textAlign: 'center' }}>
              Close
            </button>
          </div>
        </>
      )}

      {/* ── Table cell context menu ───────────────────────────────────────────── */}
      {tableCtxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10100 }} onClick={() => setTableCtxMenu(null)} />
          <div style={{ position: 'fixed', left: tableCtxMenu.x, top: tableCtxMenu.y, zIndex: 10101, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: '180px', padding: '4px 0', fontFamily: 'Raleway, sans-serif', fontSize: '11px' }}>
            <div onClick={() => handleEnterTableEdit(tableCtxMenu.table)}
              style={{ padding: '6px 14px', cursor: 'pointer', fontWeight: 500, color: BRIO_GOLD }}
              onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >Edit Table (+ rows / columns)</div>
            <div style={{ borderTop: '1px solid ' + BRIO_BORDER, margin: '3px 0' }} />
            {[
              { op: 'insertRowAbove', label: 'Insert Row Above' },
              { op: 'insertRowBelow', label: 'Insert Row Below' },
              { op: 'insertColLeft',  label: 'Insert Column Left' },
              { op: 'insertColRight', label: 'Insert Column Right' },
            ].map(({ op, label }) => (
              <div key={op} onClick={() => handleTableOp(op)}
                style={{ padding: '6px 14px', cursor: 'pointer', color: '#333' }}
                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{label}</div>
            ))}
            <div style={{ borderTop: '1px solid ' + BRIO_BORDER, margin: '3px 0' }} />
            {[
              { op: 'selectRow', label: 'Select Row' },
              { op: 'selectCol', label: 'Select Column' },
            ].map(({ op, label }) => (
              <div key={op} onClick={() => handleTableOp(op)}
                style={{ padding: '6px 14px', cursor: 'pointer', color: '#333' }}
                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{label}</div>
            ))}
            <div style={{ borderTop: '1px solid ' + BRIO_BORDER, margin: '3px 0' }} />
            {[
              { op: 'deleteRow', label: 'Delete Row' },
              { op: 'deleteCol', label: 'Delete Column' },
            ].map(({ op, label }) => (
              <div key={op} onClick={() => handleTableOp(op)}
                style={{ padding: '6px 14px', cursor: 'pointer', color: BRIO_RED }}
                onMouseEnter={e => e.currentTarget.style.background = '#fdf5f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{label}</div>
            ))}
          </div>
        </>
      )}

      {/* ── Bullet / list item context menu ──────────────────────────────────── */}
      {bulletCtxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10100 }} onClick={() => setBulletCtxMenu(null)} />
          <div style={{ position: 'fixed', left: bulletCtxMenu.x, top: bulletCtxMenu.y, zIndex: 10101, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: '180px', padding: '4px 0', fontFamily: 'Raleway, sans-serif', fontSize: '11px' }}>
            <div onClick={handleAddBulletToTask}
              style={{ padding: '6px 14px', cursor: 'pointer', fontWeight: 500, color: BRIO_GOLD }}
              onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >Add to project tasks</div>
          </div>
        </>
      )}

      {/* ── Table edit mode + buttons ─────────────────────────────────────────── */}
      {tableBounds && editedTable && (() => {
        const addBtnStyle = { position: 'fixed', zIndex: 10101, width: '22px', height: '22px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, fontWeight: 300, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif' };
        return (
          <>
            <button className="mom-tbl-add-btn" title="Add column"
              style={{ ...addBtnStyle, left: tableBounds.right + 6, top: tableBounds.top + (tableBounds.bottom - tableBounds.top) / 2 - 11 }}
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleAddTableColumn(); }}>+</button>
            <button className="mom-tbl-add-btn" title="Add row"
              style={{ ...addBtnStyle, left: tableBounds.left + tableBounds.width / 2 - 11, top: tableBounds.bottom + 6 }}
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleAddTableRow(); }}>+</button>
          </>
        );
      })()}

      {/* ── Hidden file inputs ────────────────────────────────────────────────── */}
      <input ref={fileInputRef}  type="file" accept=".docx"        style={{ display: 'none' }} onChange={handleImportDocx} />
      <input ref={pdfInputRef}   type="file" accept=".pdf"         style={{ display: 'none' }} onChange={handleImportPDF} />
      <input ref={imageInputRef} type="file" accept="image/*"      style={{ display: 'none' }} onChange={handleImageSelected} />
    </>
  );
}

// ============================================================================
// COMPARE VERSIONS MODAL
// ============================================================================

function CompareVersionsModal({ project, projectVersions, displayRows, timeline, allColumns, columnLabels, format, onClose, onCompare }) {
  const defaultColKeys = new Set(allColumns.filter(c => c.key !== 'taskName').map(c => c.key));
  const initialSlots = [
    { source: 'current', colKeys: new Set(defaultColKeys) },
    { source: projectVersions.length > 0 ? projectVersions[projectVersions.length - 1].id : 'current', colKeys: new Set(defaultColKeys) },
  ];
  const [slots, setSlots] = useState(initialSlots);
  const [exporting, setExporting] = useState(false);
  const { excelExportStyles, scopePdfStyles } = useSettings();

  const getVersionLabel = (src) => {
    if (src === 'current') return 'Current Timeline';
    const v = projectVersions.find(v => v.id === src);
    if (!v) return src;
    const d = new Date(v.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${v.label} — ${d}`;
  };

  const getTimeline = (src) => src === 'current' ? timeline : (projectVersions.find(v => v.id === src)?.snapshot || {});

  const getRows = (src) => {
    if (src === 'current') return displayRows;
    const tl = getTimeline(src);
    return (tl._rows || []).map(r => ({ ...r, key: r.id }));
  };

  const getCellVal = (src, row, colKey) => {
    const rowKey = row.key || row.id;
    const tl = getTimeline(src);
    const d = tl[rowKey] || {};
    if (colKey === 'taskName') {
      if (d.taskName) return d.taskName;
      // displayRows already carry a fully-resolved .name
      if (row.name) return row.name;
      if (row.defaultLabel) return row.defaultLabel;
      if (STAGE_NAMES[rowKey]) return STAGE_NAMES[rowKey];
      if (rowKey && rowKey.startsWith('closure_')) {
        const sk = rowKey.slice('closure_'.length);
        return `Closure of ${STAGE_NAMES[sk] || sk} Stage`;
      }
      if (rowKey && rowKey.startsWith('invoicing_')) {
        const sk = rowKey.slice('invoicing_'.length);
        return `Invoicing of ${STAGE_NAMES[sk] || sk} Stage`;
      }
      return rowKey || '';
    }
    if (Array.isArray(d[colKey])) return d[colKey].join(', ');
    return d[colKey] != null ? String(d[colKey]) : '';
  };

  const updateSlot = (i, patch) => setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const moveSlot = (i, dir) => setSlots(prev => {
    const next = [...prev];
    const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeSlot = (i) => setSlots(prev => prev.filter((_, idx) => idx !== i));
  const addSlot = () => setSlots(prev => [...prev, { source: 'current', colKeys: new Set(defaultColKeys) }]);
  const toggleSlotCol = (i, key) => setSlots(prev => prev.map((s, idx) => {
    if (idx !== i) return s;
    const next = new Set(s.colKeys);
    if (next.has(key)) next.delete(key); else next.add(key);
    return { ...s, colKeys: next };
  }));

  const doExportCsv = () => {
    const row1 = ['#', 'Task / Deliverable'];
    slots.forEach(slot => {
      const cols = allColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key));
      cols.forEach(() => row1.push(getVersionLabel(slot.source)));
    });
    const row2 = ['#', 'Task / Deliverable'];
    slots.forEach(slot => {
      const cols = allColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key));
      cols.forEach(c => row2.push(columnLabels[c.key] || c.label));
    });
    const csvify = r => r.map(c => { const s = String(c ?? ''); return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s; }).join(',');
    let taskN = 0;
    const primaryRows = getRows(slots[0].source);
    const dataRows = primaryRows.map(row => {
      const isStage = (row.indent || 0) === 0;
      const num = isStage ? '' : (++taskN);
      const name = getCellVal(slots[0].source, row, 'taskName');
      const cells = [num, name];
      slots.forEach(slot => {
        const cols = allColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key));
        cols.forEach(c => cells.push(getCellVal(slot.source, row, c.key)));
      });
      return cells;
    });
    const csv = [row1, row2, ...dataRows].map(csvify).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${project.name || 'comparison'}_comparison.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const doExportPdf = () => {
    const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const slotsWithCols = slots.map(slot => ({
      slot,
      cols: allColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key)),
    })).filter(({ cols }) => cols.length > 0);
    // Column widths: task name is protected; duration columns are compact; dates medium; others default
    const colPx = (c) => {
      if (c.key === 'plannedDuration') return 50;
      if (c.key === 'percentComplete') return 56;
      if (c.type === 'date') return 78;
      return 88;
    };
    const colgroupHtml = `<col style="width:24px"><col style="width:180px">` +
      slotsWithCols.flatMap(({ cols }) => cols.map(c => `<col style="width:${colPx(c)}px">`)).join('');
    const headerRow1 = `<th class="num" rowspan="2">#</th><th class="task-name" rowspan="2">Task / Deliverable</th>` +
      slotsWithCols.map(({ slot, cols }) =>
        `<th colspan="${cols.length}">${esc(getVersionLabel(slot.source))}</th>`
      ).join('');
    const headerRow2 = slotsWithCols.flatMap(({ cols }) =>
      cols.map(c => `<th>${esc(columnLabels[c.key] || c.label)}</th>`)
    ).join('');
    const primaryRows = getRows(slots[0].source);
    let taskN = 0;
    const rowsHtml = primaryRows.map(row => {
      const isStage = (row.indent || 0) === 0;
      const bg = STAGE_ROW_COLORS[row.key] || (isStage ? '#f4f4f0' : '#fff');
      const rowNum = isStage ? '' : (++taskN);
      const taskName = esc(getCellVal(slots[0].source, row, 'taskName'));
      const pl = isStage ? '5px' : '20px';
      const cells = slotsWithCols.flatMap(({ slot, cols }) =>
        cols.map(c => `<td>${esc(c.type === 'date' ? exportDateText(getCellVal(slot.source, row, c.key)) : getCellVal(slot.source, row, c.key))}</td>`)
      ).join('');
      const boldClass = getTimeline(slots[0].source)[row.key || row.id]?.bold ? ' class="bold"' : '';
      return `<tr${boldClass} style="background:${bg}"><td class="num">${rowNum}</td><td class="task-name" style="padding-left:${pl}">${taskName}</td>${cells}</tr>`;
    }).join('');
    const titlePt = Number(scopePdfStyles?.title?.fontSize) || 10;
    const headingPt = Number(scopePdfStyles?.heading?.fontSize) || 7.5;
    const bodyPt = Number(scopePdfStyles?.body?.fontSize) || 8;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc((project.name || 'Comparison') + ' — Compare Versions')}</title>
<style>
  ${pdfFontFaceCss()}
  *{box-sizing:border-box}
  body{font-family:${pdfFontFamily(scopePdfStyles?.body)};font-size:${bodyPt}pt;margin:0;padding:10px;color:#333}
  h2{font-family:${pdfFontFamily(scopePdfStyles?.title)};font-size:${titlePt}pt;font-weight:500;margin:0 0 8px}
  table{border-collapse:collapse;width:100%;table-layout:fixed}
  th,td{border:1px solid #e0e0e0;padding:3px 4px;vertical-align:middle;word-break:break-word;font-size:${bodyPt}pt}
  th{background:#fafaf8;font-family:${pdfFontFamily(scopePdfStyles?.heading)};font-weight:600;text-transform:uppercase;letter-spacing:.05em;font-size:${headingPt}pt;text-align:center}
  td{text-align:center;font-weight:300}
  td.task-name,th.task-name{text-align:left;min-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  tr.bold td{font-weight:600}
  td.num,th.num{width:24px;min-width:24px;max-width:24px;text-align:center;color:#888;white-space:nowrap;overflow:hidden}
  @page{size:A3 landscape;margin:8mm}
</style></head><body>
<h2>${esc(project.name || 'Comparison')} — Compare Versions</h2>
<table><colgroup>${colgroupHtml}</colgroup><thead><tr>${headerRow1}</tr><tr>${headerRow2}</tr></thead><tbody>${rowsHtml}</tbody></table>
</body></html>`;
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) { alert('Allow pop-ups to export PDF.'); return; }
    win.document.write(html); win.document.close(); win.focus();
    printWhenFontsReady(win);
  };

  const doExportExcel = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Comparison');
    const slotsWithCols = slots.map(slot => ({
      slot,
      cols: allColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key)),
    }));
    const xs = excelExportStyles || {};
    const hdrStyle = { font: xlsxFont(xs.style2, 9, { bold: true }), fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAF8' } }, alignment: { wrapText: true, horizontal: 'center', vertical: 'middle' } };
    ws.columns = [
      { key: '_num', width: 5 },
      { key: '_task', width: 40 },
      ...slotsWithCols.flatMap(({ slot, cols }) =>
        cols.map(c => ({
          key: slot.source + '_' + c.key,
          width: c.key === 'plannedDuration' ? 7 : c.key === 'percentComplete' ? 9 : c.type === 'date' ? 12 : 14,
        }))
      ),
    ];
    // Row 1: version labels spanning their columns
    const row1Values = ['#', 'Task / Deliverable'];
    slotsWithCols.forEach(({ slot, cols }) => {
      row1Values.push(getVersionLabel(slot.source));
      for (let k = 1; k < cols.length; k++) row1Values.push('');
    });
    const r1 = ws.addRow(row1Values);
    r1.height = 18;
    r1.eachCell(cell => { Object.assign(cell, hdrStyle); });
    // Merge cells for each version span in row 1
    let colOffset = 3; // 1-indexed: col 1 = #, col 2 = task, col 3 = first data col
    slotsWithCols.forEach(({ cols }) => {
      if (cols.length > 1) {
        ws.mergeCells(1, colOffset, 1, colOffset + cols.length - 1);
      }
      colOffset += cols.length;
    });
    // Row 2: individual column labels
    const row2Values = ['#', 'Task / Deliverable'];
    slotsWithCols.forEach(({ cols }) => {
      cols.forEach(c => row2Values.push(columnLabels[c.key] || c.label));
    });
    const r2 = ws.addRow(row2Values);
    r2.height = 18;
    r2.eachCell(cell => { Object.assign(cell, hdrStyle); });
    const primaryRows = getRows(slots[0].source);
    let taskN = 0;
    primaryRows.forEach(row => {
      const isStage = (row.indent || 0) === 0;
      const rowNum = isStage ? '' : (++taskN);
      const values = [rowNum, getCellVal(slots[0].source, row, 'taskName')];
      slotsWithCols.forEach(({ slot, cols }) => {
        cols.forEach(c => values.push(getCellVal(slot.source, row, c.key)));
      });
      const wsRow = ws.addRow(values);
      wsRow.height = isStage ? 22 : 16;
      if (isStage) {
        wsRow.font = xlsxFont(xs.style3, 9, { bold: true });
        wsRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F4F0' } };
      } else {
        wsRow.font = xlsxFont(xs.style1, 9, getTimeline(slots[0].source)[row.key || row.id]?.bold ? { bold: true } : {});
      }
      wsRow.eachCell(cell => {
        if (!cell.alignment) cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
        else cell.alignment = { ...cell.alignment, wrapText: true, vertical: 'middle' };
      });
      wsRow.getCell(2).alignment = { indent: (row.indent || 0) * 2, wrapText: true, vertical: 'middle', horizontal: 'left' };
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${project.name || 'comparison'}_comparison.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      if (format === 'csv') doExportCsv();
      else if (format === 'pdf') doExportPdf();
      else await doExportExcel();
      onClose();
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', width: '640px', maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}>
        {/* Header bar */}
        <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fdfdf9' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>Compare Versions</span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center', color: BRIO_GREY }}>
            <X size={15} />
          </button>
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
          {slots.map((slot, i) => {
            const slotCols = allColumns.filter(c => c.key !== 'taskName');
            const versionOptions = [
              { value: 'current', label: 'Current Timeline' },
              ...projectVersions.map(v => ({ value: v.id, label: getVersionLabel(v.id) })),
            ];
            const sourceLabels = ['Source A', 'Source B', 'Source C', 'Source D'];
            return (
              <div key={i} style={{ marginBottom: '14px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px' }}>
                {/* Slot header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: BRIO_BG, borderBottom: '1px solid ' + BRIO_BORDER }}>
                  <span style={{ fontSize: '11px', fontWeight: 500, color: BRIO_GREY, minWidth: '60px' }}>
                    {sourceLabels[i] || ('Source ' + (i + 1))}
                  </span>
                  <select value={slot.source} onChange={e => updateSlot(i, { source: e.target.value })}
                    style={{ flex: 1, border: '1px solid ' + BRIO_BORDER, fontFamily: 'Raleway, sans-serif', fontSize: '12px', padding: '4px 8px', background: '#fff', color: '#333', outline: 'none' }}>
                    {versionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button onClick={() => moveSlot(i, -1)} disabled={i === 0} title="Move up"
                    style={{ border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: i === 0 ? 'default' : 'pointer', padding: '2px 5px', color: i === 0 ? '#ccc' : BRIO_GREY, display: 'inline-flex', alignItems: 'center', fontSize: '12px' }}>
                    ↑
                  </button>
                  <button onClick={() => moveSlot(i, 1)} disabled={i === slots.length - 1} title="Move down"
                    style={{ border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: i === slots.length - 1 ? 'default' : 'pointer', padding: '2px 5px', color: i === slots.length - 1 ? '#ccc' : BRIO_GREY, display: 'inline-flex', alignItems: 'center', fontSize: '12px' }}>
                    ↓
                  </button>
                  {slots.length > 1 && (
                    <button onClick={() => removeSlot(i)} title="Remove"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#bbb', display: 'inline-flex', alignItems: 'center', padding: '2px' }}>
                      <X size={12} />
                    </button>
                  )}
                </div>
                {/* Column checkboxes */}
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '10px', color: '#aaa' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#aaa', fontWeight: 300 }}>
                        <input type="checkbox" checked disabled style={{ accentColor: BRIO_GOLD }} />
                        Task Name
                      </label>
                    </span>
                    <span>
                      <button onClick={() => updateSlot(i, { colKeys: new Set(slotCols.map(c => c.key)) })}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: BRIO_GOLD, fontFamily: 'Raleway, sans-serif', fontSize: '11px', padding: '0 2px' }}>Select all</button>
                      {' / '}
                      <button onClick={() => updateSlot(i, { colKeys: new Set() })}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: BRIO_GOLD, fontFamily: 'Raleway, sans-serif', fontSize: '11px', padding: '0 2px' }}>Clear</button>
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px 10px' }}>
                    {slotCols.map(c => (
                      <label key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#333', cursor: 'pointer', fontWeight: 300 }}>
                        <input type="checkbox" checked={slot.colKeys.has(c.key)} onChange={() => toggleSlotCol(i, c.key)} style={{ accentColor: BRIO_GOLD }} />
                        {columnLabels[c.key] || c.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          {slots.length < 4 && (
            <button onClick={addSlot}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px dashed ' + BRIO_BORDER, background: 'transparent', padding: '7px 14px', cursor: 'pointer', fontSize: '11px', color: BRIO_GREY, fontFamily: 'Raleway, sans-serif', width: '100%', justifyContent: 'center' }}>
              + Add comparison source
            </button>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid ' + BRIO_BORDER, display: 'flex', justifyContent: 'flex-end', gap: '8px', flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: '7px 16px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: BRIO_GREY, cursor: 'pointer', fontFamily: 'Raleway, sans-serif', fontSize: '12px' }}>
            Cancel
          </button>
          {onCompare && (
            <button onClick={() => { onCompare(slots); onClose(); }}
              style={{ padding: '7px 16px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400 }}>
              Compare
            </button>
          )}
          <button onClick={handleExport} disabled={exporting}
            style={{ padding: '7px 16px', border: 'none', background: BRIO_GOLD, color: '#fff', cursor: exporting ? 'wait' : 'pointer', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400 }}>
            {exporting ? 'Exporting…' : `Export ${format.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// GIVE ACCESS MODAL — grant per-user access to this project's TimelinesPage.
// Mirrors the Contacts page control: a Read only / Full access dropdown per
// user. 'full' = edit tasks/dates/dependencies/stages/rows/columns but NOT sync
// (sync stays admin/supervisor-only); 'read' = view only.
// ============================================================================

// Access levels offered by the Give Access dropdown.
const TIMELINE_ACCESS_OPTIONS = [
  { value: 'none', label: 'No access' },
  { value: 'read', label: 'Read only' },
  { value: 'full', label: 'Full access' },
];

// Admins and supervisors both SEE every project's timeline (they are never scoped
// or hidden). Their EDIT rights differ, though: an admin is full everywhere; a
// supervisor is full only on projects they are assigned to (project.supervisors)
// and read-only on the rest, unless an admin grants otherwise per project via Give
// Access. See effectiveTimelineAccess.
const isAdminRole = (role) => role === 'admin' || role === 'admin-pa';


// The EXPLICIT per-user grant from _grantedAccess, or null when there is none.
// Legacy shape is an array of ids (meant full edit); the new shape is a
// { [userId]: 'read' | 'full' | 'none' } map. Returns 'read' | 'full' | 'none' | null.
function timelineGrantLevel(grantsRaw, userId) {
  if (userId == null) return null;
  if (Array.isArray(grantsRaw)) {
    return grantsRaw.map(Number).includes(Number(userId)) ? 'full' : null;
  }
  if (grantsRaw && typeof grantsRaw === 'object') {
    const v = grantsRaw[String(userId)];
    return (v === 'read' || v === 'full' || v === 'none') ? v : null;
  }
  return null;
}

// Effective access for a user on a project, by role. Returns 'read'|'full'|'none'.
//   admin / admin-pa → always 'full'.
//   supervisor       → an admin-set grant ('read' | 'full') wins; otherwise 'full'
//                      when assigned to the project (project.supervisors), else
//                      'read'. A supervisor is never hidden — 'read' is their floor,
//                      so an explicit 'none' is ignored for supervisors.
//   everyone else    → an explicit grant wins (including 'none'); otherwise 'read'
//                      when assigned via team allocation, else 'none'.
// `isAssigned` is the role-appropriate assignment signal the caller passes in:
// project.supervisors membership for a supervisor, allocation hours > 0 otherwise.
function effectiveTimelineAccess(grantsRaw, userId, role, isAssigned) {
  if (isAdminRole(role)) return 'full';
  const g = timelineGrantLevel(grantsRaw, userId);
  if (role === 'supervisor') {
    if (g === 'full' || g === 'read') return g;
    return isAssigned ? 'full' : 'read';
  }
  if (g) return g;
  return isAssigned ? 'read' : 'none';
}

// The full set of user ids who supervise a project. Two independent links feed
// this and both count:
//   • project.supervisors      — names a single lead per project in practice
//   • teams.supervisor_ids + team_projects — the supervisor grouping the Teams
//     page maintains (team names encode their project codes, e.g. "UD/AR/SA")
// Reading only project.supervisors under-reported supervision: a supervisor who
// ran a project through their team but was not that project's named lead fell
// through to the 'read' floor in effectiveTimelineAccess, so they could not edit
// or sync a timeline they own. Nothing keeps the two lists in sync, so the drift
// was silent. Union them instead.
function supervisorIdsForProject(project, teams, teamProjects) {
  const set = new Set();
  (Array.isArray(project?.supervisors) ? project.supervisors : []).forEach(id => set.add(Number(id)));
  const projectId = Number(project?.id);
  if (!Number.isNaN(projectId)) {
    const teamIds = new Set(
      (teamProjects || [])
        .filter(tp => Number(tp.projectId) === projectId)
        .map(tp => Number(tp.teamId))
    );
    if (teamIds.size > 0) {
      (teams || []).forEach(t => {
        if (!teamIds.has(Number(t.id))) return;
        (Array.isArray(t.supervisorIds) ? t.supervisorIds : []).forEach(id => set.add(Number(id)));
      });
    }
  }
  return set;
}

// Set of user ids assigned to a project via the Team Allocation list
// (tt_allocations rows with allocated hours > 0). Used to scope the Timelines
// Give Access list to people actually on the project.
function allocatedUserIdsForProject(allocations, projectId) {
  const set = new Set();
  if (projectId == null) return set;
  (allocations || []).forEach(a => {
    if (Number(a.projectId) === Number(projectId) && (a.allocatedHours || 0) > 0) {
      set.add(Number(a.userId));
    }
  });
  return set;
}

function GiveAccessModal({ users, grants, assignedIds, supervisorIds, operatorIsAdmin, selfEditableId = null, onSave, onClose }) {
  const assigned = assignedIds || new Set();       // team-allocation assignment (regular users)
  const supAssigned = supervisorIds || new Set();  // project.supervisors (supervisors)
  // Natural (grant-free) default for a user on this project.
  const naturalFor = React.useCallback((u) => {
    if (isAdminRole(u.role)) return 'full';
    if (u.role === 'supervisor') return supAssigned.has(Number(u.id)) ? 'full' : 'read';
    return assigned.has(Number(u.id)) ? 'read' : 'none';
  }, [assigned, supAssigned]);
  // Current effective level for the row's initial value (an explicit grant wins).
  const levelFor = React.useCallback((u) => {
    if (isAdminRole(u.role)) return 'full';
    const g = timelineGrantLevel(grants, u.id);
    if (u.role === 'supervisor') return (g === 'full' || g === 'read') ? g : naturalFor(u);
    return g || naturalFor(u);
  }, [grants, naturalFor]);
  // Admin rows are always locked to Full. Only an admin may change a supervisor's
  // access; a supervisor operating the modal can only change regular users, plus their
  // own row when they are on the can_grant_timeline_access() list (selfEditableId).
  const rowLocked = React.useCallback((u) => {
    if (isAdminRole(u.role)) return true;
    if (u.role === 'supervisor') return !operatorIsAdmin && !(selfEditableId != null && Number(u.id) === Number(selfEditableId));
    return false;
  }, [operatorIsAdmin, selfEditableId]);
  // Supervisors are never hidden, so their rows omit "No access".
  const optionsFor = (u) => (u.role === 'supervisor'
    ? TIMELINE_ACCESS_OPTIONS.filter(o => o.value !== 'none')
    : TIMELINE_ACCESS_OPTIONS);
  const [levels, setLevels] = React.useState(() => {
    const init = {};
    users.forEach(u => { init[String(u.id)] = levelFor(u); });
    return init;
  });
  const setLevel = (id, val) => setLevels(prev => ({ ...prev, [String(id)]: val }));
  const handleSave = () => {
    // Preserve existing entries (e.g. for users not shown, or supervisor grants a
    // non-admin operator can't touch), then set each editable row. Admins are never
    // stored (always full). Store a selection only when it differs from the natural
    // default so redundant defaults don't bloat the map.
    const map = (grants && typeof grants === 'object' && !Array.isArray(grants)) ? { ...grants } : {};
    users.forEach(u => {
      const key = String(u.id);
      if (isAdminRole(u.role)) { delete map[key]; return; }
      if (rowLocked(u)) return; // supervisor row + non-admin operator → leave existing grant intact
      const sel = levels[key] || naturalFor(u);
      const natural = naturalFor(u);
      if (sel !== natural) map[key] = sel; else delete map[key];
    });
    onSave(map);
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '360px', maxWidth: '440px', width: '100%', padding: '20px 24px', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>Give Access</div>
        <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '14px', lineHeight: '15px' }}>
          Assigned people default to <b>Read only</b>; a supervisor assigned to this
          project defaults to <b>Full access</b>. <b>Full access</b> can edit tasks, dates,
          dependencies, stages, rows and columns; only admins and assigned supervisors
          also sync to Tracking. <b>No access</b> hides the project. Admins always have full
          access, and only an admin can change a supervisor's access.
        </div>
        {users.length === 0 ? (
          <div style={{ fontSize: '11px', fontWeight: 300, color: '#aaa', marginBottom: '18px' }}>No users available.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto', marginBottom: '18px' }}>
            {users.map(u => {
              const uid = Number(u.id);
              const locked = rowLocked(u);
              const roleTag = isAdminRole(u.role) ? 'admin' : (u.role === 'supervisor' ? 'supervisor' : null);
              return (
                <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 300, color: '#333' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email || ''}>
                    {u.name || u.email || `User ${uid}`}
                    {roleTag && <span style={{ fontSize: '9px', color: BRIO_GREY, marginLeft: 6 }}>{roleTag}</span>}
                  </span>
                  <select
                    value={isAdminRole(u.role) ? 'full' : (levels[String(uid)] || naturalFor(u))}
                    disabled={locked}
                    onChange={e => setLevel(uid, e.target.value)}
                    style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', fontFamily: 'Raleway, sans-serif', background: locked ? '#f5f5f5' : '#fff', color: locked ? '#aaa' : '#333', minWidth: '112px', cursor: locked ? 'default' : 'pointer' }}
                  >
                    {optionsFor(u).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '5px 14px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', color: '#555' }}>Cancel</button>
          <button
            onClick={handleSave}
            style={{ padding: '5px 14px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 400, border: '1px solid ' + BRIO_GOLD, background: BRIO_GOLD, color: '#fff', cursor: 'pointer' }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PER-PROJECT TIMELINE EDITOR (Smartsheet-style)
// ============================================================================

// Stage (or part) row END date, in order of precedence:
//   1. the Closure row has a date → the closure end (on time: Actual; paid late: paid date; unpaid and
//      overdue: today, so the stage end carries the delay);
//   2. no closure date but the stage is approved (billing page Appr.) → the billing page Actual date;
//   3. otherwise → the latest end among the stage's rows (`latestChildEnd`).
// Returns a Date (or null), matching what the aggregations store.
function resolveStageEnd(latestChildEnd, closureRow, stageRecord) {
  const closureEnd = parseDate(closureRow?.plannedEndDate);
  if (closureEnd) return closureEnd;
  const rec = stageRecord || {};
  if (rec.clientApprovalDate && rec.actualDate) {
    const actual = parseDate(rec.actualDate);
    if (actual) return actual;
  }
  return latestChildEnd;
}

// A stage (or part) with a client approval date (billing page Appr.) is closed to the planning sync: its
// end date is no longer pushed to the Tracking page's Planned column, and right-click Sync / Update
// Actuals are greyed out. Same freeze the paid date and the Tracking page's pen lock already give.
function isApprovalLocked(stageRecord) {
  return !!(stageRecord && stageRecord.clientApprovalDate);
}

function ProjectTimelineEditor({ project, stages, supabaseProjects: supabaseProjectsRaw, isAdmin, isSupervisor, currentUser, activeUsers, allocations = [], teams = [], teamProjects = [], activeClients, contractScopesHook, showSettings, setShowSettings, showPresetSettings, setShowPresetSettings, viewMode, setViewMode, showTabBar, setShowTabBar, activeTabId, setActiveTabId, isFullscreen, toggleFullscreen, toolbarsCollapsed = false, zoomIndex, setZoomIndex, pageZoomIndex, setPageZoomIndex, onNavigateToTracking, projectVersions = [], compareModalTriggerRef, pendingCompareVersionRef, reconcileErrorRowIds = null, visibleStageKeys = null, linkSowTemplate = null, baseTimeline = null, invoicesHook = null, undoGroupRef = null, canGrantTimelineAccess = false, brmTemplateEditable = false }) {
  // Paid dates from the Invoices page win over the stage timeline's own paidDate (see
  // applyInvoicePaidDates): the billing page shows the invoice's date, and the Invoicing / Closure rows
  // here must end on the same one. The summary is stored WITH the project it was fetched for and read
  // back only while that project is the one being edited (stage keys are shared across projects), and a
  // response that lands after a newer request started is dropped. This page never writes paidDate on
  // its own account; the billing page persists the corrected date.
  const [invoicePaidState, setInvoicePaidState] = useState({ projectId: null, summary: null });
  const invoicePaidReqRef = useRef(null);
  useEffect(() => {
    if (String(project.id) === TEMPLATE_PROJECT_ID || !invoicesHook?.fetchInvoiceSummaryForProject) return;
    const pid = project.id;
    invoicePaidReqRef.current = pid;
    invoicesHook.fetchInvoiceSummaryForProject(pid).then(summary => {
      if (String(invoicePaidReqRef.current) !== String(pid)) return;
      setInvoicePaidState({ projectId: pid, summary: summary || {} });
    });
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const storedTimeline = project.stageTimeline;
  const timeline = useMemo(() => {
    const base = storedTimeline || {};
    const summary = String(invoicePaidState.projectId) === String(project.id) ? invoicePaidState.summary : null;
    return summary ? applyInvoicePaidDates(base, summary) : base;
  }, [storedTimeline, invoicePaidState, project.id]);
  // Template Builder context — enables authoring affordances (cross-stage drag, inline
  // deliverable rename, Link to SOW) intentionally restricted for real projects. The
  // template "project" has no supervisors/allocations, so access there follows role
  // (managers author templates), not per-project assignment.
  const isTemplateBuilder = String(project.id) === TEMPLATE_PROJECT_ID;
  // A standalone project (see STANDALONE_TIMELINE_CODES) has no scope and no Tracking / Invoices link.
  const isStandalone = !isTemplateBuilder && isStandaloneTimeline(project);
  // The BRM template builder: a standalone timeline held in memory, edited by can_edit_brm_templates().
  const isBrmTemplate = String(project.id) === BRM_TEMPLATE_PROJECT_ID;
  const isManager = isAdmin || isSupervisor; // admins + supervisors see every project
  // The current user's ASSIGNED-supervisor status on this project (named on the
  // project, or supervising it through a team — see supervisorIdsForProject) —
  // drives a supervisor's full access and the on-load auto-sync.
  const projectSupervisorIds = supervisorIdsForProject(project, teams, teamProjects);
  const isAssignedSupervisor = isSupervisor && projectSupervisorIds.has(Number(currentUser?.id));
  // Give access: admins anywhere, a project's own supervisors, and the can_grant_timeline_access()
  // allow-list on every project. Not in the Template Builder for the allow-list, since a template's
  // grants never reach a project (applyTemplateToProject keeps the project's own).
  const canGiveAccess = !isBrmTemplate && (isAdmin || isAssignedSupervisor || (canGrantTimelineAccess && !isTemplateBuilder));
  // Effective access for this user on THIS project. Admin → full everywhere.
  // Supervisor → full on projects they're assigned to (or where an admin granted full),
  // read-only elsewhere. Regular user → explicit grant, else 'read' when allocated, else
  // 'none'. 'full' grants edit (sync is further gated — see canSync); 'read' is view-only.
  const myRole = isAdmin ? 'admin' : (isSupervisor ? 'supervisor' : 'user');
  const myIsAssigned = isSupervisor
    ? isAssignedSupervisor
    : allocatedUserIdsForProject(allocations, project.id).has(Number(currentUser?.id));
  const myLevel = isTemplateBuilder
    ? (isManager ? 'full' : 'none')
    : isBrmTemplate ? (brmTemplateEditable ? 'full' : 'read')
    : effectiveTimelineAccess(timeline._grantedAccess, currentUser?.id, myRole, myIsAssigned);
  // A regular user (not admin or supervisor) granted Full access through "Give access" edits the
  // timeline like the project's supervisor: canEdit is on, so every row and column header right click
  // option shows. The database will not let them UPDATE projects, so their saves go through
  // save_granted_project_timeline, which keeps the access map, Sync to tracking and every stage's
  // Tracking fields as stored. Sync (canSync) and the items that write Tracking dates stay off for them.
  const isGrantedEditor = !isTemplateBuilder && !isBrmTemplate && !isManager && myLevel === 'full';
  // A supervisor holding Full on a project they do not supervise (Neha's own grant, or one an admin
  // set) cannot UPDATE it under RLS either. They save the same way and never sync to Tracking there:
  // no Sync toggle, no tracking items, and no automatic writes just from opening the page.
  const isGrantOnlySupervisor = !isTemplateBuilder && !isBrmTemplate && isSupervisor && !isAssignedSupervisor && myLevel === 'full';
  const savesViaGrant = isGrantedEditor || isGrantOnlySupervisor;
  const canEdit = myLevel === 'full';
  const canEditTasks = canEdit;
  const ownsRow = useCallback(() => canEdit, [canEdit]);
  const canEditCell = useCallback(() => canEdit, [canEdit]);
  // Every timeline write in this editor goes through supabaseProjects. For a granted editor, send it
  // through the granted save instead of a plain UPDATE (which RLS would refuse as "no rows updated").
  const supabaseProjects = useMemo(() => {
    if (!savesViaGrant || !supabaseProjectsRaw) return supabaseProjectsRaw;
    return {
      ...supabaseProjectsRaw,
      updateProjectTimelineDiff: (id, base, next, opts = {}) =>
        supabaseProjectsRaw.updateProjectTimelineDiff(id, base, next, { ...opts, granted: true }),
      updateProject: (id, updates) => {
        const keys = Object.keys(updates || {});
        if (keys.length === 1 && keys[0] === 'stageTimeline') {
          return supabaseProjectsRaw.updateProjectTimelineDiff(id, timeline, updates.stageTimeline, { granted: true });
        }
        return Promise.resolve({ success: false, error: 'Your access covers this timeline only.' });
      },
    };
  }, [savesViaGrant, supabaseProjectsRaw, timeline]);
  // Sync to Tracking (manual Sync / Sync-to-tracking toggle / right-click Sync & Update
  // Actuals, and auto-push on edit) is a manager capability: admins anywhere, a supervisor
  // only where they have full (assigned, or admin-granted). A granted "Full access" regular
  // user can edit the timeline but never syncs.
  const canSync = !isStandalone && !isGrantOnlySupervisor && (isAdmin || (isSupervisor && myLevel === 'full'));
  // Only the project's ASSIGNED supervisor or an admin may trigger the automatic, on-load
  // sync writes (invoicing/closure backfill, start-row cascade, predecessor reconciliation,
  // targetDate sync). Any other viewer — a supervisor NOT assigned to this project, or a
  // granted-access user — must never cause a write just by opening the Timelines page.
  // Manual edits remain governed by canEdit. (The Template Builder edits a fake project
  // through proxySupabase, so it keeps the normal canEdit gate.)
  const canAutoSync = isTemplateBuilder ? canEdit : (isAdmin || isAssignedSupervisor);
  // Shared export styling from Accounting Settings so timeline PDF/Excel exports match the firm's
  // configured document fonts (Scope PDF font styles for PDF; Excel export font styles for XLSX).
  const { scopePdfStyles, excelExportStyles } = useSettings();
  const paymentTermsDays = useMemo(() => {
    if (!activeClients || !project.clientId) return null;
    const client = activeClients.find(c => c.id === project.clientId);
    return client?.paymentTermsDays || null;
  }, [activeClients, project.clientId]);
  const [saving, setSaving] = useState(null);
  const [savedCells, setSavedCells] = useState(new Set());
  const [flashedStripEnd, setFlashedStripEnd] = useState(new Set()); // rowKeys whose mini-strip end-date box should flash
  const [contextMenu, setContextMenu] = useState(null);
  // Generic confirm dialog: { message, confirmLabel, cancelLabel, onConfirm }. Used by the
  // "Turn on sync to tracking" prompt (Sync/Update-Actuals while sync is off) and the closure
  // "Delayed" badge → Ignore/Cancel dialog.
  const [confirmModal, setConfirmModal] = useState(null);
  const [syncCellMenu, setSyncCellMenu] = useState(null); // { x, y, rowId } — for invoicing/closure date cells
  const [presetTaskModal, setPresetTaskModal] = useState(null); // { stageId, stageName } — Add Preset Task dialog
  const [stageLinkModal, setStageLinkModal] = useState(null); // { customStageId } — link custom stage to another
  const [giveAccessModal, setGiveAccessModal] = useState(false); // Give Access modal open
  const [manualOverrideMenu, setManualOverrideMenu] = useState(null); // { x, y, closureRowId, stageKey, refDate, manualDays } — Manual Override popup
  const [subStageStripMenu, setSubStageStripMenu] = useState(null); // { x, y, rowKey, stageId, partId, section, endDateStr, targetDateStr }
  const [delayBadgeMenu, setDelayBadgeMenu] = useState(null); // { x, y, rowKey, hidden } — right click on a Delayed badge
  const [editingPartName, setEditingPartName] = useState(null); // { stageId, partId } — inline part-stage rename
  const [partNameValue, setPartNameValue] = useState('');
  const [editingCustomStageName, setEditingCustomStageName] = useState(null); // rowId of custom stage being renamed inline
  const [customStageNameValue, setCustomStageNameValue] = useState('');
  // SOW-linked deliverable name append: admins/supervisors may only ADD text after the fixed SOW name
  // (kept as an immutable prefix), never rename it outright. editingSowAddon holds the rowId being edited;
  // sowAddonValue is the text after the "<SOW name>: " prefix.
  const [editingSowAddon, setEditingSowAddon] = useState(null);
  const [sowAddonValue, setSowAddonValue] = useState('');
  const [collapsedRows, setCollapsedRows] = useState(() => {
    const saved = project.stageTimeline?._viewSettings?.collapsedRows;
    return new Set(Array.isArray(saved) ? saved : []);
  }); // IDs of collapsed parent rows
  const [collapsedStripRows, setCollapsedStripRows] = useState(() => {
    const saved = project.stageTimeline?._viewSettings?.collapsedStripRows;
    return new Set(Array.isArray(saved) ? saved : []);
  }); // IDs of stage/substage rows whose date strip is explicitly collapsed; all others are expanded by default
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [columnWidths, setColumnWidths] = useState(() => (project.stageTimeline?._viewSettings?.columnWidths) || {});  // key -> width override
  // Ordered array of column keys; empty = default. Saved with the view settings, so it is shared per
  // project like the column widths.
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = project.stageTimeline?._viewSettings?.columnOrder;
    return Array.isArray(saved) ? saved : [];
  });
  const [columnLabels, setColumnLabels] = useState({});  // key -> renamed label
  const [headerContextMenu, setHeaderContextMenu] = useState(null); // { x, y, colKey }
  const [renamingColumn, setRenamingColumn] = useState(null); // colKey being renamed
  const [renameValue, setRenameValue] = useState('');
  const [unhidePanel, setUnhidePanel] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false); // confirm "Reset timelines"
  const [exportSubOpen, setExportSubOpen] = useState(null); // 'export' | 'compare' | 'import' | null
  const [exportStageModal, setExportStageModal] = useState(null); // null | { type: 'pdf'|'excel'|'csv', selectedStages: Set }
  const [compareModal, setCompareModal] = useState(null); // null | { format: 'pdf'|'excel'|'csv' }
  const [compareSlots, setCompareSlots] = useState(null); // null = normal mode; array = inline compare mode
  const [showCompareConfig, setShowCompareConfig] = useState(false);
  const [compareZoomIndex, setCompareZoomIndex] = useState(4); // independent zoom for the compare view
  const [compareTaskColW, setCompareTaskColW] = useState(220); // width of the compare Task/Deliverable column (px)

  // Drag-resize just the compare table's Task/Deliverable column — independent of the main table's
  // columnWidths so it never affects the editable timeline table (only this read-only side-by-side view).
  const startCompareTaskResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = compareTaskColW;
    const zoom = PAGE_ZOOM_LEVELS[compareZoomIndex]?.scale || 1; // compare view is CSS-zoomed; convert screen px → layout px
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / zoom;
      setCompareTaskColW(Math.max(120, Math.round(startW + dx)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [compareTaskColW, compareZoomIndex]);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const currentWeekId = useMemo(() => getCurrentWeekId(), []);
  const projectTasksHook = useProjectWeeklyTasks(project.id, currentWeekId, false);
  const momHook = useMOMData(project.id);
  // Set of project task IDs that currently exist — used to detect deleted tasks
  const linkedTaskIds = useMemo(() => new Set(projectTasksHook.tasks.map(t => t.id)), [projectTasksHook.tasks]);

  // Maps stageKey → partStages array for all stage rows with at least one partStage
  const partStagesMap = useMemo(() => {
    const map = {};
    // Use the timeline object to find any key with partStages (covers standard + misc stages)
    Object.entries(timeline).forEach(([key, val]) => {
      if (val && Array.isArray(val.partStages) && val.partStages.length > 0) {
        map[key] = val.partStages;
      }
    });
    return map;
  }, [timeline]);

  // Map of virtual part-stage header keys (substage_<stage>_<part>) -> { stageId, partId }.
  // Used to resolve a part stage's own date record for its Start/Invoicing/Closure rows.
  const partKeyInfo = useMemo(() => {
    const m = new Map();
    Object.entries(partStagesMap).forEach(([stageId, parts]) => {
      (parts || []).forEach(p => m.set(`substage_${stageId}_${p.id}`, { stageId, partId: p.id }));
    });
    return m;
  }, [partStagesMap]);

  // Resolve the "stage-like" date record for a (possibly part-stage) key.
  // Normal stage key -> timeline[key]; part header key substage_<S>_<P> -> the part object.
  const resolveStageRecord = useCallback((key) => {
    const info = partKeyInfo.get(key);
    if (info) {
      const parts = timeline[info.stageId]?.partStages || [];
      return parts.find(p => p.id === info.partId) || {};
    }
    return timeline[key] || {};
  }, [partKeyInfo, timeline]);

  const resizingRef = useRef(null); // { colKey, startX, startWidth, initialTableWidth }
  const tableRef = useRef(null); // reference to the <table> element for width measurements
  const tableScrollRef = useRef(null); // reference to the horizontal scroll container for the table
  const mirrorScrollRef = useRef(null); // reference to the sticky scrollbar at the bottom of the viewport
  const pageZoomScaleRef = useRef(PAGE_ZOOM_LEVELS[pageZoomIndex].scale); // tracks current zoom for resize math
  const [tableMinWidthPx, setTableMinWidthPx] = useState(null);
  const viewSettingsInitialized = useRef(false); // true once view settings have been loaded from DB for the current project
  const viewSaveTimerRef = useRef(null); // debounce timer for persisting view settings
  const timelineRef = useRef(timeline); // always-current timeline ref for use in async callbacks

  // Undo / redo history (full-timeline snapshots — every data change to the stage timeline is captured,
  // so a reset, a template apply and an ordinary edit are all one undo step each). Declared here so the
  // reset handlers above can reach the stack; the settings-list "Undo" and Ctrl+Z share it.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const isUndoRedoRef = useRef(false);
  // Set right before a derived auto-sync write (the continuous targetDate→Tracking sync) so the
  // undo-capture effect does NOT record it as its own step. Without this a single date edit can
  // leave a stray "sync only" entry on the stack, so the first Ctrl+Z reverts an invisible
  // targetDate change instead of the date the user just picked.
  const autoSyncWriteRef = useRef(false);
  const prevTimelineRef = useRef(project.stageTimeline);
  const [canUndo, setCanUndo] = useState(false); // enables the settings-list "Undo" (undo stack non-empty)

  // Keep zoom scale ref in sync so handleResizeStart always has the current value
  useEffect(() => { pageZoomScaleRef.current = PAGE_ZOOM_LEVELS[pageZoomIndex].scale; }, [pageZoomIndex]);

  // Keep timelineRef in sync with latest timeline
  useEffect(() => { timelineRef.current = timeline; }, [timeline]);

  // When column widths change (resize), reset all textarea heights so rows auto-expand to fit
  useLayoutEffect(() => {
    if (!tableRef.current) return;
    tableRef.current.querySelectorAll('textarea').forEach(el => {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    });
  }, [columnWidths]);
  const importFileRef = useRef(null);
  const cellClipboard = useRef(null); // { colKey, value } — cell copy/paste clipboard
  const columnClipboard = useRef(null); // { colKey, values: { rowKey: value } } — column copy/paste
  const rowClipboard = useRef(null); // { rowData } — row copy/paste clipboard
  const [addColumnDialog, setAddColumnDialog] = useState(null); // null | afterColKey string
  const [manageOptionsDropdown, setManageOptionsDropdown] = useState(null); // { x, y, colKey }
  const [addCategoriesMenu, setAddCategoriesMenu] = useState(null); // { x, y } — assigned-to categories panel
  const [predecessorModal, setPredecessorModal] = useState(null); // { rowKey, value } | null
  const [linkSowModal, setLinkSowModal] = useState(null); // { rowId, rowName, rowStageKey, sowRef } | null
  const [sowLinkMenu, setSowLinkMenu] = useState(null); // { x, y, rowId, rowName, rowStageKey, sowRef, hasLink } — badge add/remove-link menu
  const [predInlineEdit, setPredInlineEdit] = useState(null); // { rowKey } | null — double-click inline edit
  const [closureInvoiceModal, setClosureInvoiceModal] = useState(null); // { stageKey, stageName } | null
  const [trackingTriggerModal, setTrackingTriggerModal] = useState(null); // { triggerType, stageKey, partId, stageName } | null
  const [stageLinkMenu, setStageLinkMenu] = useState(null); // { startRowId, x, y } — stage/substage date picker for start_ rows
  const [contractDatesModal, setContractDatesModal] = useState(null); // { selectedStages: Set, startDate, sets: [{id,months,days,applied}] }
  const [subStageAssignMenu, setSubStageAssignMenu] = useState(null); // { x, y, rowId, stageId } — assign panel
  const [carryForwardMenu, setCarryForwardMenu] = useState(null); // { x, y, rowId } — carry-forward target picker
  const [showHiddenRows, setShowHiddenRows] = useState(false);
  // ── Tabs (stage/part filter views) ──────────────────────────────────
  // Tabs are a per-project filter over which stages/parts are shown in the table; everything
  // else (links, contract dates, presets, cascade) still operates on the full project.
  // showTabBar / activeTabId are LIFTED to the parent (TimeTrackerPro) and passed in as props so
  // the same active tab is shared with the gantt view (which is rendered outside this editor).
  // Persistence of these two into _viewSettings still happens here (the snapshot below reads them).
  const [tabEditor, setTabEditor] = useState(null); // { tabId|null (null = new), name, keys:Set } | null
  // Per-project toggle for the small grey helper notes shown inside cells.
  // Persisted in _viewSettings so it works independently for every project.
  const [showNotes, setShowNotes] = useState(() => {
    return project.stageTimeline?._viewSettings?.showNotes !== false; // default: shown
  });
  // Auto-sync toggle: when OFF, editing dates never writes through to the Tracking page (no
  // targetDate / contract-date sync). Defaults OFF — sync is an explicit per-project opt-in (only a
  // stored `syncEnabled === true` enables it); an unset value is treated as off. Persisted in
  // _viewSettings (so Reset timelines keeps it) and mirrored to a ref because the async
  // handleFieldChange closure reads it after an await.
  const [syncEnabled, setSyncEnabled] = useState(() => {
    // Non-sync users (anyone but admin/supervisor) can never enable sync.
    return canSync && project.stageTimeline?._viewSettings?.syncEnabled === true; // default: off (explicit opt-in only)
  });
  const syncEnabledRef = useRef(syncEnabled);
  // Gate the ref on canSync too, so a granted "Full access" user's edits never
  // push to Tracking even if a stored syncEnabled=true slipped through.
  useEffect(() => { syncEnabledRef.current = canSync && syncEnabled; }, [syncEnabled, canSync]);
  // An edit by a regular user with Full access switches Sync to tracking off in the database (see
  // save_granted_project_timeline, which records who and when in _syncAutoOff). Follow the stored
  // value down, so a supervisor's open page does not save its old "on" back with its view settings.
  const storedSyncEnabled = timeline._viewSettings?.syncEnabled === true;
  useEffect(() => { if (!storedSyncEnabled) setSyncEnabled(false); }, [storedSyncEnabled]);
  const syncAutoOff = (timeline._syncAutoOff && typeof timeline._syncAutoOff === 'object') ? timeline._syncAutoOff : null;
  // Sync-to-Tracking PAUSE — a session-level gate on the writes that PUSH a planning date to the Tracking
  // page's Planned (targetDate) / Contract columns: the continuous targetDate sync and the on-edit
  // propagation. It does NOT gate the planning page's own internal derivations (invoicing/closure
  // backfill, start-row preset cascade, predecessor-number reconciliation) — those are scoped merges that
  // never touch the Tracking columns, and must keep running (e.g. so a reset regenerates preset dates and
  // so cleared invoice/paid dates propagate even with the sync toggle off). DEFAULTS TO PAUSED on every
  // page/project open (deliberately NOT persisted), so merely viewing — or editing planning dates on —
  // the Timelines page never pushes to the Tracking Planned/Contract columns; the assigned supervisor /
  // admin resumes it explicitly via the toolbar sync icon. Reset to paused whenever the project changes.
  // A ref mirrors it for the event handlers (live value, no stale closure), exactly like syncEnabledRef.
  const [syncPaused, setSyncPaused] = useState(true);
  useEffect(() => { setSyncPaused(true); }, [project.id]);
  const syncPausedRef = useRef(syncPaused);
  useEffect(() => { syncPausedRef.current = syncPaused; }, [syncPaused]);
  // Disc-colour legend: sideways-collapsible via a chevron so it can tuck away when not needed.
  // Preference is persisted (UI-only, localStorage) so it survives reloads.
  const [legendCollapsed, setLegendCollapsed] = useState(() => {
    try { return localStorage.getItem('brioTimelineLegendCollapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('brioTimelineLegendCollapsed', legendCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [legendCollapsed]);
  // Part-stages always appear under their stage automatically — there is no per-part "show" checkbox.
  // DERIVED (not user-toggled) from partStagesMap: every stage → the full set of its part ids, so any
  // part that exists is displayed. All the display / numbering / indentation logic below already works
  // for an arbitrary active set; handing it the FULL set is exactly what makes every part show.
  const activeSubStages = useMemo(() => {
    const m = {};
    Object.entries(partStagesMap).forEach(([stageId, parts]) => {
      if (parts && parts.length) m[stageId] = new Set(parts.map(p => p.id));
    });
    return m;
  }, [partStagesMap]); // { [stageId]: Set<partId> } — every defined sub-stage is always shown
  const [selectedRows, setSelectedRows] = useState(new Set()); // row IDs selected via Ctrl+click on # column
  const [momPanel, setMOMPanel] = useState(null); // { rowId, rowKey, rowName } | null — open MOM side panel
  // Cells picked with Ctrl/Cmd+click, as `${rowKey}|${colKey}`. Editing one of them writes the same
  // value into every selected cell of that column (handleFieldChange).
  const [selectedCells, setSelectedCells] = useState(new Set());
  // The open Format toolbar: { x, y, targets: [{ kind: 'cell'|'row'|'col', rowKey?, colKey? }] }.
  const [formatBar, setFormatBar] = useState(null);
  useEffect(() => { setFormatBar(null); }, [project.id]);
  useEffect(() => { setSelectedCells(new Set()); }, [project.id]);
  // Escape clears the cell selection.
  useEffect(() => {
    if (selectedCells.size === 0) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedCells(new Set()); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedCells.size]);

  // Click outside the table clears row selection
  useEffect(() => {
    if (selectedRows.size === 0) return;
    const handler = (e) => {
      if (tableScrollRef.current && !tableScrollRef.current.contains(e.target)) {
        setSelectedRows(new Set());
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedRows.size]);

  // Re-initialize view settings whenever the user switches to a different project
  useEffect(() => {
    viewSettingsInitialized.current = false;
    const vs = (project.stageTimeline || {})._viewSettings || {};
    setColumnWidths(vs.columnWidths || {});
    setColumnOrder(Array.isArray(vs.columnOrder) ? vs.columnOrder : []);
    // activeSubStages is derived from partStagesMap now (all parts always shown) — nothing to restore.
    setCollapsedRows(new Set(Array.isArray(vs.collapsedRows) ? vs.collapsedRows : []));
    setCollapsedStripRows(new Set(Array.isArray(vs.collapsedStripRows) ? vs.collapsedStripRows : []));
    setShowNotes(vs.showNotes !== false);
    setSyncEnabled(canSync && vs.syncEnabled === true); // default: off (explicit opt-in only)
    // Mark initialized after the batched state updates have flushed
    setTimeout(() => { viewSettingsInitialized.current = true; }, 0);
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced persist of view settings — fires 1.5 s after the last change
  useEffect(() => {
    if (!viewSettingsInitialized.current) return;
    if (viewSaveTimerRef.current) clearTimeout(viewSaveTimerRef.current);
    const snapshot = {
      columnWidths,
      columnOrder,
      // activeSubStages is no longer persisted — it's derived (all parts always shown), not a saved view pref.
      collapsedRows: [...collapsedRows],
      collapsedStripRows: [...collapsedStripRows],
      showNotes,
      // Non-sync users (granted "Full access") have syncEnabled forced off in
      // their session; persist the STORED value so their view-settings saves
      // never clobber an admin/supervisor's sync-to-tracking toggle.
      syncEnabled: canSync ? syncEnabled : (timelineRef.current?._viewSettings?.syncEnabled === true),
      showTabBar,
      activeTabId,
    };
    viewSaveTimerRef.current = setTimeout(() => {
      persistTimeline({ ...timelineRef.current, _viewSettings: snapshot });
    }, 1500);
    return () => { if (viewSaveTimerRef.current) clearTimeout(viewSaveTimerRef.current); };
  }, [columnWidths, columnOrder, collapsedRows, collapsedStripRows, showNotes, syncEnabled, showTabBar, activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic state — set immediately on insert/delete/reorder so the UI
  // reflects new row order and remapped predecessor numbers without waiting
  // for the Supabase round-trip to complete.
  const [optimisticRows, setOptimisticRows] = useState(null);   // null = use prop data
  const [optimisticPreds, setOptimisticPreds] = useState({});   // rowId -> predecessors string
  const isOptimisticActive = useRef(false);
  // Clear optimistic state once the DB write lands and the prop updates
  useEffect(() => {
    setOptimisticRows(null);
    setOptimisticPreds({});
    isOptimisticActive.current = false;
  }, [timeline]);
  const colDragRef = useRef(null); // long-press drag state
  const [colDragActive, setColDragActive] = useState(null); // colKey being dragged
  const [colDragOver, setColDragOver] = useState(null); // colKey being hovered
  const [selectedColumnKeys, setSelectedColumnKeys] = useState(new Set()); // multi-selected columns

  // Visible columns (built-in + custom, respecting columnOrder, filtered by hidden set)
  const visibleColumns = useMemo(() => {
    const customCols = (timeline._customColumns || []).map(c => ({
      ...c, width: c.width || '8%', custom: true,
    }));
    const allCols = [...SCHEDULE_COLUMNS, ...customCols];
    let ordered;
    if (columnOrder.length > 0) {
      const byKey = Object.fromEntries(allCols.map(c => [c.key, c]));
      const seen = new Set();
      ordered = [
        ...columnOrder.map(k => byKey[k]).filter(Boolean),
        ...allCols.filter(c => { if (seen.has(c.key)) return false; seen.add(c.key); return !columnOrder.includes(c.key); }),
      ];
    } else {
      ordered = allCols;
    }
    return ordered.filter(c => !hiddenColumns.has(c.key));
  }, [hiddenColumns, timeline._customColumns, columnOrder]);

  // Effective visible columns (no group collapse logic needed — Contract/Actual categories removed)
  const effectiveVisibleColumns = useMemo(() => visibleColumns, [visibleColumns]);

  // Open inline compare view — callable from eye icon (via ref) or from CompareVersionsModal "Compare" button
  const openInlineCompare = useCallback((initVersionId, initSlots) => {
    if (initSlots) { setCompareSlots(initSlots); return; }
    const defaultColKeys = new Set(effectiveVisibleColumns.filter(c => c.key !== 'taskName').map(c => c.key));
    setCompareSlots([
      { source: 'current', colKeys: defaultColKeys },
      { source: initVersionId || (projectVersions.length > 0 ? projectVersions[projectVersions.length - 1].id : 'current'), colKeys: defaultColKeys },
    ]);
  }, [effectiveVisibleColumns, projectVersions]);

  // Keep an always-current ref so mount-time effects can call openInlineCompare
  const openInlineCompareRef = useRef(openInlineCompare);
  useEffect(() => { openInlineCompareRef.current = openInlineCompare; }, [openInlineCompare]);

  // Expose to TimelinesPage eye icon
  useEffect(() => {
    if (compareModalTriggerRef) compareModalTriggerRef.current = (versionId) => openInlineCompare(versionId);
    return () => { if (compareModalTriggerRef) compareModalTriggerRef.current = null; };
  }, [compareModalTriggerRef, openInlineCompare]);

  // Consume pending compare-version set by TimelinesPage when user clicks eye while viewing a snapshot
  useEffect(() => {
    if (pendingCompareVersionRef?.current) {
      const vid = pendingCompareVersionRef.current;
      pendingCompareVersionRef.current = null;
      setTimeout(() => openInlineCompareRef.current?.(vid), 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Project settings: support multiple named presets stored in _settingsPresets/_activePresetId.
  // Migrate legacy _settings (single object) on first load.
  const settingsPresets = useMemo(() => {
    if (Array.isArray(timeline._settingsPresets) && timeline._settingsPresets.length > 0) {
      return timeline._settingsPresets.map(p => sanitizePresetColumns({
        ...makeDefaultPreset(p.name || 'Project Setting 1'),
        ...p,
        dateRange: { ...DEFAULT_PROJECT_SETTINGS.dateRange, ...(p.dateRange || {}) },
        options: { ...DEFAULT_PROJECT_SETTINGS.options, ...(p.options || {}) },
        dependencies: { ...DEFAULT_PROJECT_SETTINGS.dependencies, ...(p.dependencies || {}) },
        workingDays: { ...DEFAULT_PROJECT_SETTINGS.workingDays, ...(p.workingDays || {}) },
      }));
    }
    // Migrate from legacy _settings
    if (timeline._settings && typeof timeline._settings === 'object') {
      const legacy = timeline._settings;
      return [sanitizePresetColumns({ id: 'preset_migrated', name: 'Project Setting 1',
        dateRange: { ...DEFAULT_PROJECT_SETTINGS.dateRange, ...(legacy.dateRange || {}) },
        options: { ...DEFAULT_PROJECT_SETTINGS.options, ...(legacy.options || {}) },
        dependencies: { ...DEFAULT_PROJECT_SETTINGS.dependencies, ...(legacy.dependencies || {}) },
        workingDays: { ...DEFAULT_PROJECT_SETTINGS.workingDays, ...(legacy.workingDays || {}) },
      })];
    }
    return [{ id: 'preset_default', name: 'Project Setting 1', ...DEFAULT_PROJECT_SETTINGS }];
  }, [timeline._settingsPresets, timeline._settings]);

  const activePresetId = timeline._activePresetId || settingsPresets[0]?.id;

  const projectSettings = useMemo(() => {
    const active = settingsPresets.find(p => p.id === activePresetId) || settingsPresets[0];
    return active || DEFAULT_PROJECT_SETTINGS;
  }, [settingsPresets, activePresetId]);

  const addCalendarDays = useCallback((dateStr, count) => {
    if (!dateStr || count == null || count < 0) return dateStr;
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + count);
    return formatDate(d);
  }, []);

  const addWorkDays = useCallback((dateStr, count) => {
    const d = parseDate(dateStr);
    if (!d || !count || count === 0) return dateStr;
    const workDays = new Set(projectSettings.workingDays.days);
    const holidaySet = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
    let cur = new Date(d.getTime());
    let remaining = Math.abs(count);
    const step = count > 0 ? DAY_MS : -DAY_MS;
    while (remaining > 0) {
      cur = new Date(cur.getTime() + step);
      const dow = cur.getDay();
      const iso = formatDate(cur);
      if (workDays.has(dow) && !holidaySet.has(iso)) remaining--;
    }
    return formatDate(cur);
  }, [projectSettings]);

  const handleSaveSettings = useCallback(async (newPresets, newActivePresetId) => {
    if (!supabaseProjects) return;
    const newTimeline = { ...timeline, _settingsPresets: newPresets, _activePresetId: newActivePresetId };
    // Remove legacy _settings if migrated
    delete newTimeline._settings;
    await writeTimelineDiff(supabaseProjects, project.id, timeline, newTimeline, { allowTracking: syncEnabledRef.current });
    setShowSettings(false);
  }, [supabaseProjects, project.id, timeline]);

  // ── Column resize via drag on header edge ──────────────────────────────
  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.target.parentElement;
    const handleEl = e.target; // capture the 4px handle div to reset it on mouseup
    // Divide by zoom scale so columnWidths always stores unscaled (logical) pixel values
    const scale = pageZoomScaleRef.current;
    const startWidth = th.offsetWidth / scale;

    const tableEl = th.closest('table');
    const initialTableWidth = tableEl ? tableEl.offsetWidth / scale : null;

    // Capture the current px width of EVERY column from the DOM.
    // This snapshot is applied inside onMove so the snap + target-column update
    // happen in a SINGLE setColumnWidths call (one React render), preventing the
    // "all columns stretch" flash that occurs when two separate renders fire.
    // We include ALL columns (even noResize) so percentage-based widths get frozen to px.
    const domSnapWidths = {};
    if (tableEl) {
      Array.from(tableEl.querySelectorAll('thead th[data-col-key]')).forEach(el => {
        const k = el.dataset.colKey;
        if (k) {
          domSnapWidths[k] = el.offsetWidth / scale + 'px';
        }
      });
    }

    // Capture which columns are co-selected at drag start (snapshot — avoids stale closure)
    const coSelectedKeys = selectedColumnKeys.has(colKey) && selectedColumnKeys.size > 1
      ? new Set(selectedColumnKeys)
      : null;
    // Capture starting widths for all co-selected columns (unscaled)
    const coSelectedStartWidths = {};
    if (coSelectedKeys && tableEl) {
      Array.from(tableEl.querySelectorAll('thead th[data-col-key]')).forEach(el => {
        if (coSelectedKeys.has(el.dataset.colKey)) {
          coSelectedStartWidths[el.dataset.colKey] = el.offsetWidth / scale;
        }
      });
    }

    // Read per-column minimum widths from data-min-width DOM attributes
    const colMinWidths = {};
    if (tableEl) {
      Array.from(tableEl.querySelectorAll('thead th[data-col-key]')).forEach(el => {
        const minPx = parseInt(el.getAttribute('data-min-width') || '60') || 60;
        colMinWidths[el.dataset.colKey] = minPx;
      });
    }

    resizingRef.current = { colKey, startX: e.clientX, startWidth, initialTableWidth, colMinWidths, domSnapWidths };

    // The drag moves the DOM directly and commits to state once, on mouse up: a state update per mouse
    // move re-rendered the whole table on every pixel, which is what made resizing lag.
    const thByKey = {};
    if (tableEl) {
      Array.from(tableEl.querySelectorAll('thead th[data-col-key]')).forEach(el => {
        thByKey[el.dataset.colKey] = el;
        el.style.width = domSnapWidths[el.dataset.colKey]; // freeze % widths to px for the drag
      });
      // Full screen centres the table, so a drag would grow it out of both sides. While dragging, pin
      // its left edge so the column grows to the right under the cursor; mouse up centres it again.
      if (isFullscreen) {
        const left = parseFloat(window.getComputedStyle(tableEl).marginLeft) || 0;
        tableEl.style.marginLeft = left + 'px';
        tableEl.style.marginRight = '0px';
        resizingRef.current.pinned = true;
      }
    }
    let latest = null; // { widths: { colKey: px }, tableWidth }
    let frame = 0;
    const paint = () => {
      frame = 0;
      if (!latest) return;
      Object.entries(latest.widths).forEach(([k, w]) => { if (thByKey[k]) thByKey[k].style.width = w + 'px'; });
      if (tableEl && latest.tableWidth != null) {
        tableEl.style.width = latest.tableWidth + 'px';
        tableEl.style.minWidth = latest.tableWidth + 'px';
      }
    };

    const onMove = (ev) => {
      const resizing = resizingRef.current;
      if (!resizing) return;
      // Divide mouse delta by scale: viewport px → logical (unscaled) px
      const delta = (ev.clientX - resizing.startX) / scale;
      const colMin = resizing.colMinWidths[colKey] || 60;
      const newWidth = Math.max(colMin, resizing.startWidth + delta);
      const widths = { [colKey]: newWidth };
      let totalWidthChange = newWidth - resizing.startWidth;
      if (coSelectedKeys) {
        coSelectedKeys.forEach(k => {
          if (k !== colKey) {
            const kStart = coSelectedStartWidths[k] || resizing.startWidth;
            const kMin = (resizing.colMinWidths || {})[k] || 60;
            widths[k] = Math.max(kMin, kStart + delta);
            totalWidthChange += widths[k] - kStart;
          }
        });
      }
      latest = { widths, tableWidth: resizing.initialTableWidth != null ? Math.max(0, resizing.initialTableWidth + totalWidthChange) : null };
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const onUp = () => {
      const resizing = resizingRef.current;
      if (frame) { cancelAnimationFrame(frame); frame = 0; }
      paint();
      if (resizing && latest) {
        const done = latest;
        setColumnWidths(prev => {
          const next = { ...resizing.domSnapWidths, ...prev };
          Object.entries(done.widths).forEach(([k, w]) => { next[k] = w + 'px'; });
          return next;
        });
        if (done.tableWidth != null) setTableMinWidthPx(done.tableWidth);
      }
      if (resizing && resizing.pinned && tableEl) { tableEl.style.marginLeft = 'auto'; tableEl.style.marginRight = 'auto'; }
      resizingRef.current = null;
      // Always reset handle highlight — mouse may have drifted off during drag
      handleEl.style.background = 'transparent';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [selectedColumnKeys, isFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click anywhere (without Ctrl/Cmd) clears column multi-selection ────
  useEffect(() => {
    if (selectedColumnKeys.size === 0) return;
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) return;
      setSelectedColumnKeys(new Set());
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedColumnKeys.size]);

  // ── Column header context menu handlers ────────────────────────────────
  const handleHeaderContextMenu = useCallback((e, colKey) => {
    e.preventDefault();
    setHeaderContextMenu({ x: e.clientX, y: e.clientY, colKey });
  }, []);

  const hideColumn = useCallback((colKey) => {
    setHiddenColumns(prev => { const next = new Set(prev); next.add(colKey); return next; });
    setHeaderContextMenu(null);
  }, []);

  const startRenameColumn = useCallback((colKey) => {
    const builtinLabel = SCHEDULE_COLUMNS.find(c => c.key === colKey)?.label;
    const customLabel = (timeline._customColumns || []).find(c => c.key === colKey)?.label;
    const currentLabel = columnLabels[colKey] || builtinLabel || customLabel || colKey;
    setRenamingColumn(colKey);
    setRenameValue(currentLabel);
    setHeaderContextMenu(null);
  }, [columnLabels, timeline._customColumns]);

  const commitRename = useCallback(() => {
    if (renamingColumn && renameValue.trim()) {
      setColumnLabels(prev => ({ ...prev, [renamingColumn]: renameValue.trim() }));
    }
    setRenamingColumn(null);
    setRenameValue('');
  }, [renamingColumn, renameValue]);

  const unhideColumn = useCallback((colKey) => {
    setHiddenColumns(prev => { const next = new Set(prev); next.delete(colKey); return next; });
  }, []);

  // Close header context menu on outside click
  useEffect(() => {
    if (!headerContextMenu) return;
    const handler = () => setHeaderContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [headerContextMenu]);

  // Close settings dropdown on outside click (item 5)
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = () => { setShowExportMenu(false); setExportSubOpen(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showExportMenu]);

  // ── Column header long-press drag to reorder ────────────────────────────
  // Handles both mouse (onMouseDown) and touch (onTouchStart) input
  const handleColHeaderMouseDown = useCallback((e, colKey) => {
    const isTouch = e.type === 'touchstart';
    if (!isTouch && e.button !== 0) return;
    // Ctrl+click / Cmd+click (mouse only): toggle column selection
    if (!isTouch && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      setSelectedColumnKeys(prev => {
        const next = new Set(prev);
        if (next.has(colKey)) next.delete(colKey); else next.add(colKey);
        return next;
      });
      return;
    }
    setSelectedColumnKeys(new Set());
    const timer = setTimeout(() => {
      setColDragActive(colKey);
      setColDragOver(colKey);
      colDragRef.current = { dragging: colKey, over: colKey };
      document.addEventListener('mousemove', onColDragMove);
      document.addEventListener('mouseup', onColDragEnd);
      document.addEventListener('touchmove', onColDragMove, { passive: false });
      document.addEventListener('touchend', onColDragEnd);
      document.addEventListener('touchcancel', onColDragEnd);
    }, 420);
    colDragRef.current = { timer };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onColDragMove = useCallback((e) => {
    if (!colDragRef.current?.dragging) return;
    // Prevent page scroll while dragging a column on touch
    if (e.cancelable) e.preventDefault();
    const clientX = e.touches ? e.touches[0]?.clientX : e.clientX;
    if (clientX == null) return;
    const els = document.querySelectorAll('th[data-col-key]');
    let overKey = null;
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        overKey = el.dataset.colKey;
        break;
      }
    }
    if (overKey && overKey !== colDragRef.current.over) {
      colDragRef.current.over = overKey;
      setColDragOver(overKey);
    }
  }, []);

  const onColDragEnd = useCallback(() => {
    document.removeEventListener('mousemove', onColDragMove);
    document.removeEventListener('mouseup', onColDragEnd);
    document.removeEventListener('touchmove', onColDragMove);
    document.removeEventListener('touchend', onColDragEnd);
    document.removeEventListener('touchcancel', onColDragEnd);
    if (colDragRef.current?.dragging && colDragRef.current?.over) {
      const dragging = colDragRef.current.dragging;
      const over = colDragRef.current.over;
      if (dragging !== over) {
        // Individual column drag
        setColumnOrder(prev => {
          const base = prev.length > 0 ? prev : visibleColumns.map(c => c.key);
          const from = base.indexOf(dragging);
          const to = base.indexOf(over);
          if (from === -1 || to === -1) return base;
          const next = [...base];
          next.splice(from, 1);
          next.splice(to, 0, dragging);
          return next;
        });
      }
    }
    setColDragActive(null);
    setColDragOver(null);
    colDragRef.current = null;
  }, [onColDragMove, visibleColumns]);

  // Cancels long-press timer on early release (mouse or touch)
  const handleColHeaderMouseUp = useCallback(() => {
    if (colDragRef.current?.timer) {
      clearTimeout(colDragRef.current.timer);
      colDragRef.current = null;
    }
  }, []);

  // ── Build display rows from _rows, scope, or default stages ────────────
  const confirmedScope = useMemo(() => {
    if (!contractScopesHook) return null;
    // Template Builder: the template's _rows ARE the definition, so never reconcile them against a
    // base SOW — that would hide deliverable rows whose IDs differ from the base SOW's (e.g. a SOW
    // that customised some stages' deliverables). Reconciling to the selected deliverables is a
    // project-time concern; in the builder the master template always renders verbatim.
    if (String(project.id) === TEMPLATE_PROJECT_ID) return null;
    if (isStandaloneTimeline(project)) return null;
    const scopes = contractScopesHook.scopes || [];
    const pid = String(project.id);
    return scopes.find(
      s => String(s.projectId) === pid && s.status === 'confirmed' && s.isActiveVersion
    ) || null;
  }, [contractScopesHook, project]);

  // When confirmed scope is deleted, clear all timeline row/date data (preserving settings presets).
  // Track { projectId, scope } so we only clear when the SAME project loses its scope —
  // not when the user switches to a different project that has no scope.
  const prevConfirmedScopeRef = useRef({ projectId: project.id, scope: confirmedScope });
  const timelineRef2 = useRef(timeline);
  useEffect(() => { timelineRef2.current = timeline; }, [timeline]);
  useEffect(() => {
    const prev = prevConfirmedScopeRef.current;
    prevConfirmedScopeRef.current = { projectId: project.id, scope: confirmedScope };
    if (
      prev.projectId === project.id &&
      prev.scope !== null &&
      confirmedScope === null &&
      canEdit && !savesViaGrant && supabaseProjects
    ) {
      const tl = timelineRef2.current;
      const preserved = {};
      if (tl._viewSettings) preserved._viewSettings = tl._viewSettings;
      if (tl._settingsPresets) preserved._settingsPresets = tl._settingsPresets;
      if (tl._activePresetId != null) preserved._activePresetId = tl._activePresetId;
      // Preserve billing data (stage % set in Invoices page)
      Object.entries(tl).forEach(([key, val]) => {
        if (key.startsWith('_')) return;
        if (val && typeof val === 'object' && val.billing) {
          preserved[key] = { billing: val.billing };
        }
      });
      writeTimelineDiff(supabaseProjects, project.id, tl, preserved, { allowTracking: syncEnabledRef.current });
    }
  }, [confirmedScope, project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allRows = useMemo(() => {
    // Return optimistic rows immediately if an action is in-flight
    if (optimisticRows !== null) return optimisticRows;
    const hasSavedRows = timeline._rows && Array.isArray(timeline._rows) && timeline._rows.length > 0;

    // Standalone timeline: its own rows only, or the seed. No scope, no Tracking or misc fee stages.
    if (isStandalone) {
      const rows = hasSavedRows ? timeline._rows : STANDALONE_SEED_ROWS;
      return ensureRowOrder(rows.map(r => ({
        id: r.id,
        key: r.id,
        indent: r.indent || 0,
        name: timeline[r.id]?.taskName || r.defaultLabel || (String(r.id).startsWith('row_') ? '' : r.id),
        defaultLabel: r.defaultLabel || null,
        fromTracking: false,
      })));
    }

    // Canonical stage sequence + names (from activeTasks, same as the Tracking page)
    const stageNameByKey = new Map((stages || []).map(s => [s.key, s.name]));
    const canonicalOrder = (stages || []).map(s => s.key);

    // A stage counts as "having data" using the SAME criteria the Tracking page
    // (TimelinePage) uses to decide which stages to show. Any stage visible there
    // should also be visible here, even if it is not part of the confirmed scope.
    const stageHasTrackingData = (key) => {
      const tl = timeline[key] || {};
      const b = tl.billing || {};
      const num = (v) => Number(v ?? 0) || 0;
      if (num(tl.invoicePercent) > 0) return true;
      if (num(b.percent) > 0) return true;
      if (num(b.lumpsum ?? b.lumpSum) > 0) return true;
      if (num(project.budgetAllocation?.[key]) > 0) return true;
      if (tl.targetDate || tl.contractDate || tl.actualDate || tl.invoiceDate || tl.paidDate) return true;
      return (tl.partStages || []).some(p => {
        const pb = p.billing || {};
        return num(pb.percentOfStage) > 0 || num(pb.lumpsum ?? pb.lumpSum) > 0;
      });
    };

    // Insert additional stages (with Tracking-page data but not in the confirmed
    // scope) at their canonical position so the overall stage sequence matches the
    // Tracking page. Mutates `rows` (array of { id, indent, ... }) in place.
    const addExtraDataStages = (rows) => {
      const present = new Set(rows.filter(r => (r.indent || 0) === 0).map(r => r.id));
      for (const key of canonicalOrder) {
        if (present.has(key)) continue;
        if (!stageHasTrackingData(key)) continue;
        const posInCanon = canonicalOrder.indexOf(key);
        let insertAfterIdx = -1;
        for (let ci = posInCanon - 1; ci >= 0; ci--) {
          const prevKey = canonicalOrder[ci];
          const prevIdx = rows.findIndex(r => r.id === prevKey && (r.indent || 0) === 0);
          if (prevIdx !== -1) {
            let groupEnd = prevIdx + 1;
            while (groupEnd < rows.length && (rows[groupEnd].indent || 0) > 0) groupEnd++;
            insertAfterIdx = groupEnd - 1;
            break;
          }
        }
        const newRow = { id: key, indent: 0, defaultLabel: stageNameByKey.get(key) || STAGE_NAMES[key] || null };
        if (insertAfterIdx === -1) rows.unshift(newRow);
        else rows.splice(insertAfterIdx + 1, 0, newRow);
        present.add(key);
      }
    };

    // Misc / "Additional scopes" fee stages — configured on the project (not the
    // scope of work) and shown as stages on the Tracking page under key misc__<id>.
    // Surface them here too, appended after the standard stages (same as Tracking).
    const miscScopes = Array.isArray(project.miscFeeScopes) ? project.miscFeeScopes : [];
    const miscKeySet = new Set(miscScopes.map(s => `misc__${s.id}`));
    const miscScopeHasData = (scope) => {
      const tl = timeline[`misc__${scope.id}`] || {};
      const b = tl.billing || {};
      const num = (v) => Number(v ?? 0) || 0;
      if (num(scope.amount) > 0) return true;
      if (num(b.lumpsum ?? b.lumpSum) > 0) return true;
      if (num(b.percent) > 0) return true;
      if (tl.contractDate || tl.targetDate || tl.actualDate || tl.clientApprovalDate || tl.invoiceDate || tl.paidDate) return true;
      return (tl.partStages || []).some(p => {
        const pb = p.billing || {};
        return num(pb.percentOfStage) > 0 || num(pb.lumpsum ?? pb.lumpSum) > 0;
      });
    };
    const addMiscScopeStages = (rows) => {
      const present = new Set(rows.filter(r => (r.indent || 0) === 0).map(r => r.id));
      for (const scope of miscScopes) {
        const key = `misc__${scope.id}`;
        if (present.has(key)) continue;
        if (!miscScopeHasData(scope)) continue;
        rows.push({ id: key, indent: 0, defaultLabel: scope.name || key });
        present.add(key);
      }
    };

    if (hasSavedRows && (!confirmedScope || timeline._appliedTemplateId)) {
      // Render the saved _rows verbatim (no scope reconciliation) when either:
      //  • there's no confirmed scope to reconcile against — e.g. a timeline template being edited in
      //    the Template Builder, or a project whose scope was never confirmed; or
      //  • a timeline TEMPLATE has been applied (_appliedTemplateId) — its deliverables are the
      //    template's, whose IDs don't match the scope's, so reconciling would strip them. The apply
      //    step already filtered the template to the SOW-selected stages, so it shows as authored.
      // Extra stages carrying Tracking-page or misc-fee data are still surfaced.
      const rows = timeline._rows.map(r => ({ ...r }));
      addExtraDataStages(rows);
      addMiscScopeStages(rows);
      return ensureRowOrder(rows.map(r => ({
        id: r.id,
        key: r.id,
        indent: r.indent || 0,
        name: timeline[r.id]?.taskName || r.defaultLabel || STAGE_NAMES[r.id] || (String(r.id).startsWith('row_') ? '' : r.id),
        defaultLabel: r.defaultLabel || null,
        fromTracking: false,
      })));
    }

    if (hasSavedRows && confirmedScope) {
      // Reconcile saved _rows with current confirmed scope
      const scopeRows = buildRowsFromScope(confirmedScope);
      const scopeIdSet = new Set(scopeRows.map(r => r.id));
      const savedIdSet = new Set(timeline._rows.map(r => r.id));

      // Build a map of scope stage keys to their deliverables (in order)
      const scopeStageDelivs = {};
      let currentStageKey = null;
      for (const sr of scopeRows) {
        if (sr.indent === 0) {
          currentStageKey = sr.id;
          scopeStageDelivs[currentStageKey] = [];
        } else if (currentStageKey) {
          scopeStageDelivs[currentStageKey].push(sr);
        }
      }

      // Start with saved rows: keep all that still exist in scope or are user-added (not scope-derived)
      const reconciledRows = [];
      const reconciledIds = new Set(); // prevents duplicate IDs from corrupt saved _rows
      let inRemovedStage = false;

      for (const savedRow of timeline._rows) {
        const isStageRow = savedRow.indent === 0;

        if (isStageRow) {
          if (scopeIdSet.has(savedRow.id) && !reconciledIds.has(savedRow.id)) {
            inRemovedStage = false;
            reconciledRows.push(savedRow);
            reconciledIds.add(savedRow.id);
          } else if (!scopeIdSet.has(savedRow.id)) {
            // Stage not in scope. Keep it (and its children) if it carries
            // Tracking-page data, or if it's a misc/additional-scope fee stage that
            // still exists on the project. Otherwise it was removed — drop it.
            if (!reconciledIds.has(savedRow.id) && (stageHasTrackingData(savedRow.id) || miscKeySet.has(savedRow.id) || isCustomStage(savedRow.id))) {
              inRemovedStage = false;
              reconciledRows.push(savedRow);
              reconciledIds.add(savedRow.id);
            } else {
              inRemovedStage = true;
            }
          }
          // else: duplicate stage row — skip silently
          continue;
        }

        // Non-stage row — skip if parent stage was removed
        if (inRemovedStage) continue;

        // Skip if this row ID was already added (corrupt duplicate in saved _rows)
        if (reconciledIds.has(savedRow.id)) continue;

        const isScopeDeliv = savedRow.id.startsWith('deliv_');
        if (isScopeDeliv && !scopeIdSet.has(savedRow.id)) {
          // Scope deliverable that was removed — skip
          continue;
        }

        const isClosureRow = savedRow.id.startsWith('closure_');
        if (isClosureRow && !scopeIdSet.has(savedRow.id)) {
          // Closure row whose scope stage was removed — skip (but keep if parent is a custom stage)
          if (!isCustomStage(savedRow.id.slice('closure_'.length))) continue;
        }

        const isInvoicingRow = savedRow.id.startsWith('invoicing_');
        if (isInvoicingRow && !scopeIdSet.has(savedRow.id)) {
          // Invoicing row whose scope stage was removed — skip (but keep if parent is a custom stage)
          if (!isCustomStage(savedRow.id.slice('invoicing_'.length))) continue;
        }

        const isStartRow = savedRow.id.startsWith('start_');
        if (isStartRow && !scopeIdSet.has(savedRow.id)) {
          // Start row whose scope stage was removed — skip (but keep if parent is a custom stage)
          if (!isCustomStage(savedRow.id.slice('start_'.length))) continue;
        }

        const isRevisionRow = savedRow.id.startsWith('revision_');
        if (isRevisionRow && !scopeIdSet.has(savedRow.id)) {
          continue;
        }

        // Meeting rows are user-inserted (not scope-derived) — always keep them

        // Keep: scope deliverable/closure that still exists, or user-added custom row
        reconciledRows.push(savedRow);
        reconciledIds.add(savedRow.id);
      }

      // Ordered list of stage keys from scope (used to position newly-added stages)
      const scopeStageOrder = scopeRows.filter(r => r.indent === 0).map(r => r.id);

      // Now add any new deliverables from scope that aren't in saved rows
      // Insert them after their stage's existing deliverables, preserving scope order
      for (const stageKey of scopeStageOrder) {
        if (!scopeStageDelivs[stageKey]) continue;
        const stageIdx = reconciledRows.findIndex(r => r.id === stageKey);
        if (stageIdx === -1) {
          // New stage — find the nearest preceding scope stage that is already in reconciledRows
          // and insert after its entire group (all its children)
          const posInScope = scopeStageOrder.indexOf(stageKey);
          let insertAfterIdx = -1;
          for (let si = posInScope - 1; si >= 0; si--) {
            const prevStageKey = scopeStageOrder[si];
            const prevIdx = reconciledRows.findIndex(r => r.id === prevStageKey);
            if (prevIdx !== -1) {
              // Found preceding stage — find the end of its group
              let groupEnd = prevIdx + 1;
              while (groupEnd < reconciledRows.length && reconciledRows[groupEnd].indent > 0) groupEnd++;
              insertAfterIdx = groupEnd - 1;
              break;
            }
          }
          const dedupedScopeDelivs = scopeStageDelivs[stageKey].filter(d => {
            if (reconciledIds.has(d.id)) return false;
            reconciledIds.add(d.id);
            return true;
          });
          if (!reconciledIds.has(stageKey)) { reconciledIds.add(stageKey); }
          const newRows = [{ id: stageKey, indent: 0 }, ...dedupedScopeDelivs];
          if (insertAfterIdx === -1) {
            // No preceding stage in reconciledRows — prepend before all existing rows
            reconciledRows.splice(0, 0, ...newRows);
          } else {
            reconciledRows.splice(insertAfterIdx + 1, 0, ...newRows);
          }
        } else {
          // Stage exists — find new deliverables not yet in reconciled rows (check live set, not just savedIdSet)
          const newDelivs = scopeStageDelivs[stageKey].filter(d => !reconciledIds.has(d.id));
          if (newDelivs.length > 0) {
            // Find the last row belonging to this stage group
            let insertAt = stageIdx + 1;
            while (insertAt < reconciledRows.length && reconciledRows[insertAt].indent > 0) {
              insertAt++;
            }
            // Insert new deliverables at the end of the stage group
            reconciledRows.splice(insertAt, 0, ...newDelivs);
            newDelivs.forEach(d => reconciledIds.add(d.id));
          }
        }
      }

      // Surface additional stages that carry Tracking-page data but are not in scope
      addExtraDataStages(reconciledRows);
      // Surface misc / additional-scope fee stages (appended after standard stages)
      addMiscScopeStages(reconciledRows);

      // Build stage-specific labels for closure and invoicing rows
      const specialLabels = {};
      confirmedScope.stages.forEach(stage => {
        const tk = scopeKeyToTimelineKey(stage.key);
        specialLabels[`closure_${tk}`] = `Closure of ${stage.name} Stage`;
        specialLabels[`invoicing_${tk}`] = `Invoicing of ${stage.name} Stage`;
        specialLabels[`start_${tk}`] = `Start of ${stage.name} Stage`;
      });
      return ensureRowOrder(reconciledRows.map(r => {
        const specialLabel = specialLabels[r.id];
        return {
          id: r.id,
          key: r.id,
          indent: r.indent || 0,
          name: timeline[r.id]?.taskName || specialLabel || r.defaultLabel || STAGE_NAMES[r.id] || r.id,
          defaultLabel: specialLabel || r.defaultLabel || null,
          // Stage rows not present in the confirmed scope were surfaced from the
          // Tracking page — flag them so the UI can mark them with a "-TR" suffix.
          fromTracking: (r.indent || 0) === 0 && !scopeIdSet.has(r.id),
        };
      }));
    }

    if (confirmedScope) {
      const scopeRows = buildRowsFromScope(confirmedScope);
      const scopeStageIdSet = new Set(scopeRows.filter(r => (r.indent || 0) === 0).map(r => r.id));
      // Surface additional stages that carry Tracking-page data but are not in scope
      addExtraDataStages(scopeRows);
      // Surface misc / additional-scope fee stages (appended after standard stages)
      addMiscScopeStages(scopeRows);
      return ensureRowOrder(scopeRows.map(r => ({
        id: r.id,
        key: r.id,
        indent: r.indent,
        name: timeline[r.id]?.taskName || r.defaultLabel || STAGE_NAMES[r.id] || (String(r.id).startsWith('row_') ? '' : r.id),
        defaultLabel: r.defaultLabel || null,
        fromTracking: (r.indent || 0) === 0 && !scopeStageIdSet.has(r.id),
      })));
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, confirmedScope, optimisticRows, stages, project.budgetAllocation, project.miscFeeScopes, isStandalone]);

  // Canonical extended row list: allRows + virtual part-stage system rows in grouped display
  // order. Used for predecessor numbering, the # column, and the dependency cascade so part
  // rows get chronological task numbers consistent with what is shown on screen.
  const extendedRows = useMemo(() => buildExtendedRows(allRows, partStagesMap, timeline), [allRows, partStagesMap, timeline]);

  // Summary (parent) task rows — a non-stage, non-preset task row that has at least one deeper-indent
  // child immediately below it. Their duration and start/end dates are rolled up from their children by
  // applyDependencies, so those cells are shown read-only (editing them would just be overwritten on the
  // next cascade). Stage rows and sub-stage headers already render aggregated read-only via their own
  // branches, so they're excluded here.
  const summaryRowIds = useMemo(() => {
    const set = new Set();
    for (let i = 0; i < extendedRows.length; i++) {
      const r = extendedRows[i];
      if ((r.indent || 0) === 0 || r.isSubStageHeader) continue;
      const next = extendedRows[i + 1];
      if (next && (next.indent || 0) > (r.indent || 0)) set.add(r.id);
    }
    return set;
  }, [extendedRows]);

  // Parent tasks and stages roll up each derived custom trio (a Date column with `derive`, e.g. TG's
  // Target Start / End / Duration) from the tasks under them: earliest start, latest end, and the working
  // days between them (holidays ignored, as the derived end). Read only on those rows.
  const customRollup = useMemo(() => {
    const trios = (timeline._customColumns || []).filter(c => c.derive)
      .map(c => ({ start: c.derive.start, end: c.key, duration: c.derive.duration }));
    const out = {};
    if (!trios.length) return out;
    const wd = new Set(projectSettings.workingDays.days);
    extendedRows.forEach((r, i) => {
      const isStageRow = (r.indent || 0) === 0 && !r.isSubStageHeader;
      if (!isStageRow && !summaryRowIds.has(r.id)) return;
      const ind = r.indent || 0;
      const leaves = [];
      for (let j = i + 1; j < extendedRows.length && (extendedRows[j].indent || 0) > ind; j++) {
        if (!summaryRowIds.has(extendedRows[j].id) && !extendedRows[j].isSubStageHeader) leaves.push(extendedRows[j].id);
      }
      const vals = {};
      trios.forEach(t => {
        let mn = null;
        let mx = null;
        leaves.forEach(id => {
          const d = timeline[id] || {};
          const st = d[t.start] || null;
          const en = d[t.end] || st;
          if (st && (!mn || st < mn)) mn = st;
          if (en && (!mx || en > mx)) mx = en;
        });
        vals[t.start] = mn;
        vals[t.end] = mx;
        vals[t.duration] = (mn && mx) ? workdaysBetween(mn, mx, wd) : null;
      });
      out[r.id] = vals;
    });
    return out;
  }, [timeline, summaryRowIds, extendedRows, projectSettings]);
  const rolledCustomValue = useCallback((rowId, colKey) => {
    const r = customRollup[rowId];
    return r && Object.prototype.hasOwnProperty.call(r, colKey) ? { value: r[colKey] } : null;
  }, [customRollup]);

  // User-created filter tabs (besides the implicit "Project timelines" tab which shows everything).
  const tabs = useMemo(() => (Array.isArray(timeline._tabs) ? timeline._tabs : []), [timeline._tabs]);
  const activeTab = useMemo(() => (activeTabId === '__all__' ? null : tabs.find(t => t.id === activeTabId) || null), [tabs, activeTabId]);
  // BRM: the stages of the tab on screen (its tabs are separate pages, see standaloneTabView).
  const standaloneTab = useMemo(() => (isStandalone
    ? standaloneTabView(timeline, activeTabId, allRows.filter(r => r.indent === 0 && !r.isSubStageHeader).map(r => r.id))
    : null), [isStandalone, timeline, activeTabId, allRows]);

  // Auto-collapse all stage rows the first time a project's timeline is viewed
  // (when no explicit collapsedRows have been saved yet).
  // NOTE: Must be declared AFTER allRows to avoid a const TDZ in the production bundle.
  const autoCollapseAppliedRef = useRef(false);
  useEffect(() => { autoCollapseAppliedRef.current = false; }, [project.id]);
  useEffect(() => {
    if (autoCollapseAppliedRef.current) return;
    if (allRows.length === 0) return;
    if (Array.isArray(project.stageTimeline?._viewSettings?.collapsedRows)) return;
    const stageIds = allRows
      .filter((r, i) => r.indent === 0 && rowHasChildren(allRows, i))
      .map(r => r.id);
    if (stageIds.length === 0) return;
    autoCollapseAppliedRef.current = true;
    setCollapsedRows(new Set(stageIds));
  }, [allRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-persist reconciled rows when scope changes cause allRows to differ from saved _rows
  // reconciledRef tracks the last allRowIds we saved; reset whenever timeline changes so a
  // concurrent save that rolled back _rows can be corrected on the next render cycle.
  const reconciledRef = useRef(null);
  useEffect(() => { reconciledRef.current = null; }, [timeline]); // reset lock when DB data arrives
  useEffect(() => {
    if (isOptimisticActive.current) return; // skip while optimistic update is in-flight
    if (!allRows.length || !confirmedScope || !supabaseProjects) return;
    // Opening the page must not save anything for a user saving through the grant (it would count as
    // an edit and switch Sync to tracking off). Their first real edit saves the rows anyway.
    if (savesViaGrant) return;
    const savedRows = timeline._rows;
    // Compare allRows vs savedRows; treat missing/empty _rows as needing a save
    const allRowIds = allRows.map(r => r.id).join(',');
    const savedRowIds = (savedRows && Array.isArray(savedRows)) ? savedRows.map(r => r.id).join(',') : '';
    if (allRowIds === savedRowIds) return;
    // Avoid duplicate saves within the same timeline version
    if (reconciledRef.current === allRowIds) return;
    reconciledRef.current = allRowIds;
    const rowData = allRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = { ...timeline, _rows: rowData };
    writeTimelineDiff(supabaseProjects, project.id, timeline, newTimeline, { allowTracking: syncEnabledRef.current });
  }, [allRows, confirmedScope, timeline, supabaseProjects, project.id, savesViaGrant]);

  // Auto-fill plannedDuration for invoicing rows from client's paymentTermsDays
  const invoicingDurationFilledRef = useRef(null);
  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!canEdit || savesViaGrant || !supabaseProjects || !paymentTermsDays || !allRows.length) return;
    const projectKey = `${project.id}:${paymentTermsDays}`;
    if (invoicingDurationFilledRef.current === projectKey) return;
    const invoicingRows = allRows.filter(r => r.id.startsWith('invoicing_'));
    if (!invoicingRows.length) return;
    const missing = invoicingRows.filter(r => !timeline[r.id]?.plannedDuration);
    if (!missing.length) return;
    invoicingDurationFilledRef.current = projectKey;
    const patch = { ...timeline };
    for (const r of missing) {
      patch[r.id] = { ...(patch[r.id] || {}), plannedDuration: paymentTermsDays };
    }
    writeTimelineDiff(supabaseProjects, project.id, timeline, patch, { allowTracking: syncEnabledRef.current });
  }, [allRows, canEdit, savesViaGrant, supabaseProjects, paymentTermsDays, project.id, timeline]);

  // Auto-backfill invoicing/closure row dates from stage's invoiceDate/paidDate.
  // Runs whenever timeline or allRows changes; only writes to DB if there are rows to sync.
  // After a successful sync, the invoicing/closure rows have plannedStartDate so future runs
  // find nothing to sync and exit cheaply — no infinite loop possible.

  // Persist ONLY the stageTimeline keys that actually changed, via the race-safe
  // merge_project_stage_timeline RPC. The automatic effects below (invoicing/closure backfill,
  // predecessor-number reconciliation, start_-row preset cascade, and the date sync) all fire on
  // load with no user edit and previously rewrote the WHOLE stage_timeline blob from this component's
  // in-memory copy. When that copy was stale relative to a Tracking-page edit, the full-blob write
  // silently drifted or nulled the "Planned" (targetDate) column — even with the sync toggle OFF,
  // because most of these effects are not gated by it. A scoped merge only touches the keys we
  // computed. The Template Builder keeps the full-object path because its proxySupabase intercepts
  // updateProject only (and must never persist to a real project row).
  // These effects now save through writeTimelineDiff as well, so the Sync-to-tracking guard covers
  // them: the base is the stored copy of just the keys being written.
  const saveTimelineChangedKeys = useCallback((partial, fullTimeline) => {
    if (!partial || Object.keys(partial).length === 0) return undefined;
    const current = timelineRef2.current || {};
    const base = { ...fullTimeline };
    Object.keys(partial).forEach(k => { base[k] = current[k]; });
    return writeTimelineDiff(supabaseProjects, project.id, base, fullTimeline, { allowTracking: syncEnabledRef.current });
  }, [project.id, supabaseProjects]);

  // "Ignore delay" (from the closure "Delayed" badge): stop flagging the stage as delayed and snap the
  // Closure date back to the stage's / part's actual completion date. The _delayIgnored flag persists,
  // and the backfill above honours it (closure follows the actual date, not the late paid date).
  const handleIgnoreClosureDelay = useCallback((closureKey, stageOrSubKey) => {
    if (!canEdit || !supabaseProjects) return;
    const actual = (resolveStageRecord(stageOrSubKey) || {}).actualDate || null;
    const existing = timeline[closureKey] || {};
    const next = { ...existing, _delayIgnored: true, plannedStartDate: actual, plannedEndDate: actual };
    saveTimelineChangedKeys({ [closureKey]: next }, { ...timeline, [closureKey]: next });
  }, [canEdit, supabaseProjects, resolveStageRecord, timeline, saveTimelineChangedKeys]);

  // Keep each stage's Invoicing / Closure system-row dates in sync with the parent stage's billing
  // fields (invoice / paid / actual), via the shared helper. Idempotent + scoped: writes ONLY the
  // invoicing_/closure_ rows whose derived dates actually changed (never stage entries), so it
  // self-heals existing data without the load-time clobber the merge fix removed, and settles after
  // one pass (re-computing from unchanged inputs yields the same result → no further write).
  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!canAutoSync || !supabaseProjects || !allRows.length) return;
    const partial = {};
    for (const r of allRows) {
      const isInvoicing = r.id.startsWith('invoicing_');
      const isClosure = r.id.startsWith('closure_');
      if (!isInvoicing && !isClosure) continue;
      const sk = r.id.slice((isInvoicing ? 'invoicing_' : 'closure_').length);
      const stageData = timeline[sk] || {};
      const existing = timeline[r.id] || {};
      // Driving billing fields: Appr. (else Invoice) for the invoicing row; Paid, or the running clock
      // while payment is overdue, for closure. When they are CLEARED on the Tracking page the derived row
      // clears too — this link is NOT gated by the sync toggle.
      const dur = (timeline[`invoicing_${sk}`]?.plannedDuration) || paymentTermsDays || null;
      const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(stageData, dur);
      let nextStart = null, nextEnd = null;
      if (isInvoicing) {
        nextStart = invoicingStart; nextEnd = invoicingEnd;
      } else if (closureDate) {
        // "Ignore delay" (set from the Delayed badge) snaps the closure back to the stage's actual
        // completion date instead of the late paid date / today (matches handleIgnoreClosureDelay).
        const snapped = existing._delayIgnored ? (stageData.actualDate || null) : closureDate;
        nextStart = snapped; nextEnd = snapped;
      }
      if ((existing.plannedStartDate || null) !== (nextStart || null) ||
          (existing.plannedEndDate || null) !== (nextEnd || null)) {
        partial[r.id] = { ...existing, plannedStartDate: nextStart, plannedEndDate: nextEnd };
      }
    }
    if (Object.keys(partial).length === 0) return;
    saveTimelineChangedKeys(partial, { ...timeline, ...partial });
  }, [allRows, canAutoSync, supabaseProjects, project.id, timeline, paymentTermsDays, saveTimelineChangedKeys]);

  // Auto-backfill part-stage invoicing/closure rows from each part's invoice/paid date.
  // Part stages are virtual (not in allRows), so they need their own backfill pass.
  // No ref guard: writes set plannedStartDate; the next pass finds nothing to sync and
  // exits cheaply, so new part invoice/paid dates are picked up whenever they change.
  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!canAutoSync || !supabaseProjects) return;
    const patches = [];
    Object.entries(partStagesMap).forEach(([stageId, parts]) => {
      if (isAdvanceStage(stageId)) return; // no part Invoicing / Closure rows on Advance
      (parts || []).forEach(p => {
        const subKey = `substage_${stageId}_${p.id}`;
        const invKey = `invoicing_${subKey}`;
        const closeKey = `closure_${subKey}`;
        const dur = timeline[invKey]?.plannedDuration || paymentTermsDays || null;
        const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(p, dur);
        // Clear the derived row when the part's invoice/paid date is cleared (propagates regardless of
        // the sync toggle); "Ignore delay" snaps the closure back to the part's actual completion date.
        const invEx = timeline[invKey] || {};
        const closeEx = timeline[closeKey] || {};
        const invStart = invoicingStart || null;
        const invEndVal = invoicingEnd || null;
        const closeSnap = closureDate
          ? (closeEx._delayIgnored ? (p.actualDate || null) : closureDate)
          : null;
        if ((invEx.plannedStartDate || null) !== (invStart || null) || (invEx.plannedEndDate || null) !== (invEndVal || null)) {
          patches.push([invKey, { ...invEx, plannedStartDate: invStart, plannedEndDate: invEndVal }]);
        }
        if ((closeEx.plannedStartDate || null) !== (closeSnap || null) || (closeEx.plannedEndDate || null) !== (closeSnap || null)) {
          patches.push([closeKey, { ...closeEx, plannedStartDate: closeSnap, plannedEndDate: closeSnap }]);
        }
      });
    });
    if (!patches.length) return;
    // Scoped merge (only the part invoicing_/closure_ rows we backfill) — see the stage-row backfill
    // effect above; a full-blob write would clobber unrelated stage dates from a stale in-memory copy.
    const partial = {};
    patches.forEach(([k, v]) => { partial[k] = v; });
    saveTimelineChangedKeys(partial, { ...timeline, ...partial });
  }, [partStagesMap, canAutoSync, supabaseProjects, project.id, timeline, paymentTermsDays, saveTimelineChangedKeys]);

  // Idempotent predecessor-numbering reconciliation. Stored predecessor values are positional
  // integers; the canonical numbering (extendedRows) shifts whenever part-stage rows are added,
  // removed, or reordered — including from OTHER pages (parts are edited on the Tracking page),
  // which never pass through this page's remap handlers. `_depNumKey` records the num→rowId map
  // the stored predecessors are currently expressed in. On load, if the live numbering differs,
  // we remap once and re-stamp `_depNumKey`. When already aligned this is a no-op (no DB write),
  // so it cannot double-remap or loop.
  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!allRows.length || !canAutoSync || !supabaseProjects) return;
    const predCol = projectSettings.dependencies.predecessorColumn;
    const extRows = buildExtendedRows(allRows, partStagesMap, timeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(extRows);
    // Baseline = the numbering the stored predecessors are currently expressed in:
    //  • _depNumKey present → use it (current scheme)
    //  • legacy _partDepsMigrated → the earlier ungrouped-append extendedRows numbering
    //  • otherwise → pre-feature numbering: computeTaskNums(allRows), no part rows / grouping
    const oldNumToKey = timeline._depNumKey
      || (timeline._partDepsMigrated
        ? computeTaskNums(buildLegacyExtendedRows(allRows, partStagesMap)).numToKey
        : computeTaskNums(allRows).numToKey);
    const sameNumbering = JSON.stringify(oldNumToKey) === JSON.stringify(newNumToKey);
    if (sameNumbering && timeline._depNumKey) return; // already aligned — nothing to do
    const newTimeline = { ...timeline, _depNumKey: newNumToKey };
    const partial = { _depNumKey: newNumToKey };
    Object.keys(newTimeline).forEach(key => {
      if (key.startsWith('_')) return;
      const rd = newTimeline[key];
      if (!rd || !rd[predCol]) return;
      const parsed = parsePredecessors(rd[predCol]);
      if (!parsed.length) return;
      const remapped = parsed.map(p => {
        const rowId = oldNumToKey[p.rowNumber];
        if (!rowId) return null;
        const newNum = newKeyToNum[rowId];
        if (!newNum) return null;
        return { rowNumber: newNum, type: p.type, lag: p.lag };
      }).filter(Boolean);
      const newStr = formatPredecessors(remapped);
      if (newStr !== rd[predCol]) { newTimeline[key] = { ...rd, [predCol]: newStr }; partial[key] = newTimeline[key]; }
    });
    // Scoped merge: only the remapped predecessor rows + the _depNumKey marker. This effect runs on
    // load (and after cross-page part edits) regardless of the sync toggle, so a full-blob write here
    // was a prime way the Tracking page's Planned dates got reverted from a stale in-memory copy.
    saveTimelineChangedKeys(partial, newTimeline);
  }, [project.id, allRows, partStagesMap, timeline, canAutoSync, supabaseProjects, projectSettings, saveTimelineChangedKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-init start_ rows: predecessor (SS normally, FS+lag when overdue), duration 1 day, start date from prev stage.
  // Processes start_ rows in stage order; cascades applyDependencies after each patch so every subsequent
  // stage reads the already-propagated dates from workingTimeline (single save, full inter-stage cascade).
  const startRowPredInitRef = useRef(null);
  // The Start row preset pass: for every start_ row, its preset dependency (on the linked stage's invoicing
  // row) and start date, cascaded through applyDependencies. Shared by the effect below and by picking a
  // stage in the Start row link list, so a pick applies the preset at once for whoever makes it.
  const computeStartRowCascade = useCallback((timeline) => {
    const predCol = projectSettings.dependencies.predecessorColumn;
    const depsEnabled = projectSettings.dependencies.enabled;
    const wdSet = new Set(projectSettings.workingDays.days);
    const wdHols = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));

    const addWd = (dateStr, count) => {
      let cur = parseDate(dateStr);
      if (!cur || !count) return dateStr;
      let rem = count;
      while (rem > 0) {
        cur = new Date(cur.getTime() + DAY_MS);
        if (wdSet.has(cur.getDay()) && !wdHols.has(formatDate(cur))) rem--;
      }
      return formatDate(cur);
    };
    // Working days INCLUDING brio holidays — a weekday counts even on a holiday. Used for the payment-delay
    // lag (window end → today / paid date) and the delayed start push, per product spec (weekends excluded,
    // holidays counted). Matches applyDependencies' addWorkDaysInclHolidays so the cascade agrees (no drift).
    const addWdInclHols = (dateStr, count) => {
      let cur = parseDate(dateStr);
      if (!cur || !count) return dateStr;
      let rem = count;
      while (rem > 0) { cur = new Date(cur.getTime() + DAY_MS); if (wdSet.has(cur.getDay())) rem--; }
      return formatDate(cur);
    };
    const countWdInclHols = (fromStr, toDate) => {
      const from = parseDate(fromStr);
      if (!from) return 0;
      let count = 0; let cur = new Date(from.getTime());
      while (cur.getTime() + DAY_MS < toDate.getTime()) { cur = new Date(cur.getTime() + DAY_MS); if (wdSet.has(cur.getDay())) count++; }
      return count;
    };
    // Snap forward to the next working day (no-op if already one) — for the SS invoice-issued case, so a
    // start row never lands on a weekend/holiday even when the invoice date does.
    const snapWd = (dateStr) => {
      let cur = parseDate(dateStr);
      if (!cur) return dateStr;
      let guard = 0;
      while (guard++ < 400 && !(wdSet.has(cur.getDay()) && !wdHols.has(formatDate(cur)))) cur = new Date(cur.getTime() + DAY_MS);
      return formatDate(cur);
    };

    const extRowsForEffect = buildExtendedRows(allRows, partStagesMap, timeline);
    const idToTaskNum = {};
    let taskN = 0;
    extRowsForEffect.forEach(r => { if (r.indent > 0 && !r.isSubStageHeader) idToTaskNum[r.id] = ++taskN; });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // workingTimeline accumulates patches + cascaded deps stage-by-stage in one pass
    let workingTimeline = { ...timeline };
    const startRowPatches = {}; // only start_ patches, used for ref-key dedup

    for (let i = 0; i < extRowsForEffect.length; i++) {
      const row = extRowsForEffect[i];
      if (!row.id.startsWith('start_')) continue;
      const rd = workingTimeline[row.id] || {};
      const stageKey = row.id.slice('start_'.length);
      // Part start rows (start_substage_<stage>_<part>) anchor to their parent stage for the
      // default "previous stage" preset; main start rows use their own stage position.
      const isPartStart = stageKey.startsWith('substage_');
      const anchorStageId = isPartStart ? (partKeyInfo.get(stageKey)?.stageId || null) : stageKey;
      const myStageIdx = anchorStageId ? allRows.findIndex(r => r.id === anchorStageId) : -1;

      // Find previous stage (default preset target)
      let prevSk = null;
      if (myStageIdx > 0) {
        let prevStageIdx = myStageIdx - 1;
        while (prevStageIdx >= 0 && allRows[prevStageIdx].indent !== 0) prevStageIdx--;
        if (prevStageIdx >= 0) prevSk = allRows[prevStageIdx].id;
      }
      if (isAdvanceStage(prevSk)) prevSk = null; // no preset link on Advance

      // Manually cleared preset — leave the dependency/date alone so the clear sticks.
      if (rd._presetCleared) continue;
      // Manual dependency override — the user set their own dep type / lag on this start row; don't
      // overwrite it. The normal cascade (applyDependencies) still runs and drives this row's date from
      // the chosen dependency, because the row isn't added to startRowPatches below.
      if (rd._manualPredOverride) continue;
      // Part start rows are scheduled only when the user has explicitly linked them (we don't
      // impose an automatic default on every part). Main start rows keep the default preset.
      const manualLink = manualStartLink(row.id, rd);
      if (isPartStart && !manualLink) continue;

      const needsDuration = rd.plannedDuration == null;

      // If the user manually linked to a specific stage, use that stage for all date logic;
      // otherwise fall back to the immediately previous stage (default behaviour).
      const linkedSk = manualLink || prevSk;

      const invRow = linkedSk ? (workingTimeline[`invoicing_${linkedSk}`] || {}) : {};
      const closureRow = linkedSk ? (workingTimeline[`closure_${linkedSk}`] || {}) : {};
      // For part-stage links (substage_*), paidDate lives on the part object, not timeline[linkedSk]
      let linkedPaidDate = null;
      if (linkedSk) {
        if (linkedSk.startsWith('substage_')) {
          const pki = partKeyInfo.get(linkedSk);
          const part = pki ? (partStagesMap[pki.stageId] || []).find(p => p.id === pki.partId) : null;
          linkedPaidDate = part?.paidDate || null;
        } else {
          linkedPaidDate = workingTimeline[linkedSk]?.paidDate || null;
        }
      }
      const isPaid = !!linkedPaidDate;
      // Payment-terms window end = invoice date (invoicing row's start) + terms. This used to be the
      // invoicing row's plannedEndDate, but that now displays the actual PAID date once paid, so the
      // window that drives overdue/late detection + the delayed-stage cascade is recomputed here.
      const invEnd = invRow.plannedStartDate
        ? (addCalendarDaysStr(invRow.plannedStartDate, invRow.plannedDuration != null ? Number(invRow.plannedDuration) : (paymentTermsDays != null ? Number(paymentTermsDays) : 0)) || invRow.plannedEndDate || null)
        : (invRow.plannedEndDate || null);
      const invHasStart = !!invRow.plannedStartDate;

      // Overdue: invoicing row has an end date, stage not yet paid, and today > invEnd
      const isOverdue = !isPaid && !!invEnd &&
        Date.now() > new Date(invEnd + 'T00:00:00').getTime() + DAY_MS;
      // Respect user's "Stop" flag — treat as not overdue so dep stays SS
      const effectiveOverdue = isOverdue && !rd._delayAutoStopped;

      const invTaskNum = linkedSk ? idToTaskNum[`invoicing_${linkedSk}`] : null;
      const currentDep = rd[predCol] || '';

      // Payment is "late" only when the paid date is AFTER the invoicing end (payment terms
      // exceeded). When paid within the duration the dependency must stay SS — the invoice can
      // momentarily flip to FS while real-time is past invEnd and unpaid, so we gate the freeze
      // on the actual paid date, not just the current FS dep.
      const paidLate = isPaid && !!invEnd && parseDate(linkedPaidDate) > parseDate(invEnd);
      // When payment is received after a delay, freeze the lag at the closure (paid) date and
      // keep the dep as FS+finalLag (a visible record of the delay).
      const wasDelayedNowPaid = paidLate && !rd._delayAutoStopped && !!closureRow.plannedEndDate &&
        !!invTaskNum && currentDep.startsWith(`${invTaskNum}FS`);
      let finalLag = 0;
      if (wasDelayedNowPaid && closureRow.plannedEndDate) {
        // Frozen delay lag = WORKING days (weekends excluded, brio holidays INCLUDED) from the payment-
        // terms window end to the paid date.
        finalLag = countWdInclHols(invEnd, parseDate(closureRow.plannedEndDate));
      }

      // Predecessor (on the linked stage's invoicing row):
      // - Overdue (live): FS + running lag
      // - Payment received after delay: FS + frozen lag (up to closure date)
      // - Invoice issued (not overdue): SS — overlap the next stage with the invoicing period
      // - No invoicing date yet: FS — gate the next stage on the previous stage FINISHING, so the
      //   preset link reads e.g. "11FS" (not "11SS") until an invoice date exists.
      let desiredPred = null;
      let overduelag = 0;
      if (linkedSk && invTaskNum) {
        if (effectiveOverdue) {
          // Live overdue lag = WORKING days (weekends excluded, brio holidays INCLUDED) the invoice is
          // past its window (window end → today); advances until payment is recorded.
          overduelag = countWdInclHols(invEnd, today);
          desiredPred = overduelag > 0 ? `${invTaskNum}FS+${overduelag}d` : `${invTaskNum}FS`;
        } else if (wasDelayedNowPaid) {
          desiredPred = finalLag > 0 ? `${invTaskNum}FS+${finalLag}d` : `${invTaskNum}FS`;
        } else if (invHasStart) {
          desiredPred = `${invTaskNum}SS`;
        } else {
          desiredPred = `${invTaskNum}FS`;
        }
      }

      const needsPred = !!desiredPred && rd[predCol] !== desiredPred;

      // Sync start_ date from the linked stage's live conditions:
      // - Overdue (not stopped): start = invEnd + (1 + overduelag) working days (holidays counted)
      // - Payment received after delay: start = invEnd + (1 + finalLag) working days (holidays counted)
      // - Invoice issued (not overdue): start = invStart (SS — same start as the invoicing period)
      // - Neither (no invoicing date): max child end of linked stage + 1 working day (FS — starts the
      //   working day AFTER the previous stage finishes; excludes invoicing_ rows). Fallback: linked
      //   stage row's own plannedEndDate + 1 working day.
      // The delay branches count working days INCLUDING brio holidays (weekends excluded) so
      // applyDependencies' matching delayLink application reproduces exactly this date (no drift).
      let newDateStr = null;
      if (linkedSk) {
        if (effectiveOverdue && invEnd) {
          newDateStr = addWdInclHols(invEnd, 1 + overduelag);
        } else if (wasDelayedNowPaid) {
          newDateStr = addWdInclHols(invEnd, 1 + finalLag);
        } else if (invHasStart) {
          newDateStr = snapWd(invRow.plannedStartDate); // SS: same day as the invoicing start, snapped to a working day
        } else if (linkedSk.startsWith('substage_')) {
          // Linked to a part stage: read the part's aggregated end (matching its name-row date) —
          // max planned end across the part's deliverables + its own Start/Closure rows, excluding
          // the Invoicing row (mirrors the main-stage behaviour below).
          const pki = partKeyInfo.get(linkedSk);
          let linkedMaxEnd = null;
          const consider = (d) => { const ed = parseDate(d?.plannedEndDate); if (ed && (!linkedMaxEnd || ed > linkedMaxEnd)) linkedMaxEnd = ed; };
          if (pki) {
            for (const r2 of allRows) {
              if (r2.indent === 0) continue;
              if ((workingTimeline[r2.id]?._subStageId || null) === pki.partId) consider(workingTimeline[r2.id]);
            }
          }
          consider(workingTimeline[`start_${linkedSk}`]);
          consider(workingTimeline[`closure_${linkedSk}`]);
          // FS: start the working day AFTER the part's last activity finishes.
          if (linkedMaxEnd) newDateStr = addWd(formatDate(linkedMaxEnd), 1);
          // Final fallback: the part's invoicing row end if nothing else has a date yet
          if (!newDateStr) { const ie = workingTimeline[`invoicing_${linkedSk}`]?.plannedEndDate; newDateStr = ie ? addWd(ie, 1) : null; }
        } else {
          let inLinked = false;
          let linkedMaxEnd = null;
          for (const r2 of allRows) {
            if (r2.id === linkedSk) { inLinked = true; continue; }
            if (inLinked) {
              if (r2.indent === 0) break;
              if (r2.id.startsWith('invoicing_')) continue; // exclude invoicing_, include start_
              const d2 = workingTimeline[r2.id];
              if (d2?.plannedEndDate) {
                const ed = parseDate(d2.plannedEndDate);
                if (ed && (!linkedMaxEnd || ed > linkedMaxEnd)) linkedMaxEnd = ed;
              }
            }
          }
          // FS: start the working day AFTER the previous stage's last activity finishes.
          if (linkedMaxEnd) newDateStr = addWd(formatDate(linkedMaxEnd), 1);
          // Fallback: use the linked stage row's own plannedEndDate if no child dates exist yet
          if (!newDateStr && workingTimeline[linkedSk]?.plannedEndDate) {
            newDateStr = addWd(workingTimeline[linkedSk].plannedEndDate, 1);
          }
        }
      }
      // Sanity guard: reject dates more than 5 years in the future (cascade bugs can produce far-future dates)
      if (newDateStr) {
        const maxSane = new Date();
        maxSane.setFullYear(maxSane.getFullYear() + 5);
        if (parseDate(newDateStr) > maxSane) newDateStr = null;
      }
      const needsDate = !!newDateStr && rd.plannedStartDate !== newDateStr;

      if (!needsDuration && !needsPred && !needsDate) continue;

      const patch = { ...rd };
      if (needsDuration) patch.plannedDuration = 1;
      if (needsPred) patch[predCol] = desiredPred;
      if (needsDate) patch.plannedStartDate = newDateStr;

      // Auto-compute end date from start + duration
      const startForEnd = patch.plannedStartDate ?? rd.plannedStartDate;
      const durForEnd = patch.plannedDuration ?? rd.plannedDuration;
      if (startForEnd && durForEnd != null && durForEnd > 0) {
        const computedEnd = addWd(startForEnd, Number(durForEnd) - 1);
        if (patch.plannedEndDate !== computedEnd) patch.plannedEndDate = computedEnd;
      }

      startRowPatches[row.id] = patch;
      workingTimeline = { ...workingTimeline, [row.id]: patch };

      // Cascade dependent tasks so the next start_ row sees up-to-date prevMaxEnd.
      // After cascade, restore ALL accumulated start_ row patches — applyDependencies
      // recomputes every start_ row from its dep column on each pass, which can give a
      // different date than our prevMaxEnd-based value. Without restore, each successive
      // cascade overwrites previously-fixed start_ rows, causing an infinite date-increase loop.
      if (depsEnabled) {
        const cascaded = applyDependencies(extRowsForEffect, workingTimeline, projectSettings);
        cascaded._rows = workingTimeline._rows;
        cascaded._settings = workingTimeline._settings;
        // Restore every start_ row we have already computed — don't let cascade override any of them
        workingTimeline = { ...cascaded, ...startRowPatches };
      }
    }

    // Always run a final full cascade so dependent task dates fill even when NO start row
    // changed (e.g. deliverables whose stage's start rows are already settled).
    if (depsEnabled) {
      const cascaded = applyDependencies(extRowsForEffect, workingTimeline, projectSettings);
      cascaded._rows = workingTimeline._rows;
      cascaded._settings = workingTimeline._settings;
      workingTimeline = { ...cascaded, ...startRowPatches };
    }

    return { workingTimeline, startRowPatches, extRowsForEffect };
  }, [allRows, partStagesMap, partKeyInfo, projectSettings, paymentTermsDays]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!allRows.length || !canAutoSync || !supabaseProjects) return;
    const predCol = projectSettings.dependencies.predecessorColumn;
    const { workingTimeline, extRowsForEffect } = computeStartRowCascade(timeline);

    // Dedup on the cascade INPUTS (predecessors, duration, links, invoice/paid dates) — NOT the
    // computed date output. This guarantees the effect runs once per real change and can never
    // re-trigger on its own output (which previously compounded into runaway dates).
    const durColK = projectSettings.dependencies.durationColumn;
    const endColK = projectSettings.dateRange.endDateColumn;
    const inputSig = extRowsForEffect.map(r => {
      if (r.indent === 0 || r.isSubStageHeader) return '';
      const d = timeline[r.id] || {};
      // Include the computed END date of every non-start_ row (deliverables, closure_, invoicing_)
      // so a start_ row re-derives its date the moment its UPSTREAM stage becomes dated. Without this,
      // dating PCD after mount left inputSig unchanged (dates were excluded) and the effect early-
      // returned, so start_cd_ad never picked up PCD's max end — the user had to click the link icon
      // (a signature input) to force it. Deliverable/closure/invoicing end dates are pure cascade
      // fixed-point outputs (idempotent), so re-triggering on them is safe. start_ rows stay EXCLUDED:
      // they use a prevMaxEnd value that differs from the cascade's dep-based value, so feeding them
      // back would compound into the runaway dates this dedup was built to prevent.
      const endSig = r.id.startsWith('start_') ? '' : (d[endColK] || '');
      return `${r.id}:${d[predCol] || ''}:${d[durColK] || ''}:${d._manualStartLinkedStage || ''}:${d._delayAutoStopped ? 1 : 0}:${d._presetCleared ? 1 : 0}:${d._manualPredOverride ? 1 : 0}:${endSig}`;
    }).join('|')
      + '#' + allRows.filter(r => r.indent === 0).map(r => { const d = timeline[r.id] || {}; return `${r.id}:${d.invoiceDate || ''}:${d.paidDate || ''}`; }).join('|')
      + '#' + Object.entries(partStagesMap).map(([sid, parts]) => `${sid}:` + (parts || []).map(p => `${p.id}:${p.invoiceDate || ''}:${p.paidDate || ''}`).join(',')).join(';')
      + '#' + new Date().toISOString().slice(0, 10); // recompute once/day for overdue logic
    if (startRowPredInitRef.current === inputSig) return;

    // Collect ONLY the rows that actually differ (avoids a pointless save on first mount) and scope the
    // write to those keys. These are all indent>0 row keys (start_/deliverable/closure/parent rows) — the
    // cascade never rewrites indent-0 stage entries, so a scoped merge here can't disturb the stage-level
    // Tracking data (contract/target/actual/paid dates) that the previous full-blob overwrite clobbered.
    const sCol = projectSettings.dateRange.startDateColumn;
    const eCol = projectSettings.dateRange.endDateColumn;
    const changedRows = {};
    for (const r of extRowsForEffect) {
      if (r.indent === 0 || r.isSubStageHeader) continue;
      const w = workingTimeline[r.id] || {};
      const o = timeline[r.id] || {};
      if ((w[sCol] || '') !== (o[sCol] || '') || (w[eCol] || '') !== (o[eCol] || '') || (w[predCol] || '') !== (o[predCol] || '')) {
        changedRows[r.id] = w;
      }
    }
    startRowPredInitRef.current = inputSig;
    if (Object.keys(changedRows).length === 0) return;
    saveTimelineChangedKeys(changedRows, workingTimeline);
  }, [allRows, timeline, projectSettings, partStagesMap, canAutoSync, supabaseProjects, project.id, saveTimelineChangedKeys, computeStartRowCascade]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual trigger for Delayed button: force-applies FS+lag dep and cascades all dependent tasks
  const handleApplyDelayedDep = useCallback((startRowId) => {
    if (!canEdit || !supabaseProjects) return;
    const predCol = projectSettings.dependencies.predecessorColumn;
    const depsEnabled = projectSettings.dependencies.enabled;
    const wdSet = new Set(projectSettings.workingDays.days);
    const wdHols = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));

    const idToTaskNum = {};
    let taskN = 0;
    buildExtendedRows(allRows, partStagesMap, timeline).forEach(r => { if (r.indent > 0 && !r.isSubStageHeader) idToTaskNum[r.id] = ++taskN; });

    const rd = timeline[startRowId] || {};
    const stageKey = startRowId.slice('start_'.length);
    const myStageIdx = allRows.findIndex(r => r.id === stageKey);

    let prevSk = null;
    if (myStageIdx > 0) {
      let prevStageIdx = myStageIdx - 1;
      while (prevStageIdx >= 0 && allRows[prevStageIdx].indent !== 0) prevStageIdx--;
      if (prevStageIdx >= 0) prevSk = allRows[prevStageIdx].id;
    }
    if (isAdvanceStage(prevSk)) prevSk = null; // no preset link on Advance
    // Honour a manual link (covers part start rows, which have no previous-stage default).
    const linkedSk = manualStartLink(startRowId, rd) || prevSk;
    if (!linkedSk) return;

    const invRow = timeline[`invoicing_${linkedSk}`] || {};
    // Window end = invoice date + terms (invoicing plannedEndDate now shows the paid date once paid).
    const invEnd = invRow.plannedStartDate
      ? (addCalendarDaysStr(invRow.plannedStartDate, invRow.plannedDuration != null ? Number(invRow.plannedDuration) : (paymentTermsDays != null ? Number(paymentTermsDays) : 0)) || invRow.plannedEndDate || null)
      : (invRow.plannedEndDate || null);
    if (!invEnd) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Once paid, the delay is fixed: counted to the paid date, not to today.
    let linkedPaid = null;
    if (linkedSk.startsWith('substage_')) {
      const pki = partKeyInfo.get(linkedSk);
      const part = pki ? (partStagesMap[pki.stageId] || []).find(p => p.id === pki.partId) : null;
      linkedPaid = part?.paidDate || null;
    } else {
      linkedPaid = timeline[linkedSk]?.paidDate || null;
    }
    const lagEnd = linkedPaid ? parseDate(linkedPaid) : today;

    // Delay lag = WORKING days (weekends excluded, brio holidays INCLUDED) the invoice is past its window.
    let lag = 0;
    for (let cur = parseDate(invEnd); cur.getTime() + DAY_MS < lagEnd.getTime();) {
      cur = new Date(cur.getTime() + DAY_MS);
      if (wdSet.has(cur.getDay())) lag++;
    }

    const invTaskNum = idToTaskNum[`invoicing_${linkedSk}`];
    if (!invTaskNum) return;

    const desiredPred = lag > 0 ? `${invTaskNum}FS+${lag}d` : `${invTaskNum}FS`;

    // Push (1 + lag) working days incl. holidays past the window (matches applyDependencies' delayLink).
    let newStart = parseDate(invEnd);
    for (let rem = 1 + lag; rem > 0;) {
      newStart = new Date(newStart.getTime() + DAY_MS);
      if (wdSet.has(newStart.getDay())) rem--;
    }
    const newStartStr = formatDate(newStart);

    const dur = rd.plannedDuration ?? 1;
    let endDate = new Date(newStart.getTime());
    let rem2 = Number(dur) - 1;
    while (rem2 > 0) {
      endDate = new Date(endDate.getTime() + DAY_MS);
      if (wdSet.has(endDate.getDay()) && !wdHols.has(formatDate(endDate))) rem2--;
    }
    const newEndStr = formatDate(endDate);

    const patch = { ...rd, [predCol]: desiredPred, plannedStartDate: newStartStr, plannedEndDate: newEndStr, plannedDuration: dur, _delayAutoStopped: false };
    let finalTimeline = { ...timeline, [startRowId]: patch };
    // Applying the delay takes back any Ignore on it, the linked Closure badge's included, so Delayed
    // works in one click straight from Ignore. The closure backfill re-derives its dates.
    const closureKey = `closure_${linkedSk}`;
    if (timeline[closureKey]?._delayIgnored) {
      const closure = { ...timeline[closureKey] };
      delete closure._delayIgnored;
      finalTimeline[closureKey] = closure;
    }
    if (depsEnabled) {
      const cascaded = applyDependencies(buildExtendedRows(allRows, partStagesMap, timeline), finalTimeline, projectSettings);
      cascaded._rows = finalTimeline._rows;
      cascaded._settings = finalTimeline._settings;
      finalTimeline = cascaded;
    }
    startRowPredInitRef.current = null; // reset so effect re-evaluates after save
    writeTimelineDiff(supabaseProjects, project.id, timeline, finalTimeline, { allowTracking: syncEnabledRef.current });
  }, [allRows, partStagesMap, partKeyInfo, timeline, projectSettings, canEdit, supabaseProjects, project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle the _delayAutoStopped flag on a start_ row — prevents auto-delay effect from firing.
  // One Ignore covers the whole delay: it also ignores the linked stage's Closure badge (the same
  // _delayIgnored flag and actual-date snap as handleIgnoreClosureDelay), and undoing it undoes both.
  const handleToggleDelayStop = useCallback((startRowId, closureKey = null, linkedStageKey = null) => {
    if (!canEdit || !supabaseProjects) return;
    const rd = timeline[startRowId] || {};
    const stopping = !rd._delayAutoStopped;
    const next = { ...timeline, [startRowId]: { ...rd, _delayAutoStopped: stopping } };
    if (closureKey) {
      const closure = { ...(timeline[closureKey] || {}) };
      if (stopping) {
        const actual = (resolveStageRecord(linkedStageKey) || {}).actualDate || null;
        closure._delayIgnored = true;
        closure.plannedStartDate = actual;
        closure.plannedEndDate = actual;
      } else {
        // The closure backfill re-derives the Closure dates once the flag is gone.
        delete closure._delayIgnored;
      }
      next[closureKey] = closure;
    }
    startRowPredInitRef.current = null;
    writeTimelineDiff(supabaseProjects, project.id, timeline, next, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, timeline, project.id, resolveStageRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore an ignored delay (click on the greyed Closure badge). One delay, one switch: it also
  // releases the Ignore on every Start row linked to that stage, as that Ignore set both. The closure
  // backfill re-derives the Closure dates once the flag is gone.
  const handleRestoreClosureDelay = useCallback((closureKey, stageOrSubKey) => {
    if (!canEdit || !supabaseProjects) return;
    const closure = { ...(timeline[closureKey] || {}) };
    delete closure._delayIgnored;
    const next = { ...timeline, [closureKey]: closure };
    // A Start row links to its manual pick, else (a stage's own Start row) to the stage above it.
    const linkedStageOf = (startRowId) => {
      const manual = manualStartLink(startRowId, timeline[startRowId] || {});
      if (manual) return manual;
      const myIdx = allRows.findIndex(r => r.id === startRowId.slice('start_'.length));
      let i = myIdx - 1;
      while (i >= 0 && allRows[i].indent !== 0) i--;
      return myIdx > 0 && i >= 0 && !isAdvanceStage(allRows[i].id) ? allRows[i].id : null;
    };
    Object.keys(timeline).forEach(k => {
      if (k.startsWith('start_') && timeline[k]?._delayAutoStopped && linkedStageOf(k) === stageOrSubKey) {
        next[k] = { ...timeline[k], _delayAutoStopped: false };
      }
    });
    startRowPredInitRef.current = null;
    writeTimelineDiff(supabaseProjects, project.id, timeline, next, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, timeline, project.id, allRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manually link a start_ row to a specific stage — the effect then derives the date
  // from that stage's live conditions (overdue lag, invoice date, or stage end date).
  // Save a Start row patch with its preset applied now (dependency, start date and the cascade), so a
  // link pick or a reset restores the preset for anyone who makes it: the effect above only runs for
  // admins and the project's supervisors, so a Full access user would otherwise keep stale dependencies.
  const writeStartRowPreset = useCallback((startRowId, patch) => {
    const linked = { ...timeline, [startRowId]: patch };
    const { workingTimeline, extRowsForEffect } = computeStartRowCascade(linked);
    const next = { ...linked };
    // Only task rows, as the effect saves: never stage entries or part headers.
    extRowsForEffect.forEach(r => {
      if (r.indent === 0 || r.isSubStageHeader || workingTimeline[r.id] === undefined) return;
      next[r.id] = workingTimeline[r.id];
    });
    writeTimelineDiff(supabaseProjects, project.id, timeline, next, { allowTracking: syncEnabledRef.current });
  }, [supabaseProjects, timeline, project.id, computeStartRowCascade]);

  const handleLinkStageDate = useCallback((startRowId, sourceId) => {
    if (!canEdit || !supabaseProjects) return;
    if (isOwnStageLink(startRowId, sourceId)) return; // a Start row never links to its own stage
    const rd = timeline[startRowId] || {};
    // Linking re-enables the preset for this start row (undoes a manual clear or a manual dep override).
    const patch = { ...rd, _manualStartLinkedStage: sourceId, _presetCleared: false, _manualPredOverride: false };
    setStageLinkMenu(null);
    startRowPredInitRef.current = null; // force effect to re-derive date from new linked stage
    // Apply the preset now (dependency, start date and the cascade), so a pick restores it for anyone who
    // makes it: the effect above only runs for admins and the project's supervisors. A manual dependency
    // edit sets _manualPredOverride, which the pick clears, so this is also the way back to the preset.
    writeStartRowPreset(startRowId, patch);
  }, [canEdit, supabaseProjects, timeline, writeStartRowPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Reset to preset link" — drop any manual dependency override / manual clear on a start_ row so the
  // auto-preset effect takes over again (re-derives the preset dep + date from the linked/previous stage).
  const handleResetStartPreset = useCallback((startRowId) => {
    if (!canEdit || !supabaseProjects) return;
    const rd = { ...(timeline[startRowId] || {}) };
    rd._presetCleared = false;
    rd._manualPredOverride = false;
    // Back to the stage directly above: a hand picked stage link goes too.
    delete rd._manualStartLinkedStage;
    setStageLinkMenu(null);
    startRowPredInitRef.current = null; // force the effect to recompute this row's preset
    writeStartRowPreset(startRowId, rd);
  }, [canEdit, supabaseProjects, timeline, writeStartRowPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the timeline to its original, scope-driven state — this also fully clears any timeline
  // TEMPLATE that was applied to the project:
  //  • Stage sequence reverts to the confirmed-scope (canonical) order
  //  • Applied-template / custom-added rows (a template's inserted tasks, and any extra meeting /
  //    revision rows beyond the scope's own count) are dropped ENTIRELY — both their structure and
  //    their data — so no applied-template task lingers as an orphan
  //  • Manual reordering, sub-stage assignments, task renames, assignees, planned dates and task
  //    predecessors on scope rows are discarded — the planned schedule re-derives from default
  //    presets (each start_ row links to the stage immediately above it)
  // KEPT: the page's own configuration (saved view settings, custom columns AND their per-row values,
  // and settings presets) plus ALL real project data — a reset is a planning-page operation and must NEVER
  // touch the Tracking (Timeline) page or the Invoices page. So every stage keeps its Tracking + Invoicing
  // data verbatim: contract/target/actual/approval/invoice/paid dates, invoice %, billing, and partStages.
  // Paid/invoiced stages therefore stay in position (their preserved dates drive the Invoicing/Closure rows
  // via the behaviour-settings backfill); only the planning schedule re-derives from the start-row presets.
  const handleResetTimelines = useCallback(async () => {
    // Runs on any real project (with OR without a confirmed scope); only the Template Builder — whose
    // rows ARE the definition — is exempt. Previously this bailed whenever there was no confirmed
    // scope, which made "Reset timelines" silently do nothing on unscoped projects.
    if (!canEdit || !supabaseProjects || isTemplateBuilder) return;
    const PRESERVE = ['contractDate', 'targetDate', 'actualDate', 'clientApprovalDate', 'invoiceDate', 'paidDate', 'invoicePercent', 'billing', 'partStages'];
    const META = ['_viewSettings', '_settingsPresets', '_activePresetId', '_customColumns'];
    // Row IDs that belong to the confirmed scope (stages + their start / deliverable / invoicing /
    // closure rows, plus the meeting / revision rows the scope itself defines). Anything else at row
    // level was inserted by an applied template (or added manually) and must be purged so the reset
    // genuinely undoes the template rather than leaving its tasks (or their data) behind.
    const scopeRowIds = new Set(confirmedScope ? buildRowsFromScope(confirmedScope).map(r => r.id) : []);
    const isTemplateAddedRow = (key) =>
      key.startsWith('row_') ||
      ((key.startsWith('meeting_') || key.startsWith('revision_')) && !scopeRowIds.has(key));
    const nt = {};
    META.forEach(k => { if (timeline[k] !== undefined) nt[k] = timeline[k]; });
    Object.entries(timeline).forEach(([key, val]) => {
      if (key.startsWith('_')) return;
      if (!val || typeof val !== 'object') return;
      // Applied-template / custom rows: drop entirely (structure comes off via the _rows removal
      // below; this also discards their per-row data so nothing lingers as an orphan entry).
      if (isTemplateAddedRow(key)) return;
      const kept = {};
      // A reset is a PLANNING-page operation only: it must NEVER change the Tracking (Timeline) page or the
      // Invoices page. So every tracking/billing/invoicing field is preserved verbatim for EVERY stage —
      // contract/target/actual/approval/invoice/paid dates, invoice % and billing splits, and partStages.
      // Only the planning SCHEDULE (plannedStart/End/Duration + predecessors), which are absent from
      // PRESERVE, is dropped so it re-derives from the start-row presets.
      PRESERVE.forEach(f => { if (val[f] !== undefined) kept[f] = val[f]; });
      // Keep custom-column values (custom_<id>) too, so a reset preserves custom columns fully —
      // their entered values, not just the column definitions in _customColumns.
      Object.keys(val).forEach(f => { if (f.startsWith('custom_')) kept[f] = val[f]; });
      if (Object.keys(kept).length) nt[key] = kept;
    });
    // With a confirmed scope, drop _rows so allRows rebuilds from the canonical scope order + scope
    // deliverables. Without a scope there's nothing to rebuild from, so keep the current stage
    // structure minus the template/custom rows (otherwise the page would blank out).
    // No _depNumKey → predecessor numbering re-stamps. Start-row effect re-derives default presets;
    // the invoicing/closure backfill repopulates those rows from the preserved invoice/paid dates.
    if (!confirmedScope) {
      nt._rows = (allRows || [])
        .filter(r => !isTemplateAddedRow(r.id))
        .map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    }
    startRowPredInitRef.current = null;
    isOptimisticActive.current = false;
    setOptimisticRows(null);
    setOptimisticPreds({});
    setShowResetConfirm(false);
    setShowExportMenu(false);
    await writeTimelineDiff(supabaseProjects, project.id, timeline, nt, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, confirmedScope, timeline, project.id, isTemplateBuilder, allRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move a stage (and all its children) up or down in the row order
  const handleMoveStage = useCallback((stageId, direction) => {
    if (!canEdit || !supabaseProjects) return;
    // Use saved _rows if available; otherwise build from allRows
    const baseRows = (timeline._rows && timeline._rows.length > 0)
      ? timeline._rows
      : allRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));

    const stageIdx = baseRows.findIndex(r => r.id === stageId);
    if (stageIdx === -1) return;

    // Find end of this stage's group (stage row + all its indent>0 children)
    let groupEnd = stageIdx + 1;
    while (groupEnd < baseRows.length && baseRows[groupEnd].indent > 0) groupEnd++;

    let newRows;
    let otherStageId = null; // the stage that swapped position with stageId
    if (standaloneTab) {
      // BRM: swap with the neighbouring stage of the same tab, leaving other tabs' stages in place.
      const groups = [];
      baseRows.forEach(r => { if (r.indent === 0 || groups.length === 0) groups.push([r]); else groups[groups.length - 1].push(r); });
      const inTab = new Set(standaloneTab.keys);
      const tabIdx = groups.map((g, i) => (inTab.has(g[0].id) ? i : -1)).filter(i => i >= 0);
      const me = groups.findIndex(g => g[0].id === stageId);
      const pos = tabIdx.indexOf(me);
      const other = direction === 'up' ? tabIdx[pos - 1] : tabIdx[pos + 1];
      if (pos < 0 || other == null) return;
      otherStageId = groups[other][0].id;
      const swapped = groups.slice();
      swapped[me] = groups[other];
      swapped[other] = groups[me];
      newRows = swapped.flat();
    } else if (direction === 'up') {
      // Find start of previous stage group
      let prevStageIdx = stageIdx - 1;
      while (prevStageIdx >= 0 && baseRows[prevStageIdx].indent > 0) prevStageIdx--;
      if (prevStageIdx < 0) return; // already first stage
      otherStageId = baseRows[prevStageIdx].id;
      newRows = [
        ...baseRows.slice(0, prevStageIdx),
        ...baseRows.slice(stageIdx, groupEnd),   // this group goes first
        ...baseRows.slice(prevStageIdx, stageIdx), // prev group follows
        ...baseRows.slice(groupEnd),
      ];
    } else {
      if (groupEnd >= baseRows.length) return; // already last stage
      // Find end of next stage group
      let nextGroupEnd = groupEnd + 1;
      while (nextGroupEnd < baseRows.length && baseRows[nextGroupEnd].indent > 0) nextGroupEnd++;
      otherStageId = baseRows[groupEnd].id;
      newRows = [
        ...baseRows.slice(0, stageIdx),
        ...baseRows.slice(groupEnd, nextGroupEnd), // next group goes first
        ...baseRows.slice(stageIdx, groupEnd),      // this group follows
        ...baseRows.slice(nextGroupEnd),
      ];
    }
    // Auto-update _manualStartLinkedStage for start_ rows whose linked stage was the
    // immediately-previous stage before the reorder (so the link follows the new neighbour).
    const oldStageOrder = baseRows.filter(r => r.indent === 0).map(r => r.id);
    const newStageOrder = newRows.filter(r => r.indent === 0).map(r => r.id);
    const oldPrevOf = {};
    const newPrevOf = {};
    oldStageOrder.forEach((sid, i) => { oldPrevOf[sid] = i > 0 ? oldStageOrder[i - 1] : null; });
    newStageOrder.forEach((sid, i) => { newPrevOf[sid] = i > 0 ? newStageOrder[i - 1] : null; });

    const newTimeline = { ...timeline, _rows: newRows };

    // Swap schedule-strip dates between the two swapped stages to maintain date sequence
    const DATE_FIELDS = ['plannedStartDate', 'plannedDuration', 'plannedEndDate', 'contractDate'];
    if (otherStageId) {
      const aData = newTimeline[stageId] || {};
      const bData = newTimeline[otherStageId] || {};
      const aHasDates = DATE_FIELDS.some(f => aData[f] != null);
      const bHasDates = DATE_FIELDS.some(f => bData[f] != null);
      if (aHasDates || bHasDates) {
        const aDateFields = Object.fromEntries(DATE_FIELDS.map(f => [f, aData[f] ?? null]));
        const bDateFields = Object.fromEntries(DATE_FIELDS.map(f => [f, bData[f] ?? null]));
        newTimeline[stageId] = { ...aData, ...bDateFields };
        newTimeline[otherStageId] = { ...bData, ...aDateFields };
      }
    }

    for (const sid of newStageOrder) {
      const startKey = `start_${sid}`;
      const rd = newTimeline[startKey];
      if (!rd) continue;
      const currentLink = rd._manualStartLinkedStage;
      const oldPrev = oldPrevOf[sid];
      const newPrev = newPrevOf[sid];
      if (currentLink === oldPrev && newPrev !== oldPrev) {
        if (newPrev) {
          newTimeline[startKey] = { ...rd, _manualStartLinkedStage: newPrev };
        } else {
          const { _manualStartLinkedStage, ...rest } = rd; // eslint-disable-line no-unused-vars
          newTimeline[startKey] = rest;
        }
      }
    }
    // Remap all predecessor cell values to reflect the new task numbering after stage reorder
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(newRows, partStagesMap, newTimeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    const remappedTimeline = remapPredecessorValues(newTimeline, newExtRows, oldNumToKey, newKeyToNum);
    remappedTimeline._depNumKey = newNumToKey;

    startRowPredInitRef.current = null; // force start_ row effect to re-derive dates
    writeTimelineDiff(supabaseProjects, project.id, timeline, remappedTimeline, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, timeline, allRows, partStagesMap, project.id, standaloneTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter out rows hidden by collapsed parents, then inject sub-stage header rows
  const displayRows = useMemo(() => {
    // Active tab filter — keeps only the selected stages/parts in the rendered table. Numbering,
    // links, contract dates and presets still operate on the full project (they don't use this).
    const tabFilter = (rows) => {
      // BRM: show only the stages of the tab on screen, each with everything under it.
      if (standaloneTab) {
        const keep = new Set(standaloneTab.keys);
        let cur = null;
        return rows.filter(r => { if (r.indent === 0 && !r.isSubStageHeader) cur = r.id; return keep.has(cur); });
      }
      if (!activeTab) return rows;
      const keys = new Set(activeTab.keys || []);
      const out = [];
      let curStage = null;
      let stageSelected = false; // the stage itself is selected (show its own/non-part rows)
      let stageVisible = false;  // stage row shown as a container (selected OR has a selected part)
      for (const row of rows) {
        if (row.indent === 0) {
          curStage = row.id;
          stageSelected = keys.has(row.id);
          const anyPart = (partStagesMap[row.id] || []).some(p => keys.has(`substage_${row.id}_${p.id}`));
          stageVisible = stageSelected || anyPart;
          // Only render the parent stage row when the stage itself is selected. When a tab
          // selects just part-stages, the parent header is hidden but its parts still show.
          if (stageSelected) out.push(row);
          continue;
        }
        if (!stageVisible) continue;
        // Part header / part system rows: gate on the substage key being selected
        if (row.isSubStageHeader || row.isPartSystem) {
          const sk = row.isSubStageHeader ? row.id : row.id.replace(/^(start_|invoicing_|closure_)/, '');
          if (sk.startsWith('substage_') && !keys.has(sk)) continue;
          out.push(row); continue;
        }
        // Task assigned to a part: gate on that part being selected (works even if the stage isn't)
        const sid = timeline[row.id]?._subStageId;
        if (sid && curStage) {
          if (!keys.has(`substage_${curStage}_${sid}`)) continue;
          out.push(row); continue;
        }
        // Stage-level row (stage start/invoicing/closure, unassigned deliverables): only when the
        // stage itself is selected.
        if (!stageSelected) continue;
        out.push(row);
      }
      return out;
    };

    // Step 0: filter user-hidden rows (meeting_ / revision_ rows hidden via right-click)
    const visible = showHiddenRows
      ? allRows
      : allRows.filter(r => !timeline[r.id]?._hidden);

    // Step 1: apply collapse filter
    let collapsed;
    if (collapsedRows.size === 0) {
      collapsed = visible;
    } else {
      collapsed = [];
      const skipBelow = []; // stack of indent levels for collapsed ancestors
      for (let i = 0; i < visible.length; i++) {
        const row = visible[i];
        while (skipBelow.length > 0 && row.indent <= skipBelow[skipBelow.length - 1]) {
          skipBelow.pop();
        }
        if (skipBelow.length > 0) continue; // hidden
        collapsed.push(row);
        if (collapsedRows.has(row.id) && rowHasChildren(visible, i)) {
          skipBelow.push(row.indent);
        }
      }
    }

    // Step 2: inject virtual sub-stage header rows for actively selected sub-stages only
    const hasAnyActive = Object.keys(activeSubStages).some(sid => activeSubStages[sid]?.size > 0);
    if (!hasAnyActive) return tabFilter(collapsed);

    const result = [];
    let idx = 0;
    while (idx < collapsed.length) {
      const row = collapsed[idx];
      const selectedParts = row.indent === 0 && !collapsedRows.has(row.id)
        ? (activeSubStages[row.id] ? (partStagesMap[row.id] || []).filter(p => activeSubStages[row.id].has(p.id)).sort((a, b) => (a.name || '').localeCompare(b.name || '')) : [])
        : [];

      if (selectedParts.length === 0) {
        result.push(row);
        idx++;
        continue;
      }

      // Stage row with active sub-stages — collect all visible children, then re-emit grouped
      result.push(row);
      idx++;
      const children = [];
      while (idx < collapsed.length && collapsed[idx].indent > 0) {
        children.push(collapsed[idx]);
        idx++;
      }
      const startRows = children.filter(r => r.id.startsWith('start_'));
      const systemRows = children.filter(r => r.id.startsWith('closure_') || r.id.startsWith('invoicing_'));
      const regularRows = children.filter(r => !r.id.startsWith('closure_') && !r.id.startsWith('invoicing_') && !r.id.startsWith('start_'));
      const selectedPartIds = new Set(selectedParts.map(p => p.id));

      // Group children by assigned sub-stage (only selected parts get headers)
      const grouped = {};
      selectedParts.forEach(p => { grouped[p.id] = []; });
      const unassignedRows = [];
      regularRows.forEach(r => {
        const sid = timeline[r.id]?._subStageId || null;
        if (sid && selectedPartIds.has(sid)) grouped[sid].push(r);
        else unassignedRows.push(r);
      });

      // Start-of-stage rows always stay at the very top of the stage group
      startRows.forEach(r => result.push(r));

      // Unassigned rows next (no sub-stage header — they sit directly under stage row)
      unassignedRows.forEach(r => result.push(r));

      // Selected sub-stage header rows + their tasks.
      // Each part stage also gets its own Start / Invoicing / Closure rows (mirrors
      // main stages) keyed start_/invoicing_/closure_<substageKey> so the existing
      // render + sync paths handle them; dates are stored under those keys.
      selectedParts.forEach(part => {
        const subKey = `substage_${row.id}_${part.id}`;
        const partName = part.name || part.id;
        result.push({ id: subKey, key: subKey, indent: 1, name: partName, isSubStageHeader: true, partId: part.id, stageId: row.id, defaultLabel: null });
        // Collapsing a sub-stage header hides its Start/task/Invoicing/Closure rows (kept within
        // the main stage group).
        if (collapsedRows.has(subKey)) return;
        if (isAdvanceStage(row.id)) { grouped[part.id].forEach(r => result.push(r)); return; }
        const startKey = `start_${subKey}`;
        result.push({ id: startKey, key: startKey, indent: 1, name: `Start of ${partName} Stage`, defaultLabel: `Start of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
        grouped[part.id].forEach(r => result.push(r));
        const invKey = `invoicing_${subKey}`;
        const closeKey = `closure_${subKey}`;
        result.push({ id: invKey, key: invKey, indent: 1, name: `Invoicing of ${partName} Stage`, defaultLabel: `Invoicing of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
        result.push({ id: closeKey, key: closeKey, indent: 1, name: `Closure of ${partName} Stage`, defaultLabel: `Closure of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
      });

      // Invoicing/closure rows always at the bottom of the stage group
      systemRows.forEach(r => result.push(r));
    }
    return tabFilter(result);
  }, [allRows, collapsedRows, partStagesMap, timeline, activeSubStages, showHiddenRows, activeTab, standaloneTab]);

  const hiddenRowCount = useMemo(
    () => allRows.filter(r => timeline[r.id]?._hidden).length,
    [allRows, timeline]
  );

  const toggleCollapse = useCallback((rowId) => {
    setCollapsedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  }, []);

  // Task Name header icon: expand / minimise every stage and part stage at once, in three states cycled
  // by click. 'expanded' opens every stage's tasks and leaves each contract dates strip as it is;
  // 'minStrips' minimises the stages and their strips; 'minOpen' minimises the stages with their strips
  // open. The current state is read off collapsedRows / collapsedStripRows, so a single stage's own
  // chevron still works and the icon picks up from whatever the table shows.
  const bulkCollapseIds = useMemo(
    () => extendedRows.filter(r => r.indent === 0 || r.isSubStageHeader).map(r => r.id),
    [extendedRows]
  );
  const bulkCollapseMode = useMemo(() => {
    if (!bulkCollapseIds.length || !bulkCollapseIds.every(id => collapsedRows.has(id))) return 'expanded';
    return bulkCollapseIds.every(id => collapsedStripRows.has(id)) ? 'minStrips' : 'minOpen';
  }, [bulkCollapseIds, collapsedRows, collapsedStripRows]);
  const cycleBulkCollapse = useCallback(() => {
    const ids = bulkCollapseIds;
    const without = (prev) => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; };
    const withAll = (prev) => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; };
    if (bulkCollapseMode === 'expanded') {
      setCollapsedRows(withAll);
      setCollapsedStripRows(withAll);
    } else if (bulkCollapseMode === 'minStrips') {
      setCollapsedStripRows(without);
    } else {
      setCollapsedRows(without);
    }
  }, [bulkCollapseIds, bulkCollapseMode]);

  const toggleStripCollapse = useCallback((rowId) => {
    setCollapsedStripRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  }, []);

  // Task numbers keyed by row ID — covers ALL part-stage system rows via extendedRows. This is
  // the STORAGE numbering for predecessors: stable regardless of which sub-stages are selected,
  // so toggling a part's checkbox never shifts stored references or drops dependencies.
  const taskNumMap = useMemo(() => computeTaskNums(extendedRows).keyToNum, [extendedRows]);

  // Visible row order for DISPLAY numbering — mirrors displayRows (only SELECTED sub-stages and
  // their start/invoicing/closure rows are present) but without the collapse filter, so the #
  // column reads sequentially with no gaps for unselected parts.
  const numOrderRows = useMemo(() => {
    // Include hidden rows in the numbering order so hiding a row does NOT renumber the others —
    // a hidden row keeps its reserved number; only unselected part-stages are compacted out below.
    // EXCEPTION: the Template Builder's stage filter (visibleStageKeys) genuinely removes stages from
    // view, so those rows are dropped from the numbering too — the # column then reads continuously
    // 1..N over the stages actually shown, and dependency numbers (which resolve through this map)
    // follow. Predecessors are stored as stable seq numbers, so nothing is lost when the filter clears.
    // BRM numbers each tab from 1, the same way: only the tab's stages are counted.
    let visible = allRows;
    const numStageKeys = visibleStageKeys || (standaloneTab ? new Set(standaloneTab.keys) : null);
    if (numStageKeys) {
      const stageOf = new Map();
      let cur = null;
      allRows.forEach(r => { if ((r.indent || 0) === 0) cur = r.id; stageOf.set(r.id, cur); });
      visible = allRows.filter(r => { const sk = stageOf.get(r.id); return sk == null || numStageKeys.has(sk); });
    }
    const hasAnyActive = Object.keys(activeSubStages).some(sid => activeSubStages[sid]?.size > 0);
    if (!hasAnyActive) return visible;

    const result = [];
    let idx = 0;
    while (idx < visible.length) {
      const row = visible[idx];
      if (row.indent !== 0) { result.push(row); idx++; continue; }
      const selectedParts = activeSubStages[row.id]
        ? (partStagesMap[row.id] || []).filter(p => activeSubStages[row.id].has(p.id)).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        : [];
      result.push(row);
      idx++;
      if (selectedParts.length === 0) continue;

      const children = [];
      while (idx < visible.length && visible[idx].indent > 0) { children.push(visible[idx]); idx++; }
      const startRows = children.filter(r => r.id.startsWith('start_'));
      const systemRows = children.filter(r => r.id.startsWith('closure_') || r.id.startsWith('invoicing_'));
      const regularRows = children.filter(r => !r.id.startsWith('closure_') && !r.id.startsWith('invoicing_') && !r.id.startsWith('start_'));
      const selectedPartIds = new Set(selectedParts.map(p => p.id));
      const grouped = {};
      selectedParts.forEach(p => { grouped[p.id] = []; });
      const unassigned = [];
      regularRows.forEach(r => {
        const sid = timeline[r.id]?._subStageId || null;
        if (sid && selectedPartIds.has(sid)) grouped[sid].push(r); else unassigned.push(r);
      });
      startRows.forEach(r => result.push(r));
      unassigned.forEach(r => result.push(r));
      selectedParts.forEach(part => {
        const subKey = `substage_${row.id}_${part.id}`;
        result.push({ id: subKey, indent: 1, isSubStageHeader: true });
        if (isAdvanceStage(row.id)) { grouped[part.id].forEach(r => result.push(r)); return; }
        result.push({ id: `start_${subKey}`, indent: 1, isPartSystem: true });
        grouped[part.id].forEach(r => result.push(r));
        result.push({ id: `invoicing_${subKey}`, indent: 1, isPartSystem: true });
        result.push({ id: `closure_${subKey}`, indent: 1, isPartSystem: true });
      });
      systemRows.forEach(r => result.push(r));
    }
    return result;
  }, [allRows, timeline, activeSubStages, partStagesMap, showHiddenRows, visibleStageKeys, standaloneTab]);

  // Flat sequential display numbers (whole numbers) based on numOrderRows, so the # column reflects
  // only the currently-selected sub-stages (no reserved gaps for unselected parts). Parents and
  // their subtasks share one continuous 1..N sequence — no dotted "5.1" hierarchy — matching the
  // stored predecessor numbering so a task's # equals the number used to reference it.
  const displayNumMap = useMemo(() => computeTaskNums(numOrderRows).keyToNum, [numOrderRows]);

  // Maps stored (stable) predecessor number → visible display number. Falls back to the raw
  // number when the referenced row is not currently visible (e.g. its part is unselected).
  const seqToHierLabel = useMemo(() => {
    const { numToKey } = computeTaskNums(extendedRows);
    const map = {};
    Object.entries(numToKey).forEach(([seq, rowId]) => {
      map[Number(seq)] = displayNumMap[rowId] || String(seq);
    });
    return map;
  }, [extendedRows, displayNumMap]);

  // Reverse map: visible display label (what the user sees/types in the # column) → stored
  // (stable) predecessor number. Used to translate manually-typed dependency numbers so a typed
  // "27" references the row shown as 27, not the internal storage row 27.
  const displayLabelToTaskNum = useMemo(() => {
    const m = {};
    Object.entries(displayNumMap).forEach(([rowId, label]) => {
      const sn = taskNumMap[rowId];
      if (sn != null) m[String(label)] = sn;
    });
    return m;
  }, [displayNumMap, taskNumMap]);

  // Set of row IDs whose tasks are in an active sub-stage — used for extra indentation display
  const subStageIndentRows = useMemo(() => {
    const set = new Set();
    const hasActive = Object.keys(activeSubStages).some(sid => activeSubStages[sid]?.size > 0);
    if (!hasActive) return set;
    let currentStageId = null;
    allRows.forEach(row => {
      if (row.indent === 0) { currentStageId = row.id; return; }
      if (!currentStageId) return;
      const subId = timeline[row.id]?._subStageId;
      if (subId && activeSubStages[currentStageId]?.has(subId)) {
        // Only indent if the sub-stage still exists (guards against stale activeSubStages after deletion)
        const exists = (partStagesMap[currentStageId] || []).some(p => p.id === subId);
        if (exists) set.add(row.id);
      }
    });
    return set;
  }, [allRows, timeline, activeSubStages, partStagesMap]);

  // Each display row's owning stage (nearest preceding indent-0 row) as its timeline key. Used by the
  // Template Builder's stage filter to hide unselected stages at RENDER time only — numbering,
  // dependencies and saved data all stay computed off the full row set, so nothing is lost.
  const rowStageKeyById = useMemo(() => {
    const m = new Map();
    let cur = null;
    displayRows.forEach(r => { if ((r.indent || 0) === 0) cur = r.id; m.set(r.id, cur); });
    return m;
  }, [displayRows]);
  const isStageVisible = useCallback(
    (rowId) => {
      if (!visibleStageKeys) return true;
      const sk = rowStageKeyById.get(rowId);
      return sk == null || visibleStageKeys.has(sk); // rows with no owning stage always render
    },
    [visibleStageKeys, rowStageKeyById]
  );

  // Row IDs for drag-and-drop (hidden stages excluded so dnd-kit has no orphaned sortable nodes;
  // matches exactly the rows the table body renders via isStageVisible)
  const rowIds = useMemo(
    () => displayRows.filter(r => isStageVisible(r.id)).map(r => r.id),
    [displayRows, isStageVisible]
  );

  // DnD sensors — long press (300ms hold) on row number triggers drag
  // PointerSensor handles mouse + stylus + touch via Pointer Events API
  // TouchSensor provides fallback for older Android browsers using Touch Events API
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } })
  );

  // ── Save helpers ──────────────────────────────────────────────────────
  const persistTimeline = useCallback(async (newTimeline) => {
    if (!supabaseProjects) return false;
    const result = await writeTimelineDiff(supabaseProjects, project.id, timeline, newTimeline, { allowTracking: syncEnabledRef.current });
    return result.success;
  }, [supabaseProjects, project.id]);

  // ── Derivative-template per-stage base sync ──────────────────────────────
  // In the Template Builder, a "created from" derivative (its base's timeline arrives as `baseTimeline`)
  // controls, per stage, which authored columns the base may overwrite — stored on the timeline as
  // `_syncPrefs`. Default = every column ON (full auto-sync when the base is saved); unchecking a column
  // holds the base back for that stage; all columns off freezes the stage entirely.
  const isDerivativeTemplate = isTemplateBuilder && !!baseTimeline;
  const syncPrefs = (timeline && timeline._syncPrefs && typeof timeline._syncPrefs === 'object') ? timeline._syncPrefs : {};
  const [syncMenuStage, setSyncMenuStage] = useState(null); // { stageId, x, y } | null — open column checklist
  useEffect(() => {
    if (!syncMenuStage) return;
    const onDown = () => setSyncMenuStage(null);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [syncMenuStage]);
  // Flip one column on/off for a stage. Turning ON deletes the explicit false (absent ⇒ on, keeps prefs
  // minimal); turning OFF stores false.
  const toggleSyncColumn = useCallback(async (stageKey, colKey) => {
    const prefs = (timeline._syncPrefs && typeof timeline._syncPrefs === 'object') ? timeline._syncPrefs : {};
    const stagePref = prefs[stageKey] || {};
    const cols = { ...(stagePref.columns || {}) };
    if (cols[colKey] === false) delete cols[colKey]; else cols[colKey] = false;
    await persistTimeline({ ...timeline, _syncPrefs: { ...prefs, [stageKey]: { ...stagePref, columns: cols } } });
  }, [timeline, persistTimeline]);
  // Check all / none for a stage.
  const setStageSyncAll = useCallback(async (stageKey, on) => {
    const prefs = (timeline._syncPrefs && typeof timeline._syncPrefs === 'object') ? timeline._syncPrefs : {};
    const cols = on ? {} : Object.fromEntries(SYNC_COLUMN_KEYS.map(k => [k, false]));
    await persistTimeline({ ...timeline, _syncPrefs: { ...prefs, [stageKey]: { ...(prefs[stageKey] || {}), columns: cols } } });
  }, [timeline, persistTimeline]);
  // Manual "Sync now" for ONE stage — pull the base into just this stage (respecting its checklist),
  // leaving every other stage untouched.
  const handleSyncStage = useCallback(async (stageKey) => {
    if (!canSync || !baseTimeline) return;
    const merged = mergeBaseDeliverablesIntoDerived(baseTimeline, timeline, { restrictToStages: new Set([stageKey]) });
    await persistTimeline(merged);
  }, [canSync, baseTimeline, timeline, persistTimeline]);
  // Cells in a derivative that diverge from the base it was created from — drives a faint underline on
  // manually-edited cells (a new task, or a changed Task Name / Duration / Dependency / Assigned To).
  // Returns rowId → Set<colKey>. Predecessors are compared by resolved keys (base & derivative number
  // their rows differently), so a pure re-number doesn't read as an edit.
  const baseDivergence = useMemo(() => {
    if (!isDerivativeTemplate || !baseTimeline) return null;
    const baseRows = Array.isArray(baseTimeline._rows) ? baseTimeline._rows : [];
    const derRows = Array.isArray(timeline._rows) ? timeline._rows : [];
    const baseIds = new Set(baseRows.map(r => r.id));
    const numToId = (rows) => { const m = {}; let n = 0; rows.forEach(r => { if ((r.indent || 0) > 0) m[++n] = r.id; }); return m; };
    const baseNumToId = numToId(baseRows);
    const derNumToId = numToId(derRows);
    const predSig = (arr) => (arr || []).map(p => `${p.id}:${p.type}:${p.lag}`).sort().join('|');
    const norm = (v) => (v === undefined || v === null) ? '' : (Array.isArray(v) ? v.join('') : String(v)).trim();
    const map = {};
    derRows.forEach(r => {
      const id = r.id;
      if (!isDeliverableRowId(id)) return; // only task / deliverable rows carry authored fields
      const dd = timeline[id] || {};
      const diverged = new Set();
      if (!baseIds.has(id)) {
        diverged.add('taskName'); // a manually-added task the base doesn't have
        if (norm(dd.plannedDuration)) diverged.add('plannedDuration');
        if (norm(dd.predecessors)) diverged.add('predecessors');
        if (norm(dd.assignedTo)) diverged.add('assignedTo');
      } else {
        const bd = baseTimeline[id] || {};
        if (norm(dd.taskName) !== norm(bd.taskName)) diverged.add('taskName');
        if (norm(dd.plannedDuration) !== norm(bd.plannedDuration)) diverged.add('plannedDuration');
        if (norm(dd.assignedTo) !== norm(bd.assignedTo)) diverged.add('assignedTo');
        if (predSig(resolvePredKeys(dd.predecessors, derNumToId)) !== predSig(resolvePredKeys(bd.predecessors, baseNumToId))) diverged.add('predecessors');
      }
      if (diverged.size) map[id] = diverged;
    });
    return map;
  }, [isDerivativeTemplate, baseTimeline, timeline]);

  // ── Undo / redo (session-scoped: the working day's edits) ────────────────
  // Every DATA change to the stage timeline — from any code path — is captured here by observing
  // project.stageTimeline, so Ctrl+Z / Ctrl+Y can walk back and forth through the day's edits.
  // Pure view-only churn (_viewSettings / _settingsPresets / _activePresetId) is ignored, and the
  // change a redo/undo itself makes is not re-captured. "Undo Reset" restores the pre-reset snapshot.
  // (Refs + undoState are declared higher up so the reset handlers can reach them.)
  const MAX_UNDO = 300;

  const dataSig = useCallback((tl) => {
    if (!tl || typeof tl !== 'object') return '';
    const { _viewSettings, _settingsPresets, _activePresetId, ...rest } = tl; // eslint-disable-line no-unused-vars
    try { return JSON.stringify(rest); } catch { return ''; }
  }, []);

  // Capture the prior state before each meaningful data change.
  useEffect(() => {
    const cur = project.stageTimeline;
    const prev = prevTimelineRef.current;
    prevTimelineRef.current = cur;
    if (cur === prev) return;
    if (isUndoRedoRef.current) { isUndoRedoRef.current = false; return; }
    // Derived auto-sync write (targetDate → Tracking): advance the baseline but don't make it its
    // own undo step, so Ctrl+Z lands on the user's actual edit, not the sync side effect.
    if (autoSyncWriteRef.current) { autoSyncWriteRef.current = false; return; }
    if (dataSig(prev) === dataSig(cur)) return; // view-only change — not undoable
    // A template apply is one undo step: its own write and every write it sets off in the next few
    // seconds (dependency cascade, invoicing / closure rows, start row presets) fold into the snapshot
    // taken before the apply, so a single Undo returns the table to exactly how it was.
    const grp = undoGroupRef?.current;
    if (grp && Date.now() < grp.until) {
      if (grp.captured) return;
      grp.captured = true;
    }
    undoStackRef.current.push(prev);
    if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
    redoStackRef.current = [];
    setCanUndo(true);
  }, [project.stageTimeline, dataSig, undoGroupRef]);

  // Scroll the first row that differs between two timelines into view + briefly flash it, so undo /
  // redo jumps to where the change happened.
  const scrollToFirstDiff = useCallback((oldTl, newTl) => {
    const ids = new Set([
      ...Object.keys(oldTl || {}), ...Object.keys(newTl || {}),
    ].filter(k => !k.startsWith('_')));
    let target = null;
    for (const id of ids) {
      if (JSON.stringify(oldTl?.[id]) !== JSON.stringify(newTl?.[id])) { target = id; break; }
    }
    // Fall back to a structural (_rows) change — jump to the first row whose position/shape changed.
    if (!target) {
      const oldRows = (oldTl?._rows || []).map(r => r.id).join(',');
      const newRows = (newTl?._rows || []).map(r => r.id).join(',');
      if (oldRows !== newRows) {
        const a = (oldTl?._rows || []).map(r => r.id);
        const b = (newTl?._rows || []).map(r => r.id);
        target = b.find((id, i) => a[i] !== id) || a.find((id, i) => b[i] !== id) || null;
      }
    }
    if (!target) return;
    setTimeout(() => {
      try {
        const sel = (window.CSS && CSS.escape) ? CSS.escape(target) : target;
        const el = tableRef.current?.querySelector(`[data-rowid="${sel}"]`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const cells = el.querySelectorAll('td');
        cells.forEach(c => { c.style.transition = 'background 0.2s'; c.style.background = 'rgba(159,144,42,0.20)'; });
        setTimeout(() => cells.forEach(c => { c.style.background = ''; }), 950);
      } catch { /* ignore */ }
    }, 130);
  }, []);

  const applyUndoRedo = useCallback((targetTimeline) => {
    if (!targetTimeline) return;
    isUndoRedoRef.current = true;
    scrollToFirstDiff(prevTimelineRef.current, targetTimeline);
    persistTimeline(targetTimeline);
  }, [persistTimeline, scrollToFirstDiff]);

  const doUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current.pop();
    redoStackRef.current.push(project.stageTimeline);
    setCanUndo(undoStackRef.current.length > 0);
    applyUndoRedo(prev);
  }, [project.stageTimeline, applyUndoRedo]);

  const doRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push(project.stageTimeline);
    setCanUndo(true);
    applyUndoRedo(next);
  }, [project.stageTimeline, applyUndoRedo]);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y (or Shift+Ctrl+Z) = redo — but never while editing a field, so
  // native input undo keeps working inside cells.
  useEffect(() => {
    if (!canEdit) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); doUndo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); doRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, doUndo, doRedo]);

  // Inline rename for a part stage — saves the updated name directly into timeline[stageId].partStages
  const handlePartNameSave = useCallback((stageId, partId, newName) => {
    if (!canEdit || !newName?.trim()) return;
    const parts = (timeline[stageId]?.partStages || []).map(p =>
      p.id === partId ? { ...p, name: newName.trim() } : p
    );
    persistTimeline({ ...timeline, [stageId]: { ...(timeline[stageId] || {}), partStages: parts } });
  }, [canEdit, timeline, persistTimeline]);

  // ── Contract dates auto-input ─────────────────────────────────────────
  const handleApplyContractDates = useCallback((setId) => {
    if (!contractDatesModal || !canEdit) return;
    const { selectedStages, startDate, sets } = contractDatesModal;
    const cfg = sets.find(s => s.id === setId);
    if (!cfg) return;
    // Ordered list of selected items — stages and their part stages (substage_* keys), in order.
    const orderedSelected = [];
    allRows.filter(r => r.indent === 0).forEach(sr => {
      if (selectedStages.has(sr.id)) orderedSelected.push(sr.id);
      (partStagesMap[sr.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach(p => {
        const sub = `substage_${sr.id}_${p.id}`;
        if (selectedStages.has(sub)) orderedSelected.push(sub);
      });
    });
    if (orderedSelected.length === 0 || !startDate) return;

    // Total calendar days split equally
    const totalCalDays = cfg.months * 30 + cfg.days;
    if (totalCalDays <= 0) return;
    const perStageCalDays = Math.round(totalCalDays / orderedSelected.length);

    const workDaySet = new Set(projectSettings.workingDays.days);
    const holidaySet = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
    const countWD = (fromStr, toStr) => {
      let d = parseDate(fromStr);
      const end = parseDate(toStr);
      if (!d || !end) return 1;
      let count = 0;
      while (d <= end) {
        if (workDaySet.has(d.getDay()) && !holidaySet.has(formatDate(d))) count++;
        d = new Date(d.getTime() + DAY_MS);
      }
      return Math.max(1, count);
    };

    const newTimeline = { ...timeline };
    let curStart = startDate;
    orderedSelected.forEach((stageId, i) => {
      const isLast = i === orderedSelected.length - 1;
      let stageCal = perStageCalDays;
      if (isLast) {
        const totalStartDate = new Date(startDate + 'T00:00:00');
        const totalEnd = new Date(totalStartDate.getTime() + totalCalDays * DAY_MS - DAY_MS);
        stageCal = Math.round((totalEnd - new Date(curStart + 'T00:00:00')) / DAY_MS) + 1;
      }
      const endDate = addDaysToDateStr(curStart, stageCal - 1);
      const dur = countWD(curStart, endDate);
      newTimeline[stageId] = {
        ...(newTimeline[stageId] || {}),
        contractStartDate: curStart,
        contractDuration: dur,
        contractEndDate: endDate,
      };
      curStart = addDaysToDateStr(curStart, stageCal);
    });
    persistTimeline(newTimeline);
    // Mark the set as applied and record which stages were assigned to it
    setContractDatesModal(prev => {
      const setIdx = prev.sets.findIndex(s => s.id === setId);
      const setLabel  = CONTRACT_SET_LABELS[setIdx]  || String(setIdx + 1);
      const setAccent = CONTRACT_SET_ACCENTS[setIdx % CONTRACT_SET_ACCENTS.length];
      const nextMap = { ...(prev.stageSetMap || {}) };
      orderedSelected.forEach(stageId => { nextMap[stageId] = { label: setLabel, accent: setAccent }; });
      return {
        ...prev,
        sets: prev.sets.map(s => s.id === setId ? { ...s, applied: true } : s),
        stageSetMap: nextMap,
      };
    });
  }, [contractDatesModal, canEdit, allRows, partStagesMap, projectSettings, timeline, persistTimeline]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearContractDates = useCallback(() => {
    if (!canEdit) return;
    const newTimeline = { ...timeline };
    // Clear strip fields (contractStartDate, contractDuration, contractEndDate) from every
    // stage row and sub-stage header row. contractDate (tracking Contract column) and all
    // other dates are left untouched.
    allRows.forEach(row => {
      if (row.indent === 0 || row.isSubStageHeader) {
        const entry = newTimeline[row.id] || newTimeline[row.key];
        const key = newTimeline[row.id] !== undefined ? row.id : row.key;
        if (!entry) return;
        const { contractStartDate, contractDuration, contractEndDate, ...rest } = entry; // eslint-disable-line no-unused-vars
        newTimeline[key] = rest;
      }
    });
    persistTimeline(newTimeline);
    // Reset the set assignment badges in the modal
    setContractDatesModal(prev => prev ? { ...prev, stageSetMap: {} } : prev);
  }, [canEdit, allRows, timeline, persistTimeline]);

  const [showResetTimelineConfirm, setShowResetTimelineConfirm] = useState(false);
  const handleResetTimeline = useCallback(async () => {
    const preserved = {};
    Object.entries(timeline).forEach(([key, val]) => {
      // Preserve all metadata keys (_viewSettings, _rows, _customColumns, etc.) as-is
      if (key.startsWith('_')) { preserved[key] = val; return; }
      if (!val || typeof val !== 'object') return;
      // Invoicing and closure rows belong to the Invoices page — keep entirely
      if (key.startsWith('invoicing_') || key.startsWith('closure_')) {
        preserved[key] = val;
        return;
      }
      // For stage and deliverable rows: remove the planned schedule date fields
      // (Start Date / Duration / End Date) and dependency (Predecessors) values.
      // All other fields are preserved: tracking dates, invoice/paid dates,
      // billing splits, contract strip fields, partStages, custom column values, etc.
      const { plannedStartDate, plannedDuration, plannedEndDate, predecessors, ...rest } = val; // eslint-disable-line no-unused-vars
      if (Object.keys(rest).length > 0) preserved[key] = rest;
    });
    await persistTimeline(preserved);
    setCollapsedRows(new Set(allRows.filter(r => r.indent === 0).map(r => r.id)));
    setShowResetTimelineConfirm(false);
  }, [timeline, persistTimeline, allRows, setCollapsedRows]);

  // ── Custom column handlers ─────────────────────────────────────────────
  const handleAddColumn = useCallback(async (label, type, afterColKey) => {
    if (!label.trim() || !canEdit) return;
    const key = 'custom_' + Date.now();
    const NEW_COL_PX = 120;
    // Lock all existing column widths so adding a new column doesn't redistribute them
    if (tableRef.current) {
      const lockWidths = {};
      Array.from(tableRef.current.querySelectorAll('thead th[data-col-key]')).forEach(el => {
        lockWidths[el.dataset.colKey] = el.offsetWidth + 'px';
      });
      lockWidths[key] = NEW_COL_PX + 'px';
      setColumnWidths(lockWidths);
      setTableMinWidthPx((tableRef.current.offsetWidth || 0) + NEW_COL_PX);
    }
    const newCol = { key, label: label.trim(), type, options: [] };
    const existing = timeline._customColumns || [];
    let newCustomCols;
    if (afterColKey) {
      // Find insertion point: after afterColKey in the full column list
      const allCols = [...SCHEDULE_COLUMNS, ...existing];
      const afterIdx = allCols.findIndex(c => c.key === afterColKey);
      // Convert to position within custom cols array
      const builtinCount = SCHEDULE_COLUMNS.length;
      const customInsertIdx = Math.max(0, afterIdx + 1 - builtinCount);
      newCustomCols = [...existing.slice(0, customInsertIdx), newCol, ...existing.slice(customInsertIdx)];
    } else {
      newCustomCols = [...existing, newCol];
    }
    // Also update columnOrder to insert at right position
    if (columnOrder.length > 0 && afterColKey) {
      const idx = columnOrder.indexOf(afterColKey);
      if (idx !== -1) {
        const newOrder = [...columnOrder.slice(0, idx + 1), key, ...columnOrder.slice(idx + 1)];
        setColumnOrder(newOrder);
      }
    }
    const newTimeline = { ...timeline, _customColumns: newCustomCols };
    await persistTimeline(newTimeline);
    setAddColumnDialog(null);
  }, [canEdit, timeline, columnOrder, persistTimeline]);

  const handleUpdateColumnOptions = useCallback(async (colKey, options) => {
    if (!canEdit) return;
    const customCols = (timeline._customColumns || []).map(c =>
      c.key === colKey ? { ...c, options } : c
    );
    const newTimeline = { ...timeline, _customColumns: customCols };
    await persistTimeline(newTimeline);
  }, [canEdit, timeline, persistTimeline]);

  const handleUpdateAssignedToCategories = useCallback(async (categories) => {
    if (!canEdit) return;
    const newTimeline = { ...timeline, _assignedToCategories: categories };
    await persistTimeline(newTimeline);
  }, [canEdit, timeline, persistTimeline]);

  const handleDeleteCustomColumn = useCallback(async (colKey) => {
    if (!canEdit) return;
    const customCols = (timeline._customColumns || []).filter(c => c.key !== colKey);
    const newTimeline = { ...timeline, _customColumns: customCols };
    await persistTimeline(newTimeline);
    setColumnOrder(prev => prev.filter(k => k !== colKey));
    setHeaderContextMenu(null);
  }, [canEdit, timeline, persistTimeline]);

  // ── Column clipboard operations ────────────────────────────────────────
  const handleCopyColumn = useCallback((colKey) => {
    const values = {};
    displayRows.forEach(row => {
      const rowData = timeline[row.key] || {};
      values[row.key] = rowData[colKey] ?? null;
    });
    columnClipboard.current = { colKey, values };
    setHeaderContextMenu(null);
  }, [displayRows, timeline]);

  const handlePasteColumn = useCallback(async (colKey) => {
    if (!canEdit || !columnClipboard.current) return;
    const { values } = columnClipboard.current;
    const newTimeline = { ...timeline };
    // Build work-day adder (same logic as handleFieldChange) for end-date computation
    const workDays = new Set(projectSettings.workingDays.days);
    const holidaySet = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
    const addWD = (dateStr, count) => {
      const d = parseDate(dateStr);
      if (!d || !count || count <= 0) return dateStr;
      let cur = new Date(d.getTime());
      let rem = Number(count);
      while (rem > 0) {
        cur = new Date(cur.getTime() + DAY_MS);
        const iso = formatDate(cur);
        if (workDays.has(cur.getDay()) && !holidaySet.has(iso)) rem--;
      }
      return formatDate(cur);
    };
    displayRows.forEach(row => {
      if (values[row.key] !== undefined) {
        newTimeline[row.key] = { ...(newTimeline[row.key] || {}), [colKey]: values[row.key] };
        const rd = newTimeline[row.key];
        // Re-derive end dates after paste so computed cells stay in sync
        if (colKey === 'plannedStartDate' || colKey === 'plannedDuration') {
          rd.plannedEndDate = (rd.plannedStartDate && rd.plannedDuration != null)
            ? addWD(rd.plannedStartDate, Number(rd.plannedDuration) - 1) : null;
        }
      }
    });
    await persistTimeline(newTimeline);
    setHeaderContextMenu(null);
  }, [canEdit, displayRows, timeline, projectSettings, persistTimeline]);

  const handleDuplicateColumn = useCallback(async (colKey) => {
    if (!canEdit) return;
    const srcCol = (timeline._customColumns || []).find(c => c.key === colKey);
    if (!srcCol) return;
    const newKey = 'custom_' + Date.now();
    const newCol = { ...srcCol, key: newKey, label: srcCol.label + ' (copy)' };
    const existing = timeline._customColumns || [];
    const srcIdx = existing.findIndex(c => c.key === colKey);
    const newCustomCols = [...existing.slice(0, srcIdx + 1), newCol, ...existing.slice(srcIdx + 1)];
    // Copy values
    const newTimeline = { ...timeline, _customColumns: newCustomCols };
    displayRows.forEach(row => {
      const rowData = timeline[row.key] || {};
      if (rowData[colKey] != null) {
        newTimeline[row.key] = { ...(newTimeline[row.key] || {}), [newKey]: rowData[colKey] };
      }
    });
    await persistTimeline(newTimeline);
    setHeaderContextMenu(null);
  }, [canEdit, displayRows, timeline, persistTimeline]);

  const handleClearColumn = useCallback(async (colKey) => {
    if (!canEdit) return;
    const newTimeline = { ...timeline };
    displayRows.forEach(row => {
      if (newTimeline[row.key] && newTimeline[row.key][colKey] != null) {
        newTimeline[row.key] = { ...newTimeline[row.key], [colKey]: null };
      }
    });
    await persistTimeline(newTimeline);
    setHeaderContextMenu(null);
  }, [canEdit, displayRows, timeline, persistTimeline]);

  const saveRows = useCallback(async (newRows) => {
    const rowData = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = { ...timeline, _rows: rowData };
    return persistTimeline(newTimeline);
  }, [timeline, persistTimeline]);

  // Fully clear a row's SOW link — drops the deliverable number, the label fingerprint, the
  // unlinked flag and the scope-source marker so the row renders as a plain (non-SOW) task. Used by
  // the badge's "Remove link" action (distinct from handleFieldChange's null-sowRef path, which keeps
  // the row flagged unlinked).
  const clearSowLink = useCallback((rowId) => {
    if (!canEdit) return;
    const nt = { ...timeline, [rowId]: { ...(timeline[rowId] || {}) } };
    delete nt[rowId].sowRef;
    delete nt[rowId].sowRefLabels;
    delete nt[rowId].sowSource;
    delete nt[rowId]._sowNew;
    // A deliv_/meeting_/revision_ row is scope-highlighted by its id alone, so removing the link must
    // also suppress that fill (_sowUnlinked) and detach it into a plain, deletable task (_detached).
    // A row_ workflow task carries no id-highlight, so it just drops back to a plain task.
    const idHighlights = !rowId.startsWith('row_') && !rowId.startsWith('start_')
      && !rowId.startsWith('invoicing_') && !rowId.startsWith('closure_');
    if (idHighlights) { nt[rowId]._sowUnlinked = true; nt[rowId]._detached = true; }
    else delete nt[rowId]._sowUnlinked;
    persistTimeline(nt);
  }, [canEdit, timeline, persistTimeline]);

  // Save a field value to stageTimeline
  const handleFieldChange = useCallback(async (rowId, field, value, extraFields = null) => {
    if (!supabaseProjects) return;
    if (!canEdit) {
      const r = allRows.find(x => x.id === rowId);
      if (!canEditCell(r || { id: rowId }, field)) return;
    }

    // Paid stages are frozen: never let the planning page overwrite a paid stage's tracking
    // dates (Planned / Actual / Contract). Those belong to the Tracking page, which manages
    // them (and its own unlock). Applies ONLY to stages with a recorded paid date (Paid status).
    if (['targetDate', 'actualDate', 'contractDate'].includes(field) && timeline[rowId]?.paidDate) {
      return;
    }

    const cellKey = `${rowId}:${field}`;
    setSaving(cellKey);

    const newTimeline = {
      ...timeline,
      [rowId]: {
        ...(timeline[rowId] || {}),
        [field]: value === '' ? null : value,
      },
    };
    // Extra fields written atomically with the primary field (e.g. the SOW link's label fingerprint
    // `sowRefLabels`, captured alongside `sowRef` so it lands in the same timeline write / undo step).
    if (extraFields && typeof extraFields === 'object') Object.assign(newTimeline[rowId], extraFields);

    // Ctrl+click multi-select: an edit to one selected cell fills every selected cell in that column.
    if (selectedCells.has(`${rowId}|${field}`) && !['taskName', 'sowRef', 'predecessors'].includes(field)) {
      selectedCells.forEach(cellKey => {
        const cut = cellKey.lastIndexOf('|');
        const otherId = cellKey.slice(0, cut);
        if (otherId === rowId || cellKey.slice(cut + 1) !== field) return;
        if (['targetDate', 'actualDate', 'contractDate'].includes(field) && timeline[otherId]?.paidDate) return;
        const od = { ...(newTimeline[otherId] || {}), [field]: value === '' ? null : value };
        if ((field === 'plannedDuration' || field === 'plannedStartDate') && od.plannedStartDate && od.plannedDuration != null) {
          od.plannedEndDate = addWorkDays(od.plannedStartDate, Number(od.plannedDuration) - 1);
        }
        newTimeline[otherId] = od;
      });
    }

    // Linking/unlinking a row to a SOW deliverable also toggles the scope-fill highlight: clearing
    // the link (sowRef null/empty) marks the row unlinked so it stops rendering as a SOW task;
    // re-linking clears the flag. The highlight (isScopeRow) reads _sowUnlinked.
    if (field === 'sowRef') {
      const unlinking = (value === '' || value == null);
      newTimeline[rowId]._sowUnlinked = unlinking;
      // Once the user sets, changes or removes the link, the "new" review state is resolved.
      delete newTimeline[rowId]._sowNew;
      // Removing a link detaches a deliverable/meeting row (highlighted by its id) into a plain,
      // deletable task; re-linking (or a stage link) re-attaches it.
      const idHighlights = !rowId.startsWith('row_') && !rowId.startsWith('start_')
        && !rowId.startsWith('invoicing_') && !rowId.startsWith('closure_');
      if (unlinking && idHighlights) newTimeline[rowId]._detached = true;
      else delete newTimeline[rowId]._detached;
      // The label fingerprint travels only with a live numeric link; drop it when the row is unlinked
      // or linked to the stage's info text (stage links are never reconciled).
      if (value === '' || value == null || String(value) === SOW_STAGE_REF) {
        delete newTimeline[rowId].sowRefLabels;
      }
    }

    // Editing a start_ row's predecessor:
    //  • empty value = a manual clear that must stick (auto-preset effect skips _presetCleared rows).
    //  • non-empty value = a MANUAL OVERRIDE: the user has picked their own dependency type / lag, so
    //    the auto-preset effect must stop overwriting this row (it flags _manualPredOverride and lets the
    //    normal cascade drive the date from the chosen dependency). "Reset to preset link" clears both.
    if (field === 'predecessors' && rowId.startsWith('start_')) {
      const cleared = (value === '' || value == null);
      newTimeline[rowId]._presetCleared = cleared;
      newTimeline[rowId]._manualPredOverride = !cleared;
      startRowPredInitRef.current = null;
    }

    // Auto-compute end dates: end = start + duration (using working days from settings)
    // When duration changes: recompute end from start + new duration
    // When start changes: recompute end from new start + existing duration

    const rowData = newTimeline[rowId];
    if (field === 'plannedDuration' || field === 'plannedStartDate') {
      if (rowData.plannedStartDate && rowData.plannedDuration != null) {
        rowData.plannedEndDate = addWorkDays(rowData.plannedStartDate, Number(rowData.plannedDuration) - 1);
      } else if (!rowData.plannedStartDate && rowData.plannedDuration == null) {
        // Both start and duration are cleared — clear the end date too
        rowData.plannedEndDate = null;
      }
      // One of the two is cleared but not both: leave plannedEndDate as-is (manual value preserved)
    }
    // Custom columns derived from this one (e.g. TG's Target End Date from Target Start + Duration).
    const derivedWorkDays = new Set(projectSettings.workingDays.days);
    applyDerivedColumns(rowData, field, timeline._customColumns, derivedWorkDays);
    applyStatusDone(rowData, field);
    selectedCells.forEach(cellKey => {
      const cut = cellKey.lastIndexOf('|');
      const otherId = cellKey.slice(0, cut);
      if (otherId !== rowId && cellKey.slice(cut + 1) === field && newTimeline[otherId]) {
        applyDerivedColumns(newTimeline[otherId], field, timeline._customColumns, derivedWorkDays);
        applyStatusDone(newTimeline[otherId], field);
      }
    });

    // Contract strip fields — kept separate from planned schedule columns.
    // contractEndDate is the strip-only end date; contractDate remains the tracking Contract column.
    if (field === 'contractDuration' || field === 'contractStartDate') {
      if (rowData.contractStartDate && rowData.contractDuration != null) {
        rowData.contractEndDate = addWorkDays(rowData.contractStartDate, Number(rowData.contractDuration) - 1);
      } else if (!rowData.contractStartDate && rowData.contractDuration == null) {
        rowData.contractEndDate = null;
      }
    }
    if (field === 'contractEndDate') {
      if (rowData.contractStartDate && rowData.contractEndDate) {
        const wd = new Set(projectSettings.workingDays.days);
        const hol = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
        const dur = countWorkingDays(parseDate(rowData.contractStartDate), parseDate(rowData.contractEndDate), wd, hol);
        if (dur != null) rowData.contractDuration = dur;
      }
    }
    if (field === 'contractStartDate' && rowData.contractEndDate && rowData.contractDuration == null) {
      const wd = new Set(projectSettings.workingDays.days);
      const hol = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
      const dur = countWorkingDays(parseDate(rowData.contractStartDate), parseDate(rowData.contractEndDate), wd, hol);
      if (dur != null) rowData.contractDuration = dur;
    }

    // Auto-populate invoicing row when invoiceDate is set on a stage row
    const editedRowForSync = allRows.find(r => r.id === rowId);
    if (field === 'invoiceDate' && editedRowForSync && editedRowForSync.indent === 0) {
      const invoicingKey = `invoicing_${rowId}`;
      const dur = paymentTermsDays || newTimeline[invoicingKey]?.plannedDuration || null;
      newTimeline[invoicingKey] = {
        ...(newTimeline[invoicingKey] || {}),
        plannedStartDate: value || null,
        plannedDuration: dur,
        plannedEndDate: (value && dur) ? addCalendarDays(value, Number(dur)) : (newTimeline[invoicingKey]?.plannedEndDate || null),
      };
    }

    // Auto-populate closure row when paidDate is set on a stage row
    if (field === 'paidDate' && editedRowForSync && editedRowForSync.indent === 0) {
      const closureKey = `closure_${rowId}`;
      newTimeline[closureKey] = {
        ...(newTimeline[closureKey] || {}),
        plannedStartDate: value || null,
        plannedEndDate: value || null,
      };
    }

    // Apply dependency calculations if enabled
    const finalTimeline = projectSettings.dependencies.enabled
      ? applyDependencies(extendedRows, newTimeline, projectSettings)
      : newTimeline;
    // Preserve metadata keys
    finalTimeline._rows = newTimeline._rows;
    finalTimeline._settings = newTimeline._settings;

    // Auto-sync: when a child task's planned dates change, update the parent stage's
    // targetDate (and the relevant sub-stage partStage targetDate) to match the new
    // aggregated planned end date so the tracking view stays in sync.
    // Gated by the Sync toggle — when auto-sync is off, no date is pushed to the Tracking page.
    if (syncEnabledRef.current && !syncPausedRef.current && ['plannedEndDate', 'plannedStartDate', 'plannedDuration'].includes(field)) {
      // Walk allRows to find the parent stage and all its child IDs
      let parentStageId = null;
      let stageChildIds = [];
      let foundInStage = false;
      let curStageId = null;
      let curChildIds = [];
      for (const r of allRows) {
        if (r.indent === 0) {
          if (foundInStage) break;
          curStageId = r.id;
          curChildIds = [];
        } else {
          if (r.id === rowId) foundInStage = true;
          curChildIds.push(r.id);
        }
      }
      if (foundInStage) { parentStageId = curStageId; stageChildIds = curChildIds; }

      if (parentStageId) {
        // Compute new max plannedEndDate across all children of this stage
        let maxStageEnd = null;
        for (const cid of stageChildIds) {
          const d = finalTimeline[cid] || {};
          const pe = parseDate(d.plannedEndDate);
          if (pe && (!maxStageEnd || pe > maxStageEnd)) maxStageEnd = pe;
        }
        // Same end the stage row shows (closure end, else Actual once approved, else latest row end).
        const parentEnd = resolveStageEnd(maxStageEnd, isAdvanceStage(parentStageId) ? null : finalTimeline[`closure_${parentStageId}`], finalTimeline[parentStageId]);
        // Frozen parent (paid, sync-locked or approved) → never push a synced Planned date onto it. An
        // unlocked parent syncs even with part-stages (the lock decides).
        if (parentEnd && !finalTimeline[parentStageId]?.paidDate && !finalTimeline[parentStageId]?._syncLocked && !isApprovalLocked(finalTimeline[parentStageId])) {
          finalTimeline[parentStageId] = {
            ...(finalTimeline[parentStageId] || {}),
            targetDate: formatDate(parentEnd),
          };
        }

        // If the changed row belongs to a sub-stage, update that partStage's targetDate too
        const subStageId = finalTimeline[rowId]?._subStageId || null;
        if (subStageId) {
          let maxSubEnd = null;
          for (const cid of stageChildIds) {
            const d = finalTimeline[cid] || {};
            if ((d._subStageId || null) !== subStageId) continue;
            const pe = parseDate(d.plannedEndDate);
            if (pe && (!maxSubEnd || pe > maxSubEnd)) maxSubEnd = pe;
          }
          if (maxSubEnd) {
            const newSubDateStr = formatDate(maxSubEnd);
            const currentParts = finalTimeline[parentStageId]?.partStages || [];
            finalTimeline[parentStageId] = {
              ...finalTimeline[parentStageId],
              partStages: currentParts.map(p =>
                (p.id === subStageId && !p.paidDate && !isApprovalLocked(p)) ? { ...p, targetDate: newSubDateStr } : p
              ),
            };
          }
        }
      }
    }

    // Auto-sync for stage rows: when a stage row's own plannedEndDate is computed
    // (from plannedStartDate + plannedDuration), keep targetDate in step so the
    // Tracking page (TimelinePage) always reflects the latest planned end date.
    if (syncEnabledRef.current && !syncPausedRef.current && ['plannedEndDate', 'plannedStartDate', 'plannedDuration'].includes(field)) {
      const editedRow = allRows.find(r => r.id === rowId);
      if (editedRow && editedRow.indent === 0) {
        const newEndDate = finalTimeline[rowId]?.plannedEndDate;
        if (newEndDate && !finalTimeline[rowId]?.paidDate && !finalTimeline[rowId]?._syncLocked && !isApprovalLocked(finalTimeline[rowId])) {
          finalTimeline[rowId] = {
            ...(finalTimeline[rowId] || {}),
            targetDate: newEndDate,
          };
        }
      }
    }

    // Auto-sync for contract strip end date: when contractEndDate (or the fields that
    // compute it) changes, immediately sync targetDate in the tracking view so the
    // Planned column stays current without requiring a manual right-click.
    if (syncEnabledRef.current && !syncPausedRef.current && ['contractEndDate', 'contractStartDate', 'contractDuration'].includes(field)) {
      const editedRow = allRows.find(r => r.id === rowId);
      if (editedRow && editedRow.indent === 0) {
        const newEnd = finalTimeline[rowId]?.contractEndDate;
        if (newEnd && !finalTimeline[rowId]?.paidDate && !finalTimeline[rowId]?._syncLocked && !isApprovalLocked(finalTimeline[rowId])) {
          finalTimeline[rowId] = {
            ...(finalTimeline[rowId] || {}),
            targetDate: newEnd,
          };
        }
      } else if (editedRow && editedRow.isSubStageHeader && editedRow.stageId && editedRow.partId) {
        const newEnd = finalTimeline[rowId]?.contractEndDate;
        if (newEnd) {
          const currentParts = finalTimeline[editedRow.stageId]?.partStages || [];
          finalTimeline[editedRow.stageId] = {
            ...(finalTimeline[editedRow.stageId] || {}),
            partStages: currentParts.map(p =>
              (p.id === editedRow.partId && !p.paidDate && !isApprovalLocked(p)) ? { ...p, targetDate: newEnd } : p
            ),
          };
        }
      }
    }

    const result = await writeTimelineDiff(supabaseProjects, project.id, timeline, finalTimeline, { allowTracking: syncEnabledRef.current });
    setSaving(null);

    if (result.success) {
      setSavedCells(prev => { const next = new Set(prev); next.add(cellKey); return next; });
      setTimeout(() => {
        setSavedCells(prev => { const next = new Set(prev); next.delete(cellKey); return next; });
      }, 2000);
    } else {
      alert('Error saving: ' + (result.error || 'Unknown error'));
    }
  }, [canEdit, canEditCell, supabaseProjects, project.id, timeline, projectSettings, allRows, extendedRows, paymentTermsDays, addWorkDays, addCalendarDays, selectedCells]);

  // ── Instant save (debounced auto-save while typing) ───────────────────
  // Free-text cells save on blur, but a page reload can happen before blur (network drop, crash,
  // accidental refresh). These helpers debounce a save while the user is still typing so the latest
  // input is persisted without leaving the field. handleFieldChange's identity changes on every save
  // (it closes over `timeline`), so the timer calls it through a ref to always write the freshest
  // state; the closure captured at schedule time would otherwise clobber concurrent edits.
  const handleFieldChangeRef = useRef(handleFieldChange);
  useEffect(() => { handleFieldChangeRef.current = handleFieldChange; }, [handleFieldChange]);
  const instantSaveTimerRef = useRef(null);
  const scheduleInstantSave = useCallback((rowId, field, value) => {
    if (!canEditTasks) return; // handleFieldChange applies the per-cell rule
    if (instantSaveTimerRef.current) clearTimeout(instantSaveTimerRef.current);
    instantSaveTimerRef.current = setTimeout(() => {
      instantSaveTimerRef.current = null;
      const fn = handleFieldChangeRef.current;
      if (fn) fn(rowId, field, value);
    }, INSTANT_SAVE_DEBOUNCE_MS);
  }, [canEditTasks]);
  // Called by blur (which saves immediately) so a pending debounce doesn't fire a duplicate write.
  const cancelInstantSave = useCallback(() => {
    if (instantSaveTimerRef.current) { clearTimeout(instantSaveTimerRef.current); instantSaveTimerRef.current = null; }
  }, []);
  // Drop any pending save when the editor unmounts (the boundary is keyed per project/template, so a
  // project or template switch remounts and runs this cleanup), so a queued write can't fire after the
  // instance is gone. Blur already flushes the current value when the user leaves a field.
  useEffect(() => () => { if (instantSaveTimerRef.current) clearTimeout(instantSaveTimerRef.current); }, []);

  // ── Sync invoicing/closure row from tracking dates ────────────────────
  const handleSyncFromTracking = useCallback(async (rowId) => {
    if (!canSync || !supabaseProjects) return;
    const isInvoicing = rowId.startsWith('invoicing_');
    const isClosure = rowId.startsWith('closure_');
    if (!isInvoicing && !isClosure) return;
    const stageKey = isInvoicing ? rowId.slice('invoicing_'.length) : rowId.slice('closure_'.length);
    const stageData = resolveStageRecord(stageKey);
    const existing = timeline[rowId] || {};
    const newTimeline = { ...timeline };
    if (isInvoicing) {
      const invoiceDate = stageData.invoiceDate || null;
      const dur = existing.plannedDuration || paymentTermsDays || null;
      const endDate = (invoiceDate && dur) ? addCalendarDays(invoiceDate, Number(dur)) : null;
      newTimeline[rowId] = { ...existing, plannedStartDate: invoiceDate, plannedEndDate: endDate };
    } else {
      const paidDate = stageData.paidDate || null;
      newTimeline[rowId] = { ...existing, plannedStartDate: paidDate, plannedEndDate: paidDate };
    }
    newTimeline._rows = timeline._rows;
    newTimeline._settingsPresets = timeline._settingsPresets;
    newTimeline._activePresetId = timeline._activePresetId;
    await writeTimelineDiff(supabaseProjects, project.id, timeline, newTimeline, { allowTracking: syncEnabledRef.current });
  }, [canSync, supabaseProjects, project.id, timeline, paymentTermsDays, addCalendarDays, resolveStageRecord]);

  // ── Push subsequent stage timelines by N working days (triggered from Delayed button) ──
  const handlePushTimelines = useCallback(async (closureRowId, workingDays) => {
    if (!canEdit || !supabaseProjects || !workingDays || workingDays === 0) return;
    const sk = closureRowId.slice('closure_'.length);
    const stageIdx = allRows.findIndex(r => r.id === sk);
    if (stageIdx === -1) return;

    // Collect rows to push — only consecutive stages and their children.
    // The current stage's invoicing/closure dates stay fixed.
    // The start_ row's SS dependency on the previous invoicing row will cascade
    // the shift to subsequent stages via applyDependencies.
    // Sub-stage headers are excluded (no own timeline data).
    const subsequentRows = [];
    let pastCurrentStage = false;
    for (let i = stageIdx + 1; i < allRows.length; i++) {
      const r = allRows[i];
      if (r.indent === 0) {
        pastCurrentStage = true;
        subsequentRows.push(r); // push stage row so its strip start date shifts
      } else if (pastCurrentStage && !r.isSubStageHeader) {
        subsequentRows.push(r);
      }
    }

    const predCol = projectSettings.dependencies.predecessorColumn;

    const newTimeline = { ...timeline };

    for (const r of subsequentRows) {
      const rd = newTimeline[r.id];
      if (!rd) continue;
      const updated = { ...rd };

      // Shift planned dates forward by N working days
      if (updated.plannedStartDate) updated.plannedStartDate = addWorkDays(updated.plannedStartDate, workingDays);
      if (updated.plannedEndDate)   updated.plannedEndDate   = addWorkDays(updated.plannedEndDate,   workingDays);

      newTimeline[r.id] = updated;
    }

    const finalTimeline = projectSettings.dependencies.enabled
      ? applyDependencies(buildExtendedRows(allRows, partStagesMap, timeline), newTimeline, projectSettings)
      : newTimeline;
    finalTimeline._rows              = timeline._rows;
    finalTimeline._settings          = timeline._settings;
    finalTimeline._settingsPresets   = timeline._settingsPresets;
    finalTimeline._activePresetId    = timeline._activePresetId;

    await writeTimelineDiff(supabaseProjects, project.id, timeline, finalTimeline, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, project.id, timeline, allRows, partStagesMap, projectSettings, addWorkDays]);

  // ── Row operations ────────────────────────────────────────────────────
  const handleInsertRow = useCallback(async (rowIndex, position, count = 1) => {
    if (!canEditTasks) return;
    const n = Math.max(1, Math.min(50, parseInt(count, 10) || 1));
    const current = displayRows[rowIndex];
    const baseIndent = current ? current.indent : 0;
    const indent = baseIndent > 0 ? baseIndent : 1;
    // Build N fresh, empty rows (unique ids). name:''/defaultLabel:null → the task-name cell renders
    // blank, not the row id (see the row-name fallbacks, which now blank `row_` ids).
    const usedIds = new Set(allRows.map(r => r.id));
    const newRowsToInsert = [];
    for (let k = 0; k < n; k++) {
      let id = generateRowId();
      while (usedIds.has(id)) id = generateRowId();
      usedIds.add(id);
      newRowsToInsert.push({ id, key: id, indent, name: '', defaultLabel: null });
    }
    // Virtual sub-stage header rows (isSubStageHeader) are not in allRows.
    // For those, resolve the real anchor row: the stage itself (for 'above') or
    // the last child of that stage before the first closure/invoicing row (for 'below').
    let allRowIndex = allRows.findIndex(r => r.id === current.id);
    if (allRowIndex === -1 && current.isSubStageHeader) {
      const stageIdx = allRows.findIndex(r => r.id === current.stageId);
      if (stageIdx !== -1) {
        if (position === 'above') {
          allRowIndex = stageIdx + 1; // first child slot
        } else {
          // Find the last non-system child of this stage in allRows
          let last = stageIdx;
          for (let k = stageIdx + 1; k < allRows.length && allRows[k].indent > 0; k++) {
            const id = allRows[k].id;
            if (!id.startsWith('closure_') && !id.startsWith('invoicing_')) last = k;
          }
          allRowIndex = last;
        }
      }
    }
    // A part's Start / Invoicing / Closure rows are built for display and are not in allRows either.
    // Inserting from one adds tasks inside that part: first in the part from its Start row, last from
    // its Invoicing or Closure row. The new rows are filed under the part through _subStageId.
    let insertAtOverride = null;
    let partAssign = null;
    if (allRowIndex === -1 && current.isPartSystem) {
      const subKey = current.id.replace(/^(start_|invoicing_|closure_)/, '');
      let stageId = null;
      Object.entries(partStagesMap).forEach(([sid, parts]) => (parts || []).forEach(p => {
        if (`substage_${sid}_${p.id}` === subKey) { stageId = sid; partAssign = p.id; }
      }));
      const stageIdx = stageId ? allRows.findIndex(r => r.id === stageId) : -1;
      if (stageIdx !== -1) {
        let end = stageIdx + 1;
        while (end < allRows.length && allRows[end].indent > 0) end++;
        const inPart = [];
        let lastTask = stageIdx;
        for (let k = stageIdx + 1; k < end; k++) {
          const id = allRows[k].id;
          if (timeline[id]?._subStageId === partAssign) inPart.push(k);
          if (!id.startsWith('closure_') && !id.startsWith('invoicing_')) lastTask = k;
        }
        const first = current.id.startsWith('start_');
        insertAtOverride = inPart.length ? (first ? inPart[0] : inPart[inPart.length - 1] + 1) : lastTask + 1;
        allRowIndex = insertAtOverride;
      }
    }
    if (allRowIndex === -1) return; // safety: still unresolved, skip
    // Inserting below a collapsed parent task: land after its whole subtree at the parent's own
    // indent, so the new row neither becomes a subtask nor steals the subtasks (which would also
    // expand the group). Indenting it under the parent is left to the user.
    if (position === 'below' && current.indent > 0 && collapsedRows.has(current.id)) {
      while (allRowIndex + 1 < allRows.length && allRows[allRowIndex + 1].indent > current.indent) allRowIndex++;
    }
    const insertAt = insertAtOverride != null ? insertAtOverride : (position === 'above' ? allRowIndex : allRowIndex + 1);
    const newRows = [...allRows];
    newRows.splice(insertAt, 0, ...newRowsToInsert);

    // Remap predecessor values (task numbers shift)
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    // Rows added inside a part are numbered where the part shows them, so file them first.
    const baseTl = { ...timeline };
    if (partAssign) newRowsToInsert.forEach(r => { baseTl[r.id] = { _subStageId: partAssign }; });
    const newExtRows = buildExtendedRows(newRows, partStagesMap, baseTl);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    const rowData = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = remapPredecessorValues({ ...baseTl, _rows: rowData }, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;

    // Optimistic: update rows + pred values immediately, no DB round-trip wait
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    const predSnapshot = {};
    newRows.forEach(r => { if (newTimeline[r.id]?.predecessors != null) predSnapshot[r.id] = newTimeline[r.id].predecessors; });
    setOptimisticPreds(predSnapshot);

    await persistTimeline(newTimeline);
  }, [canEditTasks, displayRows, allRows, collapsedRows, partStagesMap, timeline, persistTimeline]);

  // Collect a row's whole subtree (the row + all deeper-indent rows immediately below it in allRows,
  // whether currently collapsed/hidden or not) so indent/outdent move a parent and its subtasks as one.
  const rowSubtreeIds = useCallback((targetId) => {
    const ids = new Set([targetId]);
    const ai = allRows.findIndex(r => r.id === targetId);
    if (ai === -1) return ids;
    const baseIndent = allRows[ai].indent;
    for (let i = ai + 1; i < allRows.length && allRows[i].indent > baseIndent; i++) ids.add(allRows[i].id);
    return ids;
  }, [allRows]);

  const handleIndent = useCallback(async (rowIndex) => {
    if (!canEditTasks || rowIndex === 0) return;
    const above = displayRows[rowIndex - 1];
    // Can indent up to one level deeper than the row above
    if (displayRows[rowIndex].indent > above.indent) return;
    // Indent the target AND its whole subtree together (subtasks keep their relative nesting).
    const group = rowSubtreeIds(displayRows[rowIndex].id);
    if (![...group].every(ownsRow)) return;
    const newRows = allRows.map(r => group.has(r.id) ? { ...r, indent: r.indent + 1 } : r);
    await saveRows(newRows);
  }, [canEditTasks, ownsRow, displayRows, allRows, saveRows, rowSubtreeIds]);

  const handleOutdent = useCallback(async (rowIndex) => {
    if (!canEditTasks) return;
    const row = displayRows[rowIndex];
    // Never let a task outdent to indent-0 (stage level): a top-level task (indent 1) stays a task.
    // Mirrors the multi-row outdent guard (`r.indent > 1`) so no outdent path turns a task into a stage.
    if (row.indent <= 1) return;
    // Outdent the target AND its whole subtree together.
    const group = rowSubtreeIds(row.id);
    if (![...group].every(ownsRow)) return;
    const newRows = allRows.map(r => group.has(r.id) ? { ...r, indent: r.indent - 1 } : r);
    await saveRows(newRows);
  }, [canEditTasks, ownsRow, displayRows, allRows, saveRows, rowSubtreeIds]);

  const handleDeleteRow = useCallback(async (rowIndex) => {
    if (!canEditTasks) return;
    const row = displayRows[rowIndex];
    if (row.indent === 0) return;
    if (!ownsRow(row.id)) return;
    const targetId = row.id;
    const newRows = allRows.filter(r => r.id !== targetId);

    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(newRows, partStagesMap, timeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    const base = { ...timeline };
    delete base[targetId];
    base._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = remapPredecessorValues(base, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;

    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    const predSnapshot = {};
    newRows.forEach(r => { if (newTimeline[r.id]?.predecessors != null) predSnapshot[r.id] = newTimeline[r.id].predecessors; });
    setOptimisticPreds(predSnapshot);

    await persistTimeline(newTimeline);
  }, [canEditTasks, ownsRow, displayRows, allRows, partStagesMap, timeline, persistTimeline]);

  const handleDeleteCustomStage = useCallback(async (stageRowId) => {
    if (!canEdit || !isCustomStage(stageRowId)) return;
    const stageIdx = allRows.findIndex(r => r.id === stageRowId);
    if (stageIdx < 0) return;
    const idsToRemove = new Set([stageRowId]);
    for (let i = stageIdx + 1; i < allRows.length; i++) {
      if (allRows[i].indent === 0) break;
      idsToRemove.add(allRows[i].id);
    }
    const newRows = allRows.filter(r => !idsToRemove.has(r.id));
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(newRows, partStagesMap, timeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    const base = { ...timeline };
    idsToRemove.forEach(id => delete base[id]);
    base._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = remapPredecessorValues(base, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    await persistTimeline(newTimeline);
  }, [canEdit, allRows, partStagesMap, timeline, persistTimeline]);

  const handleAddNewStage = useCallback(async (rowIndex) => {
    if (!canEdit) return;
    const row = displayRows[rowIndex];
    // Find the stage row that owns this row
    let stageId = null;
    if (row && row.indent === 0 && !row.isSubStageHeader) {
      stageId = row.id;
    } else if (row && row.isSubStageHeader) {
      stageId = row.stageId || row.id;
    } else if (row) {
      for (let pi = rowIndex - 1; pi >= 0; pi--) {
        if (displayRows[pi].indent === 0 && !displayRows[pi].isSubStageHeader) { stageId = displayRows[pi].id; break; }
      }
    }
    // Find end of stage group in allRows (after all children)
    const stageAllIdx = stageId ? allRows.findIndex(r => r.id === stageId) : -1;
    let insertIdx = stageAllIdx >= 0 ? stageAllIdx + 1 : allRows.length;
    while (insertIdx < allRows.length && allRows[insertIdx].indent > 0) insertIdx++;
    const newStageId = generateCustomStageId();
    const newRow = { id: newStageId, indent: 0, defaultLabel: isStandalone ? 'New stage' : 'New Stage' };
    const newRows = [...allRows];
    newRows.splice(insertIdx, 0, newRow);
    const rowData = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const next = { ...timeline, _rows: rowData };
    // BRM: a stage added in a tab belongs to that tab (the first tab owns every stage no tab lists).
    if (isStandalone && activeTabId !== '__all__' && Array.isArray(timeline._tabs)) {
      next._tabs = timeline._tabs.map(t => t.id === activeTabId ? { ...t, keys: [...(t.keys || []), newStageId] } : t);
    }
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    await persistTimeline(next);
  }, [canEdit, displayRows, allRows, timeline, persistTimeline, isStandalone, activeTabId]);

  const handleAddPresetRows = useCallback(async (stageId, selected) => {
    if (!canEdit) return;
    const stageAllIdx = allRows.findIndex(r => r.id === stageId);
    if (stageAllIdx < 0) return;
    // Find end of stage group
    let groupEnd = stageAllIdx + 1;
    while (groupEnd < allRows.length && allRows[groupEnd].indent > 0) groupEnd++;
    const existingIds = new Set(allRows.map(r => r.id));
    const toAdd = [];
    if (selected.start && !existingIds.has(`start_${stageId}`)) {
      toAdd.push({ id: `start_${stageId}`, indent: 1, defaultLabel: 'Start of Stage' });
    }
    if (selected.invoicing && !existingIds.has(`invoicing_${stageId}`)) {
      toAdd.push({ id: `invoicing_${stageId}`, indent: 1, defaultLabel: 'Invoicing of Stage' });
    }
    if (selected.closure && !existingIds.has(`closure_${stageId}`)) {
      toAdd.push({ id: `closure_${stageId}`, indent: 1, defaultLabel: 'Closure of Stage' });
    }
    if (!toAdd.length) return;
    const newRows = [...allRows];
    // Insert start rows at beginning of group, invoicing/closure at end
    let startInsert = stageAllIdx + 1;
    let endInsert = groupEnd;
    for (const r of toAdd) {
      if (r.id.startsWith('start_')) {
        newRows.splice(startInsert, 0, r);
        startInsert++;
        endInsert++;
      } else {
        newRows.splice(endInsert, 0, r);
        endInsert++;
      }
    }
    const rowData = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    await persistTimeline({ ...timeline, _rows: rowData });
  }, [canEdit, allRows, timeline, persistTimeline]);

  // ── Drag-and-drop handler ─────────────────────────────────────────────
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = displayRows.findIndex(r => r.id === active.id);
    const newIndex = displayRows.findIndex(r => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const draggedRow = displayRows[oldIndex];
    // Only allow dragging non-stage, non-closure, non-invoicing, non-start rows
    if (draggedRow.indent === 0 || draggedRow.id.startsWith('closure_') || draggedRow.id.startsWith('invoicing_') || draggedRow.id.startsWith('start_')) return;
    // A granted editor moves only rows they added, together with a subtree that is all theirs.
    if (!canEdit && (draggedRow.isSubStageHeader || ![...rowSubtreeIds(draggedRow.id)].every(ownsRow))) return;

    // Sub-stage header drag: reorder partStages in timeline
    if (draggedRow.isSubStageHeader) {
      const stageId = draggedRow.stageId;
      // Find the nearest OTHER sub-stage header in the same stage near the drop position.
      // Scanning both backward and forward from the drop position handles all drop targets
      // (another header, a task row, or an unassigned row).
      let targetPartId = null;
      for (let i = newIndex; i >= 0; i--) {
        const r = displayRows[i];
        if (r.indent === 0) break;
        if (r.isSubStageHeader && r.stageId === stageId && r.partId !== draggedRow.partId) { targetPartId = r.partId; break; }
      }
      if (!targetPartId) {
        for (let i = newIndex + 1; i < displayRows.length; i++) {
          const r = displayRows[i];
          if (r.indent === 0) break;
          if (r.isSubStageHeader && r.stageId === stageId && r.partId !== draggedRow.partId) { targetPartId = r.partId; break; }
        }
      }
      if (!targetPartId) return;
      const parts = [...(timeline[stageId]?.partStages || [])];
      const fromIdx = parts.findIndex(p => p.id === draggedRow.partId);
      const toIdx = parts.findIndex(p => p.id === targetPartId);
      if (fromIdx === -1 || toIdx === -1) return;
      const [moved] = parts.splice(fromIdx, 1);
      parts.splice(toIdx, 0, moved);
      await persistTimeline({ ...timeline, [stageId]: { ...(timeline[stageId] || {}), partStages: parts } });
      return;
    }

    // Check both source and target are within the same stage group — EXCEPT in the Template Builder,
    // where authors may drag tasks/deliverables across stages to restructure the template. The block
    // move + ensureRowOrder + predecessor remap below already handle a cross-group destination.
    const srcGroup = getStageGroupBounds(displayRows, oldIndex);
    const dstGroup = getStageGroupBounds(displayRows, newIndex);
    if (!isTemplateBuilder && srcGroup.stageStart !== dstGroup.stageStart) return;

    const overRow = displayRows[newIndex];

    // Determine the new sub-stage assignment + whether this is a virtual drop target:
    //  • sub-stage header or one of its part rows → assign to that part
    //  • a regular task row → inherit that row's assignment
    //  • a main stage row → not a valid drop target
    let newSubStageId;
    const overIsVirtual = !!(overRow.isSubStageHeader || overRow.isPartSystem);
    if (overIsVirtual) {
      newSubStageId = overRow.partId ?? null;
    } else if (overRow.indent === 0 || overRow.id.startsWith('closure_') || overRow.id.startsWith('invoicing_') || overRow.id.startsWith('start_')) {
      return;
    } else {
      newSubStageId = timeline[overRow.id]?._subStageId ?? null;
    }

    // Reorder in allRows — move dragged row (or all selected rows) plus indented children as a block
    const allOld = allRows.findIndex(r => r.id === active.id);
    const allNew = allRows.findIndex(r => r.id === over.id); // -1 when the target is a virtual row
    if (allOld === -1) return;

    let newRows = [...allRows];
    let block;

    if (selectedRows.has(active.id) && selectedRows.size > 1) {
      // Multi-select drag: collect all selected rows (and their children) in order, remove, reinsert
      const selectedIds = new Set(selectedRows);
      if (!canEdit && ![...selectedIds].every(id => [...rowSubtreeIds(id)].every(ownsRow))) return;
      // Build ordered list of (startIdx, block) for each selected root row
      const multiBlocks = [];
      for (let i = 0; i < newRows.length; i++) {
        if (selectedIds.has(newRows[i].id)) {
          const bIndent = newRows[i].indent;
          let bSize = 1;
          while (i + bSize < newRows.length && newRows[i + bSize].indent > bIndent) bSize++;
          multiBlocks.push({ startIdx: i, rows: newRows.slice(i, i + bSize) });
          i += bSize - 1;
        }
      }
      // Remove all selected blocks from bottom to top (preserves indices)
      for (let b = multiBlocks.length - 1; b >= 0; b--) {
        newRows.splice(multiBlocks[b].startIdx, multiBlocks[b].rows.length);
      }
      block = multiBlocks.flatMap(b => b.rows);
    } else {
      // Single row drag: the row itself + all consecutive children (higher indent)
      const draggedIndent = allRows[allOld].indent;
      let blockSize = 1;
      while (allOld + blockSize < allRows.length && allRows[allOld + blockSize].indent > draggedIndent) blockSize++;
      block = newRows.splice(allOld, blockSize);
    }

    // A move that lands in a DIFFERENT stage group OR a DIFFERENT sub-stage (part) must PRESERVE the
    // dragged block's own nesting — dropping a subtask into another stage/part should keep it indented
    // under its parent, not flatten it to the drop target's level. The re-level below is therefore
    // reserved for in-place reorders within the same stage + same part (so dragging a subtask out of
    // its parent within its own group still un-indents, as before).
    const crossGroup = srcGroup.stageStart !== dstGroup.stageStart
      || newSubStageId !== (timeline[active.id]?._subStageId ?? null);

    // Find insertion index after splice.
    let spliceAt;
    const insertAt = newRows.findIndex(r => r.id === over.id);
    if (insertAt !== -1) {
      const overIndent = newRows[insertAt].indent;
      const overHasHiddenKids = insertAt + 1 < newRows.length && newRows[insertAt + 1].indent > overIndent;
      if (collapsedRows.has(over.id) && overHasHiddenKids) {
        // Dropping onto a COLLAPSED parent: land AFTER its whole (hidden) subtree, as a sibling of the
        // parent — not wedged between the parent and its first hidden subtask (which read as outdented).
        let after = insertAt + 1;
        while (after < newRows.length && newRows[after].indent > overIndent) after++;
        spliceAt = after;
        if (!crossGroup) {
          const delta = overIndent - block[0].indent;
          if (delta !== 0) block = block.map(r => ({ ...r, indent: Math.max(0, r.indent + delta) }));
        }
      } else {
        // When dragging downward the drop target shifts up by the block size after removal,
        // so we insert AFTER it (insertAt + 1); when dragging upward, insert before it.
        spliceAt = allNew > allOld ? insertAt + 1 : insertAt;
        // Re-level the dragged block to the drop target's indent so an indented (sub) task
        // dropped outside its parent lands at that position un-indented, rather than staying
        // wedged in as a child. Children of the block keep their relative nesting.
        // Skipped for cross-stage / cross-part moves (see crossGroup above) so nesting is preserved.
        if (!crossGroup) {
          const delta = overIndent - block[0].indent;
          if (delta !== 0) block = block.map(r => ({ ...r, indent: Math.max(1, r.indent + delta) }));
        }
      }
    } else {
      // Virtual drop target (sub-stage header / part row) — insert at the end of the stage group,
      // before its main invoicing/closure rows. Display regroups by _subStageId, so exact
      // position within the group only affects ordering among the part's own tasks.
      const stageId = overRow.stageId;
      const si = stageId ? newRows.findIndex(r => r.id === stageId) : -1;
      if (si === -1) return;
      let end = si + 1;
      while (end < newRows.length && newRows[end].indent > 0
        && !newRows[end].id.startsWith('invoicing_') && !newRows[end].id.startsWith('closure_')) end++;
      spliceAt = end;
    }

    // Cross-stage / cross-part move: re-base the block to the top task level (indent 1) in its new
    // home, preserving the block's OWN relative nesting. A subtask dragged alone (its parent left
    // behind) therefore becomes a plain top-level task in the destination part instead of staying
    // wedged in at its old depth; dragging a parent together WITH its children keeps them nested
    // under it (the shallowest row becomes indent 1, the rest shift by the same delta). In-place
    // re-leveling within the same stage + part is handled above.
    if (crossGroup) {
      const minIndent = Math.min(...block.map(r => r.indent || 1));
      const delta = 1 - minIndent;
      if (delta !== 0) block = block.map(r => ({ ...r, indent: Math.max(1, (r.indent || 1) + delta) }));
    }
    newRows.splice(spliceAt, 0, ...block);

    // Ensure closure/invoicing rows stay at the bottom of each stage group after reorder
    const orderedRows = ensureRowOrder(newRows);

    // Apply any sub-stage reassignment FIRST so the new grouping is reflected in the numbering.
    let assignedTimeline = { ...timeline };
    if (newSubStageId !== (timeline[active.id]?._subStageId ?? null)) {
      block.forEach(r => {
        assignedTimeline[r.id] = { ...(assignedTimeline[r.id] || {}), _subStageId: newSubStageId };
      });
    }

    // Remap predecessor values after reorder (grouping uses the post-assignment timeline)
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtOrdRows = buildExtendedRows(orderedRows, partStagesMap, assignedTimeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtOrdRows);
    const rowData = orderedRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    let newTimeline = remapPredecessorValues({ ...assignedTimeline, _rows: rowData }, newExtOrdRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;

    // Optimistic update — show new row order and remapped predecessors immediately
    isOptimisticActive.current = true;
    setOptimisticRows(orderedRows);
    const predSnapshot = {};
    orderedRows.forEach(r => { if (newTimeline[r.id]?.predecessors != null) predSnapshot[r.id] = newTimeline[r.id].predecessors; });
    setOptimisticPreds(predSnapshot);

    await persistTimeline(newTimeline);
  }, [displayRows, allRows, rowIds, timeline, persistTimeline, partStagesMap, selectedRows, isTemplateBuilder, collapsedRows, canEdit, ownsRow, rowSubtreeIds]);

  // ── Assign sub-stage to a task row ───────────────────────────────────
  const handleAssignSubStage = useCallback(async (rowId, partId) => {
    if (!canEdit) return;
    // Reassignment changes the grouped order, so task numbers shift — remap predecessors.
    const assignedTimeline = { ...timeline, [rowId]: { ...(timeline[rowId] || {}), _subStageId: partId || null } };
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(allRows, partStagesMap, assignedTimeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    const newTimeline = remapPredecessorValues(assignedTimeline, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;
    setSubStageAssignMenu(null);
    await persistTimeline(newTimeline);
  }, [canEdit, allRows, partStagesMap, timeline, persistTimeline]);

  // ── Carry forward one OR MORE deliverable/task rows to another stage or part stage ───
  // Moves each selected row into the target group (in its original order), tags it as carried-forward
  // (keeps the original stage name as the origin label so "Revert to stage" can put it back), assigns
  // the target sub-stage (if any) and remaps predecessor numbering — all in a single write. Works for
  // SOW (deliv_) and custom rows alike, for one row or a Ctrl+multi-selection.
  const handleCarryForwardMulti = useCallback(async (rowIds, targetStageId, targetPartId) => {
    if (!canEdit || !Array.isArray(rowIds) || !rowIds.length) { setCarryForwardMenu(null); return; }
    const idSet = new Set(rowIds);
    const cleanPart = (nm) => (nm || '').replace(/\s*[—–-]?\s*part\s*/i, ' ').replace(/\s+/g, ' ').trim();
    const meta = {}; // rowId → { originLabel, originStageId, originSubStageId }
    rowIds.forEach(rowId => {
      const srcIdx = allRows.findIndex(r => r.id === rowId);
      if (srcIdx === -1) return;
      let originLabel = timeline[rowId]?._cfFromLabel || null;
      let originStageId = timeline[rowId]?._cfFromStageId || null;
      let originSubStageId = (timeline[rowId]?._cfFromStageId !== undefined && timeline[rowId]?._cfFromStageId !== null)
        ? (timeline[rowId]?._cfFromSubStageId ?? null) : null;
      if (!originLabel || !originStageId) {
        let stageLabel = null, stageId = null;
        for (let i = srcIdx; i >= 0; i--) {
          if (allRows[i].indent === 0) { stageLabel = allRows[i].name || allRows[i].defaultLabel || allRows[i].id; stageId = allRows[i].id; break; }
        }
        const srcPartId = timeline[rowId]?._subStageId || null;
        const srcPart = srcPartId && stageId ? (partStagesMap[stageId] || []).find(p => p.id === srcPartId) : null;
        if (!originLabel) originLabel = srcPart ? (cleanPart(srcPart.name) || stageLabel || '') : (stageLabel || '');
        if (!originStageId) { originStageId = stageId; originSubStageId = srcPartId; }
      }
      meta[rowId] = { originLabel: originLabel || '', originStageId: originStageId || null, originSubStageId: originSubStageId || null };
    });
    // A parent carries its whole subtree: every row indented under it moves too and keeps its nesting
    // relative to the parent (the parent lands at indent 1). A selected row already inside another
    // selected row's subtree just travels with that parent.
    const rootIndent = {};   // moved row id → indent of the root it travels under
    const childOf = {};      // descendant id → root id
    allRows.forEach(r => {
      if (!idSet.has(r.id) || rootIndent[r.id] !== undefined) return;
      const sub = rowSubtreeIds(r.id);
      sub.forEach(id => { if (rootIndent[id] === undefined) rootIndent[id] = r.indent; if (id !== r.id) childOf[id] = r.id; });
    });
    const moveSet = new Set(Object.keys(rootIndent));
    const blocks = allRows.filter(r => moveSet.has(r.id)).map(r => ({ ...r, indent: 1 + (r.indent - rootIndent[r.id]) }));
    if (!blocks.length) { setCarryForwardMenu(null); return; }
    const without = allRows.filter(r => !moveSet.has(r.id));
    const stageIdx = without.findIndex(r => r.id === targetStageId);
    if (stageIdx === -1) { setCarryForwardMenu(null); return; }
    let end = stageIdx + 1;
    while (end < without.length && without[end].indent > 0
      && !without[end].id.startsWith('invoicing_') && !without[end].id.startsWith('closure_')) end++;
    const newRows = [...without.slice(0, end), ...blocks, ...without.slice(end)];
    const assignedTimeline = { ...timeline };
    // Subtasks moved with a parent: tagged with the parent they travel with (no origin prefix of their
    // own, so the name column stays readable) and their own sub-stage, so Revert can put them back.
    Object.keys(childOf).forEach(id => {
      if (timeline[id]?._isCarriedForward) {
        assignedTimeline[id] = { ...(timeline[id] || {}), _subStageId: targetPartId || null };
        return;
      }
      assignedTimeline[id] = {
        ...(timeline[id] || {}),
        _cfWithParent: childOf[id],
        _cfFromSubStageId: timeline[id]?._subStageId || null,
        _subStageId: targetPartId || null,
      };
    });
    rowIds.forEach(rowId => {
      if (!meta[rowId] || childOf[rowId]) return;
      assignedTimeline[rowId] = {
        ...(timeline[rowId] || {}),
        _isCarriedForward: true,
        _cfFromLabel: meta[rowId].originLabel,
        _cfFromStageId: meta[rowId].originStageId,
        _cfFromSubStageId: meta[rowId].originSubStageId,
        _subStageId: targetPartId || null,
      };
    });
    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(newRows, partStagesMap, assignedTimeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    assignedTimeline._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = remapPredecessorValues(assignedTimeline, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;
    setCarryForwardMenu(null);
    setSelectedRows(new Set());
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    await persistTimeline(newTimeline);
  }, [canEdit, allRows, partStagesMap, timeline, persistTimeline, rowSubtreeIds]);

  // Revert a carried-forward task back to the stage/part it came from and strip the CF tags.
  const handleRevertCarryForward = useCallback(async (rowId) => {
    if (!canEdit) return;
    const rd = timeline[rowId] || {};
    if (!rd._isCarriedForward) { setContextMenu(null); return; }
    const srcIdx = allRows.findIndex(r => r.id === rowId);
    if (srcIdx === -1) { setContextMenu(null); return; }
    const targetStageId = rd._cfFromStageId || null;
    const targetPartId = rd._cfFromSubStageId || null;

    // Build the destination row list. If we know the origin stage, move the row to the end of that
    // stage's group; otherwise leave it where it is and just strip the tags.
    // The row travels back with its whole subtree, nesting kept, the row itself at indent 1.
    const subtree = rowSubtreeIds(rowId);
    const baseIndent = allRows[srcIdx].indent;
    const block = allRows.filter(r => subtree.has(r.id)).map(r => ({ ...r, indent: 1 + (r.indent - baseIndent) }));
    let newRows;
    if (targetStageId && allRows.some(r => r.id === targetStageId)) {
      const without = allRows.filter(r => !subtree.has(r.id));
      const stageIdx = without.findIndex(r => r.id === targetStageId);
      let end = stageIdx + 1;
      while (end < without.length && without[end].indent > 0
        && !without[end].id.startsWith('invoicing_') && !without[end].id.startsWith('closure_')) end++;
      newRows = [...without.slice(0, end), ...block, ...without.slice(end)];
    } else {
      newRows = allRows.map(r => r.id === rowId ? { ...r } : r);
    }

    // Strip CF tags + restore original sub-stage assignment.
    const cleaned = { ...(timeline[rowId] || {}) };
    delete cleaned._isCarriedForward;
    delete cleaned._cfFromLabel;
    delete cleaned._cfFromStageId;
    delete cleaned._cfFromSubStageId;
    cleaned._subStageId = targetPartId || null;
    const assignedTimeline = { ...timeline, [rowId]: cleaned };
    // Subtasks that came along with this row go back to their own sub-stage and lose the travel tag.
    subtree.forEach(id => {
      if (id === rowId || timeline[id]?._cfWithParent !== rowId) return;
      const c = { ...(timeline[id] || {}) };
      c._subStageId = c._cfFromSubStageId || null;
      delete c._cfWithParent;
      delete c._cfFromSubStageId;
      assignedTimeline[id] = c;
    });

    const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
    const newExtRows = buildExtendedRows(newRows, partStagesMap, assignedTimeline);
    const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
    assignedTimeline._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
    const newTimeline = remapPredecessorValues(assignedTimeline, newExtRows, oldNumToKey, newKeyToNum);
    newTimeline._depNumKey = newNumToKey;

    setContextMenu(null);
    isOptimisticActive.current = true;
    setOptimisticRows(newRows);
    await persistTimeline(newTimeline);
  }, [canEdit, allRows, partStagesMap, timeline, persistTimeline, rowSubtreeIds]);

  // ── Tabs: save (create/update) and delete ─────────────────────────────
  const handleSaveTab = useCallback(async (editor) => {
    if (!canEdit || !supabaseProjects || !editor) return;
    // BRM's first tab can be renamed; its name is stored apart from the tab list.
    if (editor.tabId === '__all__') {
      setTabEditor(null);
      const name = (editor.name || '').trim();
      const next = { ...timeline };
      if (name && name !== 'Project timelines') next._mainTabName = name; else delete next._mainTabName;
      await writeTimelineDiff(supabaseProjects, project.id, timeline, next, { allowTracking: syncEnabledRef.current });
      return;
    }
    // A BRM tab owns its stages (they are added inside it), so the editor only names it.
    const keysArr = isStandalone ? null : Array.from(editor.keys || []);
    const existing = Array.isArray(timeline._tabs) ? timeline._tabs : [];
    let newTabs, tabId;
    if (editor.tabId) {
      tabId = editor.tabId;
      newTabs = existing.map(t => t.id === tabId ? { ...t, name: (editor.name || '').trim() || t.name, keys: keysArr || t.keys || [] } : t);
    } else {
      tabId = 'tab_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
      newTabs = [...existing, { id: tabId, name: (editor.name || '').trim() || `Tab ${existing.length + 1}`, keys: keysArr || [] }];
    }
    setTabEditor(null);
    setActiveTabId(tabId);
    await writeTimelineDiff(supabaseProjects, project.id, timeline, { ...timeline, _tabs: newTabs }, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, timeline, project.id, isStandalone]);

  const handleDeleteTab = useCallback(async (tabId) => {
    if (!canEdit || !supabaseProjects) return;
    const newTabs = (Array.isArray(timeline._tabs) ? timeline._tabs : []).filter(t => t.id !== tabId);
    setActiveTabId(prev => prev === tabId ? '__all__' : prev);
    setTabEditor(null);
    await writeTimelineDiff(supabaseProjects, project.id, timeline, { ...timeline, _tabs: newTabs }, { allowTracking: syncEnabledRef.current });
  }, [canEdit, supabaseProjects, timeline, project.id]);

  // ── Context menu handler ──────────────────────────────────────────────
  const displayRowsRef = useRef(displayRows);
  useEffect(() => { displayRowsRef.current = displayRows; }, [displayRows]);
  // Live refs for the current multi-selection, so the context menu can snapshot it at open time.
  // (Clicking a menu item mousedowns outside the table, which clears selectedRows before the action
  // runs — so the action must read a snapshot taken when the menu opened, not the live state.)
  const selectedRowsRef = useRef(selectedRows);
  useEffect(() => { selectedRowsRef.current = selectedRows; }, [selectedRows]);

  // Ctrl+B (Cmd+B) bolds the selected rows, saved on each row as `bold` so everyone sees it and the
  // exports print it. Bolds them all unless every one is already bold, then unbolds them all.
  // Left alone while typing in a field, where Ctrl+B belongs to that field (e.g. the MOM editor).
  useEffect(() => {
    if (!canEdit || selectedRows.size === 0) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'b') return;
      const t = e.target;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      e.preventDefault();
      const ids = [...selectedRows];
      const makeBold = !ids.every(id => timeline[id]?.bold);
      const next = { ...timeline };
      ids.forEach(id => {
        const entry = { ...(timeline[id] || {}) };
        if (makeBold) entry.bold = true; else delete entry.bold;
        next[id] = entry;
      });
      persistTimeline(next).then(ok => { if (!ok) alert('Could not save the bold setting.'); });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canEdit, selectedRows, timeline, persistTimeline]);
  const selectedColumnKeysRef = useRef(selectedColumnKeys);
  useEffect(() => { selectedColumnKeysRef.current = selectedColumnKeys; }, [selectedColumnKeys]);

  useEffect(() => {
    if (!subStageStripMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setSubStageStripMenu(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [subStageStripMenu]);

  const handleContextMenu = useCallback((e, rowIndex, colKey) => {
    // All users may right-click on task-name / # cells to access M.O.M options.
    // Other columns (date pickers, selects, etc.) remain edit-only.
    const isMOMColumn = colKey === null || colKey === 'taskName';
    if (!canEdit && !isMOMColumn) return;
    e.preventDefault();
    // Stage and sub-stage header rows: only show menu on trackable end date columns
    if (colKey !== null && colKey !== 'taskName') {
      const row = displayRowsRef.current[rowIndex];
      if (row && (row.indent === 0 || row.isSubStageHeader)) {
        const isTrackable = colKey === 'plannedEndDate';
        if (!isTrackable) return;
      }
    }
    // Snapshot the multi-selection now — a later menu-item click clears it before the action reads it.
    setContextMenu({
      x: e.clientX, y: e.clientY, rowIndex, colKey: colKey !== undefined ? colKey : null,
      selectedRowIds: new Set(selectedRowsRef.current),
      selectedColKeys: new Set(selectedColumnKeysRef.current),
    });
  }, [canEdit]);

  // ── Format toolbar: read and write a target's format (see cellFormatOf for where each lives) ──
  const formatOfTarget = useCallback((tl, t) => (t.kind === 'cell' ? cellFormatOf(tl, t.rowKey, t.colKey)
    : t.kind === 'row' ? rowFormatOf(tl, t.rowKey) : (((tl || {})._colFmt || {})[t.colKey] || {})), []);
  // patchFor(currentFormat) → the props to set (undefined removes one); clear: true drops the target's own format.
  const applyFormat = useCallback((targets, patchFor, { clear = false } = {}) => {
    if (!canEdit || !supabaseProjects || !targets?.length) return;
    const next = { ...timeline };
    const tidy = (o) => { Object.keys(o).forEach(k => { if (o[k] === undefined || o[k] === null) delete o[k]; }); return o; };
    targets.forEach(t => {
      const patch = clear ? null : patchFor(formatOfTarget(next, t));
      if (t.kind === 'col') {
        const all = { ...(next._colFmt || {}) };
        const own = clear ? {} : tidy({ ...(all[t.colKey] || {}), ...patch });
        if (own.b === false) delete own.b;
        if (Object.keys(own).length) all[t.colKey] = own; else delete all[t.colKey];
        if (Object.keys(all).length) next._colFmt = all; else delete next._colFmt;
        return;
      }
      const rd = { ...(next[t.rowKey] || {}) };
      if (t.kind === 'row') {
        const own = clear ? {} : tidy({ ...(rd._rowFmt || {}), ...(rd.bold ? { b: true } : {}), ...patch });
        if (own.b) rd.bold = true; else delete rd.bold; // bold stays where Ctrl+B keeps it
        delete own.b;
        if (Object.keys(own).length) rd._rowFmt = own; else delete rd._rowFmt;
      } else {
        const fmts = { ...(rd._fmt || {}) };
        const own = clear ? {} : tidy({ ...(fmts[t.colKey] || {}), ...patch });
        // An explicit "not bold" only matters inside a bold row or column.
        if (own.b === false) {
          const inherited = { ...(((next._colFmt || {})[t.colKey]) || {}), ...rowFormatOf(next, t.rowKey) };
          if (!inherited.b) delete own.b;
        }
        if (Object.keys(own).length) fmts[t.colKey] = own; else delete fmts[t.colKey];
        if (Object.keys(fmts).length) rd._fmt = fmts; else delete rd._fmt;
      }
      next[t.rowKey] = rd;
    });
    writeTimelineDiff(supabaseProjects, project.id, timeline, next, { allowTracking: syncEnabledRef.current })
      .then(res => { if (res && res.success === false) alert('Could not save the format: ' + (res.error || 'permission denied.')); });
  }, [canEdit, supabaseProjects, timeline, project.id, formatOfTarget]);
  // What the toolbar shows: a toggle is on only when every target has it; size and alignment read the first.
  const formatBarState = useMemo(() => {
    if (!formatBar) return null;
    const fs = formatBar.targets.map(t => formatOfTarget(timeline, t));
    return { b: fs.every(f => f.b === true), i: fs.every(f => !!f.i), u: fs.every(f => !!f.u), size: fs[0]?.size, align: fs[0]?.align };
  }, [formatBar, timeline, formatOfTarget]);

  const handleContextAction = useCallback(async (action, arg) => {
    if (!contextMenu) return;
    const { rowIndex, colKey } = contextMenu;
    // ── Multi-select fan-out ──────────────────────────────────────────────
    // When the right-clicked row is part of a Ctrl+multi-selection, the data actions below apply to
    // EVERY selected row (and, for cell actions, every selected column) — not just the clicked
    // cell/row. handleFieldChange can't be looped for this (it reads a stale `timeline` closure and
    // each call fully re-persists, so only the last would survive); multi-cell edits are therefore
    // composed into ONE timeline write via commitCells().
    // Read the selection SNAPSHOT taken when the menu opened (the live selectedRows is cleared by the
    // menu-item mousedown before this runs).
    const selRowIds = contextMenu.selectedRowIds instanceof Set ? contextMenu.selectedRowIds : selectedRows;
    const selColKeys = contextMenu.selectedColKeys instanceof Set ? contextMenu.selectedColKeys : selectedColumnKeys;
    const clickedRow = displayRows[rowIndex];
    const rowSelActive = !!clickedRow && selRowIds.size > 1 && selRowIds.has(clickedRow.id);
    const targetRowIdxs = rowSelActive
      ? displayRows.map((r, i) => (selRowIds.has(r.id) ? i : -1)).filter(i => i >= 0)
      : [rowIndex];
    const colSel = (colKey && selColKeys.size > 1 && selColKeys.has(colKey))
      ? [...selColKeys]
      : (colKey ? [colKey] : []);
    // Whether a (row, column) pair may be written (mirrors the single-cell guards below).
    const cellEditable = (r, ck) => {
      if (!r || !ck) return false;
      const colDef = SCHEDULE_COLUMNS.find(c => c.key === ck);
      if (colDef?.computed) return false;
      if (ck === 'taskName' && !r.id.startsWith('row_') && !(isTemplateBuilder && r.id.startsWith('deliv_'))) return false;
      return true;
    };
    // Apply a list of { rowKey, field, value } edits as a single timeline write, re-deriving computed
    // end dates and the sowRef highlight flag the same way handleFieldChange does for one cell.
    const commitCells = async (changes) => {
      if (!canEdit || !changes.length) return;
      const nt = { ...timeline };
      for (const { rowKey, field, value } of changes) {
        nt[rowKey] = { ...(nt[rowKey] || {}), [field]: value === '' ? null : value };
        const rd = nt[rowKey];
        if (field === 'sowRef') {
          rd._sowUnlinked = (value === '' || value == null);
          // A pasted sowRef carries no fingerprint of its own — drop any stale one so reconciliation
          // falls back to anchor-position resolution for the new value.
          delete rd.sowRefLabels;
        }
        // Same rule as handleFieldChange: a start_ row's dependency set here is a manual override, and
        // clearing it must stick, or the start-row preset effect would put the preset link straight back.
        if (field === 'predecessors' && rowKey.startsWith('start_')) {
          const cleared = (value === '' || value == null);
          rd._presetCleared = cleared;
          rd._manualPredOverride = !cleared;
          startRowPredInitRef.current = null;
        }
        if (field === 'plannedStartDate' || field === 'plannedDuration') {
          if (rd.plannedStartDate && rd.plannedDuration != null) rd.plannedEndDate = addWorkDays(rd.plannedStartDate, Number(rd.plannedDuration) - 1);
          else if (!rd.plannedStartDate && rd.plannedDuration == null) rd.plannedEndDate = null;
        }
        applyDerivedColumns(rd, field, timeline._customColumns, new Set(projectSettings.workingDays.days));
        applyStatusDone(rd, field);
      }
      // Run the same dependency cascade and parent rollup a single cell edit gets (handleFieldChange),
      // so Clear Row / paste move dependents and re-aggregate (or clear) parent rows in the same save.
      if (projectSettings.dependencies.enabled) {
        const cascaded = applyDependencies(extendedRows, nt, projectSettings);
        cascaded._rows = nt._rows;
        cascaded._settings = nt._settings;
        await persistTimeline(cascaded);
        return;
      }
      await persistTimeline(nt);
    };
    switch (action) {
      case 'format': {
        // Scope follows the click: the # cell formats the row (or every selected row), a data cell
        // formats that cell (or every Ctrl+picked cell, or that column of every selected row).
        let targets;
        if (!colKey) {
          targets = targetRowIdxs.map(i => displayRows[i]).filter(Boolean).map(r => ({ kind: 'row', rowKey: r.key || r.id }));
        } else {
          const ck = `${clickedRow?.key}|${colKey}`;
          if (selectedCells.size > 1 && selectedCells.has(ck)) {
            targets = [...selectedCells].map(k => { const cut = k.lastIndexOf('|'); return { kind: 'cell', rowKey: k.slice(0, cut), colKey: k.slice(cut + 1) }; });
          } else {
            targets = [];
            targetRowIdxs.forEach(i => { const r = displayRows[i]; if (r) colSel.forEach(c => targets.push({ kind: 'cell', rowKey: r.key || r.id, colKey: c })); });
          }
        }
        if (targets.length) setFormatBar({ x: contextMenu.x, y: contextMenu.y, targets });
        break;
      }
      case 'copyCell': {
        const row = displayRows[rowIndex];
        if (!row || !colKey) break;
        const rowData = timeline[row.key] || {};
        cellClipboard.current = { colKey, value: rowData[colKey] ?? null };
        break;
      }
      case 'linkToSow': {
        const row = displayRows[rowIndex];
        if (!row) break;
        setLinkSowModal({
          rowId: row.id,
          rowName: (timeline[row.id] && timeline[row.id].taskName) || row.defaultLabel || row.name || '',
          rowStageKey: rowStageKeyById.get(row.id) || null,
          sowRef: (timeline[row.id] && timeline[row.id].sowRef) != null ? timeline[row.id].sowRef : null,
        });
        break;
      }
      case 'pasteCell': {
        if (!colKey || !cellClipboard.current) break;
        // Paste the clipboard value into the clicked cell — and, under a multi-selection, into every
        // selected row × selected column that accepts it.
        const val = cellClipboard.current.value;
        const changes = [];
        for (const ri of targetRowIdxs) {
          const r = displayRows[ri];
          for (const ck of colSel) if (cellEditable(r, ck)) changes.push({ rowKey: r.key, field: ck, value: val });
        }
        await commitCells(changes);
        break;
      }
      case 'clearCell': {
        if (!colKey) break;
        // Clear the clicked cell — and, under a multi-selection, every selected row × selected column.
        const changes = [];
        for (const ri of targetRowIdxs) {
          const r = displayRows[ri];
          for (const ck of colSel) if (cellEditable(r, ck)) changes.push({ rowKey: r.key, field: ck, value: null });
        }
        await commitCells(changes);
        break;
      }
      case 'manageOptions': {
        if (!colKey) break;
        setManageOptionsDropdown({ x: contextMenu.x, y: contextMenu.y, colKey });
        break;
      }
      case 'openMOM': {
        const row = displayRows[rowIndex];
        if (!row) break;
        const rd = timeline[row.key] || {};
        const name = rd.taskName || row.defaultLabel || row.name || '';
        setMOMPanel({ rowId: row.id, rowKey: row.key, rowName: name });
        break;
      }
      case 'insertMOM': {
        const row = displayRows[rowIndex];
        if (!row) break;
        // Find the parent stage for this row
        let parentStageKey = null;
        if (row.indent === 0 && !row.isSubStageHeader) {
          parentStageKey = row.id;
        } else {
          for (let pi = rowIndex - 1; pi >= 0; pi--) {
            const pr = displayRows[pi];
            if (pr.indent === 0 && !pr.isSubStageHeader) { parentStageKey = pr.id; break; }
          }
        }
        if (!parentStageKey) break;
        const esc = parentStageKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Row-ID suffix: max existing meeting_N for this stage + 1 (ensures a unique key)
        let maxN = 0;
        for (const r of allRows) {
          const m = r.id.match(new RegExp(`^meeting_(\\d+)_${esc}$`));
          if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
        }
        const nextN = maxN + 1;
        // Label number: count ALL MOM documents already attached to rows in this stage
        const stageDocCount = momHook.documents.filter(doc => {
          if (doc.task_key === parentStageKey) return true;
          const rowIdx = allRows.findIndex(r => r.id === doc.task_key);
          for (let pi = rowIdx - 1; pi >= 0; pi--) {
            if (allRows[pi].indent === 0 && !allRows[pi].isSubStageHeader) {
              return allRows[pi].id === parentStageKey;
            }
          }
          return false;
        }).length;
        const labelN = stageDocCount + 1;
        const newKey = `meeting_${nextN}_${parentStageKey}`;
        const newRow = { id: newKey, key: newKey, indent: 1, defaultLabel: `Meeting ${labelN}` };
        // Insert below the clicked row
        const idx = allRows.findIndex(r => r.id === row.id);
        const newRows = [...allRows];
        newRows.splice(idx >= 0 ? idx + 1 : newRows.length, 0, newRow);
        const rowData = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
        const newTimeline = { ...timeline, _rows: rowData };
        // Optimistic update so row appears immediately
        isOptimisticActive.current = true;
        setOptimisticRows(newRows);
        await persistTimeline(newTimeline);
        setMOMPanel({ rowId: newKey, rowKey: newKey, rowName: `Meeting ${labelN}` });
        break;
      }
      case 'insertAbove': handleInsertRow(rowIndex, 'above', arg); break;
      case 'insertBelow': handleInsertRow(rowIndex, 'below', arg); break;
      case 'indent': {
        // Single row → existing guarded helper. Multi-selection → indent every selected row's subtree
        // by one level in ONE write. Only the "top" selected rows (not nested inside another selected
        // row's subtree) contribute, so overlapping selections aren't double-shifted and relative
        // nesting is preserved.
        if (targetRowIdxs.length <= 1) { handleIndent(rowIndex); break; }
        const selIds = new Set(targetRowIdxs.map(ri => displayRows[ri]?.id).filter(Boolean));
        const contained = new Set();
        selIds.forEach(id => rowSubtreeIds(id).forEach(s => { if (s !== id && selIds.has(s)) contained.add(s); }));
        const ids = new Set();
        selIds.forEach(id => { if (contained.has(id)) return; const r = allRows.find(x => x.id === id); if (r && r.indent > 0) rowSubtreeIds(id).forEach(s => ids.add(s)); });
        if (!ids.size) break;
        if (![...ids].every(ownsRow)) break; // granted editors restructure only their own rows
        const newRows = allRows.map(r => ids.has(r.id) ? { ...r, indent: r.indent + 1 } : r);
        setSelectedRows(new Set());
        await saveRows(newRows);
        break;
      }
      case 'outdent': {
        // Mirror of indent. Skip a subtree whose top row is already at indent 1 so a task never
        // becomes an indent-0 (stage-level) row.
        if (targetRowIdxs.length <= 1) { handleOutdent(rowIndex); break; }
        const selIds = new Set(targetRowIdxs.map(ri => displayRows[ri]?.id).filter(Boolean));
        const contained = new Set();
        selIds.forEach(id => rowSubtreeIds(id).forEach(s => { if (s !== id && selIds.has(s)) contained.add(s); }));
        const ids = new Set();
        selIds.forEach(id => { if (contained.has(id)) return; const r = allRows.find(x => x.id === id); if (r && r.indent > 1) rowSubtreeIds(id).forEach(s => ids.add(s)); });
        if (!ids.size) break;
        if (![...ids].every(ownsRow)) break; // granted editors restructure only their own rows
        const newRows = allRows.map(r => ids.has(r.id) ? { ...r, indent: r.indent - 1 } : r);
        setSelectedRows(new Set());
        await saveRows(newRows);
        break;
      }
      case 'delete': {
        // Delete the clicked row, or — under a multi-selection — every selected deletable row in one
        // write (a single predecessor renumber), rather than only the clicked row.
        const isDeletable = (r) => !!r && (!canEdit ? ownsRow(r.id) : (
          r.id.startsWith('row_') || isCustomStage(r.id) || timeline[r.id]?._detached === true ||
          (isTemplateBuilder && r.indent > 0 && !r.isSubStageHeader
            && !r.id.startsWith('start_') && !r.id.startsWith('invoicing_') && !r.id.startsWith('closure_'))
        ));
        const idsToRemove = new Set(targetRowIdxs.map(ri => displayRows[ri]).filter(isDeletable).map(r => r.id));
        if (idsToRemove.size <= 1) { handleDeleteRow(rowIndex); break; }
        const newRows = allRows.filter(r => !idsToRemove.has(r.id));
        const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
        const newExtRows = buildExtendedRows(newRows, partStagesMap, timeline);
        const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
        const base = { ...timeline };
        idsToRemove.forEach(id => delete base[id]);
        base._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
        const newTimeline = remapPredecessorValues(base, newExtRows, oldNumToKey, newKeyToNum);
        newTimeline._depNumKey = newNumToKey;
        isOptimisticActive.current = true;
        setOptimisticRows(newRows);
        setSelectedRows(new Set());
        await persistTimeline(newTimeline);
        break;
      }
      case 'hide': {
        // Hide the clicked row, or every selected row under a multi-selection, in one write.
        // A parent takes its whole subtree with it, each subtask marked hidden in its own right,
        // so the hidden count includes them and Unhide brings the group back together.
        const nt = { ...timeline };
        let changed = false;
        for (const ri of targetRowIdxs) {
          const r = displayRows[ri];
          if (!r) continue;
          rowSubtreeIds(r.id).forEach(id => { nt[id] = { ...(nt[id] || {}), _hidden: true }; });
          changed = true;
        }
        if (changed) await persistTimeline(nt);
        break;
      }
      case 'unhideAll': {
        const nt = { ...timeline };
        let changed = false;
        allRows.forEach(r => {
          if (nt[r.id]?._hidden) { const { _hidden, ...rest } = nt[r.id]; nt[r.id] = rest; changed = true; } // eslint-disable-line no-unused-vars
        });
        if (changed) await persistTimeline(nt);
        break;
      }
      case 'unhideAdjacent': {
        // Unhide the contiguous hidden run(s) directly above and below the clicked row (and the
        // clicked row itself if it is hidden) — leaves non-adjacent hidden rows untouched.
        const clicked = displayRows[rowIndex];
        if (!clicked) break;
        const idx = allRows.findIndex(r => r.id === clicked.id);
        if (idx === -1) break;
        const toUnhide = new Set();
        if (timeline[clicked.id]?._hidden) toUnhide.add(clicked.id);
        for (let j = idx - 1; j >= 0 && timeline[allRows[j].id]?._hidden; j--) toUnhide.add(allRows[j].id);
        for (let j = idx + 1; j < allRows.length && timeline[allRows[j].id]?._hidden; j++) toUnhide.add(allRows[j].id);
        if (toUnhide.size === 0) break;
        const nt = { ...timeline };
        toUnhide.forEach(id => { const { _hidden, ...rest } = nt[id] || {}; nt[id] = rest; }); // eslint-disable-line no-unused-vars
        await persistTimeline(nt);
        break;
      }
      case 'selectRow': {
        const row = displayRows[rowIndex];
        if (!row || row.indent === 0) break;
        setSelectedRows(prev => {
          const next = new Set(prev);
          if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
          return next;
        });
        break;
      }
      case 'copyRow': {
        // Capture every selected row's data (or just the clicked row) so a later Paste Row can apply
        // them. A multi-row clipboard pastes pairwise onto multi-selected targets; a single-row
        // clipboard broadcasts to all targets (see pasteRow).
        const rows = targetRowIdxs.map(ri => displayRows[ri]).filter(Boolean).map(r => ({ ...(timeline[r.key] || {}) }));
        if (!rows.length) break;
        rowClipboard.current = { rows, rowData: rows[0] }; // rowData kept for back-compat
        break;
      }
      case 'pasteRow': {
        if (!rowClipboard.current) break;
        const clip = rowClipboard.current;
        const srcRows = Array.isArray(clip.rows) && clip.rows.length ? clip.rows : (clip.rowData ? [clip.rowData] : []);
        if (!srcRows.length) break;
        // Paste all non-structural fields. One copied row → broadcast onto every selected target;
        // several copied rows → pairwise (i-th copied row onto the i-th selected target). Composed
        // into one write so all rows persist (not just the last).
        const structuralKeys = new Set(['_projectTaskId', '_subStageId', 'predecessors']);
        const fieldsOf = (src) => Object.entries(src).filter(([k]) => !structuralKeys.has(k));
        const targets = targetRowIdxs.map(ri => displayRows[ri]).filter(Boolean);
        const changes = [];
        if (srcRows.length === 1) {
          const entries = fieldsOf(srcRows[0]);
          targets.forEach(r => entries.forEach(([k, v]) => changes.push({ rowKey: r.key, field: k, value: v })));
        } else {
          const n = Math.min(srcRows.length, targets.length);
          for (let i = 0; i < n; i++) fieldsOf(srcRows[i]).forEach(([k, v]) => changes.push({ rowKey: targets[i].key, field: k, value: v }));
        }
        await commitCells(changes);
        break;
      }
      case 'clearRow': {
        const clearableFields = ['plannedStartDate', 'plannedEndDate', 'plannedDuration',
          'percentComplete', 'remarks', 'delayCaused', 'assignedTo', 'predecessors'];
        const changes = [];
        for (const ri of targetRowIdxs) {
          const r = displayRows[ri];
          if (!r) continue;
          clearableFields.forEach(k => changes.push({ rowKey: r.key, field: k, value: null }));
        }
        await commitCells(changes);
        break;
      }
      case 'addToProjectTasks': {
        const row = displayRows[rowIndex];
        if (!row) break;
        const rowData = timeline[row.key] || {};
        const rawName = rowData.taskName || row.defaultLabel || row.name || '';
        if (!rawName) break;
        // Find parent stage (nearest ancestor with indent === 0)
        let stageName = '';
        let stageId = '';
        for (let i = rowIndex - 1; i >= 0; i--) {
          if (displayRows[i].indent === 0) {
            stageId = displayRows[i].id;
            stageName = STAGE_NAMES[stageId] || displayRows[i].defaultLabel || stageId;
            break;
          }
        }
        // Find part number if row is assigned to a sub-stage
        const subStageId = rowData._subStageId;
        let partNumber = null;
        if (subStageId && stageId) {
          const parts = (timeline[stageId]?.partStages) || [];
          const idx = parts.findIndex(p => p.id === subStageId);
          if (idx >= 0) partNumber = idx + 1;
        }
        // Format: "Stage_PartNum_TaskName" if in a sub-stage, else "Stage_TaskName"
        const fullTaskName = stageName && partNumber != null
          ? `${stageName}_${partNumber}_${rawName}`
          : stageName ? `${stageName}_${rawName}` : rawName;
        projectTasksHook.addTask({ task: fullTaskName, categories: ['timeline_linked'] }).then(result => {
          if (result && result.success && result.data) {
            handleFieldChange(row.key, '_projectTaskId', result.data.id);
          }
        });
        break;
      }
      case 'removeFromProjectTasks': {
        const row = displayRows[rowIndex];
        if (!row) break;
        const rowData = timeline[row.key] || {};
        const taskId = rowData._projectTaskId;
        if (!taskId) break;
        projectTasksHook.deleteTask(taskId).then(result => {
          if (result && result.success) {
            handleFieldChange(row.key, '_projectTaskId', null);
          }
        });
        break;
      }
      case 'syncTracking': {
        // Sync to tracking is disabled while the sync toggle is off — prompt to turn it on instead.
        if (!syncEnabledRef.current) {
          setConfirmModal({ message: 'Turn on sync to tracking?', confirmLabel: 'OK', cancelLabel: 'Cancel', onConfirm: () => setSyncEnabled(true) });
          break;
        }
        // Manual sync: set targetDate = planned end date (silent, no prompt).
        // Tries aggregated children end date first; falls back to the stage row's own plannedEndDate.
        const row = displayRows[rowIndex];
        if (!row || (row.indent !== 0 && !row.isSubStageHeader)) break;
        // Frozen stages/parts are managed on the Tracking page — never sync a Planned date onto them.
        // A main stage is frozen when it is PAID or SYNC-LOCKED (the Tracking page's pen/lock); a part when paid.
        const stFrozen = row.isSubStageHeader
          ? (() => { const pt = (timeline[row.stageId]?.partStages || []).find(p => p.id === row.partId); return !!(pt?.paidDate || isApprovalLocked(pt)); })()
          : !!(timeline[row.key]?.paidDate || timeline[row.key]?._syncLocked || isApprovalLocked(timeline[row.key]));
        if (stFrozen) break;
        const agg = row.isSubStageHeader ? subStageDates[row.key] : stageDates[row.key];
        const endDate = agg?.plannedEndDate || (row.indent === 0 ? parseDate(timeline[row.key]?.plannedEndDate) : null);
        if (!endDate) break;
        const dateStr = formatDate(endDate);
        const stageKey = row.isSubStageHeader ? row.stageId : row.key;
        const partId = row.isSubStageHeader ? row.partId : null;
        if (row.isSubStageHeader) {
          const updatedParts = (timeline[stageKey]?.partStages || []).map(p =>
            p.id === partId ? { ...p, targetDate: dateStr } : p
          );
          persistTimeline({ ...timeline, [stageKey]: { ...(timeline[stageKey] || {}), partStages: updatedParts } });
        } else {
          handleFieldChange(row.key, 'targetDate', dateStr);
        }
        break;
      }
      case 'updateActuals': {
        // Update-to-actuals is disabled while the sync toggle is off — prompt to turn it on instead.
        if (!syncEnabledRef.current) {
          setConfirmModal({ message: 'Turn on sync to tracking?', confirmLabel: 'OK', cancelLabel: 'Cancel', onConfirm: () => setSyncEnabled(true) });
          break;
        }
        // Set actualDate = planned end date + always show STAGE_CLOSURE prompt.
        // Tries aggregated children end date first; falls back to the stage row's own plannedEndDate.
        const row = displayRows[rowIndex];
        if (!row || (row.indent !== 0 && !row.isSubStageHeader)) break;
        // Frozen stages/parts are managed on the Tracking page — never update actuals onto them.
        // A main stage is frozen when it is PAID or SYNC-LOCKED (the Tracking page's pen/lock); a part when paid.
        const uaFrozen = row.isSubStageHeader
          ? (() => { const pt = (timeline[row.stageId]?.partStages || []).find(p => p.id === row.partId); return !!(pt?.paidDate || isApprovalLocked(pt)); })()
          : !!(timeline[row.key]?.paidDate || timeline[row.key]?._syncLocked || isApprovalLocked(timeline[row.key]));
        if (uaFrozen) break;
        const agg = row.isSubStageHeader ? subStageDates[row.key] : stageDates[row.key];
        const endDate = agg?.plannedEndDate || (row.indent === 0 ? parseDate(timeline[row.key]?.plannedEndDate) : null);
        if (!endDate) break;
        const trackingField = 'actualDate';
        const dateStr = formatDate(endDate);
        const stageKey = row.isSubStageHeader ? row.stageId : row.key;
        const partId = row.isSubStageHeader ? row.partId : null;
        const prevValue = row.isSubStageHeader
          ? ((timeline[stageKey]?.partStages || []).find(p => p.id === partId)?.[trackingField] || null)
          : (timeline[row.key]?.[trackingField] || null);
        const stageName = row.isSubStageHeader
          ? row.name
          : (timeline[row.key]?.taskName || STAGE_NAMES[row.key] || row.key);
        // Save the date immediately — the modal is informational only
        if (row.isSubStageHeader) {
          const snap = timeline;
          const updatedParts = (snap[stageKey]?.partStages || []).map(p =>
            p.id === partId ? { ...p, [trackingField]: dateStr } : p
          );
          persistTimeline({ ...snap, [stageKey]: { ...(snap[stageKey] || {}), partStages: updatedParts } });
        } else {
          handleFieldChange(row.key, trackingField, dateStr);
        }
        setTrackingTriggerModal({
          triggerType: TRIGGER_TYPES.STAGE_CLOSURE, stageKey, partId, stageName,
          trackingField, dateStr, prevValue, commit: null,
        });
        break;
      }
      case 'addDateToTracking': {
        if (!syncEnabledRef.current) {
          setConfirmModal({ message: 'Turn on sync to tracking?', confirmLabel: 'OK', cancelLabel: 'Cancel', onConfirm: () => setSyncEnabled(true) });
          break;
        }
        const row = displayRows[rowIndex];
        if (!row || (row.indent !== 0 && !row.isSubStageHeader)) break;
        // Paid stages/parts are frozen — their Planned date is managed on the Tracking page.
        const adRec = row.isSubStageHeader
          ? (timeline[row.stageId]?.partStages || []).find(p => p.id === row.partId)
          : timeline[row.key];
        if (adRec?.paidDate || isApprovalLocked(adRec)) break;
        const agg = row.isSubStageHeader ? subStageDates[row.key] : stageDates[row.key];
        const endDate = agg ? agg.plannedEndDate : null;
        if (!endDate) break;
        const trackingField = 'targetDate';
        const dateStr = formatDate(endDate);
        const stageKey = row.isSubStageHeader ? row.stageId : row.key;
        const partId = row.isSubStageHeader ? row.partId : null;

        // Read the previous tracking value so the trigger engine can detect first-entry vs modification
        const prevValue = row.isSubStageHeader
          ? ((timeline[stageKey]?.partStages || []).find(p => p.id === partId)?.[trackingField] || null)
          : (timeline[row.key]?.[trackingField] || null);

        const projectClient = activeClients.find(c => c.id === project.clientId);
        const plannedDateThreshold = projectClient?.plannedDateTriggerThresholdDays ?? project.plannedDateTriggerThresholdDays ?? 7;
        const triggerType = resolveTimelineTrigger({
          fieldKey: trackingField,
          previousValue: prevValue,
          nextValue: dateStr,
          stageKey,
          plannedDateThreshold,
        });

        // Always save the date immediately regardless of trigger
        if (row.isSubStageHeader) {
          const updatedParts = (timeline[stageKey]?.partStages || []).map(p =>
            p.id === partId ? { ...p, [trackingField]: dateStr } : p
          );
          persistTimeline({ ...timeline, [stageKey]: { ...(timeline[stageKey] || {}), partStages: updatedParts } });
        } else {
          handleFieldChange(row.key, trackingField, dateStr);
        }

        if (triggerType) {
          // Show informational modal about the workflow trigger (date already saved above)
          const stageName = row.isSubStageHeader
            ? row.name
            : (timeline[row.key]?.taskName || STAGE_NAMES[row.key] || row.key);
          setTrackingTriggerModal({
            triggerType, stageKey, partId, stageName,
            trackingField, dateStr, prevValue, commit: null,
          });
        }
        break;
      }
      case 'triggerPrompt': {
        // Force-fire the workflow trigger prompt, bypassing the normal first-entry-only gate.
        // Useful when a date was already set and the user wants to re-trigger the prompt.
        const row = displayRows[rowIndex];
        if (!row || (row.indent !== 0 && !row.isSubStageHeader)) break;
        const isPlanned = colKey === 'plannedEndDate';
        if (!isPlanned) break;
        const agg = row.isSubStageHeader ? subStageDates[row.key] : stageDates[row.key];
        const endDate = agg ? agg.plannedEndDate : null;
        if (!endDate) break;
        const trackingField = 'targetDate';
        const dateStr = formatDate(endDate);
        const stageKey = row.isSubStageHeader ? row.stageId : row.key;
        const partId   = row.isSubStageHeader ? row.partId  : null;
        const prevValue = row.isSubStageHeader
          ? ((timeline[stageKey]?.partStages || []).find(p => p.id === partId)?.[trackingField] || null)
          : (timeline[row.key]?.[trackingField] || null);
        const forcedTriggerType = TRIGGER_TYPES.TIMELINE_CHANGE;
        const stageName = row.isSubStageHeader
          ? row.name
          : (timeline[row.key]?.taskName || STAGE_NAMES[row.key] || row.key);
        let commit;
        if (row.isSubStageHeader) {
          const snap = timeline;
          const updatedParts = (snap[stageKey]?.partStages || []).map(p =>
            p.id === partId ? { ...p, [trackingField]: dateStr } : p
          );
          const newTimeline = { ...snap, [stageKey]: { ...(snap[stageKey] || {}), partStages: updatedParts } };
          commit = () => persistTimeline(newTimeline);
        } else {
          commit = () => handleFieldChange(row.key, trackingField, dateStr);
        }
        setTrackingTriggerModal({
          triggerType: forcedTriggerType, stageKey, partId, stageName,
          trackingField, dateStr, prevValue, commit,
        });
        break;
      }
      case 'clearTrackingDate': {
        if (!syncEnabledRef.current) {
          setConfirmModal({ message: 'Turn on sync to tracking?', confirmLabel: 'OK', cancelLabel: 'Cancel', onConfirm: () => setSyncEnabled(true) });
          break;
        }
        const row = displayRows[rowIndex];
        if (!row || (row.indent !== 0 && !row.isSubStageHeader)) break;
        const trackingField = 'targetDate';
        const stageKey = row.isSubStageHeader ? row.stageId : row.key;
        const partId   = row.isSubStageHeader ? row.partId  : null;
        if (row.isSubStageHeader) {
          const updatedParts = (timeline[stageKey]?.partStages || []).map(p =>
            p.id === partId ? { ...p, [trackingField]: null } : p
          );
          persistTimeline({ ...timeline, [stageKey]: { ...(timeline[stageKey] || {}), partStages: updatedParts } });
        } else {
          handleFieldChange(row.key, trackingField, null);
        }
        break;
      }
      case 'assignSubStage': {
        const row = displayRows[rowIndex];
        if (!row || row.indent === 0 || row.isSubStageHeader) break;
        // Find parent stage
        let parentStageId = null;
        for (let i = rowIndex - 1; i >= 0; i--) {
          if (displayRows[i].indent === 0) { parentStageId = displayRows[i].id; break; }
        }
        if (!parentStageId || !partStagesMap[parentStageId]) break;
        setSubStageAssignMenu({ x: contextMenu.x, y: contextMenu.y, rowId: row.id, stageId: parentStageId });
        break;
      }
      case 'carryForward': {
        const row = displayRows[rowIndex];
        if (!row || row.indent === 0 || row.isSubStageHeader) break;
        // Under a multi-selection, carry every eligible selected row; otherwise just the clicked row.
        const ids = rowSelActive
          ? targetRowIdxs.map(ri => displayRows[ri]).filter(r => r && r.indent > 0 && !r.isSubStageHeader).map(r => r.id)
          : [row.id];
        setCarryForwardMenu({ x: contextMenu.x, y: contextMenu.y, rowIds: ids.length ? ids : [row.id] });
        break;
      }
      case 'revertCarryForward': {
        const row = displayRows[rowIndex];
        if (!row) break;
        await handleRevertCarryForward(row.id);
        break;
      }
      case 'copyEndToContract': {
        const row = displayRows[rowIndex];
        if (!row || row.indent !== 0 || row.isSubStageHeader) break;
        const agg = stageDates[row.key];
        const endDate = agg?.plannedEndDate;
        if (!endDate) break;
        handleFieldChange(row.key, 'contractEndDate', formatDate(endDate));
        setFlashedStripEnd(prev => { const n = new Set(prev); n.add(row.key); return n; });
        setTimeout(() => setFlashedStripEnd(prev => { const n = new Set(prev); n.delete(row.key); return n; }), 1500);
        break;
      }
      case 'addNewStage': handleAddNewStage(rowIndex); break;
      case 'addPresetTask': {
        const row = displayRows[rowIndex];
        if (!row) break;
        const stageId = row.indent === 0 ? row.id : null;
        if (!stageId || !isCustomStage(stageId)) break;
        const stageName = timeline[stageId]?.taskName || row.defaultLabel || row.name || 'Stage';
        const _existingIds = new Set(allRows.map(r => r.id));
        setPresetTaskModal({
          stageId, stageName,
          _start: _existingIds.has(`start_${stageId}`),
          _invoicing: _existingIds.has(`invoicing_${stageId}`),
          _closure: _existingIds.has(`closure_${stageId}`),
        });
        break;
      }
      case 'renameStage': {
        const row = displayRows[rowIndex];
        if (!row || !isCustomStage(row.id)) break;
        const currentName = timeline[row.id]?.taskName || row.defaultLabel || row.name || '';
        setCustomStageNameValue(currentName);
        setEditingCustomStageName(row.id);
        break;
      }
      case 'deleteStage': {
        const row = displayRows[rowIndex];
        if (!row || !isCustomStage(row.id)) break;
        await handleDeleteCustomStage(row.id);
        break;
      }
      case 'hideStage': {
        const row = displayRows[rowIndex];
        if (!row) break;
        // From stageNameOnly: row is the stage itself; from task cells: find the owning stage
        const stageRow = isCustomStage(row.id) && row.indent === 0
          ? row
          : (() => {
              for (let pi = rowIndex - 1; pi >= 0; pi--) {
                if (displayRows[pi].indent === 0 && isCustomStage(displayRows[pi].id)) return displayRows[pi];
                if (displayRows[pi].indent === 0) return null;
              }
              return null;
            })();
        if (!stageRow) break;
        const stageIdx = allRows.findIndex(r => r.id === stageRow.id);
        const updates = {};
        for (let i = stageIdx; i < allRows.length; i++) {
          if (i > stageIdx && allRows[i].indent === 0) break;
          updates[allRows[i].id] = { ...(timeline[allRows[i].id] || {}), _hidden: true };
        }
        await persistTimeline({ ...timeline, ...updates });
        break;
      }
      case 'unhideStage': {
        // Find nearest hidden custom stage (immediately adjacent in allRows)
        const row = displayRows[rowIndex];
        if (!row) break;
        let ownerStageAllIdx = -1;
        for (let pi = rowIndex; pi >= 0; pi--) {
          if (displayRows[pi].indent === 0) { ownerStageAllIdx = allRows.findIndex(r => r.id === displayRows[pi].id); break; }
        }
        let targetStageId = null;
        // Search backwards: stop at first stage boundary
        for (let i = ownerStageAllIdx - 1; i >= 0; i--) {
          if (allRows[i].indent === 0) {
            if (isCustomStage(allRows[i].id) && timeline[allRows[i].id]?._hidden) targetStageId = allRows[i].id;
            break;
          }
        }
        if (!targetStageId) {
          // Search forwards past end of current stage group
          let afterEnd = ownerStageAllIdx + 1;
          while (afterEnd < allRows.length && allRows[afterEnd].indent > 0) afterEnd++;
          for (let i = afterEnd; i < allRows.length; i++) {
            if (allRows[i].indent === 0) {
              if (isCustomStage(allRows[i].id) && timeline[allRows[i].id]?._hidden) targetStageId = allRows[i].id;
              break;
            }
          }
        }
        if (!targetStageId) break;
        const stageAllIdx = allRows.findIndex(r => r.id === targetStageId);
        const updates = {};
        for (let i = stageAllIdx; i < allRows.length; i++) {
          if (i > stageAllIdx && allRows[i].indent === 0) break;
          const { _hidden, ...rest } = timeline[allRows[i].id] || {}; // eslint-disable-line no-unused-vars
          updates[allRows[i].id] = rest;
        }
        await persistTimeline({ ...timeline, ...updates });
        break;
      }
      case 'unhideAllStages': {
        const newTimeline = { ...timeline };
        for (const ar of allRows) {
          if (isCustomStage(ar.id) && ar.indent === 0 && newTimeline[ar.id]?._hidden) {
            const { _hidden, ...rest } = newTimeline[ar.id] || {}; // eslint-disable-line no-unused-vars
            newTimeline[ar.id] = rest;
            // Unhide children too
            const si = allRows.findIndex(r => r.id === ar.id);
            for (let i = si + 1; i < allRows.length; i++) {
              if (allRows[i].indent === 0) break;
              const { _hidden: _h2, ...rest2 } = newTimeline[allRows[i].id] || {}; // eslint-disable-line no-unused-vars
              newTimeline[allRows[i].id] = rest2;
            }
          }
        }
        await persistTimeline(newTimeline);
        break;
      }
      default: break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, displayRows, allRows, timeline, handleFieldChange, handleInsertRow, handleIndent, handleOutdent, handleDeleteRow, handleDeleteCustomStage, handleAddNewStage, projectTasksHook, partStagesMap, setSelectedRows, persistTimeline, setMOMPanel, setOptimisticRows, selectedRows, selectedColumnKeys, canEdit, ownsRow, isTemplateBuilder, addWorkDays, saveRows, rowSubtreeIds, extendedRows, projectSettings, selectedCells]);

  const handleSubStageStripAction = useCallback((action) => {
    if (!subStageStripMenu) return;
    const { rowKey, stageId, partId, endDateStr, targetDateStr } = subStageStripMenu;
    switch (action) {
      case 'clearDuration':
        handleFieldChange(rowKey, 'contractDuration', null);
        break;
      case 'clearStartDate':
        handleFieldChange(rowKey, 'contractStartDate', null);
        break;
      case 'clearEndDate':
        // Only clears the strip fields — does NOT touch contractDate or targetDate on tracking page.
        handleFieldChange(rowKey, 'contractDuration', null);
        handleFieldChange(rowKey, 'contractStartDate', null);
        handleFieldChange(rowKey, 'contractEndDate', null);
        break;
      case 'updateContractDate': {
        // Explicitly syncs the strip contractEndDate → tracking Contract (contractDate) column ONLY.
        // Planned (targetDate) is left alone: it belongs to the planning sync (gated by the Sync
        // toggle) and to the Tracking page. Writing it here overwrote Planned with Contract on every
        // stage it was used on, even with Sync off. Paid stages/parts are frozen, as everywhere else.
        if (!endDateStr || !syncEnabledRef.current) break;
        if (partId) {
          const part = (timeline[stageId]?.partStages || []).find(p => p.id === partId);
          if (part?.paidDate) { window.alert('This part is paid, so its tracking dates are frozen.'); break; }
          const updatedParts = (timeline[stageId]?.partStages || []).map(p =>
            p.id === partId ? { ...p, contractDate: endDateStr } : p
          );
          persistTimeline({ ...timeline, [stageId]: { ...(timeline[stageId] || {}), partStages: updatedParts } });
        } else {
          if (timeline[rowKey]?.paidDate) { window.alert('This stage is paid, so its tracking dates are frozen.'); break; }
          persistTimeline({ ...timeline, [rowKey]: { ...(timeline[rowKey] || {}), contractDate: endDateStr } });
        }
        break;
      }
      case 'loadFromTracking': {
        // Strip end is empty but tracking has a date — load it into the strip.
        if (!targetDateStr) break;
        handleFieldChange(rowKey, 'contractEndDate', targetDateStr);
        break;
      }
      default: break;
    }
    setSubStageStripMenu(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStageStripMenu, handleFieldChange, timeline, persistTimeline]);

  // ── % rollup ──────────────────────────────────────────────────────────
  // A row with sub tasks shows the average % of its direct sub tasks, each read at its own rolled
  // value, so a nested parent counts once rather than every task under it. Stages and parts then
  // average their direct rows the same way (stageDates / subStageDates). A row with no % is left out
  // of an average. The % cell is read only on any row with sub tasks.
  const pctRollup = useMemo(() => {
    const parentOf = new Map();
    const kids = new Map();
    const stack = [];
    for (const r of allRows) {
      const ind = r.indent || 0;
      while (stack.length && stack[stack.length - 1].indent >= ind) stack.pop();
      const parent = stack.length ? stack[stack.length - 1].id : null;
      parentOf.set(r.id, parent);
      if (parent) { if (!kids.has(parent)) kids.set(parent, []); kids.get(parent).push(r.id); }
      stack.push({ id: r.id, indent: ind });
    }
    const own = (id) => {
      const v = timeline[id]?.percentComplete;
      return v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null;
    };
    // A row with no % counts as 0, so a parent shows progress across all its rows. Blank only while
    // none of them has a % yet.
    const average = (vals) => {
      if (!vals.length || vals.every(v => v == null)) return null;
      return Math.round(vals.reduce((a, b) => a + (b == null ? 0 : b), 0) / vals.length);
    };
    // A child always follows its parent, so walking backwards rolls every child before its parent.
    const rolled = new Map();
    for (let i = allRows.length - 1; i >= 0; i--) {
      const r = allRows[i];
      if ((r.indent || 0) === 0) continue;
      const c = kids.get(r.id);
      rolled.set(r.id, c && c.length ? average(c.map(k => rolled.get(k))) : own(r.id));
    }
    return {
      parentOf,
      average,
      hasSubTasks: (id) => (kids.get(id) || []).length > 0,
      value: (id) => (rolled.has(id) ? rolled.get(id) : own(id)),
    };
  }, [allRows, timeline]);

  // ── Aggregated stage dates from child deliverables ───────────────────
  const stageDates = useMemo(() => {
    const workDays = new Set(projectSettings.workingDays.days);
    const holidaySet = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
    const result = {};
    let currentStageId = null;
    let childIds = [];
    const flush = () => {
      if (!currentStageId || childIds.length === 0) return;
      let minPlannedStart = null, maxPlannedEnd = null;
      const pctValues = [];
      for (const cid of childIds) {
        const d = timeline[cid];
        if (!d) continue;
        const ps = parseDate(d.plannedStartDate);
        const pe = parseDate(d.plannedEndDate);
        if (ps && (!minPlannedStart || ps < minPlannedStart)) minPlannedStart = ps;
        if (pe && (!maxPlannedEnd || pe > maxPlannedEnd)) maxPlannedEnd = pe;
        // Start / Invoicing / Closure rows carry no progress, so they are left out of the %.
        if (pctRollup.parentOf.get(cid) === currentStageId && !/^(start_|invoicing_|closure_)/.test(cid)) pctValues.push(pctRollup.value(cid));
      }
      const avgPct = pctRollup.average(pctValues);
      const stageEnd = resolveStageEnd(maxPlannedEnd, isAdvanceStage(currentStageId) ? null : timeline[`closure_${currentStageId}`], timeline[currentStageId]);
      result[currentStageId] = {
        plannedStartDate: minPlannedStart,
        plannedEndDate: stageEnd,
        plannedDuration: countWorkingDays(minPlannedStart, stageEnd, workDays, holidaySet),
        averagePercentComplete: avgPct,
      };
    };
    for (const row of allRows) {
      if (row.indent === 0) {
        flush();
        currentStageId = row.id;
        childIds = [];
      } else if (!row.id.startsWith('invoicing_')) {
        // Include start_ end date in stage aggregation; exclude only invoicing_ rows
        childIds.push(row.id);
      }
    }
    flush();
    return result;
  }, [allRows, timeline, projectSettings, pctRollup]);

  // ── Aggregated dates for sub-stage header rows ────────────────────────
  // Mirrors stageDates but groups children by their _subStageId assignment.
  // Keys are "substage_${stageId}_${partId}" — matching row.id on sub-stage headers.
  const subStageDates = useMemo(() => {
    const workDays = new Set(projectSettings.workingDays.days);
    const holidaySet = new Set((projectSettings.workingDays.holidays || []).map(h => h.date));
    const result = {};
    let currentStageId = null;
    let partGroups = {}; // { [partId]: string[] } — child row IDs per partition

    const flushParts = () => {
      if (!currentStageId) return;
      const parts = partStagesMap[currentStageId] || [];
      parts.forEach(p => {
        const partId = p.id;
        const subKey = `substage_${currentStageId}_${partId}`;
        // Aggregate the part's deliverables AND its own Start / Invoicing / Closure rows.
        const childIds = [...(partGroups[partId] || []), `start_${subKey}`, `invoicing_${subKey}`, `closure_${subKey}`].filter(id => !isAdvanceSystemRow(id));
        // % counts the part's top rows only (a task whose parent is not in the part), at rolled values.
        const inPart = new Set(partGroups[partId] || []);
        let minPlannedStart = null, maxPlannedEnd = null;
        const pctValues = [];
        for (const cid of childIds) {
          const d = timeline[cid];
          if (!d) continue;
          const ps = parseDate(d.plannedStartDate);
          const pe = parseDate(d.plannedEndDate);
          if (ps && (!minPlannedStart || ps < minPlannedStart)) minPlannedStart = ps;
          if (pe && (!maxPlannedEnd || pe > maxPlannedEnd)) maxPlannedEnd = pe;
          if (!inPart.has(pctRollup.parentOf.get(cid)) && !/^(start_|invoicing_|closure_)/.test(cid)) pctValues.push(pctRollup.value(cid));
        }
        const avgPct = pctRollup.average(pctValues);
        const partEnd = resolveStageEnd(maxPlannedEnd, isAdvanceSystemRow(`closure_${subKey}`) ? null : timeline[`closure_${subKey}`], p);
        result[subKey] = {
          plannedStartDate: minPlannedStart,
          plannedEndDate: partEnd,
          plannedDuration: countWorkingDays(minPlannedStart, partEnd, workDays, holidaySet),
          averagePercentComplete: avgPct,
        };
      });
    };

    for (const row of allRows) {
      if (row.indent === 0) {
        flushParts();
        currentStageId = row.id;
        partGroups = {};
      } else {
        const subStageId = timeline[row.id]?._subStageId;
        if (subStageId) {
          if (!partGroups[subStageId]) partGroups[subStageId] = [];
          partGroups[subStageId].push(row.id);
        }
      }
    }
    flushParts();
    return result;
  }, [allRows, timeline, projectSettings, partStagesMap, pctRollup]);

  // The % a row shows (table and exports): the rolled up average on stages, parts and tasks with sub
  // tasks, else the row's own value.
  const displayPercent = useCallback((row) => {
    if ((row.indent || 0) === 0 || row.isSubStageHeader) {
      return (stageDates[row.key] || subStageDates[row.key])?.averagePercentComplete ?? null;
    }
    return pctRollup.value(row.key || row.id);
  }, [stageDates, subStageDates, pctRollup]);

  // Continuously sync each stage/part's tracking "Planned" date (targetDate) to its aggregated
  // planned end — so the Tracking page reflects cascade/auto-schedule changes without lag, not
  // only on manual edits. No-op once aligned, so it settles without looping.
  useEffect(() => {
    if (isOptimisticActive.current) return;
    if (!canAutoSync || syncPaused || !supabaseProjects) return;
    if (!syncEnabled) return; // Sync toggle off → never push any date to the Tracking page
    if (!allRows.length) return;
    const patch = {};
    let changed = false;
    // Stage rows
    for (const r of allRows) {
      if (r.indent !== 0) continue;
      // Paid stages are frozen on the Tracking (Timeline) page — never overwrite their
      // dates from the planning sync. Keyed off the recorded paid date (Paid status only).
      const rTl = timeline[r.id];
      // Frozen stages (PAID or SYNC-LOCKED via the Tracking page's pen/lock) are never overwritten by the
      // planning sync. An unlocked stage still syncs even if it has part-stages — the lock decides, not the
      // parts (a part's own targetDate is still handled in the loop below regardless).
      if (rTl && (rTl.paidDate || rTl._syncLocked || isApprovalLocked(rTl))) continue;
      const end = stageDates[r.id]?.plannedEndDate ? formatDate(stageDates[r.id].plannedEndDate) : null;
      if (!end) continue;
      if ((timeline[r.id]?.targetDate || null) !== end) {
        patch[r.id] = { ...(patch[r.id] || timeline[r.id] || {}), targetDate: end };
        changed = true;
      }
    }
    // Part stages (targetDate lives on the partStages array of the parent stage)
    Object.entries(partStagesMap).forEach(([stageId, parts]) => {
      (parts || []).forEach(p => {
        if (p.paidDate || isApprovalLocked(p)) return; // paid / approved part → skip sync (dates managed on the Tracking page)
        const end = subStageDates[`substage_${stageId}_${p.id}`]?.plannedEndDate
          ? formatDate(subStageDates[`substage_${stageId}_${p.id}`].plannedEndDate) : null;
        if (!end) return;
        if ((p.targetDate || null) !== end) {
          const base = patch[stageId] || timeline[stageId] || {};
          const arr = (base.partStages || []).map(pp => pp.id === p.id ? { ...pp, targetDate: end } : pp);
          patch[stageId] = { ...base, partStages: arr };
          changed = true;
        }
      });
    });
    if (!changed) return;
    autoSyncWriteRef.current = true; // derived write — keep it off the undo stack (see capture effect)
    // Scoped merge: `patch` already holds only the stages whose targetDate this sync intends to change
    // (each value is the full stage object + new targetDate). Writing just those keys — instead of the
    // whole `{ ...timeline, ...patch }` blob — means stages this effect deliberately skipped (paid stages,
    // and stages with no computed planned end via `if (!end) continue`) keep their stored Planned date
    // instead of being nulled from a stale in-memory copy.
    saveTimelineChangedKeys(patch, { ...timeline, ...patch });
  }, [stageDates, subStageDates, allRows, partStagesMap, timeline, canAutoSync, syncPaused, supabaseProjects, project.id, syncEnabled, saveTimelineChangedKeys]);

  // Manual "Sync" — push the Timelines page's contract strip end → tracking Contract date, and the
  // aggregated planned end → tracking Planned date, for every stage and part. Does NOT touch
  // invoicing/closure/actual dates.
  // What a sync push would write: { patch, changes }. `changes` lists every Planned / Contract value that
  // would actually move ({ label, field, from, to }), so the sync disc can show them before anything saves.
  const computeTrackingSyncPatch = useCallback(() => {
    const patch = {};
    const changes = [];
    const note = (label, field, from, to) => { if ((from || null) !== (to || null)) changes.push({ label, field, from: from || null, to }); };
    for (const r of allRows) {
      if (r.indent !== 0) continue;
      const sd = timeline[r.id] || {};
      if (sd.paidDate || sd._syncLocked || isApprovalLocked(sd)) continue; // Frozen (paid, sync-locked or approved) → never overwrite (managed on Tracking page)
      const upd = {};
      if (sd.contractEndDate) upd.contractDate = sd.contractEndDate;
      const pend = stageDates[r.id]?.plannedEndDate;
      if (pend) upd.targetDate = formatDate(pend);
      if (Object.keys(upd).length) patch[r.id] = { ...(patch[r.id] || sd), ...upd };
      const label = timeline[r.id]?.taskName || r.name || STAGE_NAMES[r.id] || r.id;
      if (upd.targetDate) note(label, 'Planned', sd.targetDate, upd.targetDate);
      if (upd.contractDate) note(label, 'Contract', sd.contractDate, upd.contractDate);
    }
    Object.entries(partStagesMap).forEach(([stageId, parts]) => {
      (parts || []).forEach(p => {
        if (p.paidDate || isApprovalLocked(p)) return; // paid / approved part → skip sync (dates managed on the Tracking page)
        const subKey = `substage_${stageId}_${p.id}`;
        const strip = timeline[subKey] || {};
        const pend = subStageDates[subKey]?.plannedEndDate;
        const upd = {};
        if (strip.contractEndDate) upd.contractDate = strip.contractEndDate;
        if (pend) upd.targetDate = formatDate(pend);
        if (!Object.keys(upd).length) return;
        const base = patch[stageId] || timeline[stageId] || {};
        const arr = (base.partStages || []).map(pp => pp.id === p.id ? { ...pp, ...upd } : pp);
        patch[stageId] = { ...base, partStages: arr };
        const label = `${timeline[stageId]?.taskName || STAGE_NAMES[stageId] || stageId}: ${p.name || 'part'}`;
        if (upd.targetDate) note(label, 'Planned', p.targetDate, upd.targetDate);
        if (upd.contractDate) note(label, 'Contract', p.contractDate, upd.contractDate);
      });
    });
    return { patch, changes };
  }, [allRows, partStagesMap, timeline, stageDates, subStageDates]);

  // Tracking fields move only while "Sync to tracking" is on: with it off the save strips them (see
  // writeTimelineDiff), so this push is a no-op. That rule has no exception.
  const handleSyncAllTracking = useCallback(async () => {
    if (!canSync || !supabaseProjects || !syncEnabledRef.current) return;
    const { patch } = computeTrackingSyncPatch();
    setShowExportMenu(false);
    if (!Object.keys(patch).length) return;
    await writeTimelineDiff(supabaseProjects, project.id, timeline, { ...timeline, ...patch }, { allowTracking: syncEnabledRef.current });
  }, [canSync, supabaseProjects, timeline, project.id, computeTrackingSyncPatch]);

  // Tri-state sync-status disc: does a value differ between the planning timeline and the tracking/billing
  // page that a manual sync would reconcile? Read-only — mirrors exactly what unpausing (invoicing/closure
  // backfills) + handleSyncAllTracking (Planned/Contract push) would write, so clicking a red icon settles
  // it to green. Covers: (A) each stage's invoicing_/closure_ derived rows vs computeInvoicingClosureDates,
  // (B) the same for part-stages, (C) each non-paid/non-parent stage & part's Planned (targetDate) and
  // Contract (contractDate) vs the planning-derived end. Paid stages and parents-with-parts are frozen /
  // grouping-only, matching the writers' skip rules.
  const syncMismatch = useMemo(() => {
    if (!allRows.length) return false;
    // (A) stage invoicing/closure rows
    for (const r of allRows) {
      const isInvoicing = r.id.startsWith('invoicing_');
      const isClosure = r.id.startsWith('closure_');
      if (!isInvoicing && !isClosure) continue;
      const sk = r.id.slice((isInvoicing ? 'invoicing_' : 'closure_').length);
      const stageData = timeline[sk] || {};
      const existing = timeline[r.id] || {};
      const dur = (timeline[`invoicing_${sk}`]?.plannedDuration) || paymentTermsDays || null;
      const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(stageData, dur);
      let nextStart = null, nextEnd = null;
      if (isInvoicing) { nextStart = invoicingStart; nextEnd = invoicingEnd; }
      else if (closureDate) {
        const snapped = existing._delayIgnored ? (stageData.actualDate || null) : closureDate;
        nextStart = snapped; nextEnd = snapped;
      }
      if ((existing.plannedStartDate || null) !== (nextStart || null) ||
          (existing.plannedEndDate || null) !== (nextEnd || null)) return true;
    }
    // (B) part-stage invoicing/closure rows
    for (const [stageId, parts] of Object.entries(partStagesMap)) {
      if (isAdvanceStage(stageId)) continue;
      for (const p of (parts || [])) {
        const subKey = `substage_${stageId}_${p.id}`;
        const invKey = `invoicing_${subKey}`;
        const closeKey = `closure_${subKey}`;
        const dur = timeline[invKey]?.plannedDuration || paymentTermsDays || null;
        const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(p, dur);
        const invEx = timeline[invKey] || {};
        const closeEx = timeline[closeKey] || {};
        const invStart = invoicingStart || null;
        const invEndVal = invoicingEnd || null;
        const closeSnap = closureDate
          ? (closeEx._delayIgnored ? (p.actualDate || null) : closureDate)
          : null;
        if ((invEx.plannedStartDate || null) !== (invStart || null) || (invEx.plannedEndDate || null) !== (invEndVal || null)) return true;
        if ((closeEx.plannedStartDate || null) !== (closeSnap || null) || (closeEx.plannedEndDate || null) !== (closeSnap || null)) return true;
      }
    }
    // (C) Planned (targetDate) / Contract (contractDate) push — stages
    for (const r of allRows) {
      if (r.indent !== 0) continue;
      const sd = timeline[r.id] || {};
      if (sd.paidDate || sd._syncLocked || isApprovalLocked(sd)) continue;     // frozen (paid, sync-locked or approved) → not a mismatch
      if (sd.contractEndDate && sd.contractEndDate !== (sd.contractDate || null)) return true;
      const pend = stageDates[r.id]?.plannedEndDate;
      if (pend && formatDate(pend) !== (sd.targetDate || null)) return true;
    }
    // (C) Planned / Contract push — part-stages
    for (const [stageId, parts] of Object.entries(partStagesMap)) {
      for (const p of (parts || [])) {
        if (p.paidDate || isApprovalLocked(p)) continue;
        const subKey = `substage_${stageId}_${p.id}`;
        const strip = timeline[subKey] || {};
        if (strip.contractEndDate && strip.contractEndDate !== (p.contractDate || null)) return true;
        const pend = subStageDates[subKey]?.plannedEndDate;
        if (pend && formatDate(pend) !== (p.targetDate || null)) return true;
      }
    }
    return false;
  }, [allRows, partStagesMap, timeline, stageDates, subStageDates, paymentTermsDays]);

  // Sync-status disc click. With "Sync to tracking" OFF it does nothing but say so: it never turns the
  // toggle on and never pushes. With it ON it lists every Tracking date the push would change (stage,
  // Planned / Contract, old → new) and pushes them once on confirmation. The disc's colour is a pure
  // status indicator (red = dates out of sync, grey = in sync) — it is not affected by clicking.
  const handleSyncIconClick = useCallback(() => {
    if (!canSync) return;
    if (!syncEnabledRef.current) {
      setConfirmModal({ message: 'Sync to tracking is off, so nothing is pushed to the Tracking page. Turn the toggle on to sync.', confirmLabel: 'OK', cancelLabel: 'Close' });
      return;
    }
    const { changes } = computeTrackingSyncPatch();
    if (!changes.length) {
      setConfirmModal({ message: 'Tracking dates already match the planning timeline. Nothing to sync.', confirmLabel: 'OK', cancelLabel: 'Close' });
      return;
    }
    const fmt = (d) => (d ? formatDateDisplay(parseDate(d)) : 'empty');
    setConfirmModal({
      message: (
        <div>
          <div style={{ marginBottom: '8px' }}>Sync to the Tracking page? These dates will change:</div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid ' + BRIO_BORDER, padding: '6px 8px' }}>
            {changes.map((c, i) => (
              <div key={i} style={{ fontSize: '12px', fontWeight: 300, padding: '2px 0' }}>
                <span style={{ fontWeight: 500 }}>{c.label}</span> {c.field}: {fmt(c.from)} → {fmt(c.to)}
              </div>
            ))}
          </div>
        </div>
      ),
      confirmLabel: 'Sync', cancelLabel: 'Cancel',
      onConfirm: () => handleSyncAllTracking(),
    });
  }, [canSync, computeTrackingSyncPatch, handleSyncAllTracking]);

  // ── Export / Import handlers ──────────────────────────────────────────
  // Row number for exports: mirror the on-screen hierarchical "#" (e.g. "5", "5.1") so parent /
  // subtask grouping and numbering survive export instead of reverting to a flat 1..N sequence.
  const exportRowNum = useCallback((row) => ((row.indent || 0) === 0 ? '' : (displayNumMap[row.key] || '')), [displayNumMap]);
  // Dependency cell for exports: map stored predecessor numbers to the same flat display numbers
  // shown on-screen (identity when no part-stages hide rows), preserving type + working-day lag.
  const exportPredLabel = useCallback((predValue) => {
    if (!predValue) return '';
    return parsePredecessors(predValue)
      .slice()
      .sort((a, b) => (Number(seqToHierLabel[a.rowNumber] ?? a.rowNumber) - Number(seqToHierLabel[b.rowNumber] ?? b.rowNumber)))
      .map(p => {
        const label = seqToHierLabel[p.rowNumber] || p.rowNumber;
        return p.lag > 0 ? `${label}${p.type}+${p.lag}d` : `${label}${p.type}`;
      }).join(', ');
  }, [seqToHierLabel]);
  // Task-name cell for exports: on-screen name plus the SOW-deliverable "(N)" suffix (rows read
  // from a Scope of Work deliverable), so exports carry the same deliverable linkage as the grid.
  const exportTaskName = useCallback((row) => {
    const d = timeline[row.key] || {};
    const base = d.taskName || row.defaultLabel || row.name || '';
    const sr = d.sowRef;
    return (sr !== undefined && sr !== null && sr !== '') ? `${base} (${sr})` : base;
  }, [timeline]);
  // Whether a row heads a group in the export list — a parent task with deeper-indent children
  // immediately below (or a sub-stage header). Exports prefix these with a "▾" chevron to mirror
  // the grid's expand/collapse affordance, so parent tasks stay recognisable on paper.
  const exportIsParent = useCallback((rows, i) => {
    const cur = rows[i];
    if (!cur || (cur.indent || 0) === 0) return false; // stages already render as their own headers
    if (cur.isSubStageHeader) return true;
    const next = rows[i + 1];
    return !!(next && (next.indent || 0) > (cur.indent || 0));
  }, []);

  const handleExportCsv = useCallback((filteredRows) => {
    const rows = filteredRows || displayRows;
    const taskNameCol = visibleColumns.find(c => c.key === 'taskName');
    const nonTaskCols = visibleColumns.filter(c => c.key !== 'taskName');
    // Single flat header row
    const row1 = ['#'];
    if (taskNameCol) row1.push(columnLabels['taskName'] || 'Task Name');
    for (const col of nonTaskCols) row1.push(columnLabels[col.key] || col.label);
    const getCsvVal = (row, col) => {
      const d = timeline[row.key] || {};
      if (col.key === 'taskName') return exportTaskName(row);
      if (col.key === 'predecessors') return exportPredLabel(d.predecessors);
      return d[col.key] != null ? String(d[col.key]) : '';
    };
    const csvify = r => r.map(c => { const s = String(c ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; }).join(',');
    const dataRows = rows.map((row, i) => {
      const values = [exportRowNum(row)];
      if (taskNameCol) {
        // Preserve subtask indentation (leading spaces per depth) + parent chevron so hierarchy
        // survives a flat CSV cell.
        const indent = '   '.repeat(row.indent || 0);
        const chevron = exportIsParent(rows, i) ? '▾ ' : '';
        values.push(indent + chevron + getCsvVal(row, taskNameCol));
      }
      for (const col of nonTaskCols) values.push(getCsvVal(row, col));
      return values;
    });
    const csv = [row1, ...dataRows].map(csvify).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${project.name || 'timeline'}_timeline.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  }, [displayRows, timeline, visibleColumns, columnLabels, stageDates, project.name, exportRowNum, exportPredLabel, exportTaskName, exportIsParent]);

  // The "Delayed" badge a Closure row shows, or null. One rule for the Status cell, the PDF export and
  // the stage end-date dots, so they can never disagree.
  //  • closure_<stage>: payment landed after the invoicing window (`ignored` once the delay is ignored).
  // Start-of-Stage rows never carry it: a late payment already shows on the previous stage's Closure
  // row, and the start row's dependency still switches to the FS delay link on its own.
  // `hidden` is the per-row Hide flag set from the badge's right click menu (greyed on screen, left out
  // of the PDF).
  const delayedBadgeFor = useCallback((rowId) => {
    if (rowId.startsWith('closure_')) {
      const sk = rowId.slice('closure_'.length);
      const sd = resolveStageRecord(sk) || {};
      const closureRow = timeline[rowId] || {};
      const invDur = timeline[`invoicing_${sk}`]?.plannedDuration || paymentTermsDays || null;
      // Delayed while payment runs past the window (closure advancing daily) and once paid late. An
      // ignored delay still returns the badge, flagged `ignored`: it shows greyed and a click restores it.
      const { paidLate, overdue } = deriveRowDates(sd, invDur);
      if (!paidLate && !overdue) return null;
      return { label: 'Delayed', kind: 'closure', stageKey: sk, hidden: !!closureRow._delayBadgeHidden, ignored: !!closureRow._delayIgnored };
    }
    return null;
  }, [timeline, paymentTermsDays, resolveStageRecord]);

  // Hide / Unhide from the badge's right click menu. Saved on the row, so it persists for everyone.
  const setDelayBadgeHidden = useCallback((rowKey, hidden) => {
    if (!canEdit) return;
    const cur = timeline[rowKey] || {};
    const next = { ...cur };
    if (hidden) next._delayBadgeHidden = true; else delete next._delayBadgeHidden;
    persistTimeline({ ...timeline, [rowKey]: next });
  }, [canEdit, timeline, persistTimeline]);

  const handleExportPdf = useCallback((filteredRows) => {
    const rows = filteredRows || displayRows;
    const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const getCellText = (row, col) => {
      const d = timeline[row.key] || {};
      const rolled = rolledCustomValue(row.id, col.key);
      if (rolled) return rolled.value == null ? '' : (col.type === 'date' ? exportDateText(rolled.value) : String(rolled.value));
      if (col.key === 'taskName') return exportTaskName(row);
      if (col.key === 'predecessors') return exportPredLabel(d.predecessors);
      // Closure / Start rows: print the Delayed badge unless it has been hidden.
      if (col.type === 'status' && (row.id.startsWith('closure_') || row.id.startsWith('start_'))) {
        const delayed = delayedBadgeFor(row.id);
        return delayed && !delayed.hidden && !delayed.ignored ? delayed.label : '';
      }
      if (col.type === 'date') return exportDateText(d[col.key]);
      if (col.type === 'percent') { const v = displayPercent(row); return v != null ? String(v) : ''; }
      return d[col.key] != null ? String(d[col.key]) : '';
    };

    // Build flat single-row header
    const pdfTaskNameCol = visibleColumns.find(c => c.key === 'taskName');
    const pdfNonTaskCols = visibleColumns.filter(c => c.key !== 'taskName');
    const headerHtml = `<tr><th class="num">#</th>` +
      (pdfTaskNameCol ? `<th class="task-name">${esc(columnLabels['taskName'] || pdfTaskNameCol.label)}</th>` : '') +
      pdfNonTaskCols.map(col => `<th>${esc(columnLabels[col.key] || col.label)}</th>`).join('') +
      `</tr>`;

    // <colgroup>: use user-resized pixel widths where available, fall back to percentage-based estimates.
    // A3 landscape with 8mm margins gives ~1526px of content at 96dpi.
    // Size the # column to the widest task number so multi-digit numbers aren't clipped.
    const maxNumDigits = rows.reduce((m, r) => Math.max(m, String(exportRowNum(r) || '').length), 1);
    const NUM_COL_PX = Math.max(24, 10 + maxNumDigits * 8);
    const PDF_CONTENT_WIDTH = 1526;
    const availForCols = PDF_CONTENT_WIDTH - NUM_COL_PX;
    const colWidthsPx = visibleColumns.map(c => {
      const w = columnWidths[c.key] || c.width || '8%';
      if (typeof w === 'string' && w.endsWith('%')) return (parseFloat(w) / 100) * availForCols;
      return Math.max(20, parseFloat(w) || 80);
    });
    const totalColsPx = colWidthsPx.reduce((s, w) => s + w, 0);
    // Scale up uniformly if columns don't fill the page
    const pdfScale = totalColsPx < availForCols ? availForCols / totalColsPx : 1;
    const colgroupHtml = `<col style="width:${NUM_COL_PX}px">` +
      visibleColumns.map((_, i) => `<col style="width:${Math.round(colWidthsPx[i] * pdfScale)}px">`).join('');

    const pdfBodyPt = Number(scopePdfStyles?.body?.fontSize) || 9; // the PDF body size a 12px cell maps to
    const rowsHtml = rows.map((row, i) => {
      const isStage = row.indent === 0;
      const bg = STAGE_ROW_COLORS[row.key] || (isStage ? '#f4f4f0' : '#fff');
      const rowNum = exportRowNum(row);
      const isParent = exportIsParent(rows, i);
      const rowCells = visibleColumns.map(col => {
        const val = esc(getCellText(row, col));
        if (col.key === 'taskName') {
          // Indent by depth so parent/subtask grouping is visible (stage 5px, then +14px/level).
          const pl = isStage ? '5px' : `${6 + (row.indent || 1) * 14}px`;
          // Parent tasks get a chevron to mirror the grid's expand/collapse affordance.
          const chevron = isParent ? '<span style="color:#888">▾</span> ' : '';
          const fcss = cellFormatCss(cellFormatOf(timeline, row.key, col.key), pdfBodyPt);
          return `<td class="task-name" style="padding-left:${pl}${fcss ? ';' + fcss : ''}">${chevron}${val}</td>`;
        }
        const fcss = cellFormatCss(cellFormatOf(timeline, row.key, col.key), pdfBodyPt);
        return fcss ? `<td style="${fcss}">${val}</td>` : `<td>${val}</td>`;
      }).join('');
      const boldClass = timeline[row.key]?.bold ? ' class="bold"' : '';
      return `<tr${boldClass} style="background:${bg}"><td class="num">${rowNum}</td>${rowCells}</tr>`;
    }).join('');

    // Fonts from Accounting → Scope PDF styling (title / heading / body), Poppins as fallback.
    // Sizes are points, so the PDF prints at the size the settings page names.
    const cssFont = (style, fbSize) => {
      const size = Number(style?.fontSize) || fbSize;
      return `font-family:${pdfFontFamily(style)};font-size:${size}pt`;
    };
    const bodyCss = cssFont(scopePdfStyles?.body, 9);
    const headingCss = cssFont(scopePdfStyles?.heading, 8);
    const titleCss = cssFont(scopePdfStyles?.title, 10.5);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc(project.name || 'Timeline')}</title>
<style>
  ${pdfFontFaceCss()}
  *{box-sizing:border-box}
  body{${bodyCss};margin:0;padding:10px;color:#333}
  h2{${titleCss};font-weight:500;margin:0 0 8px}
  table{border-collapse:collapse;width:100%;table-layout:fixed}
  th,td{border:1px solid #e0e0e0;padding:3.5px 5px;vertical-align:middle;word-break:break-word;${bodyCss}}
  th{background:#fafaf8;${headingCss};font-weight:600;text-transform:uppercase;letter-spacing:.05em;text-align:center}
  td{text-align:center;font-weight:300}
  td.task-name{text-align:left}
  tr.bold td{font-weight:600}
  td.num,th.num{width:${NUM_COL_PX}px;min-width:${NUM_COL_PX}px;max-width:${NUM_COL_PX}px;text-align:center;color:#888;white-space:nowrap}
  @page{size:A3 landscape;margin:8mm}
</style></head><body>
<h2>${esc(project.name || 'Timeline')} — Stage Timeline</h2>
<table><colgroup>${colgroupHtml}</colgroup><thead>${headerHtml}</thead><tbody>${rowsHtml}</tbody></table>
</body></html>`;

    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) { alert('Allow pop-ups to export PDF.'); return; }
    win.document.write(html); win.document.close(); win.focus();
    printWhenFontsReady(win);
    setShowExportMenu(false);
  }, [displayRows, timeline, visibleColumns, columnLabels, columnWidths, stageDates, project.name, exportRowNum, exportPredLabel, exportTaskName, exportIsParent, scopePdfStyles, delayedBadgeFor, displayPercent, rolledCustomValue]);

  const handleExportExcel = useCallback(async (filteredRows) => {
    const rows = filteredRows || displayRows;
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Timeline');

      const getCellValue = (row, col) => {
        const d = timeline[row.key] || {};
        const rolled = rolledCustomValue(row.id, col.key);
        if (rolled) return rolled.value == null ? '' : String(rolled.value);
        if (col.key === 'taskName') return exportTaskName(row);
        if (col.key === 'predecessors') return exportPredLabel(d.predecessors);
        if (col.type === 'percent') { const v = displayPercent(row); return v != null ? String(v) : ''; }
        return d[col.key] != null ? String(d[col.key]) : '';
      };

      const hexToArgb = hex => 'FF' + hex.replace('#', '').toUpperCase();
      // Fonts from Accounting → Excel Export styling: style1 = body/detail (data rows),
      // style2 = subheading (header row), style3 = title/header (stage rows).
      const xs = excelExportStyles || {};
      const s1 = xs.style1 || { fontFamily: 'Calibri', fontSize: 9 };
      const s2 = xs.style2 || { fontFamily: 'Calibri', fontSize: 10 };
      const s3 = xs.style3 || { fontFamily: 'Calibri', fontSize: 11 };
      const bodyFont  = { size: Number(s1.fontSize) || 9,  name: s1.fontFamily || 'Calibri' };
      const hdrFont   = { bold: true, size: Number(s2.fontSize) || 10, name: s2.fontFamily || 'Calibri' };
      const stageFont = { bold: true, size: Number(s3.fontSize) || 11, name: s3.fontFamily || 'Calibri' };
      const hdrStyle = { font: hdrFont, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAF8' } }, alignment: { wrapText: true, horizontal: 'center', vertical: 'middle' } };
      const borderBottom = { border: { bottom: { style: 'thin', color: { argb: 'FFE5E5E5' } } } };

      // Serial-number column width sized to the widest task number so nothing is clipped.
      const xlMaxDigits = rows.reduce((m, r) => Math.max(m, String(exportRowNum(r) || '').length), 1);
      // Columns: no headers here — added manually via single header row below
      ws.columns = [
        { key: '_num', width: Math.max(5, xlMaxDigits + 2) },
        ...visibleColumns.map(c => ({
          key: c.key,
          width: c.key === 'taskName' ? 40 : (['remarks', 'delayCaused', 'assignedTo'].includes(c.key) ? 22 : 14),
        })),
      ];

      // Single flat header row
      const xlTaskNameCol = visibleColumns.find(c => c.key === 'taskName');
      const xlNonTaskCols = visibleColumns.filter(c => c.key !== 'taskName');
      // 1-based column indices
      const xlColIdx = { _num: 1 };
      let xlCi = 2;
      if (xlTaskNameCol) xlColIdx[xlTaskNameCol.key] = xlCi++;
      xlNonTaskCols.forEach(col => { xlColIdx[col.key] = xlCi++; });

      const hdrRow = ws.addRow({ _num: '#', ...(xlTaskNameCol ? { [xlTaskNameCol.key]: columnLabels['taskName'] || xlTaskNameCol.label } : {}), ...Object.fromEntries(xlNonTaskCols.map(col => [col.key, columnLabels[col.key] || col.label])) });
      hdrRow.height = 18;
      hdrRow.eachCell(cell => { Object.assign(cell, hdrStyle); Object.assign(cell, borderBottom); });

      // Data rows
      rows.forEach((row, i) => {
        const isStage = row.indent === 0;
        const rowNum = exportRowNum(row);
        const isParent = exportIsParent(rows, i);

        const values = { _num: rowNum };
        visibleColumns.forEach(col => {
          const v = getCellValue(row, col);
          // Parent tasks get a chevron prefix to mirror the grid's expand/collapse affordance;
          // subtask indentation is applied separately via the cell alignment indent below.
          values[col.key] = (col.key === 'taskName' && isParent) ? `▾ ${v}` : v;
        });

        const wsRow = ws.addRow(values);
        wsRow.height = isStage ? Math.max(22, stageFont.size * 1.5) : Math.max(16, bodyFont.size * 1.6);

        // Task name cell: ExcelJS indent for deliverables, wrap for all
        const taskColIdx = xlColIdx['taskName'] || 0;
        if (taskColIdx > 0) {
          wsRow.getCell(taskColIdx).alignment = {
            indent: row.indent * 2, wrapText: true, vertical: 'middle', horizontal: 'left',
          };
        }

        // Apply to all other cells
        wsRow.eachCell((cell, colIdx) => {
          if (colIdx === taskColIdx) return; // already set above
          if (!cell.alignment) cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
          else cell.alignment = { ...cell.alignment, wrapText: true, vertical: 'middle' };
        });

        if (isStage) {
          const stageBg = hexToArgb(STAGE_ROW_COLORS[row.key] || '#f4f4f0');
          wsRow.font = stageFont;
          wsRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stageBg } };
          // Row-number cell for stage is blank and not bold
          wsRow.getCell(1).font = { size: stageFont.size, name: stageFont.name };
        } else {
          wsRow.font = timeline[row.key]?.bold ? { ...bodyFont, bold: true } : bodyFont;
        }
        // Format toolbar: each cell's own bold / italic / underline / size / alignment.
        visibleColumns.forEach(col => {
          const ci = xlColIdx[col.key];
          if (!ci) return;
          const f = cellFormatOf(timeline, row.key, col.key);
          if (!f.i && !f.u && !f.size && !f.align && f.b === undefined) return;
          const cell = wsRow.getCell(ci);
          const base = cell.font || wsRow.font || bodyFont;
          cell.font = {
            ...base,
            ...(f.b !== undefined ? { bold: f.b } : {}),
            ...(f.i ? { italic: true } : {}),
            ...(f.u ? { underline: true } : {}),
            ...(f.size ? { size: Math.round(((base.size || bodyFont.size) * f.size / FMT_BASE_SIZE) * 2) / 2 } : {}),
          };
          if (f.align) cell.alignment = { ...(cell.alignment || {}), horizontal: f.align };
        });
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${project.name || 'timeline'}_timeline.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err); alert('Excel export failed: ' + err.message);
    }
    setShowExportMenu(false);
  }, [displayRows, timeline, visibleColumns, columnLabels, stageDates, project.name, exportRowNum, exportPredLabel, exportTaskName, exportIsParent, excelExportStyles, displayPercent, rolledCustomValue]);

  // ── Export stage selection ────────────────────────────────────────────────
  // Flat list of stage options (indent===0 rows) for the export checklist modal.
  // Stage list for the export picker — every main stage AND part stage currently visible on
  // the page (built from displayRows so it mirrors tab filtering and active sub-stages).
  const exportStageOptions = useMemo(() => {
    const opts = [];
    for (const row of displayRows) {
      if (row.indent === 0) {
        opts.push({ key: row.id, name: timeline[row.id]?.taskName || row.defaultLabel || row.name || row.id, isPart: false });
      } else if (row.isSubStageHeader) {
        opts.push({ key: row.id, name: row.name || row.defaultLabel || row.id, isPart: true });
      }
    }
    return opts;
  }, [displayRows, timeline]);

  // Opens the stage-selection modal for the given export type.
  const openExportModal = useCallback((type) => {
    setShowExportMenu(false);
    setExportSubOpen(null);
    setExportStageModal({ type, selectedStages: new Set(exportStageOptions.map(s => s.key)) });
  }, [exportStageOptions]);

  // Filters displayRows to the selected stage keys then runs the chosen export.
  const runExport = useCallback((type, selectedStages) => {
    let filtered = displayRows;
    if (selectedStages && selectedStages.size < exportStageOptions.length) {
      // Mirror the tab-filter logic: a stage is a visible container if it or any of its parts
      // is selected; part headers / part-system rows / part-assigned tasks gate on the part key;
      // stage-level rows gate on the stage itself.
      const out = [];
      let curStage = null, stageSel = false, stageVisible = false;
      for (const row of displayRows) {
        if (row.indent === 0) {
          curStage = row.id;
          stageSel = selectedStages.has(row.id);
          const anyPart = [...selectedStages].some(k => k.startsWith(`substage_${row.id}_`));
          stageVisible = stageSel || anyPart;
          if (stageVisible) out.push(row);
          continue;
        }
        if (!stageVisible) continue;
        if (row.isSubStageHeader || row.isPartSystem) {
          const sk = row.isSubStageHeader ? row.id : row.id.replace(/^(start_|invoicing_|closure_)/, '');
          if (sk.startsWith('substage_') && !selectedStages.has(sk)) continue;
          out.push(row); continue;
        }
        const sid = timeline[row.id]?._subStageId;
        if (sid && curStage) {
          if (!selectedStages.has(`substage_${curStage}_${sid}`)) continue;
          out.push(row); continue;
        }
        if (!stageSel) continue;
        out.push(row);
      }
      filtered = out;
    }
    setExportStageModal(null);
    if (type === 'pdf') handleExportPdf(filtered);
    else if (type === 'excel') handleExportExcel(filtered);
    else handleExportCsv(filtered);
  }, [displayRows, exportStageOptions.length, timeline, handleExportPdf, handleExportExcel, handleExportCsv]);

  const handleImportFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !supabaseProjects) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const sep = text.includes('\t') ? '\t' : ',';
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) return;
        const newTimeline = { ...timeline };
        lines.slice(1).forEach((line, idx) => {
          const cols = sep === ',' ? line.match(/(".*?"|[^,\n]+)(?=,|$)/g) || line.split(',') : line.split('\t');
          const clean = c => String(c || '').replace(/^"|"$/g, '').replace(/""/g, '"').trim();
          const row = displayRows[idx];
          if (!row) return;
          const rowData = newTimeline[row.key] ? { ...newTimeline[row.key] } : {};
          // cols[0]=# cols[1]=taskName cols[2]=plannedDuration cols[3]=plannedStartDate
          // cols[4]=plannedEndDate(skip-computed) cols[5]=predecessors cols[6]=assignedTo
          // cols[7]=remarks cols[8]=delayCaused cols[9]=scheduleStatus cols[10]=percentComplete
          if (cols[2] !== undefined && clean(cols[2]) !== '') rowData.plannedDuration = Number(clean(cols[2])) || null;
          if (cols[3] !== undefined && clean(cols[3]) !== '') rowData.plannedStartDate = clean(cols[3]);
          if (cols[5] !== undefined) rowData.predecessors = clean(cols[5]);
          if (cols[6] !== undefined) rowData.assignedTo = clean(cols[6]);
          if (cols[7] !== undefined) rowData.remarks = clean(cols[7]);
          if (cols[8] !== undefined) rowData.delayCaused = clean(cols[8]);
          if (cols[9] !== undefined && clean(cols[9]) !== '') rowData.scheduleStatus = clean(cols[9]);
          if (cols[10] !== undefined && clean(cols[10]) !== '') rowData.percentComplete = Number(clean(cols[10])) || null;
          newTimeline[row.key] = rowData;
        });
        writeTimelineDiff(supabaseProjects, project.id, timeline, newTimeline, { allowTracking: syncEnabledRef.current });
      } catch (err) {
        console.error('Import failed:', err);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
    setShowImportMenu(false);
  }, [displayRows, timeline, project.id, supabaseProjects]);

  // ── Snap column widths to pixels ───────────────────────────────────────
  // expandingKeys:  keys about to become visible (need a default px width).
  // collapsingKeys: keys about to be hidden (excluded from new tableMinWidthPx).
  const snapToPixels = useCallback((expandingKeys = [], collapsingKeys = []) => {
    if (!tableRef.current) return;
    const collapsingSet = new Set(collapsingKeys);
    const updates = {};      // pixel widths for ALL columns (stored in columnWidths)
    const allVisible = {};   // pixel widths for ALL visible columns (used for total)

    tableRef.current.querySelectorAll('th[data-col-key]').forEach(th => {
      const k = th.getAttribute('data-col-key');
      if (!k) return;
      const w = th.offsetWidth; // always use DOM width for accurate total
      allVisible[k] = w;
      // Snap every column to px (avoids percentage columns shifting during expand/collapse)
      if (!columnWidths[k]) updates[k] = w;
    });
    // Give expanding (currently hidden) columns their last known or canonical px width
    const colDefMap = new Map(SCHEDULE_COLUMNS.map(c => [c.key, parseFloat(c.width) || 0]));
    expandingKeys.forEach(k => {
      const stored = columnWidths[k];
      const w = typeof stored === 'number' ? stored : (stored ? parseFloat(stored) : null) || colDefMap.get(k) || 80;
      allVisible[k] = w;
      if (!stored) updates[k] = w;
    });
    setColumnWidths(prev => ({ ...prev, ...updates }));
    // Table minWidth = 30px (# col) + sum of all columns that will REMAIN visible after this action.
    const remainingTotal = 30 + Object.entries(allVisible)
      .filter(([k]) => !collapsingSet.has(k))
      .reduce((s, [, w]) => s + (typeof w === 'number' ? w : (parseFloat(w) || 80)), 0);
    setTableMinWidthPx(remainingTotal);
  }, [columnWidths]);

  // ── Cell styles ───────────────────────────────────────────────────────
  const cellStyle = (col, isHeader) => {
    // noResize columns always use the definition width — saved overrides are ignored so SCHEDULE_COLUMNS stays authoritative
    const w = col.noResize ? col.width : (columnWidths[col.key] || col.width);
    // All non-date data cells wrap text so content stays visible during column resize
    const isWrapCol = col.type !== 'date';
    return {
      ...(isHeader ? thStyle : tdStyle),
      // Header cells are always center-aligned; data cells use left/center by col type
      textAlign: isHeader ? 'center' : (((col.type === 'text' && col.key !== 'predecessors') || col.key === 'taskName') ? 'left' : 'center'),
      width: w,
      ...(col.minWidth ? { minWidth: col.minWidth } : {}),
      ...(isHeader ? {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflow: 'hidden',
        position: 'relative',
        userSelect: 'none',
      } : {
        // All non-date cells wrap; date cells need visible overflow for picker dropdown
        whiteSpace: isWrapCol ? 'normal' : 'nowrap',
        wordBreak: isWrapCol ? 'break-word' : 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        verticalAlign: 'middle',
      }),
      ...(col.sticky ? {
        position: 'sticky', left: '30px', zIndex: isHeader ? 2 : 1,
        background: isHeader ? BRIO_BG : '#fff',
        // Use box-shadow for all borders on sticky cells — avoids the scroll-repaint glitch
        // where borderRight drops out of the compositing layer during vertical scroll.
        // Header keeps its own borderBottom; body uses inset box-shadow for both borders.
        borderRight: 'none',
        borderBottom: isHeader ? '1px solid ' + BRIO_BORDER : 'none',
        boxShadow: isHeader
          ? '1px 0 0 0 ' + BRIO_BORDER
          : '1px 0 0 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER,
      } : {}),
      ...(col.key === 'taskName' && !isHeader ? { paddingRight: '6px' } : {}),
      ...(col.type === 'date' && !isHeader ? { padding: '2px 3px' } : {}),
      ...(col.key === 'plannedDuration' && !isHeader ? { padding: '2px 2px' } : {}),
      ...((col.key === 'assignedTo' || (col.custom && (col.type === 'singleSelect' || col.type === 'multiSelect'))) && !isHeader ? { padding: '2px 3px' } : {}),
      ...(col.key === 'scheduleStatus' && !isHeader ? { padding: '2px 3px' } : {}),
      ...(col.key === 'percentComplete' && !isHeader ? { padding: '2px 2px' } : {}),
    };
  };

  // ── Cell renderer ─────────────────────────────────────────────────────
  const renderCell = (row, col) => {
    const rowData = timeline[row.key] || {};
    const cellKey = `${row.key}:${col.key}`;
    const isSavingCell = saving === cellKey;
    const isSaved = savedCells.has(cellKey);
    // Parent/summary task row — its duration + start/end dates are rolled up from its children by the
    // dependency cascade, so those cells render read-only. Only when dependencies are enabled (the rollup
    // that derives them); with dependencies off there's no derivation and the cells stay editable.
    const isSummaryRow = summaryRowIds.has(row.id) && projectSettings.dependencies.enabled;

    // Sub-stage header rows only show aggregated data for date/number/percent columns.
    // All other column types (status, text, select) render nothing.
    if (row.isSubStageHeader && col.key !== 'taskName' &&
        col.type !== 'date' && col.type !== 'number' && col.type !== 'percent') {
      return null;
    }

    const inputBase = {
      fontFamily: 'Raleway, sans-serif',
      fontSize: '12px',
      fontWeight: 300,
      border: '1px solid transparent',
      borderRadius: '0',
      background: 'transparent',
      padding: '2px 4px',
      outline: 'none',
      opacity: isSavingCell ? 0.5 : 1,
    };

    const savedMark = null; // confirmation tick removed

    // Task name — with indent, expand/collapse for parents
    if (col.key === 'taskName') {
      const val = rowData.taskName || row.defaultLabel || row.name;
      // Carry-forward decorations: non-editable origin-stage prefix + "- CF" suffix (helper font)
      const isCF = !!rowData._isCarriedForward;
      const cfPrefix = isCF && rowData._cfFromLabel ? `${rowData._cfFromLabel}_ ` : '';
      const cfPrefixEl = cfPrefix ? <span style={{ color: '#555', fontWeight: 400 }}>{cfPrefix}</span> : null;
      const cfSuffixEl = isCF ? <span style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', marginLeft: '3px' }}>- CF</span> : null;
      // SOW deliverable number — a helper-styled "(N)" suffix on tasks read from a Scope of Work
      // deliverable (deliverable rows, and workflow tasks explicitly linked to a deliverable).
      // Write-up-derived tasks and meeting rows carry no sowRef and show no suffix.
      const sowRefVal = rowData.sowRef;
      const hasSowRef = sowRefVal !== undefined && sowRefVal !== null && sowRefVal !== '';
      // A row linked to its stage's info text (sentinel sowRef) rather than a numbered deliverable.
      // No inline badge is drawn for it — the scope-fill cell highlight already marks it as linked.
      const isStageRef = String(sowRefVal) === SOW_STAGE_REF;
      // A freshly appended project deliverable that no existing task covered — carries a "new" review badge.
      const isSowNew = rowData._sowNew === true;
      const sowRefTitle = isStageRef ? 'Linked to the stage’s info text' : `Scope of Work deliverable ${sowRefVal}`;
      // Both link badges act as buttons when a SOW is available and the row is editable: clicking opens
      // a small add/change/remove-link menu (sowLinkMenu). Inert (plain text) otherwise.
      const badgeInteractive = !!linkSowTemplate && canEdit && row.indent > 0
        && !row.id.startsWith('start_') && !row.id.startsWith('invoicing_') && !row.id.startsWith('closure_');
      const openLinkMenu = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setSowLinkMenu({
          x: rect.left, y: rect.bottom + 3,
          rowId: row.id,
          rowName: rowData.taskName || row.defaultLabel || row.name || '',
          rowStageKey: rowStageKeyById.get(row.id) || null,
          sowRef: rowData.sowRef != null ? rowData.sowRef : null,
          hasLink: (rowData.sowRef != null && rowData.sowRef !== '') || rowData._sowUnlinked === true,
        });
      };
      // Numbered "(N)" badge — only for a real deliverable link. Stage links show no text (highlight
      // alone), and "new" rows use the dedicated badge below instead of a number.
      const sowRefEl = (hasSowRef && !isStageRef && !isSowNew) ? (
        <span
          title={badgeInteractive ? 'Click to change or remove this SOW link' : sowRefTitle}
          onClick={badgeInteractive ? openLinkMenu : undefined}
          style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px', cursor: badgeInteractive ? 'pointer' : 'default' }}
        >
          ({sowRefVal})
        </span>
      ) : null;
      // Hover tooltip for highlighted Scope-of-Work tasks (deliverable rows + workflow tasks linked
      // to a deliverable). Applied to both the read-only name and the editable name field.
      const isReconcileError = !!(reconcileErrorRowIds && reconcileErrorRowIds.has(row.id));
      const isSowSource = timeline[row.id]?.sowSource === true;
      const sowHoverTitle = isReconcileError
        ? 'Reconciliation error — the SOW deliverable this task was linked to no longer exists in the selected SOW template. Re-link it or update the task.'
        : (hasSowRef
          ? sowRefTitle
          : ((row.id.startsWith('deliv_') || isSowSource) ? 'SOW task' : undefined));
      // "new" review badge — an appended project deliverable no existing task covered. Clicking it opens
      // the same add/change/remove-link menu so the user can decide whether to keep or remove the link.
      const sowNewEl = isSowNew ? (
        <span
          title={badgeInteractive ? 'New — added from this project’s SOW, not matched to an existing task. Click to change or remove its link.' : 'New — added from this project’s Scope of Work'}
          onClick={badgeInteractive ? openLinkMenu : undefined}
          style={{ fontSize: '8.5px', fontWeight: 500, color: '#2f7d55', background: '#e9f5ee', border: '1px solid #bfe2cd', borderRadius: '2px', padding: '0 4px', fontFamily: 'Raleway, sans-serif', lineHeight: '1.5', whiteSpace: 'nowrap', cursor: badgeInteractive ? 'pointer' : 'default' }}
        >
          new{badgeInteractive ? ' ▾' : ''}
        </span>
      ) : null;
      const isStage = row.indent === 0;
      const indentPx = row.indent * 10 + ((subStageIndentRows.has(row.id) || row.isPartSystem) ? 10 : 0);
      // Find this row's index in allRows to check for children
      const allIdx = allRows.findIndex(r => r.id === row.id);
      // Sub-stage headers have collapsible children (their Start/Invoicing/Closure + tasks); an Advance
      // part has no system rows, so only its own tasks. A stage with parts always has children, even
      // with no rows of its own (Advance usually has none), since its parts are inserted for display.
      const hasKids = row.isSubStageHeader
        ? (!isAdvanceStage(row.stageId) || allRows.some(r => r.indent > 0 && timeline[r.id]?._subStageId === row.partId))
        : (allIdx !== -1 && (rowHasChildren(allRows, allIdx) || (isStage && (partStagesMap[row.id] || []).length > 0)));
      const isCollapsed = collapsedRows.has(row.id);
      // First stage in the row list — where the "Add contract dates" button lives
      const firstStageIdx = allRows.findIndex(r => r.indent === 0);
      const isFirstStage = isStage && allIdx === firstStageIdx;

      const toggleBtn = hasKids ? (
        <span
          onClick={(e) => { e.stopPropagation(); toggleCollapse(row.id); }}
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', flexShrink: 0, color: BRIO_GREY, fontSize: '12px', userSelect: 'none' }}
        >
          {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
        </span>
      ) : (
        <span style={{ width: '13px', flexShrink: 0, display: 'inline-block' }} />
      );

      // Scope rows (stages, deliv_, closure_) are always read-only for task name — EXCEPT in the
      // Template Builder, where deliverable (deliv_) rows can be renamed inline (a rename override,
      // like the template's own `rename=`), so a template author can retitle SOW deliverables.
      // user-added rows (row_) require canEdit; meeting rows are renameable by all users.
      const isTemplateBuilder = String(project.id) === TEMPLATE_PROJECT_ID;
      const isNameEditable = (canEditCell(row, 'taskName') && row.id.startsWith('row_')) || row.id.startsWith('meeting_')
        || (canEdit && isTemplateBuilder && row.id.startsWith('deliv_'));
      // SOW-linked deliverable rows (deliv_ / SOW-source, or a row carrying a numbered SOW link) render
      // read-only above, but an admin/supervisor may APPEND to their name. The SOW name stays as an
      // immutable prefix and the addition is stored after a ": " — e.g. "Space planning" → "Space
      // planning: Zoning". The base is captured in _sowNameBase so the prefix is locked on later edits.
      const isSowDeliverableRow = row.indent > 0 && !isTemplateBuilder
        && !row.id.startsWith('start_') && !row.id.startsWith('invoicing_') && !row.id.startsWith('closure_')
        && !isCustomStage(row.id)
        && (row.id.startsWith('deliv_') || isSowSource || (hasSowRef && !isStageRef));
      const sowAddonEditable = canEdit && isSowDeliverableRow;
      const sowBase = rowData._sowNameBase || rowData.taskName || row.defaultLabel || row.name || '';
      const sowAddon = (sowBase && rowData.taskName && rowData.taskName.startsWith(sowBase + ': '))
        ? rowData.taskName.slice((sowBase + ': ').length)
        : '';
      const saveSowAddon = async () => {
        const addon = sowAddonValue.trim();
        const newName = addon ? `${sowBase}: ${addon}` : sowBase;
        setEditingSowAddon(null);
        if (newName !== (rowData.taskName || '') || (addon && rowData._sowNameBase !== sowBase)) {
          await handleFieldChange(row.key, 'taskName', newName, { _sowNameBase: sowBase });
        }
      };
      // Disc: shown when task is linked AND the project task still exists (not deleted)
      const hasProjectTaskLink = !!(rowData._projectTaskId) && linkedTaskIds.has(rowData._projectTaskId);
      const discIndicator = hasProjectTaskLink ? (
        <span style={{ position: 'absolute', top: '2px', right: '2px', width: '4px', height: '4px', borderRadius: '50%', background: BRIO_GOLD, display: 'inline-block', pointerEvents: 'none' }} />
      ) : null;

      // Stage row: static "N parts" indicator when this stage has part-stages. Parts always render as
      // grouped rows automatically now (no per-part "show" checkbox), so this is purely informational.
      const stageParts = isStage ? (partStagesMap[row.id] || null) : null;
      const partsBtn = (stageParts && stageParts.length) ? (
        <span style={{ marginLeft: 'auto', flexShrink: 0, padding: '1px 3px', fontSize: '9px', fontWeight: 400, color: BRIO_GOLD, whiteSpace: 'nowrap' }}>
          {stageParts.length} {stageParts.length === 1 ? 'part' : 'parts'}
        </span>
      ) : null;

      const subStageBadge = null; // grouping shown via sub-stage header rows, not inline badges

      // Derivative-template per-stage base-sync controls — a "Sync now" icon + a ▾ column checklist,
      // pinned to the right of the stage name. Only on real (non-custom) stage rows of a "created from"
      // template; a custom stage has no base counterpart to sync.
      const syncStageControls = (canSync && isStage && isDerivativeTemplate && !isCustomStage(row.id)) ? (() => {
        const st = row.id;
        const active = syncStageActive(syncPrefs, st);
        const menuOpen = syncMenuStage?.stageId === st;
        return (
          <span style={{ marginLeft: partsBtn ? '4px' : 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '1px', position: 'relative' }}>
            <span
              onMouseDown={e => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); if (active) handleSyncStage(st); }}
              title={active ? 'Sync this stage from the base template now' : 'All columns are off — this stage is frozen (nothing to sync)'}
              style={{ cursor: active ? 'pointer' : 'not-allowed', color: active ? BRIO_GOLD : BRIO_GREY, opacity: active ? 0.85 : 0.4, display: 'inline-flex', alignItems: 'center', padding: '1px 2px', borderRadius: '2px' }}
              onMouseEnter={e => { if (active) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(159,144,42,0.10)'; } }}
              onMouseLeave={e => { e.currentTarget.style.opacity = active ? '0.85' : '0.4'; e.currentTarget.style.background = 'transparent'; }}
            >
              <RefreshCw size={11} />
            </span>
            <span
              onMouseDown={e => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setSyncMenuStage(prev => prev?.stageId === st ? null : { stageId: st, x: r.right, y: r.bottom + 3 }); }}
              title="Choose which columns sync from the base template"
              style={{ cursor: 'pointer', color: menuOpen ? BRIO_GOLD : BRIO_GREY, display: 'inline-flex', alignItems: 'center', padding: '1px', borderRadius: '2px', borderBottom: '1px solid ' + (menuOpen ? BRIO_GOLD : 'transparent') }}
            >
              <ChevronDown size={11} />
            </span>
          </span>
        );
      })() : null;

      // M.O.M icon — shown for non-stage, non-sub-stage-header rows that have a MOM doc
      const hasMOMDoc = momHook.documents.some(d => d.task_key === row.key);
      const momIconBtn = (hasMOMDoc && !isStage && !row.isSubStageHeader) ? (
        <span
          onClick={e => { e.stopPropagation(); setMOMPanel({ rowId: row.id, rowKey: row.key, rowName: rowData.taskName || row.defaultLabel || row.name || '' }); }}
          title="View M.O.M"
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: BRIO_GOLD, padding: '1px 2px', borderRadius: '2px' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(159,144,42,0.10)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <FileText size={11} />
        </span>
      ) : null;

      if (!isNameEditable) {
        const isStageOrSub = isStage || row.isSubStageHeader;
        const isStripCollapsed = isStageOrSub && collapsedStripRows.has(row.id);
        const stripToggleBtn = isStageOrSub ? (
          <span
            onClick={(e) => { e.stopPropagation(); toggleStripCollapse(row.id); }}
            title={isStripCollapsed ? 'Expand date strip' : 'Collapse date strip'}
            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: BRIO_GREY, padding: '1px 2px', borderRadius: '2px' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Minus size={10} />
          </span>
        ) : null;
        // Delayed / Undo / Stop / Manual Override buttons — on start_ rows when linked invoice is overdue
        const delayedAndOverrideBtns = (() => {
          if (!row.id.startsWith('start_')) return null;
          const myStageKey = row.id.slice('start_'.length);
          const isPartStart = myStageKey.startsWith('substage_');
          const rd0 = timeline[row.id] || {};
          const manualLink0 = manualStartLink(row.id, rd0);
          // Part start rows show delay status only when manually linked to a stage/part.
          // Main start rows fall back to the immediately previous stage.
          let prevSk = null;
          if (isPartStart) {
            if (!manualLink0) return null;
          } else {
            const myStageIdx = allRows.findIndex(r => r.id === myStageKey);
            if (myStageIdx <= 0) return null;
            let prevStageIdx = myStageIdx - 1;
            while (prevStageIdx >= 0 && allRows[prevStageIdx].indent !== 0) prevStageIdx--;
            if (prevStageIdx < 0) return null;
            prevSk = allRows[prevStageIdx].id;
            if (isAdvanceStage(prevSk)) prevSk = null; // no preset link on Advance
          }
          const linkedSk = manualLink0 || prevSk;
          if (!linkedSk) return null;
          // If this main start row only links to the stage by DEFAULT (no manual link) and a part
          // of this stage is explicitly linked to that same stage, show the delay on the part only.
          if (!isPartStart && !manualLink0) {
            const parts = partStagesMap[myStageKey] || [];
            const partLinkedToSame = parts.some(p => (timeline[`start_substage_${myStageKey}_${p.id}`]?._manualStartLinkedStage) === linkedSk);
            if (partLinkedToSame) return null;
          }
          const prevInvRow = timeline[`invoicing_${linkedSk}`] || {};
          // For part-stage links the paid/invoice dates live on the part object.
          let linkedPaid, linkedInvoiceDate;
          if (linkedSk && linkedSk.startsWith('substage_')) {
            const pki = partKeyInfo.get(linkedSk);
            const part = pki ? (partStagesMap[pki.stageId] || []).find(p => p.id === pki.partId) : null;
            linkedPaid = part?.paidDate; linkedInvoiceDate = part?.invoiceDate;
          } else {
            const prevStageData = timeline[linkedSk] || {};
            linkedPaid = prevStageData.paidDate; linkedInvoiceDate = prevStageData.invoiceDate;
          }
          // The payment window ends invoice date + terms (the invoicing row's own end shows the paid date
          // once paid). The buttons show while payment is past it, and stay once it was paid late.
          const refDate = prevInvRow.plannedStartDate
            ? (addCalendarDaysStr(prevInvRow.plannedStartDate, prevInvRow.plannedDuration != null ? Number(prevInvRow.plannedDuration) : (paymentTermsDays != null ? Number(paymentTermsDays) : 0)) || prevInvRow.plannedEndDate || null)
            : (prevInvRow.plannedEndDate || linkedInvoiceDate || null);
          if (!refDate) return null;
          if (linkedPaid) {
            if (parseDate(linkedPaid) <= parseDate(refDate)) return null;
          } else if (Date.now() <= new Date(refDate + 'T00:00:00').getTime() + DAY_MS) return null;

          // Determine if auto-delay has been applied (dep is FS-based on the linked invoicing row)
          const predCol = projectSettings.dependencies.predecessorColumn;
          const invTN = taskNumMap[`invoicing_${linkedSk}`];
          const currentDep = (timeline[row.id] || {})[predCol] || '';
          const isDelayApplied = !!invTN && currentDep.startsWith(`${invTN}FS`);
          const isStopped = !!(timeline[row.id] || {})._delayAutoStopped;

          const closureRowId = `closure_${linkedSk}`;
          const btnBase = { flexShrink: 0, display: 'inline-flex', alignItems: 'center', padding: '1px 5px', fontSize: '10px', fontWeight: 400, letterSpacing: '0.02em', borderRadius: '2px', lineHeight: '1.4', userSelect: 'none' };

          // The three buttons are one choice: clicking any of them switches straight over, with no extra
          // click to release Ignore first. "Delayed": soft-filled when auto-delay is active, clickable to
          // force-apply. While Ignore is on it shows greyed, and a click applies the delay, releasing Ignore.
          const delayedBg = isDelayApplied ? 'rgba(220,53,69,0.10)' : 'transparent';
          const delayedBtn = isStopped ? (
            <span
              onClick={canEdit ? (e) => { e.stopPropagation(); handleApplyDelayedDep(row.id); } : undefined}
              title={canEdit ? 'Delay ignored. Click to apply the delay instead.' : 'Delay ignored.'}
              style={{ ...btnBase, color: BRIO_GREY, border: `0.5px solid ${BRIO_BORDER}`, background: 'transparent', opacity: 0.6, cursor: canEdit ? 'pointer' : 'default' }}
              onMouseEnter={canEdit ? (e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = BRIO_RED; e.currentTarget.style.borderColor = BRIO_RED; }) : undefined}
              onMouseLeave={canEdit ? (e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = BRIO_GREY; e.currentTarget.style.borderColor = BRIO_BORDER; }) : undefined}
            >
              Delayed
            </span>
          ) : canEdit ? (
            <span
              onClick={(e) => { e.stopPropagation(); handleApplyDelayedDep(row.id); }}
              title={linkedPaid
                ? (isDelayApplied ? 'Paid late: start pushed by the days the payment was late' : 'Paid late: click to push start of stage by the days the payment was late')
                : (isDelayApplied ? 'Auto-delay active — click to re-apply with current lag' : 'Payment is delayed — click to push start of stage to today')}
              style={{ ...btnBase, color: BRIO_RED, border: `0.5px solid ${BRIO_RED}`, background: delayedBg, cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,53,69,0.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = delayedBg; }}
            >
              Delayed
            </span>
          ) : (
            <span title="Payment is delayed beyond invoicing terms" style={{ ...btnBase, color: BRIO_RED, border: `0.5px solid ${BRIO_RED}`, background: delayedBg }}>
              Delayed
            </span>
          );

          // "Ignore" button: toggle — pressed style when active, prevents auto-delay effect
          const stopPressed = isStopped;
          const stopBtn = canEdit ? (
            <span
              onClick={(e) => { e.stopPropagation(); handleToggleDelayStop(row.id, closureRowId, linkedSk); }}
              title="Click button to ignore / undo delay"
              style={{
                ...btnBase,
                color: stopPressed ? '#555' : BRIO_GREY,
                border: `0.5px solid ${stopPressed ? '#888' : BRIO_BORDER}`,
                background: stopPressed ? 'rgba(60,60,60,0.10)' : 'transparent',
                boxShadow: stopPressed ? 'inset 0 1px 3px rgba(0,0,0,0.18)' : 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { if (!stopPressed) { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = '#aaa'; } }}
              onMouseLeave={e => { if (!stopPressed) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = BRIO_BORDER; } }}
            >
              Ignore
            </span>
          ) : null;

          // "Manual Override" button
          const overrideBtn = canEdit ? (
            <span
              onClick={(e) => {
                e.stopPropagation();
                // Choosing Manual Override takes back an Ignore (Start row and Closure badge) first.
                if (isStopped) handleToggleDelayStop(row.id, closureRowId, linkedSk);
                setManualOverrideMenu({ x: e.clientX, y: e.clientY, closureRowId, stageKey: linkedSk, refDate, manualDays: '' });
              }}
              title="Manual override — manually push consecutive stage timelines"
              style={{ ...btnBase, color: BRIO_GREY, border: `0.5px solid ${BRIO_BORDER}`, background: 'transparent', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#aaa'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = BRIO_BORDER; }}
            >
              Manual Override
            </span>
          ) : null;

          return <>{delayedBtn}{stopBtn}{overrideBtn}</>;
        })();

        const nameOnly = (
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', fontSize: '12px', fontWeight: isStage ? 700 : 300, paddingLeft: indentPx + 'px', paddingRight: (hasProjectTaskLink || hasSowRef) ? '12px' : undefined, lineHeight: '1.3' }}>
            {/* Top row: toggle + name + icons — name always fully visible */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '3px' }}>
              <span style={{ flexShrink: 0 }}>{toggleBtn}</span>
              {(subStageIndentRows.has(row.id) || row.isPartSystem) && (
                <span style={{ flexShrink: 0, fontSize: '10px', color: BRIO_GREY, opacity: 0.6, lineHeight: 1, marginRight: '1px' }}>↳</span>
              )}
              {subStageBadge}
              {isCustomStage(row.id) && editingCustomStageName === row.id ? (
                <input
                  autoFocus
                  value={customStageNameValue}
                  onChange={e => setCustomStageNameValue(e.target.value)}
                  onBlur={async () => {
                    const trimmed = customStageNameValue.trim();
                    if (trimmed) await handleFieldChange(row.key, 'taskName', trimmed);
                    setEditingCustomStageName(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
                    if (e.key === 'Escape') { setEditingCustomStageName(null); }
                  }}
                  onClick={e => e.stopPropagation()}
                  style={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 700, fontFamily: 'Raleway, sans-serif', border: 'none', borderBottom: '1px solid ' + BRIO_GOLD, outline: 'none', background: 'transparent', padding: '0 2px', lineHeight: '1.3' }}
                />
              ) : (sowAddonEditable && editingSowAddon === row.id) ? (
                <span onClick={e => e.stopPropagation()} style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333' }}>{sowBase}: </span>
                  <input
                    autoFocus
                    value={sowAddonValue}
                    onChange={e => setSowAddonValue(e.target.value)}
                    onBlur={saveSowAddon}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } if (e.key === 'Escape') { setEditingSowAddon(null); } }}
                    placeholder="add note…"
                    style={{ flex: 1, minWidth: '60px', fontSize: '12px', fontWeight: 300, fontFamily: 'Raleway, sans-serif', border: 'none', borderBottom: '1px solid ' + BRIO_GOLD, outline: 'none', background: 'transparent', padding: '0 2px', lineHeight: '1.3' }}
                  />
                </span>
              ) : (
                <span
                  title={isCustomStage(row.id) ? 'Double-click to rename' : (sowAddonEditable ? 'Double-click to add a note after the SOW name (the SOW name stays as a fixed prefix)' : sowHoverTitle)}
                  onDoubleClick={
                    isCustomStage(row.id) && canEdit ? (e) => {
                      e.stopPropagation();
                      const currentName = timeline[row.id]?.taskName || row.defaultLabel || row.name || '';
                      setCustomStageNameValue(currentName);
                      setEditingCustomStageName(row.id);
                    }
                    : sowAddonEditable ? (e) => {
                      e.stopPropagation();
                      setSowAddonValue(sowAddon);
                      setEditingSowAddon(row.id);
                    }
                    : undefined
                  }
                  style={{ flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', cursor: (isCustomStage(row.id) && canEdit) || sowAddonEditable ? 'text' : undefined }}
                >
                  {cfPrefixEl}{val}{cfSuffixEl}
                  {row.fromTracking && !isCustomStage(row.id) && (
                    <span
                      title="Read from the Tracking page (not in the scope of work)"
                      style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px' }}
                    >
                      -TR
                    </span>
                  )}
                  {isCustomStage(row.id) && !isStandalone && (
                    <span
                      title="Manually added stage (not in the scope of work)"
                      style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', marginLeft: '3px' }}
                    >
                      -N
                    </span>
                  )}
                </span>
              )}
              {momIconBtn}
              {partsBtn}
              {syncStageControls}
              {/* Link icon — only for custom (user-added) stages that have at least one other stage to link to */}
              {isStage && isCustomStage(row.id) && allRows.some(r => r.indent === 0 && r.id !== row.id) && (() => {
                const linkedStage = timeline[row.id]?._linkedToStage;
                const linkedRow = linkedStage ? allRows.find(r => r.id === linkedStage) : null;
                const linkedLabel = linkedRow ? (timeline[linkedRow.id]?.taskName || linkedRow.defaultLabel || linkedRow.name || linkedRow.id) : null;
                return (
                  <span
                    onClick={(e) => { e.stopPropagation(); setStageLinkModal({ customStageId: row.id, currentLinked: linkedStage || null }); }}
                    title={linkedLabel ? `Linked to: ${linkedLabel} — click to change` : 'Link to another stage'}
                    style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: linkedLabel ? BRIO_GOLD : BRIO_GREY, padding: '1px 2px', borderRadius: '2px' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Link size={10} />
                  </span>
                );
              })()}
              {/* Show collapse toggle in name row only when strip is collapsed (so it can be re-expanded) */}
              {isStripCollapsed && stripToggleBtn}
              {/* SOW deliverable number — pinned to the right edge of the cell so the (N) badges
                  line up in a vertical column instead of trailing each (variable-length) name. */}
              {sowRefEl && <span style={{ flexShrink: 0, marginLeft: '4px' }}>{sowRefEl}</span>}
              {sowNewEl && <span style={{ flexShrink: 0, marginLeft: '4px' }}>{sowNewEl}</span>}
              {isFirstStage && canEdit && !isStandalone && (
                <span
                  onMouseDown={e => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    const stageIds = allRows.filter(r => r.indent === 0).map(r => r.id);
                    const firstStartDate = (stageIds.length > 0 && timeline[stageIds[0]]?.plannedStartDate) || '';
                    setContractDatesModal({
                      selectedStages: new Set(stageIds),
                      startDate: firstStartDate,
                      sets: [{ id: Date.now(), months: 0, days: 0, applied: false }],
                      stageSetMap: {},
                    });
                  }}
                  title="Inputs contract dates for all stages with average duration across all stages"
                  style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: BRIO_GOLD, padding: '1px 4px', borderRadius: '2px', fontSize: '9px', fontWeight: 400, whiteSpace: 'nowrap', border: '0.5px solid transparent', opacity: 0.8 }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(159,144,42,0.08)'; e.currentTarget.style.borderColor = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  Add contract dates
                </span>
              )}
              {discIndicator}
            </div>
            {/* Hint note row — stage/substage only, when strip is expanded */}
            {showNotes && (isStage || row.isSubStageHeader) && !isStripCollapsed && !isStandalone && (
              <div style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', paddingLeft: '13px', marginTop: '1px' }}>(Add dates below, right click to update contract column)</div>
            )}
            {/* Note for start_ rows */}
            {showNotes && row.id.startsWith('start_') && (
              <div title="Auto reads previous stage end date if Invoice date is not added. Dependencies auto change when payment is delayed" style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', paddingLeft: '13px', marginTop: '1px' }}>(Auto reads previous stage end date if Invoice date is not added. Dependencies auto change when payment is delayed)</div>
            )}
            {/* Bottom row: delayed / manual override buttons (closure rows only) */}
            {delayedAndOverrideBtns && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px', paddingLeft: '13px' }}>
                {delayedAndOverrideBtns}
              </div>
            )}
          </div>
        );
        // A standalone timeline (BRM) has no contract: no strip under its stage names.
        if (!isStageOrSub || isStandalone) return nameOnly;

        // Stage name strip — uses contract-specific fields so planned schedule columns are unaffected
        const storedDuration = rowData.contractDuration ?? null;
        const storedStart = rowData.contractStartDate || null;
        const storedEndDate = rowData.contractEndDate || null;
        const effectiveEndDate = storedEndDate ? parseDate(storedEndDate) : null;
        // Disc: compare strip contractEndDate against contractDate (Contract column in tracking page)
        // Gold = match, Red = mismatch or one side is empty
        const stripContractDate = rowData.contractDate || null;
        const stripDiscColor = (!storedEndDate && !stripContractDate) ? null
          : (storedEndDate && stripContractDate && storedEndDate === stripContractDate) ? BRIO_GOLD
          : BRIO_RED;
        const stripDiscTitle = storedEndDate && stripContractDate
          ? (storedEndDate === stripContractDate ? 'Matches contract date in tracking' : `Contract date in tracking: ${formatDateDisplay(parseDate(stripContractDate))} — right-click to sync`)
          : (!storedEndDate && stripContractDate) ? `Contract date in tracking: ${formatDateDisplay(parseDate(stripContractDate))} — right-click to load`
          : '';
        const stripInputBase = { ...inputBase, fontSize: '10px', opacity: 1 };
        const computedStageEndStr = effectiveEndDate ? formatDate(effectiveEndDate) : null;
        const openStageStripMenu = (e, section) => {
          if (!canEdit) return;
          e.stopPropagation();
          e.preventDefault();
          setSubStageStripMenu({ x: e.clientX, y: e.clientY, rowKey: row.key, stageId: row.key, partId: null, isStageRow: true, section, endDateStr: computedStageEndStr, targetDateStr: stripContractDate });
        };
        const stageSecHoverOn = (e) => { e.currentTarget.style.borderColor = BRIO_GOLD; };
        const stageSecHoverOff = (e) => { e.currentTarget.style.borderColor = flashedStripEnd.has(row.key) ? BRIO_GOLD : BRIO_BORDER; };
        return (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Top: name */}
            <div style={{ padding: '4px 6px 2px 6px' }}>{nameOnly}</div>
            {/* Bottom: duration | start date | end date | collapse — hidden when strip is collapsed */}
            {!isStripCollapsed && <div style={{ display: 'flex', alignItems: 'center', marginTop: '1px', marginBottom: '1px', padding: '1px 3px', gap: '3px' }}>
              <div
                title={`Contract duration${canEdit ? ' (right-click to clear)' : ''}`}
                onContextMenu={canEdit ? (e) => openStageStripMenu(e, 'duration') : undefined}
                onMouseEnter={canEdit ? stageSecHoverOn : undefined}
                onMouseLeave={canEdit ? stageSecHoverOff : undefined}
                style={{ flex: '0 0 28%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '0px 4px', minHeight: '16px' }}
              >
                {canEdit ? (
                  <DurationCell
                    value={storedDuration}
                    inputBase={stripInputBase}
                    savedMark={null}
                    onChange={(num) => handleFieldChange(row.key, 'contractDuration', num)}
                  />
                ) : (
                  <span style={{ fontSize: '10px', fontWeight: 300, color: storedDuration != null ? '#333' : '#ccc' }}>
                    {storedDuration != null ? `${storedDuration}d` : '–'}
                  </span>
                )}
              </div>
              <div
                title={`Contract start${canEdit ? ' (right-click to clear)' : ''}`}
                onContextMenu={canEdit ? (e) => openStageStripMenu(e, 'startDate') : undefined}
                onMouseEnter={canEdit ? stageSecHoverOn : undefined}
                onMouseLeave={canEdit ? stageSecHoverOff : undefined}
                style={{ flex: '0 0 33%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '0px 2px', minHeight: '16px' }}
              >
                {canEdit ? (
                  <BrioDatePicker
                    value={storedStart || ''}
                    onChange={(v) => handleFieldChange(row.key, 'contractStartDate', v)}
                    placeholder=""
                    clearable={true}
                    centerAlign={true}
                    as="button"
                    className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                    style={{ width: '100%' }}
                    triggerStyle={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: storedStart ? '#333' : '#ccc', width: '100%' }}
                  />
                ) : (
                  <span style={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: storedStart ? '#333' : '#ccc', textAlign: 'center', display: 'block' }}>
                    {storedStart ? formatDateDisplay(parseDate(storedStart)) : '–'}
                  </span>
                )}
              </div>
              <div
                title={`Contract end date${stripDiscTitle ? ' — ' + stripDiscTitle : ''}${canEdit ? ' (right-click to sync / clear)' : ''}`}
                onContextMenu={canEdit ? (e) => openStageStripMenu(e, 'endDate') : undefined}
                onMouseEnter={canEdit ? stageSecHoverOn : undefined}
                onMouseLeave={canEdit ? stageSecHoverOff : undefined}
                style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + (flashedStripEnd.has(row.key) ? BRIO_GOLD : BRIO_BORDER), borderRadius: '3px', padding: '0px 2px', minHeight: '16px', background: flashedStripEnd.has(row.key) ? 'rgba(159,144,42,0.12)' : 'transparent', transition: 'background 0.5s, border-color 0.5s' }}
              >
                {canEdit ? (
                  <BrioDatePicker
                    value={computedStageEndStr || ''}
                    onChange={(v) => handleFieldChange(row.key, 'contractEndDate', v)}
                    placeholder=""
                    clearable={true}
                    centerAlign={true}
                    as="button"
                    className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                    style={{ width: '100%' }}
                    triggerStyle={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: effectiveEndDate ? '#333' : '#ccc', width: '100%' }}
                  />
                ) : (
                  <span style={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: effectiveEndDate ? '#333' : '#ccc', textAlign: 'center', display: 'block' }}>
                    {effectiveEndDate ? formatDateDisplay(effectiveEndDate) : '–'}
                  </span>
                )}
                {stripDiscColor && (
                  <span style={{ position: 'absolute', top: '1px', right: '2px', width: '4px', height: '4px', borderRadius: '50%', background: stripDiscColor, display: 'inline-block', pointerEvents: 'none' }} />
                )}
              </div>
              {/* Collapse toggle — uniform position at right end of strip for all stage/sub-stage rows */}
              {stripToggleBtn}
            </div>}
          </div>
        );
      }
      return (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '3px', paddingLeft: indentPx + 'px', paddingRight: (hasProjectTaskLink || hasSowRef) ? '12px' : undefined }}>
          <span style={{ flexShrink: 0 }}>{toggleBtn}</span>
          {subStageIndentRows.has(row.id) && (
            <span style={{ flexShrink: 0, fontSize: '10px', color: BRIO_GREY, opacity: 0.6, lineHeight: 1, marginRight: '1px' }}>↳</span>
          )}
          {subStageBadge}
          {cfPrefixEl && <span style={{ flexShrink: 0 }}>{cfPrefixEl}</span>}
          <textarea
            title={sowHoverTitle}
            defaultValue={val}
            rows={1}
            onBlur={e => { cancelInstantSave(); const v = e.target.value.trim(); if (v !== val) handleFieldChange(row.key, 'taskName', v || row.name); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur(); } }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; scheduleInstantSave(row.key, 'taskName', e.target.value.trim() || row.name); }}
            style={{ ...inputBase, padding: '2px 0', width: '100%', fontSize: '12px', fontWeight: isStage ? 500 : 300, resize: 'none', overflow: 'hidden', lineHeight: '1.3' }}
            onFocus={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          />
          {cfSuffixEl && <span style={{ flexShrink: 0 }}>{cfSuffixEl}</span>}
          {momIconBtn}
          {savedMark}
          {discIndicator}
          {/* SOW deliverable number — pinned to the right edge of the cell so the (N) badges
              line up in a vertical column instead of trailing each (variable-length) name. */}
          {sowRefEl && <span style={{ flexShrink: 0, marginLeft: 'auto' }}>{sowRefEl}</span>}
          {sowNewEl && <span style={{ flexShrink: 0, marginLeft: sowRefEl ? '4px' : 'auto' }}>{sowNewEl}</span>}
        </div>
      );
    }

    // Date fields — stage rows and sub-stage headers show aggregated dates (read-only)
    if (col.type === 'date') {
      // Custom date columns hold their own value on every row (stages and parts included): nothing
      // rolls them up. Same look as the Start date cells.
      if (col.custom) {
        const rolledDate = rolledCustomValue(row.id, col.key);
        if (rolledDate) return (
          <span title="Earliest / latest of the sub tasks" style={{ fontSize: '11px', fontWeight: 400, color: rolledDate.value ? '#333' : '#ccc', display: 'block', width: '100%', textAlign: 'center' }}>
            {rolledDate.value ? formatDateDisplay(parseDate(rolledDate.value)) : ''}
          </span>
        );
        const value = rowData[col.key] || '';
        if (!canEditCell(row, col.key) || col.derive) return (
          <span style={{ fontSize: '11px', fontWeight: 300, color: value ? '#333' : '#ccc', display: 'block', width: '100%', textAlign: 'center' }}>
            {value ? formatDateDisplay(parseDate(value)) : ''}
          </span>
        );
        return (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <BrioDatePicker
              value={value}
              onChange={(v) => handleFieldChange(row.key, col.key, v)}
              placeholder="" clearable={true} centerAlign={true} as="button"
              className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
              style={{ width: '100%' }}
              triggerStyle={{ fontSize: '11px', fontWeight: 300, color: value ? '#333' : '#ccc', opacity: isSavingCell ? 0.5 : 1, width: '100%' }}
            />
            {savedMark}
          </span>
        );
      }
      // ── Sub-stage header: all date columns are aggregated read-only ──
      if (row.isSubStageHeader) {
        const agg = subStageDates[row.key];
        const aggDateVal = agg ? agg[col.key] : null;
        const effectiveDateVal = aggDateVal;

        // Disc: green only when actualDate is set AND matches the current aggregated end date.
        // If end date was edited after the actual was recorded, fall back to gold/red from targetDate.
        const trackingKey = col.key === 'plannedEndDate' ? 'targetDate' : null;
        const partData = trackingKey ? (timeline[row.stageId]?.partStages || []).find(p => p.id === row.partId) : null;
        const trackedDate = partData ? (partData[trackingKey] || null) : null;
        const subActualDate = col.key === 'plannedEndDate' ? (partData?.actualDate || null) : null;
        const subEndStr = effectiveDateVal ? formatDate(effectiveDateVal) : null;
        const subActualMatchesEnd = !!(subActualDate && subEndStr && subActualDate === subEndStr);
        const mismatch = trackedDate && subEndStr && trackedDate !== subEndStr;

        return (
          <div title={col.key === 'plannedEndDate' ? '(Auto syncs to planned, Right click to update actuals)' : undefined} style={{ position: 'relative', width: '100%', overflowX: 'hidden', textAlign: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 300, color: effectiveDateVal ? '#333' : '#ccc', fontStyle: effectiveDateVal ? 'normal' : 'italic', display: 'block', width: '100%', textAlign: 'center' }}>
              {effectiveDateVal ? formatDateDisplay(effectiveDateVal) : ''}
            </span>
            {col.key === 'plannedEndDate'
              ? (
                <div style={{ position: 'absolute', top: '1px', right: '2px', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', pointerEvents: 'none' }}>
                  {/* top = Planned-column sync (gold/red); bottom = Actual-column status (grey/green/red). Both always shown. */}
                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: (trackedDate || null) === (subEndStr || null) ? BRIO_GOLD : BRIO_RED, display: 'inline-block' }} />
                  {/* lower (actuals) disc: green = actual matches end, red = actual recorded but differs, grey = no actual yet */}
                  <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: !subActualDate ? '#b0b0b0' : (subActualMatchesEnd ? BRIO_GREEN : BRIO_RED), display: 'inline-block' }} />
                </div>
              )
              : (subActualMatchesEnd || trackedDate) && (
                <span style={{ position: 'absolute', top: '1px', right: '2px', width: '4px', height: '4px', borderRadius: '50%', background: subActualMatchesEnd ? BRIO_GREEN : (mismatch ? BRIO_RED : BRIO_GOLD), display: 'inline-block', pointerEvents: 'none' }} />
              )
            }
            {showNotes && col.key === 'plannedEndDate' && <div style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', marginTop: '1px', lineHeight: '1.4', fontFamily: 'Raleway, sans-serif', whiteSpace: 'normal', wordBreak: 'break-word' }}>(Auto syncs to planned, Right click to update actuals)</div>}
          </div>
        );
      }

      // ── Invoicing / closure rows: read-only (synced from tracking dates) unless it's an
      //    unlinked custom stage preset row, in which case the user enters dates manually ──
      const isInvoicingRow = row.id.startsWith('invoicing_');
      const isClosureRow = row.id.startsWith('closure_');
      if ((isInvoicingRow || isClosureRow) && (col.key === 'plannedStartDate' || col.key === 'plannedEndDate')) {
        const syncStageKey = isInvoicingRow ? row.id.slice('invoicing_'.length) : row.id.slice('closure_'.length);
        const isCustomPreset = isCustomStage(syncStageKey);
        const isLinked = isCustomPreset && !!(timeline[syncStageKey]?._linkedToStage);
        // Unlinked custom stage preset rows: render editable date picker
        if (isCustomPreset && !isLinked && col.key === 'plannedStartDate' && canEdit) {
          const value = rowData[col.key] || '';
          return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <BrioDatePicker
                value={value}
                onChange={(v) => handleFieldChange(row.key, col.key, v)}
                placeholder=""
                clearable={true}
                centerAlign={true}
                as="button"
                className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                style={{ width: '100%' }}
                triggerStyle={{ fontSize: '11px', fontWeight: 300, color: value ? '#333' : '#ccc', opacity: isSavingCell ? 0.5 : 1, width: '100%' }}
              />
              {savedMark}
            </span>
          );
        }
        // Unlinked custom stage preset rows: plannedEndDate is still computed (start + duration)
        if (isCustomPreset && !isLinked) {
          const value = rowData[col.key] || '';
          return (
            <span style={{ fontSize: '11px', fontWeight: 300, color: value ? '#333' : '#ccc', display: 'block', width: '100%', textAlign: 'center' }}>
              {value ? formatDateDisplay(parseDate(value)) : ''}
            </span>
          );
        }
        const syncStageData = resolveStageRecord(syncStageKey);
        const storedVal = rowData[col.key] || null;
        // Derived expected value from the parent stage's billing fields (shared helper). The invoicing
        // row's own duration is the payment-terms window; the closure row reads its stage's invoicing
        // row for that duration.
        const syncDur = (isInvoicingRow ? rowData.plannedDuration : timeline[`invoicing_${syncStageKey}`]?.plannedDuration) || paymentTermsDays || null;
        const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(syncStageData, syncDur);
        const sourceDate = isInvoicingRow ? (invoicingStart || null) : (closureDate || null);
        let expectedVal = null;
        if (isInvoicingRow && sourceDate) {
          expectedVal = col.key === 'plannedStartDate' ? invoicingStart : invoicingEnd;
        } else if (isClosureRow && sourceDate) {
          expectedVal = rowData._delayIgnored ? (syncStageData.actualDate || null) : closureDate;
        }
        const isSynced = !!(sourceDate && storedVal && storedVal === expectedVal);
        const isOutOfSync = !!(sourceDate && storedVal !== expectedVal);
        const discColor = isSynced ? BRIO_GOLD : (isOutOfSync ? BRIO_RED : null);
        const syncNote = isInvoicingRow
          ? '(Date auto syncs when the stage is approved)'
          : '(Date auto syncs when payment is received)';
        return (
          <div title={syncNote} style={{ position: 'relative', width: '100%', overflowX: 'hidden', textAlign: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 300, color: storedVal ? '#333' : '#ccc' }}>
              {storedVal ? formatDateDisplay(parseDate(storedVal)) : ''}
            </span>
            {discColor && (
              <span style={{ position: 'absolute', top: '1px', right: '2px', width: '4px', height: '4px', borderRadius: '50%', background: discColor, display: 'inline-block', pointerEvents: 'none' }} />
            )}
            {showNotes && <div style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', marginTop: '1px', lineHeight: '1.4', fontFamily: 'Raleway, sans-serif', whiteSpace: 'normal', wordBreak: 'break-word' }}>{syncNote}</div>}
          </div>
        );
      }

      const isStage = row.indent === 0;
      const dateFields = ['plannedStartDate', 'plannedEndDate'];
      const isAutoDateField = isStage && dateFields.includes(col.key);

      // Stage rows: all date fields are auto-aggregated read-only.
      // Contract start/end (targetStartDate, targetEndDate) support double-click manual override.
      if (isAutoDateField) {
        const agg = stageDates[row.key];
        const aggDateVal = agg ? agg[col.key] : null;
        const effectiveDateVal = aggDateVal;

        // For plannedEndDate two stacked discs are shown:
        //  • upper (tracking): gold when the contract/target date matches the planned end, red when it drifts.
        //  • lower (actuals): GREEN when an actual date is recorded AND matches the planned end, RED when an
        //    actual is recorded but does NOT match ("dates don't match"), GREY only when no actual is
        //    recorded yet ("not updated to actuals"). Mirrors the billing page's ActualStatusMarker and
        //    applies to every stage — paid/frozen or not — since it's the mismatch, not the lock, that reds.
        // A standalone timeline has no Tracking dates to compare against, so it shows no discs.
        const trackingKey = (col.key === 'plannedEndDate' && !isStandalone) ? 'targetDate' : null;
        const trackedDate = trackingKey ? rowData[trackingKey] : null;
        const actualDateVal = col.key === 'plannedEndDate' ? (rowData['actualDate'] || null) : null;
        const endDateStr = effectiveDateVal ? formatDate(effectiveDateVal) : null;
        const actualMatchesEnd = !!(actualDateVal && endDateStr && actualDateVal === endDateStr);
        // Top disc (Planned-column sync): gold when the Tracking Planned (targetDate) matches this planned
        // end, red when they differ — shown at ALL times (sync on/off, after reset), even with nothing tracked.
        let plannedSynced = (trackedDate || null) === (endDateStr || null);
        let actualDotColor = !actualDateVal ? '#b0b0b0' : (actualMatchesEnd ? BRIO_GREEN : BRIO_RED);
        // While the stage's payment is delayed (Closure shows Delayed, not ignored), the stage end is
        // carried by the Closure row: the paid date once paid late, today while still unpaid. That is the
        // correct state, so both dots check the stage end against the closure end: top gold / bottom
        // green when they match, red when they don't. An ignored delay keeps the rule above.
        if (trackingKey && delayedBadgeFor(`closure_${row.key}`) && !delayedBadgeFor(`closure_${row.key}`).ignored) {
          const closureEnd = timeline[`closure_${row.key}`]?.plannedEndDate || null;
          const endIsClosure = !!closureEnd && endDateStr === closureEnd;
          plannedSynced = endIsClosure;
          actualDotColor = endIsClosure ? BRIO_GREEN : BRIO_RED;
        }

        if (trackingKey) {
          return (
            <div title="(Auto syncs to planned, Right click to update actuals)" style={{ position: 'relative', width: '100%', overflowX: 'hidden', textAlign: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 400, color: effectiveDateVal ? '#333' : '#ccc', fontStyle: effectiveDateVal ? 'normal' : 'italic' }}>
                {effectiveDateVal ? formatDateDisplay(effectiveDateVal) : ''}
              </span>
              <div style={{ position: 'absolute', top: '1px', right: '2px', display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', pointerEvents: 'none' }}>
                {/* top = Planned-column sync (gold/red); bottom = Actual-column status (grey/green/red). Both always shown. */}
                <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: plannedSynced ? BRIO_GOLD : BRIO_RED, display: 'inline-block' }} />
                <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: actualDotColor, display: 'inline-block' }} />
              </div>
              {showNotes && <div style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', marginTop: '1px', lineHeight: '1.4', fontFamily: 'Raleway, sans-serif', whiteSpace: 'normal', wordBreak: 'break-word' }}>(Auto syncs to planned, Right click to update actuals)</div>}
            </div>
          );
        }
        return (
          <span style={{ fontSize: '11px', fontWeight: 400, color: effectiveDateVal ? '#333' : '#ccc', fontStyle: effectiveDateVal ? 'normal' : 'italic' }}>
            {effectiveDateVal ? formatDateDisplay(effectiveDateVal) : ''}
          </span>
        );
      }

      // Stage rows — Contract / Planned / Actual date columns: red text when date ≠ stage's planned end
      const TRACKED_DATE_KEYS = ['contractDate', 'targetDate', 'actualDate'];
      if (isStage && TRACKED_DATE_KEYS.includes(col.key)) {
        const aggEnd = stageDates[row.key]?.plannedEndDate; // Date object or null
        const cellStr = rowData[col.key] || '';
        const hasMismatch = !!(aggEnd && cellStr && formatDate(aggEnd) !== cellStr);
        const trackedColor = hasMismatch ? BRIO_RED : (cellStr ? '#333' : '#ccc');
        if (!canEdit || !syncEnabled) return (
          <span style={{ fontSize: '11px', fontWeight: 300, color: trackedColor, display: 'block', width: '100%', textAlign: 'center' }}>
            {cellStr ? formatDateDisplay(parseDate(cellStr)) : ''}
          </span>
        );
        return (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <BrioDatePicker
              value={cellStr}
              onChange={(v) => handleFieldChange(row.key, col.key, v)}
              placeholder="" clearable={true} centerAlign={true} as="button"
              className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
              style={{ width: '100%' }}
              triggerStyle={{ fontSize: '11px', fontWeight: 300, color: trackedColor, opacity: isSavingCell ? 0.5 : 1, width: '100%' }}
            />
            {savedMark}
          </span>
        );
      }

      // End dates (plannedEndDate) are auto-computed from start + duration — read-only
      if (col.computed) {
        const value = rowData[col.key] || '';
        const endDateFontSize = '11px';
        return (
          <span style={{ fontSize: endDateFontSize, fontWeight: 300, color: value ? '#333' : '#ccc', display: 'block', width: '100%', textAlign: 'center' }}>
            {value ? formatDateDisplay(parseDate(value)) : ''}
          </span>
        );
      }

      // Start dates are editable — except on summary/parent rows, where the start date is rolled up
      // from the children (earliest child start) and is therefore read-only.
      const startDateFontSize = '11px';
      const value = rowData[col.key] || '';
      if (!canEditCell(row, col.key) || (isSummaryRow && col.key === 'plannedStartDate')) return (
        <span style={{ fontSize: startDateFontSize, fontWeight: 300, color: value ? '#333' : '#ccc', display: 'block', width: '100%', textAlign: 'center' }}>
          {value ? formatDateDisplay(parseDate(value)) : ''}
        </span>
      );
      return (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <BrioDatePicker
            value={value}
            onChange={(v) => handleFieldChange(row.key, col.key, v)}
            placeholder=""
            clearable={true}
            centerAlign={true}
            as="button"
            className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
            style={{ width: '100%' }}
            triggerStyle={{ fontSize: startDateFontSize, fontWeight: 300, color: value ? '#333' : '#ccc', opacity: isSavingCell ? 0.5 : 1, width: '100%' }}
          />
          {savedMark}
        </span>
      );
    }

    // Number fields (duration in working days) — 11px text height; stage rows and sub-stage headers show aggregated (read-only)
    if (col.type === 'number') {
      // Custom duration columns hold their own value on every row, like the custom date columns.
      if (col.custom) {
        const rolledDur = rolledCustomValue(row.id, col.key);
        if (rolledDur) return (
          <span title="Working days from the earliest sub task start to the latest end" style={{ fontSize: '11px', fontWeight: 400, color: rolledDur.value != null ? '#333' : '#ccc' }}>
            {rolledDur.value != null ? `${rolledDur.value}d` : ''}
          </span>
        );
        const value = rowData[col.key];
        if (!canEditCell(row, col.key)) return (
          <span style={{ fontSize: '11px', fontWeight: 300, color: value != null ? '#333' : '#ccc' }}>
            {value != null ? <>{value}<span style={{ fontSize: '11px', fontWeight: 485 }}>d</span></> : ''}
          </span>
        );
        return (
          <DurationCell
            value={value}
            inputBase={{ ...inputBase, fontSize: '11px' }}
            savedMark={savedMark}
            onChange={(num) => handleFieldChange(row.key, col.key, num)}
          />
        );
      }
      // Invoicing/closure rows: duration is auto-filled from payment terms and read-only,
      // UNLESS the parent is an unlinked custom stage — then allow manual entry.
      if (row.id.startsWith('invoicing_') || row.id.startsWith('closure_')) {
        const presetPrefix = row.id.startsWith('invoicing_') ? 'invoicing_' : 'closure_';
        const parentStageId = row.id.slice(presetPrefix.length);
        const isCustomPreset = isCustomStage(parentStageId);
        const isLinked = isCustomPreset && !!(timeline[parentStageId]?._linkedToStage);
        if (isCustomPreset && !isLinked && canEdit) {
          const value = rowData[col.key];
          return (
            <DurationCell
              value={value}
              inputBase={{ ...inputBase, fontSize: '11px' }}
              savedMark={savedMark}
              onChange={(num) => handleFieldChange(row.key, col.key, num)}
            />
          );
        }
        const value = rowData[col.key];
        return (
          <span style={{ fontSize: '11px', fontWeight: 300, color: value != null ? '#333' : '#ccc' }}>
            {value != null ? <>{value}<span style={{ fontSize: '11px', fontWeight: 485 }}>d</span></> : ''}
          </span>
        );
      }
      const isStage = row.indent === 0 || row.isSubStageHeader;
      if (isStage) {
        const agg = stageDates[row.key] || subStageDates[row.key];
        const durVal = agg ? agg[col.key] : null;
        return (
          <span style={{ fontSize: '11px', fontWeight: 400, color: durVal != null ? '#333' : '#ccc' }}>
            {durVal != null ? `${durVal}d` : ''}
          </span>
        );
      }
      const value = rowData[col.key];
      // Summary/parent rows: duration is rolled up from children — read-only.
      if (!canEditCell(row, col.key) || (isSummaryRow && col.key === 'plannedDuration')) return (
        <span style={{ fontSize: '11px', fontWeight: 300, color: value != null ? '#333' : '#ccc' }}>
          {value != null ? <>{value}<span style={{ fontSize: '11px', fontWeight: 485 }}>d</span></> : ''}
        </span>
      );
      return (
        <DurationCell
          value={value}
          inputBase={{ ...inputBase, fontSize: '11px' }}
          savedMark={savedMark}
          onChange={(num) => handleFieldChange(row.key, col.key, num)}
        />
      );
    }

    // Percent complete
    if (col.type === 'percent') {
      const isStageRow = row.indent === 0 || row.isSubStageHeader;
      // Stage rows, sub-stage headers and tasks with sub tasks: the rolled up average (read-only)
      if (isStageRow || pctRollup.hasSubTasks(row.id)) {
        const agg = isStageRow ? (stageDates[row.key] || subStageDates[row.key]) : null;
        const pct = isStageRow ? (agg?.averagePercentComplete ?? null) : pctRollup.value(row.id);
        const barColor = pct === 100 ? BRIO_GREEN : pct >= 50 ? BRIO_ORANGE : pct > 0 ? BRIO_RED : BRIO_BORDER;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <span style={{ fontSize: '11px', fontWeight: 400, color: pct != null ? '#333' : '#ccc' }}>{pct != null ? `${pct}%` : ''}</span>
            {pct != null && (
              <div style={{ width: '100%', padding: '0 2px' }}>
                <div style={{ width: '100%', height: '3px', background: BRIO_BORDER }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
                </div>
              </div>
            )}
          </div>
        );
      }
      const value = rowData[col.key];
      const pct = value != null ? Number(value) : null;
      const barColor = pct === 100 ? BRIO_GREEN : pct >= 50 ? BRIO_ORANGE : pct > 0 ? BRIO_RED : BRIO_BORDER;
      if (!canEdit) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <span style={{ fontSize: '11px', fontWeight: 300, color: pct != null ? '#333' : '#ccc' }}>{pct != null ? `${pct}%` : ''}</span>
          {pct != null && (
            <div style={{ width: '100%', padding: '0 3px', boxSizing: 'border-box' }}>
              <div style={{ width: '100%', height: '3px', background: BRIO_BORDER }}>
                <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
              </div>
            </div>
          )}
        </div>
      );
      return <PercentCell value={value} inputBase={inputBase} savedMark={savedMark} onChange={(num) => handleFieldChange(row.key, col.key, num)} />;
    }

    // Status dropdown — matches ProjectTasksPage progress column style
    if (col.type === 'status') {
      const BRIO_GREEN_LOCAL = '#4caf50';
      // Framed badge: same font/size/padding as the existing filled badges, border-only styling
      const framedBadge = (label, borderColor, textColor) => (
        <span style={{
          display: 'block', width: '100%', boxSizing: 'border-box',
          padding: '2px 8px', fontSize: '8.4px', fontWeight: 400, letterSpacing: '0.02em',
          color: textColor, border: `0.3px solid ${borderColor}`,
          background: 'transparent', textAlign: 'center',
        }}>
          {label}
        </span>
      );

      // ── Invoicing rows: mirror the billing page's Invoice → Paid progression for this stage.
      // "Invoiced" once an invoice date exists, then "Paid" once the payment date is recorded. ──
      if (row.id.startsWith('invoicing_')) {
        const sk = row.id.slice('invoicing_'.length);
        const sd = resolveStageRecord(sk) || {};
        if (sd.paidDate) return framedBadge('Paid', BRIO_GREEN_LOCAL, BRIO_GREEN_LOCAL);
        if (sd.invoiceDate) return framedBadge('Invoiced', BRIO_GOLD, BRIO_GOLD);
        return null;
      }

      // ── Closure rows ("Delayed" when payment landed after the invoicing window) and Start of Stage rows
      // ("Delayed" while the linked stage's payment is overdue, or paid late with the FS delay link
      // still in place): both from delayedBadgeFor. Right click → Hide / Unhide; a hidden badge stays
      // on screen greyed out and is left out of the PDF export. Left click on a Closure badge still
      // offers Ignore (snap the closure back to the actual end date). ──
      if (row.id.startsWith('closure_') || row.id.startsWith('start_')) {
        const delayed = delayedBadgeFor(row.id);
        if (!delayed) return null;
        const isClosure = delayed.kind === 'closure';
        const badge = (delayed.hidden || delayed.ignored)
          ? <span style={{ opacity: 0.6 }}>{framedBadge(delayed.label, '#c8c8c8', '#b0b0b0')}</span>
          : framedBadge(delayed.label, BRIO_RED, BRIO_RED);
        const canClick = isClosure && canEdit;
        return (
          <span
            onClick={canClick ? (e) => { e.stopPropagation(); setConfirmModal(delayed.ignored ? {
              message: 'Restore the delay? The Closure date follows the late payment again and the next stage’s start is pushed by it.',
              confirmLabel: 'Restore delay', cancelLabel: 'Cancel',
              onConfirm: () => handleRestoreClosureDelay(row.id, delayed.stageKey),
            } : {
              message: 'Ignore the delay and snap the Closure date back to the stage’s actual end date?',
              confirmLabel: 'Ignore', cancelLabel: 'Cancel',
              onConfirm: () => handleIgnoreClosureDelay(row.id, delayed.stageKey),
            }); } : undefined}
            onContextMenu={canEdit ? (e) => {
              e.preventDefault(); e.stopPropagation();
              setDelayBadgeMenu({ x: e.clientX, y: e.clientY, rowKey: row.id, hidden: delayed.hidden });
            } : undefined}
            style={{ cursor: canClick ? 'pointer' : 'default', display: 'block', width: '100%' }}
            title={(delayed.ignored ? 'Delay ignored. Click to restore it. ' : (canClick ? 'Payment was late. Click to ignore and snap closure to the actual end date. ' : '')) + 'Right click to ' + (delayed.hidden ? 'unhide.' : 'hide (greyed here, left out of the PDF).')}
          >
            {badge}
          </span>
        );
      }

      // ── Stage rows (indent === 0): derive from invoiceDate / paidDate ──
      // Only the invoiced / paid milestones are surfaced here; the stage-level "Delayed" badge lives on
      // the stage's Start row. A stage that has neither shows a blank status cell (no "In progress").
      if (row.indent === 0) {
        const stageData = timeline[row.id] || {};
        // A stage split into parts: Paid once every part is paid, Part paid while only some are.
        const parts = partStagesMap[row.id] || [];
        if (parts.length > 0) {
          const paidParts = parts.filter(p => resolveStageRecord(`substage_${row.id}_${p.id}`).paidDate).length;
          if (paidParts === parts.length) return framedBadge('Paid', BRIO_GREEN_LOCAL, BRIO_GREEN_LOCAL);
          if (paidParts > 0) return framedBadge('Part paid', BRIO_ORANGE, BRIO_ORANGE);
        }
        const hasPaid = !!(stageData.paidDate);
        const hasInvoice = !!(stageData.invoiceDate);
        if (hasPaid) return framedBadge('Paid', BRIO_GREEN_LOCAL, BRIO_GREEN_LOCAL);
        if (hasInvoice) return framedBadge('Invoiced', BRIO_GOLD, BRIO_GOLD);
        return null;
      }

      // ── Regular task rows: manual editable status select ──
      const raw = rowData[col.key];
      const numVal = raw != null && raw !== '' ? Number(raw) : null;
      const opt = numVal != null ? STATUS_OPTIONS.find(o => o.value === numVal) : null;
      const badge = (o) => (
        <span style={{
          display: 'inline-block', padding: '2px 8px', fontSize: '8.4px', fontWeight: 400,
          color: o ? o.color : '#999', background: o ? o.bg : '#f0f0f0', letterSpacing: '0.02em',
        }}>
          {o ? o.label : ''}
        </span>
      );
      if (!canEdit) return badge(opt);
      return (
        <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <select
            value={numVal != null ? numVal : ''}
            onChange={e => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              handleFieldChange(row.key, col.key, v);
              if (v === 100 && row.key.startsWith('closure_')) {
                const stageKey = row.key.replace('closure_', '');
                const stageRow = allRows.find(r => r.id === stageKey);
                const stageName = stageRow?.name || STAGE_NAMES[stageKey] || stageKey;
                setClosureInvoiceModal({ stageKey, stageName, invoiceDate: formatDate(new Date()) });
              }
            }}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '8.4px', fontWeight: 400,
              padding: '2px 8px', border: 'none', borderRadius: '0',
              background: opt ? opt.bg : '#f0f0f0', color: opt ? opt.color : '#999',
              cursor: 'pointer', opacity: isSavingCell ? 0.5 : 1,
              appearance: 'none', WebkitAppearance: 'none', textAlign: 'center', width: '100%',
            }}
          >
            <option value="">—</option>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      );
    }

    // Custom single/multi select columns + built-in assignedTo — unified SelectCell
    if (col.key === 'assignedTo' || (col.custom && (col.type === 'singleSelect' || col.type === 'multiSelect'))) {
      const isMulti = col.key === 'assignedTo' || col.type === 'multiSelect';
      let options, raw, values;
      if (col.key === 'assignedTo') {
        options = timeline._assignedToCategories || [];
        raw = rowData.assignedTo;
        values = Array.isArray(raw) ? raw : (raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []);
      } else {
        options = col.options || [];
        raw = rowData[col.key];
        values = isMulti ? (Array.isArray(raw) ? raw : (raw ? [raw] : [])) : (raw ? [raw] : []);
      }
      return (
        <SelectCell
          options={options}
          values={values}
          isMulti={isMulti}
          canEdit={canEditCell(row, col.key)}
          onChange={newVals => {
            const stored = isMulti ? newVals : (newVals[0] || null);
            handleFieldChange(row.key, col.key, stored);
          }}
        />
      );
    }

    // Text fields (predecessors, remarks, delayCaused, and custom text columns)
    const isWrapTextField = ['remarks', 'delayCaused'].includes(col.key) || (col.custom && col.type === 'text');
    const textFontSize = '12px';
    const value = rowData[col.key] || '';

    // Predecessors: single-click = inline text entry; double-click = modal editor
    if (col.key === 'predecessors') {
      // Invoicing and closure rows cannot have dependencies
      if (row.id.startsWith('invoicing_') || row.id.startsWith('closure_')) return null;
      const predValue = (row.id in optimisticPreds) ? (optimisticPreds[row.id] || '') : value;
      const display = predValue
        ? parsePredecessors(predValue)
          .slice()
          .sort((a, b) => (Number(seqToHierLabel[a.rowNumber] ?? a.rowNumber) - Number(seqToHierLabel[b.rowNumber] ?? b.rowNumber)))
          .map(p => {
            const label = seqToHierLabel[p.rowNumber] || p.rowNumber;
            return p.lag > 0 ? `${label}${p.type}+${p.lag}d` : `${label}${p.type}`;
          }).join(', ')
        : '';

      // start_ rows: show dep text centred + link icon pinned to right for stage date picker
      if (row.id.startsWith('start_')) {
        return (
          <div title="Preset link to previous stage. Double-click to manually set the dependency type (FS / SS / FF / SF) and lag; use the link icon to pick which stage it links to or reset to the preset. When invoice date is missing, it links to end date of previous stage; when invoice date is added, it links to invoice start date." style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'hidden' }}>
            <span
              style={{ fontSize: '11px', fontWeight: 300, color: display ? '#333' : '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: canEdit ? 'pointer' : 'default' }}
              onDoubleClick={canEdit ? (e) => { e.stopPropagation(); setPredecessorModal({ rowKey: row.key, value: predValue }); } : undefined}
            >{display || '—'}</span>
            {canEdit && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  setStageLinkMenu({ startRowId: row.id, x: rect.left, y: rect.bottom + 2 });
                }}
                title="Link start date to a stage or substage end date"
                style={{ position: 'absolute', right: 2, flexShrink: 0, color: BRIO_GOLD, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', opacity: 0.7 }}
                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
              >
                <Link size={10} />
              </span>
            )}
          </div>
        );
      }

      const isInline = canEditCell(row, 'predecessors') && predInlineEdit?.rowKey === row.key;
      if (isInline) {
        return (
          <input
            autoFocus
            defaultValue={display}
            style={{ width: '100%', border: 'none', outline: 'none', fontSize: '11px', fontWeight: 300, textAlign: 'center', background: 'rgba(159,144,42,0.06)', padding: '0 2px' }}
            onBlur={e => {
              const raw = e.target.value.trim();
              const parsed = raw.split(/[\s,]+/).filter(Boolean).map(tok => {
                const m = tok.match(/^(\d+)(FS|FF|SS|SF)?([+-]\d+d?)?$/i);
                if (!m) return null;
                // The typed number is the visible # (display) — translate to the stored number.
                const num = displayLabelToTaskNum[m[1]];
                if (num == null) return null;
                const type = (m[2] || 'FS').toUpperCase();
                const lag = m[3] ? parseInt(m[3], 10) : 0;
                return { rowNumber: num, type, lag };
              }).filter(Boolean);
              handleFieldChange(row.key, 'predecessors', formatPredecessors(parsed));
              setPredInlineEdit(null);
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setPredInlineEdit(null); }
              if (e.key === 'Enter') { e.currentTarget.blur(); }
            }}
          />
        );
      }
      return (
        <PredecessorCell
          display={display}
          canEdit={canEditCell(row, 'predecessors')}
          onSingleClick={() => setPredInlineEdit({ rowKey: row.key })}
          onDoubleClick={() => setPredecessorModal({ rowKey: row.key, value: predValue })}
        />
      );
    }

    if (!canEditCell(row, col.key)) return (
      <span style={{ fontSize: textFontSize, fontWeight: 300, color: value ? '#333' : '#ccc', whiteSpace: isWrapTextField ? 'pre-wrap' : undefined }}>{value || ''}</span>
    );
    if (isWrapTextField) {
      return (
        <textarea
          key={`${row.key}:${col.key}`}
          defaultValue={value}
          rows={1}
          onBlur={e => { cancelInstantSave(); const v = e.target.value.trim(); if (v !== value) handleFieldChange(row.key, col.key, v); }}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur(); } }}
          onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; scheduleInstantSave(row.key, col.key, e.target.value.trim()); }}
          style={{ ...inputBase, width: '100%', resize: 'none', overflow: 'hidden', lineHeight: '1.35', fontSize: textFontSize, display: 'block' }}
          onFocus={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
        />
      );
    }
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
        <input
          type="text"
          defaultValue={value}
          onChange={e => scheduleInstantSave(row.key, col.key, e.target.value.trim())}
          onBlur={e => { cancelInstantSave(); const v = e.target.value.trim(); if (v !== value) handleFieldChange(row.key, col.key, v); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          placeholder=""
          style={{ ...inputBase, width: '100%', fontSize: textFontSize }}
        />
        {savedMark}
      </div>
    );
  };

  // No rows to display — show empty state. A BRM tab with no stages yet keeps the page (its tab bar
  // and toolbar) and says so inside the table instead.
  if (displayRows.length === 0 && !isStandalone) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', fontFamily: 'Raleway, sans-serif' }}>
        <p style={{ fontSize: '13px', fontWeight: 300, color: BRIO_GREY, margin: 0 }}>
          No confirmed scope of work found for this project.
        </p>
        <p style={{ fontSize: '12px', fontWeight: 300, color: '#999', margin: '8px 0 0' }}>
          Please confirm the scope in the Contracts &amp; Scope page first to populate the timeline stages and deliverables.
        </p>
      </div>
    );
  }

  // Manage-options dropdown column — 'assignedTo' is built-in but uses _assignedToCategories
  const isManageAssignedTo = manageOptionsDropdown?.colKey === 'assignedTo';
  const manageOptCol = manageOptionsDropdown && !isManageAssignedTo
    ? (timeline._customColumns || []).find(c => c.key === manageOptionsDropdown.colKey)
    : null;

  return (
    <div>
      {/* Add Column dialog */}
      {addColumnDialog !== null && (
        <AddColumnDialog
          onAdd={(label, type) => handleAddColumn(label, type, addColumnDialog)}
          onClose={() => setAddColumnDialog(null)}
        />
      )}

      {/* Manage Options inline dropdown — handles both custom columns and built-in assignedTo */}
      {manageOptionsDropdown && (manageOptCol || isManageAssignedTo) && (
        <ManageOptionsDropdown
          x={manageOptionsDropdown.x}
          y={manageOptionsDropdown.y}
          colLabel={isManageAssignedTo ? 'Assigned To' : (columnLabels[manageOptCol.key] || manageOptCol.label)}
          options={isManageAssignedTo ? (timeline._assignedToCategories || []) : (manageOptCol.options || [])}
          onUpdate={isManageAssignedTo
            ? handleUpdateAssignedToCategories
            : (opts) => handleUpdateColumnOptions(manageOptCol.key, opts)}
          onClose={() => setManageOptionsDropdown(null)}
        />
      )}

      {/* Add Categories dropdown (Assigned To) */}
      {addCategoriesMenu && (
        <AddCategoriesDropdown
          x={addCategoriesMenu.x}
          y={addCategoriesMenu.y}
          currentCategories={timeline._assignedToCategories || []}
          onUpdate={handleUpdateAssignedToCategories}
          onClose={() => setAddCategoriesMenu(null)}
        />
      )}

      {/* Settings modal */}
      {showSettings && (
        <ProjectSettingsModal
          presets={settingsPresets}
          activePresetId={activePresetId}
          onSave={handleSaveSettings}
          onCancel={() => setShowSettings(false)}
        />
      )}

      {/* Preset Settings table modal */}
      {showPresetSettings && (
        <PresetSettingsModal
          presets={settingsPresets}
          activePresetId={activePresetId}
          onSave={(newPresets, newActiveId) => { handleSaveSettings(newPresets, newActiveId); setShowPresetSettings(false); }}
          onCancel={() => setShowPresetSettings(false)}
        />
      )}

      {/* M.O.M side panel — all authenticated users can create and edit MOMs */}
      {momPanel && (
        <MOMPanel
          projectId={project.id}
          taskKey={momPanel.rowKey}
          rowName={momPanel.rowName}
          contractCurrency={project.contractCurrency}
          canEdit={true}
          isAdmin={isAdmin}
          isSupervisor={isSupervisor}
          momHook={momHook}
          projectTasksHook={projectTasksHook}
          onClose={() => setMOMPanel(null)}
          onDeleteRow={async (rowKey) => {
            const newRows = allRows.filter(r => r.id !== rowKey);
            const { numToKey: oldNumToKey } = computeTaskNums(buildExtendedRows(allRows, partStagesMap, timeline));
            const newExtRows = buildExtendedRows(newRows, partStagesMap, timeline);
            const { keyToNum: newKeyToNum, numToKey: newNumToKey } = computeTaskNums(newExtRows);
            const base = { ...timeline };
            delete base[rowKey];
            base._rows = newRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
            const newTimeline = remapPredecessorValues(base, newExtRows, oldNumToKey, newKeyToNum);
            newTimeline._depNumKey = newNumToKey;
            isOptimisticActive.current = true;
            setOptimisticRows(newRows);
            await persistTimeline(newTimeline);
          }}
        />
      )}

      {/* Preset Task Modal — choose which system rows (Start / Invoicing / Closure) to add to a custom stage */}
      {presetTaskModal && (() => {
        const stageId = presetTaskModal.stageId;
        const existingIds = new Set(allRows.map(r => r.id));
        const alreadyHasStart = existingIds.has(`start_${stageId}`);
        const alreadyHasInvoicing = existingIds.has(`invoicing_${stageId}`);
        const alreadyHasClosure = existingIds.has(`closure_${stageId}`);
        const ptStart = presetTaskModal._start ?? false;
        const ptInvoicing = presetTaskModal._invoicing ?? false;
        const ptClosure = presetTaskModal._closure ?? false;
        const setPtStart = (v) => setPresetTaskModal(p => ({ ...p, _start: v }));
        const setPtInvoicing = (v) => setPresetTaskModal(p => ({ ...p, _invoicing: v }));
        const setPtClosure = (v) => setPresetTaskModal(p => ({ ...p, _closure: v }));
        const presetRows = [
          { key: 'start',     label: 'Start of Stage',     checked: ptStart,     set: setPtStart },
          { key: 'invoicing', label: 'Invoicing of Stage', checked: ptInvoicing, set: setPtInvoicing },
          { key: 'closure',   label: 'Closure of Stage',   checked: ptClosure,   set: setPtClosure },
        ];
        const hasChange = (ptStart !== alreadyHasStart) || (ptInvoicing !== alreadyHasInvoicing) || (ptClosure !== alreadyHasClosure);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPresetTaskModal(null)}>
            <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '280px', maxWidth: '340px', padding: '20px 24px', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Preset Tasks</div>
              <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '14px' }}>Select the system rows for <strong>{presetTaskModal.stageName || 'this stage'}</strong>:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
                {presetRows.map(r => (
                  <label
                    key={r.key}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 300, color: '#333' }}
                  >
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={e => r.set(e.target.checked)}
                      style={{ accentColor: BRIO_GOLD, width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setPresetTaskModal(null)} style={{ padding: '5px 14px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', color: '#555' }}>Cancel</button>
                <button
                  disabled={!hasChange}
                  onClick={async () => {
                    setPresetTaskModal(null);
                    // Rows to add (newly checked, not yet present)
                    const toAdd = { start: !alreadyHasStart && ptStart, invoicing: !alreadyHasInvoicing && ptInvoicing, closure: !alreadyHasClosure && ptClosure };
                    // Rows to remove (unchecked but currently present)
                    const idsToRemove = new Set([
                      ...(alreadyHasStart && !ptStart ? [`start_${stageId}`] : []),
                      ...(alreadyHasInvoicing && !ptInvoicing ? [`invoicing_${stageId}`] : []),
                      ...(alreadyHasClosure && !ptClosure ? [`closure_${stageId}`] : []),
                    ]);
                    let baseRows = allRows;
                    let baseTimeline = { ...timeline };
                    if (idsToRemove.size > 0) {
                      baseRows = baseRows.filter(r => !idsToRemove.has(r.id));
                      idsToRemove.forEach(id => delete baseTimeline[id]);
                      baseTimeline._rows = baseRows.map(r => ({ id: r.id, indent: r.indent, defaultLabel: r.defaultLabel || undefined }));
                      isOptimisticActive.current = true;
                      setOptimisticRows(baseRows);
                    }
                    if (toAdd.start || toAdd.invoicing || toAdd.closure) {
                      await handleAddPresetRows(stageId, toAdd);
                    } else if (idsToRemove.size > 0) {
                      await persistTimeline(baseTimeline);
                    }
                  }}
                  style={{ padding: '5px 14px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 400, border: '1px solid ' + (hasChange ? BRIO_GOLD : BRIO_BORDER), background: hasChange ? BRIO_GOLD : '#f5f5f5', color: hasChange ? '#fff' : '#bbb', cursor: hasChange ? 'pointer' : 'default' }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Give Access modal — table view. Lists ALL active users. Allocated people
          default to Read only; a supervisor assigned to the project defaults to Full.
          Admin rows are locked to Full, and only an admin (operatorIsAdmin) can change
          a supervisor's access. */}
      {giveAccessModal && (() => {
        const activeUsersList = (activeUsers || []).filter(u => u.isActive !== false && u.status !== 'inactive');
        const assignedIds = allocatedUserIdsForProject(allocations, project.id);
        const supervisorIds = supervisorIdsForProject(project, teams, teamProjects);
        return (
          <GiveAccessModal
            users={activeUsersList}
            grants={timeline._grantedAccess}
            assignedIds={assignedIds}
            supervisorIds={supervisorIds}
            operatorIsAdmin={isAdmin}
            selfEditableId={canGrantTimelineAccess && !isAdmin ? currentUser?.id : null}
            onSave={async (map) => {
              setGiveAccessModal(false);
              // Real projects save the grant map alone through save_project_timeline_grants; the
              // Template Builder's proxy has no such RPC and keeps the plain timeline save.
              if (isTemplateBuilder || !supabaseProjects?.saveTimelineGrants) {
                await persistTimeline({ ...timeline, _grantedAccess: map });
                return;
              }
              const result = await supabaseProjects.saveTimelineGrants(project.id, map);
              if (!result.success) alert('Could not save access: ' + result.error);
            }}
            onClose={() => setGiveAccessModal(false)}
          />
        );
      })()}

      {/* Stage Link Modal — link a custom stage to another existing stage */}
      {stageLinkModal && (() => {
        const otherStages = allRows.filter(r => r.indent === 0 && r.id !== stageLinkModal.customStageId);
        const currentLinked = stageLinkModal.currentLinked;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setStageLinkModal(null)}>
            <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: '280px', maxWidth: '360px', padding: '20px 24px', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Link Stage</div>
              <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '14px' }}>Choose a stage to link this custom stage to:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '220px', overflowY: 'auto', marginBottom: '16px', border: '1px solid ' + BRIO_BORDER }}>
                {currentLinked && (
                  <div
                    onClick={async () => {
                      const newTl = { ...timeline, [stageLinkModal.customStageId]: { ...(timeline[stageLinkModal.customStageId] || {}), _linkedToStage: null } };
                      delete newTl[stageLinkModal.customStageId]._linkedToStage;
                      setStageLinkModal(null);
                      await persistTimeline(newTl);
                    }}
                    style={{ padding: '7px 12px', fontSize: '11px', fontWeight: 400, color: BRIO_RED, cursor: 'pointer', borderBottom: '1px solid ' + BRIO_BORDER, fontFamily: 'Raleway, sans-serif' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    Remove link
                  </div>
                )}
                {otherStages.map(s => {
                  const label = timeline[s.id]?.taskName || s.defaultLabel || s.name || s.id;
                  const isLinked = currentLinked === s.id;
                  return (
                    <div
                      key={s.id}
                      onClick={async () => {
                        const newTl = { ...timeline, [stageLinkModal.customStageId]: { ...(timeline[stageLinkModal.customStageId] || {}), _linkedToStage: s.id } };
                        setStageLinkModal(null);
                        await persistTimeline(newTl);
                      }}
                      style={{ padding: '7px 12px', fontSize: '11px', fontWeight: isLinked ? 600 : 300, color: isLinked ? BRIO_GOLD : '#333', cursor: 'pointer', background: isLinked ? 'rgba(159,144,42,0.06)' : '#fff', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Raleway, sans-serif' }}
                      onMouseEnter={e => { if (!isLinked) e.currentTarget.style.background = '#f7f7f5'; }}
                      onMouseLeave={e => { if (!isLinked) e.currentTarget.style.background = '#fff'; }}
                    >
                      {isLinked && <Check size={11} color={BRIO_GOLD} />}
                      {label}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setStageLinkModal(null)} style={{ padding: '5px 14px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', color: '#555' }}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Predecessor editor modal */}
      {predecessorModal && (
        <PredecessorEditorModal
          value={predecessorModal.value}
          allRows={extendedRows}
          timeline={timeline}
          taskNumMap={taskNumMap}
          hierNumMap={displayNumMap}
          onSave={(newVal) => handleFieldChange(predecessorModal.rowKey, 'predecessors', newVal)}
          onClose={() => setPredecessorModal(null)}
        />
      )}

      {/* Link to SOW modal — Template Builder (selected SOW) or a project (its own confirmed scope) */}
      {linkSowModal && (
        <LinkToSowModal
          rowName={linkSowModal.rowName}
          rowStageKey={linkSowModal.rowStageKey}
          sowTemplate={linkSowTemplate}
          currentSowRef={linkSowModal.sowRef}
          editable={canEdit}
          onSave={(val, labels) => handleFieldChange(linkSowModal.rowId, 'sowRef', val, labels ? { sowRefLabels: labels } : null)}
          onClose={() => setLinkSowModal(null)}
        />
      )}

      {/* SOW-link badge menu — Add/Change link (opens the modal) · Remove link (clears it) */}
      {sowLinkMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10250 }} onClick={() => setSowLinkMenu(null)} onContextMenu={(e) => { e.preventDefault(); setSowLinkMenu(null); }} />
          <div style={{ position: 'fixed', left: Math.min(sowLinkMenu.x, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 180), top: sowLinkMenu.y, zIndex: 10251, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.16)', fontFamily: 'Raleway, sans-serif', minWidth: '160px', padding: '3px' }}
            onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { setLinkSowModal({ rowId: sowLinkMenu.rowId, rowName: sowLinkMenu.rowName, rowStageKey: sowLinkMenu.rowStageKey, sowRef: sowLinkMenu.sowRef }); setSowLinkMenu(null); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: '11.5px', fontWeight: 400, border: 'none', background: 'transparent', cursor: 'pointer', color: '#333', fontFamily: 'Raleway, sans-serif' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f7f6f2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >{sowLinkMenu.hasLink ? 'Change link…' : 'Add link…'}</button>
            {sowLinkMenu.hasLink && (
              <button
                onClick={() => { clearSowLink(sowLinkMenu.rowId); setSowLinkMenu(null); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: '11.5px', fontWeight: 400, border: 'none', background: 'transparent', cursor: 'pointer', color: '#b05050', fontFamily: 'Raleway, sans-serif' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fdf2f2'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >Remove link</button>
            )}
          </div>
        </>
      )}

      {/* Tab editor — pick which stages / part stages are visible in this tab */}
      {tabEditor && (() => {
        const items = [];
        allRows.filter(r => r.indent === 0).forEach(sr => {
          items.push({ id: sr.id, label: (timeline[sr.id]?.taskName) || sr.defaultLabel || sr.name, isPart: false });
          (partStagesMap[sr.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach(p => {
            items.push({ id: `substage_${sr.id}_${p.id}`, label: p.name || p.id, isPart: true });
          });
        });
        const toggle = (key) => setTabEditor(prev => {
          const k = new Set(prev.keys); if (k.has(key)) k.delete(key); else k.add(key); return { ...prev, keys: k };
        });
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setTabEditor(null)}>
            <div style={{ background: '#fff', width: '460px', maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>{tabEditor.tabId ? 'Tab settings' : 'New tab'}</span>
                <button onClick={() => setTabEditor(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={14} style={{ color: '#aaa' }} /></button>
              </div>
              <div style={{ padding: '14px 16px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 500, color: '#444', minWidth: '70px' }}>Tab name</span>
                  <input type="text" value={tabEditor.name} placeholder="e.g. Architecture"
                    onChange={e => setTabEditor(prev => ({ ...prev, name: e.target.value }))}
                    style={{ flex: 1, fontFamily: 'Raleway, sans-serif', fontSize: '12px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', padding: '4px 8px', outline: 'none' }} />
                </div>
                {!isStandalone && <>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Stages &amp; part stages to show</div>
                <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', maxHeight: '320px', overflowY: 'auto' }}>
                  {items.map((it, i) => {
                    const checked = tabEditor.keys.has(it.id);
                    return (
                      <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', paddingLeft: it.isPart ? '28px' : '10px', background: i % 2 === 0 ? '#fafaf8' : '#fff', cursor: 'pointer', borderBottom: i < items.length - 1 ? '1px solid ' + BRIO_BORDER : 'none' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggle(it.id)} style={{ accentColor: BRIO_GOLD }} />
                        <span style={{ fontSize: '11px', fontWeight: it.isPart ? 300 : 500, color: '#333' }}>{it.isPart ? `↳ ${it.label}` : it.label}</span>
                      </label>
                    );
                  })}
                </div>
                </>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '10px 16px', borderTop: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
                <div>
                  {tabEditor.tabId && tabEditor.tabId !== '__all__' && (
                    <button onClick={() => { if (window.confirm('Delete this tab?')) handleDeleteTab(tabEditor.tabId); }}
                      style={{ padding: '6px 14px', border: '1px solid ' + BRIO_BORDER, background: '#fff', fontSize: '11px', color: BRIO_RED, cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>Delete tab</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setTabEditor(null)} style={{ padding: '6px 16px', border: '1px solid ' + BRIO_BORDER, background: '#fff', fontSize: '11px', color: '#555', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>Cancel</button>
                  <button onClick={() => handleSaveTab(tabEditor)} style={{ padding: '6px 16px', border: 'none', background: BRIO_GOLD, fontSize: '11px', fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>{tabEditor.tabId ? 'Save' : 'Create tab'}</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reset timelines confirmation */}
      {showResetConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowResetConfirm(false)}>
          <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, padding: '28px 28px 22px', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#222', marginBottom: '10px' }}>Reset timelines?</div>
            <p style={{ fontSize: '12px', fontWeight: 300, color: '#444', marginBottom: '14px', lineHeight: 1.7 }}>
              This rebuilds the timeline from the confirmed scope of work and clears any applied
              template: stages return to their original order, start dates re-link to the stage
              immediately above, and template / custom rows, manual planned dates and task
              dependencies are cleared.
            </p>
            <p style={{ fontSize: '12px', fontWeight: 300, color: '#444', marginBottom: '22px', lineHeight: 1.7 }}>
              <strong style={{ fontWeight: 600 }}>Tracking dates and invoicing data are kept</strong> (contract / planned / actual / invoice / paid dates, invoice %, billing and part stages), as are your custom columns, view settings and settings presets.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowResetConfirm(false)}
                style={{ padding: '6px 16px', border: '1px solid ' + BRIO_BORDER, background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Cancel
              </button>
              <button onClick={handleResetTimelines}
                style={{ padding: '6px 16px', border: 'none', background: BRIO_GOLD, fontSize: '11px', fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                Reset timelines
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closure → Invoice Date modal */}
      {closureInvoiceModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, padding: '28px 28px 22px', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#222', marginBottom: '10px' }}>Stage Closure Marked as Done</div>
            <p style={{ fontSize: '12px', fontWeight: 300, color: '#444', marginBottom: '22px', lineHeight: 1.7 }}>
              <strong style={{ fontWeight: 600 }}>{closureInvoiceModal.stageName}</strong> has been marked as complete. Would you like to go to the Tracking page to update the invoice date for this stage?
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setClosureInvoiceModal(null)}
                style={{ padding: '6px 16px', border: '1px solid ' + BRIO_BORDER, background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Dismiss
              </button>
              {onNavigateToTracking && (
                <button
                  onClick={() => {
                    const { stageKey, stageName, invoiceDate } = closureInvoiceModal;
                    setClosureInvoiceModal(null);
                    onNavigateToTracking({ projectId: project.id, stageKey, stageName, invoiceDate });
                  }}
                  style={{ padding: '6px 16px', border: 'none', background: BRIO_GOLD, fontSize: '11px', fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  Go to Tracking
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tracking trigger prompt — date write is DEFERRED until user confirms here */}
      {trackingTriggerModal && (() => {
        const { triggerType, stageKey, partId, stageName, trackingField, dateStr, prevValue, commit } = trackingTriggerModal;
        const isPlannedChange = triggerType === TRIGGER_TYPES.TIMELINE_CHANGE;
        const isClosure = triggerType === TRIGGER_TYPES.STAGE_CLOSURE;
        const title = isPlannedChange ? 'Planned Date Updated' : isClosure ? 'Completion Date Set' : 'Tracking Date Updated';
        const body = isPlannedChange
          ? `The planned date for ${stageName} has changed significantly. The Tracking page will prompt you to notify the client of the revised date.`
          : isClosure
          ? `An actual completion date has been set for ${stageName}. The Tracking page will suggest sending a stage completion notification.`
          : `The tracking date for ${stageName} has been updated. Go to the Tracking page to review any pending actions.`;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', border: '1px solid ' + BRIO_BORDER, padding: '28px 28px 22px', maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', fontFamily: 'Raleway, sans-serif' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#222', marginBottom: '10px' }}>{title}</div>
              <p style={{ fontSize: '12px', fontWeight: 300, color: '#444', marginBottom: '22px', lineHeight: 1.7 }}>{body}</p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setTrackingTriggerModal(null)}
                  style={{ padding: '6px 16px', border: '1px solid ' + BRIO_BORDER, background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  Dismiss
                </button>
                {onNavigateToTracking && (
                  <button
                    onClick={() => {
                      commit?.(); // no-op when date was already saved eagerly
                      setTrackingTriggerModal(null);
                      // Pass the full trigger context so TimelinePage fires the workflow modal
                      onNavigateToTracking({
                        projectId: project.id,
                        stageKey,
                        partId,
                        triggerContext: {
                          triggerType,
                          stageKey,
                          partId,
                          stageName,
                          trackingField,
                          dateStr,
                          prevValue,
                        },
                      });
                    }}
                    style={{ padding: '6px 16px', border: 'none', background: BRIO_GOLD, fontSize: '11px', fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', letterSpacing: '0.02em' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Go to Tracking
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Toolbar — outside the scrollable table so it never stretches. Folded away with the
          page's project bar in full screen (toolbarsCollapsed) to give the table the height. ── */}
      {!toolbarsCollapsed && (
      <div style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', gap: '2px' }}>
                    {/* Left: Table / Gantt toggle — icons only */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '1px solid ' + BRIO_BORDER }}>
                      <button onClick={() => setViewMode('table')}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', borderRight: '1px solid ' + BRIO_BORDER, cursor: 'pointer', background: viewMode === 'table' ? BRIO_GOLD : 'transparent', color: viewMode === 'table' ? '#fff' : BRIO_GREY }}
                        title="Table view">
                        <Table size={12} />
                      </button>
                      <button onClick={() => setViewMode('gantt')}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', cursor: 'pointer', background: viewMode === 'gantt' ? BRIO_GOLD : 'transparent', color: viewMode === 'gantt' ? '#fff' : BRIO_GREY }}
                        title="Gantt chart view">
                        <BarChart3 size={12} />
                      </button>
                    </div>
                    {/* Disc colour legend — inline, right of view-toggle. Sideways-collapsible via the
                        chevron so it can tuck away when the grid needs the width. A standalone timeline
                        (BRM) has no task links or Tracking sync to explain, so it shows none. */}
                    {!isStandalone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '10px' }}>
                      <button
                        onClick={() => setLegendCollapsed(v => !v)}
                        title={legendCollapsed ? 'Show legend' : 'Hide legend'}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', color: BRIO_GREY, flexShrink: 0, padding: 0 }}>
                        {legendCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
                      </button>
                      {!legendCollapsed && (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
                          {[
                            { color: BRIO_GOLD,  label: 'Gold (On tasks)',  desc: 'Linked to Project internal tasks' },
                            { color: BRIO_GOLD,  label: 'Gold (On dates)',  desc: 'Synced correctly / dates match with tracking' },
                            { color: BRIO_RED,   label: 'Red',              desc: 'Incorrectly synced / dates don\'t match' },
                            { color: '#b0b0b0',  label: 'Grey',             desc: 'Not updated to actuals' },
                            { color: BRIO_GREEN, label: 'Green',            desc: 'Updated to actuals' },
                          ].map(item => (
                            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.color, flexShrink: 0, display: 'inline-block' }} />
                              <span style={{ fontSize: '9px', fontWeight: 400, color: '#888', fontFamily: 'Raleway, sans-serif', whiteSpace: 'nowrap' }}>
                                <span style={{ fontWeight: 600, color: '#666' }}>{item.label}</span>{' — '}{item.desc}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    )}
                    {/* Spacer pushes right-side icons to the right */}
                    <div style={{ flex: 1 }} />

                    {/* Right: frameless icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {/* Sync-status disc — a MOMENTARY "sync now" trigger + status indicator, left of the
                          tabs toggle. red = dates out of sync · grey = in sync. Clicking fires a one-time
                          sync; it never latches into a persistent "on" state. */}
                      {canEdit && !isStandalone && (
                        <ToolbarIcon
                          onClick={handleSyncIconClick}
                          color={syncMismatch ? BRIO_RED : BRIO_GREY}
                          tooltip={syncMismatch ? 'Dates out of sync — click to sync tracking now' : 'Sync tracking now'}
                        >
                          <RefreshCw size={13} strokeWidth={2.75} />
                        </ToolbarIcon>
                      )}
                      {/* Tabs (stage-filter views) toggle — left of the zoom controls */}
                      <ToolbarIcon onClick={() => setShowTabBar(v => !v)} tooltip={showTabBar ? 'Hide tabs' : 'Show tabs'} active={showTabBar}>
                        <Layers size={12} />
                      </ToolbarIcon>
                      {/* Zoom controls — compare view and table view have independent zoom levels */}
                      {compareSlots ? (
                        <>
                          <ToolbarIcon onClick={() => setCompareZoomIndex(i => Math.max(i - 1, 0))} tooltip="Zoom out (compare view)"><ZoomOut size={12} /></ToolbarIcon>
                          <ZoomResetLabel zoomLabel={PAGE_ZOOM_LEVELS[compareZoomIndex].label} onReset={() => setCompareZoomIndex(4)} />
                          <ToolbarIcon onClick={() => setCompareZoomIndex(i => Math.min(i + 1, PAGE_ZOOM_LEVELS.length - 1))} tooltip="Zoom in (compare view)"><ZoomIn size={12} /></ToolbarIcon>
                        </>
                      ) : (
                        <>
                          <ToolbarIcon onClick={() => setPageZoomIndex(i => Math.max(i - 1, 0))} tooltip="Zoom out"><ZoomOut size={12} /></ToolbarIcon>
                          <ZoomResetLabel zoomLabel={PAGE_ZOOM_LEVELS[pageZoomIndex].label} onReset={() => setPageZoomIndex(4)} />
                          <ToolbarIcon onClick={() => setPageZoomIndex(i => Math.min(i + 1, PAGE_ZOOM_LEVELS.length - 1))} tooltip="Zoom in"><ZoomIn size={12} /></ToolbarIcon>
                        </>
                      )}
                      {/* Show hidden rows toggle — appears only when at least one row is hidden */}
                      {hiddenRowCount > 0 && (
                        <ToolbarIcon
                          onClick={() => setShowHiddenRows(v => !v)}
                          tooltip={showHiddenRows ? 'Hide hidden rows' : `Show ${hiddenRowCount} hidden row${hiddenRowCount === 1 ? '' : 's'}`}
                          active={showHiddenRows}
                        >
                          <span style={{ fontSize: '9px', fontWeight: 500, minWidth: '12px', textAlign: 'center' }}>{hiddenRowCount}</span>
                        </ToolbarIcon>
                      )}
                      {/* Settings gear */}
                      <div style={{ position: 'relative' }}>
                        <ToolbarIcon onClick={e => { e.stopPropagation(); setShowExportMenu(v => !v); }} tooltip="Settings" active={showExportMenu}>
                          <Settings2 size={12} />
                        </ToolbarIcon>
                        {showExportMenu && (
                          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '185px', fontFamily: 'Raleway, sans-serif', borderRadius: '3px' }}
                            onClick={e => e.stopPropagation()}>
                            {canEdit && (
                              <button onClick={() => { setShowSettings(true); setShowExportMenu(false); setExportSubOpen(null); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <Settings2 size={11} /> Dependency Settings
                              </button>
                            )}
                            {canGiveAccess && (
                              <button onClick={() => { setGiveAccessModal(true); setShowExportMenu(false); setExportSubOpen(null); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <Users size={11} /> Give access
                              </button>
                            )}
                            {/* Tab settings — edit the active tab's stage selection (only when tabs exist) */}
                            {canEdit && tabs.length > 0 && (
                              <button onClick={() => {
                                const t = activeTab || tabs[0];
                                setTabEditor({ tabId: t.id, name: t.name, keys: new Set(t.keys || []) });
                                setShowExportMenu(false); setExportSubOpen(null);
                              }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <Layers size={11} /> Tab settings
                              </button>
                            )}

                            {/* ── Export ── */}
                            <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                              <button
                                onClick={() => setExportSubOpen(v => v === 'export' ? null : 'export')}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: exportSubOpen === 'export' ? BRIO_BG : 'transparent', fontSize: '11px', fontWeight: 400, color: '#333', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => { e.currentTarget.style.background = exportSubOpen === 'export' ? BRIO_BG : 'transparent'; }}>
                                <Download size={11} /> Export
                                <ChevronRight size={10} style={{ marginLeft: 'auto', transform: exportSubOpen === 'export' ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                              </button>
                              {exportSubOpen === 'export' && [
                                { label: 'PDF', icon: <FileText size={11} />, fn: () => openExportModal('pdf') },
                                { label: 'Excel', icon: <FileSpreadsheet size={11} />, fn: () => openExportModal('excel') },
                                { label: 'CSV', icon: <File size={11} />, fn: () => openExportModal('csv') },
                              ].map(item => (
                                <button key={item.label} onClick={item.fn}
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '6px 14px 6px 32px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {item.icon}{item.label}
                                </button>
                              ))}
                            </div>

                            {/* ── Export Comparison ── */}
                            <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                              <button
                                onClick={() => setExportSubOpen(v => v === 'compare' ? null : 'compare')}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: exportSubOpen === 'compare' ? BRIO_BG : 'transparent', fontSize: '11px', fontWeight: 400, color: '#333', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => { e.currentTarget.style.background = exportSubOpen === 'compare' ? BRIO_BG : 'transparent'; }}>
                                <Copy size={11} /> Export Comparison
                                <ChevronRight size={10} style={{ marginLeft: 'auto', transform: exportSubOpen === 'compare' ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                              </button>
                              {exportSubOpen === 'compare' && [
                                { label: 'PDF', icon: <FileText size={11} />, format: 'pdf' },
                                { label: 'Excel', icon: <FileSpreadsheet size={11} />, format: 'excel' },
                                { label: 'CSV', icon: <File size={11} />, format: 'csv' },
                              ].map(item => (
                                <button key={item.label} onClick={() => { setShowExportMenu(false); setExportSubOpen(null); setCompareModal({ format: item.format }); }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '6px 14px 6px 32px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {item.icon}{item.label}
                                </button>
                              ))}
                            </div>

                            {/* ── Import ── */}
                            <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                              <button
                                onClick={() => setExportSubOpen(v => v === 'import' ? null : 'import')}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: exportSubOpen === 'import' ? BRIO_BG : 'transparent', fontSize: '11px', fontWeight: 400, color: '#333', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => { e.currentTarget.style.background = exportSubOpen === 'import' ? BRIO_BG : 'transparent'; }}>
                                <Upload size={11} /> Import
                                <ChevronRight size={10} style={{ marginLeft: 'auto', transform: exportSubOpen === 'import' ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                              </button>
                              {exportSubOpen === 'import' && [
                                { label: 'Excel', icon: <FileSpreadsheet size={11} />, fn: () => { setShowExportMenu(false); setExportSubOpen(null); importFileRef.current.accept = '.xlsx,.xls'; importFileRef.current.click(); } },
                                { label: 'CSV', icon: <File size={11} />, fn: () => { setShowExportMenu(false); setExportSubOpen(null); importFileRef.current.accept = '.csv'; importFileRef.current.click(); } },
                              ].map(item => (
                                <button key={item.label} onClick={item.fn}
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '6px 14px 6px 32px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {item.icon}{item.label}
                                </button>
                              ))}
                            </div>

                            {/* ── Sync / view toggles / resets (bottom group) ──
                                Sync is a toggle (like Hide/Show notes): ON = Timelines dates auto-sync to
                                the Tracking page (and turning it on pushes the current dates once); OFF =
                                no date is ever synced to Tracking. Reset timelines keeps this setting.
                                Sync is admin/supervisor-only — granted "Full access" users don't see it. */}
                            {canSync && (
                              <button onClick={() => {
                                const next = !syncEnabled; syncEnabledRef.current = canSync && next; setSyncEnabled(next);
                                // Turning it back on is the supervisor's sign off: clear the "turned off by" note.
                                if (next && syncAutoOff) { const { _syncAutoOff, ...rest } = timeline; persistTimeline(rest); }
                                if (next && !syncPaused) handleSyncAllTracking();
                              }}
                                title="When on, the Timelines contract & planned dates auto-sync to the Tracking page. Turning it on pushes once ONLY if the sync disc isn't paused — while paused, nothing syncs until you click the disc. When off, no date is synced to Tracking. Reset timelines keeps this setting."
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', borderTop: '1px solid ' + BRIO_BORDER, background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#333', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                <RefreshCw size={11} /> <span>Sync to tracking</span>
                                <span style={{ marginLeft: 'auto', width: '26px', height: '14px', borderRadius: '7px', background: syncEnabled ? BRIO_GOLD : '#ccc', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                                  <span style={{ position: 'absolute', top: '2px', left: syncEnabled ? '14px' : '2px', width: '10px', height: '10px', borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                                </span>
                              </button>
                            )}
                            {canSync && !syncEnabled && syncAutoOff && (
                              <div style={{ padding: '0 14px 7px 33px', fontSize: '10px', fontWeight: 400, color: BRIO_RED, fontFamily: 'Raleway, sans-serif', lineHeight: '13px' }}>
                                Turned off: edited by {syncAutoOff.name ? String(syncAutoOff.name).replace(/\b\w/g, c => c.toUpperCase()) : 'a user with access'}
                                {syncAutoOff.at ? `, ${new Date(syncAutoOff.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                              </div>
                            )}
                            {/* Hide / Show cell notes — per-project toggle */}
                            <button onClick={() => setShowNotes(v => !v)}
                              title="Show or hide the small helper notes inside table cells (saved per project)"
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              {showNotes ? <Eye size={11} /> : <EyeOff size={11} />}
                              <span>Hide/Show notes</span>
                              <span style={{ marginLeft: 'auto', width: '26px', height: '14px', borderRadius: '7px', background: showNotes ? BRIO_GOLD : '#ccc', position: 'relative', transition: 'background 0.15s', flexShrink: 0 }}>
                                <span style={{ position: 'absolute', top: '2px', left: showNotes ? '14px' : '2px', width: '10px', height: '10px', borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                              </span>
                            </button>
                            {/* Reset column widths */}
                            <button onClick={() => { setColumnWidths({}); setTableMinWidthPx(null); setShowExportMenu(false); setExportSubOpen(null); }}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                              onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <RotateCcw size={11} /> Reset column widths
                            </button>

                            {/* ── Undo — steps back through the timeline's history one change at a time
                                (a template apply, a Reset timelines, or an edit), restoring the exact prior
                                state. Up to MAX_UNDO steps; greyed-out when there's nothing to undo. ── */}
                            {canEdit && (
                              <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                                <button onClick={canUndo ? () => { doUndo(); setShowExportMenu(false); setExportSubOpen(null); } : undefined}
                                  disabled={!canUndo}
                                  title={canUndo ? 'Undo the last change — reverts a template apply, a Reset timelines, or an edit to the exact prior state (repeat to step further back)' : 'Nothing to undo yet'}
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: canUndo ? BRIO_GOLD : '#bbb', cursor: canUndo ? 'pointer' : 'default', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                  onMouseEnter={e => { if (canUndo) e.currentTarget.style.background = BRIO_BG; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                  <RotateCcw size={11} /> Undo
                                </button>
                              </div>
                            )}
                            {/* ── Reset timelines (rebuild from confirmed scope; keeps tracking + invoicing data) ── */}
                            {canEdit && (
                              <div style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                                <button onClick={() => { setShowResetConfirm(true); setShowExportMenu(false); setExportSubOpen(null); }}
                                  title="Revert stage order and start-date presets to the original scope-driven state. Tracking and invoicing dates are kept."
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#666', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  <RotateCcw size={11} /> Reset timelines
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <input ref={importFileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleImportFile} />
                      </div>
                    </div>
      </div>
      )}{/* end toolbar */}

      {/* Tab bar — stage/part filter views. First tab ("Project timelines") shows everything. */}
      {showTabBar && !compareSlots && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 8px', borderTop: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG, flexWrap: 'wrap', fontFamily: 'Raleway, sans-serif' }}>
          {[{ id: '__all__', name: mainTabName(timeline), fixed: true }, ...tabs].map(t => {
            const isActive = activeTabId === t.id;
            return (
              <div key={t.id}
                onClick={() => setActiveTabId(t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', fontSize: '11px', fontWeight: isActive ? 600 : 400, color: isActive ? '#fff' : '#555', background: isActive ? BRIO_GOLD : '#fff', border: '1px solid ' + (isActive ? BRIO_GOLD : BRIO_BORDER), borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <span>{t.name}</span>
                {(!t.fixed || isStandalone) && canEdit && isActive && (
                  <span onClick={(e) => { e.stopPropagation(); setTabEditor({ tabId: t.id, name: t.name, keys: new Set(t.keys || []) }); }}
                    title="Edit tab" style={{ display: 'inline-flex', cursor: 'pointer', opacity: 0.85 }}><Settings2 size={10} /></span>
                )}
                {!t.fixed && canEdit && (
                  <span onClick={(e) => { e.stopPropagation(); if (window.confirm(isStandalone ? `Delete tab "${t.name}"? Its stages move to "${mainTabName(timeline)}".` : `Delete tab "${t.name}"?`)) handleDeleteTab(t.id); }}
                    title="Delete tab" style={{ display: 'inline-flex', cursor: 'pointer', opacity: 0.7 }}>×</span>
                )}
              </div>
            );
          })}
          {canEdit && (
            <span onClick={() => setTabEditor({ tabId: null, name: '', keys: new Set() })}
              title="Add tab" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', border: '1px dashed ' + BRIO_BORDER, borderRadius: '3px', cursor: 'pointer', color: BRIO_GREY }}>
              <Plus size={13} />
            </span>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* INLINE COMPARE VIEW — shown instead of the table when compareSlots is set */}
      {/* ================================================================ */}
      {compareSlots && (() => {
        const vSlotLabel = (src) => {
          if (src === 'current') return 'Current Timeline';
          const v = projectVersions.find(pv => pv.id === src);
          if (!v) return src;
          const d = new Date(v.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
          return `${v.label} — ${d}`;
        };
        const vSlotTimeline = (src) => src === 'current' ? timeline : (projectVersions.find(pv => pv.id === src)?.snapshot || {});
        const vSlotRows = (src) => {
          if (src === 'current') return displayRows;
          const tl = vSlotTimeline(src);
          return (tl._rows || []).map(r => ({ ...r, key: r.id, name: tl[r.id]?.taskName || r.defaultLabel || STAGE_NAMES[r.id] || (String(r.id).startsWith('row_') ? '' : r.id) }));
        };
        const vCellVal = (src, row, colKey) => {
          const rowKey = row.key || row.id;
          const tl = vSlotTimeline(src);
          const d = tl[rowKey] || {};
          if (colKey === 'taskName') {
            if (d.taskName) return d.taskName;
            if (row.name) return row.name;
            if (row.defaultLabel) return row.defaultLabel;
            if (STAGE_NAMES[rowKey]) return STAGE_NAMES[rowKey];
            if (rowKey?.startsWith('closure_')) return `Closure of ${STAGE_NAMES[rowKey.slice('closure_'.length)] || rowKey.slice('closure_'.length)} Stage`;
            if (rowKey?.startsWith('invoicing_')) return `Invoicing of ${STAGE_NAMES[rowKey.slice('invoicing_'.length)] || rowKey.slice('invoicing_'.length)} Stage`;
            return rowKey || '';
          }
          if (Array.isArray(d[colKey])) return d[colKey].join(', ');
          return d[colKey] != null ? String(d[colKey]) : '';
        };
        const slotsWithCols = compareSlots.map(slot => ({
          slot,
          cols: effectiveVisibleColumns.filter(c => c.key !== 'taskName' && slot.colKeys.has(c.key)),
        })).filter(({ cols }) => cols.length > 0);
        // Fixed pixel width per compare data column — the <col> widths below use the same helper so the
        // table's total width is exact. With table-layout:fixed + this explicit total (rather than
        // width:100%), widening the Task/Deliverable column grows the table instead of squeezing the
        // other columns, and the container scrolls horizontally when the total exceeds the viewport —
        // matching the main timelines table.
        const cmpColW = (c) => c.key === 'plannedDuration' ? 52 : c.key === 'percentComplete' ? 72 : c.type === 'date' ? 90 : 96;
        const cmpTotalW = 30 + compareTaskColW + slotsWithCols.reduce((s, { cols }) => s + cols.reduce((a, c) => a + cmpColW(c), 0), 0);
        const primaryRows = vSlotRows(compareSlots[0].source);
        const cVtd = { ...tdStyle, borderBottom: '1px solid ' + BRIO_BORDER };
        let cTaskN = 0;

        const updateCmpSlot = (i, patch) => setCompareSlots(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
        const toggleCmpSlotCol = (i, key) => setCompareSlots(prev => prev.map((s, idx) => {
          if (idx !== i) return s;
          const next = new Set(s.colKeys);
          if (next.has(key)) next.delete(key); else next.add(key);
          return { ...s, colKeys: next };
        }));
        const versionOptions = [
          { value: 'current', label: 'Current Timeline' },
          ...projectVersions.map(v => ({ value: v.id, label: vSlotLabel(v.id) })),
        ];
        const slotLabels = ['A', 'B', 'C', 'D'];

        const renderReadonlyCell = (src, row, c, ci) => {
          const val = vCellVal(src, row, c.key);
          const lb = ci === 0 ? { borderLeft: '2px solid ' + BRIO_BORDER } : {};
          if (c.type === 'date') {
            const d = parseDate(val);
            return <td key={src + c.key} style={{ ...cVtd, textAlign: 'center', fontSize: '11px', color: d ? '#333' : BRIO_GREY, padding: '4px 3px', ...lb }}>{d ? formatDateDisplay(d) : ''}</td>;
          }
          if (c.key === 'scheduleStatus') {
            const opt = STATUS_OPTIONS.find(o => o.value === val) || STATUS_OPTIONS[0];
            return <td key={src + c.key} style={{ ...cVtd, textAlign: 'center', padding: '3px', ...lb }}><span style={{ fontSize: '10px', fontWeight: 400, color: opt.color, background: opt.bg, padding: '2px 5px', borderRadius: '2px', display: 'inline-block', whiteSpace: 'nowrap' }}>{opt.label}</span></td>;
          }
          if (c.key === 'percentComplete') {
            const pct = val != null ? Number(val) : 0;
            return <td key={src + c.key} style={{ ...cVtd, textAlign: 'center', padding: '3px 4px', ...lb }}><div style={{ fontSize: '11px', fontWeight: 300 }}>{pct || 0}%</div><div style={{ height: '3px', background: '#eee', borderRadius: '2px', margin: '2px 0 0' }}><div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? BRIO_GREEN : BRIO_GOLD, borderRadius: '2px' }} /></div></td>;
          }
          if (c.key === 'plannedDuration') {
            return <td key={src + c.key} style={{ ...cVtd, textAlign: 'center', fontSize: '11px', color: val ? '#333' : BRIO_GREY, ...lb }}>{val ? val + 'd' : ''}</td>;
          }
          return <td key={src + c.key} style={{ ...cVtd, fontSize: '11px', color: val ? '#333' : BRIO_GREY, padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...lb }}>{val}</td>;
        };

        const renderEditableCell = (rowId, c, ci) => {
          const val = vCellVal('current', { key: rowId, id: rowId }, c.key);
          const lb = ci === 0 ? { borderLeft: '2px solid ' + BRIO_BORDER } : {};
          const inputBase = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, border: 'none', outline: 'none', background: 'transparent', width: '100%', color: '#333' };
          if (c.type === 'date') {
            const d = parseDate(val);
            // Computed columns (e.g. plannedEndDate) are auto-calculated — show read-only formatted text
            if (c.computed) {
              return <td key={'cur' + c.key} style={{ ...cVtd, textAlign: 'center', fontSize: '11px', color: d ? '#333' : BRIO_GREY, padding: '4px 3px', ...lb }}>{d ? formatDateDisplay(d) : ''}</td>;
            }
            return (
              <td key={'cur' + c.key} style={{ ...cVtd, textAlign: 'center', padding: '2px 3px', ...lb }}>
                <BrioDatePicker
                  value={val || ''}
                  onChange={(v) => handleFieldChange(rowId, c.key, v || null)}
                  placeholder="" clearable={true} centerAlign={true} as="button"
                  className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                  style={{ width: '100%' }}
                  triggerStyle={{ fontSize: '11px', fontWeight: 300, fontFamily: 'Raleway, sans-serif', color: d ? '#333' : BRIO_GREY, width: '100%' }}
                />
              </td>
            );
          }
          if (c.key === 'scheduleStatus') {
            return (
              <td key={'cur' + c.key} style={{ ...cVtd, textAlign: 'center', padding: '2px 3px', ...lb }}>
                <select defaultValue={val || ''} onChange={e => handleFieldChange(rowId, c.key, e.target.value || null)}
                  style={{ ...inputBase, cursor: 'pointer' }}>
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </td>
            );
          }
          if (c.key === 'percentComplete') {
            const pct = val != null ? Number(val) : 0;
            return (
              <td key={'cur' + c.key} style={{ ...cVtd, textAlign: 'center', padding: '2px 4px', ...lb }}>
                <input type="number" min={0} max={100} defaultValue={pct} onBlur={e => handleFieldChange(rowId, c.key, Number(e.target.value))}
                  style={{ ...inputBase, textAlign: 'center', width: '36px' }} />
                <span style={{ fontSize: '10px', color: BRIO_GREY }}>%</span>
                <div style={{ height: '3px', background: '#eee', borderRadius: '2px', margin: '2px 0 0' }}><div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? BRIO_GREEN : BRIO_GOLD, borderRadius: '2px' }} /></div>
              </td>
            );
          }
          if (c.key === 'plannedDuration') {
            return (
              <td key={'cur' + c.key} style={{ ...cVtd, textAlign: 'center', padding: '2px 3px', ...lb }}>
                <input type="number" min={0} defaultValue={val || ''} onBlur={e => handleFieldChange(rowId, c.key, e.target.value ? Number(e.target.value) : null)}
                  style={{ ...inputBase, textAlign: 'center', width: '32px' }} />
                {val ? <span style={{ fontSize: '10px', color: BRIO_GREY }}>d</span> : null}
              </td>
            );
          }
          return (
            <td key={'cur' + c.key} style={{ ...cVtd, padding: '2px 6px', ...lb }}>
              <input type="text" defaultValue={val} onBlur={e => handleFieldChange(rowId, c.key, e.target.value || null)}
                style={{ ...inputBase, overflow: 'hidden', textOverflow: 'ellipsis' }} />
            </td>
          );
        };

        return (
          <div style={{ fontFamily: 'Raleway, sans-serif' }}>
            {/* Banner — mirrors VersionTablePreview banner style */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px', background: '#fffbef', border: '1px solid #e8d87a', marginBottom: 0, fontSize: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, color: BRIO_GOLD }}>Compare</span>
              <span style={{ fontWeight: 300, color: BRIO_GREY, fontSize: '11px' }}>
                {compareSlots.map(s => s.source === 'current' ? 'Current' : vSlotLabel(s.source)).join(' ↔ ')}
              </span>
              <span style={{ fontWeight: 300, color: '#ccc' }}>·</span>
              <span style={{ fontWeight: 300, color: BRIO_GREY, fontSize: '11px' }}>Current timeline is editable · Snapshots are read-only</span>
              <button onClick={() => setShowCompareConfig(v => !v)}
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 400, color: showCompareConfig ? BRIO_GOLD : BRIO_GREY, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = showCompareConfig ? BRIO_GOLD : BRIO_GREY; }}>
                <Settings2 size={11} /> Configure
              </button>
              <button onClick={() => { setCompareSlots(null); setShowCompareConfig(false); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 400, color: BRIO_GREY, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}>
                <X size={11} /> Close
              </button>
            </div>

            {/* Config panel (collapsible) */}
            {showCompareConfig && (
              <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG, display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {compareSlots.map((slot, i) => (
                  <div key={i} style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', minWidth: '220px', flex: '1 1 220px', maxWidth: '360px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: '#fff', borderBottom: '1px solid ' + BRIO_BORDER }}>
                      <span style={{ fontSize: '10px', fontWeight: 600, color: BRIO_GREY, minWidth: '18px' }}>
                        {slotLabels[i] || (i + 1)}
                      </span>
                      <select value={slot.source}
                        onChange={e => updateCmpSlot(i, { source: e.target.value })}
                        style={{ flex: 1, border: '1px solid ' + BRIO_BORDER, fontFamily: 'Raleway, sans-serif', fontSize: '11px', padding: '3px 6px', background: '#fff', color: '#333', outline: 'none' }}>
                        {versionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {compareSlots.length > 2 && i > 0 && (
                        <button onClick={() => setCompareSlots(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#bbb', display: 'inline-flex', padding: '2px' }}>
                          <X size={11} />
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', padding: '5px 10px 0' }}>
                      <button type="button"
                        onClick={() => updateCmpSlot(i, { colKeys: new Set(effectiveVisibleColumns.filter(c => c.key !== 'taskName').map(c => c.key)) })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BRIO_GOLD, fontFamily: 'Raleway, sans-serif', fontSize: '9.5px', fontWeight: 500 }}>Select all</button>
                      <span style={{ color: '#ccc', fontSize: '9.5px' }}>·</span>
                      <button type="button"
                        onClick={() => updateCmpSlot(i, { colKeys: new Set() })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BRIO_GREY, fontFamily: 'Raleway, sans-serif', fontSize: '9.5px', fontWeight: 500 }}>Select none</button>
                    </div>
                    <div style={{ padding: '6px 10px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px 8px' }}>
                      {effectiveVisibleColumns.filter(c => c.key !== 'taskName').map(c => (
                        <label key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: '#333', cursor: 'pointer', fontWeight: 300 }}>
                          <input type="checkbox" checked={slot.colKeys.has(c.key)} onChange={() => toggleCmpSlotCol(i, c.key)} style={{ accentColor: BRIO_GOLD }} />
                          {columnLabels[c.key] || c.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {compareSlots.length < 4 && (
                  <button onClick={() => {
                    const defaultColKeys = new Set(effectiveVisibleColumns.filter(c => c.key !== 'taskName').map(c => c.key));
                    setCompareSlots(prev => [...prev, { source: projectVersions[0]?.id || 'current', colKeys: defaultColKeys }]);
                  }}
                    style={{ alignSelf: 'stretch', minWidth: '120px', border: '1px dashed ' + BRIO_BORDER, background: 'transparent', padding: '8px 14px', cursor: 'pointer', fontSize: '11px', color: BRIO_GREY, fontFamily: 'Raleway, sans-serif' }}>
                    + Add source
                  </button>
                )}
              </div>
            )}

            {/* Comparison table — same styling as VersionTablePreview. overflowX:auto + an explicit
                pixel table width (below) give it a real horizontal scrollbar when many columns are
                selected, exactly like the main timelines table. */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', zoom: PAGE_ZOOM_LEVELS[compareZoomIndex].scale, maxHeight: `calc((100vh - ${isFullscreen ? 220 : 310}px) / ${PAGE_ZOOM_LEVELS[compareZoomIndex].scale})`, border: '1px solid ' + BRIO_BORDER, background: '#fff', borderTop: showCompareConfig ? '1px solid ' + BRIO_BORDER : 'none' }}>
              <table style={{ width: cmpTotalW + 'px', minWidth: cmpTotalW + 'px', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, fontSize: '12px', fontWeight: 300, fontFamily: 'Raleway, sans-serif', borderTop: '1px solid ' + BRIO_BORDER, borderLeft: '1px solid ' + BRIO_BORDER }}>
                <colgroup>
                  <col style={{ width: '30px' }} />
                  <col style={{ width: compareTaskColW + 'px' }} />
                  {slotsWithCols.flatMap(({ cols }) => cols.map(c => (
                    <col key={c.key} style={{ width: cmpColW(c) + 'px' }} />
                  )))}
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
                  <tr style={{ background: BRIO_BG }}>
                    <th rowSpan={2} style={{ ...thStyle, textAlign: 'center', width: '30px', padding: '8px 4px', position: 'sticky', left: 0, zIndex: 6, background: BRIO_BG }}>#</th>
                    <th rowSpan={2} style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: '30px', zIndex: 5, boxShadow: '1px 0 0 0 ' + BRIO_BORDER, paddingLeft: '8px' }}>
                      Task / Deliverable
                      {/* Drag to widen/narrow only this compare column (does not affect the main table) */}
                      <div onMouseDown={startCompareTaskResize} title="Drag to resize column"
                        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 7 }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.35)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }} />
                    </th>
                    {slotsWithCols.map(({ slot, cols }, si) => (
                      <th key={si} colSpan={cols.length} style={{ ...thStyle, textAlign: 'center', borderLeft: '2px solid ' + BRIO_BORDER, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slot.source === 'current' ? <span style={{ color: BRIO_GOLD }}>Current (editable)</span> : vSlotLabel(slot.source)}
                      </th>
                    ))}
                  </tr>
                  <tr style={{ background: BRIO_BG }}>
                    {slotsWithCols.flatMap(({ slot, cols }, si) => cols.map((c, ci) => (
                      <th key={si + '_' + c.key} style={{ ...thStyle, textAlign: 'center', borderLeft: ci === 0 ? '2px solid ' + BRIO_BORDER : undefined }}>
                        {columnLabels[c.key] || c.label}
                      </th>
                    )))}
                  </tr>
                </thead>
                <tbody>
                  {primaryRows.map(row => {
                    const isStage = (row.indent || 0) === 0;
                    const rowBg = isStage ? (STAGE_ROW_COLORS[row.id] || '#f4f4f0') : '#fff';
                    const num = isStage ? '' : (++cTaskN);
                    const rowId = row.key || row.id;
                    return (
                      <tr key={rowId} style={{ background: rowBg }}>
                        <td style={{ ...cVtd, textAlign: 'center', fontSize: '11px', color: BRIO_GREY, padding: '4px', position: 'sticky', left: 0, zIndex: 2, background: rowBg }}>{num}</td>
                        <td style={{ ...cVtd, padding: `4px 6px 4px ${(row.indent || 0) * 12 + 8}px`, fontWeight: isStage ? 500 : 300, color: isStage ? '#333' : '#444', position: 'sticky', left: '30px', zIndex: 1, background: rowBg, boxShadow: '1px 0 0 0 ' + BRIO_BORDER, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {(() => {
                            const primSrc = compareSlots[0].source;
                            const d = vSlotTimeline(primSrc)[row.key || row.id] || {};
                            const isCF = !!d._isCarriedForward;
                            const cfPrefix = isCF && d._cfFromLabel ? `${d._cfFromLabel}_ ` : '';
                            return (
                              <>
                                {cfPrefix && <span style={{ color: '#555', fontWeight: 400 }}>{cfPrefix}</span>}
                                {vCellVal(primSrc, row, 'taskName')}
                                {isCF && <span style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', marginLeft: '3px' }}>- CF</span>}
                              </>
                            );
                          })()}
                        </td>
                        {slotsWithCols.flatMap(({ slot, cols }, si) => cols.map((c, ci) =>
                          slot.source === 'current' && canEdit && !isStage
                            ? renderEditableCell(rowId, c, ci)
                            : renderReadonlyCell(slot.source, row, c, ci)
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <div
        ref={tableScrollRef}
        onScroll={e => { if (mirrorScrollRef.current) mirrorScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; }}
        style={{ overflowX: 'auto', overflowY: 'auto', zoom: PAGE_ZOOM_LEVELS[pageZoomIndex].scale, scrollbarWidth: 'none', maxHeight: `calc((100vh - ${isFullscreen ? (toolbarsCollapsed ? 80 : 180) : 260}px) / ${PAGE_ZOOM_LEVELS[pageZoomIndex].scale})`, display: compareSlots ? 'none' : undefined }}
        className="timeline-table-scroll"
      >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          <table ref={tableRef} style={{
            // When tableMinWidthPx is set (after first collapse/resize), pin the table to exactly
            // that width so collapsing/expanding groups doesn't redistribute space to other columns.
            width: tableMinWidthPx ? tableMinWidthPx + 'px' : '100%',
            minWidth: tableMinWidthPx ? tableMinWidthPx + 'px' : '100%',
            // Full screen: centre a table narrower than the screen (auto margins resolve to 0 once
            // it is wider, so a wide table still scrolls from the left).
            ...(isFullscreen ? { marginLeft: 'auto', marginRight: 'auto' } : {}),
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            fontSize: '12px',
            fontWeight: 300,
            fontFamily: 'Raleway, sans-serif',
            borderTop: '1px solid ' + BRIO_BORDER,
            borderBottom: '1px solid ' + BRIO_BORDER,
          }}>
            {/* colgroup gives table-layout:fixed the authoritative column widths */}
            <colgroup>
              <col style={{ width: '30px' }} />
              {effectiveVisibleColumns.map(col => (
                <col key={col.key} style={{ width: col.noResize ? col.width : (columnWidths[col.key] || col.width) }} />
              ))}
            </colgroup>
            <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
              {/* ── Flat single-row header ── */}
              {(() => {
                const taskNameCol = effectiveVisibleColumns.find(c => c.key === 'taskName');
                const nonTaskCols = effectiveVisibleColumns.filter(c => c.key !== 'taskName');
                const colThStyle = (col) => ({
                  ...cellStyle(col, true),
                  fontSize: '10px',
                  padding: (['plannedDuration','plannedStartDate','plannedEndDate','predecessors'].includes(col.key)) ? '3px 2px' : '3px 4px',
                  cursor: colDragActive === col.key ? 'grabbing' : (colDragActive ? 'copy' : 'default'),
                  outline: colDragOver === col.key && colDragActive && colDragActive !== col.key ? '2px solid ' + BRIO_GOLD : 'none',
                  outlineOffset: '-2px',
                  background: selectedColumnKeys.has(col.key) ? 'rgba(159,144,42,0.13)' : BRIO_BG,
                  boxShadow: selectedColumnKeys.has(col.key)
                    ? (col.sticky ? '1px 0 0 0 ' + BRIO_BORDER + ', inset 0 -2px 0 ' + BRIO_GOLD + ', inset 0 1px 0 ' + BRIO_BORDER : 'inset 0 -2px 0 ' + BRIO_GOLD + ', inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER)
                    : (col.sticky ? '1px 0 0 0 ' + BRIO_BORDER + ', inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER : 'inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER),
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  ...((col.key === 'remarks' || col.key === 'predecessors') ? { whiteSpace: 'nowrap', wordBreak: 'normal', overflow: 'hidden' } : {}),
                });
                const resizeHandle = (colKey) => (
                  <div
                    onMouseDown={(e) => handleResizeStart(e, colKey)}
                    style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '4px', cursor: 'col-resize', background: 'transparent' }}
                  />
                );
                const colThHandlers = (col) => ({
                  onContextMenu: (e) => handleHeaderContextMenu(e, col.key),
                  onMouseDown: (e) => handleColHeaderMouseDown(e, col.key),
                  onMouseUp: handleColHeaderMouseUp,
                  onMouseLeave: handleColHeaderMouseUp,
                  onTouchStart: (e) => handleColHeaderMouseDown(e, col.key),
                  onTouchEnd: handleColHeaderMouseUp,
                  onTouchCancel: handleColHeaderMouseUp,
                });
                return (
                  <tr style={{ background: BRIO_BG }}>
                    <th style={{ ...thStyle, textAlign: 'center', width: '30px', minWidth: '30px', padding: '10px 4px', position: 'sticky', left: 0, zIndex: 3, background: BRIO_BG, borderLeft: '1px solid ' + BRIO_BORDER }}>
                      #
                    </th>
                    {taskNameCol && (
                      <th
                        data-col-key="taskName"
                        data-min-width={taskNameCol.minWidth ? parseInt(taskNameCol.minWidth) : 80}
                        style={{
                          ...cellStyle(taskNameCol, true),
                          cursor: colDragActive === 'taskName' ? 'grabbing' : (colDragActive ? 'copy' : 'default'),
                          outline: colDragOver === 'taskName' && colDragActive && colDragActive !== 'taskName' ? '2px solid ' + BRIO_GOLD : 'none',
                          outlineOffset: '-2px',
                          background: selectedColumnKeys.has('taskName') ? 'rgba(159,144,42,0.13)' : BRIO_BG,
                          boxShadow: selectedColumnKeys.has('taskName') ? 'inset 0 -2px 0 ' + BRIO_GOLD + ', inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER : 'inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER,
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none',
                        }}
                        {...colThHandlers(taskNameCol)}
                      >
                        {bulkCollapseIds.length > 0 && (() => {
                          const nextTip = {
                            expanded: 'Minimise all stages, contract dates minimised',
                            minStrips: 'Minimise all stages, contract dates expanded',
                            minOpen: 'Expand all stages',
                          }[bulkCollapseMode];
                          const Icon = bulkCollapseMode === 'expanded' ? ChevronsDown : ChevronsRight;
                          return (
                            <span
                              title={nextTip}
                              onMouseDown={e => e.stopPropagation()}
                              onContextMenu={e => e.stopPropagation()}
                              onDoubleClick={e => e.stopPropagation()}
                              onClick={e => { e.stopPropagation(); cycleBulkCollapse(); }}
                              style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: bulkCollapseMode === 'minOpen' ? BRIO_GOLD : BRIO_GREY, zIndex: 1 }}
                            >
                              <Icon size={14} />
                            </span>
                          );
                        })()}
                        {renamingColumn === 'taskName' ? (
                          <input
                            autoFocus type="text" value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={commitRename}
                            onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenamingColumn(null); setRenameValue(''); } }}
                            style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 500, width: '100%', border: '1px solid ' + BRIO_GOLD, padding: '2px 4px', outline: 'none', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                          />
                        ) : (columnLabels['taskName'] || taskNameCol.label)}
                        {!taskNameCol.noResize && resizeHandle('taskName')}
                      </th>
                    )}
                    {nonTaskCols.map(col => {
                      const isCtxHeader = headerContextMenu && headerContextMenu.colKey === col.key;
                      return (
                        <th
                          key={col.key}
                          data-col-key={col.key}
                          data-min-width={col.minWidth ? parseInt(col.minWidth) : (col.custom ? CUSTOM_COL_MIN_WIDTH : 60)}
                          style={{ ...colThStyle(col), ...(isCtxHeader ? { boxShadow: 'inset 0 0 0 2px ' + BRIO_GOLD + ', inset 0 0 10px rgba(159,144,42,0.08)' } : {}) }}
                          {...colThHandlers(col)}
                        >
                          {renamingColumn === col.key ? (
                            <input
                              autoFocus type="text" value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onBlur={commitRename}
                              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenamingColumn(null); setRenameValue(''); } }}
                              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 500, width: '100%', border: '1px solid ' + BRIO_GOLD, padding: '2px 4px', outline: 'none', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                            />
                          ) : (columnLabels[col.key] || col.label)}
                          {!col.noResize && resizeHandle(col.key)}
                        </th>
                      );
                    })}
                  </tr>
                );
              })()}
            </thead>
            <tbody>
              {(() => {
                // Precompute which displayRows have a hidden custom stage group immediately above them
                const hiddenStageAbove = new Set();
                if (!showHiddenRows) {
                  const displayRowIdSet = new Set(displayRows.map(r => r.id));
                  let pendingHidden = false;
                  for (const ar of allRows) {
                    if (isCustomStage(ar.id) && ar.indent === 0 && timeline[ar.id]?._hidden) {
                      pendingHidden = true;
                    } else if (displayRowIdSet.has(ar.id)) {
                      if (pendingHidden) { hiddenStageAbove.add(ar.id); pendingHidden = false; }
                    }
                  }
                }
                return displayRows.map((row, rowIndex) => {
                const isStage = row.indent === 0;
                const hasPrecedingHiddenStage = hiddenStageAbove.has(row.id);
                const hiddenStageDivider = hasPrecedingHiddenStage ? (
                  <tr key={row.id + '__hidden_divider'}>
                    <td colSpan={effectiveVisibleColumns.length + 1} style={{ padding: 0, height: '3px' }}>
                      <div style={{ width: '50%', height: '2px', background: BRIO_BORDER }} title="One or more custom stages are hidden at this location — right-click any row to unhide" />
                    </td>
                  </tr>
                ) : null;

                // Template Builder stage filter — skip rendering rows of unselected stages. rowIndex
                // (and all numbering/dependencies) stay tied to the full displayRows, so hiding a
                // stage never renumbers or breaks predecessors in the stages that remain visible.
                if (!isStageVisible(row.id)) return null;

                // Sub-stage header row — draggable via long-press on # column
                if (row.isSubStageHeader) {
                  return (
                    <React.Fragment key={row.id}>
                      {hiddenStageDivider}
                    <SortableRow key={row.id + '__sr'} id={row.id} disabled={!canEdit} style={{ borderTop: '1px solid ' + BRIO_BORDER, background: '#f0ede8' }}>
                      {(dragListeners) => (
                        <>
                          <td
                            style={{ ...tdStyle, textAlign: 'center', width: '30px', minWidth: '30px', color: BRIO_GREY, cursor: canEdit ? 'grab' : 'default', userSelect: 'none', WebkitUserSelect: 'none', touchAction: canEdit ? 'none' : 'auto', position: 'sticky', left: 0, zIndex: 2, background: '#f0ede8', borderLeft: '1px solid ' + BRIO_BORDER, ...(contextMenu && contextMenu.colKey === null && contextMenu.rowIndex === rowIndex ? { boxShadow: 'inset 0 0 0 2px ' + BRIO_GOLD + ', inset 0 0 10px rgba(159,144,42,0.08)' } : {}) }}
                            title={canEdit ? 'long press to reorder sub-stage' : ''}
                            onContextMenu={(e) => handleContextMenu(e, rowIndex, null)}
                            {...(canEdit ? dragListeners : {})}
                          />
                          {effectiveVisibleColumns.map((col, ci) => {
                            const subTdStyle = { ...cellStyle(col, false), background: '#f0ede8', padding: col.key === 'taskName' ? '0' : '2px 6px', ...(col.key === 'taskName' ? { verticalAlign: 'top' } : {}) };
                            // taskName col (ci===0) uses per-section menus; other cols keep standard highlight
                            const isCtxCell = ci !== 0 && contextMenu && contextMenu.rowIndex === rowIndex && contextMenu.colKey === col.key;
                            if (isCtxCell) subTdStyle.boxShadow = 'inset 0 0 0 2px ' + BRIO_GOLD + ', inset 0 0 10px rgba(159,144,42,0.08)';
                            return (
                              <td key={col.key} style={subTdStyle}
                                onContextMenu={ci === 0 ? (canEdit ? (e) => handleContextMenu(e, rowIndex, 'taskName') : (e) => e.preventDefault()) : (e) => handleContextMenu(e, rowIndex, col.key)}>
                                {ci === 0 ? (() => {
                                  const subRowData = timeline[row.key] || {};
                                  // Strip uses contract-specific fields; planned schedule columns are unaffected
                                  const storedDuration = subRowData.contractDuration ?? null;
                                  const storedStart = subRowData.contractStartDate || null;
                                  const subStoredEnd = subRowData.contractEndDate || null;
                                  const subEffectiveEnd = subStoredEnd ? parseDate(subStoredEnd) : null;
                                  // Disc: compare strip contractEndDate vs partStage contractDate (Contract column in tracking)
                                  // Gold = match, Red = mismatch or one side is empty
                                  const subPartData = (timeline[row.stageId]?.partStages || []).find(p => p.id === row.partId);
                                  const subContractDate = subPartData?.contractDate || null;
                                  const subDiscColor = (!subStoredEnd && !subContractDate) ? null
                                    : (subStoredEnd && subContractDate && subStoredEnd === subContractDate) ? BRIO_GOLD
                                    : BRIO_RED;
                                  const stripInputBase = { fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 300, border: '1px solid transparent', borderRadius: '0', background: 'transparent', padding: '2px 4px', outline: 'none', opacity: 1 };
                                  const computedEndStr = subEffectiveEnd ? formatDate(subEffectiveEnd) : null;
                                  const openStripMenu = (e, section) => {
                                    if (!canEdit) return;
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setSubStageStripMenu({ x: e.clientX, y: e.clientY, rowKey: row.key, stageId: row.stageId, partId: row.partId, section, endDateStr: computedEndStr, targetDateStr: subContractDate });
                                  };
                                  const sectionHoverOn = (e) => { e.currentTarget.style.borderColor = BRIO_GOLD; };
                                  const sectionHoverOff = (e) => { e.currentTarget.style.borderColor = BRIO_BORDER; };
                                  const subStripCollapsed = collapsedStripRows.has(row.id);
                                  const subCollapseBtn = (
                                    <span
                                      onClick={(e) => { e.stopPropagation(); toggleStripCollapse(row.id); }}
                                      title={subStripCollapsed ? 'Expand date strip' : 'Collapse date strip'}
                                      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: BRIO_GREY, padding: '1px 2px', borderRadius: '2px' }}
                                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <Minus size={10} />
                                    </span>
                                  );
                                  const isEditingThisPart = editingPartName?.stageId === row.stageId && editingPartName?.partId === row.partId;
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                      <div style={{ padding: '3px 6px 2px 6px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                        {/* Collapse chevron — minimizes this sub-stage's rows within the main stage */}
                                        <span
                                          onClick={(e) => { e.stopPropagation(); toggleCollapse(row.id); }}
                                          title={collapsedRows.has(row.id) ? 'Expand sub-stage' : 'Collapse sub-stage'}
                                          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: BRIO_GREY, userSelect: 'none' }}
                                        >
                                          {collapsedRows.has(row.id) ? <ChevronRight size={12} strokeWidth={1.5} /> : <ChevronDown size={12} strokeWidth={1.5} />}
                                        </span>
                                        {isEditingThisPart ? (
                                          <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '3px', minWidth: 0 }}>
                                            <span style={{ fontSize: '10px', fontWeight: 500, color: BRIO_GREY, fontFamily: 'Raleway, sans-serif', whiteSpace: 'nowrap' }}>
                                              ↳ {editingPartName.prefix} :
                                            </span>
                                            <input
                                              autoFocus
                                              type="text"
                                              value={partNameValue}
                                              placeholder="Add substage name"
                                              onChange={e => setPartNameValue(e.target.value)}
                                              onBlur={() => {
                                                const full = partNameValue.trim() ? `${editingPartName.prefix} : ${partNameValue.trim()}` : editingPartName.prefix;
                                                handlePartNameSave(row.stageId, row.partId, full);
                                                setEditingPartName(null);
                                              }}
                                              onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                  const full = partNameValue.trim() ? `${editingPartName.prefix} : ${partNameValue.trim()}` : editingPartName.prefix;
                                                  handlePartNameSave(row.stageId, row.partId, full);
                                                  setEditingPartName(null);
                                                }
                                                if (e.key === 'Escape') setEditingPartName(null);
                                              }}
                                              style={{ flex: 1, minWidth: 0, fontSize: '10px', fontWeight: 500, fontFamily: 'Raleway, sans-serif', border: '1px solid ' + BRIO_GOLD, padding: '1px 4px', outline: 'none', background: '#fffef5', color: '#333' }}
                                            />
                                          </div>
                                        ) : (
                                          <span
                                            style={{ fontSize: '12px', fontWeight: 700, color: '#333', fontStyle: row.partId ? 'normal' : 'italic', flex: 1, cursor: (canEdit && row.partId) ? 'text' : 'default' }}
                                            onClick={canEdit && row.partId ? (e) => {
                                              e.stopPropagation();
                                              const colonIdx = (row.name || '').indexOf(' : ');
                                              const prefix = colonIdx >= 0 ? (row.name || '').slice(0, colonIdx) : (row.name || '');
                                              const suffix = colonIdx >= 0 ? (row.name || '').slice(colonIdx + 3) : '';
                                              setEditingPartName({ stageId: row.stageId, partId: row.partId, prefix });
                                              setPartNameValue(suffix);
                                            } : undefined}
                                            title={canEdit && row.partId ? 'Click to edit suffix' : undefined}
                                          >
                                            <span style={{ fontWeight: 300 }}>↳ </span>{row.name}
                                          </span>
                                        )}
                                        {subStripCollapsed && subCollapseBtn}
                                      </div>
                                      {!subStripCollapsed && <div style={{ display: 'flex', alignItems: 'center', marginTop: '1px', marginBottom: '1px', padding: '1px 3px', gap: '3px' }}>
                                        <div
                                          title="Duration (right-click to clear)"
                                          onContextMenu={(e) => openStripMenu(e, 'duration')}
                                          onMouseEnter={canEdit ? sectionHoverOn : undefined}
                                          onMouseLeave={canEdit ? sectionHoverOff : undefined}
                                          style={{ flex: '0 0 28%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '0px 4px', minHeight: '16px' }}
                                        >
                                          {canEdit ? (
                                            <DurationCell value={storedDuration} inputBase={stripInputBase} savedMark={null} onChange={(num) => handleFieldChange(row.key, 'contractDuration', num)} />
                                          ) : (
                                            <span style={{ fontSize: '10px', fontWeight: 300, color: storedDuration != null ? '#333' : '#ccc' }}>{storedDuration != null ? `${storedDuration}d` : '–'}</span>
                                          )}
                                        </div>
                                        <div
                                          title="Contract start (right-click to clear)"
                                          onContextMenu={(e) => openStripMenu(e, 'startDate')}
                                          onMouseEnter={canEdit ? sectionHoverOn : undefined}
                                          onMouseLeave={canEdit ? sectionHoverOff : undefined}
                                          style={{ flex: '0 0 33%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '0px 2px', minHeight: '16px' }}
                                        >
                                          {canEdit ? (
                                            <BrioDatePicker
                                              value={storedStart || ''} onChange={(v) => handleFieldChange(row.key, 'contractStartDate', v)}
                                              placeholder="" clearable={true} centerAlign={true} as="button"
                                              className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                                              style={{ width: '100%' }} triggerStyle={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: storedStart ? '#333' : '#ccc', width: '100%' }}
                                            />
                                          ) : (
                                            <span style={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: storedStart ? '#333' : '#ccc', textAlign: 'center', display: 'block' }}>{storedStart ? formatDateDisplay(parseDate(storedStart)) : '–'}</span>
                                          )}
                                        </div>
                                        <div
                                          title={`Contract end${subContractDate ? ` — Tracking contract: ${formatDateDisplay(parseDate(subContractDate))}` : ''}${subDiscColor === BRIO_GOLD ? ' (matches)' : subDiscColor === BRIO_RED ? ' — mismatch, right-click to sync / clear' : ''}${canEdit && !subDiscColor ? ' (right-click to sync / clear)' : ''}`}
                                          onContextMenu={(e) => openStripMenu(e, 'endDate')}
                                          onMouseEnter={canEdit ? sectionHoverOn : undefined}
                                          onMouseLeave={canEdit ? sectionHoverOff : undefined}
                                          style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '0px 2px', minHeight: '16px' }}
                                        >
                                          {canEdit ? (
                                            <BrioDatePicker
                                              value={computedEndStr || ''} onChange={(v) => handleFieldChange(row.key, 'contractEndDate', v)}
                                              placeholder="" clearable={true} centerAlign={true} as="button"
                                              className="brio-datepicker-trigger--borderless brio-datepicker-trigger--flush"
                                              style={{ width: '100%' }} triggerStyle={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: subEffectiveEnd ? '#333' : '#ccc', width: '100%' }}
                                            />
                                          ) : (
                                            <span style={{ fontSize: '10px', fontWeight: 300, fontFamily: 'inherit', color: subEffectiveEnd ? '#333' : '#ccc', textAlign: 'center', display: 'block' }}>{subEffectiveEnd ? formatDateDisplay(subEffectiveEnd) : '–'}</span>
                                          )}
                                          {subDiscColor && (
                                            <span style={{ position: 'absolute', top: '1px', right: '2px', width: '4px', height: '4px', borderRadius: '50%', background: subDiscColor, display: 'inline-block', pointerEvents: 'none' }} />
                                          )}
                                        </div>
                                        {/* Collapse toggle — uniform position at right end of strip */}
                                        {subCollapseBtn}
                                      </div>}
                                    </div>
                                  );
                                })() : renderCell(row, col)}
                              </td>
                            );
                          })}
                        </>
                      )}
                    </SortableRow>
                    </React.Fragment>
                  );
                }

                const isDraggable = canEdit && row.indent > 0 && !row.id.startsWith('closure_') && !row.id.startsWith('invoicing_');
                const isRowSelected = selectedRows.has(row.id);
                // Rows that come from scope-of-work (not user-added, not system rows), plus any
                // workflow row explicitly linked to a SOW deliverable (carries a sowRef) — those get
                // the same scope highlight even though their id is a user-added row_ id.
                const hasSowRefLink = !isStage && (timeline[row.id]?.sowRef != null || timeline[row.id]?.sowSource === true);
                // A row explicitly unlinked from the SOW (via Link to SOW → cleared) loses the scope-fill
                // highlight even though its id (deliv_/meeting_/revision_) marks it as scope-derived.
                const isSowUnlinked = timeline[row.id]?._sowUnlinked === true;
                const isScopeRow = !isSowUnlinked && (hasSowRefLink || (!isStage && !row.id.startsWith('row_') && !row.id.startsWith('invoicing_') && !row.id.startsWith('closure_') && !row.id.startsWith('start_')));
                // Scope-of-work fill — faint tint, intensity reduced 20% toward white from #f6f5f3
                const SCOPE_FILL = '#f8f7f5';
                const stageBg = isStage ? (STAGE_ROW_COLORS[row.key] || '#f4f4f0') : (isRowSelected ? 'rgba(159,144,42,0.08)' : '#fff');

                return (
                  <React.Fragment key={row.id}>
                    {hiddenStageDivider}
                  <SortableRow
                    key={row.id + '__sr'}
                    id={row.id}
                    disabled={!isDraggable}
                    className={timeline[row.id]?.bold ? 'timeline-row-bold' : undefined}
                    style={{
                      borderTop: '1px solid ' + BRIO_BORDER,
                      background: stageBg,
                    }}
                  >
                    {(dragListeners) => (
                      <>
                        {/* Row number — stages have no number; non-stage rows get 1-based task number */}
                        <td
                          style={{ ...tdStyle, textAlign: 'center', fontSize: '10px', color: isRowSelected ? BRIO_GOLD : BRIO_GREY, fontWeight: isRowSelected ? 600 : undefined, width: '30px', minWidth: '30px', cursor: isDraggable ? 'grab' : 'default', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: isDraggable ? 'none' : 'auto', position: 'sticky', left: 0, zIndex: 2, background: isRowSelected ? 'rgba(159,144,42,0.12)' : (isScopeRow ? SCOPE_FILL : stageBg), borderLeft: '1px solid ' + BRIO_BORDER, ...(contextMenu && contextMenu.colKey === null && contextMenu.rowIndex === rowIndex ? { boxShadow: 'inset 0 0 0 2px ' + BRIO_GOLD + ', inset 0 0 10px rgba(159,144,42,0.08)' } : {}) }}
                          title={isDraggable ? 'Click to select, Ctrl+click to add • long press to drag' : (row.indent > 0 ? 'Click to select, Ctrl+click to add' : '')}
                          onClick={row.indent > 0 ? (e) => {
                            e.stopPropagation();
                            setSelectedCells(new Set());
                            if (e.ctrlKey || e.metaKey) {
                              setSelectedRows(prev => {
                                const next = new Set(prev);
                                if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                                return next;
                              });
                            } else {
                              setSelectedRows(prev => (prev.size === 1 && prev.has(row.id)) ? new Set() : new Set([row.id]));
                            }
                          } : undefined}
                          onContextMenu={(e) => handleContextMenu(e, rowIndex, null)}
                          {...(isDraggable ? dragListeners : {})}
                        >
                          {isStage ? (canEdit ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px' }}>
                              <span onClick={(e) => { e.stopPropagation(); handleMoveStage(row.id, 'up'); }} title="Move stage up" style={{ cursor: 'pointer', color: BRIO_GREY, display: 'flex', lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color = BRIO_GOLD} onMouseLeave={e => e.currentTarget.style.color = BRIO_GREY}><ChevronUp size={11} /></span>
                              <span onClick={(e) => { e.stopPropagation(); handleMoveStage(row.id, 'down'); }} title="Move stage down" style={{ cursor: 'pointer', color: BRIO_GREY, display: 'flex', lineHeight: 1 }} onMouseEnter={e => e.currentTarget.style.color = BRIO_GOLD} onMouseLeave={e => e.currentTarget.style.color = BRIO_GREY}><ChevronDown size={11} /></span>
                            </div>
                          ) : '') : (row.isSubStageHeader ? '' : (displayNumMap[row.id] || ''))}
                        </td>
                        {effectiveVisibleColumns.map(col => {
                          const style = cellStyle(col, false);
                          // Apply stage row color to the task name cell specifically
                          if (col.key === 'taskName' && isStage) {
                            style.background = STAGE_ROW_COLORS[row.key] || '#f4f4f0';
                          }
                          // Stage rows: task name cell uses zero-padding + top-align for the split layout
                          if (col.key === 'taskName' && isStage) {
                            style.padding = '0';
                            style.verticalAlign = 'top';
                          }
                          // Scope-of-work rows: faint fill on taskName cell to distinguish from user-added rows
                          if (col.key === 'taskName' && isScopeRow && !isRowSelected) {
                            style.background = SCOPE_FILL;
                          }
                          // Reconciliation error: this row links to a SOW deliverable that is no longer
                          // present in the base SOW (removed, or a stage whose deliverables were
                          // customised) — faint red fill on the task-name cell to flag the broken link.
                          if (col.key === 'taskName' && !isRowSelected && reconcileErrorRowIds && reconcileErrorRowIds.has(row.id)) {
                            style.background = 'rgba(220,53,69,0.10)';
                          }
                          // Highlight the right-clicked cell with a faint inset shadow
                          const isCtxCell = contextMenu && contextMenu.rowIndex === rowIndex && contextMenu.colKey === col.key;
                          if (isCtxCell) style.boxShadow = 'inset 0 0 0 2px ' + BRIO_GOLD + ', inset 0 0 10px rgba(159,144,42,0.08)';
                          // Derivative template: faint underline on a cell whose value diverges from the
                          // base it was created from (a manual edit — new task, changed duration/dep/name/
                          // assignee). Appended so it stacks with the right-click highlight above.
                          if (isDerivativeTemplate && baseDivergence && baseDivergence[row.id] && baseDivergence[row.id].has(col.key)) {
                            const underline = 'inset 0 -2px 0 -1px rgba(159,144,42,0.55)';
                            style.boxShadow = style.boxShadow ? style.boxShadow + ', ' + underline : underline;
                          }
                          // Invoicing/closure: sync-only right-click on specific date cols
                          // Unlinked custom stage preset rows are manually editable — don't intercept their right-click
                          const _presetParentId = row.id.startsWith('invoicing_') ? row.id.slice('invoicing_'.length)
                            : row.id.startsWith('closure_') ? row.id.slice('closure_'.length) : null;
                          const _isUnlinkedCustomPreset = _presetParentId && isCustomStage(_presetParentId) && !(timeline[_presetParentId]?._linkedToStage);
                          const isSyncableCell = !_isUnlinkedCustomPreset && (
                            (row.id.startsWith('invoicing_') && (col.key === 'plannedStartDate' || col.key === 'plannedEndDate')) ||
                            (row.id.startsWith('closure_') && (col.key === 'plannedStartDate' || col.key === 'plannedEndDate'))
                          );
                          const cellKey = `${row.key}|${col.key}`;
                          const isCellSelected = selectedCells.has(cellKey);
                          if (isCellSelected) {
                            style.background = 'rgba(159,144,42,0.12)';
                            style.boxShadow = 'inset 0 0 0 1px ' + BRIO_GOLD;
                          }
                          // Fillable: a cell the user edits directly. Rolled up / synced cells are left out.
                          const isFillable = canEdit && !['taskName', 'predecessors', 'sowRef'].includes(col.key) && !rolledCustomValue(row.id, col.key) && (col.custom || (
                            row.indent > 0 && !row.isSubStageHeader && !isSyncableCell
                            && !(row.id.startsWith('start_') && col.type === 'date')
                            && !(summaryRowIds.has(row.id) && ['plannedStartDate', 'plannedDuration', 'percentComplete'].includes(col.key))
                            && !col.computed));
                          // Format toolbar: column, row and cell formats, shown through classes in brio-styles.css.
                          const fmtProps = cellFormatProps(cellFormatOf(timeline, row.key, col.key));
                          if (fmtProps?.style) Object.assign(style, fmtProps.style);
                          return (
                            <td
                              key={col.key}
                              className={fmtProps?.className}
                              style={style}
                              onMouseDownCapture={(e) => {
                                if (e.button !== 0) return;
                                if (e.ctrlKey || e.metaKey) {
                                  if (!isFillable) return;
                                  e.preventDefault(); e.stopPropagation();
                                  setSelectedRows(new Set());
                                  setSelectedCells(prev => {
                                    const next = new Set(prev);
                                    if (next.has(cellKey)) next.delete(cellKey); else next.add(cellKey);
                                    return next;
                                  });
                                } else if (selectedCells.size > 0 && !isCellSelected) {
                                  setSelectedCells(new Set());
                                }
                              }}
                              onClickCapture={(e) => { if ((e.ctrlKey || e.metaKey) && isFillable) { e.preventDefault(); e.stopPropagation(); } }}
                              onContextMenu={
                                isSyncableCell
                                  ? (e) => { e.preventDefault(); setSyncCellMenu({ x: e.clientX, y: e.clientY, rowId: row.id, colKey: col.key }); }
                                  : (isStage && col.key === 'taskName')
                                    ? canEdit ? (e) => handleContextMenu(e, rowIndex, 'taskName') : (e) => e.preventDefault()
                                    : (e) => handleContextMenu(e, rowIndex, col.key)
                              }
                            >
                              {renderCell(row, col)}
                            </td>
                          );
                        })}
                      </>
                    )}
                  </SortableRow>
                  </React.Fragment>
                );
                }); // end displayRows.map
              })()}
              {isStandalone && displayRows.length === 0 && (
                <tr>
                  <td colSpan={effectiveVisibleColumns.length + 1} style={{ ...tdStyle, padding: '28px 12px', textAlign: 'center', borderLeft: '1px solid ' + BRIO_BORDER }}>
                    <div style={{ fontSize: '12px', fontWeight: 300, color: BRIO_GREY, marginBottom: canEdit ? '10px' : 0 }}>This tab has no stages yet.</div>
                    {canEdit && (
                      <button onClick={() => handleAddNewStage(-1)}
                        style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500, padding: '5px 14px', border: '1px solid ' + BRIO_GOLD, background: '#fff', color: BRIO_GOLD, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <Plus size={12} /> Add stage
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SortableContext>
      </DndContext>
      </div>{/* end overflowX scroll wrapper */}

      {/* Sticky horizontal scrollbar at bottom of viewport — mirrors the table scroll */}
      <div
        ref={mirrorScrollRef}
        onScroll={e => { if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; }}
        style={{ position: 'sticky', bottom: 0, overflowX: 'auto', overflowY: 'hidden', height: '14px', zIndex: 20, background: '#faf9f7', borderTop: '1px solid ' + BRIO_BORDER, display: compareSlots ? 'none' : undefined }}
      >
        <div style={{ width: tableMinWidthPx ? (tableMinWidthPx * (PAGE_ZOOM_LEVELS[pageZoomIndex]?.scale || 1)) + 'px' : '200%', height: '1px' }} />
      </div>

      {/* Sync-column checklist popover (derivative templates) — rendered here at the editor's top level,
          not inside the table cell, so it floats above the rows instead of being clipped by the table's
          scroll / stacking context. Position comes from the ▾ button's rect (stored in syncMenuStage). */}
      {syncMenuStage && isDerivativeTemplate && (() => {
        const st = syncMenuStage.stageId;
        const active = syncStageActive(syncPrefs, st);
        return (
          <div
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            style={{ position: 'fixed', left: syncMenuStage.x, top: syncMenuStage.y, transform: 'translateX(-100%)', zIndex: 10300, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 6px 20px rgba(0,0,0,0.16)', minWidth: '180px', fontFamily: 'Raleway, sans-serif', padding: '3px 0' }}
          >
            <div style={{ padding: '5px 12px', fontSize: '9.5px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER }}>
              Sync columns from base
            </div>
            {SYNC_COLUMNS.map(c => {
              const on = syncColumnOn(syncPrefs, st, c.key);
              return (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 12px', cursor: 'pointer', fontSize: '11.5px', fontWeight: 300, color: '#333' }}
                  onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <input type="checkbox" checked={on} onChange={() => toggleSyncColumn(st, c.key)} style={{ accentColor: BRIO_GOLD, cursor: 'pointer' }} />
                  <span>{c.label}</span>
                </label>
              );
            })}
            <div style={{ borderTop: '1px solid ' + BRIO_BORDER, padding: '5px 12px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setStageSyncAll(st, true)} style={{ fontSize: '10px', border: 'none', background: 'transparent', color: BRIO_GREY, cursor: 'pointer', padding: 0, fontFamily: 'Raleway, sans-serif' }}>All</button>
                <button onClick={() => setStageSyncAll(st, false)} style={{ fontSize: '10px', border: 'none', background: 'transparent', color: BRIO_GREY, cursor: 'pointer', padding: 0, fontFamily: 'Raleway, sans-serif' }}>None</button>
              </div>
              <span style={{ fontSize: '9px', fontWeight: 400, color: active ? '#bbb' : '#c9803f' }}>{active ? 'auto-syncs' : 'stage frozen'}</span>
            </div>
          </div>
        );
      })()}

      {/* Row context menu */}
      {confirmModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 3200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setConfirmModal(null)}
        >
          <div style={{ background: '#fff', padding: '22px 24px', minWidth: '320px', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '13px', color: '#333', marginBottom: '18px', lineHeight: 1.5 }}>{confirmModal.message}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setConfirmModal(null)} style={{ padding: '6px 14px', fontSize: '12px', fontFamily: 'Raleway, sans-serif', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>{confirmModal.cancelLabel || 'Cancel'}</button>
              <button onClick={() => { const cb = confirmModal.onConfirm; setConfirmModal(null); if (cb) cb(); }} style={{ padding: '6px 14px', fontSize: '12px', fontFamily: 'Raleway, sans-serif', border: `1px solid ${BRIO_GOLD}`, background: BRIO_GOLD, color: '#fff', cursor: 'pointer' }}>{confirmModal.confirmLabel || 'OK'}</button>
            </div>
          </div>
        </div>
      )}
      {contextMenu && (() => {
        const customCol = contextMenu.colKey
          ? (timeline._customColumns || []).find(c => c.key === contextMenu.colKey)
          : null;
        const isSelectCol = (customCol && (customCol.type === 'singleSelect' || customCol.type === 'multiSelect')) || contextMenu.colKey === 'assignedTo';
        const ctxRow = contextMenu.rowIndex != null ? displayRows[contextMenu.rowIndex] : null;
        const ctxRowData = ctxRow ? (timeline[ctxRow.key] || {}) : {};
        // alreadySent: task ID is stored AND the task still exists in project tasks
        const alreadySent = !!(ctxRowData._projectTaskId) && linkedTaskIds.has(ctxRowData._projectTaskId);
        // colKey === null means the right-click came from the serial number (#) cell — show full menu
        const isTaskNameCol = contextMenu.colKey === 'taskName' || contextMenu.colKey === null;
        // "Add date to tracking" — on targetEndDate or plannedEndDate of stage rows and sub-stage header rows
        const isStageRow = !!ctxRow && (ctxRow.indent === 0 || ctxRow.isSubStageHeader);
        const ctxAgg = isStageRow
          ? (ctxRow.isSubStageHeader ? subStageDates[ctxRow.key] : stageDates[ctxRow.key])
          : null;
        const isTrackableEndCol = contextMenu.colKey === 'plannedEndDate';
        // Stage/substage rows on a trackable end date column → show only "Add date to tracking"
        const trackingOnly = isStageRow && isTrackableEndCol;
        const cellOnly = contextMenu.colKey !== null && contextMenu.colKey !== 'taskName' && !trackingOnly;
        // "Add/Remove to project tasks" only on the task name / serial number column. A standalone
        // timeline (BRM) offers no project tasks, no M.O.M and no Link to SOW.
        const showAddToProjectTasks = canEdit && !isStandalone && !!ctxRow && !alreadySent && isTaskNameCol;
        // "Remove from project tasks" shows when the task is currently linked and exists
        // Not for a granted editor: RLS lets only managers delete project tasks.
        const showRemoveFromProjectTasks = canEdit && !isGrantedEditor && !isStandalone && !!ctxRow && alreadySent && isTaskNameCol;
        const ctxEndDate = ctxAgg ? ctxAgg[contextMenu.colKey] : null;
        // The tracking items below write Tracking dates, which a save through the grant keeps as stored.
        const showAddDateToTracking = canEdit && !savesViaGrant && isTrackableEndCol && isStageRow && !!ctxEndDate;
        // "Clear tracking date" — same column conditions, but only when a tracking date is already set
        const ctxTrackingField = 'targetDate';
        const ctxStageKey = ctxRow?.isSubStageHeader ? ctxRow.stageId : ctxRow?.key;
        const ctxPartId = ctxRow?.isSubStageHeader ? ctxRow.partId : null;
        const ctxTrackedDate = ctxRow?.isSubStageHeader
          ? ((timeline[ctxStageKey]?.partStages || []).find(p => p.id === ctxPartId)?.[ctxTrackingField] || null)
          : (ctxStageKey ? (timeline[ctxStageKey]?.[ctxTrackingField] || null) : null);
        const showClearTrackingDate = canEdit && !savesViaGrant && isTrackableEndCol && isStageRow && !!ctxTrackedDate;
        // "Trigger Prompt" — force-re-fire the workflow prompt even when date is already set.
        // Only meaningful for plannedEndDate (timeline_change).
        const showTriggerPrompt = canEdit && !savesViaGrant && isStageRow && !!ctxEndDate && contextMenu.colKey === 'plannedEndDate';
        // "Copy end date → contract" — stage rows only (indent 0, not sub-stage), when a planned end date exists
        const showCopyEndToContract = canEdit && !savesViaGrant && isTaskNameCol && !!ctxRow && ctxRow.indent === 0 && !ctxRow.isSubStageHeader && !!(ctxAgg?.plannedEndDate);
        // "Delete Row" only for user-added rows (row_ prefix), custom stages, or a deliverable row the
        // user has detached by removing its SOW link (_detached); other scope rows cannot be deleted
        const showDeleteRow = canEdit && !!ctxRow && (
          ctxRow.id.startsWith('row_') || isCustomStage(ctxRow.id) || timeline[ctxRow.id]?._detached === true
          // Template Builder: any task row is deletable, including SOW deliverable / meeting / revision
          // rows (scope rows are protected in real projects, but the builder authors the scope itself).
          || (isTemplateBuilder && ctxRow.indent > 0 && !ctxRow.isSubStageHeader
              && !ctxRow.id.startsWith('start_') && !ctxRow.id.startsWith('invoicing_') && !ctxRow.id.startsWith('closure_'))
        );
        // Stage name cell right-click → limited "stage name only" menu
        const isStageNameOnly = isStageRow && contextMenu.colKey === 'taskName' && !trackingOnly;
        // "Add New Stage" — available on stage name cells and substage name cells
        const showAddNewStage = canEdit && isStageNameOnly;
        // "Add Preset Task" — only for custom (user-added) stages
        const showAddPresetTask = canEdit && !isStandalone && isStageNameOnly && !!ctxRow && ctxRow.indent === 0 && !ctxRow.isSubStageHeader && isCustomStage(ctxRow.id);
        // Custom stage actions — only for isCustomStage rows in stageNameOnly mode
        const isCtxCustomStage = isStageNameOnly && !!ctxRow && ctxRow.indent === 0 && isCustomStage(ctxRow.id);
        const showRenameStage = canEdit && isCtxCustomStage;
        const showDeleteStage = canEdit && isCtxCustomStage;
        const isCustomStageHidden = isCtxCustomStage && !!(timeline[ctxRow.id]?._hidden);
        const showHideStage = canEdit && isCtxCustomStage && !isCustomStageHidden;
        // Stage actions for regular (non-stageNameOnly) cells — always shown, disabled when inapplicable
        const ctxOwnerCustomStageId = !isStageNameOnly ? (() => {
          for (let pi = contextMenu.rowIndex; pi >= 0; pi--) {
            if (displayRows[pi].indent === 0) return isCustomStage(displayRows[pi].id) ? displayRows[pi].id : null;
          }
          return null;
        })() : null;
        const hideStageDisabled = !canEdit || isStageNameOnly || !ctxOwnerCustomStageId || !!(timeline[ctxOwnerCustomStageId]?._hidden);
        const hasNearbyHiddenStage = !isStageNameOnly && (() => {
          let ownerIdx = -1;
          for (let pi = contextMenu.rowIndex; pi >= 0; pi--) {
            if (displayRows[pi].indent === 0) { ownerIdx = allRows.findIndex(r => r.id === displayRows[pi].id); break; }
          }
          if (ownerIdx < 0) return false;
          for (let i = ownerIdx - 1; i >= 0; i--) {
            if (allRows[i].indent === 0) return isCustomStage(allRows[i].id) && !!(timeline[allRows[i].id]?._hidden);
          }
          let afterEnd = ownerIdx + 1;
          while (afterEnd < allRows.length && allRows[afterEnd].indent > 0) afterEnd++;
          for (let i = afterEnd; i < allRows.length; i++) {
            if (allRows[i].indent === 0) return isCustomStage(allRows[i].id) && !!(timeline[allRows[i].id]?._hidden);
          }
          return false;
        })();
        const unhideStageDisabled = isStageNameOnly || !hasNearbyHiddenStage;
        const hasAnyHiddenCustomStage = allRows.some(r => isCustomStage(r.id) && r.indent === 0 && timeline[r.id]?._hidden);
        const unhideAllStagesDisabled = isStageNameOnly || !hasAnyHiddenCustomStage;
        // Hide / Unhide (rows) — any task row (indent>0, not a stage / sub-stage header / system row) can be
        // hidden; hiding never renumbers (numbering runs over the full row set incl. hidden rows). Covers the
        // meeting_/revision_ scope rows too (they can't be deleted but can be hidden).
        const isHideableRow = !!ctxRow && ctxRow.indent > 0 && !ctxRow.isSubStageHeader
          && !ctxRow.id.startsWith('start_') && !ctxRow.id.startsWith('invoicing_') && !ctxRow.id.startsWith('closure_');
        const isAlreadyHidden = !!ctxRow && !!(timeline[ctxRow.id]?._hidden);
        // Adjacency (in the full row order, which includes hidden rows) for the targeted "Unhide".
        const ctxAllIdx = ctxRow ? allRows.findIndex(r => r.id === ctxRow.id) : -1;
        const rowHiddenAt = (k) => k >= 0 && k < allRows.length && !!(timeline[allRows[k].id]?._hidden);
        const hasAdjacentHidden = ctxAllIdx >= 0 && (rowHiddenAt(ctxAllIdx - 1) || rowHiddenAt(ctxAllIdx + 1));
        const showHideRow = canEdit && isTaskNameCol && isHideableRow && !isAlreadyHidden;
        const showUnhideAll = canEdit && isTaskNameCol && hiddenRowCount > 0;
        // "Unhide" (targeted): on a row directly above/below a hidden run, or on a hidden row itself.
        const showUnhideAdjacent = canEdit && isTaskNameCol && !!ctxRow && (isAlreadyHidden || hasAdjacentHidden);
        // "Assign to sub-stage" — for task rows (indent>0, not sub-stage header) in a stage with partStages
        let showAssignSubStage = false;
        if (canEdit && ctxRow && ctxRow.indent > 0 && !ctxRow.isSubStageHeader && isTaskNameCol) {
          for (let pi = contextMenu.rowIndex - 1; pi >= 0; pi--) {
            if (displayRows[pi].indent === 0) {
              showAssignSubStage = !!(partStagesMap[displayRows[pi].id]);
              break;
            }
          }
        }
        // "Carry forward" — any deliverable/task row (incl. SOW deliv_ and system rows), on the name column
        const showCarryForward = canEdit && !!ctxRow && ctxRow.indent > 0 && !ctxRow.isSubStageHeader && isTaskNameCol;
        // "Link to SOW" — link a task/deliverable row to SOW deliverable(s). Available in the Template
        // Builder (against the selected SOW) and in a real project (against the project's own confirmed
        // scope), whenever a SOW is available. Any indent>0 non-system row on the name / # column. Shown
        // in BOTH edit and view mode (the modal itself is read-only in view mode), so links can be
        // inspected while only viewing — not just while editing.
        const showLinkToSow = !!linkSowTemplate && !isStandalone
          && !!ctxRow && ctxRow.indent > 0 && !ctxRow.isSubStageHeader && isTaskNameCol
          && !ctxRow.id.startsWith('start_') && !ctxRow.id.startsWith('invoicing_') && !ctxRow.id.startsWith('closure_');
        // "Revert to stage" — only for tasks already carried forward, on the name column
        const showRevertCarryForward = canEdit && !!ctxRow && !!ctxRowData._isCarriedForward && isTaskNameCol;
        // "Add M.O.M" — available to all users on task-name cells and user-added rows
        const showAddMOM = !isStandalone && !!ctxRow && !ctxRow.isSubStageHeader
          && !ctxRow.id.startsWith('closure_') && !ctxRow.id.startsWith('invoicing_')
          && (isTaskNameCol || ctxRow.id.startsWith('row_'));
        const hasMOM = showAddMOM && momHook.documents.some(d => d.task_key === ctxRow?.key);
        // "Insert M.o.m" — inserts a new Meeting N row + opens MOM panel; same visibility as Add M.O.M
        const showInsertMOM = showAddMOM;
        // Indent/outdent not available for the first row inside a sub-stage header
        const prevRow = contextMenu.rowIndex > 0 ? displayRows[contextMenu.rowIndex - 1] : null;
        const isFirstInSubStage = !!(prevRow?.isSubStageHeader);
        return (
          <RowContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            showFormat={canEdit}
            onAction={handleContextAction}
            onClose={() => setContextMenu(null)}
            hasClipboard={!!cellClipboard.current}
            hasRowClipboard={!!rowClipboard.current}
            stageNameOnly={isStageNameOnly}
            showAddNewStage={showAddNewStage}
            showAddPresetTask={showAddPresetTask}
            showInsertInStage={isCtxCustomStage}
            showRenameStage={showRenameStage}
            showDeleteStage={showDeleteStage}
            showHideStage={showHideStage}
            cellOnly={cellOnly && !isStageNameOnly}
            showManageOptions={canEdit && isSelectCol && !isStageNameOnly}
            manageOptionsLabel={contextMenu.colKey === 'assignedTo' ? 'Add Categories' : undefined}
            showAddToProjectTasks={showAddToProjectTasks && !isStageNameOnly}
            showRemoveFromProjectTasks={showRemoveFromProjectTasks && !isStageNameOnly}
            showAddDateToTracking={showAddDateToTracking}
            showClearTrackingDate={showClearTrackingDate}
            showTriggerPrompt={showTriggerPrompt}
            showDeleteRow={showDeleteRow && !isStageNameOnly}
            showHideRow={showHideRow && !isStageNameOnly}
            showUnhideAll={showUnhideAll && !isStageNameOnly}
            showUnhideAdjacent={showUnhideAdjacent && !isStageNameOnly}
            showAssignSubStage={showAssignSubStage && !isStageNameOnly}
            showCarryForward={showCarryForward && !isStageNameOnly}
            showRevertCarryForward={showRevertCarryForward && !isStageNameOnly}
            showLinkToSow={showLinkToSow && !isStageNameOnly}
            showAddMOM={showAddMOM && !isStageNameOnly}
            hasMOM={hasMOM}
            showInsertMOM={showInsertMOM && !isStageNameOnly}
            canEdit={canEdit}
            canSync={canSync}
            showIndent={!isFirstInSubStage && !isStageNameOnly}
            showOutdent={!isFirstInSubStage && !isStageNameOnly}
            trackingOnly={trackingOnly}
            trackingLocked={trackingOnly && isApprovalLocked(ctxRow?.isSubStageHeader
              ? (timeline[ctxStageKey]?.partStages || []).find(p => p.id === ctxPartId)
              : timeline[ctxStageKey])}
            showCopyEndToContract={showCopyEndToContract && !isStageNameOnly}
            hideClearCell={!!ctxRow && ctxRow.id.startsWith('deliv_') && contextMenu.colKey !== 'predecessors'}
            hideStageDisabled={hideStageDisabled}
            unhideStageDisabled={unhideStageDisabled}
            unhideAllStagesDisabled={unhideAllStagesDisabled}
          />
        );
      })()}

      {/* Sub-stage assign picker — opens after right-click → "Assign to sub-stage" */}
      {subStageAssignMenu && (() => {
        const parts = partStagesMap[subStageAssignMenu.stageId] || [];
        const currentId = timeline[subStageAssignMenu.rowId]?._subStageId || null;
        const menuRef2 = { current: null };
        return (
          <div
            ref={el => { menuRef2.current = el; }}
            style={{ position: 'fixed', left: subStageAssignMenu.x, top: subStageAssignMenu.y, zIndex: 10002, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.14)', minWidth: '160px', padding: '4px 0', fontFamily: 'Raleway, sans-serif' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '4px 14px 3px', fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER }}>
              Assign to sub-stage
            </div>
            {/* Unassigned option */}
            <div onClick={() => handleAssignSubStage(subStageAssignMenu.rowId, null)}
              style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 300, cursor: 'pointer', color: currentId === null ? BRIO_GOLD : '#555', fontStyle: 'italic' }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              Unassigned
            </div>
            {parts.map(part => (
              <div key={part.id} onClick={() => handleAssignSubStage(subStageAssignMenu.rowId, part.id)}
                style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 300, cursor: 'pointer', color: currentId === part.id ? BRIO_GOLD : '#333' }}
                onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                {part.name}
              </div>
            ))}
          </div>
        );
      })()}
      {subStageAssignMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 10001 }} onClick={() => setSubStageAssignMenu(null)} />}

      {/* Carry-forward target picker — opens after right-click → "Carry forward" */}
      {carryForwardMenu && (() => {
        const stageRows = allRows.filter(r => r.indent === 0);
        return (
          <div
            style={{ position: 'fixed', left: carryForwardMenu.x, top: carryForwardMenu.y, zIndex: 10002, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.14)', minWidth: '200px', maxHeight: '320px', overflowY: 'auto', padding: '4px 0', fontFamily: 'Raleway, sans-serif' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '4px 14px 3px', fontSize: '10px', fontWeight: 600, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BRIO_BORDER }}>
              Carry forward to…
            </div>
            {stageRows.map(sr => {
              const parts = (partStagesMap[sr.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              return (
                <div key={sr.id}>
                  <div onClick={() => handleCarryForwardMulti(carryForwardMenu.rowIds || [], sr.id, null)}
                    style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 400, cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    {sr.name || sr.defaultLabel || sr.id}
                  </div>
                  {parts.map(p => (
                    <div key={p.id} onClick={() => handleCarryForwardMulti(carryForwardMenu.rowIds || [], sr.id, p.id)}
                      style={{ padding: '5px 14px 5px 26px', fontSize: '11px', fontWeight: 300, cursor: 'pointer', color: '#555' }}
                      onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      ↳ {p.name || p.id}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })()}
      {carryForwardMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 10001 }} onClick={() => setCarryForwardMenu(null)} />}

      {/* Delayed badge right click menu: Hide / Unhide */}
      {formatBar && formatBarState && canEdit && (
        <FormatToolbar
          x={formatBar.x}
          y={formatBar.y}
          state={formatBarState}
          label={formatBar.targets[0].kind === 'col' ? (formatBar.targets.length > 1 ? 'Columns' : 'Column')
            : formatBar.targets[0].kind === 'row' ? (formatBar.targets.length > 1 ? 'Rows' : 'Row')
            : (formatBar.targets.length > 1 ? 'Cells' : 'Cell')}
          onToggle={(prop) => { const on = !formatBarState[prop]; applyFormat(formatBar.targets, () => ({ [prop]: on })); }}
          onSize={(d) => applyFormat(formatBar.targets, (f) => {
            const n = Math.max(FMT_MIN_SIZE, Math.min(FMT_MAX_SIZE, (f.size || FMT_BASE_SIZE) + d));
            return { size: n === FMT_BASE_SIZE ? undefined : n };
          })}
          onAlign={(a) => applyFormat(formatBar.targets, () => ({ align: a }))}
          onClear={() => applyFormat(formatBar.targets, null, { clear: true })}
          onClose={() => setFormatBar(null)}
        />
      )}

      {delayBadgeMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10002 }} onClick={() => setDelayBadgeMenu(null)} onContextMenu={(e) => { e.preventDefault(); setDelayBadgeMenu(null); }} />
          <div style={{ position: 'fixed', left: delayBadgeMenu.x, top: delayBadgeMenu.y, zIndex: 10003, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '140px', padding: '4px 0', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300 }}>
            <div
              onClick={() => { setDelayBadgeHidden(delayBadgeMenu.rowKey, !delayBadgeMenu.hidden); setDelayBadgeMenu(null); }}
              style={{ padding: '6px 16px', cursor: 'pointer', color: '#333' }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {delayBadgeMenu.hidden ? 'Unhide' : 'Hide'}
            </div>
            {canEdit && (
              <div
                onClick={() => { setFormatBar({ x: delayBadgeMenu.x, y: delayBadgeMenu.y, targets: [{ kind: 'cell', rowKey: delayBadgeMenu.rowKey, colKey: 'scheduleStatus' }] }); setDelayBadgeMenu(null); }}
                style={{ padding: '6px 16px', cursor: 'pointer', color: '#333', borderTop: '1px solid ' + BRIO_BORDER }}
                onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                Format
              </div>
            )}
          </div>
        </>
      )}

      {/* Sub-stage strip section context menu */}
      {subStageStripMenu && (() => {
        const { x, y, section, endDateStr, targetDateStr } = subStageStripMenu;
        const items = section === 'duration' ? [
          { key: 'clearDuration', label: 'Clear Duration', danger: true },
        ] : section === 'startDate' ? [
          { key: 'clearStartDate', label: 'Clear Start Date', danger: true },
        ] : [
          // Sync strip end → tracking Contract (contractDate) only. Only when strip has a date.
          // Push-to-tracking is admin/supervisor-only (canSync); granted users can't sync.
          // Disabled while "Sync to tracking" is off: the toggle is the one safeguard for Tracking dates.
          ...(endDateStr && canSync ? [syncEnabled
            ? { key: 'updateContractDate', label: 'Sync to tracking Contract date' }
            : { key: 'updateContractDate', label: 'Sync to tracking Contract date', disabled: true, note: 'Turn on Sync to push dates to Tracking' }] : []),
          // Load tracking date → strip end. Shown when strip is empty but tracking has a date.
          ...(!endDateStr && targetDateStr ? [{ key: 'loadFromTracking', label: `Load from tracking (${formatDateDisplay(parseDate(targetDateStr))})` }] : []),
          // Clear all strip date fields
          ...((endDateStr || targetDateStr) ? [{ key: 'div' }] : []),
          { key: 'clearEndDate', label: 'Clear date', danger: true },
        ];
        const approxH = items.filter(i => i.key !== 'div').length * 30 + items.filter(i => i.key === 'div').length * 9 + 8;
        const top = (y + approxH > window.innerHeight - 8) ? Math.max(8, y - approxH) : y;
        return (
          <div
            style={{ position: 'fixed', left: x, top, zIndex: 10003, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '160px', padding: '4px 0', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300 }}
            onClick={e => e.stopPropagation()}
          >
            {items.map(item => item.key === 'div'
              ? <div key="div" style={{ height: '1px', background: BRIO_BORDER, margin: '4px 0' }} />
              : (
                <div key={item.key}
                  onClick={() => { if (!item.disabled) handleSubStageStripAction(item.key); }}
                  title={item.note || undefined}
                  style={{ padding: '6px 16px', cursor: item.disabled ? 'default' : 'pointer', color: item.disabled ? '#aaa' : (item.danger ? BRIO_RED : '#333') }}
                  onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.background = BRIO_BG; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {item.label}
                  {item.note && <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>{item.note}</div>}
                </div>
              )
            )}
          </div>
        );
      })()}
      {subStageStripMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 10002 }} onClick={() => setSubStageStripMenu(null)} onContextMenu={(e) => { e.preventDefault(); setSubStageStripMenu(null); }} />}

      {syncCellMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10002 }} onClick={() => setSyncCellMenu(null)} onContextMenu={(e) => { e.preventDefault(); setSyncCellMenu(null); }} />
          <div style={{ position: 'fixed', left: syncCellMenu.x, top: syncCellMenu.y, zIndex: 10003, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '150px', fontFamily: 'Raleway, sans-serif', overflow: 'hidden' }}>
            <div
              onClick={() => { handleSyncFromTracking(syncCellMenu.rowId); setSyncCellMenu(null); }}
              style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 300, cursor: 'pointer', color: '#333', display: 'flex', alignItems: 'center', gap: '7px' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f3ed'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: BRIO_GOLD, flexShrink: 0, display: 'inline-block' }} />
              Load dates from tracking page
            </div>
            {canEdit && syncCellMenu.colKey && (
              <div
                onClick={() => { setFormatBar({ x: syncCellMenu.x, y: syncCellMenu.y, targets: [{ kind: 'cell', rowKey: syncCellMenu.rowId, colKey: syncCellMenu.colKey }] }); setSyncCellMenu(null); }}
                style={{ padding: '7px 14px', fontSize: '12px', fontWeight: 300, cursor: 'pointer', color: '#333', borderTop: '1px solid ' + BRIO_BORDER }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f3ed'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                Format
              </div>
            )}
          </div>
        </>
      )}

      {/* Manual Override popup — manually push consecutive stage timelines */}
      {manualOverrideMenu && canEdit && (() => {
        return (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10002 }} onClick={() => setManualOverrideMenu(null)} onContextMenu={(e) => { e.preventDefault(); setManualOverrideMenu(null); }} />
          <div
            style={{ position: 'fixed', left: manualOverrideMenu.x, top: manualOverrideMenu.y, zIndex: 10003, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: '260px', fontFamily: 'Raleway, sans-serif', padding: '14px 16px' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#333', marginBottom: '12px', letterSpacing: '0.02em' }}>Manual Override</div>

            {/* Push consecutive stages by N working days */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 400, color: '#555', marginBottom: '8px' }}>Push consecutive stage timelines by</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                <input
                  type="number"
                  min="1"
                  value={manualOverrideMenu.manualDays}
                  onChange={e => setManualOverrideMenu(prev => ({ ...prev, manualDays: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  style={{ width: '60px', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', padding: '3px 6px', outline: 'none', textAlign: 'center' }}
                  onFocus={e => { e.target.style.borderColor = BRIO_GOLD; }}
                  onBlur={e => { e.target.style.borderColor = BRIO_BORDER; }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const days = parseInt(manualOverrideMenu.manualDays, 10);
                      if (days !== 0 && !isNaN(days)) { handlePushTimelines(manualOverrideMenu.closureRowId, days); setManualOverrideMenu(null); }
                    } else if (e.key === 'Escape') { setManualOverrideMenu(null); }
                  }}
                />
                <span style={{ fontSize: '11px', fontWeight: 300, color: '#666' }}>working days</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <span
                  onClick={() => setManualOverrideMenu(null)}
                  style={{ fontSize: '11px', fontWeight: 300, color: '#999', cursor: 'pointer', padding: '3px 8px' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#555'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#999'; }}
                >Cancel</span>
                <span
                  onClick={() => {
                    const days = parseInt(manualOverrideMenu.manualDays, 10);
                    if (days !== 0 && !isNaN(days)) { handlePushTimelines(manualOverrideMenu.closureRowId, days); setManualOverrideMenu(null); }
                  }}
                  style={{ fontSize: '11px', fontWeight: 400, color: BRIO_GOLD, cursor: 'pointer', padding: '3px 8px', border: '0.5px solid ' + BRIO_GOLD, borderRadius: '2px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >Apply</span>
              </div>
            </div>
          </div>
        </>
        );
      })()}

      {/* Stage/substage date link picker for start_ rows */}
      {stageLinkMenu && (() => {
        const { startRowId } = stageLinkMenu;
        const rd = timeline[startRowId] || {};

        // Determine which stage is currently linked (manual or auto preset = prevSk)
        let currentLinkedId = manualStartLink(startRowId, rd);
        if (!currentLinkedId) {
          // Auto preset: find the immediately previous stage
          const stageId = startRowId.slice('start_'.length);
          const myIdx = allRows.findIndex(r => r.id === stageId);
          if (myIdx > 0) {
            let pi = myIdx - 1;
            while (pi >= 0 && allRows[pi].indent !== 0) pi--;
            if (pi >= 0) currentLinkedId = allRows[pi].id;
          }
        }

        const stageRows = allRows.filter(r => r.indent === 0);
        const items = [];
        for (const sr of stageRows) {
          // A Start row cannot link to its own stage or that stage's parts (see manualStartLink).
          if (isOwnStageLink(startRowId, sr.id)) continue;
          const agg = stageDates[sr.id];
          const srd = timeline[sr.id] || {};
          // Always list every main stage with a best-available end date (aggregated child end,
          // else the stage's own planned/target/contract date).
          const stageEnd = agg?.plannedEndDate
            || parseDate(srd.plannedEndDate)
            || parseDate(srd.targetDate)
            || parseDate(srd.contractDate)
            || null;
          items.push({ id: sr.id, label: sr.name || sr.defaultLabel || sr.id, endDate: stageEnd, isStage: true });
          // Then list every defined part stage (best-available end date via a fallback chain)
          // so the user can link to a part even before it is scheduled.
          const parts = (partStagesMap[sr.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          for (const p of parts) {
            const subKey = `substage_${sr.id}_${p.id}`;
            const sagg = subStageDates[subKey];
            const closureRd = timeline[`closure_${subKey}`] || {};
            const invRd = timeline[`invoicing_${subKey}`] || {};
            const endDate = sagg?.plannedEndDate
              || parseDate(closureRd.plannedEndDate)
              || parseDate(invRd.plannedEndDate)
              || parseDate(p.paidDate)
              || parseDate(p.invoiceDate)
              || null;
            items.push({ id: subKey, label: p.name || p.id, endDate, isStage: false, isSubstage: true });
          }
        }
        // Keep the menu within the viewport — clamp so it isn't hidden below the page edge
        // (e.g. when opened from the last stage near the bottom).
        const MENU_MAX_H = 280;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
        const clampedTop = Math.max(8, Math.min(stageLinkMenu.y, vh - MENU_MAX_H - 8));
        return (
          <div
            onMouseLeave={() => setStageLinkMenu(null)}
            style={{ position: 'fixed', left: stageLinkMenu.x, top: clampedTop, zIndex: 10003, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 12px rgba(0,0,0,0.10)', minWidth: '240px', maxHeight: `${MENU_MAX_H}px`, overflowY: 'auto', fontFamily: 'Raleway, sans-serif' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Manual dependency controls — override the preset dep type / lag, or reset back to it. */}
            <div
              onClick={() => { setStageLinkMenu(null); setPredecessorModal({ rowKey: startRowId, value: rd.predecessors || '' }); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 400, color: '#333', borderBottom: '1px solid ' + BRIO_BORDER }}
              onMouseEnter={e => e.currentTarget.style.background = BRIO_GOLD + '12'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Settings2 size={11} style={{ color: BRIO_GOLD }} /> Edit dependency (type / lag)…
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '6px 10px 4px', borderBottom: '1px solid ' + BRIO_BORDER }}>
              <span style={{ fontSize: '10px', fontWeight: 500, color: '#aaa', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Link start date to end of…</span>
              <span
                onClick={() => handleResetStartPreset(startRowId)}
                title="Reset to the preset: link to the stage directly above, with the preset dependency"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: 500, color: BRIO_GOLD, letterSpacing: '0.04em', textTransform: 'uppercase' }}
              >
                <RotateCcw size={10} /> Reset
              </span>
            </div>
            {items.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: '11px', fontWeight: 300, color: '#aaa', fontStyle: 'italic' }}>No stages with end dates</div>
            )}
            {items.map(item => {
              const isActive = item.id === currentLinkedId;
              return (
                <div
                  key={item.id}
                  onClick={() => handleLinkStageDate(startRowId, item.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: item.isStage ? 400 : 300, color: isActive ? BRIO_GOLD : '#333', background: isActive ? BRIO_GOLD + '10' : 'transparent', paddingLeft: item.isSubstage ? '22px' : '10px' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = BRIO_GOLD + '12'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isActive ? BRIO_GOLD + '10' : 'transparent'; }}
                >
                  <span style={{ flexShrink: 0, width: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: BRIO_GOLD }}>
                    {isActive && <Check size={10} strokeWidth={2.5} />}
                  </span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                  <span style={{ flexShrink: 0, fontSize: '10px', color: isActive ? BRIO_GOLD : '#999', fontWeight: 300 }}>{formatDate(item.endDate)}</span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Sub-stage "show" checkbox dropdown removed — part-stages now always appear under their stage
          automatically (activeSubStages is derived = all parts), so there is nothing to toggle. */}

      {/* Column header context menu */}
      {headerContextMenu && (() => {
        const hColKey = headerContextMenu.colKey;
        const isCustomCol = (timeline._customColumns || []).some(c => c.key === hColKey);
        const hColDef = [...SCHEDULE_COLUMNS, ...(timeline._customColumns || [])].find(c => c.key === hColKey);
        const isSelectCol = hColDef?.type === 'singleSelect' || hColDef?.type === 'multiSelect';
        const btnStyle = { display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: '12px', fontWeight: 300, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#333' };
        const divStyle = { borderTop: '1px solid ' + BRIO_BORDER };
        const hov = (e) => { e.currentTarget.style.background = BRIO_BG; };
        const unHov = (e) => { e.currentTarget.style.background = 'transparent'; };
        return (
          <div style={{
            position: 'fixed', left: headerContextMenu.x, top: headerContextMenu.y,
            zIndex: 10001, background: '#fff', border: '1px solid ' + BRIO_BORDER,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontFamily: 'Raleway, sans-serif',
            minWidth: '160px',
          }} onClick={e => e.stopPropagation()}>
            {/* Format the column (or every selected column) */}
            {canEdit && (
              <button onClick={() => {
                const keys = selectedColumnKeys.has(hColKey) && selectedColumnKeys.size > 1 ? [...selectedColumnKeys] : [hColKey];
                setFormatBar({ x: headerContextMenu.x, y: headerContextMenu.y, targets: keys.map(k => ({ kind: 'col', colKey: k })) });
                setHeaderContextMenu(null);
              }} style={{ ...btnStyle, borderBottom: '1px solid ' + BRIO_BORDER }} onMouseEnter={hov} onMouseLeave={unHov}>
                <Type size={12} /> Format
              </button>
            )}
            {/* 1. Add Column → Add Categories (grouped, no divider between) */}
            {canEdit && (
              <button onClick={() => { setHeaderContextMenu(null); setAddColumnDialog(hColKey); }} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
                <Plus size={12} /> Add Column
              </button>
            )}
            {canEdit && (hColKey === 'assignedTo' || isSelectCol) && (
              <button
                onClick={() => {
                  setHeaderContextMenu(null);
                  setManageOptionsDropdown({ x: headerContextMenu.x, y: headerContextMenu.y, colKey: hColKey });
                }}
                style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}
              >
                Add Categories
              </button>
            )}
            {canEdit && <div style={divStyle} />}
            {/* 2. Select Column */}
            <button
              onClick={() => { setSelectedColumnKeys(prev => { const n = new Set(prev); if (n.has(hColKey)) n.delete(hColKey); else n.add(hColKey); return n; }); setHeaderContextMenu(null); }}
              style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}
            >
              {selectedColumnKeys.has(hColKey) ? 'Unselect Column' : 'Select Column'}
            </button>
            <div style={divStyle} />
            {/* 3. Rename Column — only for user-added custom columns */}
            {isCustomCol && (
              <button onClick={() => startRenameColumn(hColKey)} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
                Rename Column
              </button>
            )}
            {isCustomCol && <div style={divStyle} />}
            {/* 4. Copy / Paste / Duplicate / Clear */}
            <button onClick={() => handleCopyColumn(hColKey)} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
              Copy Column
            </button>
            <button
              onClick={() => handlePasteColumn(hColKey)}
              disabled={!columnClipboard.current}
              style={{ ...btnStyle, cursor: columnClipboard.current ? 'pointer' : 'default', color: columnClipboard.current ? '#333' : '#ccc' }}
              onMouseEnter={e => { if (columnClipboard.current) e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={unHov}
            >
              Paste Column
            </button>
            {canEdit && isCustomCol && (
              <button onClick={() => handleDuplicateColumn(hColKey)} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
                Duplicate Column
              </button>
            )}
            {canEdit && (
              <button onClick={() => handleClearColumn(hColKey)} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
                Clear Column
              </button>
            )}
            <div style={divStyle} />
            {/* 5. Hide / Unhide Columns */}
            <button onClick={() => hideColumn(hColKey)} style={btnStyle} onMouseEnter={hov} onMouseLeave={unHov}>
              Hide Column
            </button>
            {/* 6. Unhide Column — no divider; grouped with Hide */}
            <button
              onClick={() => { setUnhidePanel(true); setHeaderContextMenu(null); }}
              disabled={hiddenColumns.size === 0}
              style={{ ...btnStyle, cursor: hiddenColumns.size === 0 ? 'default' : 'pointer', color: hiddenColumns.size === 0 ? '#ccc' : '#333' }}
              onMouseEnter={e => { if (hiddenColumns.size > 0) e.currentTarget.style.background = BRIO_BG; }}
              onMouseLeave={unHov}
            >
              Unhide Column{hiddenColumns.size > 0 ? ` (${hiddenColumns.size})` : ''}
            </button>
            {/* 7. Delete Column (custom cols only) */}
            {canEdit && isCustomCol && (
              <>
                <div style={divStyle} />
                <button onClick={() => handleDeleteCustomColumn(hColKey)} style={{ ...btnStyle, color: BRIO_RED }} onMouseEnter={hov} onMouseLeave={unHov}>
                  Delete Column
                </button>
              </>
            )}
          </div>
        );
      })()}

      {/* Unhide columns side panel */}
      {unhidePanel && hiddenColumns.size > 0 && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '240px',
          zIndex: 10001, background: '#fff', borderLeft: '2px solid ' + BRIO_GOLD,
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', fontFamily: 'Raleway, sans-serif',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid ' + BRIO_BORDER }}>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>Hidden Columns</span>
            <button onClick={() => setUnhidePanel(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
              <X size={14} style={{ color: BRIO_GREY }} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {[...SCHEDULE_COLUMNS, ...(timeline._customColumns || [])].filter(c => hiddenColumns.has(c.key)).map(col => (
              <button
                key={col.key}
                onClick={() => { unhideColumn(col.key); if (hiddenColumns.size <= 1) setUnhidePanel(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                  textAlign: 'left', padding: '8px 16px', fontSize: '12px', fontWeight: 300,
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  fontFamily: 'Raleway, sans-serif', color: '#333',
                }}
                onMouseEnter={e => { e.target.style.background = BRIO_BG; }}
                onMouseLeave={e => { e.target.style.background = 'transparent'; }}
              >
                <Check size={0} style={{ visibility: 'hidden' }} />
                {columnLabels[col.key] || col.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {unhidePanel && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: '240px', bottom: 0, zIndex: 10000 }}
          onClick={() => setUnhidePanel(false)} />
      )}
      {/* ── Export stage selection modal ─────────────────────────────────── */}
      {exportStageModal && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10400, background: 'rgba(0,0,0,0.35)' }} onClick={() => setExportStageModal(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10401, background: '#fff', borderRadius: '6px', boxShadow: '0 12px 40px rgba(0,0,0,0.22)', fontFamily: 'Raleway, sans-serif', width: '340px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#222' }}>
                Select stages to export
              </div>
              <div style={{ fontSize: '11px', fontWeight: 300, color: '#6b7280', marginTop: '3px' }}>
                {exportStageModal.type.toUpperCase()} export · {exportStageModal.selectedStages.size} of {exportStageOptions.length} selected
              </div>
            </div>
            <div style={{ padding: '8px 4px', overflowY: 'auto', flex: 1 }}>
              {/* Select all / none */}
              <div style={{ display: 'flex', gap: '8px', padding: '4px 14px 8px', borderBottom: '1px solid #f0f0ee' }}>
                <button
                  onClick={() => setExportStageModal(m => ({ ...m, selectedStages: new Set(exportStageOptions.map(s => s.key)) }))}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 500, color: '#9F902A', background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 0' }}>
                  Select all
                </button>
                <span style={{ color: '#ddd', fontSize: '10px', lineHeight: '18px' }}>|</span>
                <button
                  onClick={() => setExportStageModal(m => ({ ...m, selectedStages: new Set() }))}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 400, color: '#6b7280', background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 0' }}>
                  None
                </button>
              </div>
              {exportStageOptions.map(stage => {
                const checked = exportStageModal.selectedStages.has(stage.key);
                return (
                  <label key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 14px', paddingLeft: stage.isPart ? '32px' : '14px', cursor: 'pointer', background: checked ? 'rgba(159,144,42,0.04)' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setExportStageModal(m => {
                        const next = new Set(m.selectedStages);
                        if (next.has(stage.key)) next.delete(stage.key); else next.add(stage.key);
                        return { ...m, selectedStages: next };
                      })}
                      style={{ accentColor: '#9F902A', width: '13px', height: '13px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '11px', fontWeight: checked ? 500 : 300, color: checked ? '#333' : '#555' }}>
                      {stage.isPart && <span style={{ color: '#aaa', marginRight: '4px' }}>↳</span>}
                      {stage.name}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setExportStageModal(null)}
                style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, color: '#6b7280', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '3px', padding: '6px 14px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                disabled={exportStageModal.selectedStages.size === 0}
                onClick={() => runExport(exportStageModal.type, exportStageModal.selectedStages)}
                style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500, color: '#fff', background: exportStageModal.selectedStages.size === 0 ? '#ccc' : '#9F902A', border: 'none', borderRadius: '3px', padding: '6px 16px', cursor: exportStageModal.selectedStages.size === 0 ? 'default' : 'pointer' }}>
                Export {exportStageModal.type.toUpperCase()}
              </button>
            </div>
          </div>
        </>
      )}

      {compareModal && (
        <CompareVersionsModal
          project={project}
          projectVersions={projectVersions}
          displayRows={displayRows}
          timeline={timeline}
          allColumns={effectiveVisibleColumns}
          columnLabels={columnLabels}
          format={compareModal.format}
          onClose={() => setCompareModal(null)}
          onCompare={(slots) => { setCompareModal(null); openInlineCompare(null, slots); }}
        />
      )}

      {/* Contract Dates Modal */}
      {contractDatesModal && (() => {
        const { selectedStages, startDate, sets } = contractDatesModal;
        // Selectable items: stages and their part stages (indented), in order.
        const stageRows = [];
        allRows.filter(r => r.indent === 0).forEach(sr => {
          stageRows.push({ id: sr.id, label: (timeline[sr.id]?.taskName) || sr.defaultLabel || sr.name, isPart: false });
          (partStagesMap[sr.id] || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).forEach(p => {
            stageRows.push({ id: `substage_${sr.id}_${p.id}`, label: p.name || p.id, isPart: true });
          });
        });
        const update = (patch) => setContractDatesModal(prev => ({ ...prev, ...patch }));
        const updateSet = (id, patch) => setContractDatesModal(prev => ({
          ...prev,
          sets: prev.sets.map(s => s.id === id ? { ...s, ...patch } : s),
        }));
        const inputStyle = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', padding: '3px 6px', width: '44px', textAlign: 'center', outline: 'none' };
        const SET_LABELS  = CONTRACT_SET_LABELS;
        const SET_ACCENTS = CONTRACT_SET_ACCENTS;
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setContractDatesModal(null)}>
            <div style={{ background: '#fff', width: '520px', maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
              onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={15} style={{ color: BRIO_GOLD }} />
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Add Contract Dates</span>
                </div>
                <button onClick={() => setContractDatesModal(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                  <X size={14} style={{ color: '#aaa' }} />
                </button>
              </div>

              <div style={{ overflowY: 'auto', flex: 1, padding: '16px 18px 0' }}>
                {/* Start date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 500, color: '#444', minWidth: '70px' }}>Start date</span>
                  <BrioDatePicker value={startDate || ''} onChange={v => update({ startDate: v })} placeholder="Pick start date" clearable as="button"
                    triggerStyle={{ fontSize: '11px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, padding: '3px 8px', fontFamily: 'inherit', cursor: 'pointer', borderRadius: '2px' }} />
                </div>

                {/* Stage checklist */}
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Stages to include</div>
                <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', maxHeight: '160px', overflowY: 'auto', marginBottom: '14px' }}>
                  {stageRows.map((r, i) => {
                    const label = r.label;
                    const checked = selectedStages.has(r.id);
                    const setInfo = (contractDatesModal.stageSetMap || {})[r.id];
                    return (
                      <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', paddingLeft: r.isPart ? '26px' : '10px', background: i % 2 === 0 ? '#fafaf8' : '#fff', cursor: 'pointer', borderBottom: i < stageRows.length - 1 ? '1px solid ' + BRIO_BORDER : 'none' }}>
                        <input type="checkbox" checked={checked}
                          onChange={() => {
                            const next = new Set(selectedStages);
                            if (checked) next.delete(r.id); else next.add(r.id);
                            update({ selectedStages: next });
                          }}
                          style={{ accentColor: BRIO_GOLD }} />
                        <span style={{ fontSize: '11px', fontWeight: r.isPart ? 300 : 500, color: '#333', flex: 1 }}>{r.isPart ? `↳ ${label}` : label}</span>
                        {setInfo && (
                          <span style={{ fontSize: '9px', fontWeight: 600, color: setInfo.accent, background: setInfo.accent + '22', border: '0.5px solid ' + setInfo.accent + '55', padding: '1px 6px', borderRadius: '2px', flexShrink: 0, letterSpacing: '0.03em' }}>
                            Set {setInfo.label}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>

                {/* Dynamic sets */}
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Auto input — equal split between selected stages</div>
                {sets.map((s, idx) => {
                  const label  = SET_LABELS[idx]  || String(idx + 1);
                  const accent = SET_ACCENTS[idx % SET_ACCENTS.length];
                  const totalCal = s.months * 30 + s.days;
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid ' + BRIO_BORDER, opacity: s.applied ? 0.6 : 1 }}>
                      <span style={{ fontSize: '10px', fontWeight: 600, color: accent, minWidth: '36px', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Set {label}
                        {s.applied && <span style={{ fontSize: '9px', color: '#6a9a6a', fontWeight: 500 }}>✓</span>}
                      </span>
                      <input type="number" min="0" value={s.months}
                        onChange={e => updateSet(s.id, { months: Math.max(0, Number(e.target.value)), applied: false })}
                        style={inputStyle} />
                      <span style={{ fontSize: '10px', color: '#666' }}>mo</span>
                      <input type="number" min="0" value={s.days}
                        onChange={e => updateSet(s.id, { days: Math.max(0, Number(e.target.value)), applied: false })}
                        style={inputStyle} />
                      <span style={{ fontSize: '10px', color: '#666' }}>days</span>
                      <span style={{ fontSize: '10px', color: '#aaa', flex: 1 }}>
                        = {totalCal} cal / {selectedStages.size || 1} ≈ {Math.round(totalCal / Math.max(1, selectedStages.size))} each
                      </span>
                      <button
                        style={{ padding: '5px 14px', fontSize: '11px', fontWeight: 500, border: 'none', background: s.applied ? '#888' : accent, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', borderRadius: '2px', flexShrink: 0 }}
                        onClick={() => handleApplyContractDates(s.id)}
                      >{s.applied ? 'Applied' : 'Apply'}</button>
                    </div>
                  );
                })}
                {/* Add set button */}
                <div style={{ padding: '10px 0 4px' }}>
                  <button
                    style={{ fontSize: '11px', fontWeight: 400, border: '1px dashed ' + BRIO_BORDER, background: 'transparent', color: BRIO_GREY, cursor: 'pointer', padding: '4px 14px', borderRadius: '2px', fontFamily: 'Raleway, sans-serif', width: '100%' }}
                    onClick={() => setContractDatesModal(prev => ({ ...prev, sets: [...prev.sets, { id: Date.now(), months: 0, days: 0, applied: false }] }))}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; }}
                  >+ Add set</button>
                </div>
              </div>

              <div style={{ padding: '12px 18px', borderTop: '1px solid ' + BRIO_BORDER, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <button onClick={handleClearContractDates}
                  style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 400, border: '1px solid ' + BRIO_RED + '88', background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: BRIO_RED }}
                  onMouseEnter={e => { e.currentTarget.style.background = BRIO_RED + '10'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  title="Clears contract start, duration and end dates from all stages and sub-stages. Does not affect any other dates.">
                  Clear contract dates
                </button>
                <button onClick={() => setContractDatesModal(null)}
                  style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#555' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reset Timeline confirmation modal */}
      {showResetTimelineConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowResetTimelineConfirm(false)}>
          <div style={{ background: '#fff', width: '420px', maxWidth: '95vw', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid ' + BRIO_RED, background: BRIO_BG }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RotateCcw size={15} style={{ color: BRIO_RED }} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Reset Timeline</span>
              </div>
              <button onClick={() => setShowResetTimelineConfirm(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                <X size={15} style={{ color: '#aaa' }} />
              </button>
            </div>
            <div style={{ padding: '20px 20px 8px' }}>
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#333', lineHeight: 1.6, margin: 0 }}>
                This will permanently delete all timeline dates, tasks, and row data for <strong>{project.name}</strong>.
              </p>
              <p style={{ fontSize: '12px', fontWeight: 300, color: BRIO_RED, marginTop: '10px', marginBottom: 0 }}>
                Project settings presets will be kept. This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 20px', borderTop: '1px solid ' + BRIO_BORDER }}>
              <button onClick={() => setShowResetTimelineConfirm(false)}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#555' }}>
                Cancel
              </button>
              <button onClick={handleResetTimeline}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 500, border: 'none', background: BRIO_RED, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>
                Reset Timeline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reconstruct the on-screen display rows from a saved snapshot, mirroring the live
// ProjectTimelineEditor `displayRows` logic: part-stage headers, each part's Start /
// Invoicing / Closure rows, tasks grouped under their assigned part, plus the same
// hidden-row, collapse and active-tab filtering. This makes a version preview an exact
// view of the Timelines page at save time (including part stages and any active tab).
function buildVersionDisplayRows(snapshot, collapsedOverride = null, activeTabIdOverride = undefined) {
  const timeline = snapshot || {};
  const baseRows = Array.isArray(timeline._rows) ? timeline._rows : [];
  const vs = timeline._viewSettings || {};

  // partStagesMap: stageKey -> [partStages]
  const partStagesMap = {};
  Object.entries(timeline).forEach(([key, val]) => {
    if (val && Array.isArray(val.partStages) && val.partStages.length > 0) partStagesMap[key] = val.partStages;
  });

  // Part-stages always appear (no per-part "show" checkbox) — derive the active set as every defined
  // part, so a version preview shows all parts regardless of what an older snapshot persisted.
  const activeSubStages = {};
  Object.entries(partStagesMap).forEach(([k, parts]) => {
    if (parts && parts.length) activeSubStages[k] = new Set(parts.map(p => p.id));
  });
  // Collapse state: the interactive preview passes its own live set; otherwise fall back to whatever
  // was captured in the snapshot's view settings.
  const collapsedRows = collapsedOverride instanceof Set
    ? collapsedOverride
    : new Set(Array.isArray(vs.collapsedRows) ? vs.collapsedRows : []);

  // Apply the tab that was active when the version was saved, so the version reflects the same
  // tabbed view (the project's tabs are captured in the snapshot's _tabs). '__all__' / no active
  // tab shows the full timeline. Mirrors the live tabFilter, including hiding a parent stage row
  // when only its parts are selected. The preview's tab bar can override which tab is shown so all
  // captured tabs are browsable (activeTabIdOverride; '__all__' forces the full, untabbed view).
  const activeTabId = activeTabIdOverride !== undefined ? activeTabIdOverride : vs.activeTabId;
  const activeTab = (activeTabId === '__all__') ? null
    : (Array.isArray(timeline._tabs) ? timeline._tabs : []).find(t => t.id === activeTabId) || null;
  const tabFilter = (rowsIn) => {
    if (!activeTab) return rowsIn;
    const keys = new Set(activeTab.keys || []);
    const out = [];
    let curStage = null, stageSelected = false, stageVisible = false;
    for (const row of rowsIn) {
      if ((row.indent || 0) === 0) {
        curStage = row.id;
        stageSelected = keys.has(row.id);
        const anyPart = (partStagesMap[row.id] || []).some(p => keys.has(`substage_${row.id}_${p.id}`));
        stageVisible = stageSelected || anyPart;
        if (stageSelected) out.push(row);
        continue;
      }
      if (!stageVisible) continue;
      if (row.isSubStageHeader || row.isPartSystem) {
        const sk = row.isSubStageHeader ? row.id : String(row.id).replace(/^(start_|invoicing_|closure_)/, '');
        if (sk.startsWith('substage_') && !keys.has(sk)) continue;
        out.push(row); continue;
      }
      const sid = timeline[row.id]?._subStageId;
      if (sid && curStage) {
        if (!keys.has(`substage_${curStage}_${sid}`)) continue;
        out.push(row); continue;
      }
      if (!stageSelected) continue;
      out.push(row);
    }
    return out;
  };

  // Step 0: drop user-hidden rows
  const visible = baseRows.filter(r => !timeline[r.id]?._hidden);

  // Step 1: collapse filter
  let collapsed;
  if (collapsedRows.size === 0) {
    collapsed = visible;
  } else {
    collapsed = [];
    const skipBelow = [];
    for (let i = 0; i < visible.length; i++) {
      const row = visible[i];
      while (skipBelow.length > 0 && (row.indent || 0) <= skipBelow[skipBelow.length - 1]) skipBelow.pop();
      if (skipBelow.length > 0) continue;
      collapsed.push(row);
      if (collapsedRows.has(row.id) && rowHasChildren(visible, i)) skipBelow.push(row.indent || 0);
    }
  }

  // Step 2: inject virtual sub-stage rows for actively selected sub-stages
  const hasAnyActive = Object.keys(activeSubStages).some(sid => activeSubStages[sid]?.size > 0);
  if (!hasAnyActive) return tabFilter(collapsed);

  const result = [];
  let idx = 0;
  while (idx < collapsed.length) {
    const row = collapsed[idx];
    const selectedParts = (row.indent || 0) === 0 && !collapsedRows.has(row.id)
      ? (activeSubStages[row.id] ? (partStagesMap[row.id] || []).filter(p => activeSubStages[row.id].has(p.id)).sort((a, b) => (a.name || '').localeCompare(b.name || '')) : [])
      : [];
    if (selectedParts.length === 0) { result.push(row); idx++; continue; }

    result.push(row);
    idx++;
    const children = [];
    while (idx < collapsed.length && (collapsed[idx].indent || 0) > 0) { children.push(collapsed[idx]); idx++; }
    const startRows = children.filter(r => r.id.startsWith('start_'));
    const systemRows = children.filter(r => r.id.startsWith('closure_') || r.id.startsWith('invoicing_'));
    const regularRows = children.filter(r => !r.id.startsWith('closure_') && !r.id.startsWith('invoicing_') && !r.id.startsWith('start_'));
    const selectedPartIds = new Set(selectedParts.map(p => p.id));
    const grouped = {};
    selectedParts.forEach(p => { grouped[p.id] = []; });
    const unassignedRows = [];
    regularRows.forEach(r => {
      const sid = timeline[r.id]?._subStageId || null;
      if (sid && selectedPartIds.has(sid)) grouped[sid].push(r); else unassignedRows.push(r);
    });
    startRows.forEach(r => result.push(r));
    unassignedRows.forEach(r => result.push(r));
    selectedParts.forEach(part => {
      const subKey = `substage_${row.id}_${part.id}`;
      const partName = part.name || part.id;
      result.push({ id: subKey, key: subKey, indent: 1, name: partName, isSubStageHeader: true, partId: part.id, stageId: row.id, defaultLabel: null });
      if (collapsedRows.has(subKey)) return;
      if (isAdvanceStage(row.id)) { grouped[part.id].forEach(r => result.push(r)); return; }
      const startKey = `start_${subKey}`;
      result.push({ id: startKey, key: startKey, indent: 1, name: `Start of ${partName} Stage`, defaultLabel: `Start of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
      grouped[part.id].forEach(r => result.push(r));
      const invKey = `invoicing_${subKey}`;
      const closeKey = `closure_${subKey}`;
      result.push({ id: invKey, key: invKey, indent: 1, name: `Invoicing of ${partName} Stage`, defaultLabel: `Invoicing of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
      result.push({ id: closeKey, key: closeKey, indent: 1, name: `Closure of ${partName} Stage`, defaultLabel: `Closure of ${partName} Stage`, isPartSystem: true, stageId: row.id, partId: part.id });
    });
    systemRows.forEach(r => result.push(r));
  }
  return tabFilter(result);
}

// Aggregated stage / sub-stage dates for a saved snapshot — mirrors the editor's stageDates /
// subStageDates memos so a version preview can show a stage's rolled-up duration + start/end even though
// those are derived (not stored) values. Uses the snapshot's own captured working-day settings.
function computeVersionStageDates(snapshot) {
  const rows = Array.isArray(snapshot._rows) ? snapshot._rows : [];
  const settings = resolveActiveTimelineSettings(snapshot);
  const workDays = new Set(settings.workingDays.days);
  const holidaySet = new Set((settings.workingDays.holidays || []).map(h => h.date));
  const partStagesMap = {};
  Object.entries(snapshot).forEach(([key, val]) => {
    if (val && Array.isArray(val.partStages) && val.partStages.length > 0) partStagesMap[key] = val.partStages;
  });
  const aggregate = (childIds) => {
    let minS = null, maxE = null;
    for (const cid of childIds) {
      const d = snapshot[cid]; if (!d) continue;
      const ps = parseDate(d.plannedStartDate);
      const pe = parseDate(d.plannedEndDate);
      if (ps && (!minS || ps < minS)) minS = ps;
      if (pe && (!maxE || pe > maxE)) maxE = pe;
    }
    return { plannedStartDate: minS, plannedEndDate: maxE, plannedDuration: countWorkingDays(minS, maxE, workDays, holidaySet) };
  };

  // Stage rows: aggregate every child except the invoicing_ row (mirrors the editor).
  const stageDates = {};
  let curStage = null, childIds = [];
  const flush = () => { if (curStage && childIds.length) stageDates[curStage] = aggregate(childIds); };
  for (const row of rows) {
    if ((row.indent || 0) === 0) { flush(); curStage = row.id; childIds = []; }
    else if (!row.id.startsWith('invoicing_')) childIds.push(row.id);
  }
  flush();

  // Sub-stage headers: aggregate each part's deliverables plus its own start/invoicing/closure rows.
  const subStageDates = {};
  let curStage2 = null, partGroups = {};
  const flushParts = () => {
    if (!curStage2) return;
    (partStagesMap[curStage2] || []).forEach(p => {
      const subKey = `substage_${curStage2}_${p.id}`;
      subStageDates[subKey] = aggregate([...(partGroups[p.id] || []), `start_${subKey}`, `invoicing_${subKey}`, `closure_${subKey}`].filter(id => !isAdvanceSystemRow(id)));
    });
  };
  for (const row of rows) {
    if ((row.indent || 0) === 0) { flushParts(); curStage2 = row.id; partGroups = {}; }
    else { const sid = snapshot[row.id]?._subStageId; if (sid) (partGroups[sid] = partGroups[sid] || []).push(row.id); }
  }
  flushParts();
  return { stageDates, subStageDates };
}

// ============================================================================
// VERSION TABLE PREVIEW — full read-only replica of the ProjectTimelineEditor
// table, rendered inline when a saved version is selected.
// Uses the column widths and custom columns stored in the snapshot.
// Cells are read-only, but stages / parent tasks / sub-stages can be collapsed
// and expanded interactively.
// ============================================================================

function VersionTablePreview({ version, onClose }) {
  const snapshot = version.snapshot || {};
  // Tabs captured in the snapshot (the version records the whole timeline + every tab, not just the
  // tab the user was on when saving). The preview shows a read-only tab bar so all captured tabs are
  // browsable, seeded to the tab that was active at save time.
  const snapshotTabs = Array.isArray(snapshot._tabs) ? snapshot._tabs : [];
  const [activeTabId, setActiveTabId] = useState(() => (snapshot._viewSettings || {}).activeTabId || '__all__');
  // Interactive collapse state — seeded from whatever collapse the snapshot captured, then freely
  // toggled in the preview (only the data cells are read-only, not the expand/collapse).
  const [collapsedRows, setCollapsedRows] = useState(
    () => new Set(Array.isArray((snapshot._viewSettings || {}).collapsedRows) ? snapshot._viewSettings.collapsedRows : [])
  );
  const toggleCollapse = (rowId) => setCollapsedRows(prev => {
    const next = new Set(prev);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    return next;
  });
  const rows = buildVersionDisplayRows(snapshot, collapsedRows, activeTabId);
  // Rows that head a collapsible group — computed from the fully-expanded row list so a collapsed
  // parent still shows its toggle. A row has children when the next fully-expanded row is deeper.
  const hasChildrenSet = useMemo(() => {
    const full = buildVersionDisplayRows(snapshot, new Set(), activeTabId);
    const set = new Set();
    for (let i = 0; i < full.length; i++) {
      const next = full[i + 1];
      if (next && (next.indent || 0) > (full[i].indent || 0)) set.add(full[i].id);
    }
    return set;
  }, [snapshot, activeTabId]);
  // Aggregated stage / sub-stage dates as captured when the version was saved.
  const { stageDates, subStageDates } = useMemo(() => computeVersionStageDates(snapshot), [snapshot]);
  const colWidths = (snapshot._viewSettings || {}).columnWidths || {};
  // border-collapse:separate requires borders on td, not tr
  const vTdStyle = { ...tdStyle, borderBottom: '1px solid ' + BRIO_BORDER };

  // Build the same column list the editor would use
  const customCols = (snapshot._customColumns || []).map(c => ({
    ...c, width: c.width || '8%', custom: true,
  }));
  const allCols = [...SCHEDULE_COLUMNS, ...customCols];

  // Sequential numbers for task rows (sub-stage headers carry no number, mirroring the editor)
  let taskNum = 0;
  const numMap = {};
  rows.forEach(r => {
    if ((r.indent || 0) > 0 && !r.isSubStageHeader) { taskNum++; numMap[r.id] = taskNum; }
  });

  // Resolve a row's stored data record. Part-stage headers store their dates on the part object
  // inside the parent stage's partStages array, not under a top-level key.
  const getRowData = (r) => {
    if (r.isSubStageHeader && r.stageId && r.partId) {
      const parts = snapshot[r.stageId]?.partStages || [];
      return parts.find(p => p.id === r.partId) || {};
    }
    return snapshot[r.id] || {};
  };

  const getRowBg = (r) =>
    (r.indent || 0) === 0 ? (STAGE_ROW_COLORS[r.id] || '#f4f4f0')
      : r.isSubStageHeader ? '#f4f4f0'
        : '#fff';

  const getRowName = (r) => {
    const data = snapshot[r.id] || {};
    if (data.taskName) return data.taskName;
    if (r.name) return r.name;
    if (r.defaultLabel) return r.defaultLabel;
    if (STAGE_NAMES[r.id]) return STAGE_NAMES[r.id];
    if (r.id && r.id.startsWith('closure_')) {
      const sk = r.id.slice('closure_'.length);
      return `Closure of ${STAGE_NAMES[sk] || sk} Stage`;
    }
    if (r.id && r.id.startsWith('invoicing_')) {
      const sk = r.id.slice('invoicing_'.length);
      return `Invoicing of ${STAGE_NAMES[sk] || sk} Stage`;
    }
    return r.id || '';
  };

  const renderCell = (row, col) => {
    const data = getRowData(row);
    const val = data[col.key];
    const isStage = (row.indent || 0) === 0 || row.isSubStageHeader;
    // Stage / sub-stage rows show their rolled-up dates + duration (captured at save time), the same as
    // the live editor — those aren't stored on the row itself, so they come from the aggregation.
    const agg = (row.indent || 0) === 0 ? stageDates[row.id]
      : (row.isSubStageHeader ? subStageDates[row.key || row.id] : null);

    if (col.key === 'taskName') {
      const canCollapse = hasChildrenSet.has(row.id);
      const isCollapsed = collapsedRows.has(row.id);
      // Carry-forward decorations — mirror the live editor: origin-stage prefix + "- CF" suffix.
      const isCF = !!data._isCarriedForward;
      const cfPrefix = isCF && data._cfFromLabel ? `${data._cfFromLabel}_ ` : '';
      return (
        <td
          key={col.key}
          style={{
            ...vTdStyle,
            padding: `4px 6px 4px ${(row.indent || 0) * 12 + 8}px`,
            fontWeight: isStage ? 500 : 300,
            color: isStage ? '#333' : '#444',
            position: 'sticky', left: '30px', zIndex: 1,
            background: getRowBg(row),
            boxShadow: '1px 0 0 0 ' + BRIO_BORDER,
            whiteSpace: 'normal', wordBreak: 'break-word',
          }}
        >
          {canCollapse ? (
            <span
              onClick={() => toggleCollapse(row.id)}
              title={isCollapsed ? 'Expand' : 'Collapse'}
              style={{ cursor: 'pointer', display: 'inline-flex', verticalAlign: 'middle', color: BRIO_GREY, marginRight: '3px' }}
            >
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            </span>
          ) : null}
          {(row.isSubStageHeader || row.isPartSystem) && <span style={{ color: '#aaa', marginRight: '4px' }}>↳</span>}
          {cfPrefix && <span style={{ color: '#555', fontWeight: 400 }}>{cfPrefix}</span>}
          {getRowName(row)}
          {isCF && <span style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', marginLeft: '3px' }}>- CF</span>}
        </td>
      );
    }

    if (col.type === 'date') {
      const d = (agg && agg[col.key] !== undefined) ? agg[col.key] : parseDate(val);
      return (
        <td key={col.key} style={{ ...vTdStyle, textAlign: 'center', fontSize: '11px', color: d ? '#333' : BRIO_GREY, padding: '4px 3px' }}>
          {d ? formatDateDisplay(d) : ''}
        </td>
      );
    }

    if (col.key === 'scheduleStatus') {
      const opt = STATUS_OPTIONS.find(o => o.value === val) || STATUS_OPTIONS[0];
      return (
        <td key={col.key} style={{ ...vTdStyle, textAlign: 'center', padding: '3px' }}>
          <span style={{ fontSize: '10px', fontWeight: 400, color: opt.color, background: opt.bg, padding: '2px 5px', borderRadius: '2px', display: 'inline-block', whiteSpace: 'nowrap' }}>
            {opt.label}
          </span>
        </td>
      );
    }

    if (col.key === 'percentComplete') {
      const pct = val != null ? Number(val) : 0;
      return (
        <td key={col.key} style={{ ...vTdStyle, textAlign: 'center', padding: '3px 4px' }}>
          <div style={{ fontSize: '11px', fontWeight: 300 }}>{pct || 0}%</div>
          <div style={{ height: '3px', background: '#eee', borderRadius: '2px', margin: '2px 0 0' }}>
            <div style={{ height: '100%', width: pct + '%', background: pct >= 100 ? BRIO_GREEN : BRIO_GOLD, borderRadius: '2px' }} />
          </div>
        </td>
      );
    }

    if (col.key === 'plannedDuration') {
      const durVal = agg ? agg.plannedDuration : val;
      return (
        <td key={col.key} style={{ ...vTdStyle, textAlign: 'center', fontSize: '11px', color: durVal ? '#333' : BRIO_GREY }}>
          {durVal ? durVal + 'd' : ''}
        </td>
      );
    }

    if (col.key === 'assignedTo') {
      const arr = Array.isArray(val) ? val : (val ? [val] : []);
      return (
        <td key={col.key} style={{ ...vTdStyle, fontSize: '11px', color: arr.length ? '#333' : BRIO_GREY, whiteSpace: 'normal', padding: '3px 6px' }}>
          {arr.join(', ')}
        </td>
      );
    }

    // Text / select / multi-select / predecessors
    const display = Array.isArray(val) ? val.join(', ') : (val != null ? String(val) : '');
    return (
      <td key={col.key} style={{ ...vTdStyle, fontSize: '11px', color: display ? '#333' : BRIO_GREY, whiteSpace: col.key === 'remarks' || col.key === 'delayCaused' ? 'normal' : 'nowrap', padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {display}
      </td>
    );
  };

  const createdLabel = new Date(version.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* Version banner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px', background: '#fffbef', border: '1px solid #e8d87a', marginBottom: '14px', fontSize: '12px' }}>
        <span style={{ fontWeight: 500, color: BRIO_GOLD }}>{version.label}</span>
        <span style={{ fontWeight: 300, color: BRIO_GREY }}>Saved {createdLabel}</span>
        <span style={{ fontWeight: 300, color: '#ccc' }}>·</span>
        <span style={{ fontWeight: 300, color: BRIO_GREY, fontSize: '11px' }}>Read-only cells · expand/collapse to browse</span>
        <button
          onClick={onClose}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 400, color: BRIO_GREY, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
          onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}
        >
          <X size={11} /> Close
        </button>
      </div>

      {/* Tab bar — browse every tab captured in this version (read-only). Mirrors the Timelines page's
          tab bar; "Project timelines" is the implicit full (untabbed) view. Only shown when the
          snapshot actually captured tabs. */}
      {snapshotTabs.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[{ id: '__all__', name: 'Project timelines' }, ...snapshotTabs].map(tab => {
            const active = tab.id === '__all__'
              ? !snapshotTabs.some(t => t.id === activeTabId)
              : activeTabId === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTabId(tab.id)}
                title={tab.id === '__all__' ? 'Show all stages (no tab filter)' : undefined}
                style={{
                  fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: active ? 600 : 400,
                  padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
                  border: '1px solid ' + (active ? BRIO_GOLD : BRIO_BORDER),
                  background: active ? BRIO_GOLD : '#fff',
                  color: active ? '#fff' : BRIO_GREY,
                  borderRadius: '3px',
                }}>
                {tab.name || 'Tab'}
              </button>
            );
          })}
        </div>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: BRIO_GREY, fontSize: '13px', border: '1px solid ' + BRIO_BORDER }}>
          No timeline data was saved in this version.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', border: '1px solid ' + BRIO_BORDER, background: '#fff', borderRadius: '1px' }}>
          <table style={{
            borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed',
            width: '100%', minWidth: '100%',
            fontSize: '12px', fontWeight: 300, fontFamily: 'Raleway, sans-serif',
            borderTop: '1px solid ' + BRIO_BORDER, borderLeft: '1px solid ' + BRIO_BORDER,
          }}>
            <colgroup>
              <col style={{ width: '30px' }} />
              {allCols.map(col => (
                <col key={col.key} style={{ width: colWidths[col.key] || col.width }} />
              ))}
            </colgroup>
            <thead style={{ position: 'sticky', top: 0, zIndex: 4 }}>
              <tr style={{ background: BRIO_BG }}>
                <th style={{ ...thStyle, textAlign: 'center', width: '30px', padding: '8px 4px', position: 'sticky', left: 0, zIndex: 6, background: BRIO_BG }}>#</th>
                {allCols.map(col => (
                  <th
                    key={col.key}
                    style={{
                      ...thStyle,
                      textAlign: col.key === 'taskName' ? 'left' : 'center',
                      ...(col.sticky ? {
                        position: 'sticky', left: '30px', zIndex: 5,
                        boxShadow: '1px 0 0 0 ' + BRIO_BORDER + ', inset 0 1px 0 ' + BRIO_BORDER + ', inset 0 -1px 0 ' + BRIO_BORDER,
                      } : {}),
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{ background: getRowBg(row) }}>
                  <td style={{ ...vTdStyle, textAlign: 'center', fontSize: '11px', color: BRIO_GREY, padding: '4px', position: 'sticky', left: 0, zIndex: 2, background: getRowBg(row) }}>
                    {numMap[row.id] || ''}
                  </td>
                  {allCols.map(col => renderCell(row, col))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TEMPLATE BUILDER VIEW
// Wraps ProjectTimelineEditor with a fake project + proxy supabaseProjects so
// the editor saves to app_settings (timeline_templates) rather than a real project.
// ============================================================================

const TIMELINE_TEMPLATES_KEY = 'timeline_templates';
// Ids of timeline templates hidden from the projects' "apply template" dropdown (eye toggle in the
// Template Builder). A plain id list so it covers built-in and saved templates alike.
const TIMELINE_TEMPLATES_HIDDEN_KEY = 'timeline_templates_hidden';
const TEMPLATE_PROJECT_ID = 'timeline-tpl-edit';

// Save a Timelines-page edit as a field-level diff (base -> next) over the latest stored timeline,
// so an edit never writes back stale copies of fields it did not change (notably the Tracking
// page's Planned / Contract / Actual dates). The Template Builder's proxy has no diff method and
// never persists to a real project, so it keeps the whole-object write.
// Tracking-owned fields (Contract / Planned / Actual / invoice / paid / billing, on stages and parts)
// change only when the caller passes allowTracking: true, which the editor does only while "Sync to
// tracking" is on. Everything else (gantt, template apply, version restore, resets) never touches them.
function writeTimelineDiff(sp, projectId, base, next, { allowTracking = false } = {}) {
  if (String(projectId) !== TEMPLATE_PROJECT_ID && typeof sp.updateProjectTimelineDiff === 'function') {
    return sp.updateProjectTimelineDiff(projectId, base || {}, next, { allowTracking });
  }
  return sp.updateProject(projectId, { stageTimeline: next });
}

// Drift notification shown at the top of the Template Builder when the selected SOW no longer matches
// the template's task↔SOW links (see computeSowLinkDiff). Lists the linked tasks whose deliverable was
// removed/renamed away, and the SOW deliverables no task covers — answering "tell me what changed"
// when a base SOW is re-pointed or edited. Dismissible per unique diff (re-appears when the diff
// changes). Re-linking / adding tasks is done via the existing right-click → Link to SOW.
function SowDriftBanner({ orphanedRows, extras, stageNameByKey }) {
  const orphanCount = orphanedRows.length;
  const extraCount = extras.length;
  const signature = useMemo(
    () => JSON.stringify([orphanedRows.map(o => `${o.rowId}|${o.label}`), extras.map(e => `${e.stageKey}|${e.label}`)]),
    [orphanedRows, extras]
  );
  const [dismissedSig, setDismissedSig] = useState(null);
  const [open, setOpen] = useState(false); // default minimised — expand via the chevron / header
  if ((!orphanCount && !extraCount) || dismissedSig === signature) return null;
  const nameOf = (sk) => stageNameByKey[sk] || sk;
  const chip = (bg, col) => ({ fontSize: '10.5px', fontWeight: 600, color: col, background: bg, border: `1px solid ${col}44`, borderRadius: '2px', padding: '1px 7px', whiteSpace: 'nowrap' });
  const sectionTitle = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#8a6d1f', margin: '11px 0 5px' };
  const item = { fontSize: '11.5px', fontWeight: 300, color: '#5a4d2a', padding: '2px 0', lineHeight: 1.45 };
  return (
    <div style={{ border: '1px solid #eadfc2', background: '#fbf6ea', marginBottom: '16px', fontFamily: 'Raleway, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: '13px' }}>⚠</span>
        <span style={{ fontSize: '12.5px', fontWeight: 500, color: '#7a5f1a' }}>
          {orphanCount > 0 ? 'The SOW no longer matches some of this template’s links' : 'Some SOW deliverables aren’t linked to a task'}
        </span>
        {orphanCount > 0 && <span style={chip('#f6e3e3', '#b05050')}>{orphanCount} removed / renamed</span>}
        {extraCount > 0 && <span style={chip('#eaf1e6', '#5a7d3a')}>{extraCount} new / unlinked</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', color: '#8a6d1f' }} title={open ? 'Minimise' : 'Expand'}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </span>
        <button onClick={(e) => { e.stopPropagation(); setDismissedSig(signature); }}
          title="Dismiss until the SOW changes again"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#8a6d1f', fontSize: '15px', lineHeight: 1, padding: '0 2px' }}>×</button>
      </div>
      {open && (
        <div style={{ padding: '0 14px 12px 14px', borderTop: '1px solid #eadfc2' }}>
          {orphanCount > 0 && (
            <>
              <div style={sectionTitle}>Linked tasks whose SOW deliverable was removed or renamed</div>
              {orphanedRows.map((o, i) => (
                <div key={`${o.rowId}-${i}`} style={item}>
                  •&nbsp;<strong style={{ fontWeight: 500 }}>{o.label || '(unnamed task)'}</strong>
                  <span style={{ color: '#a08a4a' }}>&nbsp;— {nameOf(o.stageKey)}{o.hasSowRef ? '' : ' · from scope'}</span>
                </div>
              ))}
            </>
          )}
          {extraCount > 0 && (
            <>
              <div style={sectionTitle}>SOW deliverables not linked to any task</div>
              {extras.map((e, i) => (
                <div key={`${e.stageKey}-${i}`} style={item}>
                  •&nbsp;<strong style={{ fontWeight: 500 }}>{e.label || '(unnamed deliverable)'}</strong>
                  <span style={{ color: '#a08a4a' }}>&nbsp;— {nameOf(e.stageKey)}</span>
                </div>
              ))}
            </>
          )}
          <div style={{ fontSize: '10.5px', fontWeight: 300, color: '#a08a4a', marginTop: '11px', lineHeight: 1.5 }}>
            Right-click a task → <em>Link to SOW</em> to re-link a removed one, or to link a task to a new deliverable. <em>Reset timelines</em> rebuilds the structure from the current scope.
          </div>
        </div>
      )}
    </div>
  );
}

// Gantt view for the template builder. Reuses the same chart the project timelines use
// (PerProjectGanttChart / TimelineHeader / draggable bars) but is self-contained: its own zoom +
// collapse state and a range scoped to the template's OWN dates (a template is a relative schedule,
// not calendar-anchored). A compact toolbar mirrors the editor's Table/Gantt toggle so the user can
// switch back. Bar drags bubble up through onBarDrag, which the builder commits via its save proxy.
function TemplateGanttPanel({ project, canDrag, onBarDrag, onSwitchToTable }) {
  const default3M = () => { const i = GANTT_ZOOM_LEVELS.findIndex(z => z.label === '3M'); return i >= 0 ? i : GANTT_ZOOM_DEFAULT_INDEX; };
  const [zoomIndex, setZoomIndex] = useState(default3M);
  const [collapsed, setCollapsed] = useState(() => new Set());

  const rows = useMemo(() => buildPerProjectGanttRows(project, null), [project]);
  const displayRows = useMemo(() => {
    if (collapsed.size === 0) return rows;
    const out = [];
    let hideBelow = null;
    for (const r of rows) {
      const ind = r.indent || 0;
      if (hideBelow !== null) { if (ind > hideBelow) continue; hideBelow = null; }
      out.push(r);
      if (collapsed.has(r.id)) hideBelow = ind;
    }
    return out;
  }, [rows, collapsed]);

  // Range scoped to the template's own planned dates (padded), so an undated template still shows a
  // sensible empty window rather than a 16-year calendar.
  const { rangeStart, rangeEnd } = useMemo(() => {
    let minDate = null, maxDate = null;
    rows.forEach(r => { [r.start, r.end].forEach(d => { if (d) { if (!minDate || d < minDate) minDate = d; if (!maxDate || d > maxDate) maxDate = d; } }); });
    const now = new Date();
    return { rangeStart: addDays(minDate || now, -7), rangeEnd: addDays(maxDate || addDays(now, 120), 30) };
  }, [rows]);

  const level = GANTT_ZOOM_LEVELS[zoomIndex];
  const pxPerDay = level.pxPerDay;
  const durationDays = level.durationDays;
  const totalDays = Math.max(daysBetween(rangeStart, rangeEnd), 1);
  const totalWidth = totalDays * pxPerDay;
  const toggleCollapse = (id) => setCollapsed(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const hasData = rows.some(r => r.start && r.end);

  return (
    <div>
      {/* Compact gantt toolbar — Table/Gantt toggle (mirrors the editor) + zoom (inverted like the project gantt) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', border: '1px solid ' + BRIO_BORDER }}>
          <button onClick={onSwitchToTable} title="Table view"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', borderRight: '1px solid ' + BRIO_BORDER, cursor: 'pointer', background: 'transparent', color: BRIO_GREY }}>
            <Table size={12} />
          </button>
          <button title="Gantt chart view" disabled
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', cursor: 'default', background: BRIO_GOLD, color: '#fff' }}>
            <BarChart3 size={12} />
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <ToolbarIcon onClick={() => setZoomIndex(i => Math.min(i + 1, GANTT_ZOOM_LEVELS.length - 1))} tooltip="Zoom out"><ZoomOut size={12} /></ToolbarIcon>
        <ZoomResetLabel zoomLabel={level.label} onReset={() => setZoomIndex(default3M())} />
        <ToolbarIcon onClick={() => setZoomIndex(i => Math.max(i - 1, 0))} tooltip="Zoom in"><ZoomIn size={12} /></ToolbarIcon>
      </div>

      <TimelineLegend />

      {!hasData ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: BRIO_GREY }}>
          <p style={{ fontSize: '13px', fontWeight: 300 }}>No dated tasks in this template yet.</p>
          <p style={{ fontSize: '12px', fontWeight: 300, marginTop: '4px' }}>Set a start date (or add durations/dependencies) in the Table view to see bars here.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)', border: '1px solid ' + BRIO_BORDER, background: '#fff' }}>
          <div style={{ minWidth: 240 + totalWidth }}>
            <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, background: '#fff' }}>
              <div style={{ width: '240px', minWidth: '240px', display: 'flex', alignItems: 'stretch', borderRight: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG, position: 'sticky', left: 0, zIndex: 4, fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: BRIO_GREY }}>
                <div style={{ width: '32px', minWidth: '32px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid ' + BRIO_BORDER }}>#</div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '8px 12px' }}>Task Name</div>
              </div>
              <div style={{ flex: 1, width: totalWidth }}>
                <TimelineHeader rangeStart={rangeStart} rangeEnd={rangeEnd} pxPerDay={pxPerDay} durationDays={durationDays} />
              </div>
            </div>
            <PerProjectGanttChart
              rows={displayRows}
              rangeStart={rangeStart}
              pxPerDay={pxPerDay}
              totalWidth={totalWidth}
              collapsedIds={collapsed}
              onToggle={toggleCollapse}
              canDrag={canDrag}
              onBarDrag={onBarDrag}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// BRM template builder: the standalone editor on an in-memory timeline, in the BRM page's own column
// layout (custom columns, widths, order, hidden columns and categories are read from BRM, never stored
// in the template). Saves on the Save button; read only for anyone can_edit_brm_templates() refuses.
function BrmTemplateBuilderView({ template, brmTimeline, canEditTemplates, onSave, onDelete, onExit,
  stages, isAdmin, isSupervisor, currentUser, activeUsers, activeClients, pageZoomIndex, setPageZoomIndex }) {
  const [name, setName] = useState(template?.name || '');
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPresetSettings, setShowPresetSettings] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(2);
  const compareModalTriggerRef = useRef(null);
  const pendingCompareVersionRef = useRef(null);
  const layout = useMemo(() => {
    const b = brmTimeline || {};
    const vs = { ...(b._viewSettings || {}) };
    delete vs.collapsedRows; delete vs.activeTabId; delete vs.syncEnabled; delete vs.showTabBar;
    const out = { _viewSettings: vs };
    ['_customColumns', '_assignedToCategories', '_settings', '_colFmt'].forEach(k => { if (b[k] !== undefined) out[k] = b[k]; });
    return out;
  }, [brmTimeline]);
  const latestRef = useRef(null);
  if (latestRef.current === null) {
    const base = template?.timeline && Array.isArray(template.timeline._rows) && template.timeline._rows.length
      ? template.timeline
      : { _rows: [{ id: generateCustomStageId(), indent: 0, defaultLabel: 'New stage' }, { id: generateRowId(), indent: 1 }] };
    latestRef.current = { ...base, ...layout };
  }
  const [version, setVersion] = useState(0);
  const proxy = useMemo(() => {
    const put = async (_id, updates) => {
      if (updates && updates.stageTimeline) { latestRef.current = updates.stageTimeline; setVersion(v => v + 1); }
      return { success: true };
    };
    return { updateProject: put, updateProjectTimelineDiff: async (id, _base, next) => put(id, { stageTimeline: next }) };
  }, []);
  const fakeProject = useMemo(() => ({
    id: BRM_TEMPLATE_PROJECT_ID, name: name || 'BRM template', projectCode: 'BRM',
    stageTimeline: latestRef.current, clientId: null, color: BRIO_GOLD, stages: [], status: 'active',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [name, version]);
  const fakeScopes = useMemo(() => ({ scopes: [], loading: false }), []);
  const handleSave = async () => {
    const n = name.trim();
    if (!n) { alert('Give the template a name first.'); return; }
    const tl = latestRef.current || {};
    setSaving(true);
    const res = await onSave({ id: template?.id || ('brmtpl_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5)), name: n,
      timeline: brmTemplateTimelineFrom(tl, Array.isArray(tl._rows) ? tl._rows : []) });
    setSaving(false);
    if (res && res.success === false) alert('Could not save the template: ' + (res.error || 'permission denied.'));
  };
  const btn = { fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '4px 12px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#555', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', fontFamily: 'Raleway, sans-serif' }}>
        <span style={{ fontSize: '11px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>BRM template</span>
        <input type="text" value={name} placeholder="Template name" disabled={!canEditTemplates}
          onChange={e => setName(e.target.value)}
          style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', border: '1px solid ' + BRIO_BORDER, padding: '4px 8px', minWidth: '220px', outline: 'none' }} />
        {canEditTemplates && (
          <button onClick={handleSave} disabled={saving} style={{ ...btn, border: 'none', background: BRIO_GOLD, color: '#fff', fontWeight: 500, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save template'}
          </button>
        )}
        {canEditTemplates && template && (
          <button onClick={() => { if (window.confirm(`Delete the template "${template.name}"?`)) onDelete(template.id); }} style={{ ...btn, color: BRIO_RED }}>
            <Trash2 size={11} /> Delete
          </button>
        )}
        {!canEditTemplates && <span style={{ fontSize: '10px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', border: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>Read only</span>}
        <button onClick={onExit} style={{ ...btn, marginLeft: 'auto' }}><ChevronLeft size={12} /> Back to BRM</button>
      </div>
      <ProjectTimelineEditor
        project={fakeProject}
        stages={stages}
        supabaseProjects={proxy}
        isAdmin={isAdmin}
        isSupervisor={isSupervisor}
        currentUser={currentUser}
        activeUsers={activeUsers}
        activeClients={activeClients}
        contractScopesHook={fakeScopes}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        showPresetSettings={showPresetSettings}
        setShowPresetSettings={setShowPresetSettings}
        viewMode="table"
        setViewMode={() => {}}
        showTabBar={false}
        setShowTabBar={() => {}}
        activeTabId="__all__"
        setActiveTabId={() => {}}
        isFullscreen={false}
        toggleFullscreen={() => {}}
        zoomIndex={zoomIndex}
        setZoomIndex={setZoomIndex}
        pageZoomIndex={pageZoomIndex}
        setPageZoomIndex={setPageZoomIndex}
        onNavigateToTracking={() => {}}
        projectVersions={[]}
        compareModalTriggerRef={compareModalTriggerRef}
        pendingCompareVersionRef={pendingCompareVersionRef}
        brmTemplateEditable={canEditTemplates}
      />
    </div>
  );
}

function TemplateBuilderView({
  template,              // null = new; { id, name, sowTemplateId, stageTimeline } = existing
  sowTemplates,          // from scopeTemplatesHook (SOW / base templates)
  timelineTemplates = [], // all timeline templates (for the "Create from" base picker)
  onSave,                // (updatedTemplate) → save/update existing
  onCreate,              // (name, stageTimeline, sowTemplateId) → create new
  onDelete,              // (id) → delete
  onExit,                // () → exit builder mode
  stages,
  supabaseProjects,
  isAdmin, isSupervisor,
  activeUsers, activeClients,
  pageZoomIndex, setPageZoomIndex,
  templateHidden = false,        // whether this template is hidden from the projects' apply dropdown
  onToggleTemplateHidden,        // (templateId) → flip that visibility
  canEditTemplates = true,       // false → read only: no Edit / Delete / visibility toggle (see can_edit_timeline_templates)
  compactHeader = false,         // apply-dropdown preview: drop the Stages count and the "from" base template box
}) {
  const [selectedSowId, setSelectedSowId] = useState(template?.sowTemplateId || '');
  // "Create from" base template — this template inherits its base's deliverable names, SOW links and
  // newly-added deliverables. null = standalone template.
  const [baseTemplateId, setBaseTemplateId] = useState(template?.baseTemplateId || null);
  const [templateName, setTemplateName] = useState(template?.name || '');
  // Free-text "info" note — surfaced as the hover tooltip on this template in the projects' apply-template
  // dropdown, so an admin can describe when to use it.
  const [templateInfo, setTemplateInfo] = useState(template?.info || '');
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [editorKey, setEditorKey] = useState(0); // incremented to remount editor on SOW change
  // isEditMode: true for new templates, false for existing (view/read-only until Edit clicked)
  // A read-only viewer never enters edit mode (the parent always hands them an existing template).
  const [isEditMode, setIsEditMode] = useState(!template && canEditTemplates);
  // selectedStageKeys: null = all stages selected; Set<string> = explicit selection
  const [selectedStageKeys, setSelectedStageKeys] = useState(
    template?.selectedStageKeys ? new Set(template.selectedStageKeys) : null
  );
  const [showStageFilter, setShowStageFilter] = useState(false);
  const stageFilterRef = useRef(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPresetSettings, setShowPresetSettings] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [builderViewMode, setBuilderViewMode] = useState('table'); // 'table' | 'gantt' for the builder
  // Preview mode (edit mode only): seed a start date and cascade ephemeral dates through the table so a
  // template author can feel each stage's duration + how dependencies interlink. Dates are display-only.
  const [previewMode, setPreviewMode] = useState(false);
  const [previewStartDate, setPreviewStartDate] = useState('');
  // Preview is an edit-mode-only tool — leaving edit mode turns it off (requirement 4).
  useEffect(() => { if (!isEditMode) setPreviewMode(false); }, [isEditMode]);
  const compareModalTriggerRef = useRef(null);
  const pendingCompareVersionRef = useRef(null);

  // Close stage filter dropdown on outside click
  useEffect(() => {
    if (!showStageFilter) return;
    const handler = (e) => { if (stageFilterRef.current && !stageFilterRef.current.contains(e.target)) setShowStageFilter(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStageFilter]);

  // Latest stageTimeline is kept in a ref; proxy intercepts saves from the editor.
  // Seed initial _rows from the SOW so saving without edits still captures structure.
  const latestTimelineRef = useRef((() => {
    const base = template?.stageTimeline || {};
    if (base._rows && base._rows.length > 0) return base;
    const initSowId = template?.sowTemplateId || selectedSowId;
    if (initSowId) {
      const initSow = sowTemplates.find(t => t.id === initSowId);
      if (initSow) {
        const scope = sowTemplateToScope(initSow, TEMPLATE_PROJECT_ID);
        if (scope) {
          const rows = ensureRowOrder(buildRowsFromScope(scope));
          return { ...base, _rows: rows.map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) })) };
        }
      }
    }
    return base;
  })());

  // Auto-save gate: only true after a GENUINE user edit (a timeline mutation captured by the proxy,
  // or a name / SOW / stage-filter change). The auto-save effect refuses to fire while this is false,
  // so load-time state settling can never persist the editor's initial contents. This is what stops
  // a stale built-in default from silently overwriting a saved DB override.
  const dirtyRef = useRef(false);

  // Counter incremented each time the proxy captures a timeline update.
  // Included in fakeProject memo deps so the editor receives the fresh timeline.
  const [proxyVersion, setProxyVersion] = useState(0);

  // Ephemeral, fully-dated timeline for Preview mode — cascaded from the start date over the template's
  // own durations + dependencies. null when not previewing. Recomputes whenever the template changes
  // (proxyVersion) or the start date changes. Never persisted (see proxySupabase strip below).
  const previewTimeline = useMemo(
    () => (previewMode && previewStartDate) ? computePreviewTimeline(latestTimelineRef.current, previewStartDate) : null,
    [previewMode, previewStartDate, proxyVersion] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Fake project passed to ProjectTimelineEditor
  const fakeProject = useMemo(() => ({
    id: TEMPLATE_PROJECT_ID,
    name: templateName || 'Timeline Template',
    projectCode: null,
    stageTimeline: (previewMode && previewTimeline) ? previewTimeline : latestTimelineRef.current,
    clientId: null,
    color: BRIO_GOLD,
    contractCurrency: null,
    plannedDateTriggerThresholdDays: 7,
    stages: [],
    status: 'active',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [templateName, editorKey, proxyVersion, previewMode, previewTimeline]);

  // Fake scope built from the selected SOW template, filtered by selectedStageKeys
  const selectedSow = useMemo(() => sowTemplates.find(t => t.id === selectedSowId), [sowTemplates, selectedSowId]);
  // Stages offered by the stage filter, keyed by TIMELINE key:
  //  • existing template → its OWN stages (the indent-0 rows already in _rows), named via the SOW.
  //    A base SOW can carry more stages than the template materialised, so filtering must track the
  //    template's actual stages, not the SOW's full list.
  //  • new/empty template → the SOW's stages, i.e. the menu of stages to seed from.
  const builderStages = useMemo(() => {
    const nameByTk = {};
    (selectedSow?.stages || []).forEach(s => { nameByTk[scopeKeyToTimelineKey(s.key)] = s.name; });
    const tlRows = latestTimelineRef.current?._rows || [];
    const stageRows = tlRows.filter(r => (r.indent || 0) === 0);
    if (template && stageRows.length > 0) {
      return stageRows.map(r => ({ key: r.id, name: nameByTk[r.id] || r.id }));
    }
    return (selectedSow?.stages || []).map(s => ({ key: scopeKeyToTimelineKey(s.key), name: s.name }));
  }, [template, selectedSow, proxyVersion, editorKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // ── SOW reconciliation (INTENT-based, not word-matching) ──────────────────────────────────────
  // Every linked task carries a `sowRef` — the NUMBER(s) of the base-SOW deliverable it was
  // consciously linked to (e.g. DD-AD sowRef 9 → "Design integration with all appointed
  // consultants…"), even when the task's own wording differs. That intent is authored against the
  // template's OWN base SOW (its sowTemplateId). So reconciliation never compares a task's free
  // text to the SOW; it resolves each sowRef to its anchor deliverable in the authored base SOW and
  // then asks whether that same deliverable still exists in the CURRENTLY-selected SOW:
  //   • selected SOW === authored base → every intentional link holds by construction → no flags.
  //   • selected SOW changed           → a link is flagged only when its anchor deliverable has no
  //                                       match in the new SOW (genuinely removed/replaced).
  // The existence check is text-interpreted (reconLabelsMatch), so the anchor is followed through
  // reordering, id changes and minor label edits in the new SOW.
  const anchorSow = useMemo(
    () => (template?.sowTemplateId ? sowTemplates.find(t => t.id === template.sowTemplateId) : null),
    [sowTemplates, template]
  );
  // Authored base SOW: stage timelineKey → ORDERED normalised standard-deliverable labels.
  // sowRef N addresses index N-1 (the way deliverables are numbered in the scope UI).
  const anchorStageDelivs = useMemo(() => {
    const m = {};
    (anchorSow?.stages || []).forEach(s => {
      m[scopeKeyToTimelineKey(s.key)] = (s.standardDeliverables || []).map(d => normReconLabel(d?.label || ''));
    });
    return m;
  }, [anchorSow]);
  // Currently-selected SOW: stage timelineKey → all normalised deliverable labels to search for the
  // anchor within (standard + optional + custom — a deliverable may have moved between lists).
  const currentStageLabels = useMemo(() => {
    const m = {};
    (selectedSow?.stages || []).forEach(s => {
      m[scopeKeyToTimelineKey(s.key)] = [
        ...(s.standardDeliverables || []),
        ...(s.optionalDeliverables || []),
        ...(s.customDeliverables || []),
      ].map(d => normReconLabel(d?.label || '')).filter(Boolean);
    });
    return m;
  }, [selectedSow]);
  // Currently-selected SOW: stage timelineKey → its STANDARD deliverables (the "extras" candidates a
  // drift report offers to add as tasks). Labels kept raw for display; normalised on compare.
  const currentStandardDelivs = useMemo(() => {
    const m = {};
    (selectedSow?.stages || []).forEach(s => {
      m[scopeKeyToTimelineKey(s.key)] = (s.standardDeliverables || []).map(d => ({ label: d?.label || '' }));
    });
    return m;
  }, [selectedSow]);
  // SOW-link reconciliation diff — orphaned links/rows (removed or renamed-away deliverables) and
  // "extra" uncovered deliverables. Fingerprint-first (see computeSowLinkDiff): a row's stored
  // sowRefLabels are matched against the selected SOW, so an in-place edit / reorder / rename of the
  // base SOW is detected even when the SOW id is unchanged; legacy links without a fingerprint fall
  // back to anchor-position resolution (identical to the prior behaviour).
  const sowLinkDiff = useMemo(() => {
    if (!selectedSow) return { orphanedLinks: new Set(), orphanedRows: [], extras: [] };
    const tl = latestTimelineRef.current || {};
    return computeSowLinkDiff({
      rows: tl._rows || [],
      timelineData: tl,
      anchorStageDelivsNorm: anchorStageDelivs,
      targetWideNorm: currentStageLabels,
      targetPrimary: currentStandardDelivs,
    });
  }, [selectedSow, anchorStageDelivs, currentStageLabels, currentStandardDelivs, proxyVersion, editorKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Linked rows (carrying a sowRef) whose deliverable is absent from the selected SOW — the editor
  // tints their task-name cell faint red. Structural deliverable rows and rows without a sowRef
  // (meeting rows, sowSource tasks, headers) are surfaced only in the drift report, never tinted.
  const reconcileErrorRowIds = sowLinkDiff.orphanedLinks;
  // Stage timelineKey → display name, for the drift banner's grouping.
  const driftStageNames = useMemo(() => {
    const m = {};
    builderStages.forEach(s => { m[s.key] = s.name; });
    return m;
  }, [builderStages]);
  // selectedStageKeys holds TIMELINE keys (cd_ad …) — the same ids used by stage rows in _rows and
  // stored on the built-in template — so the selection maps directly onto rendered stages.
  const effectiveStageKeys = useMemo(() => {
    if (selectedStageKeys === null) return new Set(builderStages.map(s => s.key));
    return selectedStageKeys;
  }, [selectedStageKeys, builderStages]);
  // Drift report scoped to the stages the template actually includes (the stage-filter
  // selection). Deliverables belonging to SOW stages this template leaves out are neither
  // rendered nor editable here, so reporting them as "removed / renamed" or "new / unlinked"
  // is noise — filter both lists to effectiveStageKeys (which is every builder stage when the
  // filter is "All"). Both stageKey fields are timeline keys, the same space as effectiveStageKeys.
  const visibleSowLinkDiff = useMemo(() => ({
    orphanedRows: sowLinkDiff.orphanedRows.filter(o => effectiveStageKeys.has(o.stageKey)),
    extras: sowLinkDiff.extras.filter(e => effectiveStageKeys.has(e.stageKey)),
  }), [sowLinkDiff, effectiveStageKeys]);
  const fakeScope = useMemo(() => {
    if (!selectedSow) return null;
    const full = sowTemplateToScope(selectedSow, TEMPLATE_PROJECT_ID);
    if (!full) return null;
    if (effectiveStageKeys.size === builderStages.length) return full;
    return { ...full, stages: full.stages.filter(s => effectiveStageKeys.has(scopeKeyToTimelineKey(s.key))) };
  }, [selectedSow, effectiveStageKeys, builderStages.length]);
  const fakeContractScopesHook = useMemo(() => ({
    scopes: fakeScope ? [fakeScope] : [],
    loading: false,
    createScope: async () => ({ success: false }),
    updateScope: async () => ({ success: false }),
    confirmScope: async () => ({ success: false }),
  }), [fakeScope]);

  // Proxy supabaseProjects — intercept updateProject to capture latest timeline.
  // Also triggers a fakeProject recompute so the editor sees the updated timeline
  // after optimistic updates clear (prevents added rows from disappearing).
  const proxySupabase = useMemo(() => ({
    ...supabaseProjects,
    updateProject: async (_projectId, updates) => {
      if (updates.stageTimeline) {
        // In preview mode the editor's timeline carries cascaded dates — strip them so an edit made while
        // previewing (a duration/dependency change) persists WITHOUT ever writing dates into the template.
        latestTimelineRef.current = previewMode ? stripPreviewDates(updates.stageTimeline) : updates.stageTimeline;
        dirtyRef.current = true; // a real editor mutation — auto-save may now persist
        setProxyVersion(v => v + 1);
      }
      return { success: true };
    },
  }), [supabaseProjects, previewMode]);

  // Commit a gantt bar drag in the builder: rewrite the row's planned schedule (shared helper, same as
  // the project gantt) and push it through the proxy, so it lands in latestTimelineRef, bumps
  // proxyVersion (re-rendering the gantt from the fresh timeline) and triggers the template auto-save.
  // Gantt bar undo/redo for the builder — same { before, after } history as the project gantt, written
  // through the save proxy. Cleared when the builder leaves gantt view (below).
  const tplGanttUndoRef = useRef([]);
  const tplGanttRedoRef = useRef([]);
  const handleTemplateBarDrag = useCallback((rowId, mode, newStart, newEnd) => {
    const before = latestTimelineRef.current || {};
    const newTl = computeGanttBarDragTimeline(before, rowId, mode, newStart, newEnd);
    if (!newTl) return;
    tplGanttUndoRef.current.push({ before, after: newTl });
    if (tplGanttUndoRef.current.length > 200) tplGanttUndoRef.current.shift();
    tplGanttRedoRef.current = [];
    proxySupabase.updateProject(TEMPLATE_PROJECT_ID, { stageTimeline: newTl });
  }, [proxySupabase]);
  const doTplGanttUndo = useCallback(() => {
    if (!tplGanttUndoRef.current.length) return;
    const entry = tplGanttUndoRef.current.pop();
    tplGanttRedoRef.current.push(entry);
    proxySupabase.updateProject(TEMPLATE_PROJECT_ID, { stageTimeline: entry.before });
  }, [proxySupabase]);
  const doTplGanttRedo = useCallback(() => {
    if (!tplGanttRedoRef.current.length) return;
    const entry = tplGanttRedoRef.current.pop();
    tplGanttUndoRef.current.push(entry);
    proxySupabase.updateProject(TEMPLATE_PROJECT_ID, { stageTimeline: entry.after });
  }, [proxySupabase]);
  // Reset the gantt history whenever the builder leaves gantt view, and bind Ctrl/Cmd+Z / +Y while in it.
  useEffect(() => {
    if (builderViewMode !== 'gantt') { tplGanttUndoRef.current = []; tplGanttRedoRef.current = []; return undefined; }
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); doTplGanttUndo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); doTplGanttRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [builderViewMode, doTplGanttUndo, doTplGanttRedo]);

  // When the SOW template changes:
  //  • a template that already has task rows just RE-POINTS its base SOW — the grid reconciles
  //    against the new SOW in place (deliverable links re-validate; unmatched ones get the red
  //    flag), and the task organisation is preserved. It is NEVER rebuilt/wiped.
  //  • an empty (brand-new) template seeds its rows from the selected SOW's deliverables.
  const handleSowChange = (sowId) => {
    dirtyRef.current = true; // user changed the base SOW
    const existingRows = latestTimelineRef.current?._rows || [];
    if (existingRows.length > 0) {
      setSelectedSowId(sowId); // reconcile in place — tasks preserved, no rebuild, no remount
      return;
    }
    setSelectedSowId(sowId);
    setSelectedStageKeys(null); // reset to all stages selected
    if (sowId) {
      const sow = sowTemplates.find(t => t.id === sowId);
      if (sow) {
        const scope = sowTemplateToScope(sow, TEMPLATE_PROJECT_ID);
        if (scope) {
          const rows = ensureRowOrder(buildRowsFromScope(scope));
          latestTimelineRef.current = { _rows: rows.map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) })) };
        } else { latestTimelineRef.current = {}; }
      } else { latestTimelineRef.current = {}; }
    } else { latestTimelineRef.current = {}; }
    setEditorKey(k => k + 1);
  };

  const handleSave = () => {
    const tl = latestTimelineRef.current;
    const stageKeysArray = selectedStageKeys === null ? null : Array.from(selectedStageKeys);
    if (template) {
      onSave({ ...template, name: templateName || template.name, info: templateInfo || null, sowTemplateId: selectedSowId || null, stageTimeline: tl, selectedStageKeys: stageKeysArray, baseTemplateId: baseTemplateId || null, updatedAt: new Date().toISOString() });
      dirtyRef.current = false; // persisted via the Save button
    } else {
      if (!templateName.trim()) { alert('Please enter a template name before saving.'); return; }
      onCreate(templateName.trim(), tl, selectedSowId || null, stageKeysArray, baseTemplateId || null, templateInfo || null);
    }
  };

  const handleSaveAs = () => {
    if (!saveAsName.trim()) return;
    const stageKeysArray = selectedStageKeys === null ? null : Array.from(selectedStageKeys);
    onCreate(saveAsName.trim(), latestTimelineRef.current, selectedSowId || null, stageKeysArray, baseTemplateId || null, templateInfo || null);
    setShowSaveAsDialog(false);
  };

  // Set / change this template's "Create from" base. Selecting a base immediately re-syncs the current
  // editor to it — order, indent, dependencies, durations, assignees, names and SOW links flow from the
  // base (local rows kept); the link is remembered so future base edits keep flowing in on the base's next save.
  const changeBaseTemplate = (newBaseId) => {
    setBaseTemplateId(newBaseId || null);
    dirtyRef.current = true;
    if (newBaseId && newBaseId !== template?.id) {
      const baseTpl = timelineTemplates.find(t => t.id === newBaseId);
      if (baseTpl) {
        // Switching to a (possibly different) base establishes a fresh baseline: drop any prior
        // _baseSnapshot / _baseRowIds so the merge treats the current derived rows as user-owned and
        // doesn't clobber or wrongly delete them on this first sync.
        const { _baseSnapshot, _baseRowIds, ...derivedNoBaseline } = latestTimelineRef.current || {}; // eslint-disable-line no-unused-vars
        latestTimelineRef.current = mergeBaseDeliverablesIntoDerived(baseTpl.stageTimeline || {}, derivedNoBaseline);
        setProxyVersion(v => v + 1);
        setEditorKey(k => k + 1);
      }
    }
  };

  // Auto-save: for an already-persisted template (the Master template or any saved template), each
  // edit captured by the proxy (proxyVersion bump) or a name / SOW / stage-filter change is written
  // to the DB after a short debounce — so edits "write directly to the DB" without the Save button.
  // New, never-saved templates still use Save (they need a name first). `template` is read through a
  // ref so the save it triggers (which updates the template prop) can't re-trigger this effect.
  const autoSaveTimer = useRef(null);
  const autoSaveTemplateRef = useRef(template);
  useEffect(() => { autoSaveTemplateRef.current = template; }, [template]);
  useEffect(() => {
    if (!autoSaveTemplateRef.current) return;                 // unsaved templates use the Save button
    if (!dirtyRef.current) return;                            // only persist after a genuine user edit
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      const t = autoSaveTemplateRef.current;
      if (!t || !dirtyRef.current) return;
      const stageKeysArray = selectedStageKeys === null ? null : Array.from(selectedStageKeys);
      onSave({ ...t, name: templateName || t.name, info: templateInfo || null, sowTemplateId: selectedSowId || null, stageTimeline: latestTimelineRef.current, selectedStageKeys: stageKeysArray, baseTemplateId: baseTemplateId || null, updatedAt: new Date().toISOString() });
      dirtyRef.current = false;                               // persisted — reset until the next edit
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyVersion, templateName, templateInfo, selectedSowId, selectedStageKeys, baseTemplateId]);

  const inputStyle = { fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, padding: '5px 10px', outline: 'none', background: '#fff', color: '#333' };
  const btnStyle = (gold) => ({ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '5px 14px', border: gold ? 'none' : '1px solid ' + BRIO_BORDER, background: gold ? BRIO_GOLD : '#fff', color: gold ? '#fff' : '#555', cursor: 'pointer', whiteSpace: 'nowrap' });
  const selectStyle = { fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300, border: '1px solid ' + BRIO_BORDER, padding: '5px 28px 5px 10px', background: '#fff url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%23666\'/%3E%3C/svg%3E") no-repeat right 8px center', appearance: 'none', WebkitAppearance: 'none', color: '#333', cursor: 'pointer', outline: 'none' };
  // Read-only field "chip" for VIEW mode — a light frame around each value, filled with the banner's own
  // colour, so name / SOW / stages / create-from / info all read as uniform framed boxes (same font + size).
  const roFieldBox = { display: 'inline-flex', alignItems: 'center', gap: '5px', fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400, lineHeight: 1.25, color: '#444', background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', padding: '5px 10px', whiteSpace: 'nowrap' };
  const roFieldLabel = { fontSize: '12px', fontWeight: 400, color: '#a79f7a' }; // faint prefix inside a chip (e.g. "from") — same size, lighter colour

  const confirmedSowTemplates = sowTemplates.filter(t => t.status === 'confirmed');

  // Built-in defaults (e.g. the residential master template) aren't persisted, so they can't be
  // deleted in place — an admin edits or "Save As"-es them into an owned copy instead.
  const isBuiltIn = !!template?.builtIn;
  const defaultBadge = (
    <span style={{ fontFamily: 'Raleway, sans-serif', fontSize: '9px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRIO_GOLD, border: '1px solid ' + BRIO_GOLD, borderRadius: '2px', padding: '1px 5px', whiteSpace: 'nowrap' }}>Default</span>
  );

  return (
    <div>
      {/* ── Template builder toolbar — one wrapping row: fields stretch to fill; the action buttons ride
             on the same line when they fit (view mode) and wrap to a second line only when they don't
             (edit mode's five buttons). ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '10px 14px', background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, marginBottom: '16px' }}>

        {/* Visibility toggle — decides whether this template appears in the projects' apply dropdown.
            Only for saved/existing templates (a brand-new one has no id to remember yet). */}
        {template && onToggleTemplateHidden && canEditTemplates && (
          <button
            onClick={() => onToggleTemplateHidden(template.id)}
            title={templateHidden
              ? 'Hidden from projects — click to show it in the apply-template list'
              : 'Visible to projects — click to hide it from the apply-template list'}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', background: '#fff', cursor: 'pointer', color: templateHidden ? '#bbb' : BRIO_GOLD, flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; }}
          >
            {templateHidden ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}

        {/* Template name — editable when new or in edit mode, read-only otherwise */}
        {isEditMode || !template ? (
          <input
            value={templateName}
            onChange={e => { dirtyRef.current = true; setTemplateName(e.target.value); }}
            placeholder="Template name…"
            style={{ ...inputStyle, minWidth: '110px', flex: '1 1 auto' }}
          />
        ) : (
          <span style={{ ...roFieldBox, minWidth: '110px', flex: '1 1 auto' }}>
            {templateName || '(untitled)'}
            {isBuiltIn && defaultBadge}
          </span>
        )}

        {/* SOW template selector — editable when new or in edit mode, read-only otherwise. No text label:
            the dropdown value ("SOW Template …") already reads as the SOW. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0.8 1 auto', minWidth: 0 }}>
          {isEditMode || !template ? (
            <select value={selectedSowId} onChange={e => handleSowChange(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 0 }} title="Scope-of-Work template this timeline is built from">
              <option value="">— SOW: None —</option>
              {confirmedSowTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          ) : (
            <span style={{ ...roFieldBox, flex: 1, minWidth: 0 }} title="Scope-of-Work template">
              {confirmedSowTemplates.find(t => t.id === selectedSowId)?.name || '—'}
            </span>
          )}
        </div>

        {/* Stage filter — editable only in edit mode (or for a brand-new template); in view mode it
            renders as a read-only count, matching the SOW selector. Hides unselected stages. */}
        {builderStages.length > 0 && !compactHeader && (() => { const stageFilterEditable = isEditMode || !template; return (
          <div ref={stageFilterRef} style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
            <button
              onClick={() => { if (stageFilterEditable) setShowStageFilter(v => !v); }}
              disabled={!stageFilterEditable}
              title={stageFilterEditable ? 'Show or hide stages in this template' : 'Click Edit to change which stages are shown'}
              style={stageFilterEditable ? {
                ...btnStyle(false),
                display: 'flex', alignItems: 'center', gap: '5px', width: '100%',
                cursor: 'pointer',
                borderColor: selectedStageKeys !== null && selectedStageKeys.size < builderStages.length ? BRIO_GOLD : BRIO_BORDER,
                color: selectedStageKeys !== null && selectedStageKeys.size < builderStages.length ? BRIO_GOLD : '#555',
              } : {
                ...roFieldBox, cursor: 'default', width: '100%',
                color: selectedStageKeys !== null && selectedStageKeys.size < builderStages.length ? BRIO_GOLD : '#444',
              }}
            >
              <span>Stages</span>
              <span style={{ fontSize: '10px', opacity: 0.7 }}>
                {selectedStageKeys === null ? `(${builderStages.length})` : `(${selectedStageKeys.size}/${builderStages.length})`}
              </span>
              {stageFilterEditable && <svg width="8" height="5" viewBox="0 0 8 5" style={{ opacity: 0.5 }}><path d="M0 0l4 5 4-5z" fill="currentColor"/></svg>}
            </button>
            {showStageFilter && stageFilterEditable && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 200, marginTop: '2px',
                background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                minWidth: '220px', padding: '6px 0', fontFamily: 'Raleway, sans-serif',
              }}>
                {/* Select all / deselect all */}
                <div style={{ display: 'flex', gap: '6px', padding: '4px 10px 6px', borderBottom: '1px solid ' + BRIO_BORDER }}>
                  <button
                    onClick={() => { dirtyRef.current = true; setSelectedStageKeys(null); }}
                    style={{ ...btnStyle(false), fontSize: '10px', padding: '2px 8px' }}
                  >All</button>
                  <button
                    onClick={() => { dirtyRef.current = true; setSelectedStageKeys(new Set()); }}
                    style={{ ...btnStyle(false), fontSize: '10px', padding: '2px 8px' }}
                  >None</button>
                </div>
                {builderStages.map(stage => {
                  const tk = stage.key; // already a timeline key
                  const isChecked = selectedStageKeys === null || selectedStageKeys.has(tk);
                  return (
                    <label
                      key={tk}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 300, color: '#333' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8f6f0'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          dirtyRef.current = true;
                          const next = new Set(selectedStageKeys === null ? builderStages.map(s => s.key) : selectedStageKeys);
                          if (isChecked) { next.delete(tk); } else { next.add(tk); }
                          const isAll = next.size === builderStages.length;
                          setSelectedStageKeys(isAll ? null : next);
                        }}
                        style={{ accentColor: BRIO_GOLD, cursor: 'pointer' }}
                      />
                      <span>{stage.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ); })()}

        {/* "Create from" base template — editable in edit mode, read-only info otherwise (mirrors the
            SOW selector). Selecting a base pulls its deliverable names / links / new deliverables in. */}
        {timelineTemplates.length > 0 && !compactHeader && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0.8 1 auto', minWidth: 0 }}>
            {(isEditMode || !template) && <span style={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400, color: BRIO_GREY, whiteSpace: 'nowrap' }}>Create from</span>}
            {isEditMode || !template ? (
              <select value={baseTemplateId || ''} onChange={e => changeBaseTemplate(e.target.value || null)} style={{ ...selectStyle, flex: 1, minWidth: 0 }}>
                <option value="">— None —</option>
                {timelineTemplates.filter(t => t.id !== template?.id).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <span style={{ ...roFieldBox, flex: 1, minWidth: 0 }} title="Created from base template">
                <span style={roFieldLabel}>from</span>
                {timelineTemplates.find(t => t.id === baseTemplateId)?.name || '—'}
              </span>
            )}
          </div>
        )}

        {/* "Info" note — free text describing the template. Surfaced as the hover tooltip on this
            template in the projects' apply-template dropdown. No text label: the placeholder self-labels. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1.4 1 auto', minWidth: 0 }}>
          {isEditMode || !template ? (
            <input
              value={templateInfo}
              onChange={e => { dirtyRef.current = true; setTemplateInfo(e.target.value); }}
              placeholder="Info — hover text for the template picker…"
              title="Shown as the hover tooltip for this template in the project timelines apply-template dropdown"
              style={{ ...inputStyle, minWidth: 0, width: '100%' }}
            />
          ) : (
            <span title={templateInfo || undefined} style={{ ...roFieldBox, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <span style={roFieldLabel}>info</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{templateInfo || '—'}</span>
            </span>
          )}
        </div>

        {/* Action area. Edit / new mode: it takes a full row of its own (flex-basis 100% forces the wrap)
            with Preview pinned LEFT and the save / exit group RIGHT via space-between. View mode: a compact
            group (flex-shrink 0) that rides the top line's right edge next to the fields. */}
        <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '6px', ...((isEditMode || !template) ? { flex: '1 1 100%', justifyContent: 'space-between' } : { flexShrink: 0 }) }}>

        {/* Preview toggle — leftmost of the action buttons, edit mode only. Seeds a start date and
            cascades ephemeral, display-only dates (see the preview cascade above). */}
        {(isEditMode || !template) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              onClick={() => setPreviewMode(v => !v)}
              title="Preview the template scheduled from a start date — planned dates cascade from this template’s durations & dependencies (Mon–Fri, no holidays). Display only, never saved; edits to durations/dependencies still persist."
              style={{ ...btnStyle(previewMode), display: 'inline-flex', alignItems: 'center', gap: '5px' }}
            >
              <Calendar size={12} /> Preview
            </button>
            {previewMode && (
              <input
                type="date"
                className="brio-touch-date"
                value={previewStartDate}
                onChange={e => setPreviewStartDate(e.target.value)}
                title="Start date for the preview cascade"
                style={{ fontSize: '11px', padding: '3px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: '4px', fontFamily: 'Raleway, sans-serif', color: '#333' }}
              />
            )}
          </div>
        )}

        {/* Action buttons */}
        {!template ? (
          /* New template — just Save Template */
          <button onClick={handleSave} style={{ ...btnStyle(true), flexShrink: 0 }}>Save Template</button>
        ) : isEditMode ? (
          /* Existing template in edit mode — Save As + Save on the left, Edit + Delete on the right */
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => { setSaveAsName(template.name + ' (Copy)'); setShowSaveAsDialog(true); }} style={btnStyle(false)}>Save As</button>
            <button onClick={() => { handleSave(); setIsEditMode(false); }} style={btnStyle(true)}>Save</button>
            <div style={{ width: '1px', height: '20px', background: BRIO_BORDER }} />
            <button
              onClick={() => {
                // Exit discards every unsaved edit and restores the template's saved state:
                // task/row edits, the SOW selection, the stage filter and the name all revert.
                latestTimelineRef.current = template?.stageTimeline || {};
                dirtyRef.current = false; // Exit discards unsaved edits — nothing to persist
                setTemplateName(template?.name || '');
                setTemplateInfo(template?.info || '');
                setSelectedSowId(template?.sowTemplateId || '');
                setSelectedStageKeys(template?.selectedStageKeys ? new Set(template.selectedStageKeys) : null);
                setBaseTemplateId(template?.baseTemplateId || null);
                setProxyVersion(v => v + 1);
                setEditorKey(k => k + 1);
                setIsEditMode(false);
              }}
              style={btnStyle(false)}
            >Exit</button>
            {!isBuiltIn && (
              <button
                onClick={() => { if (window.confirm(`Delete template "${template.name}"?`)) { onDelete(template.id); } }}
                style={{ ...btnStyle(false), color: '#b05050', borderColor: '#e0c0c0' }}
              >Delete</button>
            )}
          </div>
        ) : canEditTemplates && (
          /* Existing template, view mode — Edit + Delete, right-aligned like Save/Save As */
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => setIsEditMode(true)} style={btnStyle(false)}>Edit</button>
            {!isBuiltIn && (
              <button
                onClick={() => { if (window.confirm(`Delete template "${template.name}"?`)) { onDelete(template.id); } }}
                style={{ ...btnStyle(false), color: '#b05050', borderColor: '#e0c0c0' }}
              >Delete</button>
            )}
          </div>
        )}
        </div>{/* end Row 2 (buttons) */}
      </div>

      {/* ── SOW drift notification (removed / renamed links + new unlinked deliverables) ── */}
      {(isEditMode || !template) && (
        <SowDriftBanner
          orphanedRows={visibleSowLinkDiff.orphanedRows}
          extras={visibleSowLinkDiff.extras}
          stageNameByKey={driftStageNames}
        />
      )}

      {/* ── Save As dialog ── */}
      {showSaveAsDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowSaveAsDialog(false)}>
          <div style={{ background: '#fff', padding: '24px', minWidth: '340px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Raleway, sans-serif' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '14px' }}>Save As New Template</div>
            <input
              autoFocus
              value={saveAsName}
              onChange={e => setSaveAsName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveAs(); if (e.key === 'Escape') setShowSaveAsDialog(false); }}
              placeholder="New template name…"
              style={{ ...inputStyle, width: '100%', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setShowSaveAsDialog(false)} style={btnStyle(false)}>Cancel</button>
              <button onClick={handleSaveAs} style={btnStyle(true)}>Save As</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview mode is toggled from the toolbar (Preview button, edit mode only); its cascaded dates
          feed the editor below via fakeProject. Nothing to render here. */}

      {/* ── Timeline editor / gantt (looks exactly like the project table & gantt views) ── */}
      {builderViewMode === 'gantt' ? (
        <TemplateGanttPanel
          project={fakeProject}
          canDrag={(!template || isEditMode) && (isAdmin || isSupervisor)}
          onBarDrag={handleTemplateBarDrag}
          onSwitchToTable={() => setBuilderViewMode('table')}
        />
      ) : (
      <TimelineEditorBoundary key={`tpl-${template?.id || 'new'}-${editorKey}-${isEditMode ? 'edit' : 'view'}`}>
        <ProjectTimelineEditor
          key={`tpl-${template?.id || 'new'}-${editorKey}-${isEditMode ? 'edit' : 'view'}`}
          project={fakeProject}
          stages={stages}
          supabaseProjects={proxySupabase}
          isAdmin={!template || isEditMode ? isAdmin : false}
          isSupervisor={!template || isEditMode ? isSupervisor : false}
          activeUsers={activeUsers}
          activeClients={activeClients}
          contractScopesHook={fakeContractScopesHook}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          showPresetSettings={showPresetSettings}
          setShowPresetSettings={setShowPresetSettings}
          viewMode="table"
          setViewMode={setBuilderViewMode}
          isFullscreen={false}
          toggleFullscreen={() => {}}
          zoomIndex={zoomIndex}
          setZoomIndex={setZoomIndex}
          pageZoomIndex={pageZoomIndex}
          setPageZoomIndex={setPageZoomIndex}
          onNavigateToTracking={() => {}}
          projectVersions={[]}
          compareModalTriggerRef={compareModalTriggerRef}
          pendingCompareVersionRef={pendingCompareVersionRef}
          reconcileErrorRowIds={reconcileErrorRowIds}
          visibleStageKeys={selectedStageKeys}
          linkSowTemplate={selectedSow}
          baseTimeline={baseTemplateId ? (timelineTemplates.find(t => t.id === baseTemplateId)?.stageTimeline || null) : null}
        />
      </TimelineEditorBoundary>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TimelinesPage({
  activeProjects,
  activeUsers,
  activeClients,
  activeTasks,
  allocations = [],
  activeTeams = [],
  activeTeamProjects = [],
  currentUser,
  supabaseProjects: supabaseProjectsRaw,
  contractScopesHook,
  scopeTemplatesHook,
  projectStatusFilter,
  onNavigateToTracking,
  invoicesHook = null,
}) {
  const { appSettings, updateSetting, loadHeavySetting, excelExportStyles, scopePdfStyles } = useSettings();

  // timeline_templates, contract_scopes and scope_templates are all left out of
  // the initial settings fetch because of their size — this page reads all
  // three, so pull them now.
  const ensureScopes = contractScopesHook?.ensureLoaded;
  const ensureTemplates = scopeTemplatesHook?.ensureLoaded;
  useEffect(() => {
    loadHeavySetting?.(TIMELINE_TEMPLATES_KEY);
    ensureScopes?.();
    ensureTemplates?.();
  }, [loadHeavySetting, ensureScopes, ensureTemplates]);
  // ── Timeline Template Builder state ──────────────────────────────────────
  const [templateBuilderMode, setTemplateBuilderMode] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = new; object = existing
  const [showCreateFromMenu, setShowCreateFromMenu] = useState(false); // "Create from" base-template picker
  const createFromMenuRef = useRef(null);
  useEffect(() => {
    if (!showCreateFromMenu) return;
    const onDown = (e) => { if (createFromMenuRef.current && !createFromMenuRef.current.contains(e.target)) setShowCreateFromMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showCreateFromMenu]);

  // User-saved timeline templates stored in app_settings under TIMELINE_TEMPLATES_KEY
  const savedTimelineTemplates = useMemo(() => {
    const raw = appSettings?.[TIMELINE_TEMPLATES_KEY];
    if (!raw) return [];
    try { return Array.isArray(raw) ? raw : JSON.parse(raw); } catch { return []; }
  }, [appSettings]);

  // Display list = built-in defaults (e.g. the residential master template) followed by saved
  // templates. A saved template with the same id shadows the built-in, so "Save As"-ing or
  // editing a built-in lets an admin override it without losing the original on a fresh install.
  const timelineTemplates = useMemo(() => {
    const savedIds = new Set(savedTimelineTemplates.map(t => t.id));
    const builtIns = DEFAULT_TIMELINE_TEMPLATES.filter(t => !savedIds.has(t.id));
    return [...builtIns, ...savedTimelineTemplates];
  }, [savedTimelineTemplates]);

  // Template ids the admin has hidden from the projects' "apply template" dropdown via the eye toggle in
  // the Template Builder. Stored as a plain id list in app_settings so it works for built-in and saved
  // templates alike, without duplicating a built-in into a saved override.
  const hiddenTemplateIds = useMemo(() => {
    const raw = appSettings?.[TIMELINE_TEMPLATES_HIDDEN_KEY];
    let arr = raw;
    if (typeof raw === 'string') { try { arr = JSON.parse(raw); } catch { arr = []; } }
    return new Set(Array.isArray(arr) ? arr : []);
  }, [appSettings]);

  // Templates offered for APPLYING to a project — everything except the ones hidden via the eye toggle.
  // Hidden templates stay fully available in the Template Builder's own picker / "Create from" list.
  const applyableTemplates = useMemo(
    () => timelineTemplates.filter(t => !hiddenTemplateIds.has(t.id)),
    [timelineTemplates, hiddenTemplateIds]
  );

  // Toggle whether a template appears in the projects' apply dropdown (the eye icon).
  const toggleTemplateHidden = useCallback(async (templateId) => {
    if (!templateId) return;
    const next = new Set(hiddenTemplateIds);
    if (next.has(templateId)) next.delete(templateId); else next.add(templateId);
    const res = await updateSetting(TIMELINE_TEMPLATES_HIDDEN_KEY, Array.from(next));
    if (res && res.success === false) alert('Could not update template visibility: ' + (res.error || 'permission denied. Timeline templates can only be changed by an administrator.'));
  }, [hiddenTemplateIds, updateSetting]);

  // Guard against the async-load race: if a saved DB override for the currently-selected BUILT-IN
  // arrives after selection (savedTimelineTemplates load after the builder opened), switch the editor
  // to that override. Otherwise the builder keeps editing the pristine code default, and its auto-save
  // could overwrite the richer DB override — the data-loss bug this replaces.
  useEffect(() => {
    if (!editingTemplate?.builtIn) return;
    const override = savedTimelineTemplates.find(t => t.id === editingTemplate.id);
    if (override) setEditingTemplate(override);
  }, [savedTimelineTemplates, editingTemplate]);

  const saveTimelineTemplates = useCallback((updated) => {
    // Returns updateSetting's { success, error } so callers can surface persistence failures.
    return updateSetting(TIMELINE_TEMPLATES_KEY, updated);
  }, [updateSetting]);

  // Drag-reorder the Saved-Templates chips. Only SAVED templates carry a stored order (built-ins are
  // code-ordered and re-prepended on load), so we reorder the display list, then persist the saved
  // templates in their new relative order.
  const draggedTplIdRef = useRef(null);
  const reorderTemplates = useCallback(async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const list = [...timelineTemplates];
    const fromIdx = list.findIndex(t => t.id === fromId);
    const toIdx = list.findIndex(t => t.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    const newSaved = list.filter(t => !t.builtIn);
    const res = await saveTimelineTemplates(newSaved);
    if (res && res.success === false) alert('Could not reorder templates: ' + (res.error || 'permission denied.'));
  }, [timelineTemplates, saveTimelineTemplates]);

  const handleCreateTemplate = useCallback(async (name, stageTimeline, sowTemplateId, selectedStageKeys, baseTemplateId, info) => {
    const newTpl = { id: `tl-tpl-${Date.now()}`, name, info: info || null, sowTemplateId: sowTemplateId || null, stageTimeline, selectedStageKeys: selectedStageKeys || null, baseTemplateId: baseTemplateId || null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    const res = await saveTimelineTemplates([...savedTimelineTemplates, newTpl]);
    if (res && res.success === false) { alert('Could not save template: ' + (res.error || 'permission denied. Timeline templates can only be changed by an administrator.')); return; }
    setEditingTemplate(newTpl);
  }, [savedTimelineTemplates, saveTimelineTemplates]);

  const handleSaveTemplate = useCallback(async (updatedTpl) => {
    // Persist a clean copy (drop the built-in marker) into app_settings. Editing a built-in that
    // isn't persisted yet appends a same-id override rather than silently no-op'ing.
    const { builtIn, ...clean } = updatedTpl; // eslint-disable-line no-unused-vars
    const exists = savedTimelineTemplates.some(t => t.id === clean.id);
    let next = exists
      ? savedTimelineTemplates.map(t => (t.id === clean.id ? clean : t))
      : [...savedTimelineTemplates, clean];
    // "Create from" inheritance: fully re-sync every template created from this one
    // (baseTemplateId === clean.id) — order, indent, dependencies, durations, assignees, names and
    // SOW links all flow from the base; each derivative's own local rows are preserved.
    next = propagateBaseToDerived(clean, next, new Date().toISOString());
    const res = await saveTimelineTemplates(next);
    if (res && res.success === false) { alert('Could not save template: ' + (res.error || 'permission denied. Timeline templates can only be changed by an administrator.')); return; }
    setEditingTemplate(clean);
  }, [savedTimelineTemplates, saveTimelineTemplates]);

  // "Create from" — make a new template seeded from a base template (a full copy of its structure,
  // dates and links) and remember the base as baseTemplateId so future base edits keep flowing in.
  const handleCreateFromTemplate = useCallback(async (baseTpl) => {
    if (!baseTpl) return;
    const copyTl = JSON.parse(JSON.stringify(baseTpl.stageTimeline || {}));
    // Capture the base snapshot (the common ancestor) so the very first base edit merges correctly —
    // untouched tasks take the base change, tasks the user edits in this derivative are preserved.
    copyTl._baseSnapshot = buildBaseSnapshot(baseTpl.stageTimeline || {});
    const now = new Date().toISOString();
    const newTpl = {
      id: `tl-tpl-${Date.now()}`,
      name: `${baseTpl.name || 'Template'} (from base)`,
      sowTemplateId: baseTpl.sowTemplateId || null,
      stageTimeline: copyTl,
      selectedStageKeys: Array.isArray(baseTpl.selectedStageKeys) ? [...baseTpl.selectedStageKeys] : null,
      baseTemplateId: baseTpl.id,
      createdAt: now,
      updatedAt: now,
    };
    const res = await saveTimelineTemplates([...savedTimelineTemplates, newTpl]);
    if (res && res.success === false) { alert('Could not create template: ' + (res.error || 'permission denied. Timeline templates can only be changed by an administrator.')); return; }
    setEditingTemplate(newTpl);
  }, [savedTimelineTemplates, saveTimelineTemplates]);

  const handleDeleteTemplate = useCallback(async (id) => {
    // Only persisted templates can be deleted. Deleting a saved override of a built-in restores
    // the built-in; pure built-ins have no persisted row and are left in place. Surface a rejected
    // write (e.g. RLS: app_settings.timeline_templates is admin-writable only) instead of silently
    // no-op'ing, and only drop the editor selection once the delete actually persists.
    const res = await saveTimelineTemplates(savedTimelineTemplates.filter(t => t.id !== id));
    if (res && res.success === false) { alert('Could not delete template: ' + (res.error || 'permission denied. Timeline templates can only be changed by an administrator.')); return; }
    if (editingTemplate?.id === id) setEditingTemplate(null);
  }, [savedTimelineTemplates, saveTimelineTemplates, editingTemplate]);
  // ─────────────────────────────────────────────────────────────────────────

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [ganttCollapsedStages, setGanttCollapsedStages] = useState(new Set()); // collapsed stage IDs in per-project gantt
  // Tab (stage-filter view) selection — lifted here so the table editor AND the gantt view share
  // one active tab. Both read/write these; initialised from the selected project's saved view
  // settings by the effect below. Persisted from the table editor's debounced view-settings save,
  // and from the gantt via persistTabViewSetting (defined after selectedProject).
  const [showTabBar, setShowTabBar] = useState(false);
  const [activeTabId, setActiveTabId] = useState('__all__');
  const [ganttDateFilter, setGanttDateFilter] = useState('both'); // 'both' | 'target' | 'actual'
  const [showGanttDateFilterMenu, setShowGanttDateFilterMenu] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(2); // default: Quarter (for per-project gantt pxPerDay)
  const [ganttZoomIndex, setGanttZoomIndex] = useState(() => loadGanttZoom(LS_GANTT_ZOOM_KEY)); // per-project gantt (persisted)
  const [multiGanttZoomIndex, setMultiGanttZoomIndex] = useState(() => loadGanttZoom(LS_MULTI_GANTT_ZOOM_KEY)); // multi-project gantt (persisted)
  // Persist gantt zoom across refreshes.
  useEffect(() => { try { localStorage.setItem(LS_GANTT_ZOOM_KEY, String(ganttZoomIndex)); } catch { /* ignore */ } }, [ganttZoomIndex]);
  useEffect(() => { try { localStorage.setItem(LS_MULTI_GANTT_ZOOM_KEY, String(multiGanttZoomIndex)); } catch { /* ignore */ } }, [multiGanttZoomIndex]);
  const [pageZoomIndex, setPageZoomIndex] = useState(4); // default: 100%
  const [ganttContainerWidth, setGanttContainerWidth] = useState(0); // measured scroll container width
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    try { return localStorage.getItem(LS_PROJECT_KEY) || null; } catch { return null; }
  });
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_VIEW_KEY);
      if (saved) return saved;
      return 'gantt';
    } catch { return 'gantt'; }
  });
  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    try { localStorage.setItem(LS_VIEW_KEY, mode); } catch { /* ignore */ }
  }, []);
  // Persist project selection across navigations and refreshes
  const selectProject = useCallback((id) => {
    setSelectedProjectId(id);
    try { if (id) localStorage.setItem(LS_PROJECT_KEY, id); else localStorage.removeItem(LS_PROJECT_KEY); } catch { /* ignore */ }
  }, []);
  // Persist the per-project gantt stage-collapse state so a minimised stage stays collapsed across
  // refreshes. Stored as { [projectId]: [rowId, …] } under one localStorage key. loadedCollapseProjectRef
  // tracks which project the live `ganttCollapsedStages` belongs to, so the save effect never writes one
  // project's set under another's id during the load-on-switch transition.
  const loadedCollapseProjectRef = useRef(null);
  useEffect(() => {
    const pid = selectedProjectId;
    loadedCollapseProjectRef.current = pid;
    if (!pid) { setGanttCollapsedStages(new Set()); return; }
    try {
      const raw = localStorage.getItem(LS_GANTT_COLLAPSED_KEY);
      const map = raw ? JSON.parse(raw) : {};
      const arr = Array.isArray(map[pid]) ? map[pid] : [];
      setGanttCollapsedStages(new Set(arr));
    } catch { setGanttCollapsedStages(new Set()); }
  }, [selectedProjectId]);
  useEffect(() => {
    const pid = loadedCollapseProjectRef.current;
    if (!pid) return;
    try {
      const raw = localStorage.getItem(LS_GANTT_COLLAPSED_KEY);
      const map = raw ? JSON.parse(raw) : {};
      if (ganttCollapsedStages.size === 0) delete map[pid];
      else map[pid] = Array.from(ganttCollapsedStages);
      localStorage.setItem(LS_GANTT_COLLAPSED_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
  }, [ganttCollapsedStages]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPresetSettings, setShowPresetSettings] = useState(false);
  const [showGanttSettingsMenu, setShowGanttSettingsMenu] = useState(false);
  const [showDeleteTimelineConfirm, setShowDeleteTimelineConfirm] = useState(false);
  const [showGanttGiveAccess, setShowGanttGiveAccess] = useState(false);
  const [infoTipHovered, setInfoTipHovered] = useState(false);
  const [infoTipLocked, setInfoTipLocked] = useState(false);
  const infoTipRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pageContainerRef = useRef(null);
  const ganttImportFileRef = useRef(null);
  const multiExportMenuRef = useRef(null);
  const [showMultiExportMenu, setShowMultiExportMenu] = useState(false);

  // ── Timeline versioning (DB-backed, visible to all users) ──────────────────
  const timelineVersionsHook = useTimelineVersions();
  const [versionContextMenu, setVersionContextMenu] = useState(null); // { versionId, x, y }
  const [viewingVersion, setViewingVersion] = useState(null); // version object for preview modal
  const versionScrollRef = useRef(null);
  const [versionScrollState, setVersionScrollState] = useState({ canLeft: false, canRight: false });
  const compareModalTriggerRef = useRef(null); // set by ProjectTimelineEditor; called by eye icon
  const pendingCompareVersionRef = useRef(null); // used to pass version ID across mount/unmount cycle


  const updateVersionScroll = useCallback(() => {
    const el = versionScrollRef.current;
    if (!el) return;
    setVersionScrollState({ canLeft: el.scrollLeft > 0, canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
  }, []);

  const scrollVersions = useCallback((dir) => {
    const el = versionScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 120, behavior: 'smooth' });
  }, []);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
  const isSupervisor = currentUser?.role === 'supervisor';
  const isManager = isAdmin || isSupervisor;

  // Who may author timeline templates: admins plus a named allow-list, decided by
  // public.can_edit_timeline_templates() (the same function RLS on app_settings reads). Everyone
  // else gets the Template Builder read only. Until the RPC answers, or if it fails, fall back to
  // admins only so a non-editor never sees write controls the database would refuse.
  const [canEditTemplatesRpc, setCanEditTemplatesRpc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc('can_edit_timeline_templates').then(({ data, error }) => {
      if (!cancelled) setCanEditTemplatesRpc(error ? null : data === true);
    });
    return () => { cancelled = true; };
  }, [currentUser?.id]);
  const canEditTemplates = canEditTemplatesRpc === null ? isAdmin : canEditTemplatesRpc;

  // ── BRM templates (see BRM_TEMPLATES_KEY) ─────────────────────────────────────────────────────
  // Who may write them: can_edit_brm_templates(), read by RPC; until it answers, admins and supervisors.
  const [canEditBrmTemplatesRpc, setCanEditBrmTemplatesRpc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc('can_edit_brm_templates').then(({ data, error }) => {
      if (!cancelled) setCanEditBrmTemplatesRpc(error ? null : data === true);
    });
    return () => { cancelled = true; };
  }, [currentUser?.id]);
  const canEditBrmTemplates = canEditBrmTemplatesRpc === null ? (isAdmin || isSupervisor) : canEditBrmTemplatesRpc;
  const brmTemplates = useMemo(() => {
    const raw = appSettings?.[BRM_TEMPLATES_KEY];
    let arr = raw;
    if (typeof raw === 'string') { try { arr = JSON.parse(raw); } catch { arr = []; } }
    return Array.isArray(arr) ? arr : [];
  }, [appSettings]);
  // The open builder: { key, template } (template null = a new one), or null.
  const [brmBuilder, setBrmBuilder] = useState(null);
  const [showBrmTemplateMenu, setShowBrmTemplateMenu] = useState(false);
  const brmTemplateMenuRef = useRef(null);
  useEffect(() => {
    if (!showBrmTemplateMenu) return undefined;
    const onDown = (e) => { if (brmTemplateMenuRef.current && !brmTemplateMenuRef.current.contains(e.target)) setShowBrmTemplateMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showBrmTemplateMenu]);
  const saveBrmTemplate = useCallback(async (tpl) => {
    const exists = brmTemplates.some(t => t.id === tpl.id);
    const next = exists ? brmTemplates.map(t => (t.id === tpl.id ? tpl : t)) : [...brmTemplates, tpl];
    const res = await updateSetting(BRM_TEMPLATES_KEY, next);
    if (!res || res.success !== false) setBrmBuilder(prev => (prev ? { ...prev, template: tpl } : prev));
    return res;
  }, [brmTemplates, updateSetting]);
  const deleteBrmTemplate = useCallback(async (id) => {
    const res = await updateSetting(BRM_TEMPLATES_KEY, brmTemplates.filter(t => t.id !== id));
    if (res && res.success === false) { alert('Could not delete the template: ' + (res.error || 'permission denied.')); return; }
    setBrmBuilder(null);
  }, [brmTemplates, updateSetting]);

  // Who may give timeline access on every project, assigned or not: admins plus a named allow-list,
  // decided by public.can_grant_timeline_access() (the same function save_project_timeline_grants
  // reads). Falls back to admins only until the RPC answers or if it fails.
  const [canGrantAccessRpc, setCanGrantAccessRpc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc('can_grant_timeline_access').then(({ data, error }) => {
      if (!cancelled) setCanGrantAccessRpc(error ? null : data === true);
    });
    return () => { cancelled = true; };
  }, [currentUser?.id]);
  const canGrantTimelineAccess = canGrantAccessRpc === null ? isAdmin : canGrantAccessRpc;

  // Projects the current user is assigned to via the Team Allocation list — these
  // give 'read' access by default (unless an explicit grant overrides).
  const myAssignedProjectIds = useMemo(() => {
    const uid = Number(currentUser?.id);
    const set = new Set();
    (allocations || []).forEach(a => {
      if (Number(a.userId) === uid && (a.allocatedHours || 0) > 0) set.add(Number(a.projectId));
    });
    return set;
  }, [allocations, currentUser]);

  // Whoever has effective 'full' access on the SELECTED project can edit the gantt —
  // bar dragging + dependencies — same rule as the table editor. Admin → full
  // everywhere; supervisor → full on projects they're assigned to (project.supervisors)
  // or where an admin granted full, read-only elsewhere; regular user → their grant,
  // else read when allocated. (Gantt edits only update the timeline, never Tracking.)
  const myRole = isAdmin ? 'admin' : (isSupervisor ? 'supervisor' : 'user');
  const ganttGrantProject = (activeProjects || []).find(p => Number(p.id) === Number(selectedProjectId));
  const selIsAssigned = isSupervisor
    ? supervisorIdsForProject(ganttGrantProject, activeTeams, activeTeamProjects).has(Number(currentUser?.id))
    : myAssignedProjectIds.has(Number(selectedProjectId));
  // A regular user granted Full access edits tasks in the table only (see isGrantedEditor in
  // ProjectTimelineEditor); the Gantt's bar drags write stage and task dates directly, so it stays
  // read-only for them.
  const selLevel = effectiveTimelineAccess(ganttGrantProject?.stageTimeline?._grantedAccess, currentUser?.id, myRole, selIsAssigned);
  const canEdit = (isAdmin || isSupervisor) && selLevel === 'full';
  // Who saves the selected project's timeline through save_granted_project_timeline: a regular user
  // with Full, or a supervisor with Full on a project they do not supervise (savesViaGrant in the
  // editor). RLS refuses their plain UPDATE, so every page level timeline write (template apply,
  // Make current, their undo / redo) goes through the grant for them. They may apply templates and
  // make a version current; the database still keeps Tracking fields and the access map as stored.
  const selSavesViaGrant = !isAdmin && selLevel === 'full' && !(isSupervisor && selIsAssigned);
  const canManageTimeline = canEdit || selSavesViaGrant;
  const supabaseProjects = useMemo(() => {
    if (!selSavesViaGrant || !supabaseProjectsRaw) return supabaseProjectsRaw;
    return {
      ...supabaseProjectsRaw,
      updateProjectTimelineDiff: (id, base, next, opts = {}) =>
        supabaseProjectsRaw.updateProjectTimelineDiff(id, base, next, { ...opts, granted: true }),
    };
  }, [selSavesViaGrant, supabaseProjectsRaw]);

  // Least-privilege project scoping: admins and supervisors see every project (managers
  // are never hidden — a supervisor is at least read-only everywhere). Only regular users
  // are scoped, to projects they can access: assigned (default read) or granted, minus any
  // explicit 'No access'. null = no restriction.
  const accessibleProjectIdSet = useMemo(() => {
    if (isManager) return null;
    const set = new Set();
    (activeProjects || []).forEach(p => {
      const lvl = effectiveTimelineAccess(p.stageTimeline?._grantedAccess, currentUser?.id, 'user', myAssignedProjectIds.has(Number(p.id)));
      if (lvl !== 'none') set.add(Number(p.id));
    });
    return set;
  }, [isManager, activeProjects, currentUser, myAssignedProjectIds]);

  // Build sorted stage list from activeTasks (matches TimelinePage pattern)
  const sortedStages = useMemo(() => {
    if (!activeTasks || activeTasks.length === 0) {
      return Object.entries(DEFAULT_ID_TO_KEY).map(([id, key]) => ({
        key,
        name: STAGE_NAMES[key] || key.replace(/_/g, ' ').toUpperCase(),
        id: Number(id),
      }));
    }
    return [...activeTasks]
      .sort((a, b) => {
        const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
        const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
        return oa - ob;
      })
      .map(t => ({
        key: DEFAULT_ID_TO_KEY[t.id] || `custom_${t.id}`,
        name: t.name,
        id: t.id,
      }));
  }, [activeTasks]);

  // Selected project object
  const selectedProject = useMemo(() => {
    if (!selectedProjectId || !activeProjects) return null;
    const p = activeProjects.find(pr => Number(pr.id) === Number(selectedProjectId)) || null;
    // Non-managers may only resolve a project they can access (least privilege).
    if (p && accessibleProjectIdSet && !accessibleProjectIdSet.has(Number(p.id))) return null;
    return p;
  }, [selectedProjectId, activeProjects, accessibleProjectIdSet]);

  // A supervisor assigned to the selected project (in project.supervisors) may
  // give access on it; admins may on any project (see the give-access buttons).
  const ganttIsAssignedSupervisor = isSupervisor && selectedProject
    && (Array.isArray(selectedProject.supervisors) ? selectedProject.supervisors : []).some(id => Number(id) === Number(currentUser?.id));

  // Initialise the shared tab selection from the project's saved view settings. Guarded by a ref on
  // the project id so it runs when the project first loads / changes, but does NOT clobber a live
  // selection on subsequent timeline refreshes (which give a new selectedProject object, same id).
  const tabInitRef = useRef(null);
  useEffect(() => {
    if (!selectedProject) { tabInitRef.current = null; return; }
    if (tabInitRef.current === selectedProject.id) return;
    tabInitRef.current = selectedProject.id;
    const vs = selectedProject.stageTimeline?._viewSettings || {};
    setActiveTabId(vs.activeTabId || '__all__');
    setShowTabBar(!!vs.showTabBar);
  }, [selectedProject]);

  // Tabs defined on the selected project, and the currently active one ('__all__' → null = show all).
  const projectTabs = useMemo(
    () => (Array.isArray(selectedProject?.stageTimeline?._tabs) ? selectedProject.stageTimeline._tabs : []),
    [selectedProject]
  );
  const activeTab = useMemo(
    () => (activeTabId === '__all__' ? null : projectTabs.find(t => t.id === activeTabId) || null),
    [projectTabs, activeTabId]
  );

  // Persist a tab view-setting change made from the gantt view, where the table editor — which owns
  // the debounced view-settings save — is unmounted. Merges into _viewSettings so the editor's other
  // saved settings (column widths, collapse state, etc.) survive.
  const persistTabViewSetting = useCallback((patch) => {
    if (!selectedProject || !supabaseProjects) return;
    const tl = selectedProject.stageTimeline || {};
    const vs = { ...(tl._viewSettings || {}), ...patch };
    writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, { ...tl, _viewSettings: vs });
  }, [selectedProject, supabaseProjects]);

  // Fetch versions and close any open preview whenever the selected project changes
  useEffect(() => {
    timelineVersionsHook.fetchVersions(selectedProjectId || null);
    setViewingVersion(null);
  }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convenience aliases for the DB-backed version list (already sorted + labelled by hook)
  const projectVersions = timelineVersionsHook.versions;

  // When versions load/change: scroll to the rightmost (latest) position, then update arrow state
  useEffect(() => {
    const el = versionScrollRef.current;
    if (!el) { setTimeout(updateVersionScroll, 50); return; }
    // Defer until the DOM has painted the new buttons
    setTimeout(() => {
      el.scrollLeft = el.scrollWidth;
      updateVersionScroll();
    }, 50);
  }, [projectVersions, updateVersionScroll]);

  // Info tooltip: close locked state on click-outside or Escape
  useEffect(() => {
    if (!infoTipLocked) return;
    const onDown = (e) => { if (infoTipRef.current && !infoTipRef.current.contains(e.target)) setInfoTipLocked(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setInfoTipLocked(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [infoTipLocked]);

  // All projects sorted by project code for dropdown — filtered by main page status filter
  // and (for non-managers) scoped to the projects they've been granted.
  const sortedProjects = useMemo(() => {
    if (!activeProjects) return [];
    let filtered = activeProjects.filter(p => !accessibleProjectIdSet || accessibleProjectIdSet.has(Number(p.id)));
    if (projectStatusFilter && projectStatusFilter !== 'default' && projectStatusFilter !== 'all') {
      filtered = filtered.filter(p => (p.status || 'active') === projectStatusFilter);
    }
    return filtered.sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  }, [activeProjects, projectStatusFilter, accessibleProjectIdSet]);

  // Filter projects by status (and, for non-managers, by granted access).
  const filteredProjects = useMemo(() => {
    if (!activeProjects) return [];
    const scoped = activeProjects.filter(p => !accessibleProjectIdSet || accessibleProjectIdSet.has(Number(p.id)));
    if (statusFilter === 'all') return scoped;
    return scoped.filter(p => (p.status || 'active') === statusFilter);
  }, [activeProjects, statusFilter, accessibleProjectIdSet]);

  // Non-managers: only ever clear a stale/inaccessible selection — never auto-pick
  // a project. When nothing is selected they land on the "select a project" prompt
  // (they must not see the multi-project overview table), and a persisted selection
  // they can no longer access is dropped rather than silently shown.
  useEffect(() => {
    if (!accessibleProjectIdSet) return; // managers: unrestricted
    if (!selectedProjectId) return; // no selection → show the "select a project" prompt
    if (accessibleProjectIdSet.has(Number(selectedProjectId))) return; // still accessible
    selectProject(null); // stale/inaccessible → clear
  }, [accessibleProjectIdSet, selectedProjectId, selectProject]);

  // Build row data
  const projectRows = useMemo(() => buildProjectRows(filteredProjects), [filteredProjects]);

  // When a project is selected, show only that project's rows in the gantt
  const ganttDisplayRows = useMemo(() => {
    // Non-managers never see the multi-project overview — they must pick a project.
    if (!selectedProject) return isManager ? projectRows : [];
    const existing = projectRows.find(p => Number(p.id) === Number(selectedProject.id));
    if (existing) return [existing];
    // Project may have a different status than the gantt filter — build fresh
    return buildProjectRows([selectedProject]);
  }, [selectedProject, projectRows, isManager]);

  // confirmedScope for the selected project (same lookup as ProjectTimelineEditor)
  const perProjectConfirmedScope = useMemo(() => {
    if (!contractScopesHook || !selectedProject) return null;
    const scopes = contractScopesHook.scopes || [];
    const pid = String(selectedProject.id);
    return scopes.find(s => String(s.projectId) === pid && s.status === 'confirmed' && s.isActiveVersion) || null;
  }, [contractScopesHook, selectedProject]);

  // The project's own confirmed scope, shaped like a SOW template so the Link-to-SOW modal (and its
  // deliverable checklist) works in the project view exactly as in the Template Builder. Deliverables
  // are numbered as in the scope: included standard, then included optional, then custom.
  const projectSowTemplate = useMemo(() => {
    if (!perProjectConfirmedScope) return null;
    const stages = (perProjectConfirmedScope.stages || []).filter(s => s.selected).map(s => {
      const dv = s.deliverables || {};
      const delivs = [
        ...((dv.standard || []).filter(x => x.included)),
        ...((dv.optional || []).filter(x => x.included)),
        ...(dv.custom || []),
      ].map(x => ({ id: x.id, label: x.label || '' }));
      return { key: s.key, name: s.name, standardDeliverables: delivs, introduction: s.introduction || s.description || '' };
    });
    return { id: 'project-scope', name: 'Project SOW', stages };
  }, [perProjectConfirmedScope]);

  // ── Apply-template state (must come after perProjectConfirmedScope) ───
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [applyTemplateConfirm, setApplyTemplateConfirm] = useState(null); // { template } | null
  // Template opened read only from the eye icon in the apply dropdown (null = closed).
  const [previewTemplate, setPreviewTemplate] = useState(null);
  useEffect(() => {
    if (!previewTemplate) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewTemplate(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewTemplate]);
  // Stage picker for the apply dialog. null = apply to ALL of the project's SOW-selected stages (the
  // default); a Set of timeline keys = apply ONLY to those stages, leaving the rest untouched.
  const [applyPickedStages, setApplyPickedStages] = useState(null);
  const applyStageOptions = useMemo(() => {
    const stages = perProjectConfirmedScope?.stages || [];
    return stages.filter(s => s.selected).map(s => {
      const tk = scopeKeyToTimelineKey(s.key);
      return { key: tk, name: s.name || STAGE_NAMES[tk] || s.key };
    });
  }, [perProjectConfirmedScope]);
  const [applyResult, setApplyResult] = useState(null); // { projectId, templateName, addedCount, prevTimeline } — apply summary
  const templateUndoGroupRef = useRef(null); // { until, captured } — one undo step per template apply
  const templateMenuRef = useRef(null);

  useEffect(() => {
    if (!showTemplateMenu) return;
    const onDown = (e) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target)) setShowTemplateMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showTemplateMenu]);

  const handleApplyTemplate = useCallback(async (template, preserveTasks = false, pickedStages = null) => {
    if (!selectedProject || !supabaseProjects) return;
    if (isStandaloneTimeline(selectedProject)) return;
    // The editor folds this apply and its follow-on writes into one undo step (see its capture effect).
    templateUndoGroupRef.current = { until: Date.now() + 5000, captured: false };
    const currentTimeline = selectedProject.stageTimeline || {};
    // Apply the template as a 3-way merge against the project's own confirmed scope: keep the template
    // structure, flag template deliverables this project's SOW lacks (_sowUnlinked), and append the
    // project-SOW deliverables the template doesn't cover. Tracking/invoicing data and layout are
    // preserved on matching rows inside the helper. The template's own base SOW is passed as the anchor
    // so legacy explicit links (no label fingerprint) still resolve.
    const anchorSow = (template.sowTemplateId && scopeTemplatesHook?.templates)
      ? scopeTemplatesHook.templates.find(t => t.id === template.sowTemplateId) || null
      : null;
    // pickedStages (a Set of timeline keys) restricts the apply to just those stages, leaving the rest
    // of the project's timeline untouched; null applies to every SOW-selected stage (the default).
    const { timeline: newTimeline, addedCount, addedRows } =
      applyTemplateToSelectedStages(template, currentTimeline, perProjectConfirmedScope, pickedStages, anchorSow, preserveTasks);
    await writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, newTimeline);
    setApplyTemplateConfirm(null);
    setApplyPickedStages(null);
    setShowTemplateMenu(false);
    // Surface a post-apply summary (with undo) when the project's SOW added deliverables the template
    // didn't cover (each appears with a "new" badge). addedRows drives the per-deliverable list + the
    // ✕ that removes an individual added task; appliedTimeline is the working timeline those ✕ removals
    // are applied against.
    if (addedCount > 0) {
      // template / preserveTasks / anchorSow / excludedIds let the ✕ re-run the same apply against
      // prevTimeline with that deliverable excluded — so the removal is renumbered correctly, not spliced.
      setApplyResult({ projectId: selectedProject.id, templateName: template.name, addedCount, addedRows, appliedTimeline: newTimeline, prevTimeline: currentTimeline, template, preserveTasks, anchorSow, excludedIds: [], pickedStages: pickedStages ? Array.from(pickedStages) : null });
    }
  }, [selectedProject, supabaseProjects, perProjectConfirmedScope, scopeTemplatesHook]);

  // Revert from the timeline as it is now, so the follow-on writes the apply set off (dependency cascade,
  // invoicing / closure rows) are reverted too and the table lands exactly on its pre-apply state.
  const undoApplyTemplate = useCallback(async () => {
    if (!applyResult || !supabaseProjects) return;
    const current = String(selectedProject?.id) === String(applyResult.projectId)
      ? (selectedProject.stageTimeline || applyResult.appliedTimeline)
      : applyResult.appliedTimeline;
    await writeTimelineDiff(supabaseProjects, applyResult.projectId, current, applyResult.prevTimeline);
    setApplyResult(null);
  }, [applyResult, supabaseProjects, selectedProject]);

  // Remove ONE added deliverable from the apply summary's list (its ✕). Re-runs the same apply against
  // the pre-apply timeline with that deliverable (and any previously removed ones) excluded, so it's
  // never added as a task and the rest of the timeline is renumbered correctly. Undo still restores the
  // original pre-apply timeline. When the last added deliverable is removed, the summary closes.
  const removeAddedDeliverable = useCallback(async (rowId) => {
    if (!applyResult || !supabaseProjects) return;
    const nextExcluded = Array.from(new Set([...(applyResult.excludedIds || []), rowId]));
    const { timeline: newTimeline, addedRows } = applyTemplateToSelectedStages(
      applyResult.template, applyResult.prevTimeline, perProjectConfirmedScope,
      applyResult.pickedStages ? new Set(applyResult.pickedStages) : null,
      applyResult.anchorSow, applyResult.preserveTasks, new Set(nextExcluded)
    );
    await writeTimelineDiff(supabaseProjects, applyResult.projectId, applyResult.appliedTimeline, newTimeline);
    if (!addedRows || addedRows.length === 0) { setApplyResult(null); return; }
    setApplyResult(prev => (prev ? { ...prev, addedRows, addedCount: addedRows.length, appliedTimeline: newTimeline, excludedIds: nextExcluded } : prev));
  }, [applyResult, supabaseProjects, perProjectConfirmedScope]);
  // ─────────────────────────────────────────────────────────────────────

  // ── Version op undo/redo (delete version + make current) ──────────────
  // Deleting a version or making one current are undoable with Ctrl/Cmd+Z (redo: Ctrl/Cmd+Y). Each op
  // records inverse operations. This stack is separate from the table editor's and the gantt's own undo
  // stacks; a capture-phase key handler (below) claims Ctrl+Z/Y ONLY while a version op is the most
  // recent action (`versionOpLatestRef`), otherwise it yields to those handlers. Any genuine table/gantt
  // edit (a non-version change to stageTimeline) clears the flag so their undo takes over again.
  const versionUndoRef = useRef([]);
  const versionRedoRef = useRef([]);
  const versionOpLatestRef = useRef(false);
  const versionSelfWriteRef = useRef(false); // guards our own make-current writes from clearing the flag
  const versionPrevTimelineRef = useRef(selectedProject?.stageTimeline);
  useEffect(() => {
    versionUndoRef.current = []; versionRedoRef.current = []; versionOpLatestRef.current = false;
  }, [selectedProjectId]);
  useEffect(() => {
    const cur = selectedProject?.stageTimeline;
    if (cur === versionPrevTimelineRef.current) return;
    versionPrevTimelineRef.current = cur;
    if (versionSelfWriteRef.current) { versionSelfWriteRef.current = false; return; } // our own make-current write
    versionOpLatestRef.current = false; // a real table/gantt edit — hand Ctrl+Z back to their stacks
  }, [selectedProject?.stageTimeline]);

  const doVersionUndo = useCallback(() => {
    const entry = versionUndoRef.current.pop();
    if (!entry) return;
    versionRedoRef.current.push(entry);
    versionOpLatestRef.current = true;
    Promise.resolve(entry.undo()).catch(() => {});
  }, []);
  const doVersionRedo = useCallback(() => {
    const entry = versionRedoRef.current.pop();
    if (!entry) return;
    versionUndoRef.current.push(entry);
    versionOpLatestRef.current = true;
    Promise.resolve(entry.redo()).catch(() => {});
  }, []);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y (or Shift+Ctrl+Z) = redo — capture phase so it runs before the
  // table/gantt handlers, and only intercepts (stopImmediatePropagation) when a version op is the
  // latest action AND there is something to undo/redo; otherwise the event flows through untouched.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !versionOpLatestRef.current) return;
      const t = e.target; const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      const isUndo = k === 'z' && !e.shiftKey;
      const isRedo = k === 'y' || (k === 'z' && e.shiftKey);
      if (isUndo && versionUndoRef.current.length) { e.preventDefault(); e.stopImmediatePropagation(); doVersionUndo(); }
      else if (isRedo && versionRedoRef.current.length) { e.preventDefault(); e.stopImmediatePropagation(); doVersionRedo(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [doVersionUndo, doVersionRedo]);

  const saveVersion = useCallback(async () => {
    if (!selectedProject) return;
    const timeline = selectedProject.stageTimeline || {};
    const snapshotTimeline = JSON.parse(JSON.stringify(timeline));
    // Materialize _rows if missing so the preview has something to show
    if (!Array.isArray(snapshotTimeline._rows) || snapshotTimeline._rows.length === 0) {
      if (perProjectConfirmedScope) {
        snapshotTimeline._rows = ensureRowOrder(buildRowsFromScope(perProjectConfirmedScope));
      } else {
        const stageKeys = Object.keys(snapshotTimeline).filter(k => !k.startsWith('_') && STAGE_NAMES[k]);
        if (stageKeys.length > 0) {
          snapshotTimeline._rows = stageKeys.map(k => ({ id: k, indent: 0 }));
        }
      }
    }
    await timelineVersionsHook.saveVersion({
      projectId: selectedProject.id,
      projectName: selectedProject.name || selectedProject.projectCode || '',
      snapshot: snapshotTimeline,
    });
  }, [selectedProject, timelineVersionsHook, perProjectConfirmedScope]);

  const deleteVersion = useCallback(async (versionId) => {
    const version = projectVersions.find(v => String(v.id) === String(versionId));
    const projId = selectedProjectId;
    await timelineVersionsHook.deleteVersion(versionId, projId);
    setVersionContextMenu(null);
    if (viewingVersion && viewingVersion.id === String(versionId)) setViewingVersion(null);
    // Undo restores the deleted snapshot as a fresh version (new id, tracked for redo).
    if (version?.snapshot) {
      let vid = String(versionId);
      const projectName = version.projectName || selectedProject?.name || selectedProject?.projectCode || '';
      versionUndoRef.current.push({
        undo: async () => {
          const res = await timelineVersionsHook.saveVersion({ projectId: projId, projectName, snapshot: version.snapshot });
          if (res?.id != null) vid = String(res.id);
        },
        redo: async () => { await timelineVersionsHook.deleteVersion(vid, projId); },
      });
      versionRedoRef.current = [];
      versionOpLatestRef.current = true;
    }
  }, [timelineVersionsHook, selectedProjectId, viewingVersion, projectVersions, selectedProject]);

  const makeCurrentVersion = useCallback(async (versionId) => {
    if (!selectedProject || !supabaseProjects) return;
    const version = projectVersions.find(v => v.id === versionId);
    if (!version?.snapshot) return;
    const projId = selectedProject.id;
    const prevTimeline = selectedProject.stageTimeline || {};
    versionSelfWriteRef.current = true;
    await writeTimelineDiff(supabaseProjects, projId, prevTimeline, version.snapshot);
    setVersionContextMenu(null);
    // Undo restores the timeline that was live before this op; redo re-applies the snapshot. Both flag
    // their write as self-originated so the stageTimeline observer keeps version-undo ownership.
    versionUndoRef.current.push({
      undo: async () => { versionSelfWriteRef.current = true; await writeTimelineDiff(supabaseProjects, projId, version.snapshot, prevTimeline); },
      redo: async () => { versionSelfWriteRef.current = true; await writeTimelineDiff(supabaseProjects, projId, prevTimeline, version.snapshot); },
    });
    versionRedoRef.current = [];
    versionOpLatestRef.current = true;
  }, [selectedProject, supabaseProjects, projectVersions]);

  // Per-project gantt rows — same source as table view (all rows, scope-reconciled when available)
  const perProjectGanttRows = useMemo(() => {
    if (!selectedProject) return [];
    const rows = buildPerProjectGanttRows(selectedProject, perProjectConfirmedScope);
    // Every stage on a standalone timeline is a manual one, so the "-N" tag would say nothing.
    return isStandaloneTimeline(selectedProject) ? rows.map(r => ({ ...r, hideCustomTag: true })) : rows;
  }, [selectedProject, perProjectConfirmedScope]);

  // ── Gantt bar drag → reschedule ───────────────────────────────────────
  // Commit a bar drag from the per-project gantt. The row component hands us the dragged geometry as
  // calendar Dates; here we translate that into the row's planned-schedule fields using the project's
  // own working-day calendar and persist directly (the same updateProject path the version restore /
  // access handlers use). Gestures:
  //   • move        → shift start, preserve the stored working-day duration, recompute end
  //   • resize-left → keep the end fixed, move the start, derive the new duration
  //   • resize-right→ keep the start fixed, extend/contract the end, derive the new duration
  // Only leaf rows are draggable (enforced in the row component), so this never rewrites a stage
  // summary. Dates land on working days; the parent-stage bar re-derives from its children on re-render.
  // ── Gantt bar undo/redo ───────────────────────────────────────────────
  // The table editor has its own undo stack, but it is unmounted in gantt view, so the gantt keeps a
  // self-contained history of bar drags. Each entry stores both endpoints { before, after } so undo/
  // redo are robust against the async project refetch (no need to read "current" state). Cleared when
  // the selected project or the view mode changes, so it never applies one project's snapshot to another
  // or collides with edits made in the table view.
  const ganttUndoRef = useRef([]);
  const ganttRedoRef = useRef([]);
  useEffect(() => { ganttUndoRef.current = []; ganttRedoRef.current = []; }, [selectedProjectId, viewMode]);

  const handleGanttBarDrag = useCallback((rowId, mode, newStart, newEnd) => {
    if (!canEdit || !supabaseProjects || !selectedProject) return;
    const before = selectedProject.stageTimeline || {};
    const newTl = computeGanttBarDragTimeline(before, rowId, mode, newStart, newEnd);
    if (!newTl) return;
    ganttUndoRef.current.push({ before, after: newTl });
    if (ganttUndoRef.current.length > 200) ganttUndoRef.current.shift();
    ganttRedoRef.current = [];
    writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, newTl);
  }, [canEdit, supabaseProjects, selectedProject]);

  // ── Gantt dependency (predecessor) editing ────────────────────────────
  // The gantt's Dependency column lets predecessors be changed without switching to the table. Typed
  // values use the gantt's own task # (its # column). Predecessors are stored in the numbering recorded
  // by `_depNumKey` (storedNum → rowId); we round-trip through rowId so a value stays correct even when
  // the table (which groups by sub-stage) numbers rows differently. Row order isn't changed here, so
  // `_depNumKey` stays valid and is left untouched. Dependent dates re-cascade when the table view
  // (which owns the cascade effect) is next opened.
  const ganttDepMaps = useMemo(() => {
    const idToNum = {}, numToId = {};
    perProjectGanttRows.forEach(r => { if (r.num != null) { idToNum[r.id] = r.num; numToId[r.num] = r.id; } });
    const dnk = selectedProject?.stageTimeline?._depNumKey;
    const depNumKey = (dnk && typeof dnk === 'object') ? dnk : null;
    const depIdToNum = {};
    if (depNumKey) Object.entries(depNumKey).forEach(([num, id]) => { depIdToNum[id] = Number(num); });
    return { idToNum, numToId, depNumKey, depIdToNum };
  }, [perProjectGanttRows, selectedProject]);

  const ganttDepDisplay = useCallback((rowId) => {
    const stored = selectedProject?.stageTimeline?.[rowId]?.predecessors;
    if (!stored) return '';
    const { idToNum, numToId, depNumKey } = ganttDepMaps;
    return parsePredecessors(stored).map(p => {
      const refId = depNumKey ? depNumKey[p.rowNumber] : numToId[p.rowNumber];
      const dispNum = (refId != null && idToNum[refId] != null) ? idToNum[refId] : p.rowNumber;
      return p.lag > 0 ? `${dispNum}${p.type}+${p.lag}d` : `${dispNum}${p.type}`;
    }).join(', ');
  }, [selectedProject, ganttDepMaps]);

  const handleGanttDependencyChange = useCallback((rowId, typedString) => {
    if (!canEdit || !supabaseProjects || !selectedProject) return;
    const { numToId, depIdToNum, depNumKey } = ganttDepMaps;
    const tokens = String(typedString || '').split(/[\s,]+/).filter(Boolean).map(tok => {
      const m = tok.match(/^(\d+)(FS|FF|SS|SF)?([+-]\d+d?)?$/i);
      if (!m) return null;
      const visNum = Number(m[1]);
      const refId = numToId[visNum];
      if (!refId || refId === rowId) return null; // unknown task # or self-reference
      // Express in the stored numbering so `_depNumKey` stays valid; fall back to the visible # when the
      // referenced row isn't in the stored map yet (numbering is already aligned in the common case).
      const storedNum = (depNumKey && depIdToNum[refId] != null) ? depIdToNum[refId] : visNum;
      return { rowNumber: storedNum, type: (m[2] || 'FS').toUpperCase(), lag: m[3] ? parseInt(m[3], 10) : 0 };
    }).filter(Boolean);
    const tl = selectedProject.stageTimeline || {};
    const formatted = formatPredecessors(tokens);
    const newTl = { ...tl, [rowId]: { ...(tl[rowId] || {}), predecessors: formatted } };
    writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, newTl);
  }, [canEdit, supabaseProjects, selectedProject, ganttDepMaps]);

  const doGanttUndo = useCallback(() => {
    if (!ganttUndoRef.current.length || !selectedProject || !supabaseProjects) return;
    const entry = ganttUndoRef.current.pop();
    ganttRedoRef.current.push(entry);
    writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, entry.before);
  }, [selectedProject, supabaseProjects]);

  const doGanttRedo = useCallback(() => {
    if (!ganttRedoRef.current.length || !selectedProject || !supabaseProjects) return;
    const entry = ganttRedoRef.current.pop();
    ganttUndoRef.current.push(entry);
    writeTimelineDiff(supabaseProjects, selectedProject.id, selectedProject.stageTimeline, entry.after);
  }, [selectedProject, supabaseProjects]);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y (or Shift+Ctrl+Z) = redo — only in the project gantt view.
  useEffect(() => {
    if (!canEdit || templateBuilderMode || viewMode !== 'gantt' || !selectedProject) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      const k = (e.key || '').toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); doGanttUndo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); doGanttRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, templateBuilderMode, viewMode, selectedProject, doGanttUndo, doGanttRedo]);

  // The BRM template builder belongs to BRM: leaving the project closes it.
  useEffect(() => { setBrmBuilder(null); }, [selectedProjectId]);
  // BRM: add a template to the tab on screen, or save that tab as a template.
  const applyBrmTemplate = useCallback(async (tpl) => {
    if (!selectedProject || !supabaseProjects || !tpl) return;
    const tl = selectedProject.stageTimeline || {};
    const tabName = activeTabId === '__all__' ? mainTabName(tl) : ((tl._tabs || []).find(t => t.id === activeTabId)?.name || 'this tab');
    if (!window.confirm(`Add the template "${tpl.name}" to the tab "${tabName}"? Its stages go below the tab's last stage.`)) return;
    const result = applyBrmTemplateToTab(tpl, tl, activeTabId);
    if (!result) { alert('That template has no rows yet.'); return; }
    const res = await writeTimelineDiff(supabaseProjects, selectedProject.id, tl, result.timeline);
    if (res && res.success === false) alert('Could not apply the template: ' + (res.error || 'permission denied.'));
  }, [selectedProject, supabaseProjects, activeTabId]);
  const saveBrmTabAsTemplate = useCallback(async () => {
    if (!selectedProject) return;
    const tl = selectedProject.stageTimeline || {};
    const rows = Array.isArray(tl._rows) && tl._rows.length ? tl._rows : STANDALONE_SEED_ROWS;
    const keep = new Set(standaloneTabView(tl, activeTabId).keys);
    let cur = null;
    const tabRows = rows.filter(r => { if ((r.indent || 0) === 0) cur = r.id; return keep.has(cur); });
    if (!tabRows.length) { alert('This tab has no stages to save.'); return; }
    const tabName = activeTabId === '__all__' ? mainTabName(tl) : ((tl._tabs || []).find(t => t.id === activeTabId)?.name || '');
    const name = (window.prompt('Template name', tabName) || '').trim();
    if (!name) return;
    const res = await saveBrmTemplate({ id: 'brmtpl_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5), name, timeline: brmTemplateTimelineFrom(tl, tabRows) });
    if (res && res.success === false) alert('Could not save the template: ' + (res.error || 'permission denied.'));
  }, [selectedProject, activeTabId, saveBrmTemplate]);

  // Per-project gantt rows filtered by collapsed rows. A collapsed row (stage OR parent task) hides
  // every deeper-indent row beneath it until the indent returns to its level, so the hierarchy
  // collapses exactly like the table.
  const perProjectDisplayRows = useMemo(() => {
    // Apply the active tab (stage/part filter view) first, then the stage-collapse filter — so the
    // gantt renders exactly the tab the user selected, matching the table view.
    const tabbed = filterGanttRowsByTab(perProjectGanttRows,
      isStandaloneTimeline(selectedProject) ? standaloneTabView(selectedProject?.stageTimeline, activeTabId) : activeTab,
      selectedProject?.stageTimeline);
    if (ganttCollapsedStages.size === 0) return tabbed;
    const result = [];
    let hideBelowIndent = null; // while set, skip rows deeper than this (inside a collapsed subtree)
    for (const row of tabbed) {
      const ind = row.indent || 0;
      if (hideBelowIndent !== null) {
        if (ind > hideBelowIndent) continue;
        hideBelowIndent = null; // exited the collapsed subtree
      }
      result.push(row);
      if (ganttCollapsedStages.has(row.id)) hideBelowIndent = ind;
    }
    return result;
  }, [perProjectGanttRows, ganttCollapsedStages, activeTab, activeTabId, selectedProject]);

  // Compute global timeline range.
  // 1M zoom: scoped to project lifespan so scroll area matches the data, not 16 years.
  // Other zooms, one project or all: from the 1st of the month two months before the earliest date to
  // 31 Dec of the latest date's year, so the chart grows as the projects' dates do (this year when
  // there are no dates yet).
  const { rangeStart, rangeEnd } = useMemo(() => {
    const activeLabel = GANTT_ZOOM_LEVELS[selectedProject ? ganttZoomIndex : multiGanttZoomIndex].label;
    const is1M = activeLabel === '1M';

    // Collect data date range
    let minDate = null;
    let maxDate = null;
    if (selectedProject && perProjectGanttRows.length > 0) {
      perProjectGanttRows.forEach(r => {
        const dates = [r.start, r.end, r.targetStart, r.targetEnd, r.actualStart, r.actualEnd].filter(Boolean);
        dates.forEach(d => {
          if (!minDate || d < minDate) minDate = d;
          if (!maxDate || d > maxDate) maxDate = d;
        });
      });
    } else {
      ganttDisplayRows.forEach(p => {
        if (p.projectStart && (!minDate || p.projectStart < minDate)) minDate = p.projectStart;
        if (p.projectEnd && (!maxDate || p.projectEnd > maxDate)) maxDate = p.projectEnd;
      });
    }

    if (is1M) {
      // Scope to project lifespan with small padding
      const now = new Date();
      return {
        rangeStart: addDays(minDate || now, -7),
        rangeEnd: addDays(maxDate || now, 30),
      };
    }

    const now = new Date();
    const first = minDate || new Date(now.getFullYear(), 0, 1);
    const last = maxDate || now;
    const start = new Date(first.getFullYear(), first.getMonth() - 2, 1);
    return { rangeStart: start, rangeEnd: new Date(Math.max(start.getFullYear(), last.getFullYear()), 11, 31) };
  }, [selectedProject, perProjectGanttRows, ganttDisplayRows, ganttZoomIndex, multiGanttZoomIndex]);

  // pxPerDay: dynamic based on container width so that durationDays fills the visible area exactly
  const activeZoomLevel = selectedProject ? GANTT_ZOOM_LEVELS[ganttZoomIndex] : GANTT_ZOOM_LEVELS[multiGanttZoomIndex];
  const durationDays = activeZoomLevel.durationDays;
  const pxPerDay = ganttContainerWidth > 300
    ? Math.max(0.05, (ganttContainerWidth - 240) / durationDays)
    : activeZoomLevel.pxPerDay; // fallback until container is measured
  const totalDays = daysBetween(rangeStart, rangeEnd);
  const totalWidth = totalDays * pxPerDay;

  // Toggle expand/collapse
  const toggleProject = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Scroll to today on mount
  useEffect(() => {
    if (scrollContainerRef.current && rangeStart) {
      const today = new Date();
      const todayOffset = daysBetween(rangeStart, today) * pxPerDay;
      // Center today in the viewport
      const containerWidth = scrollContainerRef.current.clientWidth - 240; // minus label column
      scrollContainerRef.current.scrollLeft = Math.max(0, todayOffset - containerWidth / 3);
    }
    // Only on mount and zoom change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pxPerDay]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Full screen only: fold the project bar and the editor's legend toolbar away so the table gets
  // the height. Leaving full screen opens them again.
  const [toolbarsCollapsed, setToolbarsCollapsed] = useState(false);
  useEffect(() => { if (!isFullscreen) setToolbarsCollapsed(false); }, [isFullscreen]);

  // Escape leaves full screen, unless it was meant for something else: typing in a field, or a
  // modal open on top of the page.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      if (document.querySelector('.brio-modal-overlay, [aria-modal="true"]')) return;
      setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // Measure gantt scroll container width for dynamic pxPerDay
  useEffect(() => {
    if (viewMode !== 'gantt') return;
    const measure = () => {
      if (scrollContainerRef.current) {
        setGanttContainerWidth(scrollContainerRef.current.clientWidth);
      }
    };
    // Defer slightly so the DOM is fully painted
    const timer = setTimeout(measure, 50);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timer); window.removeEventListener('resize', measure); };
  }, [viewMode, selectedProject]);

  // Close multi-export menu on outside click
  useEffect(() => {
    if (!showMultiExportMenu) return;
    const handler = e => { if (multiExportMenuRef.current && !multiExportMenuRef.current.contains(e.target)) setShowMultiExportMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMultiExportMenu]);

  // ── Delete timeline ────────────────────────────────────────────────────────
  // Reset Timeline (Settings menu) — revert to the original, scope-driven state and REMOVE any applied
  // template: stage order returns to the confirmed-scope canonical order, template/custom-added rows are
  // dropped, and the applied-template marker + template-authored planned dates / durations / dependencies
  // / renames on scope rows are cleared (so a mis-applied template's broken dependencies are undone).
  // KEPT: real project data — Tracking/Invoicing dates (contract/target/actual/approval/invoice/paid,
  // invoice %, billing, partStages) and custom-column values — plus view settings, custom columns,
  // settings presets, granted access and tabs. Mirrors the table view's "Reset timelines".
  const handleDeleteTimeline = useCallback(async () => {
    if (!selectedProject || !supabaseProjects) return;
    const tl = selectedProject.stageTimeline || {};
    const confirmedScope = perProjectConfirmedScope;
    const PRESERVE = ['contractDate', 'targetDate', 'actualDate', 'clientApprovalDate', 'invoiceDate', 'paidDate', 'invoicePercent', 'billing', 'partStages'];
    const META = ['_viewSettings', '_settingsPresets', '_activePresetId', '_customColumns', '_assignedToCategories', '_grantedAccess', '_tabs', '_mainTabName', '_colFmt'];
    const scopeRowIds = new Set(confirmedScope ? buildRowsFromScope(confirmedScope).map(r => r.id) : []);
    const isTemplateAddedRow = (key) =>
      key.startsWith('row_') ||
      ((key.startsWith('meeting_') || key.startsWith('revision_')) && !scopeRowIds.has(key));
    const preserved = {};
    // _appliedTemplateId / _depNumKey are intentionally NOT in META, so they're dropped — the template
    // is no longer "applied" and predecessor numbering re-stamps from the rebuilt scope order.
    META.forEach(k => { if (tl[k] !== undefined) preserved[k] = tl[k]; });
    Object.entries(tl).forEach(([key, val]) => {
      if (key.startsWith('_')) return;
      if (!val || typeof val !== 'object') return;
      if (isTemplateAddedRow(key)) return; // drop template/custom-added rows entirely
      const kept = {};
      PRESERVE.forEach(f => { if (val[f] !== undefined) kept[f] = val[f]; });
      Object.keys(val).forEach(f => { if (f.startsWith('custom_')) kept[f] = val[f]; });
      if (Object.keys(kept).length) preserved[key] = kept;
    });
    // With a confirmed scope, drop _rows so the structure rebuilds from the canonical scope order.
    // Without one, keep the current stage structure minus the template/custom rows.
    if (!confirmedScope) {
      const baseRows = Array.isArray(tl._rows) ? tl._rows : [];
      preserved._rows = baseRows
        .filter(r => !isTemplateAddedRow(r.id))
        .map(r => ({ id: r.id, indent: r.indent, ...(r.defaultLabel ? { defaultLabel: r.defaultLabel } : {}) }));
    }
    await writeTimelineDiff(supabaseProjects, selectedProject.id, tl, preserved);
    setShowDeleteTimelineConfirm(false);
    setShowGanttSettingsMenu(false);
  }, [selectedProject, supabaseProjects, perProjectConfirmedScope]);

  const handleSaveGanttAccess = useCallback(async (grants) => {
    if (!selectedProject || !supabaseProjects) return;
    setShowGanttGiveAccess(false);
    const result = await supabaseProjects.saveTimelineGrants(selectedProject.id, grants);
    if (!result.success) alert('Could not save access: ' + result.error);
  }, [selectedProject, supabaseProjects]);

  // ── Per-project gantt export helpers ──────────────────────────────────────
  const handleGanttExportCsv = useCallback(() => {
    if (!selectedProject || perProjectGanttRows.length === 0) return;
    const esc = v => '"' + String(v || '').replace(/"/g, '""') + '"';
    const headers = ['Task', 'Contract Date', 'Target Date', 'Actual Date', 'Invoice Date', 'Paid Date', 'Status'];
    const rows = perProjectGanttRows.map(row => [
      esc('  '.repeat(row.indent) + row.name),
      row.contractDate ? formatDate(row.contractDate) : '',
      row.targetDate ? formatDate(row.targetDate) : '',
      row.actualDate ? formatDate(row.actualDate) : '',
      row.invoiceDate ? formatDate(row.invoiceDate) : '',
      row.paidDate ? formatDate(row.paidDate) : '',
      esc(row.status?.label || ''),
    ].join(','));
    const csv = [headers.join(',')].concat(rows).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = (selectedProject.name || 'timeline') + '_gantt.csv'; a.click();
    URL.revokeObjectURL(url);
    setShowGanttSettingsMenu(false);
  }, [selectedProject, perProjectGanttRows]);

  const handleGanttExportExcel = useCallback(async () => {
    if (!selectedProject || perProjectGanttRows.length === 0) return;
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Timeline');
      const hexToArgb = hex => 'FF' + hex.replace('#', '').toUpperCase();
      const periods = buildGanttPeriods(rangeStart, rangeEnd);
      const thinBorder = { style: 'thin', color: { argb: 'FFE0E0E0' } };
      const hairBorder = { style: 'hair', color: { argb: 'FFE8E8E8' } };
      ws.columns = [
        { header: 'Task / Deliverable', key: '_name', width: 30 },
        ...periods.map((p, i) => ({ header: p.label, key: `_p${i}`, width: 7 })),
      ];
      const headerRow = ws.getRow(1);
      headerRow.font = { name: xlsxFamily(excelExportStyles?.style2), bold: true, size: 8 };
      headerRow.height = 22;
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAF8' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
      });
      headerRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      perProjectGanttRows.forEach(row => {
        const rowData = { _name: row.name };
        periods.forEach((p, i) => { rowData[`_p${i}`] = ''; });
        const wsRow = ws.addRow(rowData);
        wsRow.height = row.isStage ? 16 : 13;
        const barArgb = hexToArgb(resolveCssColor(row.color));
        const stageBg = row.isStage ? (STAGE_ROW_COLORS[row.id] || '#f4f4f0') : null;
        const stageBgArgb = stageBg ? hexToArgb(stageBg) : null;
        const nameCell = wsRow.getCell(1);
        const rowBold = row.isStage || !!selectedProject.stageTimeline?.[row.id]?.bold;
        nameCell.font = { name: xlsxFamily(row.isStage ? excelExportStyles?.style3 : excelExportStyles?.style1), bold: rowBold, size: 8 };
        nameCell.alignment = { horizontal: 'left', vertical: 'middle', indent: row.indent * 2 };
        nameCell.border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
        if (stageBgArgb) nameCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stageBgArgb } };
        periods.forEach((p, i) => {
          const cell = wsRow.getCell(i + 2);
          const overlaps = row.start && row.end && row.start < p.end && row.end > p.start;
          if (overlaps) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: barArgb } };
          } else if (stageBgArgb) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stageBgArgb } };
          }
          cell.border = { top: hairBorder, bottom: hairBorder, left: hairBorder, right: hairBorder };
        });
      });
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = (selectedProject.name || 'timeline') + '_gantt.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Excel export failed: ' + err.message);
    }
    setShowGanttSettingsMenu(false);
  }, [selectedProject, perProjectGanttRows, rangeStart, rangeEnd, excelExportStyles]);

  const handleGanttExportPdf = useCallback(() => {
    if (!selectedProject || perProjectGanttRows.length === 0) return;
    const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Start export from the current scroll position so the PDF matches the visible view
    const scrollLeft = scrollContainerRef.current?.scrollLeft ?? 0;
    const scrolledDays = pxPerDay > 0 ? Math.floor(scrollLeft / pxPerDay) : 0;
    const exportRangeStart = addDays(rangeStart, scrolledDays);
    const exportDurationDays = GANTT_ZOOM_LEVELS[ganttZoomIndex].durationDays;
    const exportRangeEnd = addDays(exportRangeStart, exportDurationDays);
    const periods = buildGanttPeriods(exportRangeStart, exportRangeEnd);
    const exportDurationMs = exportDurationDays * DAY_MS;

    // Convert a date to a percentage of the export range (0–100), clamped
    const toPct = (d) => {
      if (!d) return null;
      return Math.max(0, Math.min(100, (d.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100));
    };
    // Build a bar div if dates are within/overlapping the export range
    const barDiv = (start, end, color, topPx = 4, heightPx = 10) => {
      if (!start || !end) return '';
      const l = (start.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100;
      const r = (end.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100;
      if (r <= 0 || l >= 100) return '';
      const left = Math.max(0, l).toFixed(3);
      const w = Math.max(0.15, Math.min(100, r) - Math.max(0, l)).toFixed(3);
      return `<div style="position:absolute;left:${left}%;width:${w}%;top:${topPx}px;height:${heightPx}px;background:${color}"></div>`;
    };
    // Period divider lines (shared across all rows)
    const gridLines = periods.map(p => {
      const pct = toPct(p.end);
      if (pct === null || pct <= 0 || pct >= 100) return '';
      return `<div style="position:absolute;left:${pct.toFixed(3)}%;top:0;width:1px;height:100%;background:#f0f0f0"></div>`;
    }).join('');
    // Today marker
    const todayPct = toPct(new Date());
    const todayLine = (todayPct !== null && todayPct > 0 && todayPct < 100)
      ? `<div style="position:absolute;left:${todayPct.toFixed(3)}%;top:0;width:2px;height:100%;background:#DB6B5D;z-index:2"></div>`
      : '';
    // Header: period labels with borders
    const headerInner = periods.map(p => {
      const x1 = Math.max(0, toPct(p.start) ?? 0);
      const x2 = Math.min(100, toPct(p.end) ?? 100);
      const w = x2 - x1;
      if (w <= 0) return '';
      return `<div style="position:absolute;left:${x1.toFixed(3)}%;width:${w.toFixed(3)}%;height:100%;border-right:1px solid #e0e0e0;display:flex;align-items:center;justify-content:center;font-size:8pt;font-weight:400;color:#94a3b8;overflow:hidden;white-space:nowrap">${w > 3 ? esc(p.label) : ''}</div>`;
    }).join('') + todayLine;
    // Render bars per row respecting the current date filter (matches screen PerProjectGanttRow)
    const rowsHtml = perProjectGanttRows.map(row => {
      const indent = row.indent * 12;
      const barColor = resolveCssColor(row.color);
      let bars = '';
      if (row.isStage) {
        // Stage rows: always single bar from tracking dates
        bars = barDiv(row.start, row.end, barColor);
      } else {
        // Deliverable rows: respect ganttDateFilter
        if (ganttDateFilter === 'both') {
          // Dual half-height bars: target (top, blue) + actual (bottom, green)
          bars = barDiv(row.targetStart || row.start, row.targetEnd || row.end, '#93c5fd', 2, 7)
               + barDiv(row.actualStart, row.actualEnd, '#6ee7b7', 9, 7);
        } else if (ganttDateFilter === 'target') {
          bars = barDiv(row.targetStart || row.start, row.targetEnd || row.end, '#93c5fd');
        } else {
          // 'actual'
          bars = barDiv(row.actualStart || row.start, row.actualEnd || row.end, '#6ee7b7');
        }
      }
      const barCell = `<div style="position:relative;width:100%;height:18px;overflow:hidden">${gridLines}${bars}</div>`;
      const nameWeight = selectedProject.stageTimeline?.[row.id]?.bold ? 600 : 300;
      return `<tr style="height:18px"><td style="padding-left:${indent + 4}px;font-weight:${nameWeight};font-size:10pt;white-space:nowrap;overflow:hidden;border:1px solid #e0e0e0">${esc(row.name)}</td><td style="padding:0;border:1px solid #e0e0e0">${barCell}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(selectedProject.name || 'Gantt')}</title>
<style>
  ${pdfFontFaceCss()}
  *{box-sizing:border-box}body{font-family:${pdfFontFamily(scopePdfStyles?.body)};font-size:10pt;margin:0;padding:10px;color:#333}
  h2{font-family:${pdfFontFamily(scopePdfStyles?.title)};font-size:11pt;font-weight:500;margin:0 0 8px}
  th{font-family:${pdfFontFamily(scopePdfStyles?.heading)}}
  table{border-collapse:collapse;width:100%;table-layout:fixed}
  @page{size:A3 landscape;margin:8mm}
</style></head><body>
<h2>${esc(selectedProject.name || 'Gantt')} — Timeline Gantt</h2>
<table>
  <colgroup><col style="width:220px"><col></colgroup>
  <thead><tr style="height:28px">
    <th style="text-align:left;padding:2px 4px;border:1px solid #e0e0e0;background:#fafaf8;font-size:10pt;font-weight:600;vertical-align:middle">Task / Deliverable</th>
    <th style="padding:0;border:1px solid #e0e0e0;background:#fafaf8"><div style="position:relative;width:100%;height:28px;overflow:hidden">${headerInner}</div></th>
  </tr></thead>
  <tbody>${rowsHtml}</tbody>
</table></body></html>`;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Allow pop-ups to export PDF.'); return; }
    win.document.write(html); win.document.close(); win.focus();
    printWhenFontsReady(win);
    setShowGanttSettingsMenu(false);
  }, [selectedProject, perProjectGanttRows, rangeStart, ganttZoomIndex, pxPerDay, ganttDateFilter, scopePdfStyles]);

  // ── Multi-project gantt export helpers ────────────────────────────────────
  const handleMultiExportPdf = useCallback(() => {
    if (ganttDisplayRows.length === 0) return;
    const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Start export from the current scroll position so the PDF matches the visible view
    const scrollLeft = scrollContainerRef.current?.scrollLeft ?? 0;
    const scrolledDays = pxPerDay > 0 ? Math.floor(scrollLeft / pxPerDay) : 0;
    const exportRangeStart = addDays(rangeStart, scrolledDays);
    const exportDurationDays = GANTT_ZOOM_LEVELS[multiGanttZoomIndex].durationDays;
    const exportRangeEnd = addDays(exportRangeStart, exportDurationDays);
    const periods = buildGanttPeriods(exportRangeStart, exportRangeEnd);
    const exportDurationMs = exportDurationDays * DAY_MS;

    const toPct = (d) => {
      if (!d) return null;
      return Math.max(0, Math.min(100, (d.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100));
    };
    const barDiv = (start, end, color, topPx = 4, heightPx = 10) => {
      if (!start || !end) return '';
      const l = (start.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100;
      const r = (end.getTime() - exportRangeStart.getTime()) / exportDurationMs * 100;
      if (r <= 0 || l >= 100) return '';
      const left = Math.max(0, l).toFixed(3);
      const w = Math.max(0.15, Math.min(100, r) - Math.max(0, l)).toFixed(3);
      return `<div style="position:absolute;left:${left}%;width:${w}%;top:${topPx}px;height:${heightPx}px;background:${color}"></div>`;
    };
    const gridLines = periods.map(p => {
      const pct = toPct(p.end);
      if (pct === null || pct <= 0 || pct >= 100) return '';
      return `<div style="position:absolute;left:${pct.toFixed(3)}%;top:0;width:1px;height:100%;background:#f0f0f0"></div>`;
    }).join('');
    const todayPct = toPct(new Date());
    const todayLine = (todayPct !== null && todayPct > 0 && todayPct < 100)
      ? `<div style="position:absolute;left:${todayPct.toFixed(3)}%;top:0;width:2px;height:100%;background:#DB6B5D;z-index:2"></div>`
      : '';
    const headerInner = periods.map(p => {
      const x1 = Math.max(0, toPct(p.start) ?? 0);
      const x2 = Math.min(100, toPct(p.end) ?? 100);
      const w = x2 - x1;
      if (w <= 0) return '';
      return `<div style="position:absolute;left:${x1.toFixed(3)}%;width:${w.toFixed(3)}%;height:100%;border-right:1px solid #e0e0e0;display:flex;align-items:center;justify-content:center;font-size:8pt;font-weight:400;color:#94a3b8;overflow:hidden;white-space:nowrap">${w > 3 ? esc(p.label) : ''}</div>`;
    }).join('') + todayLine;
    const rowsHtml = ganttDisplayRows.map(proj => {
      const projColor = resolveCssColor(proj.color) || '#3b82f6';
      const projBar = barDiv(proj.projectStart, proj.projectEnd, projColor);
      const projBarCell = `<div style="position:relative;width:100%;height:18px;overflow:hidden;background:#f4f4f0">${gridLines}${projBar}</div>`;
      const projRow = `<tr style="height:18px"><td style="padding-left:4px;font-weight:300;font-size:10pt;white-space:nowrap;overflow:hidden;border:1px solid #e0e0e0;background:#f4f4f0">${esc(proj.code ? `[${proj.code}] ` : '')}${esc(proj.name)}</td><td style="padding:0;border:1px solid #e0e0e0">${projBarCell}</td></tr>`;
      const stageRows = proj.stages.map(s => {
        const sColor = resolveCssColor(s.color);
        const sBar = barDiv(s.start, s.end, sColor);
        const sBarCell = `<div style="position:relative;width:100%;height:18px;overflow:hidden">${gridLines}${sBar}</div>`;
        return `<tr style="height:18px"><td style="padding-left:16px;font-weight:300;font-size:10pt;white-space:nowrap;overflow:hidden;border:1px solid #e0e0e0">${esc(s.name)}</td><td style="padding:0;border:1px solid #e0e0e0">${sBarCell}</td></tr>`;
      }).join('');
      return projRow + stageRows;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Timelines Overview — Gantt</title>
<style>
  ${pdfFontFaceCss()}
  *{box-sizing:border-box}body{font-family:${pdfFontFamily(scopePdfStyles?.body)};font-size:10pt;margin:0;padding:10px;color:#333}
  h2{font-family:${pdfFontFamily(scopePdfStyles?.title)};font-size:11pt;font-weight:500;margin:0 0 8px}
  th{font-family:${pdfFontFamily(scopePdfStyles?.heading)}}
  table{border-collapse:collapse;width:100%;table-layout:fixed}
  @page{size:A3 landscape;margin:8mm}
</style></head><body>
<h2>Timelines Overview — Gantt</h2>
<table>
  <colgroup><col style="width:220px"><col></colgroup>
  <thead><tr style="height:28px">
    <th style="text-align:left;padding:2px 4px;border:1px solid #e0e0e0;background:#fafaf8;font-size:10pt;font-weight:600;vertical-align:middle">Project / Stage</th>
    <th style="padding:0;border:1px solid #e0e0e0;background:#fafaf8"><div style="position:relative;width:100%;height:28px;overflow:hidden">${headerInner}</div></th>
  </tr></thead>
  <tbody>${rowsHtml}</tbody>
</table></body></html>`;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Allow pop-ups to export PDF.'); return; }
    win.document.write(html); win.document.close(); win.focus();
    printWhenFontsReady(win);
    setShowMultiExportMenu(false);
  }, [ganttDisplayRows, rangeStart, multiGanttZoomIndex, pxPerDay, scopePdfStyles]);

  const handleMultiExportCsv = useCallback(() => {
    if (ganttDisplayRows.length === 0) return;
    const esc = v => '"' + String(v || '').replace(/"/g, '""') + '"';
    const headers = ['Project', 'Stage', 'Target Date', 'Actual Date', 'Invoice Date', 'Paid Date', 'Status'];
    const rows = [];
    ganttDisplayRows.forEach(p => {
      p.stages.forEach(s => {
        rows.push([
          esc(p.name),
          esc(s.name),
          s.targetDate ? formatDate(s.targetDate) : '',
          s.actualDate ? formatDate(s.actualDate) : '',
          s.invoiceDate ? formatDate(s.invoiceDate) : '',
          s.paidDate ? formatDate(s.paidDate) : '',
          esc(s.status?.label || ''),
        ].join(','));
      });
    });
    const csv = [headers.join(',')].concat(rows).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = 'timelines_overview.csv'; a.click();
    URL.revokeObjectURL(url);
    setShowMultiExportMenu(false);
  }, [ganttDisplayRows]);

  const handleMultiExportExcel = useCallback(async () => {
    if (ganttDisplayRows.length === 0) return;
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Timelines');
      const hexToArgb = hex => 'FF' + hex.replace('#', '').toUpperCase();
      const periods = buildGanttPeriods(rangeStart, rangeEnd);
      const thinBorder = { style: 'thin', color: { argb: 'FFE0E0E0' } };
      const hairBorder = { style: 'hair', color: { argb: 'FFE8E8E8' } };
      ws.columns = [
        { header: 'Project / Stage', key: '_name', width: 32 },
        ...periods.map((p, i) => ({ header: p.label, key: `_p${i}`, width: 7 })),
      ];
      const headerRow = ws.getRow(1);
      headerRow.font = { name: xlsxFamily(excelExportStyles?.style2), bold: true, size: 8 };
      headerRow.height = 22;
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAF8' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
      });
      headerRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      ganttDisplayRows.forEach(proj => {
        const projColor = hexToArgb(resolveCssColor(proj.color) || '#3b82f6');
        const projBgArgb = 'FFF4F4F0';
        const projRowData = { _name: (proj.code ? `[${proj.code}] ` : '') + proj.name };
        periods.forEach((p, i) => { projRowData[`_p${i}`] = ''; });
        const projWsRow = ws.addRow(projRowData);
        projWsRow.height = 18;
        projWsRow.getCell(1).font = { name: xlsxFamily(excelExportStyles?.style3), bold: true, size: 8 };
        projWsRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: projBgArgb } };
        projWsRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
        projWsRow.getCell(1).border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
        periods.forEach((p, i) => {
          const cell = projWsRow.getCell(i + 2);
          const overlaps = proj.projectStart && proj.projectEnd && proj.projectStart < p.end && proj.projectEnd > p.start;
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: overlaps ? projColor : projBgArgb } };
          cell.border = { top: hairBorder, bottom: hairBorder, left: hairBorder, right: hairBorder };
        });
        proj.stages.forEach(s => {
          const stageBg = STAGE_ROW_COLORS[s.key] || '#ffffff';
          const stageBgArgb = hexToArgb(stageBg);
          const barArgb = hexToArgb(resolveCssColor(s.color));
          const stageRowData = { _name: s.name };
          periods.forEach((p, i) => { stageRowData[`_p${i}`] = ''; });
          const stageWsRow = ws.addRow(stageRowData);
          stageWsRow.height = 13;
          stageWsRow.getCell(1).font = { name: xlsxFamily(excelExportStyles?.style1), size: 8 };
          stageWsRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stageBgArgb } };
          stageWsRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 2 };
          stageWsRow.getCell(1).border = { top: hairBorder, bottom: hairBorder, left: thinBorder, right: hairBorder };
          periods.forEach((p, i) => {
            const cell = stageWsRow.getCell(i + 2);
            const overlaps = s.start && s.end && s.start < p.end && s.end > p.start;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: overlaps ? barArgb : stageBgArgb } };
            cell.border = { top: hairBorder, bottom: hairBorder, left: hairBorder, right: hairBorder };
          });
        });
      });
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = 'timelines_overview.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Excel export failed: ' + err.message);
    }
    setShowMultiExportMenu(false);
  }, [ganttDisplayRows, rangeStart, rangeEnd, excelExportStyles]);

  // Zoom handlers
  const zoomIn = () => setZoomIndex(i => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  const zoomOut = () => setZoomIndex(i => Math.max(i - 1, 0));

  // Summary stats
  const overdueCount = useMemo(
    () => ganttDisplayRows.reduce((sum, p) => sum + p.stages.filter(s => s.isOverdue).length, 0),
    [ganttDisplayRows]
  );

  // The toolbar fold is a full screen affordance for the project table only.
  const canCollapseToolbars = isFullscreen && !templateBuilderMode && !viewingVersion && viewMode === 'table' && !!selectedProject;

  const selectArrowSvg = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%23666\'/%3E%3C/svg%3E")';

  return (
    <div ref={pageContainerRef} style={isFullscreen ? {
      fontFamily: 'Raleway, sans-serif',
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 9999, backgroundColor: 'white',
      overflow: 'auto',
      padding: '16px 24px',
    } : { fontFamily: 'Raleway, sans-serif', maxWidth: '100%' }}>

      {/* ── Info icon — fixed top-left corner (matches Donut Tracker position) ── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: '44px',
        display: 'flex', alignItems: 'center',
        padding: '0 16px', zIndex: 99999, pointerEvents: 'none',
        fontFamily: "'Raleway', 'Inter', sans-serif",
      }}>
        <span ref={infoTipRef} style={{ pointerEvents: 'auto', position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          {/* Icon trigger */}
          <span
            style={{ cursor: 'pointer', display: 'inline-flex', opacity: (infoTipHovered || infoTipLocked) ? 1 : 0.45, transition: 'opacity 0.15s' }}
            onMouseEnter={() => setInfoTipHovered(true)}
            onMouseLeave={() => setInfoTipHovered(false)}
            onClick={() => setInfoTipLocked(v => !v)}
          >
            <Info style={{ width: 14, height: 14 }} />
          </span>
          {/* Tooltip panel — shown on hover OR when locked open */}
          {(infoTipHovered || infoTipLocked) && (
            <div
              style={{
                position: 'fixed', top: '44px', left: '16px', width: '340px',
                maxHeight: 'calc(100vh - 60px)',
                overflowY: 'auto', overflowX: 'hidden',
                padding: '14px 16px',
                background: '#fff', color: '#444',
                fontSize: '10px', fontWeight: 400, lineHeight: 1.55,
                letterSpacing: 0, zIndex: 100000,
                borderRadius: '4px', boxShadow: '0 4px 20px rgba(0,0,0,0.14)',
                fontFamily: "'Raleway', 'Inter', sans-serif",
                WebkitOverflowScrolling: 'touch',
              }}
              onMouseEnter={() => setInfoTipHovered(true)}
              onMouseLeave={() => setInfoTipHovered(false)}
            >
              <strong style={{ fontSize: '11px', display: 'block', marginBottom: '8px' }}>Timelines page</strong>
              <span><i>The timelines page is used for project scheduling, tracking project delays and recording M.O.M's.</i> Tasks auto read from SOW to build deliverables.</span>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Building Project Timelines</strong>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 1:</u></b> <b>Initial Page Setup</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>In &quot;<i>Settings</i>&quot; button &gt; Turn off Sync to tracking.</li>
                <li>In &quot;<i>Dependency Settings</i>&quot; &gt; Enable dependency (if off) &gt; include BRIO Holidays in the &quot;<i>Working days</i>&quot; section.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 2:</u></b> <b>Organize stage sequence</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Use the Up/ Down arrow on the left of the Stage names cell to organise stage order as required.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 3:</u></b> <b>Apply Template</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>If a project SOW is confirmed in the Contract &amp; Scope page, Timelines page will auto read the deliverables to create tasks.</li>
                <li>Alternatively, use a preset timelines template from the template list on the top right corner. Has preset duration and dependencies.</li>
                <li>Eye icon to preview template details before you apply.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 4:</u></b> <b>Contract dates</b> (lower section within the stage name cell). This is an estimated project duration as mentioned in contract.</span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Click &quot;<i>Add contract dates</i>&quot; &gt; Select stages &gt; set duration &gt; Apply.</li>
                <li>This will equally split the duration between selected stages.</li>
                <li>You can create multiple sets of stages with different durations. For e.g. Design duration and Construction duration.</li>
                <li>For manual entry: Add start date and duration. End date will be calculated automatically.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 5:</u></b> <b>Set the timelines</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Shuffle tasks: Long press on the Sr. No and drag and drop.</li>
                <li>Add/ Edit dependency: Single click for manual entry. Double click to open settings window.</li>
                <li>New Row, Clear, Copy, Paste, Delete, Hide, etc.: Right click on task name cells for options.</li>
                <li>Subtask: Right click on row &gt; Indent.</li>
                <li>Parent task: Right click on row &gt; Outdent.</li>
                <li>Multiple select: &quot;<i>CTRL + Select</i>&quot;.</li>
                <li>New column, Copy, Clear, etc.: Right click on column header.</li>
                <li>Single select and multiselect columns: Right click on column header &gt; Add categories.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 6:</u></b> <b>Finalize &amp; sync</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>After project timelines are fully built, turn on Sync to tracking in the &quot;<i>Settings</i>&quot; list.</li>
                <li>Read sync info below.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 7:</u></b> Project <b>In-Progress</b></span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Stage end dates will auto sync to the Planned column in tracking page.</li>
                <li>In case of a glitch, right click on stage end date &gt; Sync.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 8:</u></b> Stage deliverable completion.</span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Once all deliverables of a stage are complete and ready for invoicing.</li>
                <li>Right click on stage end date &gt; Update Actuals &gt; Triggers email to client.</li>
                <li>This is not auto sync and must only be done on stage completion.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 9:</u></b> Invoicing</span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>Client approves stage completion &amp; Invoice is raised.</li>
                <li>These dates auto read from tracking page and are updated on timelines page in the invoicing of stage row.</li>
                <li>In case of a glitch, right click on the invoicing of stage &gt; Load from tracking.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 10a:</u></b> Stage closure.</span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>When payment is received, the date from the paid column on tracking page will be updated on the closure of stage row.</li>
              </ol>
              <span style={{ display: 'block', marginTop: '8px', marginBottom: '2px' }}><b><u>Step 10b:</u></b> Delayed Payment</span>
              <ol style={{ margin: '0 0 0 16px', padding: 0, listStyle: 'decimal' }}>
                <li>When a payment is delayed beyond invoicing duration, it will push all consecutive stage dates ahead.</li>
                <li>Dependency of Start date of stage is pre-set to start date of invoicing stage; this auto changes to end date of invoicing of stage with a lag until payment is received.</li>
                <li>Once payment is received the lag will be auto paused.</li>
                <li>Options on the start of next stage to ignore delay or keep delay will appear.</li>
              </ol>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Sync Indication dot</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li><b>Red:</b> Incorrect sync/ dates don't match</li>
                <li><b>Gold:</b> Synced correctly/ Dates match, Tasks linked to Projects task</li>
                <li><b>Green:</b> Date updated to Actual in Tracking</li>
                <li><b>Grey:</b> Not updated to actuals</li>
              </ul>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Auto-sync rules</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li><b>Estimated project schedule (Contract):</b> End Date within the lower section of Stage/ sub-stage names syncs to the &quot;Contract column&quot; date on tracking page.</li>
                <li><b>In progress (Planned):</b> End Date of all Stage/ sub-stage rows auto sync to the &quot;Planned column&quot;.</li>
                <li><b>Stage completion (Actual):</b> Once all deliverables of a stage are completed, right click on the end date and select Update Actuals. Syncs to the &quot;Actual column&quot;.</li>
                <li><b>Invoicing stage (Invoice):</b> Once the invoice is sent and its date is added in tracking page, it will auto sync the invoicing start date in Timelines. Invoicing duration is read from payment terms as per contract.</li>
                <li><b>Closure of stage (Paid):</b> Once the invoice is paid and its date is added in tracking page, it will auto sync with the closure of stage.</li>
              </ul>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Project Tasks &amp; M.O.M</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li>Right click and select Add M.O.M, to add an M.O.M on a deliverable.</li>
                <li>Insert M.O.M to insert a new meeting.</li>
                <li>These options open an M.O.M builder with pre-existing templates.</li>
                <li>To add additional M.O.M's that don't need to be linked or added to timelines, use the separate M.O.M page.</li>
                <li>Deliverables recorded in M.O.M's and tasks on timelines can be added to project task page using the right click options.</li>
              </ul>

              <strong style={{ fontSize: '10px', display: 'block', marginTop: '12px', marginBottom: '4px' }}>Versions &amp; Exports</strong>
              <ul style={{ margin: '0 0 0 14px', padding: 0, listStyle: 'disc' }}>
                <li>Use the plus sign on the right side of the project dropdown menu to create or compare multiple versions. These are non editable.</li>
                <li>These can be exported from the settings list.</li>
              </ul>
            </div>
          )}
        </span>
      </div>

      {/* Full screen, toolbars folded: a slim strip keeps the arrow to open them and the exit. */}
      {canCollapseToolbars && toolbarsCollapsed && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', marginBottom: '8px' }}>
          <button
            onClick={() => setToolbarsCollapsed(false)}
            title="Show toolbars"
            aria-label="Show toolbars"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: BRIO_GREY, transition: 'color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}
          >
            <ChevronDown size={14} />
          </button>
          <button
            onClick={toggleFullscreen}
            title="Exit fullscreen"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: BRIO_GOLD }}
          >
            <Minimize2 size={13} />
          </button>
        </div>
      )}

      {/* Top toolbar: project dropdown + fullscreen (matching ProjectTasksPage layout) */}
      {!(canCollapseToolbars && toolbarsCollapsed) && (
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center',
        gap: '16px', marginBottom: '20px', marginTop: '8px',
        paddingBottom: '16px', borderBottom: '1px solid ' + BRIO_BORDER,
      }}>
        {/* Full screen: fold this bar and the table's legend toolbar away */}
        {canCollapseToolbars && (
          <button
            onClick={() => setToolbarsCollapsed(true)}
            title="Hide toolbars"
            aria-label="Hide toolbars"
            style={{ ...{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: BRIO_GREY, transition: 'color 0.15s' }, marginRight: '-8px' }}
            onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}
          >
            <ChevronUp size={14} />
          </button>
        )}
        {/* Project selector — hidden in template builder mode */}
        {!templateBuilderMode && <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '12px', fontWeight: 400, color: BRIO_GREY, whiteSpace: 'nowrap' }}>
            Project
          </label>
          <select
            value={selectedProjectId || ''}
            onChange={e => {
              const val = e.target.value ? Number(e.target.value) : null;
              selectProject(val);
              if (!val) changeViewMode('gantt');
              else changeViewMode('table');
            }}
            style={{
              fontFamily: 'Raleway, sans-serif',
              fontSize: '13px',
              fontWeight: 300,
              padding: '6px 28px 6px 10px',
              border: '1px solid ' + BRIO_BORDER,
              borderRadius: '0',
              minWidth: '280px',
              maxWidth: '420px',
              background: '#fff',
              color: '#333',
              backgroundImage: selectArrowSvg,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">Select a project…</option>
            {sortedProjects.map(p => (
              <option key={p.id} value={p.id}>
                {p.projectCode ? `${p.projectCode} — ` : ''}{p.name}
              </option>
            ))}
          </select>
          {/* Version buttons — shown when a project is selected */}
          {selectedProject && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              {/* Left scroll arrow — only visible when content is scrolled right */}
              {versionScrollState.canLeft && (
                <button
                  onClick={() => scrollVersions(-1)}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '18px', height: '22px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', background: '#fff', color: BRIO_GREY, cursor: 'pointer', transition: 'border-color 0.12s, color 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; }}
                >
                  <ChevronLeft size={11} />
                </button>
              )}
              {/* Scrollable version button strip — capped at 10 visible buttons (≈400px) */}
              <div
                ref={versionScrollRef}
                onScroll={updateVersionScroll}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'hidden', scrollbarWidth: 'none', maxWidth: '400px' }}
              >
                {projectVersions.map(v => {
                  const isActive = viewingVersion?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      title={`Created: ${new Date(v.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                      onClick={() => { setViewingVersion(isActive ? null : v); setTimeout(updateVersionScroll, 50); }}
                      onContextMenu={e => { e.preventDefault(); setVersionContextMenu({ versionId: v.id, x: e.clientX, y: e.clientY }); }}
                      style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '3px 7px', border: '1px solid ' + (isActive ? BRIO_GOLD : BRIO_BORDER), borderRadius: '2px', background: isActive ? BRIO_GOLD : '#fff', color: isActive ? '#fff' : BRIO_GREY, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'border-color 0.12s, color 0.12s, background 0.12s' }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; } }}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; } }}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
              {/* Right scroll arrow — only visible when more content is to the right */}
              {versionScrollState.canRight && (
                <button
                  onClick={() => scrollVersions(1)}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '18px', height: '22px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', background: '#fff', color: BRIO_GREY, cursor: 'pointer', transition: 'border-color 0.12s, color 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; }}
                >
                  <ChevronRight size={11} />
                </button>
              )}
              {/* Plus — save current timeline as new version */}
              <button
                onClick={async () => { await saveVersion(); setTimeout(updateVersionScroll, 100); }}
                title="Save current timeline as a new version"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '22px', height: '22px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', background: '#fff', color: BRIO_GREY, cursor: 'pointer', transition: 'border-color 0.12s, color 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; }}
              >
                <Plus size={11} />
              </button>
              {/* Eye — compare saved versions (works in normal mode and while viewing a snapshot) */}
              {projectVersions.length > 0 && (
                <button
                  onClick={() => {
                    if (viewingVersion) {
                      // User is inside a snapshot view — exit it and enter compare mode with that version
                      pendingCompareVersionRef.current = viewingVersion.id;
                      setViewingVersion(null);
                    } else {
                      compareModalTriggerRef.current?.();
                    }
                  }}
                  title="Compare saved versions"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '22px', height: '22px', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', background: '#fff', color: BRIO_GREY, cursor: 'pointer', transition: 'border-color 0.12s, color 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = BRIO_GREY; }}
                >
                  <GitCompare size={11} />
                </button>
              )}
            </div>
          )}
        </div>}
        {/* Template Builder button — shown only when no project is selected, left of fullscreen */}
        {!selectedProject && !templateBuilderMode && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => { setTemplateBuilderMode(true); setEditingTemplate(canEditTemplates ? null : (timelineTemplates[0] || null)); }}
              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '5px 14px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', transition: 'border-color 0.12s, color 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#555'; }}
            >
              <FileText size={12} /> Template Builder
            </button>
            {/* Fullscreen toggle */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: isFullscreen ? BRIO_GOLD : BRIO_GREY, transition: 'color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.color = isFullscreen ? BRIO_GOLD : BRIO_GREY; }}
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
        )}
        {/* Exit Builder button + fullscreen (when in template builder mode) */}
        {templateBuilderMode && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => { setTemplateBuilderMode(false); setEditingTemplate(null); }}
              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '4px 12px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', transition: 'border-color 0.12s, color 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#555'; }}
            >
              <ChevronLeft size={12} /> Exit Builder
            </button>
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: isFullscreen ? BRIO_GOLD : BRIO_GREY, transition: 'color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.color = isFullscreen ? BRIO_GOLD : BRIO_GREY; }}
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
        )}
        {/* Template dropdown + fullscreen (when project is selected, not in builder mode) */}
        {selectedProject && !templateBuilderMode && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* BRM's own template list (independent of the project templates): Create a template and
                Save this tab as template for can_edit_brm_templates(), then every template, applied to
                the tab on screen by anyone who can edit BRM. */}
            {isStandaloneTimeline(selectedProject) && !brmBuilder && (canManageTimeline || canEditBrmTemplates) && (
              <div ref={brmTemplateMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowBrmTemplateMenu(v => !v)}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '4px 10px', border: '1px solid ' + BRIO_BORDER, background: showBrmTemplateMenu ? BRIO_BG : '#fff', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#555'; }}
                >
                  <FileText size={11} /> Template <ChevronDown size={10} />
                </button>
                {showBrmTemplateMenu && (() => {
                  const item = { display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left', padding: '7px 14px', fontSize: '12px', fontWeight: 300, color: '#333', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' };
                  return (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 2000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '230px', fontFamily: 'Raleway, sans-serif' }}>
                      {canEditBrmTemplates && (
                        <>
                          <button style={{ ...item, fontWeight: 500, color: BRIO_GOLD }}
                            onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            onClick={() => { setShowBrmTemplateMenu(false); setViewMode('table'); setBrmBuilder({ key: Date.now(), template: null }); }}>
                            <Plus size={12} /> Create a template
                          </button>
                          <button style={item}
                            onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            onClick={() => { setShowBrmTemplateMenu(false); saveBrmTabAsTemplate(); }}>
                            <Copy size={11} /> Save this tab as a template
                          </button>
                        </>
                      )}
                      <div style={{ padding: '6px 14px 4px', fontSize: '10px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: canEditBrmTemplates ? '1px solid ' + BRIO_BORDER : 'none', borderBottom: '1px solid ' + BRIO_BORDER }}>
                        Templates
                      </div>
                      {brmTemplates.length === 0 && (
                        <div style={{ padding: '8px 14px', fontSize: '11px', fontWeight: 300, color: '#aaa', fontStyle: 'italic' }}>No templates yet</div>
                      )}
                      {brmTemplates.map(tpl => (
                        <div key={tpl.id} style={{ display: 'flex', alignItems: 'center' }}
                          onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                          <button style={{ ...item, flex: 1, minWidth: 0, cursor: canManageTimeline ? 'pointer' : 'default', color: canManageTimeline ? '#333' : '#999' }}
                            title={canManageTimeline ? 'Add to the tab on screen' : 'You can view this template but not add it to BRM'}
                            onClick={() => { if (!canManageTimeline) return; setShowBrmTemplateMenu(false); applyBrmTemplate(tpl); }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.name}</span>
                          </button>
                          <button
                            title={canEditBrmTemplates ? `Edit "${tpl.name}"` : `View "${tpl.name}"`}
                            onClick={() => { setShowBrmTemplateMenu(false); setViewMode('table'); setBrmBuilder({ key: Date.now(), template: tpl }); }}
                            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', alignSelf: 'stretch', border: 'none', borderLeft: '1px solid ' + BRIO_BORDER, background: 'transparent', cursor: 'pointer', color: BRIO_GREY }}
                            onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }} onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}>
                            {canEditBrmTemplates ? <Pencil size={12} /> : <Eye size={13} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Template dropdown — only when templates exist and user can edit. Not on a standalone
                timeline: templates are built around scope stages. */}
            {timelineTemplates.length > 0 && (isAdmin || isSupervisor || selSavesViaGrant) && !isStandaloneTimeline(selectedProject) && (
              <div ref={templateMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowTemplateMenu(v => !v)}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '4px 10px', border: '1px solid ' + BRIO_BORDER, background: showTemplateMenu ? BRIO_BG : '#fff', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', transition: 'border-color 0.12s, color 0.12s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#555'; }}
                >
                  <FileText size={11} />
                  <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {timelineTemplates.find(t => t.id === selectedProject?.stageTimeline?._appliedTemplateId)?.name || 'Template'}
                  </span>
                  <ChevronDown size={10} />
                </button>
                {showTemplateMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 2000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '220px', fontFamily: 'Raleway, sans-serif' }}>
                    <div style={{ padding: '6px 12px 4px', fontSize: '10px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid ' + BRIO_BORDER }}>
                      Saved Templates
                    </div>
                    {applyableTemplates.map(tpl => {
                      const isApplied = tpl.id === selectedProject?.stageTimeline?._appliedTemplateId;
                      return (
                      <div
                        key={tpl.id}
                        style={{ display: 'flex', alignItems: 'center', background: isApplied ? BRIO_BG : 'transparent', transition: 'background 0.1s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isApplied ? BRIO_BG : 'transparent'; }}
                      >
                        <button
                          title={tpl.info || undefined}
                          onClick={() => { setShowTemplateMenu(false); setApplyPickedStages(null); setApplyTemplateConfirm({ template: tpl }); }}
                          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', textAlign: 'left', padding: '7px 6px 7px 14px', fontSize: '12px', fontWeight: isApplied ? 500 : 300, color: isApplied ? BRIO_GOLD : '#333', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}
                        >
                          <span>{tpl.name}</span>
                          {isApplied && <Check size={12} style={{ flexShrink: 0 }} />}
                        </button>
                        {/* Preview — opens the template read only, before deciding to apply it */}
                        <button
                          title={`Preview "${tpl.name}"`}
                          aria-label={`Preview ${tpl.name}`}
                          onClick={() => { setShowTemplateMenu(false); setPreviewTemplate(tpl); }}
                          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '30px', alignSelf: 'stretch', border: 'none', borderLeft: '1px solid ' + BRIO_BORDER, background: 'transparent', cursor: 'pointer', color: BRIO_GREY, transition: 'color 0.1s' }}
                          onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
                          onMouseLeave={e => { e.currentTarget.style.color = BRIO_GREY; }}
                        >
                          <Eye size={13} />
                        </button>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', borderRadius: '3px', background: 'transparent', cursor: 'pointer', color: isFullscreen ? BRIO_GOLD : BRIO_GREY, transition: 'color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = BRIO_GOLD; }}
              onMouseLeave={e => { e.currentTarget.style.color = isFullscreen ? BRIO_GOLD : BRIO_GREY; }}
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
        )}
      </div>
      )}

      {/* ================================================================ */}
      {/* VERSION TABLE PREVIEW (inline, replaces table/gantt when active) */}
      {/* ================================================================ */}
      {viewingVersion ? (
        <VersionTablePreview version={viewingVersion} onClose={() => setViewingVersion(null)} />
      ) : (
      <>
      {/* ================================================================ */}
      {/* TEMPLATE BUILDER MODE                                            */}
      {/* ================================================================ */}
      {templateBuilderMode && (
        <>
          {/* Template picker strip — shown above the editor */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', fontWeight: 400, color: BRIO_GREY, whiteSpace: 'nowrap' }}>Saved Templates</span>
            {!canEditTemplates && (
              <span title="Templates can be edited by admins and named template authors only" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', border: '1px solid ' + BRIO_BORDER, background: BRIO_BG }}>
                <Eye size={10} /> Read only
              </span>
            )}
            {timelineTemplates.length === 0 ? (
              <span style={{ fontSize: '11px', fontWeight: 300, color: '#bbb' }}>No saved templates yet</span>
            ) : timelineTemplates.map(tpl => {
              const isActive = editingTemplate?.id === tpl.id;
              return (
                <button
                  key={tpl.id}
                  draggable={canEditTemplates}
                  onClick={() => setEditingTemplate(isActive && canEditTemplates ? null : tpl)}
                  {...(canEditTemplates ? {
                    onDragStart: e => { draggedTplIdRef.current = tpl.id; e.dataTransfer.effectAllowed = 'move'; e.currentTarget.style.opacity = '0.4'; },
                    onDragEnd: e => { e.currentTarget.style.opacity = '1'; draggedTplIdRef.current = null; },
                    onDragOver: e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
                    onDrop: e => { e.preventDefault(); reorderTemplates(draggedTplIdRef.current, tpl.id); draggedTplIdRef.current = null; },
                  } : {})}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: isActive ? 500 : 300, padding: '4px 12px', border: '1px solid ' + (isActive ? BRIO_GOLD : BRIO_BORDER), background: isActive ? BRIO_GOLD : '#fff', color: isActive ? '#fff' : '#444', cursor: canEditTemplates ? 'grab' : 'pointer', whiteSpace: 'nowrap', transition: 'all 0.12s' }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#444'; } }}
                >
                  {tpl.name}
                  {tpl.builtIn && <span style={{ marginLeft: '5px', fontSize: '9px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.7 }}>Default</span>}
                </button>
              );
            })}
            {canEditTemplates && (
            <button
              onClick={() => setEditingTemplate(null)}
              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: editingTemplate === null ? 500 : 300, padding: '4px 12px', border: '1px solid ' + (editingTemplate === null ? BRIO_GOLD : BRIO_BORDER), background: editingTemplate === null ? BRIO_GOLD : '#fff', color: editingTemplate === null ? '#fff' : '#444', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              + New Template
            </button>
            )}
            {/* "Create from" — start a new template seeded from (and linked to) an existing one */}
            {timelineTemplates.length > 0 && canEditTemplates && (
              <div ref={createFromMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowCreateFromMenu(v => !v)}
                  style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, padding: '4px 12px', border: '1px solid ' + BRIO_BORDER, background: showCreateFromMenu ? BRIO_BG : '#fff', color: '#444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = BRIO_GOLD; e.currentTarget.style.color = BRIO_GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BRIO_BORDER; e.currentTarget.style.color = '#444'; }}
                >
                  Create from <ChevronDown size={10} />
                </button>
                {showCreateFromMenu && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 2000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '220px', maxHeight: '320px', overflowY: 'auto', fontFamily: 'Raleway, sans-serif' }}>
                    <div style={{ padding: '6px 12px 4px', fontSize: '10px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid ' + BRIO_BORDER }}>
                      Base template
                    </div>
                    {timelineTemplates.map(tpl => (
                      <button
                        key={tpl.id}
                        onClick={() => { setShowCreateFromMenu(false); handleCreateFromTemplate(tpl); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 14px', fontSize: '12px', fontWeight: 300, color: '#333', background: 'transparent', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.background = BRIO_BG; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {!canEditTemplates && !editingTemplate ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: '12px', fontWeight: 300, color: BRIO_GREY, border: '1px dashed ' + BRIO_BORDER }}>
              {timelineTemplates.length === 0 ? 'No saved templates yet.' : 'Select a template above to view it.'}
            </div>
          ) : (
          <TemplateBuilderView
            key={(editingTemplate?.id || 'new') + (editingTemplate?.builtIn ? ':builtin' : ':saved') + (canEditTemplates ? '' : ':ro')}
            template={editingTemplate}
            sowTemplates={scopeTemplatesHook?.templates || []}
            timelineTemplates={timelineTemplates}
            onSave={handleSaveTemplate}
            onCreate={handleCreateTemplate}
            onDelete={handleDeleteTemplate}
            onExit={() => { setTemplateBuilderMode(false); setEditingTemplate(null); }}
            stages={sortedStages}
            supabaseProjects={supabaseProjects}
            isAdmin={isAdmin}
            isSupervisor={isSupervisor}
            activeUsers={activeUsers}
            activeClients={activeClients}
            pageZoomIndex={pageZoomIndex}
            setPageZoomIndex={setPageZoomIndex}
            templateHidden={editingTemplate ? hiddenTemplateIds.has(editingTemplate.id) : false}
            onToggleTemplateHidden={toggleTemplateHidden}
            canEditTemplates={canEditTemplates}
          />
          )}
        </>
      )}

      {/* ================================================================ */}
      {/* TABLE VIEW (default) — per-project stage timeline editor         */}
      {/* ================================================================ */}
      {!templateBuilderMode && viewMode === 'table' && brmBuilder && isStandaloneTimeline(selectedProject) && (
        <BrmTemplateBuilderView
          key={brmBuilder.key}
          template={brmBuilder.template}
          brmTimeline={selectedProject.stageTimeline}
          canEditTemplates={canEditBrmTemplates}
          onSave={saveBrmTemplate}
          onDelete={deleteBrmTemplate}
          onExit={() => setBrmBuilder(null)}
          stages={sortedStages}
          isAdmin={isAdmin}
          isSupervisor={isSupervisor}
          currentUser={currentUser}
          activeUsers={activeUsers}
          activeClients={activeClients}
          pageZoomIndex={pageZoomIndex}
          setPageZoomIndex={setPageZoomIndex}
        />
      )}
      {!templateBuilderMode && viewMode === 'table' && !(brmBuilder && isStandaloneTimeline(selectedProject)) && (
        selectedProject ? (
          <TimelineEditorBoundary key={selectedProject.id}>
          <ProjectTimelineEditor
            project={selectedProject}
            stages={sortedStages}
            supabaseProjects={supabaseProjects}
            invoicesHook={invoicesHook}
            undoGroupRef={templateUndoGroupRef}
            canGrantTimelineAccess={canGrantTimelineAccess}
            isAdmin={isAdmin}
            isSupervisor={isSupervisor}
            currentUser={currentUser}
            activeUsers={activeUsers}
            allocations={allocations}
            teams={activeTeams}
            teamProjects={activeTeamProjects}
            activeClients={activeClients}
            contractScopesHook={contractScopesHook}
            showSettings={showSettings}
            setShowSettings={setShowSettings}
            showPresetSettings={showPresetSettings}
            setShowPresetSettings={setShowPresetSettings}
            viewMode={viewMode}
            setViewMode={changeViewMode}
            showTabBar={showTabBar}
            setShowTabBar={setShowTabBar}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            toolbarsCollapsed={canCollapseToolbars && toolbarsCollapsed}
            zoomIndex={zoomIndex}
            setZoomIndex={setZoomIndex}
            pageZoomIndex={pageZoomIndex}
            setPageZoomIndex={setPageZoomIndex}
            onNavigateToTracking={onNavigateToTracking}
            projectVersions={projectVersions}
            compareModalTriggerRef={compareModalTriggerRef}
            pendingCompareVersionRef={pendingCompareVersionRef}
            linkSowTemplate={projectSowTemplate}
          />
          </TimelineEditorBoundary>
        ) : (
          <div style={{ padding: '48px 0', textAlign: 'center', color: BRIO_GREY }}>
            <p style={{ fontSize: '13px', fontWeight: 300 }}>Select a project above to view or edit its stage timeline.</p>
          </div>
        )
      )}

      {/* ================================================================ */}
      {/* GANTT VIEW                                                       */}
      {/* ================================================================ */}
      {!templateBuilderMode && viewMode === 'gantt' && (
        <>
          {/* Gantt toolbar — matches table toolbar when project selected */}
          {selectedProject ? (
            <div style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', gap: '2px', marginBottom: '8px' }}
              onClick={() => { setShowGanttSettingsMenu(false); setShowGanttDateFilterMenu(false); }}>
              {/* Icon-only Table / Gantt toggle */}
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid ' + BRIO_BORDER }}>
                <button onClick={() => changeViewMode('table')}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', borderRight: '1px solid ' + BRIO_BORDER, cursor: 'pointer', background: 'transparent', color: BRIO_GREY }}
                  title="Table view">
                  <Table size={12} />
                </button>
                <button
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '24px', border: 'none', cursor: 'default', background: BRIO_GOLD, color: '#fff' }}
                  title="Gantt chart view">
                  <BarChart3 size={12} />
                </button>
              </div>
              {/* Spacer */}
              <div style={{ flex: 1 }} />
              {/* Right-side icons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {/* Tabs (stage-filter views) toggle — only when the project has tabs; shares the
                    active tab with the table view and filters the gantt to match. */}
                {projectTabs.length > 0 && (
                  <ToolbarIcon
                    onClick={() => { const nv = !showTabBar; setShowTabBar(nv); persistTabViewSetting({ showTabBar: nv }); }}
                    tooltip={showTabBar ? 'Hide tabs' : 'Show tabs'}
                    active={showTabBar}
                  >
                    <Layers size={12} />
                  </ToolbarIcon>
                )}
                {/* Date filter — target / actual / both */}
                <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                  <ToolbarIcon
                    onClick={() => setShowGanttDateFilterMenu(v => !v)}
                    tooltip="Filter dates"
                    active={ganttDateFilter !== 'both' || showGanttDateFilterMenu}
                  >
                    <Filter size={12} />
                  </ToolbarIcon>
                  {showGanttDateFilterMenu && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '150px', fontFamily: 'Raleway, sans-serif', borderRadius: '3px' }}>
                      {[
                        { key: 'both',   label: 'Target + Actual' },
                        { key: 'target', label: 'Target dates only' },
                        { key: 'actual', label: 'Actual dates only' },
                      ].map(opt => (
                        <button key={opt.key} onClick={() => { setGanttDateFilter(opt.key); setShowGanttDateFilterMenu(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: ganttDateFilter === opt.key ? 'rgba(159,144,42,0.08)' : 'transparent', fontSize: '11px', fontWeight: ganttDateFilter === opt.key ? 500 : 300, color: ganttDateFilter === opt.key ? BRIO_GOLD : '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                          onMouseEnter={e => { if (ganttDateFilter !== opt.key) e.currentTarget.style.background = BRIO_BG; }}
                          onMouseLeave={e => { if (ganttDateFilter !== opt.key) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {ganttDateFilter === opt.key && <Check size={10} />}{opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <ToolbarIcon onClick={() => setGanttZoomIndex(i => Math.min(i + 1, GANTT_ZOOM_LEVELS.length - 1))} tooltip="Zoom out"><ZoomOut size={12} /></ToolbarIcon>
                <ZoomResetLabel zoomLabel={GANTT_ZOOM_LEVELS[ganttZoomIndex].label} onReset={() => setGanttZoomIndex(GANTT_ZOOM_DEFAULT_INDEX)} />
                <ToolbarIcon onClick={() => setGanttZoomIndex(i => Math.max(i - 1, 0))} tooltip="Zoom in"><ZoomIn size={12} /></ToolbarIcon>
                {/* Settings gear — same structure as ProjectTasksPage */}
                <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                  <ToolbarIcon onClick={() => setShowGanttSettingsMenu(v => !v)} tooltip="Settings" active={showGanttSettingsMenu}>
                    <Settings2 size={12} />
                  </ToolbarIcon>
                  {showGanttSettingsMenu && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '170px', fontFamily: 'Raleway, sans-serif', borderRadius: '3px' }}>
                      {canEdit && (
                        <button onClick={() => { setShowSettings(true); setShowGanttSettingsMenu(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Settings2 size={11} /> Dependency Settings
                        </button>
                      )}
                      {(isAdmin || ganttIsAssignedSupervisor || canGrantTimelineAccess) && selectedProject && (
                        <button onClick={() => { setShowGanttSettingsMenu(false); setShowGanttGiveAccess(true); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Users size={11} /> Give access
                        </button>
                      )}
                      <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', borderTop: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Download size={9} /> Export
                      </div>
                      {[
                        { label: 'Export as PDF', icon: <FileText size={11} />, action: handleGanttExportPdf },
                        { label: 'Export as Excel', icon: <FileSpreadsheet size={11} />, action: handleGanttExportExcel },
                        { label: 'Export as CSV', icon: <File size={11} />, action: handleGanttExportCsv },
                      ].map(item => (
                        <button key={item.label} onClick={item.action}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {item.icon}{item.label}
                        </button>
                      ))}
                      <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', borderTop: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Upload size={9} /> Import
                      </div>
                      {[
                        { label: 'Import from Excel', icon: <FileSpreadsheet size={11} />, accept: '.xlsx,.xls' },
                        { label: 'Import from CSV', icon: <File size={11} />, accept: '.csv' },
                      ].map(item => (
                        <button key={item.label}
                          onClick={() => { setShowGanttSettingsMenu(false); if (ganttImportFileRef.current) { ganttImportFileRef.current.accept = item.accept; ganttImportFileRef.current.click(); } }}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {item.icon}{item.label}
                        </button>
                      ))}
                      {(isAdmin || (isSupervisor && canEdit)) && selectedProject && (
                        <>
                          <div style={{ height: '1px', background: BRIO_BORDER, margin: '4px 0' }} />
                          <button
                            onClick={() => { setShowGanttSettingsMenu(false); setShowDeleteTimelineConfirm(true); }}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 400, color: BRIO_RED, cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fdf2f2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <RotateCcw size={11} /> Reset Timeline
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Multi-project toolbar — matches frameless icon style */
            <div style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', gap: '2px', marginBottom: '8px' }}>
              {/* Left: stats */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '12px', fontWeight: 300, color: BRIO_GREY }}>
                  {ganttDisplayRows.length} project{ganttDisplayRows.length !== 1 ? 's' : ''} with timeline data
                </span>
                {overdueCount > 0 && (
                  <span style={{ fontSize: '12px', fontWeight: 400, color: BRIO_RED }}>
                    {overdueCount} overdue stage{overdueCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {/* Spacer */}
              <div style={{ flex: 1 }} />
              {/* Right: frameless icons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <ToolbarIcon onClick={() => setMultiGanttZoomIndex(i => Math.min(i + 1, GANTT_ZOOM_LEVELS.length - 1))} tooltip="Zoom out"><ZoomOut size={12} /></ToolbarIcon>
                <ZoomResetLabel zoomLabel={GANTT_ZOOM_LEVELS[multiGanttZoomIndex].label} onReset={() => setMultiGanttZoomIndex(GANTT_ZOOM_DEFAULT_INDEX)} />
                <ToolbarIcon onClick={() => setMultiGanttZoomIndex(i => Math.max(i - 1, 0))} tooltip="Zoom in"><ZoomIn size={12} /></ToolbarIcon>
                {/* Settings gear — export overview data, same pattern as ProjectTasksPage */}
                <div ref={multiExportMenuRef} style={{ position: 'relative' }}>
                  <ToolbarIcon onClick={() => setShowMultiExportMenu(v => !v)} tooltip="Settings" active={showMultiExportMenu}>
                    <Settings2 size={12} />
                  </ToolbarIcon>
                  {showMultiExportMenu && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '170px', fontFamily: 'Raleway, sans-serif', borderRadius: '3px' }}>
                      <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Download size={9} /> Export
                      </div>
                      {[
                        { label: 'Export as PDF', icon: <FileText size={11} />, action: handleMultiExportPdf },
                        { label: 'Export as Excel', icon: <FileSpreadsheet size={11} />, action: handleMultiExportExcel },
                        { label: 'Export as CSV', icon: <File size={11} />, action: handleMultiExportCsv },
                      ].map(item => (
                        <button key={item.label} onClick={item.action}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 14px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', textAlign: 'left', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {item.icon}{item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab bar (gantt) — stage/part filter views, shared with the table view. The gantt is
              filtered to the active tab; tabs are created/edited from the table view. */}
          {showTabBar && projectTabs.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 8px', border: '1px solid ' + BRIO_BORDER, background: BRIO_BG, flexWrap: 'wrap', fontFamily: 'Raleway, sans-serif', marginBottom: '8px' }}>
              {[{ id: '__all__', name: mainTabName(selectedProject?.stageTimeline), fixed: true }, ...projectTabs].map(t => {
                const isActive = activeTabId === t.id;
                return (
                  <div key={t.id}
                    onClick={() => { setActiveTabId(t.id); persistTabViewSetting({ activeTabId: t.id }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', fontSize: '11px', fontWeight: isActive ? 600 : 400, color: isActive ? '#fff' : '#555', background: isActive ? BRIO_GOLD : '#fff', border: '1px solid ' + (isActive ? BRIO_GOLD : BRIO_BORDER), borderRadius: '3px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <span>{t.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <TimelineLegend />

          {/* Gantt chart */}
          {selectedProject ? (
            /* Per-project view — flat list of all stages and deliverables */
            perProjectGanttRows.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: BRIO_GREY }}>
                <p style={{ fontSize: '13px', fontWeight: 300 }}>No timeline data for this project.</p>
                <p style={{ fontSize: '12px', fontWeight: 300, marginTop: '4px' }}>Add stage dates in the Table view to see them here.</p>
              </div>
            ) : perProjectDisplayRows.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: BRIO_GREY }}>
                <p style={{ fontSize: '13px', fontWeight: 300 }}>No stages in this tab.</p>
                <p style={{ fontSize: '12px', fontWeight: 300, marginTop: '4px' }}>Pick another tab above, or choose stages for this tab in the Table view.</p>
              </div>
            ) : (
              <div style={{ zoom: PAGE_ZOOM_LEVELS[pageZoomIndex].scale }}>
                <div ref={scrollContainerRef} style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: `calc(100vh - ${isFullscreen ? 150 : 320}px)`, border: '1px solid ' + BRIO_BORDER, background: '#fff' }}>
                  <div style={{ minWidth: GANTT_LEFT_W + totalWidth }}>
                    <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, background: '#fff' }}>
                      <div style={{ width: GANTT_LEFT_W + 'px', minWidth: GANTT_LEFT_W + 'px', display: 'flex', alignItems: 'stretch', borderRight: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG, position: 'sticky', left: 0, zIndex: 4, fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: BRIO_GREY }}>
                        <div style={{ width: '32px', minWidth: '32px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid ' + BRIO_BORDER }}>#</div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '8px 12px' }}>Task Name</div>
                        <div style={{ width: GANTT_DEP_W + 'px', minWidth: GANTT_DEP_W + 'px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 4px', borderLeft: '1px solid ' + BRIO_BORDER }} />
                      </div>
                      <div style={{ flex: 1, width: totalWidth }}>
                        <TimelineHeader rangeStart={rangeStart} rangeEnd={rangeEnd} pxPerDay={pxPerDay} durationDays={durationDays} />
                      </div>
                    </div>
                    <PerProjectGanttChart
                      rows={perProjectDisplayRows}
                      rangeStart={rangeStart}
                      pxPerDay={pxPerDay}
                      totalWidth={totalWidth}
                      collapsedIds={ganttCollapsedStages}
                      onToggle={(rowId) => setGanttCollapsedStages(prev => {
                        const next = new Set(prev);
                        if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
                        return next;
                      })}
                      canDrag={canEdit}
                      onBarDrag={handleGanttBarDrag}
                      showDeps={true}
                      depDisplayFor={ganttDepDisplay}
                      onDepChange={canEdit ? handleGanttDependencyChange : undefined}
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Multi-project view */
            ganttDisplayRows.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: BRIO_GREY }}>
                {!isManager ? (
                  <p style={{ fontSize: '13px', fontWeight: 300 }}>Select a project above to view its timeline.</p>
                ) : (
                  <>
                    <p style={{ fontSize: '13px', fontWeight: 300 }}>No projects with timeline data found.</p>
                    <p style={{ fontSize: '12px', fontWeight: 300, marginTop: '4px' }}>Add stage dates in the Table view to see them here.</p>
                  </>
                )}
              </div>
            ) : (
              <div style={{ zoom: PAGE_ZOOM_LEVELS[pageZoomIndex].scale }}>
                <div ref={scrollContainerRef} style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: `calc(100vh - ${isFullscreen ? 150 : 320}px)`, border: '1px solid ' + BRIO_BORDER, background: '#fff' }}>
                  <div style={{ minWidth: 240 + totalWidth }}>
                    <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, background: '#fff' }}>
                      <div style={{ width: '240px', minWidth: '240px', padding: '8px 12px', borderRight: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: BRIO_GREY, display: 'flex', alignItems: 'center', background: BRIO_BG, position: 'sticky', left: 0, zIndex: 4 }}>
                        Project
                      </div>
                      <div style={{ flex: 1, width: totalWidth }}>
                        <TimelineHeader rangeStart={rangeStart} rangeEnd={rangeEnd} pxPerDay={pxPerDay} durationDays={durationDays} />
                      </div>
                    </div>
                    {ganttDisplayRows.map(project => (
                      <ProjectRow key={project.id} project={project} rangeStart={rangeStart} pxPerDay={pxPerDay} totalWidth={totalWidth} isExpanded={expandedIds.has(project.id)} onToggle={() => toggleProject(project.id)} activeUsers={activeUsers} />
                    ))}
                  </div>
                </div>
              </div>
            )
          )}
        </>
      )}
      </>
      )}
      {/* Hidden file input for gantt import — triggers table view switch for actual editing */}
      <input ref={ganttImportFileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
        onChange={() => { changeViewMode('table'); }} />

      {/* Template preview modal — read only, opened from the eye icon in the apply dropdown. Renders
          the same builder view (table and Gantt) with every write control removed; Apply hands over
          to the normal apply confirmation below. */}
      {previewTemplate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          onClick={() => setPreviewTemplate(null)}>
          <div style={{ background: '#fff', width: '100%', maxWidth: '1400px', height: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 20px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Eye size={15} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewTemplate.name}</span>
                <span style={{ fontSize: '10px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', border: '1px solid ' + BRIO_BORDER, background: '#fff', flexShrink: 0 }}>Preview · read only</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {selectedProject && (
                  <button
                    onClick={() => { const tpl = previewTemplate; setPreviewTemplate(null); setApplyPickedStages(null); setApplyTemplateConfirm({ template: tpl }); }}
                    style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500, padding: '5px 14px', border: '1px solid ' + BRIO_GOLD, background: BRIO_GOLD, color: '#fff', cursor: 'pointer' }}
                  >Apply…</button>
                )}
                <button onClick={() => setPreviewTemplate(null)} title="Close preview" style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                  <X size={15} style={{ color: '#aaa' }} />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px' }}>
              <TemplateBuilderView
                key={previewTemplate.id + ':preview'}
                template={previewTemplate}
                sowTemplates={scopeTemplatesHook?.templates || []}
                timelineTemplates={timelineTemplates}
                onSave={() => {}}
                onCreate={() => {}}
                onDelete={() => {}}
                onExit={() => setPreviewTemplate(null)}
                stages={sortedStages}
                supabaseProjects={supabaseProjects}
                isAdmin={false}
                isSupervisor={false}
                activeUsers={activeUsers}
                activeClients={activeClients}
                pageZoomIndex={pageZoomIndex}
                setPageZoomIndex={setPageZoomIndex}
                canEditTemplates={false}
                compactHeader
              />
            </div>
          </div>
        </div>
      )}

      {/* Apply Template confirmation modal */}
      {applyTemplateConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setApplyTemplateConfirm(null)}>
          <div style={{ background: '#fff', width: '440px', maxWidth: '95vw', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={15} style={{ color: BRIO_GOLD }} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Apply Template</span>
              </div>
              <button onClick={() => setApplyTemplateConfirm(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                <X size={15} style={{ color: '#aaa' }} />
              </button>
            </div>
            <div style={{ padding: '20px 20px 8px' }}>
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#333', lineHeight: 1.6, margin: 0 }}>
                Apply template <strong style={{ fontWeight: 500 }}>&ldquo;{applyTemplateConfirm.template.name}&rdquo;</strong> to <strong style={{ fontWeight: 500 }}>{selectedProject?.name}</strong>, filtered to the stages your Scope of Work has selected.{!applyTemplateConfirm.template.sowTemplateId && <> This template has no Scope of Work linked, so only its own tasks are added: no SOW deliverables are added, linked or flagged.</>} Choose how to handle this project&rsquo;s existing tasks:
              </p>
              <ul style={{ fontSize: '12px', fontWeight: 300, color: '#555', margin: '10px 0 0', paddingLeft: '18px', lineHeight: 1.6 }}>
                <li><strong style={{ fontWeight: 600 }}>Preserve task list</strong> — keep this project&rsquo;s own (non-SOW) tasks, moved under a <strong style={{ fontWeight: 500 }}>&ldquo;Preserved task list&rdquo;</strong> group below each stage&rsquo;s start row (indents and durations kept, dependencies dropped). Use this for a project with a hand-built timeline.</li>
                <li><strong style={{ fontWeight: 600 }}>Ignore</strong> — replace the timeline with the template&rsquo;s tasks as authored, discarding this project&rsquo;s previous tasks.</li>
              </ul>
              <p style={{ fontSize: '11px', fontWeight: 300, color: '#888', marginTop: '10px', marginBottom: 0, lineHeight: 1.5 }}>
                Either way, tracking &amp; invoicing dates, custom columns and view settings are kept on matching rows, and this can be reversed with Settings&nbsp;&rarr;&nbsp;Undo.
              </p>
              {/* Stage picker — apply the template only to the chosen stages; the rest stay untouched. */}
              {applyStageOptions.length > 0 && (
                <div style={{ marginTop: '14px', borderTop: '1px solid ' + BRIO_BORDER, paddingTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Apply to stages</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => setApplyPickedStages(null)}
                        style={{ fontSize: '10px', fontWeight: 400, padding: '2px 8px', border: '1px solid ' + BRIO_BORDER, background: applyPickedStages === null ? BRIO_BG : '#fff', color: '#555', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>All</button>
                      <button onClick={() => setApplyPickedStages(new Set())}
                        style={{ fontSize: '10px', fontWeight: 400, padding: '2px 8px', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: '#555', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>None</button>
                    </div>
                  </div>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px' }}>
                    {applyStageOptions.map(st => {
                      const checked = applyPickedStages === null || applyPickedStages.has(st.key);
                      return (
                        <label key={st.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 300, color: '#333' }}
                          onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <input type="checkbox" checked={checked}
                            onChange={() => setApplyPickedStages(prev => {
                              const base = prev === null ? new Set(applyStageOptions.map(s => s.key)) : new Set(prev);
                              if (base.has(st.key)) base.delete(st.key); else base.add(st.key);
                              return base.size === applyStageOptions.length ? null : base;
                            })}
                            style={{ accentColor: BRIO_GOLD, cursor: 'pointer' }} />
                          <span>{st.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p style={{ fontSize: '11px', fontWeight: 300, color: '#888', marginTop: '6px', marginBottom: 0, lineHeight: 1.5 }}>
                    {applyPickedStages === null
                      ? 'Applies to all your SOW-selected stages. Uncheck any to leave them untouched.'
                      : (applyPickedStages.size === 0
                        ? 'Select at least one stage to apply the template to.'
                        : `Only the ${applyPickedStages.size} selected stage${applyPickedStages.size === 1 ? '' : 's'} will change; the rest stay as they are.`)}
                  </p>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 20px', borderTop: '1px solid ' + BRIO_BORDER }}>
              <button onClick={() => setApplyTemplateConfirm(null)}
                style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#555' }}>
                Cancel
              </button>
              <button disabled={applyPickedStages !== null && applyPickedStages.size === 0}
                onClick={() => handleApplyTemplate(applyTemplateConfirm.template, false, applyPickedStages)}
                style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: (applyPickedStages !== null && applyPickedStages.size === 0) ? 'not-allowed' : 'pointer', fontFamily: 'Raleway, sans-serif', color: '#333', opacity: (applyPickedStages !== null && applyPickedStages.size === 0) ? 0.5 : 1 }}>
                Ignore
              </button>
              <button disabled={applyPickedStages !== null && applyPickedStages.size === 0}
                onClick={() => handleApplyTemplate(applyTemplateConfirm.template, true, applyPickedStages)}
                style={{ padding: '7px 16px', fontSize: '12px', fontWeight: 500, border: 'none', background: BRIO_GOLD, color: '#fff', cursor: (applyPickedStages !== null && applyPickedStages.size === 0) ? 'not-allowed' : 'pointer', fontFamily: 'Raleway, sans-serif', opacity: (applyPickedStages !== null && applyPickedStages.size === 0) ? 0.5 : 1 }}>
                Preserve task list
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Template — post-apply summary (SOW divergence + undo) */}
      {applyResult && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setApplyResult(null)}>
          <div style={{ background: '#fff', width: '470px', maxWidth: '95vw', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid ' + BRIO_GOLD, background: BRIO_BG }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={15} style={{ color: BRIO_GOLD }} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Template applied</span>
              </div>
              <button onClick={() => setApplyResult(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                <X size={15} style={{ color: '#aaa' }} />
              </button>
            </div>
            <div style={{ padding: '18px 20px 6px' }}>
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#333', lineHeight: 1.6, margin: 0 }}>
                Applied <strong style={{ fontWeight: 500 }}>&ldquo;{applyResult.templateName}&rdquo;</strong> to <strong style={{ fontWeight: 500 }}>{selectedProject?.name}</strong>, filtered to this project&rsquo;s Scope of Work.
              </p>
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {applyResult.addedCount > 0 && (
                  <div style={{ fontSize: '12.5px', fontWeight: 300, color: '#3a5d2a', background: '#eef4e9', border: '1px solid #cfe0c2', padding: '8px 11px', lineHeight: 1.5 }}>
                    <div style={{ marginBottom: (applyResult.addedRows && applyResult.addedRows.length) ? '9px' : 0 }}>
                      <strong style={{ fontWeight: 600 }}>{applyResult.addedCount}</strong> deliverable{applyResult.addedCount === 1 ? '' : 's'} from this project&rsquo;s SOW {applyResult.addedCount === 1 ? 'was' : 'were'} <strong style={{ fontWeight: 600 }}>added as {applyResult.addedCount === 1 ? 'a task' : 'tasks'}</strong> that no template task covered — each carries a <strong style={{ fontWeight: 600 }}>&ldquo;new&rdquo;</strong> badge.
                    </div>
                    {applyResult.addedRows && applyResult.addedRows.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '210px', overflowY: 'auto' }}>
                        {applyResult.addedRows.map(r => (
                          <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', background: '#fff', border: '1px solid #d8e6cd', borderRadius: '2px', padding: '4px 5px 4px 9px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                              <span style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#6b7d53', background: '#eef4e9', border: '1px solid #cfe0c2', borderRadius: '2px', padding: '1px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>{STAGE_NAMES[r.stageKey] || r.stageKey}</span>
                              <span style={{ fontSize: '12px', fontWeight: 300, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label || '(unnamed deliverable)'}</span>
                            </span>
                            <button onClick={() => removeAddedDeliverable(r.id)}
                              title="Remove — don't add this deliverable as a task"
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#b05050', fontSize: '16px', lineHeight: 1, padding: '0 3px', flexShrink: 0 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p style={{ fontSize: '11px', fontWeight: 300, color: '#888', marginTop: '12px', marginBottom: 0, lineHeight: 1.5 }}>
                Click <strong style={{ fontWeight: 500 }}>×</strong> next to a deliverable to drop it — it won&rsquo;t be added as a task. Or click a “new” badge → Link to SOW to change its link. To reverse the whole apply, use Settings → <strong style={{ fontWeight: 500 }}>Undo</strong> (or Ctrl+Z).
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '16px 20px', borderTop: '1px solid ' + BRIO_BORDER }}>
              <button onClick={undoApplyTemplate}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 400, border: '1px solid #e0c0c0', background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#b05050' }}>
                Undo apply
              </button>
              <button onClick={() => setApplyResult(null)}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 500, border: 'none', background: BRIO_GOLD, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete / Reset Timeline confirmation modal */}
      {showDeleteTimelineConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowDeleteTimelineConfirm(false)}>
          <div style={{ background: '#fff', width: '420px', maxWidth: '95vw', fontFamily: 'Raleway, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid ' + BRIO_BORDER }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '2px solid ' + BRIO_RED, background: BRIO_BG }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RotateCcw size={15} style={{ color: BRIO_RED }} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>Reset Timeline</span>
              </div>
              <button onClick={() => setShowDeleteTimelineConfirm(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px' }}>
                <X size={15} style={{ color: '#aaa' }} />
              </button>
            </div>
            <div style={{ padding: '20px 20px 8px' }}>
              <p style={{ fontSize: '13px', fontWeight: 300, color: '#333', lineHeight: 1.6, margin: 0 }}>
                This reverts <strong>{selectedProject?.name}</strong>&rsquo;s timeline to its original scope-driven stages and <strong>removes any applied template</strong> — template / custom-added tasks, manual planned dates and task dependencies are cleared, and start-of-stage links return to their default presets.
              </p>
              <p style={{ fontSize: '12px', fontWeight: 300, color: '#555', marginTop: '10px', marginBottom: 0, lineHeight: 1.6 }}>
                Tracking &amp; invoicing data (contract / planned / actual / invoice / paid dates, invoice %, billing and part stages), custom columns and their values, view settings and settings presets are kept.
              </p>
              <p style={{ fontSize: '12px', fontWeight: 300, color: BRIO_RED, marginTop: '10px', marginBottom: 0 }}>
                This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 20px', borderTop: '1px solid ' + BRIO_BORDER }}>
              <button onClick={() => setShowDeleteTimelineConfirm(false)}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 400, border: '1px solid ' + BRIO_BORDER, background: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif', color: '#555' }}>
                Cancel
              </button>
              <button onClick={handleDeleteTimeline}
                style={{ padding: '7px 18px', fontSize: '12px', fontWeight: 500, border: 'none', background: BRIO_RED, color: '#fff', cursor: 'pointer', fontFamily: 'Raleway, sans-serif' }}>
                Reset Timeline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Give Access modal — Gantt view. Lists ALL active users. Allocated people
          default to Read only; a supervisor assigned to the project defaults to Full.
          Admin rows are locked to Full, and only an admin (operatorIsAdmin) can change
          a supervisor's access. */}
      {showGanttGiveAccess && selectedProject && (() => {
        const tl = selectedProject.stageTimeline || {};
        const activeUsersList = (activeUsers || []).filter(u => u.isActive !== false && u.status !== 'inactive');
        const assignedIds = allocatedUserIdsForProject(allocations, selectedProject.id);
        const supervisorIds = supervisorIdsForProject(selectedProject, activeTeams, activeTeamProjects);
        return (
          <GiveAccessModal
            users={activeUsersList}
            grants={tl._grantedAccess}
            assignedIds={assignedIds}
            supervisorIds={supervisorIds}
            operatorIsAdmin={isAdmin}
            selfEditableId={canGrantTimelineAccess && !isAdmin ? currentUser?.id : null}
            onSave={handleSaveGanttAccess}
            onClose={() => setShowGanttGiveAccess(false)}
          />
        );
      })()}

      {/* Version right-click context menu */}
      {versionContextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10400 }} onClick={() => setVersionContextMenu(null)} />
          <div
            style={{ position: 'fixed', left: versionContextMenu.x, top: versionContextMenu.y, zIndex: 10401, background: '#fff', border: '1px solid ' + BRIO_BORDER, borderRadius: '2px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '150px', fontFamily: 'Raleway, sans-serif', overflow: 'hidden' }}
          >
            {(isAdmin || isSupervisor || canManageTimeline) && (
            <button
              onClick={() => makeCurrentVersion(versionContextMenu.versionId)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: '12px', fontWeight: 300, color: '#333', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ed'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Make current
            </button>
            )}
            <div style={{ height: '1px', background: BRIO_BORDER, margin: '0 8px' }} />
            <button
              onClick={() => deleteVersion(versionContextMenu.versionId)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: '12px', fontWeight: 300, color: '#934948', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fdf2f2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Delete version
            </button>
          </div>
        </>
      )}

    </div>
  );
}
